/* eslint-disable */
import React, { useState, useEffect, useRef, useContext, createContext } from "react";
import ReactDOM from "react-dom";
import { supabase } from "./lib/supabase";
import { Avatar } from "./components/UI";
import { printHtml } from "./lib/printHtml";

// ── Theme context (provides C colors dynamically) ─────────────────────────────
const ThemeCtx = createContext(null);
const LIGHT_C = {
  // Tactile Archive palette — primary navy #24389c, warm paper bg #faf9f8
  bg:"#FAF9F8", surface:"#FFFFFF", surfaceAlt:"#F4F3F2", border:"#E5E3E0", border2:"#C5C5D4",
  green:"#2F6B3A", greenL:"#6CBF7A", greenM:"#E6EDE7",
  amber:"#A06014", amberL:"#F5EBD9",
  red:"#8A2828", redL:"#F1DADA",
  blue:"#24389C", blueL:"#DEE0FF",
  purple:"#6B3FA0", purpleL:"#ECE3F5",
  teal:"#1A7A6A", tealL:"#E0F0EC",
  text:"#1A1C1C", muted:"#454652", light:"#6A6B76",
  indigo:"#24389C", indigoL:"rgba(36,56,156,.10)",
  rose:"#8A2828", roseL:"#F1DADA",
  sky:"#24389C", emerald:"#2F6B3A", violet:"#6B3FA0",
  shadowSm:"0 1px 3px rgba(26,28,28,.06),0 1px 2px rgba(26,28,28,.04)",
  shadowMd:"0 4px 12px rgba(26,28,28,.08),0 2px 4px rgba(26,28,28,.04)",
  shadowLg:"0 10px 30px rgba(26,28,28,.10),0 4px 8px rgba(26,28,28,.05)",
  bg2:"#F4F3F2", bg3:"#E9E8E7", text2:"#454652", text3:"#6A6B76",
  inputBg:"#F4F3F2", cardBg:"#FFFFFF", isDark:false,
};
const DARK_C = {
  // Tactile Archive dark palette — bg #17161a, surface #232227
  bg:"#17161A", surface:"#232227", surfaceAlt:"#1E1D22", border:"rgba(255,255,255,.08)", border2:"rgba(255,255,255,.14)",
  green:"#6CBF7A", greenL:"#2F6B3A", greenM:"rgba(108,191,122,.12)",
  amber:"#DBA756", amberL:"rgba(219,167,86,.12)",
  red:"#E0797A", redL:"rgba(224,121,122,.12)",
  blue:"#7F8FE0", blueL:"rgba(127,143,224,.12)",
  purple:"#B79CE0", purpleL:"rgba(183,156,224,.12)",
  teal:"#5CB8A4", tealL:"rgba(92,184,164,.12)",
  text:"#F2F1EE", muted:"#C7C6D0", light:"#8F8E99",
  indigo:"#7F8FE0", indigoL:"rgba(127,143,224,.15)",
  rose:"#E0797A", roseL:"rgba(224,121,122,.12)",
  sky:"#7F8FE0", emerald:"#6CBF7A", violet:"#B79CE0",
  shadowSm:"0 1px 3px rgba(0,0,0,.4),0 1px 2px rgba(0,0,0,.3)",
  shadowMd:"0 4px 12px rgba(0,0,0,.5),0 2px 4px rgba(0,0,0,.3)",
  shadowLg:"0 10px 30px rgba(0,0,0,.6),0 4px 8px rgba(0,0,0,.4)",
  bg2:"#1E1D22", bg3:"#302F35", text2:"#C7C6D0", text3:"#8F8E99",
  inputBg:"#17161A", cardBg:"#232227", isDark:true,
};
const THEME_KEY = "aspreo_theme";
function getStoredTheme() { try { return localStorage.getItem(THEME_KEY)||"light"; } catch { return "light"; } }
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  const toggleTheme = () => setThemeState(t => { const n=t==="light"?"dark":"light"; try{localStorage.setItem(THEME_KEY,n);}catch{}; return n; });
  useEffect(() => {
    const C = theme==="dark" ? DARK_C : LIGHT_C;
    document.documentElement.setAttribute("data-theme", theme);
    document.body.style.background = C.bg;
    document.body.style.color = C.text;
    document.body.style.transition = "background .3s, color .3s";
    // Sync CSS variables for components using var(--surface) etc
    const root = document.documentElement.style;
    root.setProperty('--surface', C.surface);
    root.setProperty('--border', C.border);
    root.setProperty('--border2', C.border2);
    root.setProperty('--text', C.text);
    root.setProperty('--text2', C.muted);
    root.setProperty('--text3', C.light);
    root.setProperty('--bg', C.bg);
    root.setProperty('--bg2', C.bg2);
    root.setProperty('--bg3', C.bg3);
    root.setProperty('--green', C.green);
    root.setProperty('--amber', C.amber);
    root.setProperty('--red', C.red);
    root.setProperty('--blue', C.blue);
    root.setProperty('--purple', C.purple);
    root.setProperty('--indigo', C.indigo);
  }, [theme]);
  const C = theme==="dark" ? DARK_C : LIGHT_C;
  return <ThemeCtx.Provider value={{ C, theme, toggleTheme, isDark:theme==="dark" }}>{children}</ThemeCtx.Provider>;
}
function useC() { const ctx = useContext(ThemeCtx); return ctx ? ctx.C : LIGHT_C; }
export function useThemeToggle() { const ctx = useContext(ThemeCtx); return ctx ? ctx.toggleTheme : ()=>{}; }
export function useIsDark() { const ctx = useContext(ThemeCtx); return ctx ? ctx.isDark : false; }

// ── Date helpers ─────────────────────────────────────────────────────────────
// ── fmt uses LOCAL date (not UTC) so today's date is always correct regardless of timezone
// Always use IST (Asia/Kolkata) to avoid date mismatch on Vercel servers
const fmtIST = (d) => {
  const ist = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const y   = ist.getFullYear();
  const m   = String(ist.getMonth()+1).padStart(2,'0');
  const day = String(ist.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};
const fmt = fmtIST; // use IST everywhere
// `let`, not `const` — recomputed by DiaryApp's own effect below so a tab left
// open past midnight IST doesn't keep every "is today" check pinned to yesterday
// until the user does a manual hard refresh.
let TODAY_D = fmtIST(new Date());
let TODAY = TODAY_D; // alias used throughout components

// ── Global UI styles ─────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('diary-ui-styles')) {
  const _s = document.createElement('style');
  _s.id = 'diary-ui-styles';
  // Load Material Symbols for DiaryApp icons
  _s.textContent = [
    // Animations used by DiaryApp components
    "@keyframes bdaySlideIn{from{opacity:0;transform:translateY(20px) scale(.95);}to{opacity:1;transform:none;}}",
    "@keyframes shareCardIn{from{opacity:0;transform:scale(.92) translateY(20px);}to{opacity:1;transform:none;}}",
    // Smooth section transitions
    ".diary-section{animation:fadeUp .3s cubic-bezier(.4,0,.2,1) both;}",
  ].join('');
  document.head.appendChild(_s);
}

const daysBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
const MON_NAMES = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"]; // Monday-first
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// ── Colour palette (original green-based diary theme) ─────────────────────────
// C is now dynamic — always read from ThemeCtx at component level via useC()
// For module-level helpers (inp/btn/ta/lbl) that can't use hooks,
// we use a live proxy so they always reflect the current theme.
const _cProxy = new Proxy({}, { get(_, k) { try { const ctx=document.documentElement.getAttribute("data-theme"); return (ctx==="dark"?DARK_C:LIGHT_C)[k]; } catch { return LIGHT_C[k]; } } });
const C = _cProxy;

const PILLAR_COLORS = ["#24389C","#8A2828","#6B3FA0","#A06014","#2F6B3A","#1A7A6A","#6A6B76","#8A4D12"];
const FONT = "'Manrope',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const FONT_DISPLAY = "'Newsreader','Source Serif Pro',Georgia,serif";
const T = {
  h1:{ fontSize:28, fontWeight:900, lineHeight:1.2, letterSpacing:"-.025em", fontFamily:FONT },
  h2:{ fontSize:22, fontWeight:800, lineHeight:1.25, letterSpacing:"-.02em", fontFamily:FONT },
  h3:{ fontSize:16, fontWeight:700, lineHeight:1.3, letterSpacing:"-.01em", fontFamily:FONT },
  h4:{ fontSize:11, fontWeight:700, lineHeight:1.4, letterSpacing:".06em", textTransform:"uppercase", fontFamily:FONT },
  body:{ fontSize:13, fontWeight:400, lineHeight:1.6, fontFamily:FONT },
  small:{ fontSize:12, fontWeight:400, lineHeight:1.5, fontFamily:FONT },
  label:{ fontSize:10, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase", fontFamily:FONT },
};

// ── Shared style helpers ──────────────────────────────────────────────────────
const inp = (extra = {}) => ({ padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:"var(--radius-sm,8px)", fontFamily:FONT, fontSize:13, color:C.text, background:C.bg2||C.surfaceAlt, outline:"none", boxSizing:"border-box", transition:"border-color var(--t,.18s),box-shadow var(--t,.18s),background var(--t,.18s)", ...extra });
const ta  = (h = 100) => ({ ...inp(), width:"100%", height:h, resize:"vertical", display:"block", lineHeight:1.6 });
const lbl = { display:"block", fontSize:10, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase", color:C.light, marginBottom:5, fontFamily:FONT };
const btn = (color, h) => ({ background:color, color:"#fff", border:"none", borderRadius:"var(--radius-sm,8px)", padding: h ? `${Math.max(0,(h-28)/2)}px ${Math.max(4,(h-28)/2)}px` : "8px 16px", cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight:600, minWidth: h||"auto", height: h||"auto", display:"inline-flex", alignItems:"center", justifyContent:"center", transition:"all var(--t,.18s)", boxShadow:`0 2px 8px ${color}44` });

function Card({ title, color = C.green, children, right }) {
  return (
    <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--radius,12px)", padding:"16px 18px", boxShadow:"var(--shadow-sm)", transition:"box-shadow var(--t,.18s),border-color var(--t,.18s)", position:"relative", overflow:"hidden" }}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="var(--shadow-md)";e.currentTarget.style.borderColor="var(--border2)";}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="var(--shadow-sm)";e.currentTarget.style.borderColor="var(--border)";}}>
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:color, borderRadius:"12px 12px 0 0" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h4 style={{ margin:0, ...T.h4, color }}>{title}</h4>
        {right}
      </div>
      {children}
    </div>
  );
}

// ── Editorial masthead — shared header treatment across every section ────────
// eyebrow: small mono uppercase kicker. title: JSX, wrap the emphasised word in <em>.
function SectionMasthead({ eyebrow, title, sub, actions }) {
  return (
    <div className="ta-row ta-between" style={{alignItems:"flex-end",marginBottom:20,flexWrap:"wrap",gap:16}}>
      <div>
        {eyebrow && <div className="ta-eh-eyebrow">{eyebrow}</div>}
        <h1 className="ta-eh-title" style={{fontSize:36}}>{title}</h1>
        {sub && <div className="ta-eh-sub" style={{marginTop:6}}>{sub}</div>}
      </div>
      {actions && <div className="ta-row ta-gap-2" style={{flexWrap:"wrap"}}>{actions}</div>}
    </div>
  );
}

// ── Persistent AudioContext — must be created/resumed on user gesture ────────
let _actx = null;
function getAudioCtx() {
  try {
    // Recreate if closed (can happen after long inactivity)
    if (!_actx || _actx.state === 'closed') {
      _actx = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended — required by browser autoplay policy
    if (_actx.state === 'suspended') {
      _actx.resume().catch(() => {});
    }
    return _actx;
  } catch(e) {
    _actx = null;
    return null;
  }
}
// Unlock audio on ANY user interaction (click/touch/keydown)
if (typeof window !== 'undefined') {
  const unlock = () => {
    try {
      const ctx = getAudioCtx();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch {}
  };
  ['click','touchstart','keydown','pointerdown'].forEach(ev =>
    document.addEventListener(ev, unlock, { once: false, passive: true })
  );
}

function playDing(type = "bell") {
  try {
    const actx = getAudioCtx();
    if (!actx) return;
    if (type === "bell") {
      // Rich bell chord — C4, E4, G4, C5
      [[261,0],[329,0.05],[392,0.1],[523,0.18]].forEach(([freq, delay]) => {
        const osc  = actx.createOscillator();
        const gain = actx.createGain();
        osc.connect(gain); gain.connect(actx.destination);
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, actx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(0.28, actx.currentTime + delay + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + delay + 3.5);
        osc.start(actx.currentTime + delay);
        osc.stop(actx.currentTime + delay + 3.5);
      });
    } else if (type === "complete") {
      // 3-tone success jingle
      [[523,0],[659,0.18],[784,0.36]].forEach(([freq,delay]) => {
        const osc  = actx.createOscillator();
        const gain = actx.createGain();
        osc.connect(gain); gain.connect(actx.destination);
        osc.type = "triangle"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, actx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + delay + 0.8);
        osc.start(actx.currentTime + delay);
        osc.stop(actx.currentTime + delay + 0.8);
      });
    } else {
      const osc  = actx.createOscillator();
      const gain = actx.createGain();
      osc.connect(gain); gain.connect(actx.destination);
      osc.type = "sine"; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.4, actx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + 0.8);
      osc.start(actx.currentTime); osc.stop(actx.currentTime + 0.8);
    }
  } catch(e) { console.warn('playDing error', e); }
}

// ── Local notification store (bell icon) — persisted in sessionStorage ───────
const LOCAL_NOTIF_KEY = 'aspreo_local_notifs';
function getLocalNotifs() { try { return JSON.parse(sessionStorage.getItem(LOCAL_NOTIF_KEY)||'[]'); } catch { return []; } }
function addLocalNotif(title, message) {
  const notifs = getLocalNotifs();
  notifs.unshift({ id: Date.now(), title, message, time: new Date().toISOString(), read: false });
  try { sessionStorage.setItem(LOCAL_NOTIF_KEY, JSON.stringify(notifs.slice(0,20))); } catch {}
  // Dispatch custom event so Navbar can re-render
  window.dispatchEvent(new CustomEvent('aspreo_notif', { detail: { title, message } }));
}


// ══════════════════════════════════════════════════════════════════════════════
// DEFAULTS
// ══════════════════════════════════════════════════════════════════════════════

// Default vision pillars — used only as fallback if user has none saved
const DEFAULT_VISION_PILLARS = [
  { id:"edu",      label:"Education",          icon:"🎓", color:"#1D5F8A" },
  { id:"career",   label:"Career & Job",        icon:"💼", color:"#B94040" },
  { id:"health",   label:"Health & Fitness",    icon:"💪", color:"#2D7A2D" },
  { id:"relation", label:"Relationships",       icon:"❤️", color:"#C8405A" },
  { id:"certif",   label:"Certifications",      icon:"🏆", color:"#6B3FA0" },
  { id:"travel",   label:"Travel & Vacations",  icon:"✈️", color:"#0E7490" },
];
const LS_PILLARS = "diary_vision_pillars";
const loadStoredPillars = () => { try { const p=JSON.parse(localStorage.getItem(LS_PILLARS)); return p?.length?p:null; } catch{ return null; } };
const VISION_PILLARS = loadStoredPillars() || DEFAULT_VISION_PILLARS;


// ── DiaryApp: main entry point for all diary views ────────────────────────────
export default function DiaryApp({ data, view, setView, onToast, profile: appProfile }) {
  const C = useC(); // live theme colors — updates on light/dark toggle
  const [, forceUpdate] = useState(0);
  // Task selected via "Focus on this" — consumed by Dashboard's Pomodoro and Deep Work
  const [focusTask, setFocusTask] = useState(null);
  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    window.addEventListener('aspreo_theme_change', handler);
    return () => window.removeEventListener('aspreo_theme_change', handler);
  }, []);
  // Keep the app-wide TODAY/TODAY_D constants from going stale in a long-lived tab —
  // without this, everything that compares against "today" (calendar highlighting,
  // default dates on new items) silently uses yesterday's date past midnight IST
  // until the user does a manual hard refresh.
  useEffect(() => {
    const checkToday = () => {
      const t = fmtIST(new Date());
      if (t !== TODAY) { TODAY = t; TODAY_D = t; forceUpdate(n => n + 1); }
    };
    const interval = setInterval(checkToday, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') checkToday(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkToday);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', checkToday); };
  }, []);
  const {
    selDate, setSelDate,
    goals, addGoal, updateGoal, deleteGoal,
    tasks, addTask, updateTask, deleteTask,
    getDailyEntry, updateDailyEntry, loadDailyEntry,
    checkinDefs, addCheckinDef, deleteCheckinDef,
    dailyCi, toggleCi, loadDayCi,
    habits, addHabit, deleteHabit, archiveHabit, unarchiveHabit,
    habitLog, toggleHabit, loadHabitLogForDate,
    expenses, addExpense, deleteExpense,
    income, addIncome, deleteIncome,
    fixedIncome, updateFixedIncome,
    ideas, addIdea, updateIdea, deleteIdea,
    books, addBook, updateBook, deleteBook, archiveBook, unarchiveBook,
    subs, addSub, updateSub, deleteSub,
    reminders, addReminder, dismissReminder, deleteReminder,
    visions, addVision, updateVision, deleteVision, togglePinVision,
    visionPillars, addVisionPillar, updateVisionPillar, deleteVisionPillar,
    banner, updateBanner, dashboardVisions,
    contentPillars, addContentPillar, deleteContentPillar,
    contentPosts, addContentPost, updateContentPost, deleteContentPost,
    contentStrategy, updateContentStrategy,
    weekPlan, updateWeekPlan, loadWeekPlan,
    monthPlan, updateMonthPlan, loadMonthPlan,
    uploadUserFile,
    vaultFolders, addVaultFolder, updateVaultFolder, deleteVaultFolder,
    vaultLinks, addVaultLink, updateVaultLink, deleteVaultLink,
    projects, addProject, updateProject, deleteProject,
    projectTasks, addProjectTask, updateProjectTask, deleteProjectTask,
    specialDates, addSpecialDate, deleteSpecialDate,
    contentStrategies, addContentStrategy2, updateContentStrategy2, deleteContentStrategy2,
    archiveGoal, unarchiveGoal,
    recurringTasks, addRecurringTask, updateRecurringTask, deleteRecurringTask,
    sharedTemplates, addSharedTemplate, deleteSharedTemplate, hardDeleteSharedTemplate, fetchPublicTemplates, fetchMyTemplates, fetchTemplateById,
    reflections, addReflection, updateReflection, deleteReflection,
    journalEntries, addJournalEntry, updateJournalEntry, deleteJournalEntry,
    journalTags, getOrCreateJournalTag, deleteJournalTag,
    journalEntryTags, setJournalEntryTagList,
    journalAttachments, addJournalAttachment, deleteJournalAttachment,
    saveJournalVersion, getJournalVersions,
    fitnessMeals, addFitnessMeal, deleteFitnessMeal,
    fitnessBodyStats, addFitnessBodyStat, deleteFitnessBodyStat,
    fitnessGoals, updateFitnessGoals,
    dietFoods, addDietFood, updateDietFood, deleteDietFood,
    dietMealLogs, addDietMealLog, deleteDietMealLog,
    dietWaterLogs, addDietWaterLog, deleteDietWaterLog,
    dietSupplements, addDietSupplement, updateDietSupplement, deleteDietSupplement,
    dietSupplementLogs, toggleDietSupplementLog,
    dietTargets, updateDietTargets,
    dietMealPlan, addDietMealPlanItem, deleteDietMealPlanItem,
    workoutExercises, addWorkoutExercise, updateWorkoutExercise, deleteWorkoutExercise,
    workoutPlans, addWorkoutPlan, updateWorkoutPlan, deleteWorkoutPlan,
    workoutPlanExercises, addWorkoutPlanExercise, deleteWorkoutPlanExercise,
    workoutSchedule, addWorkoutScheduleItem, deleteWorkoutScheduleItem,
    workoutLogs, addWorkoutLog, updateWorkoutLog, deleteWorkoutLog,
    workoutLogExercises, addWorkoutLogExercise, deleteWorkoutLogExercise,
    workoutLogSets, addWorkoutLogSet, updateWorkoutLogSet, deleteWorkoutLogSet,
    bodyProfile, updateBodyProfile,
    bodyCompositionLogs, addBodyCompositionLog, deleteBodyCompositionLog,
    healthGoals, addHealthGoal, updateHealthGoal, deleteHealthGoal,
    deepWorkSessions, addDeepWorkSession, deleteDeepWorkSession,
    calendarEvents, addCalendarEvent, deleteCalendarEvent,
  } = data;
  // ── Collaboration + Feed + Fellows (assigned from data directly) ──────────
  const projectMembers     = data.projectMembers     || [];
  const inviteProjectMember= data.inviteProjectMember;
  const acceptProjectInvite= data.acceptProjectInvite;
  const removeProjectMember= data.removeProjectMember;
  const assignProjectTask  = data.assignProjectTask;
  const feedPosts          = data.feedPosts          || [];
  const addFeedPost        = data.addFeedPost;
  const deleteFeedPost     = data.deleteFeedPost;
  const toggleFeedLike     = data.toggleFeedLike;
  const fellows            = data.fellows            || [];
  const sendFellowRequest  = data.sendFellowRequest;
  const respondFellowRequest = data.respondFellowRequest;
  const removeFellow       = data.removeFellow;
  // SaaS Plans
  const userPlan              = data.userPlan            || null;
  const upgradePlan           = data.upgradePlan;
  const workspaces            = data.workspaces          || [];
  const addWorkspace          = data.addWorkspace;
  const updateWorkspace       = data.updateWorkspace;
  const deleteWorkspace       = data.deleteWorkspace;
  const workspaceMembers      = data.workspaceMembers    || [];
  const inviteWorkspaceMember = data.inviteWorkspaceMember;
  const updateWorkspaceMember = data.updateWorkspaceMember;
  const removeWorkspaceMember = data.removeWorkspaceMember;
  // Payment Automation
  const products          = data.products          || [];
  const orders            = data.orders            || [];
  const automations       = data.automations       || [];
  const addProduct        = data.addProduct;
  const updateProduct     = data.updateProduct;
  const deleteProduct     = data.deleteProduct;
  const addOrder          = data.addOrder;
  const updateOrder       = data.updateOrder;
  const addAutomation     = data.addAutomation;
  const updateAutomation  = data.updateAutomation;
  const deleteAutomation  = data.deleteAutomation;
  const services          = data.services          || [];
  const addService        = data.addService;
  const updateService     = data.updateService;
  const deleteService     = data.deleteService;
  const bizProfile        = data.bizProfile        || {};
  const updateBizProfile  = data.updateBizProfile;
  // ── Business + Social + Meetings (from data) ──────────────────────────────
  const leads              = data.leads              || [];
  const clients            = data.clients            || [];
  const proposals          = data.proposals          || [];
  const invoices           = data.invoices           || [];
  const sheetLinks         = data.sheetLinks         || [];
  const setLeads           = data.setLeads;
  const setClients         = data.setClients;
  const setInvoices        = data.setInvoices;
  const setProposals       = data.setProposals;
  const addLead            = data.addLead;
  const updateLead         = data.updateLead;
  const deleteLead         = data.deleteLead;
  const convertLeadToClient= data.convertLeadToClient;
  const addClient          = data.addClient;
  const updateClient       = data.updateClient;
  const deleteClient       = data.deleteClient;
  const addProposal        = data.addProposal;
  const updateProposal     = data.updateProposal;
  const deleteProposal     = data.deleteProposal;
  const addInvoice         = data.addInvoice;
  const updateInvoice      = data.updateInvoice;
  const deleteInvoice      = data.deleteInvoice;
  const addSheetLink       = data.addSheetLink;
  const updateSheetLink    = data.updateSheetLink;
  const deleteSheetLink    = data.deleteSheetLink;
  const consultants        = data.consultants        || [];
  const addConsultant      = data.addConsultant;
  const updateConsultant   = data.updateConsultant;
  const deleteConsultant   = data.deleteConsultant;
  const socialAccounts     = data.socialAccounts     || [];
  const addSocialAccount   = data.addSocialAccount;
  const updateSocialAccount= data.updateSocialAccount;
  const deleteSocialAccount= data.deleteSocialAccount;
  const dmTemplates        = data.dmTemplates        || [];
  const addDMTemplate      = data.addDMTemplate;
  const updateDMTemplate   = data.updateDMTemplate;
  const deleteDMTemplate   = data.deleteDMTemplate;
  const viralLinks         = data.viralLinks         || [];
  const addViralLink       = data.addViralLink;
  const updateViralLink    = data.updateViralLink;
  const deleteViralLink    = data.deleteViralLink;
  const engagement         = data.engagement         || [];
  const saveEngagement     = data.saveEngagement;
  const meetingTypes       = data.meetingTypes       || [];
  const addMeetingType     = data.addMeetingType;
  const updateMeetingType  = data.updateMeetingType;
  const deleteMeetingType  = data.deleteMeetingType;
  const availability       = data.availability       || [];
  const saveAvailability   = data.saveAvailability;
  const meetings           = data.meetings           || [];
  const addMeeting         = data.addMeeting;
  const updateMeeting      = data.updateMeeting;
  const deleteMeeting      = data.deleteMeeting;
  const searchUserByUsername = data.searchUserByUsername;
  const uid                = data.uid || '';

  // ── Request browser notification permission on mount ─────────────────────
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // ── Reminder polling ──────────────────────────────────────────────────────
  const firedRef = useRef(new Set());
  useEffect(() => {
    const check = () => {
      const now = new Date();
      reminders.forEach(r => {
        if (!r.reminder_datetime || r.dismissed) return;
        if (firedRef.current.has(r.id)) return;
        const diff = (new Date(r.reminder_datetime) - now) / 1000;
        if (diff >= -30 && diff <= 30) {
          firedRef.current.add(r.id);
          playDing("bell");
          onToast(`⏰ ${r.text}`);
          addLocalNotif(`⏰ Reminder`, r.text);
          if (Notification.permission === 'granted') {
            try { new Notification('⏰ Reminder', { body: r.text, icon: '/favicon.ico' }); } catch {}
          }
          dismissReminder(r.id);
        }
      });
    };
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [reminders]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Comprehensive notification system (birthdays, tasks, fellows, invites) ─
  useEffect(() => {
    if (!uid) return;
    const FIRED_KEY = 'aspreo_notif_fired2';
    const getFired = () => { try { return new Set(JSON.parse(sessionStorage.getItem(FIRED_KEY)||'[]')); } catch { return new Set(); } };
    const addFired = k => { try { const s=getFired(); s.add(k); sessionStorage.setItem(FIRED_KEY,JSON.stringify([...s].slice(-100))); } catch {} };
    const isSoundOn = () => { try { return localStorage.getItem('aspreo_notif_sound') !== 'false'; } catch { return true; } };
    const notify = (title, body, soundType='bell') => {
      if (isSoundOn()) try { playDing(soundType); } catch {}
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification(title, { body, icon: '/logo192.png', silent: true }); } catch {}
      }
      addLocalNotif(title, body);
    };
    const check = () => {
      const fired = getFired();
      const today = new Date();
      const todayStr = fmtIST(today); // IST-correct "today", not toISOString()'s UTC date
      const nowMs = today.getTime();
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      // Reminders are owned exclusively by the tight ±30s poller above — handling
      // them here too used to fire a second, duplicate notification for the same
      // reminder (once from each poller).
      // 2. Due tasks today — tasks is a map {date:[tasks]}
      const taskArr = typeof tasks === 'object' && !Array.isArray(tasks)
        ? Object.values(tasks).flat()
        : (Array.isArray(tasks) ? tasks : []);
      const due = taskArr.filter(t => !t.done && (t.task_date === todayStr || t.date === todayStr));
      if (due.length > 0) {
        const key = `due-tasks-${todayStr}`;
        if (!fired.has(key)) { addFired(key); notify('📋 Tasks Due Today', due.length===1?`"${due[0].text}" is due today`:`${due.length} tasks due today`); }
      }
      // 2b. Task-specific time notifications (fire at the exact task time)
      due.forEach(t => {
        if (!t.reminder_datetime && !t.task_time) return;
        let taskTimeMs = null;
        if (t.reminder_datetime) {
          taskTimeMs = new Date(t.reminder_datetime).getTime();
        } else if (t.task_time && (t.task_date || t.date)) {
          taskTimeMs = new Date(`${t.task_date||t.date}T${t.task_time}:00`).getTime();
        }
        if (!taskTimeMs) return;
        const key = `task-time-${t.id}`;
        if (fired.has(key)) return;
        const diff = taskTimeMs - nowMs;
        if (diff >= -60000 && diff <= 300000) {
          addFired(key);
          notify('⏰ Task Due Now', `"${t.text}" is due`);
        }
      });
      // 3. Birthdays today
      const sdArr = Array.isArray(specialDates) ? specialDates : [];
      sdArr.forEach(d => {
        if (Number(d.month)!==mm || Number(d.day)!==dd) return;
        const key = `bday-${d.id||d.name}-${todayStr}`;
        if (fired.has(key)) return;
        addFired(key);
        const isBday = (d.type||'birthday')==='birthday';
        notify(isBday?'🎂 Birthday Today!':'💍 Anniversary Today!', `${d.name}'s ${isBday?'birthday':'anniversary'} is today`);
      });
      // 4. Pending fellow requests — use IDs in key so new requests always notify
      const fellowArr = Array.isArray(fellows) ? fellows : [];
      const pf = fellowArr.filter(f => f.status==='pending' && f.addressee_id===uid);
      pf.forEach(f => {
        const key = `fellow-${f.id||f.requester_id}`;
        if (!fired.has(key)) { addFired(key); notify('👥 Fellow Request', `${f.requester_name||'Someone'} sent you a fellow request`); }
      });
      // 5. Pending project invites — use IDs in key
      const pmArr = Array.isArray(projectMembers) ? projectMembers : [];
      const pi = pmArr.filter(m => m.status==='pending' && m.user_id===uid);
      pi.forEach(m => {
        const key = `invite-${m.id||m.project_id}`;
        if (!fired.has(key)) { addFired(key); notify('📁 Project Invite', 'You have a pending project invitation'); }
      });
      // 6. Upcoming meetings — heads-up 15 minutes before start
      const meetingArr = Array.isArray(meetings) ? meetings : [];
      meetingArr.forEach(m => {
        if (!m.start_time || m.status === 'cancelled') return;
        const key = `meeting-${m.id}`;
        if (fired.has(key)) return;
        const diff = new Date(m.start_time).getTime() - nowMs;
        if (diff >= 0 && diff <= 900000) { // within the next 15 minutes
          addFired(key);
          const title = m.meeting_types?.title || 'Meeting';
          const mins = Math.max(1, Math.round(diff/60000));
          notify('📅 Upcoming Meeting', `${title}${m.guest_name?` with ${m.guest_name}`:''} in ${mins} min`);
        }
      });
    };
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, [uid, reminders, meetings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build ctx for all section components ─────────────────────────────────
  // Components call both ctx.setGoals(fn) AND ctx.addGoal(obj) patterns.
  // The setXxx wrappers diff state and route through the real DB functions.
  // ── Plan access helper ─────────────────────────────────────────────────────
  const planId   = userPlan?.plan_id || 'hobby';
  const planMods = userPlan?.plans?.modules
    ? (typeof userPlan.plans.modules === 'string' ? JSON.parse(userPlan.plans.modules) : userPlan.plans.modules)
    : ['dashboard','goals','visions','habits','weekly','monthly','finance','books','subs','ideas','vault','projects','feed'];
  // Workspace memberships for this user (they were invited by an agency owner)
  const myActiveMemberships = (workspaceMembers||[]).filter(
    m => m.member_id === uid && m.status === 'active' && m.owner_id !== uid
  );
  const isWorkspaceMember = myActiveMemberships.length > 0;

  const isAdmin = appProfile?.role === 'admin';
  // Single universal tier — every enrolled user gets full access to every
  // module, no plan-based gating. (planId/planMods/isWorkspaceMember above
  // are left in place since other code still reads them for display, but
  // access itself is no longer conditional on any of it.)
  const canAccess = () => true;
  const isPaid    = isAdmin || planId === 'solopreneur' || planId === 'agency';
  const isAgency  = isAdmin || planId === 'agency';

  const ctx = {
    selDate, setSelDate,
    goals, tasks, habits, habitLog, checkinDefs, dailyCi,
    expenses, income, fixedIncome,
    ideas, books, subs, reminders, visions,
    banner, visionBoardBanner: banner || {},
    dashboardVisions,
    contentPillars, contentPosts, contentStrategy,
    // setXxx wrappers — diff old vs new, call real DB functions
    setGoals:    (fn) => { const G=goals,next=typeof fn==='function'?fn(G):fn; next.forEach(n=>{if(!G.find(g=>g.id===n.id))addGoal(n);else if(JSON.stringify(G.find(g=>g.id===n.id))!==JSON.stringify(n))updateGoal(n.id,n);}); G.forEach(g=>{if(!next.find(n=>n.id===g.id))deleteGoal(g.id);}); },
    setTasks:    (fn) => { const T=tasks,next=typeof fn==='function'?fn(T):fn; Object.entries(next).forEach(([date,arr])=>{ const prev=T[date]||[]; (arr||[]).forEach(t=>{if(!prev.find(p=>p.id===t.id))addTask(date,t.text);else{ const p=prev.find(x=>x.id===t.id); if(JSON.stringify(p)!==JSON.stringify(t))updateTask(date,t.id,{done:t.done,priority:t.priority,text:t.text});}}); prev.forEach(pt=>{if(!(arr||[]).find(x=>x.id===pt.id))deleteTask(date,pt.id);}); }); },
    setHabits:   (fn) => { const H=habits,next=typeof fn==='function'?fn(H):fn; next.forEach(n=>{if(!H.find(h=>h.id===n.id))addHabit(n);}); H.forEach(h=>{if(!next.find(n=>n.id===h.id))deleteHabit(h.id);}); },    setHabitLog: (fn) => { const L=habitLog,next=typeof fn==='function'?fn(L):fn; Object.entries(next).forEach(([key,done])=>{if(L[key]!==done){const[date,hid]=key.split(':');toggleHabit(date,hid);}}); },
    setExpenses: (fn) => { const E=expenses,next=typeof fn==='function'?fn(E):fn; next.forEach(n=>{if(!E.find(e=>e.id===n.id))addExpense(n);}); E.forEach(e=>{if(!next.find(n=>n.id===e.id))deleteExpense(e.id);}); },
    setIncome:   (fn) => { const I=income,next=typeof fn==='function'?fn(I):fn; next.forEach(n=>{if(!I.find(i=>i.id===n.id))addIncome(n);}); I.forEach(i=>{if(!next.find(n=>n.id===i.id))deleteIncome(i.id);}); },
    setFixedIncome: updateFixedIncome,
    setIdeas:    (fn) => { const A=ideas,next=typeof fn==='function'?fn(A):fn; next.forEach(n=>{if(!A.find(i=>i.id===n.id))addIdea(n);}); A.forEach(i=>{if(!next.find(n=>n.id===i.id))deleteIdea(i.id);}); },
    setBooks:    (fn) => { const B=books,next=typeof fn==='function'?fn(B):fn; next.forEach(n=>{if(!B.find(b=>b.id===n.id))addBook(n);else if(JSON.stringify(B.find(b=>b.id===n.id))!==JSON.stringify(n))updateBook(n.id,n);}); B.forEach(b=>{if(!next.find(n=>n.id===b.id))deleteBook(b.id);}); },
    setSubs:     (fn) => { const S=subs,next=typeof fn==='function'?fn(S):fn; next.forEach(n=>{if(!S.find(s=>s.id===n.id))addSub(n);}); S.forEach(s=>{if(!next.find(n=>n.id===s.id))deleteSub(s.id);}); },
    setReminders:(fn) => { const R=reminders,next=typeof fn==='function'?fn(R):fn; next.forEach(n=>{if(!R.find(r=>r.id===n.id))addReminder(n);}); R.forEach(r=>{if(!next.find(n=>n.id===r.id))deleteReminder(r.id);}); },
    setVisions:  (fn) => { const V=visions,next=typeof fn==='function'?fn(V):fn; next.forEach(n=>{if(!V.find(v=>v.id===n.id))addVision(n);else if(JSON.stringify(V.find(v=>v.id===n.id))!==JSON.stringify(n))updateVision(n.id,n);}); V.forEach(v=>{if(!next.find(n=>n.id===v.id))deleteVision(v.id);}); },
    setVisionBoardBanner: updateBanner,
    setDashboardVisions: (fn) => { const D=dashboardVisions,next=typeof fn==='function'?fn(D):fn; next.forEach(id=>{if(!D.includes(id))togglePinVision(id);}); D.forEach(id=>{if(!next.includes(id))togglePinVision(id);}); },
    setCheckinDefs: (fn) => { const C=checkinDefs,next=typeof fn==='function'?fn(C):fn; next.forEach(c=>{if(!C.find(x=>x.id===c.id))addCheckinDef(c);}); C.forEach(c=>{if(!next.find(x=>x.id===c.id))deleteCheckinDef(c.id);}); },
    setDailyCi: (fn) => { const DC=dailyCi,next=typeof fn==='function'?fn(DC):fn; Object.entries(next).forEach(([date,map])=>{Object.entries(map).forEach(([ciId,done])=>{if((DC[date]||{})[ciId]!==done)toggleCi(date,ciId);});}); },
    setContentPillars: (fn) => { const P=contentPillars,next=typeof fn==='function'?fn(P):fn; next.forEach(n=>{if(!P.find(x=>x.id===n.id))addContentPillar(n);}); P.forEach(p=>{if(!next.find(n=>n.id===p.id))deleteContentPillar(p.id);}); },
    setContentPosts:   (fn) => { const P=contentPosts,next=typeof fn==='function'?fn(P):fn; next.forEach(n=>{if(!P.find(x=>x.id===n.id))addContentPost(n);else if(JSON.stringify(P.find(x=>x.id===n.id))!==JSON.stringify(n))updateContentPost(n.id,n);}); P.forEach(p=>{if(!next.find(n=>n.id===p.id))deleteContentPost(p.id);}); },
    setContentStrategy:(fn) => { const next=typeof fn==='function'?fn(contentStrategy):fn; updateContentStrategy(next); },
    // Utilities
    notes:{}, setNotes:()=>{}, grateful:{}, setGrateful:()=>{}, learnings:{}, setLearnings:()=>{},
    priorities:{}, setPriorities:()=>{},
    weekPlan, setWeekPlan: (fn) => { const next = typeof fn==='function' ? fn(weekPlan) : fn; Object.entries(next).forEach(([wk, val]) => { if (JSON.stringify(weekPlan[wk]) !== JSON.stringify(val)) updateWeekPlan(wk, val); }); },
    monthPlan, setMonthPlan: (fn) => { const next = typeof fn==='function' ? fn(monthPlan) : fn; Object.entries(next).forEach(([mk, val]) => { if (JSON.stringify(monthPlan[mk]) !== JSON.stringify(val)) updateMonthPlan(mk, val); }); },
    getDailyEntry, updateDailyEntry, loadDailyEntry,
    backups:[], createBackup: data.exportBackup, importBackup:()=>{},
    uid: data.uid,
    profile: appProfile || data.profile,
    view, setView, setAlert: onToast,
    // Direct named functions (for components that call these explicitly)
    addGoal, updateGoal, deleteGoal,
    addTask, updateTask, deleteTask,
    addCheckinDef, deleteCheckinDef, toggleCi, loadDayCi,
    addHabit, deleteHabit, toggleHabit, loadHabitLogForDate,
    addExpense, deleteExpense, addIncome, deleteIncome, updateFixedIncome,
    addIdea, updateIdea, deleteIdea,
    addBook, updateBook, deleteBook,
    addSub, updateSub, deleteSub,
    addReminder, dismissReminder, deleteReminder,
    addVision, updateVision, deleteVision, togglePinVision,
    visionPillars, addVisionPillar, updateVisionPillar, deleteVisionPillar,
    addContentPillar, deleteContentPillar,
    addContentPost, updateContentPost, deleteContentPost,
    updateContentStrategy, updateBanner, uploadUserFile,
    updateWeekPlan, loadWeekPlan, updateMonthPlan, loadMonthPlan,
    // LinkVault
    vaultFolders, addVaultFolder, updateVaultFolder, deleteVaultFolder,
    vaultLinks, addVaultLink, updateVaultLink, deleteVaultLink,
    // Projects + collaboration
    projects: projects||[], addProject, updateProject, deleteProject,
    projectTasks: projectTasks||[], addProjectTask, updateProjectTask, deleteProjectTask,
    projectMembers: projectMembers||[], inviteProjectMember, acceptProjectInvite, removeProjectMember, assignProjectTask,
    // Feed + Fellows (removed — use Projects for collaboration)
    feedPosts: [], addFeedPost:()=>{}, deleteFeedPost:()=>{}, toggleFeedLike:()=>{},
    fellows: [], sendFellowRequest:()=>{}, respondFellowRequest:()=>{}, removeFellow:()=>{}, searchUserByUsername:async()=>({data:[]}),
    // Special Dates
    specialDates: specialDates||[], addSpecialDate, deleteSpecialDate,
    // Content Strategies (multiple)
    contentStrategies: contentStrategies||[], addContentStrategy2, updateContentStrategy2, deleteContentStrategy2,
    // Archive functions
    archiveHabit, unarchiveHabit, archiveGoal, unarchiveGoal, archiveBook, unarchiveBook,
    // Recurring tasks
    recurringTasks: recurringTasks||[], addRecurringTask, updateRecurringTask, deleteRecurringTask,
    // Templates
    sharedTemplates: sharedTemplates||[], addSharedTemplate, deleteSharedTemplate, hardDeleteSharedTemplate, fetchPublicTemplates, fetchMyTemplates, fetchTemplateById,
    // Business Module
    leads:     leads     || [],
    clients:   clients   || [],
    proposals: proposals || [],
    invoices:  invoices  || [],
    setLeads,  setClients, setInvoices, setProposals,
    sheetLinks:sheetLinks|| [],
    addLead,      updateLead,      deleteLead,      convertLeadToClient,
    addClient,    updateClient,    deleteClient,
    addProposal,  updateProposal,  deleteProposal,
    addInvoice,   updateInvoice,   deleteInvoice,
    addSheetLink, updateSheetLink, deleteSheetLink,
    consultants: consultants || [],
    addConsultant, updateConsultant, deleteConsultant,
    // Services + Biz Profile
    services: services || [], addService, updateService, deleteService,
    bizProfile: bizProfile || {}, updateBizProfile,
    // Social Media
    socialAccounts: socialAccounts || [],
    addSocialAccount, updateSocialAccount, deleteSocialAccount,
    dmTemplates: dmTemplates || [],
    addDMTemplate, updateDMTemplate, deleteDMTemplate,
    viralLinks: viralLinks || [],
    addViralLink, updateViralLink, deleteViralLink,
    engagement: engagement || [],
    saveEngagement,
    // Meeting Scheduler
    meetingTypes: meetingTypes || [],
    addMeetingType, updateMeetingType, deleteMeetingType,
    availability: availability || [],
    saveAvailability,
    meetings: meetings || [],
    addMeeting, updateMeeting, deleteMeeting,
    // SaaS Plans
    userPlan, upgradePlan,
    workspaces, addWorkspace, updateWorkspace, deleteWorkspace,
    workspaceMembers, inviteWorkspaceMember, updateWorkspaceMember, removeWorkspaceMember,
    // Wiki
    wikiPages: data.wikiPages||[], addWikiPage: data.addWikiPage, updateWikiPage: data.updateWikiPage, deleteWikiPage: data.deleteWikiPage,
    // Knowledge Base (Second Brain)
    knowledgeDomains: data.knowledgeDomains||[], addKnowledgeDomain: data.addKnowledgeDomain, updateKnowledgeDomain: data.updateKnowledgeDomain, deleteKnowledgeDomain: data.deleteKnowledgeDomain,
    knowledgeCollections: data.knowledgeCollections||[], addKnowledgeCollection: data.addKnowledgeCollection, updateKnowledgeCollection: data.updateKnowledgeCollection, deleteKnowledgeCollection: data.deleteKnowledgeCollection,
    knowledgeTags: data.knowledgeTags||[], getOrCreateKnowledgeTag: data.getOrCreateKnowledgeTag, deleteKnowledgeTag: data.deleteKnowledgeTag,
    knowledgeItems: data.knowledgeItems||[], addKnowledgeItem: data.addKnowledgeItem, updateKnowledgeItem: data.updateKnowledgeItem, deleteKnowledgeItem: data.deleteKnowledgeItem, recordKnowledgeItemView: data.recordKnowledgeItemView,
    knowledgeItemTags: data.knowledgeItemTags||[], setKnowledgeItemTagList: data.setKnowledgeItemTagList,
    knowledgeRelated: data.knowledgeRelated||[], addKnowledgeRelated: data.addKnowledgeRelated, deleteKnowledgeRelated: data.deleteKnowledgeRelated,
    knowledgeAttachments: data.knowledgeAttachments||[], addKnowledgeAttachment: data.addKnowledgeAttachment, deleteKnowledgeAttachment: data.deleteKnowledgeAttachment,
    saveKnowledgeVersion: data.saveKnowledgeVersion, getKnowledgeVersions: data.getKnowledgeVersions,
    // Integrations
    integrations: data.integrations||[], saveIntegration: data.saveIntegration, removeIntegration: data.removeIntegration,
    // Business Expenses
    businessExpenses: data.businessExpenses||[], addBusinessExpense: data.addBusinessExpense, deleteBusinessExpense: data.deleteBusinessExpense,
    senderProfiles: data.senderProfiles||[], addSenderProfile: data.addSenderProfile, updateSenderProfile: data.updateSenderProfile, deleteSenderProfile: data.deleteSenderProfile,
    // Time Logs
    timeLogs: data.timeLogs||[], addTimeLog: data.addTimeLog,
    // Payment Automation
    products:    products    || [],
    orders:      orders      || [],
    automations: automations || [],
    addProduct, updateProduct, deleteProduct,
    addOrder,   updateOrder,
    addAutomation, updateAutomation, deleteAutomation,
    // Reflections
    reflections: reflections||[], addReflection, updateReflection, deleteReflection,
    // Journal
    journalEntries: journalEntries||[], addJournalEntry, updateJournalEntry, deleteJournalEntry,
    journalTags: journalTags||[], getOrCreateJournalTag, deleteJournalTag,
    journalEntryTags: journalEntryTags||[], setJournalEntryTagList,
    journalAttachments: journalAttachments||[], addJournalAttachment, deleteJournalAttachment,
    saveJournalVersion, getJournalVersions,
    // Fitness & Diet
    fitnessMeals: fitnessMeals||[], addFitnessMeal, deleteFitnessMeal,
    fitnessBodyStats: fitnessBodyStats||[], addFitnessBodyStat, deleteFitnessBodyStat,
    fitnessGoals: fitnessGoals||null, updateFitnessGoals,
    dietFoods: dietFoods||[], addDietFood, updateDietFood, deleteDietFood,
    dietMealLogs: dietMealLogs||[], addDietMealLog, deleteDietMealLog,
    dietWaterLogs: dietWaterLogs||[], addDietWaterLog, deleteDietWaterLog,
    dietSupplements: dietSupplements||[], addDietSupplement, updateDietSupplement, deleteDietSupplement,
    dietSupplementLogs: dietSupplementLogs||[], toggleDietSupplementLog,
    dietTargets: dietTargets||null, updateDietTargets,
    dietMealPlan: dietMealPlan||[], addDietMealPlanItem, deleteDietMealPlanItem,
    workoutExercises: workoutExercises||[], addWorkoutExercise, updateWorkoutExercise, deleteWorkoutExercise,
    workoutPlans: workoutPlans||[], addWorkoutPlan, updateWorkoutPlan, deleteWorkoutPlan,
    workoutPlanExercises: workoutPlanExercises||[], addWorkoutPlanExercise, deleteWorkoutPlanExercise,
    workoutSchedule: workoutSchedule||[], addWorkoutScheduleItem, deleteWorkoutScheduleItem,
    workoutLogs: workoutLogs||[], addWorkoutLog, updateWorkoutLog, deleteWorkoutLog,
    workoutLogExercises: workoutLogExercises||[], addWorkoutLogExercise, deleteWorkoutLogExercise,
    workoutLogSets: workoutLogSets||[], addWorkoutLogSet, updateWorkoutLogSet, deleteWorkoutLogSet,
    bodyProfile: bodyProfile||null, updateBodyProfile,
    bodyCompositionLogs: bodyCompositionLogs||[], addBodyCompositionLog, deleteBodyCompositionLog,
    healthGoals: healthGoals||[], addHealthGoal, updateHealthGoal, deleteHealthGoal,
    // Deep Work
    deepWorkSessions: deepWorkSessions||[], addDeepWorkSession, deleteDeepWorkSession,
    // Calendar Events (week time-blocking)
    calendarEvents: calendarEvents||[], addCalendarEvent, deleteCalendarEvent,
    // Focus linking — "Focus on this" from a task card
    focusTask, setFocusTask,
  };

  return (
    <div className="diary-section" style={{ minHeight:'100%', paddingBottom:32 }}>
      {view==="dashboard" && <Dashboard ctx={ctx} />}
      {view==="goals"     && <GoalsSection ctx={ctx} />}
      {view==="visions"   && <VisionsSection ctx={ctx} />}
      {view==="habits"    && <HabitsSection ctx={ctx} />}
      {view==="weekly"    && <WeeklyPlanner ctx={ctx} />}
      {view==="monthly"   && <MonthlyPlanner ctx={ctx} />}  {/* Calendar */}
      {view==="finance"   && <FinanceTracker ctx={ctx} />}
      {view==="books"     && <BookTracker ctx={ctx} />}
      {view==="subs"      && <SubTracker ctx={ctx} />}
      {view==="ideas"     && <IdeasBoard ctx={ctx} />}
      {view==="content"   && (canAccess('content')  ? <ContentStrategy   ctx={ctx}/> : <UpgradeGate mod="content"   plan={planId} onUpgrade={()=>setView('pricing')}/>)}
      {view==="vault"     && <LinkVault ctx={ctx} />}
      {view==="projects"  && <ProjectsSection ctx={ctx} />}
      {view==="business"  && (canAccess('business') ? <BusinessModule    ctx={ctx}/> : <UpgradeGate mod="business"  plan={planId} onUpgrade={()=>setView('pricing')}/>)}
      {view==="payments"  && (canAccess('payments') ? <PaymentAutomation ctx={ctx}/> : <UpgradeGate mod="payments"  plan={planId} onUpgrade={()=>setView('pricing')}/>)}
      {view==="meetings"  && (canAccess('meetings') ? <MeetingScheduler  ctx={ctx}/> : <UpgradeGate mod="meetings"  plan={planId} onUpgrade={()=>setView('pricing')}/>)}
      {view==="pricing"   && <PricingPage ctx={ctx} userPlan={userPlan} upgradePlan={upgradePlan}/>}
      {view==="wiki"        && <KnowledgeSection ctx={ctx} />}
      {view==="integrations" && <IntegrationsSection ctx={ctx} />}
      {view==="reflections" && <JournalSection ctx={ctx} />}
      {view==="fitness"     && <FitnessSection ctx={ctx} />}
      {view==="deepwork"    && <DeepWorkSection ctx={ctx} />}
      {view==="analytics"   && <AnalyticsSection ctx={ctx} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TASK EDIT MODAL
// ══════════════════════════════════════════════════════════════════════════════
function TaskEditModal({ task, onSave, onClose }) {
  const [text, setText]     = useState(task.text||"");
  const [status, setStatus] = useState(task.status||"new");
  const [priority, setPri]  = useState(task.priority||"normal");
  const [taskTime, setTime] = useState(task.task_time||"");
  const [notes, setNotes]   = useState(task.notes||"");
  return (
    <div style={{position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"var(--ta-surface-lowest)",borderRadius:"var(--r-lg)",padding:24,width:"100%",maxWidth:480,boxShadow:"rgba(26,28,28,0.18) 0 30px 60px -20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 className="ta-serif" style={{margin:0,color:"var(--ta-on-surface)",fontSize:20,fontWeight:400}}>Edit task</h3>
          <button onClick={onClose} className="ta-btn-icon" style={{border:"none",cursor:"pointer"}}>✕</button>
        </div>
        <div style={{marginBottom:10}}><label style={lbl}>Task Text</label><input value={text} onChange={e=>setText(e.target.value)} style={{...inp(),width:"100%"}} autoFocus/></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><label style={lbl}>Status</label>
            <select value={status} onChange={e=>setStatus(e.target.value)} style={{...inp(),width:"100%"}}>
              <option value="new">🆕 New</option><option value="working">⚡ Working</option>
              <option value="completed">✅ Done</option><option value="assigned">👤 Assigned</option>
              <option value="rescheduled">🔁 Rescheduled</option>
            </select>
          </div>
          <div><label style={lbl}>Priority</label>
            <select value={priority} onChange={e=>setPri(e.target.value)} style={{...inp(),width:"100%"}}>
              <option value="critical">🔴 Critical</option><option value="high">🟠 High</option>
              <option value="medium">🟡 Medium</option><option value="normal">🟢 Normal</option>
            </select>
          </div>
        </div>
        <div style={{marginBottom:10}}><label style={lbl}>⏰ Task Time (optional)</label><input type="time" value={taskTime} onChange={e=>setTime(e.target.value)} style={{...inp(),width:"100%"}}/></div>
        <div style={{marginBottom:16}}><label style={lbl}>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any notes for this task..." style={{...ta(70),width:"100%"}}/></div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} className="ta-btn ta-btn-ghost">Cancel</button>
          <button onClick={()=>{if(!text.trim())return;onSave({text,status,priority,task_time:taskTime||null,notes,done:status==="completed"});}} className="ta-btn ta-btn-primary">Save</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BIRTHDAY / ANNIVERSARY BANNER — auto-rotating corner widget
// ══════════════════════════════════════════════════════════════════════════════
function BirthdayBanner({ specialDates }) {
  const today = new Date();
  const mm = today.getMonth()+1, dd = today.getDate();
  const todays = (specialDates||[]).filter(d => d.month===mm && d.day===dd);
  const [idx, setIdx] = useState(0);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (todays.length <= 1) return;
    const t = setInterval(()=>setIdx(i=>(i+1)%todays.length), 3500);
    return ()=>clearInterval(t);
  }, [todays.length]);
  if (!todays.length || closed) return null;
  const cur = todays[idx % todays.length];
  const isBday = (cur.type||"birthday")==="birthday";
  return (
    <div style={{
      position:"fixed", bottom:24, right:24, zIndex:4000,
      background:isBday?"linear-gradient(135deg,#FF6B9D,#C8375A)":"linear-gradient(135deg,#764BA2,#4A1680)",
      color:"#fff", borderRadius:16, padding:"14px 20px", maxWidth:260,
      boxShadow:"0 8px 32px rgba(0,0,0,.25)", animation:"bdaySlideIn .5s ease",
      fontFamily:FONT,
    }}>
      <button onClick={()=>setClosed(true)} style={{
        position:"absolute",top:8,right:10,background:"rgba(255,255,255,.25)",
        border:"none",color:"#fff",borderRadius:"50%",width:22,height:22,
        cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",
        lineHeight:1,padding:0,fontFamily:FONT,
      }}>✕</button>
      <div onClick={()=>setIdx(i=>(i+1)%Math.max(1,todays.length))} style={{cursor:"pointer"}} title="Click to see next">
        <div style={{fontSize:28,marginBottom:4}}>{isBday?"🎂":"💍"}</div>
        <div style={{fontWeight:700,fontSize:14,marginBottom:2}}>{isBday?"Happy Birthday!":"Happy Anniversary!"}</div>
        <div style={{fontSize:13,opacity:.9}}>{cur.name}</div>
        {cur.year && <div style={{fontSize:10,opacity:.65,marginTop:2}}>Since {cur.year} · {today.getFullYear()-cur.year} years</div>}
        {todays.length>1 && <div style={{fontSize:10,opacity:.55,marginTop:4}}>{idx+1}/{todays.length} — tap for next</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════

// ── BookQuoteWidget — rotating book quotes/notes for dashboard ────────────────
function BookQuoteWidget({ books }) {
  const allQuotes = [];
  (books||[]).forEach(b=>{
    (b.entries||[]).forEach(e=>{
      allQuotes.push({text:e.text, bookTitle:b.title||"Unknown", bookAuthor:b.author||""});
    });
  });
  const FALLBACK = [{text:"Learning never exhausts the mind. Go read a book.", bookTitle:"Leonardo da Vinci", bookAuthor:""}];
  const pool = allQuotes.length > 0 ? allQuotes : FALLBACK;
  const [idx, setIdx] = useState(() => Math.floor(Math.random()*pool.length));
  const q = pool[idx % pool.length];
  return (
    <div style={{
      display:"flex", alignItems:"flex-start", gap:14,
      background:"linear-gradient(135deg,#2D1B69 0%,#1e1047 100%)",
      borderRadius:14, padding:"14px 18px", marginBottom:14,
      boxShadow:"0 2px 12px rgba(99,102,241,.18)",
      border:"1px solid rgba(139,92,246,.25)",
      position:"relative", overflow:"hidden",
    }}>
      {/* decorative quote mark */}
      <div style={{
        position:"absolute", top:-8, left:12,
        fontSize:72, color:"rgba(139,92,246,.15)",
        fontFamily:"Georgia,serif", lineHeight:1, pointerEvents:"none", userSelect:"none"
      }}>"</div>
      <div style={{flex:1, position:"relative", zIndex:1}}>
        <p style={{
          margin:"0 0 8px", fontSize:13, lineHeight:1.65,
          color:"rgba(255,255,255,.88)", fontStyle:"italic",
          fontFamily:"'Playfair Display','Georgia',serif",
        }}>{q.text}</p>
        <div style={{fontSize:11, color:"rgba(255,255,255,.45)", fontFamily:FONT, fontWeight:600}}>
          — {q.bookTitle}{q.bookAuthor ? `, ${q.bookAuthor}` : ""}
        </div>
      </div>
      <button
        onClick={()=>setIdx(i=>(i+1)%pool.length)}
        title="Next quote"
        style={{
          flexShrink:0, background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.12)",
          borderRadius:8, color:"rgba(255,255,255,.6)", cursor:"pointer",
          padding:"5px 8px", fontSize:14, transition:"all .15s",
          alignSelf:"center",
        }}
        onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,.16)";}}
        onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,.08)";}}
      >→</button>
    </div>
  );
}

function Dashboard({ ctx }) {
  const C = useC();
  const { selDate, setSelDate, dailyCi, checkinDefs, tasks,
    goals, setGoals, habits, habitLog,
    expenses, income, fixedIncome, visions, visionBoardBanner, setVisionBoardBanner,
    dashboardVisions, getDailyEntry, updateDailyEntry, loadDailyEntry, books,
    specialDates, fellows=[], feedPosts=[], uid,
    meetings=[], dietMealLogs=[], dietTargets } = ctx;

  const [newTask, setNewTask] = useState("");
  const [remDate, setRemDate] = useState("");
  const [remTime, setRemTime] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [showDates, setShowDates] = useState(false);
  const [showIdeaForm, setShowIdeaForm] = useState(false);
  const [newIdea, setNewIdea] = useState({ name:"", desc:"" });
  const [weekOffset, setWeekOffset] = useState(0);
  const [newTaskStatus, setNewTaskStatus] = useState("new");
  const [newTaskPriority, setNewTaskPriority] = useState("normal");
  const [showFin, setShowFin] = useState(() => { try { return localStorage.getItem('diary_show_fin') !== '0'; } catch { return true; } });
  const toggleFin = () => { const next = !showFin; setShowFin(next); try { localStorage.setItem('diary_show_fin', next ? '1' : '0'); } catch {} };
  const [pomoRunning, setPomoRunning] = useState(false);
  const [visionExpanded, setVisionExpanded] = useState(false);
  const [dashSlide, setDashSlide] = useState(0); // 0 = vision banner, 1 = life summary
  const visionEditingRef = useRef(false);
  const dashHoverRef = useRef(false);
  useEffect(()=>{ const id = setInterval(()=>setDashSlide(s=> (visionEditingRef.current || dashHoverRef.current) ? s : (s+1)%2), 5000); return ()=>clearInterval(id); },[]);
  useEffect(()=>{ if(loadDailyEntry) loadDailyEntry(selDate); if(ctx.loadDayCi) ctx.loadDayCi(selDate); if(ctx.loadHabitLogForDate) ctx.loadHabitLogForDate(selDate); },[selDate]); // eslint-disable-line

  const _tnow = new Date();
  const _dow0 = _tnow.getDay();
  const _monOff = _dow0 === 0 ? -6 : 1 - _dow0;
  const _mon = new Date(_tnow); _mon.setDate(_tnow.getDate() + _monOff + weekOffset * 7);
  const weekDays = Array.from({ length:7 }, (_,i) => { const d=new Date(_mon); d.setDate(_mon.getDate()+i); return fmt(d); });
  const dcCi = dailyCi[selDate] || {};
  const dateTasks = tasks[selDate] || [];

  const monthKey = selDate.slice(0,7);
  const monthExp = expenses.filter(e=>(e.expense_date||e.date||"").startsWith(monthKey)).reduce((s,e)=>s+e.amount,0);
  const monthInc = income.filter(i=>(i.income_date||i.date||"").startsWith(monthKey)).reduce((s,i)=>s+i.amount,0) + Number(fixedIncome||0);
  const remaining = monthInc - monthExp;

  const toggleCi   = id => ctx.toggleCi(selDate, id);
  const addTask    = () => {
    if(!newTask.trim())return;
    const taskDate = remDate || selDate;
    const reminderDt = remDate && remTime ? `${remDate}T${remTime}` : undefined;
    ctx.addTask(taskDate, newTask, reminderDt, {status:newTaskStatus,priority:newTaskPriority,task_time:remTime||undefined});
    setNewTask(""); setRemDate(""); setRemTime("");
  };
  const delTask    = id => ctx.deleteTask(selDate, id);
  const toggleHabit= hid => {
    if (selDate > TODAY) { ctx.setAlert?.('⚠️ Cannot mark habits for future dates.'); return; }
    if (selDate < TODAY) { ctx.setAlert?.('🔒 Habits can only be marked for today to keep streaks genuine.'); return; }
    ctx.toggleHabit(selDate, hid);
  };

  const starredTasks = dateTasks.filter(t=>t.priority&&!t.done);
  const topGoals = [...goals].filter(g=>!g.archived).sort((a,b)=>(a.priority||99)-(b.priority||99)).slice(0,3);

  // ── Life-summary slide data (today, regardless of which date is selected above) ──
  const todayTasks = tasks[TODAY] || [];
  const todayTasksDone = todayTasks.filter(t=>t.done).length;
  const meetingsToday = meetings.filter(m=>m.status==='confirmed' && m.start_time && fmt(new Date(m.start_time))===TODAY).length;
  const readingBooks = books.filter(b=>!b.archived && b.status==='reading').slice(0,2);
  const mealsToday = dietMealLogs.filter(l=>l.log_date===TODAY).length;
  const mealTargetCount = 4;
  const caloriesToday = Math.round(dietMealLogs.filter(l=>l.log_date===TODAY).reduce((s,l)=>s+Number(l.calories||0),0));
  const goalsAvgPct = topGoals.length ? Math.round(topGoals.reduce((s,g)=>{
    const totalDays = g.startDate&&g.endDate ? daysBetween(g.startDate,g.endDate) : 100;
    const elapsed = g.startDate ? Math.min(totalDays,daysBetween(g.startDate,TODAY)) : 0;
    return s + (totalDays>0 ? elapsed/totalDays*100 : 0);
  },0) / topGoals.length) : 0;
  const pinnedVisionsList = visions.filter(v=>(dashboardVisions||[]).includes(v.id));
  const visionsForSummary = pinnedVisionsList.length ? pinnedVisionsList : visions;
  const visionsAvgPct = visionsForSummary.length ? Math.round(visionsForSummary.reduce((s,v)=>{
    const ts=(v.steps||[]).length, ds=(v.steps||[]).filter(x=>x.done).length;
    return s + (ts>0 ? ds/ts*100 : 0);
  },0) / visionsForSummary.length) : 0;
  // Stitch-style layout: date in topbar, 2-col grid from top
  const todayLabel = new Date(selDate+"T00:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"});
  const acceptedFellows = fellows.filter(f=>f.status==='accepted');
  const pendingFellows  = fellows.filter(f=>f.status==='pending'&&f.addressee_id===uid);

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      {ReactDOM.createPortal(<BirthdayBanner specialDates={specialDates} />, document.body)}
      {showDates && <SpecialDatesModal specialDates={specialDates} addSpecialDate={ctx.addSpecialDate} deleteSpecialDate={ctx.deleteSpecialDate} onClose={()=>setShowDates(false)}/>}
      {editingTask && <TaskEditModal task={editingTask} onSave={(upd)=>{ ctx.updateTask(selDate,editingTask.id,upd); setEditingTask(null); }} onClose={()=>setEditingTask(null)} />}

      {/* ── EDITORIAL MASTHEAD — Tactile Archive header treatment ── */}
      <div className="ta-row ta-between" style={{alignItems:"flex-end",marginBottom:24,flexWrap:"wrap",gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">{todayLabel}</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>
            {(() => { const h=new Date().getHours(); return h<12?"Good morning":h<17?"Good afternoon":"Good evening"; })()}
            {ctx.profile?.full_name ? <>, <em>{ctx.profile.full_name.split(" ")[0]}</em></> : "."}
          </h1>
        </div>
        <div className="ta-row ta-gap-2" style={{flexWrap:"wrap"}}>
          {acceptedFellows.length>0&&(
            <button onClick={()=>ctx.setView?.('feed')} className="ta-chip" style={{cursor:"pointer",border:"none"}}>
              {acceptedFellows.length} Fellow{acceptedFellows.length!==1?"s":""}
            </button>
          )}
          {pendingFellows.length>0&&(
            <button onClick={()=>ctx.setView?.('feed')} className="ta-chip ta-chip-tertiary" style={{cursor:"pointer",border:"none"}}>
              {pendingFellows.length} pending
            </button>
          )}
          <button onClick={()=>setShowDates(true)} className="ta-btn ta-btn-secondary">
            Special Dates
          </button>
        </div>
      </div>

      {/* ── FOCUS SESSION (square, corner) + VISION EXPAND — never blurred ── */}
      <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,marginBottom:20,alignItems:"stretch"}}>
        <div style={{maxWidth:280}}>
          <PomodoroStable starredTasks={starredTasks} focusTask={ctx.focusTask} onRunningChange={setPomoRunning}/>
        </div>
        <div className="ta-card-inset" style={{display:"flex",flexDirection:"column"}}
          onMouseEnter={()=>{dashHoverRef.current=true;}} onMouseLeave={()=>{dashHoverRef.current=false;}}>
          <div className="ta-row ta-between" style={{marginBottom:8}}>
            <span className="ta-section-head-meta">{dashSlide===0 ? "Vision board" : "Today, at a glance"}</span>
            <div className="ta-row ta-gap-1">
              <button onClick={()=>setDashSlide(0)} style={{width:5,height:5,padding:0,borderRadius:"50%",border:"none",cursor:"pointer",background: dashSlide===0?"var(--ta-primary)":"var(--ta-outline-variant)"}} title="Vision board"/>
              <button onClick={()=>setDashSlide(1)} style={{width:5,height:5,padding:0,borderRadius:"50%",border:"none",cursor:"pointer",background: dashSlide===1?"var(--ta-primary)":"var(--ta-outline-variant)"}} title="Today, at a glance"/>
            </div>
          </div>
          <div style={{flex:1,position:"relative",minHeight:120}}>
            <div style={{position:"absolute",inset:0,opacity:dashSlide===0?1:0,transition:"opacity .5s ease",pointerEvents:dashSlide===0?"auto":"none"}}>
              <VisionBoardBanner banner={visionBoardBanner} setBanner={setVisionBoardBanner} uid={uid}
                onExpand={()=>setVisionExpanded(true)} onEditingChange={v=>{visionEditingRef.current=v;}}/>
            </div>
            <div style={{position:"absolute",inset:0,opacity:dashSlide===1?1:0,transition:"opacity .5s ease",pointerEvents:dashSlide===1?"auto":"none",display:"flex",flexDirection:"column",justifyContent:"center",gap:10}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(64px,1fr))",gap:10,justifyItems:"center"}}>
                {[
                  { label:'Tasks',    value:`${todayTasksDone}/${todayTasks.length||0}`, pct: todayTasks.length? todayTasksDone/todayTasks.length*100 : 0, color:'var(--ta-primary)' },
                  { label:'Meals',    value:`${mealsToday}/${mealTargetCount}`,          pct: Math.min(100, mealsToday/mealTargetCount*100),              color:'var(--ta-tertiary)' },
                  { label:'Calories', value:`${caloriesToday}`,                          pct: Math.min(100, caloriesToday/(dietTargets?.calorie_target||2000)*100), color:'var(--ta-positive)' },
                  { label:'Meetings', value:`${meetingsToday}`,                          pct: Math.min(100, meetingsToday/4*100),                          color:'var(--ta-notice)' },
                  { label:'Goals',    value:`${goalsAvgPct}%`,                           pct: goalsAvgPct,                                                 color:'#6B3FA0' },
                  { label:'Visions',  value:`${visionsAvgPct}%`,                         pct: visionsAvgPct,                                               color:'#C8860A' },
                ].map(t=>(
                  <div key={t.label} style={{textAlign:"center"}}>
                    <div style={{position:"relative",width:64,height:64}}>
                      <RingGauge pct={t.pct} color={t.color} size={64}/>
                      <div className="ta-mono tnum" style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"var(--ta-on-surface)"}}>{t.value}</div>
                    </div>
                    <div className="ta-mono" style={{fontSize:9,letterSpacing:".06em",textTransform:"uppercase",color:"var(--ta-on-surface-muted)",marginTop:5}}>{t.label}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:11,color:"var(--ta-on-surface-muted)",textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",padding:"0 8px"}}>
                📖 {readingBooks.length? readingBooks.map(b=>b.title).join(', ') : "No book in progress"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {visionExpanded && ReactDOM.createPortal(
        <div style={{position:"fixed",inset:0,zIndex:4000,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
          onClick={e=>e.target===e.currentTarget&&setVisionExpanded(false)}>
          <button onClick={()=>setVisionExpanded(false)} style={{position:"fixed",top:20,right:24,background:"rgba(255,255,255,.15)",border:"none",color:"#fff",borderRadius:"50%",width:38,height:38,cursor:"pointer",fontSize:18,lineHeight:1,zIndex:1}}>✕</button>
          {(visionBoardBanner?.bg_image_url || visionBoardBanner?.bgImage) ? (
            <img src={visionBoardBanner.bg_image_url || visionBoardBanner.bgImage} alt="Vision board full view"
              style={{maxWidth:"96vw",maxHeight:"94vh",width:"auto",height:"auto",objectFit:"contain",borderRadius:8,boxShadow:"0 20px 80px rgba(0,0,0,.5)"}}/>
          ) : (
            <div style={{width:"min(900px,92vw)",height:"min(420px,60vh)"}}>
              <VisionBoardBanner banner={visionBoardBanner} setBanner={setVisionBoardBanner} uid={uid} fill/>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── Everything below blurs while a focus session is running ── */}
      <div style={{filter:pomoRunning?"blur(6px)":"none",pointerEvents:pomoRunning?"none":"auto",transition:"filter .4s ease",userSelect:pomoRunning?"none":"auto"}}>

      {/* ── FINANCE STATS ROW ── */}
      <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:6}}>
        <button onClick={toggleFin} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:99,border:"1px solid var(--border)",background:"var(--surface)",cursor:"pointer",fontSize:11,color:C.muted}}>
          {showFin?"🙈 Hide figures":"👁 Show figures"}
        </button>
      </div>
      <div className="dash-stats" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
        {[
          {label:"Monthly Income",val:monthInc,color:C.green,icon:"📈"},
          {label:"Expenses This Month",val:monthExp,color:C.red,icon:"💳"},
          {label:"Remaining Balance",val:remaining,color:remaining>=0?C.teal:C.red,icon:"💰"},
        ].map(({label,val,color,icon})=>(
          <div key={label} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"var(--shadow-sm)",transition:"box-shadow var(--t)",cursor:"default"}}
            onMouseEnter={e=>e.currentTarget.style.boxShadow="var(--shadow-md)"}
            onMouseLeave={e=>e.currentTarget.style.boxShadow="var(--shadow-sm)"}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:C.light,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{label}</div>
              <div style={{fontSize:20,fontWeight:800,color,letterSpacing:"-.01em"}}>{showFin?`₹${Math.abs(val).toLocaleString()}`:"••••••"}</div>
            </div>
            <span style={{fontSize:22,opacity:.8}}>{icon}</span>
          </div>
        ))}
      </div>
      </div>

      {/* ── WEEK STRIP ── */}
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:4,display:"flex",gap:2,marginBottom:16,boxShadow:"var(--shadow-sm)"}}>
        <button onClick={()=>setWeekOffset(p=>p-1)} style={{padding:"8px 10px",background:"transparent",border:"none",cursor:"pointer",borderRadius:8,color:C.muted,fontSize:14,flexShrink:0}} onMouseEnter={e=>e.currentTarget.style.background="var(--surface-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>◀</button>
        {weekDays.map((wd,i)=>{
          const isSel=wd===selDate,isToday=wd===TODAY,ht=(tasks[wd]||[]).length>0;
          return (
            <button key={wd} onClick={()=>setSelDate(wd)} style={{flex:1,padding:"7px 4px",background:isSel?"var(--primary)":isToday?"rgba(43,29,201,.12)":"transparent",color:isSel?"#fff":isToday?"var(--primary)":C.muted,border:isToday&&!isSel?"2px solid var(--primary)":"2px solid transparent",borderRadius:8,cursor:"pointer",textAlign:"center",transition:"all .12s",position:"relative"}}>
              <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".04em",opacity:isSel?1:isToday?1:.6}}>{MON_NAMES[i]}</div>
              <div style={{fontSize:16,fontWeight:800,marginTop:1,color:isSel?"#fff":isToday?"var(--primary)":C.text}}>{new Date(wd+"T00:00:00").getDate()}</div>
              {isToday&&!isSel&&<div style={{fontSize:7,fontWeight:800,color:"var(--primary)",marginTop:1,letterSpacing:".05em"}}>TODAY</div>}
              {ht&&<div style={{width:4,height:4,borderRadius:"50%",background:isSel?"rgba(255,255,255,.8)":"var(--primary)",margin:"3px auto 0"}}/>}
            </button>
          );
        })}
        <button onClick={()=>setWeekOffset(p=>p+1)} style={{padding:"8px 10px",background:"transparent",border:"none",cursor:"pointer",borderRadius:8,color:C.muted,fontSize:14,flexShrink:0}} onMouseEnter={e=>e.currentTarget.style.background="var(--surface-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>▶</button>
      </div>
      {weekOffset!==0&&<div style={{textAlign:"center",marginBottom:10}}><button onClick={()=>{setWeekOffset(0);setSelDate(TODAY);}} style={{...btn("var(--primary)"),fontSize:11,padding:"3px 14px"}}>↩ Back to Today</button></div>}

      {/* ── MAIN 2-COL GRID: left sidebar + right content ── */}
      <div className="dash-main-row" style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:14,alignItems:"stretch"}}>

        {/* ── LEFT SIDEBAR — stretches to match the Kanban's height so there's no dangling gap ── */}
        <div style={{display:"flex",flexDirection:"column",gap:10,height:"100%"}}>

          {/* Daily Check-ins */}
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px 14px",boxShadow:"var(--shadow-sm)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <span style={{fontSize:11,fontWeight:700,color:C.blue,textTransform:"uppercase",letterSpacing:".06em"}}>☑ Check-ins</span>
              <button onClick={()=>ctx.setView("settings")} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"var(--primary)",fontWeight:600,padding:0}}>Edit</button>
            </div>
            {checkinDefs.length===0&&<p style={{fontSize:11,color:C.muted,margin:0}}>No check-ins yet.</p>}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {checkinDefs.map(ci=>(
                <label key={ci.id} onClick={()=>toggleCi(ci.id)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"6px 8px",borderRadius:8,background:dcCi[ci.id]?"var(--primary-light)":"transparent",transition:"background var(--t-fast)"}}>
                  <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${dcCi[ci.id]?"var(--primary)":C.border}`,background:dcCi[ci.id]?"var(--primary)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .12s"}}>
                    {dcCi[ci.id]&&<span style={{fontSize:10,color:"#fff",fontWeight:900}}>✓</span>}
                  </div>
                  <span style={{fontSize:12,color:dcCi[ci.id]?"var(--primary)":C.muted,fontWeight:dcCi[ci.id]?600:400,transition:"color .12s"}}>{ci.icon||""} {ci.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Priority of the Day */}
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px",boxShadow:"var(--shadow-sm)"}}>
            <div style={{fontSize:10,fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Priority of the Day</div>
            <textarea value={(getDailyEntry(selDate)||{}).priority||""} onChange={e=>updateDailyEntry(selDate,"priority",e.target.value)} placeholder="What's your main goal?" style={{...ta(72),background:"var(--bg2)",border:"1px solid transparent",color:C.text,resize:"none"}}/>
          </div>

          {/* Notes — flex:1 so it stretches to fill the rest of the column's height */}
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px",boxShadow:"var(--shadow-sm)",display:"flex",flexDirection:"column",flex:1,minHeight:90}}>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Notes</div>
            <textarea value={(getDailyEntry(selDate)||{}).notes||""} onChange={e=>updateDailyEntry(selDate,"notes",e.target.value)} placeholder="Quick thoughts..." style={{...ta(90),flex:1,background:"var(--bg2)",border:"1px solid transparent",color:C.text,resize:"none"}}/>
          </div>

        </div>

        {/* ── RIGHT CONTENT ── */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>

          {/* KANBAN — Today's Tasks */}
          <div style={{background:"var(--ta-surface-lowest)",borderRadius:"var(--r-lg)",padding:"16px",boxShadow:"0 1px 2px rgba(26,28,28,.04)"}}>
            {/* Add task bar */}
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12,flexWrap:"wrap"}}>
              <span className="ta-mono" style={{fontSize:10,fontWeight:600,color:"var(--ta-on-surface-muted)",textTransform:"uppercase",letterSpacing:".12em",flexShrink:0}}>✅ Today's Tasks</span>
              <input value={newTask} onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTask()} placeholder="Add task + Enter..." style={{...inp(),flex:1,minWidth:120,fontSize:12}}/>
              <select value={newTaskStatus||"new"} onChange={e=>setNewTaskStatus(e.target.value)} style={{...inp(),width:110,fontSize:11}}>
                <option value="new">🆕 New</option>
                <option value="working">⚡ Working</option>
                <option value="completed">✅ Done</option>
                <option value="assigned">👤 Assigned</option>
                <option value="rescheduled">🔁 Rescheduled</option>
              </select>
              <select value={newTaskPriority||"normal"} onChange={e=>setNewTaskPriority(e.target.value)} style={{...inp(),width:105,fontSize:11}}>
                <option value="critical">🔴 Critical</option>
                <option value="high">🟠 High</option>
                <option value="medium">🟡 Medium</option>
                <option value="normal">🟢 Normal</option>
              </select>
              <input type="date" value={remDate} onChange={e=>setRemDate(e.target.value)} style={{...inp(),width:115}} title="Reminder date"/>
              <input type="time" value={remTime} onChange={e=>setRemTime(e.target.value)} style={{...inp(),width:95}} title="Task time"/>
              <button onClick={addTask} style={{padding:"8px 14px",background:"var(--primary)",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontFamily:FONT,fontSize:16,fontWeight:700,flexShrink:0,lineHeight:1}}>+</button>
              {selDate!==TODAY&&<button onClick={()=>{setSelDate(TODAY);setTimeout(()=>{ if(!newTask.trim())return; ctx.addTask(TODAY,newTask,remDate&&remTime?`${remDate}T${remTime}`:undefined,{status:newTaskStatus,priority:newTaskPriority}); setNewTask("");setRemDate("");setRemTime(""); },50);}} style={{...btn(C.amber),fontSize:10,padding:"4px 7px",flexShrink:0}}>📌 Today</button>}
            </div>

            {/* Kanban columns — horizontal scroll on mobile */}
            <div className="kanban-scroll-wrap" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <div className="kanban-grid" style={{display:"grid",gridTemplateColumns:"repeat(5,minmax(150px,1fr))",gap:8,minWidth:600}}>
              {[
                ["new","New Task","var(--ta-on-surface-variant)","#E8F0E0","⚪"],
                ["working","Working","var(--ta-notice)","#FFF3CC","🟡"],
                ["assigned","Assigned","#6B3FA0","#F0E8FF","🔵"],
                ["rescheduled","Rescheduled","var(--ta-primary)","#E0F5FF","🟠"],
                ["completed","Completed","var(--ta-positive)","#E0F5F1","🟢"],
              ].map(([status,label,col,bg,dot])=>{
                const cols=dateTasks
                  .filter(t=>{const s=t.status||'new'; return (status==='new'?(s==='new'||s==='todo'):status==='assigned'?(s==='assigned'||s==='delegated'):s===status);})
                  .sort((a,b)=>(a.position??9999)-(b.position??9999));
                const handleDailyDrop = (e, targetId) => {
                  e.preventDefault(); e.currentTarget.style.outline="none";
                  const tid = e.dataTransfer.getData("taskId");
                  const fromStatus = e.dataTransfer.getData("taskStatus");
                  if (!tid) return;
                  if (fromStatus !== status) {
                    ctx.updateTask(selDate, tid, {status, done: status==="completed"});
                  } else if (targetId && targetId !== tid) {
                    const di = cols.findIndex(x=>x.id===tid);
                    const ti = cols.findIndex(x=>x.id===targetId);
                    if (di===-1||ti===-1) return;
                    const reordered = [...cols];
                    const [moved] = reordered.splice(di,1);
                    reordered.splice(ti,0,moved);
                    reordered.forEach((t,i) => ctx.updateTask(selDate, t.id, {position:i}));
                  }
                };
                return (
                  <div key={status}
                    style={{background:"var(--ta-surface-low)",borderRadius:"var(--r-md)",padding:"10px 8px",minHeight:100,transition:"outline .1s"}}
                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.outline=`2px dashed ${col}`;}}
                    onDragLeave={e=>{e.currentTarget.style.outline="none";}}
                    onDrop={e=>handleDailyDrop(e,null)}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:5}}>
                        <span style={{width:7,height:7,borderRadius:"50%",background:col,display:"inline-block"}}/>
                        <span className="ta-mono" style={{fontSize:10,fontWeight:600,color:"var(--ta-on-surface-muted)",textTransform:"uppercase",letterSpacing:".08em"}}>{label}</span>
                      </div>
                      <span className="ta-mono" style={{fontSize:10,fontWeight:600,background:"var(--ta-surface-high)",color:col,borderRadius:"var(--r-sm)",padding:"1px 6px"}}>{cols.length}</span>
                    </div>
                    {/* Fixed to ~5 card-slots tall (not maxHeight) so the column never
                        grows/shrinks with task count — always shows room for 5, scrolls past that. */}
                    <div style={{display:"flex",flexDirection:"column",gap:5,height:320,overflowY:"auto"}}>
                      {cols.map((t,idx)=>(
                        <div key={t.id}
                          draggable
                          onDragStart={e=>{e.dataTransfer.setData("taskId",t.id);e.dataTransfer.setData("taskStatus",status);e.currentTarget.style.opacity="0.5";}}
                          onDragEnd={e=>{e.currentTarget.style.opacity="1";}}
                          onDragOver={e=>{e.preventDefault();e.stopPropagation();e.currentTarget.style.borderTop=`2px solid ${col}`;}}
                          onDragLeave={e=>{e.currentTarget.style.borderTop="";}}
                          onDrop={e=>{e.currentTarget.style.borderTop="";handleDailyDrop(e,t.id);}}
                          style={{background:"var(--ta-surface-lowest)",borderLeft:`3px solid ${t.priority==="critical"?"var(--ta-negative)":t.priority==="high"?"var(--ta-negative)":t.priority==="medium"?"var(--ta-primary)":col}`,borderRadius:"var(--r-md)",padding:"8px 9px",fontSize:12,boxShadow:"0 1px 2px rgba(26,28,28,.05)",cursor:"grab",transition:"box-shadow .12s"}}
                          onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 10px rgba(26,28,28,.08)"}
                          onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 2px rgba(26,28,28,.05)"}>
                          <div style={{display:"flex",alignItems:"flex-start",gap:5,marginBottom:3}}>
                            <input type="checkbox" checked={t.done||status==="completed"} onChange={()=>ctx.updateTask(selDate,t.id,{done:!t.done,status:t.done?"new":"completed"})} style={{accentColor:"var(--ta-primary)",flexShrink:0,marginTop:1}}/>
                            <span style={{flex:1,fontSize:13,fontWeight:500,textDecoration:(t.done||status==="completed")?"line-through":"none",color:(t.done||status==="completed")?"var(--ta-on-surface-muted)":"var(--ta-on-surface)",wordBreak:"break-word",lineHeight:1.4}}>{t.text}</span>
                          </div>
                          {t.task_time&&<div className="ta-mono" style={{fontSize:9,color:"var(--ta-on-surface-muted)",marginTop:2,letterSpacing:".04em"}}>⏰ {t.task_time}</div>}
                          <div style={{display:"flex",gap:3,marginTop:5,flexWrap:"wrap",alignItems:"center"}}>
                            <select value={t.status||"new"} onChange={e=>ctx.updateTask(selDate,t.id,{status:e.target.value,done:e.target.value==="completed"})} style={{fontSize:9,padding:"1px 3px",border:`1px solid var(--border)`,borderRadius:4,background:"var(--surface)",color:C.muted,cursor:"pointer"}}>
                              <option value="new">🆕 New</option>
                              <option value="working">⚡ Working</option>
                              <option value="completed">✅ Done</option>
                              <option value="assigned">👤 Assigned</option>
                              <option value="rescheduled">🔁 Rescheduled</option>
                            </select>
                            <select value={t.priority||"normal"} onChange={e=>ctx.updateTask(selDate,t.id,{priority:e.target.value})} style={{fontSize:9,padding:"1px 3px",border:`1px solid var(--border)`,borderRadius:4,background:"var(--surface)",color:C.muted,cursor:"pointer"}}>
                              <option value="critical">🔴</option>
                              <option value="high">🟠</option>
                              <option value="medium">🟡</option>
                              <option value="normal">🟢</option>
                            </select>
                            <button onClick={()=>{ctx.setFocusTask&&ctx.setFocusTask({id:t.id,text:t.text});ctx.setView&&ctx.setView('deepwork');}} title="Focus on this task" style={{background:"none",border:"none",cursor:"pointer",color:"var(--ta-primary)",fontSize:10,padding:0}}>🎯</button>
                            <button onClick={()=>setEditingTask(t)} style={{background:"none",border:"none",cursor:"pointer",color:C.amber,fontSize:10,padding:0}}>✏️</button>
                            <button onClick={()=>delTask(t.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text3)",fontSize:10,padding:0,marginLeft:"auto"}}>✕</button>
                          </div>
                          {(t.status==="assigned"||t.status==="delegated")&&(
                            <input value={t.assignee||""} onChange={e=>ctx.updateTask(selDate,t.id,{assignee:e.target.value})} placeholder="Assign to..." style={{...inp(),width:"100%",fontSize:9,padding:"2px 5px",marginTop:3}}/>
                          )}
                          {t.status==="rescheduled"&&(
                            <input type="date" value={t.rescheduleDate||""} onChange={e=>{const nd=e.target.value;ctx.updateTask(selDate,t.id,{rescheduleDate:nd});if(nd)ctx.addTask(nd,t.text,undefined,{status:"new",priority:t.priority||"normal"});}} style={{...inp(),fontSize:9,padding:"2px 4px",width:"100%",marginTop:3}}/>
                          )}
                        </div>
                      ))}
                      {cols.length===0&&<div style={{fontSize:10,color:"var(--text3)",textAlign:"center",padding:"12px 0",border:"2px dashed var(--border)",borderRadius:8,fontStyle:"italic"}}>No tasks</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>{/* end kanban scroll wrapper */}
          </div>

          {/* BOTTOM ROW: Top Goals + Grateful + Learning + Idea + Habits — all with generous, non-scrolling dedicated space */}
          <div className="dash-bottom-row" style={{display:"grid",gridTemplateColumns:"1.2fr 1fr 1fr 1fr 1fr",gap:10}}>
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px",boxShadow:"var(--shadow-sm)",minHeight:240,display:"flex",flexDirection:"column"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <span style={{fontSize:10,fontWeight:700,color:C.light,textTransform:"uppercase",letterSpacing:".06em"}}>Top Goals</span>
                <button onClick={()=>ctx.setView('goals')} style={{fontSize:10,color:"var(--primary)",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>View all →</button>
              </div>
              {topGoals.length===0
                ?<p style={{fontSize:12,color:C.light,margin:0}}>No goals yet.</p>
                :<div style={{display:"flex",flexDirection:"column",gap:14,flex:1,justifyContent:"space-evenly"}}>
                  {topGoals.map(g=>{
                    const totalDays=g.startDate&&g.endDate?daysBetween(g.startDate,g.endDate):100;
                    const elapsed=g.startDate?Math.min(totalDays,daysBetween(g.startDate,TODAY)):0;
                    const pct=totalDays>0?Math.round(elapsed/totalDays*100):0;
                    const checkedToday=(g.checkinHistory||{})[TODAY];
                    return(
                      <div key={g.id}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                          <span style={{fontSize:12,fontWeight:600,color:C.text,flex:1,marginRight:6}}>{g.title}</span>
                          <button
                            onClick={()=>setGoals(prev=>prev.map(gg=>gg.id!==g.id?gg:{...gg,checkinHistory:{...(gg.checkinHistory||{}),[TODAY]:!checkedToday}}))}
                            style={{padding:"2px 8px",borderRadius:6,border:`1px solid ${checkedToday?C.green:C.border}`,background:checkedToday?C.greenM:"transparent",color:checkedToday?C.green:C.muted,fontSize:9,fontWeight:700,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}
                          >{checkedToday?"✓ Done":"Check-in"}</button>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{flex:1,background:"var(--bg3)",borderRadius:99,height:5,overflow:"hidden"}}>
                            <div style={{width:`${pct}%`,height:"100%",background:"var(--primary)",borderRadius:99,transition:"width .6s"}}/>
                          </div>
                          <span style={{fontSize:10,fontWeight:700,color:C.muted,flexShrink:0}}>{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            </div>
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px",boxShadow:"var(--shadow-sm)",display:"flex",flexDirection:"column",minHeight:240}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><span style={{color:"var(--primary)",fontSize:14}}>❤</span><span style={{fontSize:10,fontWeight:700,color:"var(--primary)",textTransform:"uppercase",letterSpacing:".06em"}}>Grateful For</span></div>
              <textarea value={(getDailyEntry(selDate)||{}).grateful||""} onChange={e=>updateDailyEntry(selDate,"grateful",e.target.value)} placeholder="Enter three things..." style={{...ta(0),flex:1,fontSize:12,background:"transparent",border:"none",resize:"none",padding:0,color:C.text}}/>
            </div>
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px",boxShadow:"var(--shadow-sm)",display:"flex",flexDirection:"column",minHeight:240}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><span style={{fontSize:14}}>📘</span><span style={{fontSize:10,fontWeight:700,color:C.blue,textTransform:"uppercase",letterSpacing:".06em"}}>Learning of the Day</span></div>
              <textarea value={(getDailyEntry(selDate)||{}).learning||""} onChange={e=>updateDailyEntry(selDate,"learning",e.target.value)} placeholder="What did you learn today?" style={{...ta(0),flex:1,fontSize:12,background:"transparent",border:"none",resize:"none",padding:0,color:C.text}}/>
            </div>
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px",boxShadow:"var(--shadow-sm)",display:"flex",flexDirection:"column",minHeight:240}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><span style={{fontSize:14}}>💡</span><span style={{fontSize:10,fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:".06em"}}>Idea Block</span></div>
              {!showIdeaForm
                ?<button onClick={()=>setShowIdeaForm(true)} style={{width:"100%",padding:8,background:"var(--amber-bg)",border:`1.5px dashed ${C.amber}`,borderRadius:8,cursor:"pointer",color:C.amber,fontFamily:FONT,fontSize:12,marginTop:"auto"}}>+ Capture Idea</button>
                :<div style={{flex:1,display:"flex",flexDirection:"column",gap:5}}>
                  <input placeholder="Idea name" value={newIdea.name} onChange={e=>setNewIdea(p=>({...p,name:e.target.value}))} style={{...inp(),width:"100%",fontSize:12}}/>
                  <textarea placeholder="Describe..." value={newIdea.desc} onChange={e=>setNewIdea(p=>({...p,desc:e.target.value}))} style={{...ta(55),fontSize:12}}/>
                  <div style={{display:"flex",gap:5}}><button onClick={()=>{if(!newIdea.name.trim())return;ctx.addIdea({...newIdea,date:selDate,tags:[]});setNewIdea({name:"",desc:""});setShowIdeaForm(false);}} style={btn(C.amber)}>Save</button><button onClick={()=>setShowIdeaForm(false)} style={btn("#999")}>✕</button></div>
                </div>
              }
            </div>
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px",boxShadow:"var(--shadow-sm)",minHeight:240}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}><span style={{fontSize:14}}>🔥</span><span style={{fontSize:10,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:".06em"}}>Habit Tracker</span></div>
              {habits.filter(h=>!h.archived).length===0
                ?<p style={{fontSize:11,color:C.light,margin:0}}>Add habits in Weekly → Habits.</p>
                :<div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {habits.filter(h=>!h.archived).map(h=>{
                    const k=`${selDate}:${h.id}`,done=habitLog[k];
                    const streak=(()=>{let s=0,d=new Date(selDate+"T00:00:00");while(habitLog[`${fmt(d)}:${h.id}`]){s++;d.setDate(d.getDate()-1);}return s;})();
                    return(
                      <div key={h.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",flex:1}} onClick={()=>toggleHabit(h.id)}>
                          <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${done?"var(--primary)":C.border}`,background:done?"var(--primary)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .12s"}}>
                            {done&&<span style={{fontSize:9,color:"#fff",fontWeight:900}}>✓</span>}
                          </div>
                          <span style={{fontSize:12,color:done?"var(--text)":C.muted,fontWeight:done?600:400}}>{h.icon||"🔷"} {h.name}</span>
                        </label>
                        {streak>0&&<ShareButton type="habit" data={{habitName:h.name,streak}} style={{padding:"2px 5px",fontSize:9,borderRadius:8}}/>}
                      </div>
                    );
                  })}
                </div>
              }
            </div>
          </div>

          {/* Pinned visions */}
          <PinnedVisions visions={visions} dashboardVisions={dashboardVisions} />
        </div>
      </div>
      </div>{/* end blur wrapper */}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// VISION BOARD BANNER — hero banner on dashboard
// ══════════════════════════════════════════════════════════════════════════════
function VisionBoardBanner({ banner, setBanner, fill, onExpand, onEditingChange, uid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(banner);
  const [imgUploading, setImgUploading] = useState(false);
  const imgRef = useRef(null);

  useEffect(()=>{ onEditingChange?.(editing); }, [editing]); // eslint-disable-line

  const b = banner || { headline:"", subtext:"", bgImage:"" };
  const hasContent = b.headline || b.bg_image_url || b.bgImage;

  // Upload to Storage and store the public URL — storing the raw base64 data
  // directly in the DB (the old approach) silently failed to save for any
  // real photo, since the request body blew past the API's payload limit.
  const handleImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImgUploading(true);
    try {
      const ext = file.name.split('.').pop().toLowerCase() || 'jpg';
      const path = `${uid || 'user'}/vision-board/${Date.now()}.${ext}`;
      const { data: uploadData, error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data: pubData } = supabase.storage.from('avatars').getPublicUrl(uploadData.path);
      if (pubData?.publicUrl) setDraft(p => ({ ...p, bgImage: pubData.publicUrl }));
    } catch(err) {
      console.error('vision banner image upload failed:', err.message);
      const reader = new FileReader();
      reader.onload = ev => setDraft(p => ({ ...p, bgImage: ev.target.result }));
      reader.readAsDataURL(file);
    }
    setImgUploading(false);
  };

  const save = () => { setBanner(draft); setEditing(false); };
  const clear = () => { const empty={headline:"",subtext:"",bgImage:""}; setBanner(empty); setDraft(empty); setEditing(false); };

  if (!hasContent && !editing) {
    return (
      <div style={{marginBottom:12,...(fill?{flex:1,display:"flex",flexDirection:"column"}:{})}}>
        <button onClick={()=>{setDraft(b);setEditing(true);}} style={{width:"100%",flex:fill?1:undefined,padding:"14px",background:`linear-gradient(135deg,${C.green}18,${C.purple}12)`,border:`2px dashed ${C.green}50`,borderRadius:12,cursor:"pointer",fontFamily:FONT,fontSize:13,color:C.green,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          🌟 Click to add your Vision Board Banner — your inspiring headline for every day
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{marginBottom:12,background:C.surface,border:`1.5px solid ${C.green}`,borderRadius:12,padding:16}}>
        <h4 style={{margin:"0 0 12px",color:C.green}}>🌟 Edit Vision Board Banner</h4>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lbl}>Headline / Main Vision Statement</label>
            <input value={draft.headline||""} onChange={e=>setDraft(p=>({...p,headline:e.target.value}))} placeholder="e.g. Building a life of impact, freedom and purpose" style={{...inp(),width:"100%",fontSize:15,fontWeight:"bold"}}/>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lbl}>Sub-text / Motivational quote</label>
            <input value={draft.subtext||""} onChange={e=>setDraft(p=>({...p,subtext:e.target.value}))} placeholder="e.g. Every day is a step toward the architect I am becoming" style={{...inp(),width:"100%"}}/>
          </div>
          <div>
            <label style={lbl}>Background Image (optional)</label>
            <button onClick={()=>imgRef.current?.click()} disabled={imgUploading} style={{...btn(C.purple),fontSize:12,padding:"6px 12px",opacity:imgUploading?0.6:1}}>{imgUploading?"Uploading…":"📷 Upload Image"}</button>
            <input ref={imgRef} type="file" accept="image/*" onChange={handleImage} style={{display:"none"}} disabled={imgUploading}/>
            {draft.bgImage && !imgUploading && <span style={{fontSize:10,color:C.green,marginLeft:8}}>✓ Image loaded</span>}
          </div>
          {draft.bgImage && (
            <div>
              <label style={lbl}>Preview</label>
              <img src={draft.bgImage} alt="preview" style={{height:60,borderRadius:6,border:`1px solid ${C.border}`,objectFit:"cover",maxWidth:"100%"}}/>
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={save} style={btn(C.green)}>💾 Save Banner</button>
          <button onClick={()=>setEditing(false)} style={btn("#999")}>Cancel</button>
          {hasContent && <button onClick={clear} style={btn(C.red)}>🗑 Clear</button>}
        </div>
      </div>
    );
  }

  // Display banner — when `fill` (dashboard expanded view), stretch to use
  // all the height the row already has (matches the Pomodoro card's height)
  // instead of stopping at a fixed 120px and leaving blank space below it.
  const bannerStyle = {
    borderRadius:12, overflow:"hidden", position:"relative",
    minHeight: fill?undefined:120, height: fill?"100%":undefined, flex: fill?1:undefined,
    display:"flex", alignItems:"center",
    background: (b.bg_image_url||b.bgImage)
      ? `linear-gradient(to right, rgba(0,0,0,.65) 0%, rgba(0,0,0,.35) 60%, rgba(0,0,0,.1) 100%)`
      : `linear-gradient(135deg, ${C.green} 0%, #1A4A0A 40%, ${C.teal} 100%)`,
    boxShadow:"0 4px 20px rgba(0,0,0,.15)",
  };

  return (
    <div onClick={onExpand} style={{marginBottom:fill?0:12,position:"relative",borderRadius:12,overflow:"hidden",minHeight:fill?undefined:120,boxShadow:"0 4px 20px rgba(0,0,0,.15)",cursor:onExpand?"pointer":"default",...(fill?{flex:1,display:"flex",flexDirection:"column"}:{})}}>
      {(b.bg_image_url||b.bgImage) && (
        <img src={b.bg_image_url||b.bgImage} alt="vision" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",zIndex:0}}/>
      )}
      <div style={{...bannerStyle,position:"relative",zIndex:1,padding: fill?"28px 32px":"20px 28px"}}>
        <div style={{flex:1}}>
          <div style={{fontSize:9,letterSpacing:3,textTransform:"uppercase",color:"rgba(255,255,255,.7)",marginBottom:6,fontFamily:FONT}}>🌟 My Vision</div>
          <h2 style={{margin:"0 0 6px",fontSize:fill?28:22,color:"#fff",fontFamily:FONT,textShadow:"0 2px 8px rgba(0,0,0,.4)",lineHeight:1.3}}>{b.headline || "Your Vision Headline"}</h2>
          {b.subtext && <p style={{margin:0,fontSize:fill?15:13,color:"rgba(255,255,255,.85)",fontStyle:"italic",textShadow:"0 1px 4px rgba(0,0,0,.3)"}}>{b.subtext}</p>}
        </div>
        <button onClick={e=>{e.stopPropagation();setDraft(b);setEditing(true);}} style={{...btn("rgba(255,255,255,.2)"),fontSize:11,padding:"5px 10px",border:"1px solid rgba(255,255,255,.3)",marginLeft:16,flexShrink:0}}>✏️ Edit</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PINNED VISIONS — selected visions shown on dashboard
// ══════════════════════════════════════════════════════════════════════════════
function PinnedVisions({ visions, dashboardVisions }) {
  const pinned = (visions || []).filter(v => (dashboardVisions || []).includes(v.id));
  if (!pinned.length) return null;

  return (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:"bold",color:C.muted,marginBottom:8,letterSpacing:1,textTransform:"uppercase"}}>📌 Pinned Visions</div>
      <div style={{display:"flex",gap:10,overflowX:"auto",paddingBottom:4}}>
        {pinned.map(v => {
          const pillar = VISION_PILLARS.find(p => p.id===v.pillar) || VISION_PILLARS[0];
          const ts = (v.steps||[]).length;
          const ds = (v.steps||[]).filter(s=>s.done).length;
          const pct = ts > 0 ? Math.round(ds/ts*100) : 0;
          return (
            <div key={v.id} style={{minWidth:240,maxWidth:280,background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${pillar.color}`,borderRadius:10,padding:12,flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
              {v.image && (
                <img src={v.image} alt={v.title} style={{width:"100%",height:80,objectFit:"cover",borderRadius:6,marginBottom:8}}/>
              )}
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:4}}>
                <span style={{fontSize:14}}>{pillar.icon}</span>
                <span style={{fontSize:9,color:pillar.color,fontWeight:"bold",textTransform:"uppercase",letterSpacing:.5}}>{pillar.label}</span>
              </div>
              <div style={{fontSize:13,fontWeight:"bold",color:C.text,marginBottom:4,lineHeight:1.3}}>{v.title}</div>
              {v.description && <div style={{fontSize:11,color:C.muted,marginBottom:6,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{v.description}</div>}
              {ts > 0 && (
                <div>
                  <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{ds}/{ts} steps · {pct}%</div>
                  <div style={{background:C.border,borderRadius:3,height:4}}><div style={{width:`${pct}%`,background:pillar.color,height:"100%",borderRadius:3}}/></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// POMODORO — stable across tab switches
// Uses endTime timestamp instead of counting secs with setInterval
// Tab-switch safe: calculates remaining from wall clock
// ══════════════════════════════════════════════════════════════════════════════
const POMO_KEY = "pomo_state_v4";

function PomodoroStable({ starredTasks, focusTask, onRunningChange }) {
  const DURATIONS = { work:25*60, short:5*60, long:15*60 };

  // Load persisted state — always start PAUSED on fresh page load.
  // Only preserve the session count and current mode; never auto-resume a timer.
  const initState = () => {
    try {
      const s = JSON.parse(localStorage.getItem(POMO_KEY) || "null");
      if (s) {
        // If timer was running, check if it has already expired
        if (s.running && s.endTime) {
          const remaining = Math.round((s.endTime - Date.now()) / 1000);
          if (remaining > 0) {
            // Timer still has time left — restore exactly as-is so it keeps counting
            return { ...s, secsLeft: remaining };
          } else {
            // Timer expired while away — mark session complete, reset
            return {
              mode: s.mode || "work",
              sessions: s.mode === "work" ? (s.sessions || 0) + 1 : (s.sessions || 0),
              secsLeft: DURATIONS[s.mode] || DURATIONS.work,
              endTime: null,
              running: false,
            };
          }
        }
        // Was paused — restore exactly
        return s;
      }
    } catch {}
    return { mode:"work", endTime:null, secsLeft:25*60, sessions:0, running:false };
  };

  const [ps, setPsRaw] = useState(initState);
  const [display, setDisplay] = useState(ps.secsLeft);
  const animRef = useRef(null);

  const savePs = (next) => {
    localStorage.setItem(POMO_KEY, JSON.stringify(next));
    setPsRaw(next);
  };

  // Recalculate display from endTime every second via requestAnimationFrame
  useEffect(() => {
    const tick = () => {
      setPsRaw(prev => {
        if (!prev.running || !prev.endTime) {
          setDisplay(prev.secsLeft);
          return prev;
        }
        const remaining = Math.max(0, Math.round((prev.endTime - Date.now()) / 1000));
        setDisplay(remaining);
        if (remaining <= 0) {
          playDing("complete");
          const modeLabel = prev.mode === "work" ? "🍅 Focus Session" : prev.mode === "short" ? "☕ Short Break" : "🧘 Long Break";
          addLocalNotif(`${modeLabel} Complete!`, prev.mode === "work" ? "Great focus session! Time for a break." : "Break over — ready to focus again?");
          // Browser notification if permitted
          if (Notification.permission === "granted") {
            try { new Notification(modeLabel + " Complete!", { body: prev.mode === "work" ? "Time for a break!" : "Back to work!", icon: "/favicon.ico" }); } catch {}
          }
          const next = { ...prev, running:false, secsLeft:0, endTime:null, sessions: prev.mode==="work"?prev.sessions+1:prev.sessions };
          localStorage.setItem(POMO_KEY, JSON.stringify(next));
          return next;
        }
        return { ...prev, secsLeft: remaining };
      });
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  const switchMode = m => { const next={mode:m,endTime:null,secsLeft:DURATIONS[m],sessions:ps.sessions,running:false}; savePs(next); setDisplay(DURATIONS[m]); };
  const toggleRun  = () => {
    if (ps.running) {
      // pause: store remaining secs
      const rem = ps.endTime ? Math.max(0, Math.round((ps.endTime - Date.now())/1000)) : ps.secsLeft;
      savePs({ ...ps, running:false, endTime:null, secsLeft:rem });
    } else {
      // start/resume
      const endTime = Date.now() + (ps.secsLeft || DURATIONS[ps.mode]) * 1000;
      savePs({ ...ps, running:true, endTime });
    }
  };
  const reset = () => { const next={...ps,running:false,endTime:null,secsLeft:DURATIONS[ps.mode]}; savePs(next); setDisplay(DURATIONS[ps.mode]); };

  const total = DURATIONS[ps.mode];
  const mm = String(Math.floor(display/60)).padStart(2,"0");
  const ss = String(display%60).padStart(2,"0");
  const focused = ps.running; // the card inverts to a dark, ambient focus tone
  // the instant a session starts; the rest of the dashboard blurs (see onRunningChange).
  const taskLabel = focusTask?.text || (starredTasks.length>0 ? starredTasks[0]?.text : null);

  useEffect(() => { onRunningChange && onRunningChange(ps.running); }, [ps.running]); // eslint-disable-line

  return (
    <div className="ta-card-prominent" style={{
      display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",
      width:"100%",aspectRatio:"1 / 1",padding:"24px 22px",
      background: focused ? "linear-gradient(135deg,#1a1c1c 0%,#2a2d35 100%)" : "var(--ta-surface-lowest)",
      color: focused ? "#f2f1ee" : "var(--ta-on-surface)",
      transition:"background .5s ease, color .5s ease",
    }}>
      <div className="ta-row ta-between" style={{width:"100%",marginBottom:10}}>
        <span className="ta-mono" style={{fontSize:10,letterSpacing:".2em",textTransform:"uppercase",color:focused?"rgba(242,241,238,.6)":"var(--ta-on-surface-muted)"}}>Focus</span>
        {ps.sessions>0&&<span className="ta-mono" style={{fontSize:10,color:focused?"rgba(242,241,238,.5)":"var(--ta-on-surface-muted)"}}>{ps.sessions} today</span>}
      </div>
      <div className="ta-row ta-gap-1" style={{marginBottom:16}}>
        {[["work","Work"],["short","Short"],["long","Long"]].map(([m,l])=>(
          <button key={m} onClick={()=>switchMode(m)} className="ta-mono" style={{
            padding:"4px 9px",borderRadius:"var(--r-sm)",border:"none",cursor:"pointer",
            fontSize:9,letterSpacing:".06em",textTransform:"uppercase",fontWeight:600,
            background: ps.mode===m ? (focused?"rgba(255,255,255,.16)":"var(--ta-primary)") : "transparent",
            color: ps.mode===m ? "#fff" : (focused?"rgba(242,241,238,.55)":"var(--ta-on-surface-muted)"),
            transition:"all .15s",
          }}>{l}</button>
        ))}
      </div>

      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
        <div className="ta-serif tnum" style={{fontSize:48,fontWeight:300,letterSpacing:"-0.02em",lineHeight:1}}>{mm}:{ss}</div>
        {taskLabel&&(
          <div className="ta-serif ta-italic" style={{fontSize:12,opacity:.8,marginTop:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%"}}>
            "{taskLabel}"
          </div>
        )}
      </div>

      <div className="ta-row ta-gap-2" style={{width:"100%"}}>
        <button onClick={toggleRun} className="ta-btn" style={{flex:1,justifyContent:"center",background:focused?"rgba(255,255,255,.14)":"var(--ta-primary)",color:"#fff"}}>
          {ps.running?"Pause":"Start"}
        </button>
        <button onClick={reset} className="ta-btn" style={{justifyContent:"center",background:"transparent",border:`1px solid ${focused?"rgba(255,255,255,.25)":"var(--ta-outline-variant)"}`,color:focused?"rgba(242,241,238,.7)":"var(--ta-on-surface-variant)"}}>
          Reset
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// GOALS
// ══════════════════════════════════════════════════════════════════════════════
function GoalsSection({ ctx }) {
  const C = useC();
  const { goals, setGoals, setAlert, archiveGoal, unarchiveGoal } = ctx;
  const [showForm,setShowForm]=useState(false);
  const [editId,setEditId]=useState(null);
  const [expanded,setExpanded]=useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const blank={title:"",description:"",startDate:TODAY,endDate:"",priority:1,links:[],subItems:[]};
  const [form,setForm]=useState(blank);

  const activeGoals   = goals.filter(g => !g.archived);
  const archivedGoals = goals.filter(g => g.archived);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const tmpl = await ctx.fetchPublicTemplates('goal');
      setPublicTemplates(tmpl || []);
    } catch(e) { console.warn('templates', e); }
    setLoadingTemplates(false);
  };

  const importTemplate = (tmpl) => {
    const d = tmpl.data || {};
    setForm({ title: d.title||tmpl.title, description: d.description||tmpl.description||'', startDate: TODAY, endDate: '', priority: d.priority||1, links: d.links||[], subItems: (d.subItems||[]).map(s=>({...s, id:Date.now()+Math.random(), done:false})) });
    setShowTemplates(false);
    setShowForm(true);
    setAlert('📥 Template loaded — customise and save!');
  };

  const save=()=>{
    if(!form.title.trim())return;
    if(editId){setGoals(p=>p.map(g=>g.id===editId?{...g,...form}:g));setEditId(null);}
    else setGoals(p=>[...p,{...form,checkinHistory:{}}]);
    setForm(blank);setShowForm(false);
  };

  const addRem=g=>{
    if(!g.endDate){setAlert("⚠️ Set an end date first.");return;}
    ctx.addReminder({text:`Goal deadline: ${g.title}`,datetime:`${g.endDate}T09:00`});
    setAlert(`⏰ Reminder set for ${g.endDate} at 9:00 AM`);
  };

  // ── Add a goal (or one of its sub-goals/steps) to Tasks — prompts for a date
  // range + optional time, then creates one task per matching day. Tasks with
  // a time already render on the calendar's hour grid, so this covers both.
  const [taskPromptItem,setTaskPromptItem]=useState(null); // {text}
  const [taskPromptForm,setTaskPromptForm]=useState({fromDate:TODAY,toDate:TODAY,time:""});
  const submitTaskPrompt=()=>{
    if(!taskPromptItem)return;
    const from=new Date(taskPromptForm.fromDate+"T00:00:00");
    const to=new Date(taskPromptForm.toDate+"T00:00:00");
    if(to<from){setAlert("⚠️ \"To\" date must be on or after \"From\" date.");return;}
    const days=Math.round((to-from)/86400000)+1;
    if(days>60){setAlert("⚠️ That range is too long — please keep it under 60 days.");return;}
    for(let i=0;i<days;i++){
      const d=new Date(from); d.setDate(from.getDate()+i);
      const dateStr=fmt(d);
      ctx.addTask(dateStr, taskPromptItem.text, taskPromptForm.time?`${dateStr}T${taskPromptForm.time}`:undefined, {priority:"normal"});
    }
    setAlert(`✅ Added "${taskPromptItem.text}" to ${days} day${days!==1?"s":""}.`);
    setTaskPromptItem(null);
  };

  // Share goal as private URL-based template (like Notion — only shareable via link)
  const [shareModal, setShareModal] = useState(null); // { url, title }
  const [importUrl, setImportUrl]   = useState('');
  const [importLoading, setImportLoading] = useState(false);

  const shareGoalTemplate = async (g) => {
    if (!ctx.addSharedTemplate) return;
    const saved = await ctx.addSharedTemplate({
      type: 'goal', title: g.title,
      description: g.description || '',
      data: { title: g.title, description: g.description, subItems: g.subItems||[], links: g.links||[], priority: g.priority },
      is_public: false, // Private by default — only accessible via direct link
    });
    if (saved?.id) {
      const url = `${window.location.origin}/?import_template=${saved.id}`;
      setShareModal({ url, title: g.title });
    }
  };

  const importByUrl = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    try {
      // Extract template ID from URL
      const match = importUrl.match(/import_template=([a-zA-Z0-9_-]+)/);
      const id = match?.[1] || importUrl.trim();
      const tmpl = await ctx.fetchTemplateById(id);
      if (!tmpl) { ctx.setAlert?.('❌ Template not found. Check the link.'); setImportLoading(false); return; }
      const d = tmpl.data || {};
      setForm({ title: d.title||tmpl.title, description: d.description||'', startDate: TODAY, endDate: '', priority: d.priority||1, links: d.links||[], subItems: (d.subItems||[]).map(s=>({...s, id: Date.now()+Math.random(), done: false})) });
      setShowTemplates(false);
      setShowForm(true);
      setImportUrl('');
      ctx.setAlert?.('📥 Template loaded — customise and save!');
    } catch(e) { ctx.setAlert?.('❌ Error loading template.'); }
    setImportLoading(false);
  };

  // Check URL for import_template param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get('import_template');
    if (tid && ctx.fetchTemplateById) {
      ctx.fetchTemplateById(tid).then(tmpl => {
        if (!tmpl) return;
        const d = tmpl.data || {};
        setForm({ title: d.title||tmpl.title, description: d.description||'', startDate: TODAY, endDate: '', priority: d.priority||1, links: d.links||[], subItems: (d.subItems||[]).map(s=>({...s, id: Date.now()+Math.random(), done: false})) });
        setShowForm(true);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return(
    <div>
      <SectionMasthead
        eyebrow={`Personal · ${activeGoals.length} active goal${activeGoals.length!==1?'s':''}`}
        title={<>The <em>life</em> being built.</>}
        actions={<>
          <button onClick={()=>{setShowTemplates(true);loadTemplates();}} className="ta-btn ta-btn-secondary">Templates</button>
          <button onClick={()=>setShowArchived(v=>!v)} className="ta-btn ta-btn-ghost">
            {showArchived?`Active (${activeGoals.length})`:`Archived (${archivedGoals.length})`}
          </button>
          {!showArchived && <button onClick={()=>{setForm(blank);setEditId(null);setShowForm(true);}} className="ta-btn ta-btn-primary">+ New goal</button>}
        </>}
      />

      {/* Share modal — Notion-style private URL */}
      {shareModal && (
        <div style={{position:"fixed",inset:0,zIndex:9000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&setShareModal(null)}>
          <div style={{background:C.surface,border:`1.5px solid ${C.indigo}`,borderRadius:16,padding:28,width:"100%",maxWidth:480,boxShadow:"0 24px 80px rgba(0,0,0,.4)"}}>
            <div style={{fontSize:36,textAlign:"center",marginBottom:12}}>🔗</div>
            <h3 style={{margin:"0 0 6px",color:C.indigo,textAlign:"center",fontSize:17}}>Template Link Created</h3>
            <p style={{margin:"0 0 18px",color:C.muted,fontSize:13,textAlign:"center",lineHeight:1.6}}>Share this private link — only people with it can import your goal template. Perfect for selling personal templates!</p>
            <div style={{background:C.surfaceAlt,borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:C.text,flex:1,wordBreak:"break-all",fontFamily:"monospace"}}>{shareModal.url}</span>
              <button onClick={()=>{navigator.clipboard.writeText(shareModal.url);ctx.setAlert?.('✅ Link copied!');}} style={{...btn(C.indigo),fontSize:11,padding:"5px 10px",flexShrink:0}}>Copy</button>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
              <button onClick={()=>window.open(`https://wa.me/?text=${encodeURIComponent(`🎯 Goal Template: "${shareModal.title}" — Import it on Aspreo Life: ${shareModal.url}`)}`, '_blank')} style={{...btn("#25D366"),fontSize:11,padding:"6px 12px"}}>💬 WhatsApp</button>
              <button onClick={()=>window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareModal.url)}`, '_blank')} style={{...btn("#0A66C2"),fontSize:11,padding:"6px 12px"}}>💼 LinkedIn</button>
              <button onClick={()=>window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`🎯 "${shareModal.title}" goal template — import on Aspreo Life:`)}&url=${encodeURIComponent(shareModal.url)}`, '_blank')} style={{...btn("#1DA1F2"),fontSize:11,padding:"6px 12px"}}>🐦 X</button>
            </div>
            <button onClick={()=>setShareModal(null)} style={{...btn("#999"),width:"100%",marginTop:14}}>Close</button>
          </div>
        </div>
      )}

      {/* Template panel — My Saved + Import via URL */}
      {showTemplates && (()=>{
        const myGoalTemplates = (ctx.sharedTemplates||[]).filter(t => t.type === 'goal');
        return (
        <div style={{background:C.surface,border:`1.5px solid ${C.indigo}`,borderRadius:14,padding:20,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h4 style={{margin:0,color:C.indigo,fontSize:15}}>📋 Goal Templates</h4>
            <button onClick={()=>setShowTemplates(false)} style={{...btn("#999"),padding:"4px 8px",fontSize:12}}>✕</button>
          </div>

          {/* My saved templates */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>
              🗂 My Saved Templates ({myGoalTemplates.length})
            </div>
            {myGoalTemplates.length === 0 && (
              <div style={{background:"#f8fafc",borderRadius:10,padding:"14px",fontSize:12,color:C.muted,textAlign:"center"}}>
                No saved templates yet. Click <strong>🌐 Share</strong> on any goal card to save it as a template.
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {myGoalTemplates.map(t => (
                <div key={t.id} style={{background:"#f8fafc",border:`1px solid ${C.border}`,borderLeft:`3px solid ${C.indigo}`,borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.title}</div>
                    {t.description && <div style={{fontSize:11,color:C.muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.description}</div>}
                    <div style={{fontSize:10,color:C.light,marginTop:3,display:"flex",gap:8}}>
                      <span>📅 {new Date(t.created_at).toLocaleDateString()}</span>
                      <span>📥 {t.import_count||0} imports</span>
                      <span style={{color:t.is_public?"#16a34a":"#94a3b8"}}>{t.is_public?"🔗 Link active":"🔒 Link deactivated"}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                    <button onClick={()=>importTemplate(t)} style={{...btn(C.indigo),fontSize:10,padding:"4px 8px"}}>📥 Use</button>
                    <button onClick={()=>{
                      const url = `${window.location.origin}/?import_template=${t.id}`;
                      navigator.clipboard.writeText(url);
                      ctx.setAlert?.('✅ Link copied!');
                    }} style={{...btn(C.green),fontSize:10,padding:"4px 8px"}} title="Copy share link">🔗 Link</button>
                    <button onClick={()=>ctx.deleteSharedTemplate(t.id)} style={{...btn('#6B7280'),fontSize:10,padding:"4px 8px"}} title="Deactivate link (keeps template in My list)">🔒 Off</button>
                    <button onClick={()=>{if(window.confirm(`Permanently delete "${t.title}"? Cannot be undone.`))ctx.hardDeleteSharedTemplate(t.id);}} style={{...btn(C.red),fontSize:10,padding:"4px 8px"}} title="Permanently delete from database">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16,marginTop:4}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>📥 Import Template via Link</div>
            <p style={{margin:"0 0 10px",fontSize:12,color:C.muted,lineHeight:1.6}}>
              Paste a template link shared by someone else:
            </p>
            <div style={{display:"flex",gap:8}}>
              <input value={importUrl} onChange={e=>setImportUrl(e.target.value)} onKeyDown={e=>e.key==='Enter'&&importByUrl()} placeholder={`${window.location.origin}/?import_template=...`} style={{...inp(),flex:1,fontSize:12}}/>
              <button onClick={importByUrl} disabled={importLoading||!importUrl.trim()} style={{...btn(C.indigo),padding:"8px 16px",fontSize:12,opacity:importLoading||!importUrl.trim()?0.5:1}}>{importLoading?"Loading...":"📥 Import"}</button>
            </div>
          </div>
        </div>
        );
      })()}
      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${C.green}`,borderRadius:12,padding:20,marginBottom:20}}>
          <h3 style={{margin:"0 0 14px",color:C.green}}>{editId?"Edit":"New"} Goal</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} style={ta(60)} placeholder="Why is this important?"/></div>
            <div><label style={lbl}>Start Date</label><input type="date" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>End / Deadline</label><input type="date" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Priority (1=highest)</label><input type="number" min={1} max={10} value={form.priority} onChange={e=>setForm(p=>({...p,priority:Number(e.target.value)}))} style={{...inp(),width:"100%"}}/></div>
          </div>
          <div style={{marginTop:12}}>
            <label style={lbl}>Support Links</label>
            {(form.links||[]).map((lk,i)=>(
              <div key={i} style={{display:"flex",gap:6,marginBottom:5}}>
                <input value={lk.label} onChange={e=>setForm(p=>({...p,links:p.links.map((l,j)=>j===i?{...l,label:e.target.value}:l)}))} placeholder="Label" style={{...inp(),flex:1}}/>
                <input value={lk.url} onChange={e=>setForm(p=>({...p,links:p.links.map((l,j)=>j===i?{...l,url:e.target.value}:l)}))} placeholder="https://..." style={{...inp(),flex:2}}/>
                <button onClick={()=>setForm(p=>({...p,links:p.links.filter((_,j)=>j!==i)}))} style={btn("#999",32)}>✕</button>
              </div>
            ))}
            <button onClick={()=>setForm(p=>({...p,links:[...(p.links||[]),{label:"",url:""}]}))} style={{...btn(C.amber),fontSize:11,padding:"4px 8px"}}>+ Link</button>
          </div>
          <div style={{marginTop:12}}>
            <label style={lbl}>Sub-Goals & Steps</label>
            {(form.subItems||[]).map(s=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                <span style={{fontSize:10,color:s.type==="subgoal"?C.green:C.amber,minWidth:40,fontWeight:"bold"}}>{s.type==="subgoal"?"🎯":"📌"}</span>
                <input value={s.text} onChange={e=>setForm(p=>({...p,subItems:p.subItems.map(x=>x.id===s.id?{...x,text:e.target.value}:x)}))} placeholder={s.type==="subgoal"?"Sub-goal...":"Step..."} style={{...inp(),flex:1}}/>
                <button onClick={()=>setForm(p=>({...p,subItems:p.subItems.filter(x=>x.id!==s.id)}))} style={{background:"none",border:"none",cursor:"pointer",color:"#999"}}>✕</button>
              </div>
            ))}
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setForm(p=>({...p,subItems:[...(p.subItems||[]),{id:Date.now(),type:"subgoal",text:"",done:false}]}))} style={{...btn(C.green),fontSize:11,padding:"4px 8px"}}>+ Sub-Goal</button>
              <button onClick={()=>setForm(p=>({...p,subItems:[...(p.subItems||[]),{id:Date.now(),type:"step",text:"",done:false}]}))} style={{...btn(C.amber),fontSize:11,padding:"4px 8px"}}>+ Step</button>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:14}}>
            <button onClick={save} style={btn(C.green)}>💾 Save</button>
            <button onClick={()=>{setShowForm(false);setEditId(null);}} style={btn("#999")}>Cancel</button>
          </div>
        </div>
      )}

      {/* Archived goals view */}
      {showArchived && (
        <div>
          <div style={{background:"#FFF8E1",border:"1px solid #FDD835",borderRadius:10,padding:"10px 16px",marginBottom:14,fontSize:13,color:"#7B6000"}}>
            📦 <strong>Archive</strong> — {archivedGoals.length} completed/archived goals preserved here. Click a goal to restore it.
          </div>
          {archivedGoals.length===0 && <p style={{color:C.light}}>No archived goals yet.</p>}
          <div style={{display:"grid",gap:10}}>
            {archivedGoals.map(g=>(
              <div key={g.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`4px solid #9CA3AF`,borderRadius:10,padding:14,opacity:.8}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:C.muted,fontSize:14}}>✅ {g.title}</div>
                    {g.description && <div style={{fontSize:12,color:C.light,marginTop:2}}>{g.description}</div>}
                    <div style={{fontSize:11,color:C.light,marginTop:3}}>{g.startDate} → {g.endDate||"?"}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>{if(ctx.fetchPublicTemplates)shareGoalTemplate(g);}} style={{...btn(C.indigo),fontSize:11,padding:"4px 8px"}}>🌐 Share</button>
                    <button onClick={()=>unarchiveGoal(g.id)} style={{...btn(C.green),fontSize:11,padding:"4px 8px"}}>↩ Restore</button>
                    <button onClick={()=>setGoals(p=>p.filter(x=>x.id!==g.id))} style={{...btn(C.red),fontSize:11,padding:"4px 8px"}}>🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active goals */}
      {!showArchived && (
        <div style={{display:"grid",gap:12}}>
          {activeGoals.length===0&&<p style={{color:C.light}}>No active goals. {archivedGoals.length>0&&<span>You have {archivedGoals.length} archived.</span>}</p>}
          {[...activeGoals].sort((a,b)=>(a.priority||99)-(b.priority||99)).map(g=>(
            <GoalCard key={g.id} goal={g} ctx={ctx}
              expanded={expanded===g.id} onToggle={()=>setExpanded(expanded===g.id?null:g.id)}
              onEdit={()=>{setForm({title:g.title,description:g.description,startDate:g.startDate,endDate:g.endDate,priority:g.priority,links:g.links||[],subItems:g.subItems||[]});setEditId(g.id);setShowForm(true);}}
              onDelete={()=>setGoals(p=>p.filter(x=>x.id!==g.id))}
              onArchive={()=>{archiveGoal(g.id);setAlert('📦 Goal archived.');}}
              onCheckin={()=>setGoals(p=>p.map(gg=>gg.id!==g.id?gg:{...gg,checkinHistory:{...(gg.checkinHistory||{}),[TODAY]:!(gg.checkinHistory||{})[TODAY]}}))}
              onReminder={()=>addRem(g)}
              onShare={()=>shareGoalTemplate(g)}
              onAddToTask={(text)=>{setTaskPromptForm({fromDate:TODAY,toDate:TODAY,time:""});setTaskPromptItem({text});}}
            />
          ))}
        </div>
      )}

      {/* Add goal (or a sub-goal/step) to Tasks — date range + optional time */}
      {taskPromptItem && (
        <div style={{position:'fixed',inset:0,zIndex:5000,background:'rgba(0,0,0,.55)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setTaskPromptItem(null)}>
          <div style={{background:C.surface,border:`1.5px solid ${C.green}`,borderRadius:16,padding:24,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.3)'}}>
            <h3 style={{margin:'0 0 4px',color:C.green,fontSize:16}}>➜ Add "{taskPromptItem.text}" to Tasks</h3>
            <p style={{margin:'0 0 14px',fontSize:12,color:C.muted}}>Creates a task for each day in this range. Give it a time to also see it on the calendar.</p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <div><label style={lbl}>From</label><input type="date" value={taskPromptForm.fromDate} onChange={e=>setTaskPromptForm(p=>({...p,fromDate:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>To</label><input type="date" value={taskPromptForm.toDate} onChange={e=>setTaskPromptForm(p=>({...p,toDate:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
              <div style={{gridColumn:'1/-1'}}><label style={lbl}>Time (optional)</label><input type="time" value={taskPromptForm.time} onChange={e=>setTaskPromptForm(p=>({...p,time:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setTaskPromptItem(null)} style={btn('#999')}>Cancel</button>
              <button onClick={submitTaskPrompt} style={btn(C.green)}>💾 Add to Tasks</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GoalCard({goal,ctx,onEdit,onDelete,onArchive,onShare,expanded,onToggle,onCheckin,onReminder,onAddToTask,mini}){
  const {setGoals,knowledgeItems=[]}=ctx;
  const linkedToGoal = knowledgeItems.filter(k=>k.linked_goal_id===goal.id);
  const linkedWhole = linkedToGoal.filter(k=>!k.linked_subitem_id);
  const totalDays=goal.startDate&&goal.endDate?daysBetween(goal.startDate,goal.endDate):100;
  const elapsed=goal.startDate?Math.min(totalDays,daysBetween(goal.startDate,TODAY)):0;
  const pct=totalDays>0?Math.round(elapsed/totalDays*100):0;
  const ch=goal.checkinHistory||{};
  const checkedToday=ch[TODAY];
  const dotsCount=Math.min(100,totalDays||10);
  const dots=Array.from({length:dotsCount},(_,i)=>{if(!goal.startDate)return false;const dd=new Date(goal.startDate+"T00:00:00");dd.setDate(dd.getDate()+i);return ch[fmt(dd)];});
  const toggleStep=sid=>setGoals(p=>p.map(g=>g.id!==goal.id?g:{...g,subItems:(g.subItems||[]).map(s=>s.id===sid?{...s,done:!s.done}:s)}));
  return(
    <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderLeft:`4px solid ${C.green}`,borderRadius:10,padding:mini?12:16,boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:10,background:C.greenM,color:C.green,borderRadius:10,padding:"2px 7px",fontWeight:"bold"}}>P{goal.priority}</span>
            <span style={{fontSize:mini?13:15,fontWeight:"bold",color:C.green}}>{goal.title}</span>
          </div>
          {!mini&&goal.description&&<p style={{margin:"5px 0 0",fontSize:12,color:C.muted}}>{goal.description}</p>}
          {(goal.startDate||goal.endDate)&&<div style={{fontSize:10,color:C.light,marginTop:3}}>{goal.startDate} → {goal.endDate||"?"} · {elapsed}/{totalDays}d · {pct}%</div>}
          <div style={{marginTop:6,background:C.border,borderRadius:99,height:6,overflow:"hidden"}}><div style={{width:`${pct}%`,background:`linear-gradient(90deg,${C.green},${C.greenL})`,height:"100%",borderRadius:99,transition:"width .6s cubic-bezier(.4,0,.2,1)"}}/></div>
          {!mini&&dotsCount>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3,marginTop:8}}>{dots.map((f,i)=><div key={i} style={{width:9,height:9,borderRadius:"50%",background:f?C.green:C.border}} title={`Day ${i+1}`}/>)}</div>}
          {!mini&&(goal.links||[]).length>0&&<div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:5}}>{goal.links.map((lk,i)=><a key={i} href={lk.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:C.green,background:C.greenM,padding:"2px 8px",borderRadius:10,textDecoration:"none"}}>🔗 {lk.label||lk.url}</a>)}</div>}
          {!mini&&linkedWhole.length>0&&(
            <div title={linkedWhole.map(k=>k.title).join(', ')} onClick={()=>ctx.setView&&ctx.setView('wiki')}
              style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:4,fontSize:11,color:C.indigo,background:C.indigo+"18",padding:"2px 8px",borderRadius:10,cursor:"pointer"}}>
              📚 {linkedWhole.length} linked note{linkedWhole.length!==1?"s":""}
            </div>
          )}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
          {onCheckin&&<button onClick={onCheckin} style={{...btn(checkedToday?C.green:"#ccc"),fontSize:10,padding:"4px 8px"}}>{checkedToday?"✓ Done":"Check-in"}</button>}
          {onReminder&&<button onClick={onReminder} style={{...btn(C.blue),fontSize:10,padding:"4px 8px"}}>⏰ Remind</button>}
          {onShare&&<button onClick={onShare} style={{...btn(C.indigo),fontSize:10,padding:"4px 8px"}}>🔗 Template</button>}
          {!mini&&<ShareButton type="goal" data={{title:goal.title,pct:Math.round((Object.values(goal.checkinHistory||{}).filter(Boolean).length/Math.max(1,dotsCount))*100)}} style={{fontSize:10,padding:"4px 8px"}}/>}
          {onArchive&&<button onClick={onArchive} style={{...btn("#6B7280"),fontSize:10,padding:"4px 8px"}} title="Archive (keeps history)">📦 Archive</button>}
          {onToggle&&<button onClick={onToggle} style={btn("#888",28)}>{expanded?"▲":"▼"}</button>}
          {onEdit&&<button onClick={onEdit} style={btn(C.amber,28)}>✏️</button>}
          {onDelete&&<button onClick={()=>{if(window.confirm("⚠️ Permanently delete this goal? This cannot be undone."))onDelete();}} style={btn(C.red,28)} title="Permanently delete">🗑</button>}
        </div>
      </div>
      {expanded&&(goal.subItems||[]).length>0&&(
        <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
          {goal.subItems.map(s=>{
            const linkedHere = linkedToGoal.filter(k=>k.linked_subitem_id===String(s.id));
            return (
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:7,marginBottom:5}}>
                {s.type==="step"&&<input type="checkbox" checked={s.done||false} onChange={()=>toggleStep(s.id)} style={{accentColor:C.green}}/>}
                <span style={{fontSize:10,color:s.type==="subgoal"?C.green:C.amber}}>{s.type==="subgoal"?"🎯":"📌"}</span>
                <span style={{flex:1,fontSize:12,textDecoration:s.done?"line-through":"none",color:s.done?C.light:C.text}}>{s.text}</span>
                {linkedHere.length>0&&(
                  <span title={linkedHere.map(k=>k.title).join(', ')} onClick={()=>ctx.setView&&ctx.setView('wiki')}
                    style={{fontSize:10,color:C.indigo,background:C.indigo+"18",padding:"1px 6px",borderRadius:8,cursor:"pointer",flexShrink:0}}>📚 {linkedHere.length}</span>
                )}
                {onAddToTask&&s.text.trim()&&<button onClick={()=>onAddToTask(s.text)} title="Add this step to Tasks" style={{background:"none",border:"none",cursor:"pointer",color:C.teal,fontSize:11,flexShrink:0}}>➜ Task</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// VISIONS
// ══════════════════════════════════════════════════════════════════════════════
function VisionsSection({ctx}){
  const C = useC();
  const {visions,dashboardVisions}=ctx;
  const pillars = ctx.visionPillars?.length ? ctx.visionPillars : (loadStoredPillars()||DEFAULT_VISION_PILLARS);
  const savePillars=(next)=>{
    // Sync to DB via ctx functions
    const cur = pillars;
    // Re-stamp sort_order from each item's new array position — drag-reordering
    // only moves items within the array, it doesn't touch their own fields, so
    // without this the change-detection below never sees a difference to save
    // and a reload (which is fetched ordered by sort_order) snaps back to the
    // old order.
    const reindexed = next.map((p,i)=>({...p, sort_order:i}));
    reindexed.forEach(p => {
      const existing = cur.find(x=>x.id===p.id);
      if (!existing) ctx.addVisionPillar(p);
      else if (JSON.stringify(existing) !== JSON.stringify(p)) ctx.updateVisionPillar(p.id, p);
    });
    cur.forEach(p => { if (!reindexed.find(x=>x.id===p.id)) ctx.deleteVisionPillar(p.id); });
    // Also keep localStorage in sync as fast fallback
    try { localStorage.setItem(LS_PILLARS, JSON.stringify(reindexed)); } catch {}
  };
  const [showPillarMgr,setShowPillarMgr]=useState(false);
  const [newPillar,setNewPillar]=useState({label:"",icon:"🌟",color:"#1D5F8A"});
  const [editPillarId,setEditPillarId]=useState(null);
  const [activePillar,setActivePillar]=useState(pillars[0]?.id);
  const [showForm,setShowForm]=useState(false);
  const [editVisionId,setEditVisionId]=useState(null);
  const [form,setForm]=useState({title:"",description:"",pillar:pillars[0]?.id,steps:[],remDate:"",remTime:""});
  const formImgRef=useRef(null);
  const [formImage,setFormImage]=useState(null);

  // When DB pillars load in (async), sync activePillar to the first real pillar
  useEffect(()=>{
    if(ctx.visionPillars?.length && !ctx.visionPillars.find(p=>p.id===activePillar)){
      setActivePillar(ctx.visionPillars[0].id);
    }
  },[ctx.visionPillars]); // eslint-disable-line

  const pillar=pillars.find(p=>p.id===activePillar)||pillars[0]||{id:"",label:"—",icon:"🌟",color:C.green};
  const pillarVisions=visions.filter(v=>v.pillar===activePillar);

  const handleFormImage=(e)=>{
    const file=e.target.files[0]; if(!file)return;
    // Store the File object — useData will upload to storage on save
    // Use createObjectURL just for the local preview thumbnail
    file._previewUrl = URL.createObjectURL(file);
    setFormImage(file);
  };

  const saveVision=()=>{
    if(!form.title.trim())return;
    if(editVisionId){
      ctx.updateVision(editVisionId,{...form},formImage instanceof File?formImage:undefined);
    } else {
      ctx.addVision({...form,createdAt:TODAY},formImage instanceof File?formImage:undefined);
      if(form.remDate&&form.remTime)ctx.addReminder({text:`Vision: ${form.title}`,datetime:`${form.remDate}T${form.remTime}`});
    }
    setEditVisionId(null);
    setForm({title:"",description:"",pillar:activePillar,steps:[],remDate:"",remTime:""});
    setFormImage(null); setShowForm(false);
  };

  const toggleStep=(vId,sId)=>{
    // Update local state via updateVision directly
    const v=visions.find(x=>x.id===vId); if(!v)return;
    ctx.updateVision(vId,{steps:(v.steps||[]).map(s=>s.id===sId?{...s,done:!s.done}:s)});
  };

  const togglePin=(vid)=>ctx.togglePinVision(vid);

  const addImageToVision=(vid,file)=>ctx.updateVision(vid,{},file);
  const removeImageFromVision=(vid)=>ctx.updateVision(vid,{image_url:null});

  // ── Add vision to Goals — carries title/description/steps over, prompts for dates+priority ──
  const [goalPromptVision,setGoalPromptVision]=useState(null);
  const [goalPromptForm,setGoalPromptForm]=useState({startDate:TODAY,endDate:"",priority:2});
  const submitVisionToGoal=()=>{
    if(!goalPromptVision)return;
    ctx.addGoal({
      title: goalPromptVision.title,
      description: goalPromptVision.description||"",
      startDate: goalPromptForm.startDate,
      endDate: goalPromptForm.endDate,
      priority: goalPromptForm.priority,
      links: [],
      subItems: (goalPromptVision.steps||[]).map(s=>({id:Date.now()+Math.random(),type:"step",text:s.text,done:s.done||false})),
      checkinHistory: {},
    });
    ctx.setAlert?.(`🎯 "${goalPromptVision.title}" added to Goals!`);
    setGoalPromptVision(null);
  };

  return(
    <div>
      <SectionMasthead
        eyebrow={`Personal · ${visions.length} vision${visions.length!==1?'s':''} across ${pillars.length} pillars`}
        title={<>Picture it <em>vividly</em>.</>}
        actions={<button onClick={()=>setShowPillarMgr(v=>!v)} className="ta-btn ta-btn-secondary">{showPillarMgr?"✕ Close":"Manage pillars"}</button>}
      />
      {/* Pillar manager */}
      {showPillarMgr&&(
        <div style={{background:C.surface,border:`1.5px solid ${C.purple}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:"0 0 10px",color:C.purple}}>⚙️ Manage Your Vision Pillars</h4>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
            {pillars.map((p,pi)=>(
              <div key={p.id} draggable
                onDragStart={e=>{e.dataTransfer.setData('pillarIdx',String(pi));e.currentTarget.style.opacity='0.45';}}
                onDragEnd={e=>{e.currentTarget.style.opacity='1';}}
                onDragOver={e=>{e.preventDefault();e.currentTarget.style.outline=`2px dashed ${p.color}`;}}
                onDragLeave={e=>{e.currentTarget.style.outline='none';}}
                onDrop={e=>{e.preventDefault();e.currentTarget.style.outline='none';const from=parseInt(e.dataTransfer.getData('pillarIdx'));if(from===pi)return;const next=[...pillars];const[moved]=next.splice(from,1);next.splice(pi,0,moved);savePillars(next);}}
                style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:p.color+"10",border:`1px solid ${p.color}40`,borderRadius:8,cursor:'grab'}}>
                <span style={{fontSize:11,color:'#aaa',userSelect:'none',marginRight:2}}>⠿</span>
                {editPillarId===p.id
                  ?<>
                    <input value={p.icon} onChange={e=>savePillars(pillars.map(x=>x.id===p.id?{...x,icon:e.target.value}:x))} style={{...inp(),width:46,fontSize:18,textAlign:"center",padding:"2px 4px"}}/>
                    <input value={p.label} onChange={e=>savePillars(pillars.map(x=>x.id===p.id?{...x,label:e.target.value}:x))} style={{...inp(),flex:1,fontSize:12}}/>
                    <input type="color" value={p.color} onChange={e=>savePillars(pillars.map(x=>x.id===p.id?{...x,color:e.target.value}:x))} style={{width:36,height:28,border:"none",cursor:"pointer",borderRadius:4}}/>
                    <button onClick={()=>setEditPillarId(null)} style={btn(C.green,26)}>✓</button>
                  </>
                  :<>
                    <span style={{fontSize:18,minWidth:24}}>{p.icon}</span>
                    <span style={{flex:1,fontWeight:"bold",color:p.color,fontSize:12}}>{p.label}</span>
                    <span style={{fontSize:10,color:C.muted}}>{visions.filter(v=>v.pillar===p.id).length} visions</span>
                    <button onClick={()=>setEditPillarId(p.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,color:C.amber}}>✏️</button>
                    <button onClick={()=>{if(visions.filter(v=>v.pillar===p.id).length>0){alert("Remove visions from this pillar first.");return;}const next=pillars.filter(x=>x.id!==p.id);savePillars(next);if(activePillar===p.id)setActivePillar(next[0]?.id);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,color:C.red}}>🗑</button>
                  </>
                }
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input value={newPillar.icon} onChange={e=>setNewPillar(p=>({...p,icon:e.target.value}))} placeholder="🌟" style={{...inp(),width:46,fontSize:18,textAlign:"center"}}/>
            <input value={newPillar.label} onChange={e=>setNewPillar(p=>({...p,label:e.target.value}))} placeholder="Pillar name..." style={{...inp(),flex:1}}/>
            <input type="color" value={newPillar.color} onChange={e=>setNewPillar(p=>({...p,color:e.target.value}))} style={{width:36,height:34,border:"none",cursor:"pointer",borderRadius:4}}/>
            <button onClick={()=>{
              if(!newPillar.label.trim())return;
              const next=[...pillars,{...newPillar,id:`p_${Date.now()}`}];
              savePillars(next); setNewPillar({label:"",icon:"🌟",color:"#1D5F8A"});
            }} style={btn(C.purple)}>+ Add Pillar</button>
          </div>
        </div>
      )}
      {pillars.length===0&&<p style={{color:C.muted}}>No pillars yet. Click ⚙️ Manage Pillars to add your vision pillars.</p>}
      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
        {pillars.map(p=>(
          <button key={p.id} onClick={()=>{setActivePillar(p.id);setShowForm(false);}} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",borderRadius:20,border:`2px solid ${activePillar===p.id?p.color:C.border}`,background:activePillar===p.id?p.color+"22":C.surface,cursor:"pointer",fontFamily:FONT,fontSize:12,color:activePillar===p.id?p.color:C.muted,fontWeight:activePillar===p.id?"bold":"normal"}}>
            {p.icon} {p.label}
            <span style={{fontSize:9,background:activePillar===p.id?p.color:"#ccc",color:"#fff",borderRadius:10,padding:"1px 5px"}}>{visions.filter(v=>v.pillar===p.id).length}</span>
          </button>
        ))}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,padding:"11px 15px",background:pillar.color+"15",border:`1.5px solid ${pillar.color}40`,borderRadius:10}}>
        <div><span style={{fontSize:22,marginRight:8}}>{pillar.icon}</span><span style={{fontSize:17,fontWeight:"bold",color:pillar.color}}>{pillar.label}</span><span style={{fontSize:11,color:C.muted,marginLeft:10}}>{pillarVisions.length} vision{pillarVisions.length!==1?"s":""}</span></div>
        <button onClick={()=>{setEditVisionId(null);setForm({title:"",description:"",pillar:activePillar,steps:[],remDate:"",remTime:""});setFormImage(null);setShowForm(true);}} style={btn(pillar.color)}>+ Add Vision</button>
      </div>
      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${pillar.color}`,borderRadius:12,padding:20,marginBottom:20}}>
          <h3 style={{margin:"0 0 14px",color:pillar.color}}>{pillar.icon} {editVisionId?"Edit":"New"} Vision — {pillar.label}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Vision Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="What's your vision?" style={{...inp(),width:"100%"}}/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Describe this vision..." style={ta(80)}/></div>
            <div><label style={lbl}>Reminder Date</label><input type="date" value={form.remDate} onChange={e=>setForm(p=>({...p,remDate:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Reminder Time</label><input type="time" value={form.remTime} onChange={e=>setForm(p=>({...p,remTime:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Vision Image (optional)</label>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <button onClick={()=>formImgRef.current?.click()} style={{...btn(C.purple),fontSize:12,padding:"6px 12px"}}>📷 Upload Image</button>
                <input ref={formImgRef} type="file" accept="image/*" onChange={handleFormImage} style={{display:"none"}}/>
                {formImage&&<>
                  <img src={formImage instanceof File ? formImage._previewUrl : formImage} alt="preview" style={{height:50,borderRadius:6,border:`1px solid ${C.border}`,objectFit:"cover"}}/>
                  <button onClick={()=>setFormImage(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.red,fontSize:11}}>✕ Remove</button>
                </>}
              </div>
            </div>
          </div>
          <div style={{marginTop:14}}>
            <label style={lbl}>Steps to Achieve</label>
            {form.steps.map((s,i)=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <span style={{fontSize:12,color:C.muted,minWidth:20}}>{i+1}.</span>
                <input value={s.text} onChange={e=>setForm(p=>({...p,steps:p.steps.map(x=>x.id===s.id?{...x,text:e.target.value}:x)}))} placeholder="Step..." style={{...inp(),flex:1}}/>
                <button onClick={()=>setForm(p=>({...p,steps:p.steps.filter(x=>x.id!==s.id)}))} style={{background:"none",border:"none",cursor:"pointer",color:"#999"}}>✕</button>
              </div>
            ))}
            <button onClick={()=>setForm(p=>({...p,steps:[...p.steps,{id:Date.now(),text:"",done:false}]}))} style={{...btn(pillar.color),fontSize:11,padding:"5px 10px"}}>+ Add Step</button>
          </div>
          <div style={{display:"flex",gap:8,marginTop:14}}>
            <button onClick={saveVision} style={btn(pillar.color)}>💾 {editVisionId?"Update":"Save"} Vision</button>
            <button onClick={()=>setShowForm(false)} style={btn("#999")}>Cancel</button>
          </div>
        </div>
      )}
      {pillarVisions.length===0&&<p style={{color:C.light,fontSize:13}}>No visions yet for {pillar.label}.</p>}
      <div style={{display:"grid",gap:14}}>
        {pillarVisions.map(v=>(
          <VisionCard key={v.id} v={v} pillar={pillar}
            isPinned={(dashboardVisions||[]).includes(v.id)}
            onTogglePin={()=>togglePin(v.id)}
            onToggleStep={(sId)=>toggleStep(v.id,sId)}
            onAddImage={(imgData)=>addImageToVision(v.id,imgData)}
            onRemoveImage={()=>removeImageFromVision(v.id)}
            onEdit={()=>{setEditVisionId(v.id);setForm({title:v.title,description:v.description||"",pillar:v.pillar,steps:v.steps||[],remDate:v.reminderDate||v.reminder_date||"",remTime:v.reminderTime||v.reminder_time||""});setFormImage(v.image||v.image_url||null);setShowForm(true);window.scrollTo({top:0,behavior:"smooth"});}}
            onDelete={()=>ctx.deleteVision(v.id)}
            onAddToGoals={()=>{setGoalPromptForm({startDate:TODAY,endDate:"",priority:2});setGoalPromptVision(v);}}
          />
        ))}
      </div>

      {/* Add vision to Goals — prompt for dates + priority, everything else carries over */}
      {goalPromptVision && (
        <div style={{position:'fixed',inset:0,zIndex:5000,background:'rgba(0,0,0,.55)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setGoalPromptVision(null)}>
          <div style={{background:C.surface,border:`1.5px solid ${C.blue}`,borderRadius:16,padding:24,width:'100%',maxWidth:420,boxShadow:'0 24px 80px rgba(0,0,0,.3)'}}>
            <h3 style={{margin:'0 0 4px',color:C.blue,fontSize:16}}>🎯 Add "{goalPromptVision.title}" to Goals</h3>
            <p style={{margin:'0 0 14px',fontSize:12,color:C.muted}}>{(goalPromptVision.steps||[]).length} step{(goalPromptVision.steps||[]).length!==1?'s':''} will carry over as goal steps.</p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <div><label style={lbl}>Start Date</label><input type="date" value={goalPromptForm.startDate} onChange={e=>setGoalPromptForm(p=>({...p,startDate:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>Finish Date</label><input type="date" value={goalPromptForm.endDate} onChange={e=>setGoalPromptForm(p=>({...p,endDate:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
              <div style={{gridColumn:'1/-1'}}><label style={lbl}>Priority (1=highest)</label><input type="number" min={1} max={10} value={goalPromptForm.priority} onChange={e=>setGoalPromptForm(p=>({...p,priority:Number(e.target.value)}))} style={{...inp(),width:'100%'}}/></div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setGoalPromptVision(null)} style={btn('#999')}>Cancel</button>
              <button onClick={submitVisionToGoal} style={btn(C.blue)}>💾 Add to Goals</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VisionCard({v,pillar,isPinned,onTogglePin,onToggleStep,onAddImage,onRemoveImage,onEdit,onDelete,onAddToGoals}){
  const vImgRef=useRef(null);
  const [viewFull,setViewFull]=useState(false);
  const handleVisionImage=(e)=>{
    const file=e.target.files[0]; if(!file)return;
    // Pass the raw File so useData uploads to Supabase Storage (not base64)
    onAddImage(file);
  };
  const ts=(v.steps||[]).length,ds=(v.steps||[]).filter(s=>s.done).length,sp=ts>0?Math.round(ds/ts*100):0;
  return(
    <div style={{background:C.surface,border:`1.5px solid ${isPinned?pillar.color:C.border}`,borderLeft:`4px solid ${pillar.color}`,borderRadius:10,padding:16,boxShadow:isPinned?`0 0 0 2px ${pillar.color}22`:"none"}}>
      {(v.image_url||v.image)&&(
        <div style={{position:"relative",marginBottom:12}}>
          <img onClick={()=>setViewFull(true)} src={v.image_url||v.image} alt={v.title} style={{width:"100%",maxHeight:220,objectFit:"cover",borderRadius:8,display:"block",cursor:"pointer"}} title="Click to view full size"/>
          <button onClick={onRemoveImage} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,.55)",border:"none",color:"#fff",borderRadius:4,padding:"2px 8px",cursor:"pointer",fontSize:11}}>✕ Remove</button>
        </div>
      )}
      {viewFull && ReactDOM.createPortal(
        <div onClick={()=>setViewFull(false)} style={{position:"fixed",inset:0,zIndex:4000,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <button onClick={()=>setViewFull(false)} style={{position:"fixed",top:20,right:24,background:"rgba(255,255,255,.15)",border:"none",color:"#fff",borderRadius:"50%",width:38,height:38,cursor:"pointer",fontSize:18,lineHeight:1}}>✕</button>
          <img src={v.image_url||v.image} alt={v.title} style={{maxWidth:"96vw",maxHeight:"94vh",width:"auto",height:"auto",objectFit:"contain",borderRadius:8,boxShadow:"0 20px 80px rgba(0,0,0,.5)"}}/>
        </div>,
        document.body
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div style={{flex:1}}>
          <h3 style={{margin:"0 0 6px",color:pillar.color,fontSize:15}}>{v.title}</h3>
          {v.description&&<p style={{margin:"0 0 10px",fontSize:12,color:C.muted}}>{v.description}</p>}
          <div style={{fontSize:10,color:C.light}}>Created: {v.createdAt}</div>
          {ts>0&&<div style={{marginTop:8}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Steps: {ds}/{ts} ({sp}%)</div>
            <div style={{background:C.border,borderRadius:4,height:6}}><div style={{width:`${sp}%`,background:pillar.color,height:"100%",borderRadius:4}}/></div>
            <div style={{marginTop:8}}>{v.steps.map((s,i)=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                <input type="checkbox" checked={s.done||false} onChange={()=>onToggleStep(s.id)} style={{accentColor:pillar.color}}/>
                <span style={{fontSize:12,textDecoration:s.done?"line-through":"none",color:s.done?C.light:C.text}}>{i+1}. {s.text}</span>
              </div>
            ))}</div>
          </div>}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginLeft:12,alignItems:"flex-end",flexShrink:0}}>
          <button onClick={onTogglePin} title={isPinned?"Unpin from dashboard":"Pin to dashboard"} style={{...btn(isPinned?pillar.color:"#bbb"),fontSize:11,padding:"4px 10px"}}>
            {isPinned?"📌 Pinned":"📌 Pin"}
          </button>
          {onEdit&&<button onClick={onEdit} style={{...btn(C.amber),fontSize:11,padding:"4px 10px"}}>✏️ Edit</button>}
          {onAddToGoals&&<button onClick={onAddToGoals} title="Add this vision to Goals" style={{...btn(C.blue),fontSize:11,padding:"4px 10px"}}>🎯 Add to Goals</button>}
          <button onClick={()=>vImgRef.current?.click()} style={{...btn(C.purple),fontSize:11,padding:"4px 10px"}}>
            {(v.image_url||v.image)?"🖼 Change":"📷 Image"}
          </button>
          <input ref={vImgRef} type="file" accept="image/*" onChange={handleVisionImage} style={{display:"none"}}/>
          <button onClick={onDelete} style={btn(C.red,30)}>🗑</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// WEEKLY PLANNER
// ══════════════════════════════════════════════════════════════════════════════
// Self-contained task input for each day cell — avoids stale closure on taskMap
function WeekDayTaskInput({ wd, ctx, C, FONT }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const add = () => {
    const val = text.trim();
    if (!val) return;
    ctx.addTask(wd, val);
    setText('');
    if (inputRef.current) inputRef.current.focus();
  };
  return (
    <div style={{display:"flex",gap:3,marginTop:5}}>
      <input
        ref={inputRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder="+ task"
        style={{flex:1,fontSize:10,padding:"3px 5px",border:"none",borderRadius:"var(--r-sm)",fontFamily:"var(--font-body)",background:"var(--ta-surface-high)",color:"var(--ta-on-surface)",outline:"none"}}
      />
      <button
        onMouseDown={e => { e.preventDefault(); add(); }}
        style={{background:"var(--ta-primary)",color:"var(--ta-on-primary)",border:"none",borderRadius:"var(--r-sm)",padding:"2px 6px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}
      >+</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HABITS SECTION — standalone page for managing habits
// ══════════════════════════════════════════════════════════════════════════════
function HabitsSection({ ctx }) {
  const C = useC();
  const { habits, habitLog, addHabit: ctxAddHabit, deleteHabit, toggleHabit, archiveHabit, unarchiveHabit, loadHabitLogForDate } = ctx;
  const [newHabit, setNewHabit] = useState({ name: "", icon: "" });

  // Load last 7 days of habit log for streak display
  useEffect(() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - i); return fmt(d);
    });
    days.forEach(d => loadHabitLogForDate && loadHabitLogForDate(d));
  }, []); // eslint-disable-line

  const addHabit = () => {
    if (!newHabit.name.trim()) return;
    ctxAddHabit({ name: newHabit.name, icon: newHabit.icon });
    setNewHabit({ name: "", icon: "" });
  };

  // Compute streak for a habit
  const getStreak = (hid) => {
    let s = 0, d = new Date();
    while (habitLog[`${fmt(d)}:${hid}`]) { s++; d.setDate(d.getDate() - 1); }
    return s;
  };

  // Last 7 days for mini trend
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i)); return fmt(d);
  });

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      <SectionMasthead
        eyebrow={`Personal · ${habits.filter(h => !h.archived).length} active habit${habits.filter(h => !h.archived).length !== 1 ? "s" : ""}`}
        title={<>You are what you <em>repeat</em>.</>}
        sub="Build streaks, track consistency, stay accountable."
      />

      {/* Add new habit */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 16, marginBottom: 20, boxShadow: "var(--shadow-sm)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>Add New Habit</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={newHabit.icon}
            onChange={e => setNewHabit(p => ({ ...p, icon: e.target.value }))}
            placeholder="🔷"
            style={{ ...inp(), width: 56, textAlign: "center", fontSize: 18 }}
          />
          <input
            value={newHabit.name}
            onChange={e => setNewHabit(p => ({ ...p, name: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && addHabit()}
            placeholder="Habit name (e.g. Read 30 mins, Exercise, Meditate)..."
            style={{ ...inp(), flex: 1 }}
          />
          <button onClick={addHabit} style={{ ...btn(C.red), padding: "8px 20px", flexShrink: 0 }}>+ Add</button>
        </div>
      </div>

      {/* Active habits grid */}
      {habits.filter(h => !h.archived).length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 24px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: C.muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>No habits yet</div>
          <div style={{ fontSize: 13 }}>Add your first habit above to start building streaks.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px,1fr))", gap: 12, marginBottom: 24 }}>
          {habits.filter(h => !h.archived).map(h => {
            const streak = getStreak(h.id);
            const todayDone = habitLog[`${fmt(new Date())}:${h.id}`];
            return (
              <div key={h.id} style={{ background: "var(--surface)", border: `1px solid ${todayDone ? C.green : "var(--border)"}`, borderRadius: "var(--radius)", padding: 16, boxShadow: "var(--shadow-sm)", transition: "all var(--t)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: todayDone ? C.greenM : "var(--surface-alt)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, border: `2px solid ${todayDone ? C.green : "var(--border)"}`, transition: "all var(--t)" }}>
                      {h.icon || "🔷"}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{h.name}</div>
                      <div style={{ fontSize: 11, color: streak > 0 ? C.amber : C.muted, fontWeight: 600, marginTop: 2 }}>
                        {streak > 0 ? `🔥 ${streak}-day streak` : "No streak yet today"}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => toggleHabit(fmt(new Date()), h.id)}
                      style={{ padding: "5px 10px", borderRadius: 8, border: `2px solid ${todayDone ? C.green : "var(--border)"}`, background: todayDone ? C.greenM : "transparent", color: todayDone ? C.green : C.muted, cursor: "pointer", fontSize: 11, fontWeight: 700, transition: "all .12s" }}
                    >
                      {todayDone ? "✓ Done" : "Mark Today"}
                    </button>
                  </div>
                </div>

                {/* Last 7 days mini trend */}
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {last7.map((d, i) => {
                    const done = habitLog[`${d}:${h.id}`];
                    const isToday = d === fmt(new Date());
                    return (
                      <div key={d} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ width: "100%", aspectRatio: "1", borderRadius: 4, background: done ? C.green : "var(--bg3)", border: isToday ? `2px solid ${C.green}` : "2px solid transparent", transition: "background .12s" }} />
                        <div style={{ fontSize: 8, color: C.light, marginTop: 2 }}>{["S","M","T","W","T","F","S"][new Date(d+"T00:00:00").getDay()]}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                  <button onClick={() => archiveHabit && archiveHabit(h.id)} style={{ flex: 1, padding: "5px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 11 }}>📦 Archive</button>
                  <button onClick={() => { if (window.confirm(`Permanently delete "${h.name}"? All history will be lost.`)) deleteHabit && deleteHabit(h.id); }} style={{ flex: 1, padding: "5px", borderRadius: 7, border: `1px solid ${C.red}22`, background: C.redL, color: C.red, cursor: "pointer", fontSize: 11 }}>🗑 Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Archived habits */}
      {habits.filter(h => h.archived).length > 0 && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 16, boxShadow: "var(--shadow-sm)" }}>
          <details>
            <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 4 }}>
              📦 {habits.filter(h => h.archived).length} Archived Habit{habits.filter(h => h.archived).length !== 1 ? "s" : ""} (history preserved)
            </summary>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {habits.filter(h => h.archived).map(h => (
                <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 20, padding: "6px 14px", fontSize: 12, color: C.muted }}>
                  {h.icon || "🔷"} {h.name}
                  <button onClick={() => unarchiveHabit && unarchiveHabit(h.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.green, fontSize: 11, fontWeight: 700 }}>↩ Restore</button>
                  <button onClick={() => { if (window.confirm(`Delete "${h.name}"?`)) deleteHabit && deleteHabit(h.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 11 }}>🗑</button>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function WeeklyPlanner({ctx}){
  const C = useC();
  const {tasks,weekPlan,habits,habitLog,loadHabitLogForDate,addHabit:ctxAddHabit,deleteHabit,toggleHabit,loadWeekPlan,archiveHabit,unarchiveHabit}=ctx;
  // Load week plan from DB on mount and when week changes
  const now=new Date();
  // Monday-first: shift so week starts Mon
  const [ws,setWs]=useState(()=>{
    const d=new Date(now); const dow=d.getDay();
    d.setDate(d.getDate()-(dow===0?6:dow-1)); return fmt(d);
  });
  const [newHabit,setNewHabit]=useState({name:"",icon:""});
  const [showMultiDay,setShowMultiDay]=useState(false);
  const [multiDayText,setMultiDayText]=useState('');
  const [multiDaySelected,setMultiDaySelected]=useState([]);
  const [view,setView]=useState('week'); // week | matrix
  const weekDays=Array.from({length:7},(_,i)=>{const d=new Date(ws+"T00:00:00");d.setDate(d.getDate()+i);return fmt(d);});
  const wk=ws;

  // Eisenhower placement for the matrix view — urgent = due today/tomorrow, important = high/critical priority
  const tomorrow = fmt(new Date(new Date(TODAY+'T00:00:00').getTime()+86400000));
  const quadOf = (wd,t) => {
    const urgent = wd===TODAY || wd===tomorrow;
    const important = t.priority==='critical' || t.priority==='high';
    if (urgent && important)  return 'do';
    if (!urgent && important) return 'schedule';
    if (urgent && !important) return 'delegate';
    return 'drop';
  };
  const QUADS = {
    do:       { label:'Do — now',      sub:'Urgent · Important',      tone:'var(--ta-negative)' },
    schedule: { label:'Schedule',      sub:'Important · Not urgent',  tone:'var(--ta-primary)'  },
    delegate: { label:'Delegate',      sub:'Urgent · Not important',  tone:'var(--ta-tertiary)' },
    drop:     { label:'Drop or batch', sub:'Neither',                 tone:'var(--ta-on-surface-muted)' },
  };
  const weekTasksFlat = weekDays.flatMap(wd => (tasks[wd]||[]).filter(t=>!t.done).map(t => ({ ...t, wd })));

  // Load habit log for ALL 7 days whenever the week changes — fixes vanishing trend
  useEffect(()=>{ weekDays.forEach(d=>loadHabitLogForDate(d)); },[ws]); // eslint-disable-line
  useEffect(()=>{ if(loadWeekPlan) loadWeekPlan(wk); },[wk]); // eslint-disable-line

  const addHabit=()=>{if(!newHabit.name.trim())return;ctxAddHabit({name:newHabit.name,icon:newHabit.icon});setNewHabit({name:"",icon:""}); };

  return(
    <div>
      {/* ── EDITORIAL MASTHEAD — same treatment as Tasks ── */}
      <div className="ta-row ta-between" style={{alignItems:'flex-end',marginBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Execution · Week view</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Plan the <em>week</em>.</h1>
        </div>
        <div className="ta-row ta-gap-2" style={{flexWrap:'wrap'}}>
          <div className="ta-row ta-gap-1" style={{background:'var(--ta-surface-low)',padding:3,borderRadius:'var(--r-md)'}}>
            {['week','matrix'].map(v=>(
              <button key={v} onClick={()=>setView(v)} className="ta-mono" style={{
                background: view===v ? 'var(--ta-surface-lowest)' : 'transparent',
                color: view===v ? 'var(--ta-on-surface)' : 'var(--ta-on-surface-muted)',
                border:'none', padding:'6px 12px', fontSize:11, letterSpacing:'.12em',
                textTransform:'uppercase', cursor:'pointer', borderRadius:4, fontWeight:600,
              }}>{v}</button>
            ))}
          </div>
          <button onClick={()=>setShowMultiDay(v=>!v)} className="ta-btn ta-btn-secondary">+ Add to multiple days</button>
          <div className="ta-row ta-gap-1" style={{background:'var(--ta-surface-low)',padding:3,borderRadius:'var(--r-md)'}}>
            <button onClick={()=>{const d=new Date(ws+"T00:00:00");d.setDate(d.getDate()-7);setWs(fmt(d));}} className="ta-mono" style={{padding:'6px 12px',border:'none',background:'transparent',color:'var(--ta-on-surface-variant)',cursor:'pointer',fontSize:11,borderRadius:'var(--r-sm)'}}>‹ Prev</button>
            <span className="ta-mono" style={{padding:'6px 10px',fontSize:11,color:'var(--ta-on-surface)',fontWeight:600}}>{ws} – {weekDays[6]}</span>
            <button onClick={()=>{const d=new Date(ws+"T00:00:00");d.setDate(d.getDate()+7);setWs(fmt(d));}} className="ta-mono" style={{padding:'6px 12px',border:'none',background:'transparent',color:'var(--ta-on-surface-variant)',cursor:'pointer',fontSize:11,borderRadius:'var(--r-sm)'}}>Next ›</button>
          </div>
        </div>
      </div>

      {showMultiDay&&(
        <div style={{background:C.surface,border:'1.5px solid #4F46E5',borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontWeight:700,fontSize:13,color:'#4F46E5',marginBottom:10}}>📋 Add Task to Multiple Days</div>
          <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
            <input value={multiDayText} onChange={e=>setMultiDayText(e.target.value)} placeholder="Task name..." style={{...inp(),flex:1,minWidth:160}}/>
            <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
              {[{label:'All 7',days:[0,1,2,3,4,5,6]},{label:'Mon–Fri',days:[0,1,2,3,4]},{label:'Weekends',days:[5,6]}].map(preset=>(
                <button key={preset.label} onClick={()=>setMultiDaySelected(preset.days)} style={{fontSize:11,padding:'5px 10px',borderRadius:20,border:'1.5px solid #4F46E5',background:'#eef2ff',color:'#4F46E5',cursor:'pointer',fontWeight:600}}>{preset.label}</button>
              ))}
            </div>
          </div>
          <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
            {weekDays.map((wd,i)=>(
              <button key={wd} onClick={()=>setMultiDaySelected(prev=>prev.includes(i)?prev.filter(x=>x!==i):[...prev,i])}
                style={{padding:'6px 12px',borderRadius:8,border:`1.5px solid ${multiDaySelected.includes(i)?'#4F46E5':C.border}`,background:multiDaySelected.includes(i)?'#4F46E5':C.surface,color:multiDaySelected.includes(i)?'#fff':C.text,cursor:'pointer',fontWeight:600,fontSize:12}}>
                {MON_NAMES[i]} {new Date(wd+'T00:00:00').getDate()}
              </button>
            ))}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={async()=>{
              if(!multiDayText.trim()||multiDaySelected.length===0)return;
              for(const i of multiDaySelected){ await ctx.addTask(weekDays[i], multiDayText.trim()); }
              setMultiDayText('');setMultiDaySelected([]);setShowMultiDay(false);
            }} style={{...btn('#4F46E5'),fontSize:12}}>
              ✅ Add to {multiDaySelected.length} day{multiDaySelected.length!==1?'s':''}
            </button>
            <button onClick={()=>{setShowMultiDay(false);setMultiDayText('');setMultiDaySelected([]);}} style={{...btn('#888'),fontSize:12}}>Cancel</button>
          </div>
        </div>
      )}

      {view==='matrix' ? (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:14}}>
          {Object.entries(QUADS).map(([k,info])=>{
            const ts = weekTasksFlat.filter(t=>quadOf(t.wd,t)===k);
            return (
              <div key={k} className="ta-card-inset" style={{minHeight:220}}>
                <div className="ta-row ta-between" style={{alignItems:'baseline',marginBottom:14}}>
                  <div>
                    <div className="ta-serif" style={{fontSize:20,color:info.tone,fontWeight:500,letterSpacing:'-.01em'}}>{info.label}</div>
                    <div className="ta-mono" style={{fontSize:10,letterSpacing:'.16em',textTransform:'uppercase',color:'var(--ta-on-surface-muted)',marginTop:4}}>{info.sub}</div>
                  </div>
                  <span className="ta-serif ta-tnum" style={{fontSize:28,fontWeight:300,color:info.tone}}>{ts.length}</span>
                </div>
                <div className="ta-col ta-gap-2">
                  {ts.map(t=>(
                    <div key={t.wd+t.id} className="ta-row ta-between" style={{padding:'8px 12px',background:'var(--ta-surface-lowest)',borderRadius:'var(--r-md)'}}>
                      <div className="ta-row ta-gap-2" style={{alignItems:'baseline'}}>
                        <span className="ta-mono" style={{fontSize:9,color:info.tone,fontWeight:700,letterSpacing:'.1em'}}>{(t.priority||'normal').toUpperCase()}</span>
                        <span style={{fontSize:13}}>{t.text}</span>
                      </div>
                      <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)'}}>{MON_NAMES[weekDays.indexOf(t.wd)]}</span>
                    </div>
                  ))}
                  {ts.length===0&&<div className="ta-serif ta-italic" style={{fontSize:13,color:'var(--ta-on-surface-muted)'}}>Nothing here. That's a quiet kind of progress.</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (<>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
        <Card title="🎯 Week Objective" color={C.green}><textarea value={(weekPlan[wk]||{}).objective||""} onChange={e=>ctx.updateWeekPlan(wk,{objective:e.target.value})} placeholder="Main focus this week..." style={ta(70)}/></Card>
        <Card title="⭐ Top Priority This Week" color={C.amber}><textarea value={(weekPlan[wk]||{}).topPriority||""} onChange={e=>ctx.updateWeekPlan(wk,{topPriority:e.target.value})} placeholder="The #1 priority for this week..." style={ta(70)}/></Card>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:8,marginBottom:14}}>
        {weekDays.map((wd,i)=>{
          const isToday=wd===TODAY,dayTasks=tasks[wd]||[];
          return(
            <div key={wd} style={{background:isToday?"rgba(36,56,156,0.05)":"var(--ta-surface-lowest)",borderRadius:"var(--r-md)",padding:9,display:"flex",flexDirection:"column",boxShadow:"0 1px 2px rgba(26,28,28,.04)"}}>
              <div style={{textAlign:"center",marginBottom:6,flexShrink:0}}>
                <div className="ta-mono" style={{fontSize:10,letterSpacing:".1em",color:isToday?"var(--ta-primary)":"var(--ta-on-surface-muted)"}}>{MON_NAMES[i].toUpperCase()}</div>
                <div className="ta-serif ta-tnum" style={{fontSize:20,fontWeight:400,color:isToday?"var(--ta-primary)":"var(--ta-on-surface)"}}>{new Date(wd+"T00:00:00").getDate()}</div>
              </div>
              {/* Dedicated, static height — scrolls once tasks exceed it, same pattern as the dashboard kanban */}
              <div style={{height:150,overflowY:"auto",marginBottom:6}}>
                {dayTasks.map(t=>(
                  <div key={t.id} style={{display:"flex",alignItems:"flex-start",gap:3,marginBottom:3}}>
                    <input type="checkbox" checked={t.done} onChange={()=>ctx.updateTask(wd,t.id,{done:!t.done})} style={{accentColor:"var(--ta-primary)",marginTop:2}}/>
                    <span style={{fontSize:12,textDecoration:t.done?"line-through":"none",color:t.done?"var(--ta-on-surface-muted)":"var(--ta-on-surface)"}}>{t.text}</span>
                  </div>
                ))}
              </div>
              <WeekDayTaskInput wd={wd} ctx={ctx} C={C} FONT={FONT} />
            </div>
          );
        })}
      </div>
      <Card title="🔥 This Week's Habits" color={C.red}>
        {habits.filter(h=>!h.archived).length===0
          ?<p style={{fontSize:12,color:C.light,margin:0}}>No habits yet. Go to <strong>Habits</strong> in the sidebar to add some.</p>
          :<div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",fontSize:11,width:"100%",tableLayout:"fixed"}}>
              <colgroup><col style={{width:"28%"}}/>{weekDays.map(wd=><col key={wd}/>)}</colgroup>
              <thead><tr><th style={{padding:"4px 8px",textAlign:"left",color:C.muted}}>Habit</th>{weekDays.map((wd,i)=><th key={wd} style={{padding:"4px 6px",textAlign:"center",color:wd===TODAY?C.green:C.muted,fontWeight:wd===TODAY?800:500}}>{MON_NAMES[i]}</th>)}</tr></thead>
            </table>
            {/* Dedicated, static height for ~5 habit rows — scrolls past that */}
            <div style={{height:190,overflowY:"auto"}}>
              <table style={{borderCollapse:"collapse",fontSize:11,width:"100%",tableLayout:"fixed"}}>
                <colgroup><col style={{width:"28%"}}/>{weekDays.map(wd=><col key={wd}/>)}</colgroup>
                <tbody>{habits.filter(h=>!h.archived).map(h=>(
                  <tr key={h.id}>
                    <td style={{padding:"4px 8px",color:C.text,fontWeight:500}}>{h.icon||"🔷"} {h.name}</td>
                    {weekDays.map(wd=>{const done=habitLog[`${wd}:${h.id}`];return<td key={wd} style={{padding:"4px 6px",textAlign:"center"}}><div onClick={()=>{ if(wd!==TODAY){return;} toggleHabit(wd,h.id); }} title={wd===TODAY?'Click to toggle':'Past dates locked 🔒'} style={{width:20,height:20,borderRadius:"50%",background:done?C.red:"var(--bg3)",margin:"0 auto",cursor:wd===TODAY?"pointer":"not-allowed",transition:"background .12s",border:wd===TODAY&&!done?`2px solid ${C.red}`:"2px solid transparent"}}/></td>;})}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        }
      </Card>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:14}}>
        <Card title="🏆 Wins" color={C.greenL}><textarea value={(weekPlan[wk]||{}).wins||""} onChange={e=>ctx.updateWeekPlan(wk,{wins:e.target.value})} placeholder="What went well?" style={ta(80)}/></Card>
        <Card title="📖 Learnings" color={C.blue}><textarea value={(weekPlan[wk]||{}).learnings||""} onChange={e=>ctx.updateWeekPlan(wk,{learnings:e.target.value})} placeholder="What did you learn?" style={ta(80)}/></Card>
      </div>
      </>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MONTHLY PLANNER
// ══════════════════════════════════════════════════════════════════════════════
// ── RecurringTaskModal ─────────────────────────────────────────────────────
function RecurringTaskModal({ task, onSave, onClose }) {
  const blank = { title:'', start_date:TODAY, end_date:'', start_time:'', end_time:'', color:'#4F46E5', repeat_type:'daily', repeat_days:[], add_to_daily:false, notes:'' };
  const [form, setForm] = useState(task ? { ...blank, ...task, repeat_days: task.repeat_days||[] } : blank);
  const upd = (k,v) => setForm(p => ({...p, [k]:v}));
  const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const COLORS = ['#4F46E5','#DC2626','#16A34A','#D97706','#7C3AED','#0891B2','#BE185D','#374151'];
  const toggleDay = d => upd('repeat_days', form.repeat_days.includes(d) ? form.repeat_days.filter(x=>x!==d) : [...form.repeat_days, d]);
  return (
    <div style={{position:'fixed',inset:0,zIndex:8000,background:'rgba(0,0,0,.55)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:480,boxShadow:'0 16px 60px rgba(0,0,0,.3)',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <h3 style={{margin:0,color:'#1e293b',fontSize:16,fontWeight:700}}>{task ? '✏️ Edit' : '+ New'} Recurring Event</h3>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#94a3b8'}}>✕</button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div><label style={lbl}>Title *</label><input value={form.title} onChange={e=>upd('title',e.target.value)} placeholder="e.g. Team Standup, Morning Run..." style={{...inp(),width:'100%'}} autoFocus/></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={lbl}>Start Date *</label><input type='date' value={form.start_date} onChange={e=>upd('start_date',e.target.value)} style={{...inp(),width:'100%'}}/></div>
            <div><label style={lbl}>End Date (leave blank = forever)</label><input type='date' value={form.end_date} onChange={e=>upd('end_date',e.target.value)} style={{...inp(),width:'100%'}}/></div>
            <div><label style={lbl}>Start Time</label><input type='time' value={form.start_time} onChange={e=>upd('start_time',e.target.value)} style={{...inp(),width:'100%'}}/></div>
            <div><label style={lbl}>End Time</label><input type='time' value={form.end_time} onChange={e=>upd('end_time',e.target.value)} style={{...inp(),width:'100%'}}/></div>
          </div>
          <div>
            <label style={lbl}>Repeat</label>
            <div style={{display:'flex',gap:6}}>
              {['daily','weekly','monthly'].map(r=>(
                <button key={r} onClick={()=>upd('repeat_type',r)} style={{flex:1,padding:'6px',borderRadius:7,border:`2px solid ${form.repeat_type===r?'#4F46E5':'#e2e8f0'}`,background:form.repeat_type===r?'#eef2ff':'#fff',color:form.repeat_type===r?'#4F46E5':'#64748b',cursor:'pointer',fontSize:12,fontWeight:form.repeat_type===r?700:400,textTransform:'capitalize'}}>{r}</button>
              ))}
            </div>
          </div>
          {form.repeat_type==='weekly' && (
            <div>
              <label style={lbl}>On Days</label>
              <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                {DAY_LABELS.map((d,i)=>(
                  <button key={i} onClick={()=>toggleDay(i+1)} style={{padding:'5px 10px',borderRadius:20,border:`2px solid ${form.repeat_days.includes(i+1)?'#4F46E5':'#e2e8f0'}`,background:form.repeat_days.includes(i+1)?'#4F46E5':'#fff',color:form.repeat_days.includes(i+1)?'#fff':'#64748b',cursor:'pointer',fontSize:11,fontWeight:600}}>{d}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label style={lbl}>Color</label>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {COLORS.map(c=><button key={c} onClick={()=>upd('color',c)} style={{width:26,height:26,borderRadius:'50%',background:c,border:form.color===c?'3px solid #1e293b':'2px solid transparent',cursor:'pointer'}}/>)}
            </div>
          </div>
          <div><label style={lbl}>Notes</label><textarea value={form.notes} onChange={e=>upd('notes',e.target.value)} placeholder="Optional notes..." style={{...ta(48),width:'100%'}}/></div>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#475569',cursor:'pointer',userSelect:'none'}}>
            <input type='checkbox' checked={form.add_to_daily} onChange={e=>upd('add_to_daily',e.target.checked)} style={{accentColor:'#4F46E5'}}/>
            Auto-add to Daily Tasks on matching days
          </label>
        </div>
        <div style={{display:'flex',gap:8,marginTop:20,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{...btn('#94a3b8'),padding:'8px 18px'}}>Cancel</button>
          <button onClick={()=>{ if(!form.title.trim()||!form.start_date) return; const clean={...form}; if(!clean.end_date)delete clean.end_date; if(!clean.start_time)delete clean.start_time; if(!clean.end_time)delete clean.end_time; onSave(clean); onClose(); }} style={{...btn('#4F46E5'),padding:'8px 20px',fontWeight:700}}>Save Event</button>
        </div>
      </div>
    </div>
  );
}

// ── CalendarTemplateModal — create & share standalone calendar templates ──
// These are SEPARATE from personal recurring tasks — creating a template
// does NOT add anything to your personal calendar.
function CalendarTemplateModal({ addSharedTemplate, deleteSharedTemplate, hardDeleteSharedTemplate, sharedTemplates, fetchTemplateById, addRecurringTask, setAlert, onClose }) {
  const [tab, setTab] = useState('mine');
  const myCalTemplates = (sharedTemplates||[]).filter(t => t.type === 'calendar');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [events, setEvents] = useState([]); // events inside the template only
  const [saved, setSaved] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // template to preview before importing
  const COLORS = ['#4F46E5','#DC2626','#16A34A','#D97706','#7C3AED','#0891B2','#BE185D','#374151'];
  const blankEv = { id: Date.now(), title:'', repeat_type:'daily', start_date:'', end_date:'', start_time:'', end_time:'', color:'#4F46E5', notes:'' };
  const [draftEv, setDraftEv] = useState(blankEv);
  const updEv = (k,v) => setDraftEv(p=>({...p,[k]:v}));
  const addEvent = () => {
    if (!draftEv.title.trim() || !draftEv.start_date) { setAlert('⚠️ Event needs a title and start date.'); return; }
    setEvents(p => [...p, { ...draftEv, id: Date.now() + Math.random() }]);
    setDraftEv({ ...blankEv, id: Date.now() });
  };

  const handleCreate = async () => {
    if (!name.trim() || events.length === 0) { setAlert('⚠️ Add a name and at least one event.'); return; }
    const tmpl = await addSharedTemplate({ type:'calendar', title:name, description:desc, data:{ events }, is_public:false });
    if (tmpl?.id) setSaved({ url:`${window.location.origin}/?import_calendar=${tmpl.id}`, name });
  };

  const handleFetchPreview = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    const match = importUrl.match(/import_calendar=([a-zA-Z0-9_-]+)/);
    const id = match?.[1] || importUrl.trim();
    const tmpl = await fetchTemplateById(id);
    if (!tmpl || tmpl.type !== 'calendar') { setAlert('❌ Calendar not found. Check the link.'); setImportLoading(false); return; }
    setImportPreview(tmpl);
    setImportLoading(false);
  };

  const handleImport = () => {
    if (!importPreview) return;
    const evs = importPreview.data?.events || [];
    // Strip IDs so they get new ones when saved
    evs.forEach(ev => { const { id:_, ...rest } = ev; addRecurringTask(rest); });
    setAlert(`✅ "${importPreview.title}" imported — ${evs.length} events added to your planner!`);
    onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:8500,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'#fff',borderRadius:18,padding:24,width:'100%',maxWidth:560,boxShadow:'0 20px 60px rgba(0,0,0,.35)',maxHeight:'92vh',overflowY:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{margin:0,color:'#1e293b',fontSize:16,fontWeight:700}}>📅 Calendar Templates</h3>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#94a3b8'}}>✕</button>
        </div>
        <div style={{display:'flex',gap:0,marginBottom:20,background:'#f1f5f9',borderRadius:10,padding:3}}>
          {[['mine',`🗂 Mine (${myCalTemplates.length})`],['create','🛠 Create'],['import','📥 Import']].map(([id,label])=>(
            <button key={id} onClick={()=>{setTab(id);setSaved(null);}} style={{flex:1,padding:'7px',borderRadius:8,border:'none',background:tab===id?'#fff':'transparent',color:tab===id?'#4F46E5':'#64748b',fontWeight:tab===id?700:400,cursor:'pointer',fontSize:11,boxShadow:tab===id?'0 1px 4px rgba(0,0,0,.1)':'',transition:'all .15s'}}>{label}</button>
          ))}
        </div>

        {/* ── MY CALENDARS TAB ── */}
        {tab==='mine' && (
          <div>
            <div style={{fontSize:12,color:'#64748b',marginBottom:14,lineHeight:1.6}}>Your saved calendar templates — reuse them, copy their share link, or delete them.</div>
            {myCalTemplates.length === 0 ? (
              <div style={{background:'#f8fafc',borderRadius:12,padding:'24px',textAlign:'center'}}>
                <div style={{fontSize:32,marginBottom:8}}>📭</div>
                <div style={{fontSize:13,color:'#64748b',marginBottom:12}}>No calendar templates yet.</div>
                <button onClick={()=>setTab('create')} style={{...btn('#4F46E5'),fontSize:12}}>🛠 Create Your First</button>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {myCalTemplates.map(t=>(
                  <div key={t.id} style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderLeft:'3px solid #4F46E5',borderRadius:10,padding:'12px 14px'}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:'#1e293b'}}>{t.title}</div>
                        {t.description&&<div style={{fontSize:11,color:'#64748b',marginTop:2}}>{t.description}</div>}
                        <div style={{fontSize:10,color:'#94a3b8',marginTop:5,display:'flex',gap:10,flexWrap:'wrap'}}>
                          <span>📅 {new Date(t.created_at).toLocaleDateString()}</span>
                          <span>🗓 {(t.data?.events||[]).length} events</span>
                          <span>📥 {t.import_count||0} imports</span>
                          <span style={{color:t.is_public?'#16a34a':'#94a3b8'}}>{t.is_public?'🔗 Active':'🔒 Off'}</span>
                        </div>
                        <div style={{marginTop:6,display:'flex',gap:4,flexWrap:'wrap'}}>
                          {(t.data?.events||[]).slice(0,4).map((ev,i)=>(
                            <span key={i} style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:ev.color+'22',color:ev.color,fontWeight:600}}>{ev.title}</span>
                          ))}
                          {(t.data?.events||[]).length>4&&<span style={{fontSize:9,color:'#94a3b8'}}>+{t.data.events.length-4} more</span>}
                        </div>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:4,flexShrink:0}}>
                        <button onClick={()=>{(t.data?.events||[]).forEach(ev=>{const{id:_,...r}=ev;addRecurringTask(r);});setAlert(`✅ "${t.title}" added to your planner!`);onClose();}} style={{...btn('#16a34a'),fontSize:10,padding:'4px 8px'}}>📥 Use</button>
                        <button onClick={()=>{navigator.clipboard.writeText(`${window.location.origin}/?import_calendar=${t.id}`).then(()=>setAlert('✅ Link copied!'));}} style={{...btn('#4F46E5'),fontSize:10,padding:'4px 8px'}}>🔗 Link</button>
                        <button onClick={()=>deleteSharedTemplate(t.id)} style={{...btn('#6B7280'),fontSize:10,padding:'4px 8px'}} title="Deactivate link">🔒 Off</button>
                        <button onClick={()=>{if(window.confirm(`Permanently delete "${t.title}"?`))hardDeleteSharedTemplate(t.id);}} style={{...btn('#dc2626'),fontSize:10,padding:'4px 8px'}} title="Permanently delete">🗑</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CREATE TAB ── */}
        {tab==='create' && !saved && (<>
          <div style={{background:'#eef2ff',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#4338ca',lineHeight:1.6}}>
            💡 Build a standalone calendar (workout plan, diet, training program…). Events are <strong>only stored in the template</strong> — they won't appear in your personal calendar until imported.
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            <div style={{gridColumn:'1/-1'}}><label style={lbl}>Template Name *</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. 30-Day Fat Burn, Vegan Meal Plan…" style={{...inp(),width:'100%'}} autoFocus/></div>
            <div style={{gridColumn:'1/-1'}}><label style={lbl}>Description</label><textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="What's included? Who is this for?" style={{...ta(44),width:'100%'}}/></div>
          </div>

          {/* Add event form */}
          <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:12,padding:14,marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:10}}>➕ Add Event to Template</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div style={{gridColumn:'1/-1'}}><label style={lbl}>Title *</label><input value={draftEv.title} onChange={e=>updEv('title',e.target.value)} placeholder="e.g. Morning HIIT, Protein Meal…" style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>Start Date *</label><input type='date' value={draftEv.start_date} onChange={e=>updEv('start_date',e.target.value)} style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>End Date</label><input type='date' value={draftEv.end_date} onChange={e=>updEv('end_date',e.target.value)} style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>Start Time</label><input type='time' value={draftEv.start_time} onChange={e=>updEv('start_time',e.target.value)} style={{...inp(),width:'100%'}}/></div>
              <div><label style={lbl}>End Time</label><input type='time' value={draftEv.end_time} onChange={e=>updEv('end_time',e.target.value)} style={{...inp(),width:'100%'}}/></div>
              <div style={{gridColumn:'1/-1'}}><label style={lbl}>Repeat</label>
                <div style={{display:'flex',gap:5}}>
                  {['daily','weekly','monthly'].map(r=><button key={r} onClick={()=>updEv('repeat_type',r)} style={{flex:1,padding:'5px',borderRadius:6,border:`2px solid ${draftEv.repeat_type===r?'#4F46E5':'#e2e8f0'}`,background:draftEv.repeat_type===r?'#eef2ff':'#fff',color:draftEv.repeat_type===r?'#4F46E5':'#64748b',cursor:'pointer',fontSize:11,fontWeight:draftEv.repeat_type===r?700:400,textTransform:'capitalize'}}>{r}</button>)}
                </div>
              </div>
              <div style={{gridColumn:'1/-1'}}><label style={lbl}>Color</label>
                <div style={{display:'flex',gap:5}}>{COLORS.map(c=><button key={c} onClick={()=>updEv('color',c)} style={{width:22,height:22,borderRadius:'50%',background:c,border:draftEv.color===c?'3px solid #1e293b':'2px solid transparent',cursor:'pointer'}}/>)}</div>
              </div>
            </div>
            <button onClick={addEvent} style={{...btn('#4F46E5'),marginTop:10,width:'100%',fontWeight:700}}>+ Add to Template</button>
          </div>

          {/* Events list */}
          {events.length > 0 && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8}}>Events in this template ({events.length})</div>
              {events.map((ev,i)=>(
                <div key={ev.id} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:8,background:'#f8fafc',borderLeft:`3px solid ${ev.color}`,marginBottom:5}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:'#1e293b'}}>{ev.title}</div>
                    <div style={{fontSize:10,color:'#94a3b8'}}>{ev.repeat_type} · {ev.start_date}{ev.end_date?` → ${ev.end_date}`:''}{ev.start_time?` · ${ev.start_time.slice(0,5)}`:''}</div>
                  </div>
                  <button onClick={()=>setEvents(p=>p.filter(x=>x.id!==ev.id))} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:14}}>✕</button>
                </div>
              ))}
            </div>
          )}
          <button onClick={handleCreate} disabled={!name.trim()||events.length===0} style={{...btn('#4F46E5'),width:'100%',padding:'11px',fontWeight:700,fontSize:13,opacity:!name.trim()||events.length===0?0.4:1}}>🔗 Create Shareable Template Link</button>
        </>)}

        {/* ── SAVED STATE ── */}
        {tab==='create' && saved && (
          <div style={{textAlign:'center',padding:'10px 0'}}>
            <div style={{fontSize:44,marginBottom:10}}>🎉</div>
            <h4 style={{margin:'0 0 6px',color:'#1e293b',fontSize:16}}>"{saved.name}" Created!</h4>
            <p style={{fontSize:12,color:'#64748b',margin:'0 0 18px',lineHeight:1.6}}>Anyone with this link can import your calendar template into their Aspreo Life planner. Perfect for selling training plans, courses, or routines!</p>
            <div style={{background:'#f1f5f9',borderRadius:10,padding:'10px 14px',marginBottom:14,display:'flex',gap:8,alignItems:'center',textAlign:'left'}}>
              <span style={{fontSize:11,flex:1,wordBreak:'break-all',fontFamily:'monospace',color:'#1e293b'}}>{saved.url}</span>
              <button onClick={()=>navigator.clipboard.writeText(saved.url).then(()=>setAlert('✅ Copied!'))} style={{...btn('#4F46E5'),fontSize:11,padding:'6px 10px',flexShrink:0,fontWeight:700}}>Copy</button>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap',marginBottom:14}}>
              <button onClick={()=>window.open(`https://wa.me/?text=${encodeURIComponent(`📅 ${saved.name} — Aspreo Calendar Template: ${saved.url}`)}`, '_blank')} style={{...btn('#25D366'),fontSize:11}}>💬 WhatsApp</button>
              <button onClick={()=>window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`📅 "${saved.name}" — calendar template for Aspreo Life`)}&url=${encodeURIComponent(saved.url)}`, '_blank')} style={{...btn('#1DA1F2'),fontSize:11}}>🐦 X</button>
              <button onClick={()=>window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(saved.url)}`, '_blank')} style={{...btn('#0A66C2'),fontSize:11}}>💼 LinkedIn</button>
            </div>
            <button onClick={()=>{setSaved(null);setName('');setDesc('');setEvents([]);}} style={{...btn('#64748b'),fontSize:12}}>Create Another</button>
            <button onClick={()=>setTab('mine')} style={{...btn('#4F46E5'),fontSize:12,marginTop:8}}>🗂 View My Calendars</button>
          </div>
        )}

        {/* ── IMPORT TAB ── */}
        {tab==='import' && (<>
          <div style={{background:'#f0fdf4',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#15803d',lineHeight:1.6}}>
            📥 Paste a template link. You'll see a preview of what's included before importing. Imported events will be added to your personal recurring planner.
          </div>
          <label style={lbl}>Template Link or ID</label>
          <div style={{display:'flex',gap:8,marginBottom:12}}>
            <input value={importUrl} onChange={e=>setImportUrl(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleFetchPreview()} placeholder={`${window.location.origin}/?import_calendar=...`} style={{...inp(),flex:1,fontSize:12}}/>
            <button onClick={handleFetchPreview} disabled={importLoading||!importUrl.trim()} style={{...btn('#4F46E5'),padding:'8px 14px',fontSize:12,opacity:importLoading||!importUrl.trim()?0.5:1}}>{importLoading?'…':'Preview'}</button>
          </div>
          {importPreview && (
            <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:12,padding:14,marginBottom:14}}>
              <div style={{fontWeight:700,color:'#1e293b',fontSize:14,marginBottom:4}}>{importPreview.title}</div>
              {importPreview.description && <div style={{fontSize:12,color:'#64748b',marginBottom:10}}>{importPreview.description}</div>}
              <div style={{fontSize:11,color:'#94a3b8',marginBottom:10}}>{(importPreview.data?.events||[]).length} recurring events</div>
              {(importPreview.data?.events||[]).map((ev,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 8px',borderRadius:6,background:'#fff',borderLeft:`3px solid ${ev.color||'#4F46E5'}`,marginBottom:4}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:'#1e293b'}}>{ev.title}</div>
                    <div style={{fontSize:10,color:'#94a3b8'}}>{ev.repeat_type} · {ev.start_date}{ev.start_time?` · ${ev.start_time.slice(0,5)}`:''}</div>
                  </div>
                </div>
              ))}
              <button onClick={handleImport} style={{...btn('#16a34a'),width:'100%',padding:'10px',fontWeight:700,marginTop:8}}>✅ Import {(importPreview.data?.events||[]).length} Events to My Planner</button>
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

const CAL_KIND_STYLES = {
  deep:     { bg: 'var(--ta-primary-fixed)', tone: 'var(--ta-primary)',          label: 'DEEP'     },
  meeting:  { bg: 'var(--ta-surface-high)',  tone: 'var(--ta-on-surface)',       label: 'MEETING'  },
  admin:    { bg: 'var(--ta-surface-low)',   tone: 'var(--ta-on-surface-variant)', label: 'ADMIN'  },
  personal: { bg: '#f5ebd9',                 tone: 'var(--ta-tertiary)',         label: 'PERSONAL' },
  reflect:  { bg: '#e9f0ea',                 tone: 'var(--ta-positive)',         label: 'REFLECT'  },
};
const CAL_HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6:00 → 21:00
const CAL_PX_PER_HOUR = 56;

function MonthlyPlanner({ctx}){
  const { tasks, recurringTasks, addRecurringTask, updateRecurringTask, deleteRecurringTask,
    addSharedTemplate, deleteSharedTemplate, hardDeleteSharedTemplate, sharedTemplates, fetchTemplateById, setAlert,
    calendarEvents=[], addCalendarEvent, deleteCalendarEvent } = ctx;

  const [weekOffset, setWeekOffset] = useState(0);
  const [recurModal, setRecurModal] = useState(null); // null | 'new' | task-obj
  const [calTmplOpen, setCalTmplOpen] = useState(false);
  const [slotModal, setSlotModal] = useState(null); // { day, hour } | null
  const [evForm, setEvForm] = useState({ title:'', kind:'meeting', sH:9, eH:10 });

  const monBase = (() => { const d=new Date(); const dow=d.getDay(); d.setDate(d.getDate()-(dow===0?6:dow-1)+weekOffset*7); return d; })();
  const week = Array.from({length:7},(_,i)=>{const d=new Date(monBase);d.setDate(monBase.getDate()+i);return fmt(d);});
  const [selectedDay, setSelectedDay] = useState(TODAY);
  useEffect(()=>{ if(!week.includes(selectedDay)) setSelectedDay(week[0]); },[weekOffset]); // eslint-disable-line

  // Check URL for calendar import on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('import_calendar');
    if (cid && fetchTemplateById) {
      fetchTemplateById(cid).then(tmpl => {
        if (!tmpl || tmpl.type !== 'calendar') return;
        const events = tmpl.data?.events || [];
        events.forEach(ev => addRecurringTask({ ...ev, id: undefined }));
        setAlert?.(`✅ Imported "${tmpl.title}" — ${events.length} events added!`);
        window.history.replaceState({}, '', window.location.pathname);
      });
    }
  }, []); // eslint-disable-line

  const recurOnDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const dow = d.getDay() === 0 ? 7 : d.getDay(); // 1=Mon … 7=Sun
    const dom = d.getDate();
    return (recurringTasks || []).filter(t => {
      if (dateStr < t.start_date) return false;
      if (t.end_date && dateStr > t.end_date) return false;
      if (t.repeat_type === 'daily') return true;
      if (t.repeat_type === 'weekly') return (t.repeat_days||[]).length === 0 ? true : (t.repeat_days||[]).includes(dow);
      if (t.repeat_type === 'monthly') return new Date(t.start_date+'T00:00:00').getDate() === dom;
      return false;
    });
  };

  // Unified block list: calendar_events (timed, kind-based) + recurring tasks with a
  // start_time + daily tasks that carry a task_time — these last ones used to only
  // show as a header preview; now they're positioned on the grid like everything else.
  const blocksForDay = (dateStr) => {
    const blocks = [];
    calendarEvents.filter(e => fmt(new Date(e.starts_at)) === dateStr).forEach(e => {
      const s = new Date(e.starts_at), en = new Date(e.ends_at);
      blocks.push({ key:'ev'+e.id, id:e.id, isEvent:true, title:e.title, kind:e.kind,
        sH: s.getHours()+s.getMinutes()/60, eH: en.getHours()+en.getMinutes()/60 });
    });
    recurOnDate(dateStr).filter(rt => rt.start_time).forEach(rt => {
      const [sh,sm] = rt.start_time.split(':').map(Number);
      const sH = sh + (sm||0)/60;
      // No end_time set (it's optional on the Recurring form, unlike Time Block which
      // always has both) — default to a full 1-hour span instead of collapsing to
      // a near-zero-height sliver, which is what made these look like they weren't
      // "expanding" on the grid the way one-off Time Block events do.
      let eH = sH + 1;
      if (rt.end_time) {
        const [eh,em] = rt.end_time.split(':').map(Number);
        const candidateEH = eh + (em||0)/60;
        if (candidateEH > sH) eH = candidateEH;
      }
      blocks.push({ key:'rt'+rt.id, isEvent:false, title:rt.title, color:rt.color, sH, eH });
    });
    (tasks[dateStr]||[]).filter(t => t.task_time).forEach(t => {
      const [th,tm] = t.task_time.split(':').map(Number);
      const sH = th+(tm||0)/60;
      blocks.push({ key:'task'+t.id, id:t.id, isTask:true, title:t.text, priority:t.priority, done:t.done,
        sH, eH: sH+0.5 });
    });
    return blocks;
  };

  // Only tasks WITHOUT a time show in the header preview — timed ones now live on the grid.
  const dayTasksTop3 = (dateStr) => (tasks[dateStr]||[]).filter(t=>!t.task_time).slice().sort((a,b)=>(a.priority==='critical'?-1:0)-(b.priority==='critical'?-1:0)).slice(0,3);

  const weekDeepHrs = week.reduce((sum,d)=> sum + blocksForDay(d).filter(b=>b.kind==='deep').reduce((s,b)=>s+(b.eH-b.sH),0), 0);
  const weekMeetHrs = week.reduce((sum,d)=> sum + blocksForDay(d).filter(b=>b.kind==='meeting').reduce((s,b)=>s+(b.eH-b.sH),0), 0);
  const weekBookedHrs = week.reduce((sum,d)=> sum + blocksForDay(d).reduce((s,b)=>s+(b.eH-b.sH),0), 0);
  const weekOpenHrs = Math.max(0, (CAL_HOURS.length*7) - weekBookedHrs);

  const submitEvent = () => {
    if (!evForm.title.trim() || !slotModal) return;
    const day = slotModal.day;
    const start = new Date(day+'T00:00:00'); start.setHours(Math.floor(evForm.sH), (evForm.sH%1)*60, 0, 0);
    const end   = new Date(day+'T00:00:00'); end.setHours(Math.floor(evForm.eH), (evForm.eH%1)*60, 0, 0);
    addCalendarEvent({ title: evForm.title, kind: evForm.kind, starts_at: start.toISOString(), ends_at: end.toISOString() });
    setEvForm({ title:'', kind:'meeting', sH:9, eH:10 });
    setSlotModal(null);
  };

  // Recurring tasks marked "add_to_daily" get a real entry created in that day's
  // task list for every matching day in the visible week — previously this
  // checkbox saved to the DB but nothing ever actually acted on it. Idempotent
  // on (title, time) so re-running this on every render/remount doesn't duplicate.
  useEffect(() => {
    week.forEach(day => {
      recurOnDate(day).filter(rt => rt.add_to_daily).forEach(rt => {
        const rtTime = (rt.start_time || '').slice(0,5);
        const already = (tasks[day]||[]).some(t => t.text===rt.title && (t.task_time||'')===rtTime);
        if (!already) ctx.addTask(day, rt.title, rt.start_time ? `${day}T${rt.start_time}` : undefined, { priority:'normal' });
      });
    });
  }, [week.join(','), recurringTasks]); // eslint-disable-line

  return (
    <div style={{fontFamily:FONT}}>
      <div className="ta-row ta-between" style={{alignItems:'flex-end',marginBottom:8,flexWrap:'wrap',gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Execution · {week[0]} – {week[6]}</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>A <em>week</em> of intention.</h1>
        </div>
        <div className="ta-row ta-gap-6" style={{alignItems:'flex-end'}}>
          <div className="ta-col">
            <span className="ta-mono" style={{fontSize:10,letterSpacing:'.18em',color:'var(--ta-on-surface-muted)'}}>DEEP WORK</span>
            <span className="ta-serif ta-tnum" style={{fontSize:26,fontWeight:300}}>{Math.round(weekDeepHrs*10)/10}h</span>
          </div>
          <div className="ta-col">
            <span className="ta-mono" style={{fontSize:10,letterSpacing:'.18em',color:'var(--ta-on-surface-muted)'}}>MEETINGS</span>
            <span className="ta-serif ta-tnum" style={{fontSize:26,fontWeight:300}}>{Math.round(weekMeetHrs*10)/10}h</span>
          </div>
          <div className="ta-col">
            <span className="ta-mono" style={{fontSize:10,letterSpacing:'.18em',color:'var(--ta-on-surface-muted)'}}>OPEN</span>
            <span className="ta-serif ta-tnum" style={{fontSize:26,fontWeight:300,color:'var(--ta-positive)'}}>{Math.round(weekOpenHrs*10)/10}h</span>
          </div>
        </div>
      </div>

      <div className="ta-row ta-gap-2" style={{marginBottom:20,flexWrap:'wrap'}}>
        <button onClick={()=>setWeekOffset(w=>w-1)} className="ta-btn ta-btn-secondary">‹ Prev</button>
        <button onClick={()=>setWeekOffset(0)} className="ta-btn ta-btn-secondary">This week</button>
        <button onClick={()=>setWeekOffset(w=>w+1)} className="ta-btn ta-btn-secondary">Next ›</button>
        <div style={{flex:1}}/>
        <button onClick={()=>setCalTmplOpen(true)} className="ta-btn ta-btn-ghost">Templates</button>
        <button onClick={()=>setRecurModal('new')} className="ta-btn ta-btn-ghost">+ Recurring</button>
        <button onClick={()=>{setEvForm({title:'',kind:'meeting',sH:9,eH:10});setSlotModal({day:selectedDay,hour:9});}} className="ta-btn ta-btn-primary">+ Time block</button>
      </div>

      {/* Week grid */}
      <div style={{background:'var(--ta-surface-low)',borderRadius:'var(--r-lg)',padding:16,marginBottom:24}}>
        {/* Header row */}
        <div style={{display:'grid',gridTemplateColumns:'56px repeat(7,1fr)',gap:4,marginBottom:10}}>
          <div/>
          {week.map((d,i)=>{
            const isToday = d===TODAY;
            const isSel = d===selectedDay;
            const top3 = dayTasksTop3(d);
            return (
              <button key={d} onClick={()=>setSelectedDay(d)} style={{background:isSel?'var(--ta-surface-lowest)':'transparent',border:'none',padding:'10px 8px',cursor:'pointer',borderRadius:'var(--r-md)',textAlign:'left'}}>
                <div className="ta-row ta-between" style={{alignItems:'baseline'}}>
                  <span className="ta-mono" style={{fontSize:10,letterSpacing:'.16em',color:isToday?'var(--ta-primary)':'var(--ta-on-surface-muted)'}}>{MON_NAMES[i].toUpperCase()}</span>
                  <span className="ta-serif ta-tnum" style={{fontSize:20,fontWeight:400,color:isToday?'var(--ta-primary)':'var(--ta-on-surface)'}}>{new Date(d+'T00:00:00').getDate()}</span>
                </div>
                <div className="ta-col ta-gap-1" style={{marginTop:4}}>
                  {top3.map(t=>(
                    <div key={t.id} title={t.text} style={{fontSize:11,padding:'2px 5px',borderRadius:4,background:t.priority==='critical'?'var(--ta-primary)':'var(--ta-surface-high)',color:t.priority==='critical'?'#fff':'var(--ta-on-surface-variant)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',textDecoration:t.done?'line-through':'none'}}>
                      {t.priority==='critical'?'★ ':''}{t.text}
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Time grid */}
        <div style={{position:'relative',display:'grid',gridTemplateColumns:'56px repeat(7,1fr)'}}>
          <div>
            {CAL_HOURS.map(h=>(
              <div key={h} className="ta-mono" style={{height:CAL_PX_PER_HOUR,fontSize:10,color:'var(--ta-on-surface-muted)',textAlign:'right',paddingRight:8,paddingTop:2}}>{String(h).padStart(2,'0')}</div>
            ))}
          </div>
          {week.map((d,di)=>(
            <div key={di} style={{position:'relative',borderLeft:'1px solid var(--ta-outline-variant)',background:d===TODAY?'rgba(36,56,156,0.025)':'var(--ta-surface-lowest)'}}>
              {CAL_HOURS.map(h=>(
                <div key={h} onClick={()=>{setEvForm(f=>({...f,sH:h,eH:h+1}));setSlotModal({day:d,hour:h});}}
                  style={{height:CAL_PX_PER_HOUR,borderBottom:'1px solid var(--ta-outline-variant)',cursor:'pointer'}} className="ta-lift"/>
              ))}
              {blocksForDay(d).map(b=>{
                const top = (b.sH-6)*CAL_PX_PER_HOUR;
                const height = Math.max(20, (b.eH-b.sH)*CAL_PX_PER_HOUR-3);
                if (b.isTask) {
                  const tone = b.priority==='critical'||b.priority==='high' ? 'var(--ta-negative)' : b.priority==='medium' ? 'var(--ta-primary)' : 'var(--ta-tertiary)';
                  return (
                    <div key={b.key} onClick={e=>e.stopPropagation()}
                      style={{position:'absolute',top,left:3,right:3,height,background:'var(--ta-surface-lowest)',color:'var(--ta-on-surface)',borderRadius:'var(--r-sm)',padding:'4px 7px',fontSize:11,lineHeight:1.25,overflow:'hidden',borderLeft:`2px solid ${tone}`,boxShadow:'0 1px 2px rgba(26,28,28,.06)',display:'flex',alignItems:'flex-start',gap:5}}>
                      <input type="checkbox" checked={!!b.done} onChange={()=>ctx.updateTask(d,b.id,{done:!b.done})} style={{accentColor:tone,marginTop:1,flexShrink:0}}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontWeight:600,textDecoration:b.done?'line-through':'none',color:b.done?'var(--ta-on-surface-muted)':'var(--ta-on-surface)'}}>{b.title}</div>
                        <div className="ta-mono" style={{fontSize:9,opacity:.8}}>{String(Math.floor(b.sH)).padStart(2,'0')}:{String(Math.round((b.sH%1)*60)).padStart(2,'0')}</div>
                      </div>
                    </div>
                  );
                }
                const st = b.kind ? (CAL_KIND_STYLES[b.kind]||CAL_KIND_STYLES.meeting) : { bg:(b.color||'#4F46E5')+'22', tone:b.color||'#4F46E5', label:'RECURRING' };
                return (
                  <div key={b.key} onClick={e=>{e.stopPropagation(); if(b.isEvent && window.confirm(`Delete "${b.title}"?`)) deleteCalendarEvent(b.id);}}
                    style={{position:'absolute',top,left:3,right:3,height,background:st.bg,color:st.tone,borderRadius:'var(--r-sm)',padding:'4px 7px',fontSize:11,lineHeight:1.25,overflow:'hidden',cursor:b.isEvent?'pointer':'default',borderLeft:`2px solid ${st.tone}`}}>
                    <div style={{fontWeight:600}}>{b.title}</div>
                    <div className="ta-mono" style={{fontSize:9,opacity:.8}}>{String(Math.floor(b.sH)).padStart(2,'0')}:{String(Math.round((b.sH%1)*60)).padStart(2,'0')}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="ta-row ta-gap-4" style={{marginTop:14,padding:'0 4px',flexWrap:'wrap'}}>
          {Object.entries(CAL_KIND_STYLES).map(([k,st])=>(
            <div key={k} className="ta-row ta-gap-2">
              <span style={{width:10,height:10,borderRadius:2,background:st.bg}}/>
              <span className="ta-mono" style={{fontSize:10,letterSpacing:'.14em',color:'var(--ta-on-surface-variant)'}}>{st.label}</span>
            </div>
          ))}
          <div className="ta-row ta-gap-2">
            <span style={{width:10,height:10,borderRadius:2,background:'var(--ta-surface-lowest)',border:'2px solid var(--ta-primary)',borderWidth:'0 0 0 2px'}}/>
            <span className="ta-mono" style={{fontSize:10,letterSpacing:'.14em',color:'var(--ta-on-surface-variant)'}}>TASK · give it a time to see it here</span>
          </div>
          <span className="ta-marginalia">Click any empty slot to block time.</span>
        </div>
      </div>

      {/* Recurring events list */}
      {(recurringTasks||[]).length > 0 && (
        <div className="ta-card" style={{marginBottom:24}}>
          <div className="ta-section-head">
            <h2 className="ta-section-head-h">Recurring events</h2>
            <span className="ta-section-head-meta">{recurringTasks.length} set</span>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {recurringTasks.map(rt=>(
              <div key={rt.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:'var(--r-md)',background:'var(--ta-surface-low)',borderLeft:`3px solid ${rt.color}`}}>
                <div style={{flex:1}}>
                  <span className="ta-serif" style={{fontWeight:500,fontSize:14,color:'var(--ta-on-surface)'}}>{rt.title}</span>
                  <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginLeft:8}}>{rt.repeat_type} · {rt.start_date}{rt.end_date?` → ${rt.end_date}`:' · ongoing'}</span>
                </div>
                <button onClick={()=>setRecurModal(rt)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:26,height:26}}>✏️</button>
                <button onClick={()=>{if(window.confirm(`Delete "${rt.title}"?`))deleteRecurringTask(rt.id);}} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:26,height:26,color:'var(--ta-negative)'}}>🗑</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {slotModal && (
        <div style={{position:'fixed',inset:0,zIndex:8000,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setSlotModal(null)}>
          <div className="ta-card-prominent" style={{width:'100%',maxWidth:420}}>
            <div className="ta-row ta-between" style={{marginBottom:16}}>
              <span className="ta-serif" style={{fontSize:18}}>Block time</span>
              <button onClick={()=>setSlotModal(null)} className="ta-btn-icon" style={{border:'none',cursor:'pointer'}}>✕</button>
            </div>
            <div className="ta-col ta-gap-3">
              <div>
                <div className="ta-section-head-meta" style={{marginBottom:6}}>What's the block?</div>
                <input autoFocus className="ta-input" value={evForm.title} placeholder="Deep work · Q3 strategy" onChange={e=>setEvForm(f=>({...f,title:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&submitEvent()}/>
              </div>
              <div>
                <div className="ta-section-head-meta" style={{marginBottom:6}}>Type</div>
                <select className="ta-input" value={evForm.kind} onChange={e=>setEvForm(f=>({...f,kind:e.target.value}))}>
                  {Object.entries(CAL_KIND_STYLES).map(([k,st])=><option key={k} value={k}>{st.label}</option>)}
                </select>
              </div>
              <div className="ta-grid-2">
                <div>
                  <div className="ta-section-head-meta" style={{marginBottom:6}}>Start</div>
                  <select className="ta-input" value={evForm.sH} onChange={e=>setEvForm(f=>({...f,sH:+e.target.value}))}>{CAL_HOURS.map(h=><option key={h} value={h}>{h}:00</option>)}</select>
                </div>
                <div>
                  <div className="ta-section-head-meta" style={{marginBottom:6}}>End</div>
                  <select className="ta-input" value={evForm.eH} onChange={e=>setEvForm(f=>({...f,eH:+e.target.value}))}>{CAL_HOURS.concat([22]).map(h=><option key={h} value={h}>{h}:00</option>)}</select>
                </div>
              </div>
              <div className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{new Date(slotModal.day+'T00:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
            </div>
            <div className="ta-row ta-gap-2" style={{marginTop:20,justifyContent:'flex-end'}}>
              <button onClick={()=>setSlotModal(null)} className="ta-btn ta-btn-ghost">Cancel</button>
              <button onClick={submitEvent} className="ta-btn ta-btn-primary">Add to calendar</button>
            </div>
          </div>
        </div>
      )}
      {recurModal && (
        <RecurringTaskModal
          task={recurModal === 'new' ? null : recurModal}
          onSave={data => { if (recurModal === 'new') addRecurringTask(data); else updateRecurringTask(recurModal.id, data); }}
          onClose={() => setRecurModal(null)}
        />
      )}
      {calTmplOpen && (
        <CalendarTemplateModal
          addSharedTemplate={addSharedTemplate}
          deleteSharedTemplate={deleteSharedTemplate}
          hardDeleteSharedTemplate={hardDeleteSharedTemplate}
          sharedTemplates={sharedTemplates||[]}
          fetchTemplateById={fetchTemplateById}
          addRecurringTask={addRecurringTask}
          setAlert={setAlert}
          onClose={() => setCalTmplOpen(false)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FINANCE TRACKER
// ══════════════════════════════════════════════════════════════════════════════

// ── BorrowLend Card ──────────────────────────────────────────────────────────
function BorrowLendCard({item, onDelete, onAddTask}) {
  const [taskDate, setTaskDate] = useState("");
  const isLent = item.type === "lent";
  const col = isLent ? "#B94040" : "#1A7A6A";
  const daysLeft = item.dueDate ? Math.ceil((new Date(item.dueDate) - new Date()) / 86400000) : null;
  const urgent = daysLeft !== null && daysLeft <= 7;
  return (
    <div style={{background:"#fff",border:`1.5px solid ${urgent?"#B00":"#ddd"}`,borderLeft:`4px solid ${col}`,borderRadius:9,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div style={{flex:1,minWidth:160}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:11,background:col+"22",color:col,borderRadius:8,padding:"1px 8px",fontWeight:"bold"}}>{isLent?"💸 Lent":"💰 Borrowed"}</span>
          <span style={{fontWeight:"bold",fontSize:13,color:"#111"}}>{item.person}</span>
        </div>
        <div style={{fontSize:12,color:"#444",marginTop:2}}>₹{Number(item.amount).toLocaleString()} · {item.note||""}</div>
        {item.dueDate&&<div style={{fontSize:10,marginTop:2,color:urgent?"#B00":"#888"}}>{isLent?"Expect back by":"Return by"}: {item.dueDate}{urgent&&<span style={{fontWeight:"bold"}}> ⚠️ {daysLeft}d!</span>}</div>}
      </div>
      <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
        <input type="date" value={taskDate} onChange={e=>setTaskDate(e.target.value)} style={{fontSize:11,padding:"3px 5px",border:"1px solid #ddd",borderRadius:5,fontFamily:FONT}} title="Add task reminder on this date"/>
        <button onClick={()=>{if(taskDate){onAddTask(taskDate);setTaskDate("");}}} style={{fontSize:11,padding:"4px 9px",background:col,color:"#fff",border:"none",borderRadius:6,cursor:"pointer"}}>📌 Task</button>
        <button onClick={onDelete} style={{background:"none",border:"1px solid #ddd",borderRadius:6,cursor:"pointer",color:"#bbb",fontSize:13,padding:"3px 7px"}}>🗑</button>
      </div>
    </div>
  );
}

// ── EMI Card helper ──────────────────────────────────────────────────────────
function EmiCard({emi,onDelete,onAddTask}){
  const [taskDate,setTaskDate]=useState("");
  return(
    <div style={{background:"#fff",border:`1.5px solid ${C.border}`,borderLeft:`4px solid ${C.red}`,borderRadius:9,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div style={{flex:1,minWidth:160}}>
        <div style={{fontWeight:"bold",fontSize:13,color:"#111"}}>{emi.name}</div>
        <div style={{fontSize:11,color:C.muted}}>₹{Number(emi.amount).toLocaleString()}/month · Due day {emi.dueDay} · {emi.category}</div>
        {emi.notes&&<div style={{fontSize:10,color:C.muted,marginTop:2}}>{emi.notes}</div>}
      </div>
      <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
        <input type="date" value={taskDate} onChange={e=>setTaskDate(e.target.value)} style={{fontSize:11,padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:FONT}} title="Pick a date to add this EMI as a task"/>
        <button onClick={()=>{if(taskDate){onAddTask(taskDate);setTaskDate("");}}} style={{...btn(C.green),fontSize:11,padding:"5px 10px"}} title="Add task for this date">📌 Task</button>
        <button onClick={onDelete} style={btn(C.red,30)}>🗑</button>
      </div>
    </div>
  );
}

function FinanceTracker({ctx}){
  const C = useC();
  const {expenses,income,fixedIncome,setFixedIncome,addExpense,deleteExpense,addIncome,deleteIncome}=ctx; // eslint-disable-line no-unused-vars
  const [expForm,setExpForm]=useState({date:TODAY,description:"",amount:"",category:"Other"});
  const [incForm,setIncForm]=useState({date:TODAY,source:"",amount:""});
  const [filterMonth,setFilterMonth]=useState(TODAY.slice(0,7));
  const [tab,setTab]=useState("overview");
  // EMI / recurring fixed expenses stored in localStorage
  const EMI_KEY="diary_emis";
  const [emis,setEmisState]=useState(()=>{try{return JSON.parse(localStorage.getItem(EMI_KEY)||"[]");}catch{return [];}});
  const saveEmis=(next)=>{setEmisState(next);localStorage.setItem(EMI_KEY,JSON.stringify(next));};
  const [emiForm,setEmiForm]=useState({name:"",amount:"",dueDay:1,category:"Housing",notes:"",taskDate:""});
  const addEmi=()=>{
    if(!emiForm.name.trim()||!emiForm.amount)return;
    const emi={...emiForm,id:`emi_${Date.now()}`,amount:Number(emiForm.amount)};
    saveEmis([...emis,emi]);
    // Auto-add as expense for current filter month so balance reduces immediately
    const dueDate=`${filterMonth}-${String(emi.dueDay).padStart(2,"0")}`;
    ctx.addExpense({date:dueDate,description:`💳 EMI: ${emi.name}`,amount:Number(emi.amount),category:emi.category});
    // If taskDate set, also add it as a task on that date
    if(emiForm.taskDate&&ctx.addTask){
      ctx.addTask(emiForm.taskDate,`💳 Pay EMI: ${emi.name} ₹${Number(emi.amount).toLocaleString()}`,undefined,{status:"new",priority:"high"});
    }
    setEmiForm({name:"",amount:"",dueDay:1,category:"Housing",notes:"",taskDate:""});
  };
  const deleteEmi=(id)=>saveEmis(emis.filter(e=>e.id!==id));
  const addEmiToTask=(emi,date)=>{
    if(!date||!ctx.addTask)return;
    ctx.addTask(date,`💳 Pay EMI: ${emi.name} ₹${Number(emi.amount).toLocaleString()}`,undefined,{status:"new",priority:"high"});
    // Also add as expense on that date so balance reflects it
    ctx.addExpense({date,description:`💳 EMI: ${emi.name}`,amount:Number(emi.amount),category:emi.category});
  };
  const totalEmiMonthly=emis.reduce((s,e)=>s+Number(e.amount),0);
  const CATS=["Housing","Food","Transport","Health","Utilities","Entertainment","Education","Savings","Other"];
  // Borrow & Lend tracker — stored in localStorage
  const BL_KEY="diary_borrow_lend";
  const [blItems,setBlState]=useState(()=>{try{return JSON.parse(localStorage.getItem(BL_KEY)||"[]");}catch{return [];}});
  const saveBl=(next)=>{setBlState(next);localStorage.setItem(BL_KEY,JSON.stringify(next));};
  const [blForm,setBlForm]=useState({type:"lent",person:"",amount:"",dueDate:"",note:"",taskDate:""});
  const addBl=()=>{
    if(!blForm.person.trim()||!blForm.amount)return;
    const item={...blForm,id:`bl_${Date.now()}`,amount:Number(blForm.amount)};
    saveBl([...blItems,item]);
    // Auto-add to income (borrowed) or expense (lent)
    if(item.type==="borrowed"){
      ctx.addIncome({date:TODAY,source:`💰 Borrowed from ${item.person}`,amount:item.amount});
    } else {
      ctx.addExpense({date:TODAY,description:`💸 Lent to ${item.person}`,amount:item.amount,category:"Other"});
    }
    if(blForm.taskDate&&ctx.addTask){
      const label=item.type==="borrowed"?`💰 Return ₹${item.amount.toLocaleString()} to ${item.person}`:`💸 Collect ₹${item.amount.toLocaleString()} from ${item.person}`;
      ctx.addTask(blForm.taskDate,label,undefined,{status:"new",priority:"high"});
    }
    setBlForm({type:"lent",person:"",amount:"",dueDate:"",note:"",taskDate:""});
  };
  const deleteBl=(id)=>saveBl(blItems.filter(i=>i.id!==id));
  const addBlTask=(item,date)=>{
    if(!date||!ctx.addTask)return;
    const label=item.type==="borrowed"?`💰 Return ₹${Number(item.amount).toLocaleString()} to ${item.person}`:`💸 Collect ₹${Number(item.amount).toLocaleString()} from ${item.person}`;
    ctx.addTask(date,label,undefined,{status:"new",priority:"high"});
  };
  const totalLent=blItems.filter(i=>i.type==="lent").reduce((s,i)=>s+Number(i.amount),0);
  const totalBorrowed=blItems.filter(i=>i.type==="borrowed").reduce((s,i)=>s+Number(i.amount),0);

  const addExp=()=>{if(!expForm.description.trim()||!expForm.amount)return;ctx.addExpense({...expForm,amount:Number(expForm.amount)});setExpForm(p=>({...p,description:"",amount:""}));};
  const addInc=()=>{if(!incForm.source.trim()||!incForm.amount)return;ctx.addIncome({...incForm,amount:Number(incForm.amount)});setIncForm(p=>({...p,source:"",amount:""}));};

  const filtExp=expenses.filter(e=>(e.expense_date||e.date||"").startsWith(filterMonth));
  const filtInc=income.filter(i=>(i.income_date||i.date||"").startsWith(filterMonth));
  const totalExp=filtExp.reduce((s,e)=>s+e.amount,0);
  const totalInc=filtInc.reduce((s,i)=>s+i.amount,0)+Number(fixedIncome||0);
  const savings=totalInc-totalExp;
  const savPct=totalInc>0?Math.round(savings/totalInc*100):0;
  const byCategory={};filtExp.forEach(e=>{byCategory[e.category]=(byCategory[e.category]||0)+e.amount;});


  return(
    <div>
      <SectionMasthead eyebrow="System · Money in, money out" title={<>Where it <em>goes</em>.</>} />
      <div style={{background:C.surface,border:`1.5px solid ${C.green}`,borderRadius:10,padding:12,marginBottom:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:13,fontWeight:"bold",color:C.green}}>📌 Fixed Monthly Income:</span>
        <input type="number" value={fixedIncome} onChange={e=>setFixedIncome(Number(e.target.value))} placeholder="e.g. 111000" style={{...inp(),width:150,fontSize:15,fontWeight:"bold"}}/>
        <span style={{fontSize:12,color:C.muted}}>Auto-added to all monthly totals</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:14}}>
        {[{label:"Total Income",val:totalInc,color:C.green},{label:"Total Expenses",val:totalExp,color:C.red},{label:"Savings",val:savings,color:savings>=0?C.teal:C.red},{label:"Saved %",val:`${savPct}%`,color:C.blue,raw:true}].map(({label,val,color,raw})=>(
          <div key={label} style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${color}`,borderRadius:9,padding:14,textAlign:"center"}}>
            <div style={{fontSize:10,color:C.muted}}>{label}</div>
            <div style={{fontSize:19,fontWeight:"bold",color}}>{raw?val:`₹${Math.abs(Number(val)).toLocaleString()}`}</div>
          </div>
        ))}
      </div>
      <div style={{fontSize:11,color:C.muted,marginTop:-8,marginBottom:14}}>ℹ️ Expenses above exclude Subscriptions — see the Subscriptions tab for recurring-cost totals.</div>
      {totalInc>0&&(
        <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:9,padding:12,marginBottom:14}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Income vs Expense Ratio</div>
          <div style={{display:"flex",height:26,borderRadius:6,overflow:"hidden"}}>
            <div style={{width:`${Math.min(100,totalExp/totalInc*100)}%`,background:C.red,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:"bold"}}>{Math.round(totalExp/totalInc*100)}% Exp</div>
            <div style={{flex:1,background:C.green,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:"bold"}}>{savPct}% Saved</div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:11,color:C.muted}}>
            <span>Income: ₹{totalInc.toLocaleString()}</span>
            <span>Expenses: ₹{totalExp.toLocaleString()}</span>
            <span style={{color:savings>=0?C.teal:C.red,fontWeight:"bold"}}>Balance: ₹{savings.toLocaleString()}</span>
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:6,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        <input type="month" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{...inp(),marginRight:8}}/>
        {["overview","expenses","income","emi","borrow"].map(t=><button key={t} onClick={()=>setTab(t)} style={{fontSize:12,padding:"7px 16px",borderRadius:20,border:"none",cursor:"pointer",fontFamily:FONT,fontWeight:tab===t?700:500,background:tab===t?C.indigo:"transparent",color:tab===t?"#fff":C.muted,transition:"all .15s"}}>{t==="emi"?"💳 EMI":t==="borrow"?"🤝 Borrow/Lend":t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
      </div>
      {tab==="overview"&&<div>
        {/* Summary cards */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,marginBottom:20}}>
          {[{label:"Monthly Expenses",val:totalExp,col:C.red},{label:"Monthly Income",val:totalInc,col:C.green},{label:"Net Balance",val:totalInc-totalExp,col:totalInc-totalExp>=0?C.teal:C.red},{label:"EMI Commitments",val:totalEmiMonthly,col:"#C8860A"},{label:"Total Lent",val:totalLent,col:"#B94040"},{label:"Total Borrowed",val:totalBorrowed,col:"#1A7A6A"}].map(({label,val,col})=>(
            <div key={label} style={{background:C.surface,border:`1px solid ${col}20`,borderTop:`3px solid ${col}`,borderRadius:14,padding:"14px 12px",textAlign:"center",boxShadow:C.shadowSm,transition:"transform .15s,box-shadow .15s"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=C.shadowMd;}} onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow=C.shadowSm;}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{label}</div>
              <div style={{fontSize:17,fontWeight:"bold",color:col}}>₹{Math.abs(Number(val)||0).toLocaleString()}</div>
            </div>
          ))}
        </div>
        {/* Pie chart — expense by category */}
        {Object.entries(byCategory).length>0&&(()=>{
          const PCOLORS=["#B94040","#C8860A","#1D5F8A","#2D7A2D","#6B3FA0","#0E7490","#C8405A","#888","#555"];
          const cats=Object.entries(byCategory); const total=cats.reduce((s,[,v])=>s+v,0);
          let angle=0;
          const slices=cats.map(([cat,val],i)=>{
            const pct=val/total; const start=angle; angle+=pct*360;
            return {cat,val,pct,start,end:angle,col:PCOLORS[i%PCOLORS.length]};
          });
          const polarToXY=(deg,r)=>[50+r*Math.cos((deg-90)*Math.PI/180),50+r*Math.sin((deg-90)*Math.PI/180)];
          const arcPath=(s,e,r)=>{
            if(e-s>=359.9) return `M50,50 m-${r},0 a${r},${r} 0 1,1 ${2*r},0 a${r},${r} 0 1,1 -${2*r},0`;
            const [x1,y1]=polarToXY(s,r),[x2,y2]=polarToXY(e,r);
            return `M50,50 L${x1},${y1} A${r},${r} 0 ${e-s>180?1:0},1 ${x2},${y2} Z`;
          };
          return(
            <div style={{display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap",marginBottom:16}}>
              <svg viewBox="0 0 100 100" style={{width:160,height:160,flexShrink:0}}>
                {slices.map((sl,i)=><path key={i} d={arcPath(sl.start,sl.end,42)} fill={sl.col} stroke="#fff" strokeWidth="0.8"/>)}
                <circle cx="50" cy="50" r="22" fill={C.surface}/>
                <text x="50" y="48" textAnchor="middle" fontSize="5" fill={C.muted}>Expenses</text>
                <text x="50" y="56" textAnchor="middle" fontSize="6" fontWeight="bold" fill={C.red}>₹{Math.round(totalExp/1000)}k</text>
              </svg>
              <div style={{flex:1}}>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {slices.map(sl=>(<div key={sl.cat} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,minWidth:130}}><div style={{width:10,height:10,borderRadius:2,background:sl.col,flexShrink:0}}/><span style={{color:"#444"}}>{sl.cat}</span><span style={{marginLeft:"auto",fontWeight:"bold",color:sl.col}}>₹{sl.val.toLocaleString()} ({Math.round(sl.pct*100)}%)</span></div>))}
                </div>
                {/* EMI + Borrow/Lend summary */}
                {emis.length>0&&<div style={{marginTop:10,padding:"8px 12px",background:"#FFF3CC",borderRadius:8,border:"1px solid #C8860A40"}}>
                  <div style={{fontSize:11,fontWeight:"bold",color:"#C8860A",marginBottom:4}}>💳 EMI Commitments</div>
                  {emis.map(e=><div key={e.id} style={{fontSize:11,color:"#444",display:"flex",justifyContent:"space-between"}}><span>{e.name}</span><span style={{fontWeight:"bold"}}>₹{Number(e.amount).toLocaleString()}/mo</span></div>)}
                </div>}
                {blItems.length>0&&<div style={{marginTop:8,padding:"8px 12px",background:"#E0F5FF",borderRadius:8,border:"1px solid #0E749040"}}>
                  <div style={{fontSize:11,fontWeight:"bold",color:"#0E7490",marginBottom:4}}>🤝 Outstanding Borrow/Lend</div>
                  {blItems.map(i=><div key={i.id} style={{fontSize:11,color:"#444",display:"flex",justifyContent:"space-between"}}><span>{i.type==="lent"?"💸":"💰"} {i.person}</span><span style={{fontWeight:"bold",color:i.type==="lent"?"#B94040":"#1A7A6A"}}>₹{Number(i.amount).toLocaleString()}</span></div>)}
                </div>}
              </div>
            </div>
          );
        })()}
        {Object.entries(byCategory).length===0&&<p style={{color:C.light}}>No expenses this month.</p>}
      </div>}

      {tab==="expenses"&&<><Card title="+ Add Expense" color={C.red}><div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr auto",gap:8,alignItems:"end"}}>
        <div><label style={lbl}>Date</label><input type="date" value={expForm.date} onChange={e=>setExpForm(p=>({...p,date:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <div><label style={lbl}>Description</label><input value={expForm.description} onChange={e=>setExpForm(p=>({...p,description:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <div><label style={lbl}>Amount (₹)</label><input type="number" value={expForm.amount} onChange={e=>setExpForm(p=>({...p,amount:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <div><label style={lbl}>Category</label><select value={expForm.category} onChange={e=>setExpForm(p=>({...p,category:e.target.value}))} style={{...inp(),width:"100%"}}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
        <button onClick={addExp} style={{...btn(C.red),alignSelf:"flex-end"}}>+ Add</button>
      </div></Card><FinTable rows={filtExp} onDelete={id=>ctx.deleteExpense(id)} cols={["date","description","category","amount"]}/></>}
      {tab==="income"&&<><Card title="+ Add Income" color={C.green}><div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,alignItems:"end"}}>
        <div><label style={lbl}>Date</label><input type="date" value={incForm.date} onChange={e=>setIncForm(p=>({...p,date:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <div><label style={lbl}>Source</label><input value={incForm.source} onChange={e=>setIncForm(p=>({...p,source:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <div><label style={lbl}>Amount (₹)</label><input type="number" value={incForm.amount} onChange={e=>setIncForm(p=>({...p,amount:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
        <button onClick={addInc} style={{...btn(C.green),alignSelf:"flex-end"}}>+ Add</button>
      </div></Card>{fixedIncome>0&&<div style={{background:C.greenM,border:`1px solid ${C.green}`,borderRadius:8,padding:"8px 14px",margin:"10px 0",fontSize:12,color:C.green}}>📌 Fixed income ₹{Number(fixedIncome).toLocaleString()} is automatically included in totals.</div>}<FinTable rows={filtInc} onDelete={id=>ctx.deleteIncome(id)} cols={["date","source","amount"]}/></>}
      {tab==="emi"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${C.red}`,borderRadius:9,padding:14,textAlign:"center"}}>
              <div style={{fontSize:11,color:C.muted}}>Total Monthly EMIs</div>
              <div style={{fontSize:22,fontWeight:"bold",color:C.red}}>₹{totalEmiMonthly.toLocaleString()}</div>
            </div>
            <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${C.amber}`,borderRadius:9,padding:14,textAlign:"center"}}>
              <div style={{fontSize:11,color:C.muted}}>Total Yearly EMIs</div>
              <div style={{fontSize:22,fontWeight:"bold",color:C.amber}}>₹{(totalEmiMonthly*12).toLocaleString()}</div>
            </div>
          </div>
          <Card title="💳 Add EMI / Fixed Recurring Expense" color={C.red}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
              <div><label style={lbl}>Name / Lender</label><input value={emiForm.name} onChange={e=>setEmiForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Home Loan EMI" style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Monthly Amount (₹)</label><input type="number" value={emiForm.amount} onChange={e=>setEmiForm(p=>({...p,amount:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Due Day of Month</label><input type="number" min={1} max={31} value={emiForm.dueDay} onChange={e=>setEmiForm(p=>({...p,dueDay:Number(e.target.value)}))} style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Category</label><select value={emiForm.category} onChange={e=>setEmiForm(p=>({...p,category:e.target.value}))} style={{...inp(),width:"100%"}}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
              <div><label style={lbl}>Notes</label><input value={emiForm.notes} onChange={e=>setEmiForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes..." style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>📌 Add to Task on Date</label><input type="date" value={emiForm.taskDate} onChange={e=>setEmiForm(p=>({...p,taskDate:e.target.value}))} style={{...inp(),width:"100%"}} title="Creates a task reminder on this date"/></div>
            </div>
            <button onClick={addEmi} style={btn(C.red)}>+ Add EMI</button>
          </Card>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:14}}>
            {emis.length===0&&<p style={{color:C.muted}}>No EMIs added yet.</p>}
            {emis.map(e=>(<EmiCard key={e.id} emi={e} onDelete={()=>deleteEmi(e.id)} onAddTask={(d)=>addEmiToTask(e,d)} />))}
          </div>
        </div>
      )}
      {tab==="borrow"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div style={{background:"#fff",border:"1.5px solid #ddd",borderTop:"3px solid #B94040",borderRadius:9,padding:14,textAlign:"center"}}><div style={{fontSize:11,color:"#888"}}>Total Lent Out</div><div style={{fontSize:22,fontWeight:"bold",color:"#B94040"}}>₹{totalLent.toLocaleString()}</div></div>
            <div style={{background:"#fff",border:"1.5px solid #ddd",borderTop:"3px solid #1A7A6A",borderRadius:9,padding:14,textAlign:"center"}}><div style={{fontSize:11,color:"#888"}}>Total Borrowed</div><div style={{fontSize:22,fontWeight:"bold",color:"#1A7A6A"}}>₹{totalBorrowed.toLocaleString()}</div></div>
          </div>
          <Card title="🤝 Add Borrowed / Lent Entry" color="#0E7490">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
              <div><label style={lbl}>Type</label><select value={blForm.type} onChange={e=>setBlForm(p=>({...p,type:e.target.value}))} style={{...inp(),width:"100%"}}><option value="lent">💸 Lent (I gave money)</option><option value="borrowed">💰 Borrowed (I received money)</option></select></div>
              <div><label style={lbl}>Person / Party</label><input value={blForm.person} onChange={e=>setBlForm(p=>({...p,person:e.target.value}))} placeholder="Name..." style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Amount (₹)</label><input type="number" value={blForm.amount} onChange={e=>setBlForm(p=>({...p,amount:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Due / Return Date</label><input type="date" value={blForm.dueDate} onChange={e=>setBlForm(p=>({...p,dueDate:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Note</label><input value={blForm.note} onChange={e=>setBlForm(p=>({...p,note:e.target.value}))} placeholder="Purpose..." style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>📌 Add Reminder to Task</label><input type="date" value={blForm.taskDate} onChange={e=>setBlForm(p=>({...p,taskDate:e.target.value}))} style={{...inp(),width:"100%"}} title="Creates a task on this date"/></div>
            </div>
            <button onClick={addBl} style={{...btn("#0E7490")}}>+ Add Entry</button>
          </Card>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:14}}>
            {blItems.length===0&&<p style={{color:"#aaa"}}>No entries yet.</p>}
            {blItems.map(item=>(<BorrowLendCard key={item.id} item={item} onDelete={()=>deleteBl(item.id)} onAddTask={(d)=>addBlTask(item,d)}/>))}
          </div>
        </div>
      )}
          </div>
  );
}

function FinTable({rows,onDelete,cols}){
  const sorted=[...rows].sort((a,b)=>(b.expense_date||b.income_date||b.date||"").localeCompare(a.expense_date||a.income_date||a.date||""));
  return(
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:9,overflow:"hidden",marginTop:10}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{background:C.green,color:"#fff"}}>{cols.map(c=><th key={c} style={{padding:"9px 10px",textAlign:"left",textTransform:"capitalize"}}>{c}</th>)}<th/></tr></thead>
        <tbody>
          {sorted.length===0&&<tr><td colSpan={cols.length+1} style={{padding:16,color:C.light,textAlign:"center"}}>No entries.</td></tr>}
          {sorted.map((r,i)=>(
            <tr key={r.id} style={{background:i%2===0?C.bg:C.surface,borderBottom:`1px solid ${C.border}`}}>
              {cols.map(c=><td key={c} style={{padding:"7px 10px",color:C.text}}>{c==="amount"?`₹${r[c].toLocaleString()}`:c==="category"?<span style={{background:C.amberL,color:C.amber,borderRadius:10,padding:"1px 7px",fontSize:10}}>{r[c]}</span>:c==="date"?(r.expense_date||r.income_date||r.date||"—"):r[c]}</td>)}
              <td style={{padding:"7px 8px"}}><button onClick={()=>onDelete(r.id)} style={{background:"none",border:"none",cursor:"pointer",color:C.red}}>🗑</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BOOK TRACKER
// ══════════════════════════════════════════════════════════════════════════════
const BOOK_SPINE_COLORS = ['#a06014','#8a2828','#6c3400','#2f6b3a','#24389c','#6a4b9c'];
const bookSpineColor = (id) => {
  const s = String(id);
  let h = 0; for (let i=0;i<s.length;i++) h = (h*31 + s.charCodeAt(i)) >>> 0;
  return BOOK_SPINE_COLORS[h % BOOK_SPINE_COLORS.length];
};

const BOOK_STATUS_SECTIONS = [
  { id:'reading',   label:'Reading now'  },
  { id:'to-read',   label:'Want to read' },
  { id:'paused',    label:'Paused'       },
  { id:'completed', label:'Finished'     },
];

function BookTracker({ctx}){
  const {books,setBooks,archiveBook,unarchiveBook,
    knowledgeItems=[],knowledgeDomains=[],addKnowledgeDomain,addKnowledgeItem,updateKnowledgeItem}=ctx;
  const [showForm,setShowForm]=useState(false);
  const [selectedBook,setSelectedBook]=useState(null);
  const [editBook,setEditBook]=useState(null);
  const [noteInputs,setNoteInputs]=useState({});
  const [notePageInputs,setNotePageInputs]=useState({});
  const [showArchived,setShowArchived]=useState(false);
  const [form,setForm]=useState({title:"",author:"",category:"",totalPages:0,currentPage:0,status:"reading",startDate:TODAY,finishDate:"",notes:"",rating:0,cover_image:""});
  const STATUS_COLORS={reading:C.green,completed:C.blue,"to-read":C.amber,paused:"#888"};
  const [coverUploading, setCoverUploading] = useState(false);

  const activeBooks   = books.filter(b=>!b.archived);
  const archivedBooks = books.filter(b=>b.archived);
  const save=()=>{
    if(!form.title.trim())return;
    if(editBook){
      // Use updateBook directly to ensure cover_image persists to DB
      ctx.updateBook(editBook, { ...form });
      setBooks(p=>p.map(b=>b.id===editBook?{...b,...form}:b));
      setEditBook(null);
    } else {
      ctx.addBook({ ...form, entries:[] });
    }
    setForm({title:"",author:"",category:"",totalPages:0,currentPage:0,status:"reading",startDate:TODAY,finishDate:"",notes:"",rating:0,cover_image:""});
    setShowForm(false);
  };
  const uploadCover = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const ext  = file.name.split('.').pop().toLowerCase() || 'jpg';
      // Path must start with the uid folder segment — the "Users upload own avatar"
      // storage RLS policy checks auth.uid() = (storage.foldername(name))[1].
      const path = `${ctx.uid || 'user'}/book-covers/${Date.now()}.${ext}`;
      // Upload directly to avatars bucket (public) using supabase storage
      const { data: uploadData, error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data: pubData } = supabase.storage.from('avatars').getPublicUrl(uploadData.path);
      if (pubData?.publicUrl) setForm(p=>({...p, cover_image: pubData.publicUrl}));
    } catch(e) {
      console.error('cover upload failed:', e.message);
      // Fallback: convert to base64 data URL for local display
      const reader = new FileReader();
      reader.onload = ev => setForm(p=>({...p, cover_image: ev.target.result}));
      reader.readAsDataURL(file);
    }
    setCoverUploading(false);
  };
  const saveBookEntries = (updatedBooks) => {
    const map = {};
    updatedBooks.forEach(b => { if (b.entries?.length) map[b.id] = b.entries; });
    try { localStorage.setItem('diary_book_entries', JSON.stringify(map)); } catch {}
  };
  // Book notes auto-sync to a Knowledge Base item (one per book, titled with
  // the book's name, filed under a "Books" domain) — no cover image required.
  // Re-adding notes updates that same item's content instead of duplicating it.
  const getOrCreateBooksDomain = async () => {
    const existing = knowledgeDomains.find(d=>(d.name||'').toLowerCase()==='books');
    if (existing) return existing;
    return await addKnowledgeDomain({ name:'Books', icon:'📚', sort_order: knowledgeDomains.length });
  };
  const syncBookKnowledge = async (book) => {
    const entries = book.entries||[];
    if (!entries.length || !addKnowledgeItem) return;
    const content = entries.map(e=>`${e.page?`p.${e.page} — `:''}${e.text}`).join('\n\n');
    const existing = knowledgeItems.find(k=>k.linked_book_id===book.id);
    if (existing) {
      updateKnowledgeItem(existing.id, { content });
    } else {
      const domain = await getOrCreateBooksDomain();
      addKnowledgeItem({
        type:'book', title: book.title, summary: book.author?`by ${book.author}`:'',
        content, domain_id: domain?.id||null, status:'active', linked_book_id: book.id,
      });
    }
  };
  const addEntryWithPage=bid=>{
    const text=(noteInputs[bid]||"").trim(); if(!text)return;
    const page=(notePageInputs[bid]||"").trim()||null;
    const entry={id:Date.now(),text,date:TODAY,page};
    setBooks(p=>{
      const next=p.map(b=>b.id!==bid?b:{...b,entries:[...(b.entries||[]),entry]});
      saveBookEntries(next);
      const updated = next.find(b=>b.id===bid);
      if (updated) syncBookKnowledge(updated);
      return next;
    });
    setNoteInputs(p=>({...p,[bid]:""}));
    setNotePageInputs(p=>({...p,[bid]:""}));
  };
  const sb = selectedBook ? books.find(b=>b.id===selectedBook) : null;

  return(
    <div style={{display:"grid",gridTemplateColumns: sb ? "1fr 380px" : "1fr",gap:20,alignItems:"start"}}>
    <div>
      <SectionMasthead
        eyebrow={`Knowledge · ${activeBooks.length} book${activeBooks.length!==1?'s':''}`}
        title={<>What you're <em>reading</em>.</>}
        actions={<>
          <button onClick={()=>setShowArchived(v=>!v)} className="ta-btn ta-btn-ghost">
            {showArchived?`Active (${activeBooks.length})`:`Archived (${archivedBooks.length})`}
          </button>
          {!showArchived && <button onClick={()=>{setEditBook(null);setForm({title:"",author:"",category:"",totalPages:0,currentPage:0,status:"reading",startDate:TODAY,finishDate:"",notes:"",rating:0});setShowForm(true);}} className="ta-btn ta-btn-primary">+ Add book</button>}
        </>}
      />
      {showForm&&(
        <div className="ta-card" style={{marginBottom:20}}>
          <h3 className="ta-serif" style={{margin:"0 0 14px",fontWeight:400,fontSize:20,color:"var(--ta-on-surface)"}}>{editBook?"Edit book":"Add book"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Author</label><input value={form.author} onChange={e=>setForm(p=>({...p,author:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Category</label><input value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} placeholder="Self-help, Academic, Biography…" style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Status</label><select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={{...inp(),width:"100%"}}>{["reading","to-read","completed","paused"].map(s=><option key={s}>{s}</option>)}</select></div>
            <div><label style={lbl}>Total Pages</label><input type="number" value={form.totalPages} onChange={e=>setForm(p=>({...p,totalPages:Number(e.target.value)}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Current Page</label><input type="number" value={form.currentPage} onChange={e=>setForm(p=>({...p,currentPage:Number(e.target.value)}))} style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Rating (0-5) {"⭐".repeat(form.rating)}</label><input type="number" min={0} max={5} value={form.rating} onChange={e=>setForm(p=>({...p,rating:Number(e.target.value)}))} style={{...inp(),width:"100%"}}/></div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lbl}>Book Cover Photo</label>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {form.cover_image && <img src={form.cover_image} alt="cover" style={{width:44,height:60,objectFit:"cover",borderRadius:"var(--r-sm)"}}/>}
                <label className="ta-btn ta-btn-secondary" style={{cursor:"pointer",display:"inline-flex"}}>
                  {coverUploading?"Uploading…":"📷 Upload cover"}
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>uploadCover(e.target.files[0])} disabled={coverUploading}/>
                </label>
                {form.cover_image && <button onClick={()=>setForm(p=>({...p,cover_image:""}))} className="ta-btn ta-btn-ghost">Remove</button>}
              </div>
            </div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Initial Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={ta(60)} placeholder="Your initial thoughts..."/></div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:12}}>
            <button onClick={save} className="ta-btn ta-btn-primary">{editBook?"Save":"Add book"}</button>
            <button onClick={()=>setShowForm(false)} className="ta-btn ta-btn-ghost">Cancel</button>
          </div>
        </div>
      )}
      {/* Archived books */}
      {showArchived && (
        <div>
          <div className="ta-card-inset" style={{marginBottom:14,fontSize:13,color:"var(--ta-on-surface-variant)"}}>
            📦 <strong>Book Archive</strong> — {archivedBooks.length} books. Their quotes still appear on your dashboard.
          </div>
          {archivedBooks.length===0 && <p style={{color:"var(--ta-on-surface-muted)"}}>No archived books.</p>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:14}}>
            {archivedBooks.map(b=>{
              return(
                <div key={b.id} style={{background:"var(--ta-surface-lowest)",borderRadius:"var(--r-lg)",padding:14,opacity:.7}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <div className="ta-serif" style={{fontSize:15,fontWeight:400,color:"var(--ta-on-surface-variant)"}}>{b.title}</div>
                      {b.author&&<div style={{fontSize:11,color:"var(--ta-on-surface-muted)"}}>{b.author}</div>}
                      <div className="ta-mono" style={{fontSize:9,color:"var(--ta-on-surface-muted)",marginTop:4}}>{(b.entries||[]).length} notes · {b.rating>0?"⭐".repeat(b.rating):""}</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <button onClick={()=>unarchiveBook&&unarchiveBook(b.id)} className="ta-btn-icon" style={{border:"none",cursor:"pointer",width:28,height:28}} title="Restore">↩</button>
                      <button onClick={()=>setBooks(p=>p.filter(x=>x.id!==b.id))} className="ta-btn-icon" style={{border:"none",cursor:"pointer",width:28,height:28,color:"var(--ta-negative)"}}>🗑</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shelf — grouped by status, click any book to view it in the side panel */}
      {!showArchived && (
      <div className="ta-col ta-gap-5">
        {activeBooks.length===0&&<p style={{color:C.light}}>No books yet. {archivedBooks.length>0&&`(${archivedBooks.length} archived)`}</p>}
        {BOOK_STATUS_SECTIONS.map(section=>{
          const sectionBooks = activeBooks.filter(b=>(b.status||'reading')===section.id);
          if (!sectionBooks.length) return null;
          return (
            <div key={section.id}>
              <div className="ta-section-head-meta" style={{marginBottom:10}}>{section.label} · {sectionBooks.length}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:14}}>
                {sectionBooks.map(b=>{
                  const pct=b.totalPages>0?Math.round(b.currentPage/b.totalPages*100):0,col=STATUS_COLORS[b.status]||C.green,spine=bookSpineColor(b.id);
                  return (
                    <div key={b.id} onClick={()=>setSelectedBook(b.id)}
                      style={{cursor:"pointer",background: sb?.id===b.id?"var(--ta-primary-fixed)":"transparent",borderRadius:"var(--r-md)",padding:8,transition:"background .15s"}}>
                      {b.cover_image ? (
                        <img src={b.cover_image} alt={b.title} style={{width:"100%",aspectRatio:"2/3",objectFit:"cover",borderRadius:"var(--r-sm)",boxShadow:"0 4px 12px rgba(26,28,28,.15)"}}/>
                      ) : (
                        <div style={{width:"100%",aspectRatio:"2/3",borderRadius:"var(--r-sm)",background:spine,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 12px rgba(26,28,28,.15)"}}>
                          <span className="ta-serif" style={{fontSize:28,fontWeight:400}}>{(b.title||"?").charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div className="ta-serif" style={{fontSize:14,fontWeight:400,color:"var(--ta-on-surface)",marginTop:8,lineHeight:1.25}}>{b.title}</div>
                      {b.author&&<div style={{fontSize:11,color:"var(--ta-on-surface-muted)",marginTop:2}}>{b.author}</div>}
                      {b.category&&<div className="ta-mono" style={{fontSize:9,color:"var(--ta-on-surface-muted)",marginTop:3,textTransform:"uppercase",letterSpacing:".04em"}}>{b.category}</div>}
                      {b.totalPages>0&&<div className="ta-bar" style={{marginTop:6}}><div className="ta-bar-fill" style={{width:`${pct}%`,background:col}}/></div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>

    {sb && (
      <div className="ta-card" style={{position:"sticky",top:16,maxHeight:"calc(100vh - 32px)",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
          <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            {sb.cover_image ? (
              <img src={sb.cover_image} alt={sb.title} style={{width:64,height:90,objectFit:"cover",borderRadius:"var(--r-sm)",flexShrink:0,boxShadow:"0 4px 12px rgba(26,28,28,.15)"}}/>
            ) : (
              <div style={{width:64,height:90,borderRadius:"var(--r-sm)",flexShrink:0,background:bookSpineColor(sb.id),color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 12px rgba(26,28,28,.15)"}}>
                <span className="ta-serif" style={{fontSize:22,fontWeight:400}}>{(sb.title||"?").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <h2 className="ta-serif" style={{margin:"0 0 4px",fontWeight:400,fontSize:19,color:"var(--ta-on-surface)",lineHeight:1.25}}>{sb.title}</h2>
              {sb.author&&<div style={{fontSize:12,color:"var(--ta-on-surface-muted)"}}>by {sb.author}</div>}
              <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap",alignItems:"center"}}>
                <span className="ta-chip">{sb.status}</span>
                {sb.category&&<span className="ta-chip" style={{background:"var(--ta-surface-high)"}}>{sb.category}</span>}
                {sb.rating>0&&<span style={{fontSize:11}}>{"⭐".repeat(sb.rating)}</span>}
              </div>
            </div>
          </div>
          <button onClick={()=>setSelectedBook(null)} className="ta-btn-icon" style={{border:"none",cursor:"pointer",flexShrink:0}}>✕</button>
        </div>

        {sb.totalPages>0&&(
          <div style={{marginBottom:14}}>
            <div className="ta-row ta-between" style={{marginBottom:4}}>
              <span className="ta-mono" style={{fontSize:10,color:"var(--ta-on-surface-muted)"}}>Page {sb.currentPage}/{sb.totalPages}</span>
              <span className="ta-mono tnum" style={{fontSize:10,color:"var(--ta-on-surface-muted)"}}>{Math.round(sb.currentPage/sb.totalPages*100)}%</span>
            </div>
            <div className="ta-bar"><div className="ta-bar-fill" style={{width:`${Math.round(sb.currentPage/sb.totalPages*100)}%`,background:STATUS_COLORS[sb.status]||C.green}}/></div>
          </div>
        )}

        <div className="ta-row ta-gap-2" style={{marginBottom:16,flexWrap:"wrap"}}>
          <button onClick={()=>{const pg=Number(prompt("Current page:",sb.currentPage));if(!isNaN(pg))setBooks(p=>p.map(x=>x.id===sb.id?{...x,currentPage:pg}:x));}} className="ta-btn ta-btn-secondary">Update page</button>
          <button onClick={()=>{setEditBook(sb.id);setForm({title:sb.title,author:sb.author||"",category:sb.category||"",totalPages:sb.totalPages||0,currentPage:sb.currentPage||0,status:sb.status||"reading",startDate:sb.startDate||TODAY,finishDate:sb.finishDate||"",notes:sb.notes||"",rating:sb.rating||0,cover_image:sb.cover_image||""});setShowForm(true);}} className="ta-btn ta-btn-ghost">Edit</button>
          <ShareButton type="book" data={{title:sb.title,author:sb.author,rating:sb.rating,entries:sb.entries||[],cover_image:sb.cover_image||""}}/>
          <button onClick={()=>{archiveBook&&archiveBook(sb.id);setSelectedBook(null);}} className="ta-btn ta-btn-ghost">Archive</button>
          <button onClick={()=>{if(window.confirm(`Delete "${sb.title}"?`)){setBooks(p=>p.filter(x=>x.id!==sb.id));setSelectedBook(null);}}} className="ta-btn ta-btn-ghost" style={{color:"var(--ta-negative)"}}>Delete</button>
        </div>

        {sb.notes&&<div className="ta-card-inset" style={{marginBottom:16}}><div className="ta-section-head-meta" style={{marginBottom:6}}>Initial notes</div><div className="ta-serif ta-italic" style={{fontSize:13,color:"var(--ta-on-surface-variant)",lineHeight:1.5}}>{sb.notes}</div></div>}

        <div className="ta-section-head"><h2 className="ta-section-head-h" style={{fontSize:16}}>Notes, quotes & insights</h2><span className="ta-section-head-meta">{(sb.entries||[]).length}</span></div>
        {(sb.entries||[]).length===0&&<div className="ta-serif ta-italic" style={{fontSize:13,color:"var(--ta-on-surface-muted)",marginBottom:10}}>No notes yet. Add one below.</div>}
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
          {(sb.entries||[]).map((e,i)=>(
            <div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:8}}>
              <div style={{flex:1}}>
                <div className="ta-mono" style={{fontSize:9,color:"var(--ta-on-surface-muted)",marginBottom:3,letterSpacing:".04em"}}>#{i+1} · {e.date}{e.page&&<span style={{marginLeft:6}}>· p.{e.page}</span>}</div>
                <div className="ta-serif ta-italic" style={{fontSize:14,color:"var(--ta-on-surface)",lineHeight:1.5}}>"{e.text}"</div>
              </div>
              <button onClick={()=>setBooks(p=>{const next=p.map(bk=>bk.id!==sb.id?bk:{...bk,entries:(bk.entries||[]).filter(x=>x.id!==e.id)});saveBookEntries(next);return next;})} className="ta-btn-icon" style={{border:"none",cursor:"pointer",width:20,height:20,flexShrink:0}}>✕</button>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,borderTop:"1px solid var(--ta-outline-variant)",paddingTop:14}}>
          <div className="ta-row ta-gap-2">
            <input type="number" value={notePageInputs[sb.id]||""} onChange={e=>setNotePageInputs(p=>({...p,[sb.id]:e.target.value}))} placeholder="pg#" className="ta-input" style={{width:56,textAlign:"center"}} title="Page number (optional)"/>
            <input value={noteInputs[sb.id]||""} onChange={e=>setNoteInputs(p=>({...p,[sb.id]:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addEntryWithPage(sb.id)} placeholder="Add a note, quote, or insight and press Enter..." className="ta-input" style={{flex:1}}/>
          </div>
          <button onClick={()=>addEntryWithPage(sb.id)} className="ta-btn ta-btn-primary">+ Add</button>
        </div>
      </div>
    )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION TRACKER
// ══════════════════════════════════════════════════════════════════════════════
function SubTracker({ctx}){
  const {subs,setSubs}=ctx;
  const [form,setForm]=useState({name:"",amount:"",frequency:"monthly",renewalDate:"",category:"Entertainment",active:true});
  const FREQS=["monthly","yearly","weekly","quarterly"];
  const CATS=["Entertainment","SaaS","Health","Finance","Education","Other"];
  const add=()=>{if(!form.name.trim()||!form.amount)return;ctx.addSub({...form,amount:Number(form.amount)});setForm({name:"",amount:"",frequency:"monthly",renewalDate:"",category:"Entertainment",active:true});};
  const monthlyEq=s=>{if(s.frequency==="monthly")return s.amount;if(s.frequency==="yearly")return s.amount/12;if(s.frequency==="weekly")return s.amount*4.33;if(s.frequency==="quarterly")return s.amount/3;return 0;};
  const monthlyTotal=subs.filter(s=>s.active).reduce((sum,s)=>sum+monthlyEq(s),0);
  return(
    <div>
      <SectionMasthead eyebrow="System · Recurring costs" title={<>What renews <em>quietly</em>.</>} />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
        <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${C.green}`,borderRadius:9,padding:14,textAlign:"center"}}><div style={{fontSize:11,color:C.muted}}>Monthly Cost</div><div style={{fontSize:22,fontWeight:"bold",color:C.green}}>₹{Math.round(monthlyTotal).toLocaleString()}</div></div>
        <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${C.amber}`,borderRadius:9,padding:14,textAlign:"center"}}><div style={{fontSize:11,color:C.muted}}>Yearly Cost</div><div style={{fontSize:22,fontWeight:"bold",color:C.amber}}>₹{Math.round(monthlyTotal*12).toLocaleString()}</div></div>
      </div>
      <div style={{fontSize:11,color:C.muted,marginBottom:14,marginTop:-6}}>ℹ️ Tracked separately — subscription costs above are not included in Finance's Total/Monthly Expenses.</div>
      <Card title="+ Add Subscription" color={C.blue}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr auto",gap:8,alignItems:"end"}}>
          <div><label style={lbl}>Name</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
          <div><label style={lbl}>Amount (₹)</label><input type="number" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
          <div><label style={lbl}>Frequency</label><select value={form.frequency} onChange={e=>setForm(p=>({...p,frequency:e.target.value}))} style={{...inp(),width:"100%"}}>{FREQS.map(f=><option key={f}>{f}</option>)}</select></div>
          <div><label style={lbl}>Renewal</label><input type="date" value={form.renewalDate} onChange={e=>setForm(p=>({...p,renewalDate:e.target.value}))} style={{...inp(),width:"100%"}}/></div>
          <div><label style={lbl}>Category</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={{...inp(),width:"100%"}}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
          <button onClick={add} style={{...btn(C.blue),alignSelf:"flex-end"}}>+ Add</button>
        </div>
      </Card>
      <div style={{display:"grid",gap:8,marginTop:14}}>
        {subs.length===0&&<p style={{color:C.light}}>No subscriptions tracked yet.</p>}
        {subs.map(s=>{const daysLeft=s.renewalDate?daysBetween(TODAY,s.renewalDate):null,urgent=daysLeft!==null&&daysLeft<=7;return(
          <div key={s.id} style={{background:C.surface,border:`1.5px solid ${urgent?C.red:C.border}`,borderRadius:9,padding:12,display:"flex",alignItems:"center",gap:12}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontWeight:"bold",fontSize:14}}>{s.name}</span><span style={{fontSize:9,background:s.active?C.greenM:C.surfaceAlt,color:s.active?C.green:C.muted,borderRadius:10,padding:"1px 6px"}}>{s.active?"Active":"Paused"}</span><span style={{fontSize:9,background:C.amberL,color:C.amber,borderRadius:10,padding:"1px 6px"}}>{s.category}</span></div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>₹{s.amount.toLocaleString()}/{s.frequency} · ~₹{Math.round(monthlyEq(s))}/mo {s.renewalDate&&`· Renews ${s.renewalDate}`} {urgent&&<span style={{color:C.red,fontWeight:"bold"}}>⚠️ {daysLeft}d!</span>}</div>
            </div>
            <button onClick={()=>{if(ctx.updateSub)ctx.updateSub(s.id,{active:!s.active});else setSubs(p=>p.map(x=>x.id===s.id?{...x,active:!x.active}:x));}} style={btn(s.active?C.amber:C.green)}>{s.active?"Pause":"Activate"}</button>
            <button onClick={()=>ctx.deleteSub(s.id)} style={btn(C.red,32)}>🗑</button>
          </div>
        );})}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// IDEAS BOARD
// ══════════════════════════════════════════════════════════════════════════════
function IdeasBoard({ctx}){
  const C = useC();
  const {ideas}=ctx;
  const [showForm,setShowForm]=useState(false);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState({name:"",desc:"",tags:""});
  const [viewIdea,setViewIdea]=useState(null); // full view modal
  const saveIdea=()=>{
    if(!form.name.trim())return;
    const tags=form.tags.split(",").map(t=>t.trim()).filter(Boolean);
    if(editId){ctx.updateIdea(editId,{name:form.name,description:form.desc,tags});setEditId(null);}
    else ctx.addIdea({name:form.name,desc:form.desc,date:TODAY,tags});
    setForm({name:"",desc:"",tags:""});setShowForm(false);
  };
  return(
    <div>
      <SectionMasthead
        eyebrow="Knowledge · The capture board"
        title={<>Before it <em>slips</em>.</>}
        actions={<button onClick={()=>setShowForm(true)} className="ta-btn ta-btn-primary">+ New idea</button>}
      />
      {showForm&&<div style={{background:C.surface,border:`1.5px solid ${C.amber}`,borderRadius:12,padding:16,marginBottom:16}}>
        <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Idea name *" style={{...inp(),width:"100%",marginBottom:7}}/>
        <textarea value={form.desc} onChange={e=>setForm(p=>({...p,desc:e.target.value}))} placeholder="Describe freely..." style={{...ta(90),marginBottom:7}}/>
        <input value={form.tags} onChange={e=>setForm(p=>({...p,tags:e.target.value}))} placeholder="Tags (comma separated)" style={{...inp(),width:"100%",marginBottom:10}}/>
        <div style={{display:"flex",gap:6}}><button onClick={saveIdea} style={btn(C.amber)}>💾 {editId?"Update":"Save"}</button><button onClick={()=>{setShowForm(false);setEditId(null);setForm({name:"",desc:"",tags:""}); }} style={btn("#999")}>✕</button></div>
      </div>}

      {/* Full view modal */}
      {viewIdea && ReactDOM.createPortal(
        <div style={{position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}} onClick={()=>setViewIdea(null)}>
          <div style={{background:C.surface,border:`2px solid ${C.amber}`,borderRadius:16,padding:28,width:"100%",maxWidth:560,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,.35)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
              <h3 style={{margin:0,color:C.amber,fontSize:18,flex:1,paddingRight:12}}>{viewIdea.name}</h3>
              <button onClick={()=>setViewIdea(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:18,flexShrink:0}}>✕</button>
            </div>
            <p style={{margin:"0 0 16px",fontSize:14,color:C.text,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{viewIdea.desc||viewIdea.description}</p>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:"auto"}}>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{(viewIdea.tags||[]).map((tag,i)=><span key={i} style={{fontSize:11,background:C.amberL,color:C.amber,border:`1px solid ${C.amber}`,borderRadius:8,padding:"2px 8px"}}>{tag}</span>)}</div>
              <span style={{fontSize:11,color:C.muted}}>{viewIdea.date||viewIdea.idea_date}</span>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
        {ideas.length===0&&<p style={{color:C.light}}>No ideas yet.</p>}
        {[...ideas].reverse().map(idea=>(
          <div key={idea.id} style={{background:C.amberL,border:`1.5px solid ${C.amber}`,borderRadius:10,padding:14,display:"flex",flexDirection:"column",height:160,boxSizing:"border-box",cursor:"pointer"}} onClick={()=>setViewIdea(idea)}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4,flexShrink:0}}>
              <h4 style={{margin:0,color:C.amber,fontSize:13,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:4}}>{idea.name}</h4>
              <div style={{display:"flex",gap:3,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                <button onClick={e=>{e.stopPropagation();setEditId(idea.id);setForm({name:idea.name,desc:idea.desc||idea.description||"",tags:(idea.tags||[]).join(", ")});setShowForm(true);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:12}}>✏️</button>
                <button onClick={e=>{e.stopPropagation();ctx.deleteIdea(idea.id);}} style={{background:"none",border:"none",cursor:"pointer",color:C.light,fontSize:12}}>✕</button>
              </div>
            </div>
            <p style={{margin:"0 0 6px",fontSize:11,color:"#555",flex:1,overflow:"hidden",lineHeight:1.5,wordBreak:"break-word"}}>{idea.desc||idea.description}</p>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,borderTop:`1px solid ${C.amber}30`,paddingTop:5,marginTop:"auto"}}>
              <div style={{display:"flex",gap:3,flexWrap:"wrap",overflow:"hidden",maxHeight:18}}>{(idea.tags||[]).map((tag,i)=><span key={i} style={{fontSize:9,background:"#fff",color:C.amber,border:`1px solid ${C.amber}`,borderRadius:8,padding:"1px 5px"}}>{tag}</span>)}</div>
              <span style={{fontSize:9,color:"#888",flexShrink:0,marginLeft:4}}>{idea.date||idea.idea_date}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS
// ══════════════════════════════════════════════════════════════════════════════
// eslint-disable-next-line no-unused-vars
function SettingsPage({ctx}){
  const {checkinDefs,setCheckinDefs,reminders,setReminders,backups,createBackup,importBackup}=ctx;
  const [newCi,setNewCi]=useState({label:"",icon:""});
  const [editCi,setEditCi]=useState(null);
  const fileRef=useRef(null);
  const addCi=()=>{if(!newCi.label.trim())return;setCheckinDefs(p=>[...p,{id:String(Date.now()),...newCi}]);setNewCi({label:"",icon:""});};
  return(
    <div>
      <h2 style={{color:C.green,marginBottom:16}}>⚙️ Settings</h2>

      {/* Data persistence notice */}
      <div style={{background:C.blueL,border:`1.5px solid ${C.blue}`,borderRadius:10,padding:14,marginBottom:16}}>
        <h4 style={{margin:"0 0 8px",color:C.blue}}>💾 Data Persistence</h4>
        <p style={{fontSize:12,color:C.muted,margin:0}}>Your data is saved automatically to persistent storage every time you make a change, and again when you close or switch tabs. Data survives browser closes. <strong>Download a JSON backup regularly</strong> as an extra safety net — import it any time to restore everything.</p>
      </div>

      {/* ── Daily Check-ins Manager ── */}
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:18,boxShadow:"var(--shadow-sm)",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <span style={{fontSize:13,fontWeight:700,color:C.text}}>☑ Daily Check-ins</span>
          <span style={{fontSize:11,color:C.muted}}>{checkinDefs.length} item{checkinDefs.length!==1?"s":""}</span>
        </div>
        <p style={{fontSize:12,color:C.muted,margin:"0 0 14px"}}>These appear as toggles on your dashboard each day.</p>

        {/* Existing check-ins */}
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
          {checkinDefs.length===0&&(
            <div style={{textAlign:"center",padding:"20px",background:"var(--surface-alt)",borderRadius:"var(--radius-sm)",color:C.muted,fontSize:12}}>
              No check-ins yet. Add your first one below.
            </div>
          )}
          {checkinDefs.map(ci=>(
            <div key={ci.id} style={{display:"flex",alignItems:"center",gap:8,background:"var(--surface-alt)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",padding:"8px 12px",transition:"border-color var(--t)"}}>
              {editCi?.id===ci.id ? (
                <>
                  <input value={editCi.icon} onChange={e=>setEditCi(p=>({...p,icon:e.target.value}))} style={{...inp(),width:48,fontSize:18,textAlign:"center",padding:"4px"}}/>
                  <input value={editCi.label} onChange={e=>setEditCi(p=>({...p,label:e.target.value}))} style={{...inp(),flex:1}} onKeyDown={e=>e.key==="Enter"&&(setCheckinDefs(p=>p.map(x=>x.id===ci.id?{...ci,...editCi}:x)),setEditCi(null))}/>
                  <button onClick={()=>{setCheckinDefs(p=>p.map(x=>x.id===ci.id?{...ci,...editCi}:x));setEditCi(null);}} style={{padding:"5px 10px",borderRadius:7,border:"none",background:C.green,color:"#fff",cursor:"pointer",fontWeight:700,fontSize:12}}>✓</button>
                  <button onClick={()=>setEditCi(null)} style={{padding:"5px 8px",borderRadius:7,border:"1px solid var(--border)",background:"transparent",color:C.muted,cursor:"pointer",fontSize:12}}>✕</button>
                </>
              ) : (
                <>
                  <span style={{fontSize:20,minWidth:28,textAlign:"center"}}>{ci.icon||"☑️"}</span>
                  <span style={{flex:1,fontSize:13,color:C.text,fontWeight:500}}>{ci.label}</span>
                  <button onClick={()=>setEditCi({id:ci.id,icon:ci.icon||"☑️",label:ci.label})} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:C.muted,cursor:"pointer",fontSize:11,transition:"all var(--t-fast)"}} onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--primary)";e.currentTarget.style.color="var(--primary)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color=C.muted;}}>✏️ Edit</button>
                  <button onClick={()=>setCheckinDefs(p=>p.filter(x=>x.id!==ci.id))} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${C.red}22`,background:"var(--red-bg,#fef2f2)",color:C.red,cursor:"pointer",fontSize:11}}>🗑</button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add new */}
        <div style={{display:"flex",gap:8,paddingTop:12,borderTop:"1px solid var(--border)"}}>
          <input value={newCi.icon} onChange={e=>setNewCi(p=>({...p,icon:e.target.value}))} placeholder="🌟" style={{...inp(),width:52,fontSize:18,textAlign:"center",padding:"8px 4px"}}/>
          <input value={newCi.label} onChange={e=>setNewCi(p=>({...p,label:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addCi()} placeholder="e.g. Morning run, Meditation, Read 30 mins..." style={{...inp(),flex:1}}/>
          <button onClick={addCi} style={{padding:"8px 18px",borderRadius:"var(--radius-sm)",border:"none",background:"var(--primary)",color:"#fff",cursor:"pointer",fontWeight:700,fontSize:13,flexShrink:0}}>+ Add</button>
        </div>
      </div>

      <div style={{marginTop:14}}>
        <Card title="⏰ All Reminders" color={C.blue}>
          {reminders.length===0&&<p style={{color:C.light,fontSize:12,margin:0}}>No reminders set.</p>}
          {[...reminders].reverse().map(r=>(
            <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:`1px solid ${C.border}`,opacity:r.dismissed?.5:1}}>
              <span style={{fontSize:12,flex:1}}>{r.text}</span>
              <span style={{fontSize:11,color:C.muted}}>{r.datetime?.replace("T"," ")}</span>
              {r.dismissed&&<span style={{fontSize:10,color:C.green}}>✓</span>}
              <button onClick={()=>setReminders(p=>p.filter(x=>x.id!==r.id))} style={{background:"none",border:"none",cursor:"pointer",color:C.light}}>🗑</button>
            </div>
          ))}
        </Card>
      </div>

      <div style={{marginTop:14}}>
        <Card title="⬇️ Backup & Restore" color={C.amber}>
          <p style={{fontSize:12,color:C.muted,marginTop:0}}>Download complete diary as JSON. Import to restore from any backup file.</p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button onClick={createBackup} style={btn(C.amber)}>⬇️ Download Backup</button>
            <button onClick={()=>fileRef.current?.click()} style={btn(C.blue)}>⬆️ Import Backup</button>
            <input ref={fileRef} type="file" accept=".json" onChange={importBackup} style={{display:"none"}}/>
          </div>
          {backups.length>0&&<div style={{marginTop:12}}>
            <div style={{fontSize:11,fontWeight:"bold",color:C.muted,marginBottom:6}}>Last 3 Backups</div>
            {backups.map(b=><div key={b.id} style={{fontSize:12,padding:"4px 0",borderBottom:`1px solid ${C.border}`}}>📁 {b.label}</div>)}
          </div>}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONTENT STRATEGY
// Standard content planning system: Strategy → Pillars → Content Calendar
// ══════════════════════════════════════════════════════════════════════════════

const PLATFORMS = ["LinkedIn","Instagram","Twitter/X","YouTube","Newsletter","Blog","Podcast","TikTok"];
const POST_TYPES = ["Educational","Storytelling","Promotional","Engagement","Behind-the-Scenes","Case Study","Opinion","How-To","List","Repurposed"];
const POST_STATUSES = [
  { id:"idea",       label:"💡 Idea",       color:"#A0997A" },
  { id:"draft",      label:"✏️ Draft",       color:"#1D5F8A" },
  { id:"ready",      label:"✅ Ready",       color:"#C8860A" },
  { id:"scheduled",  label:"📅 Scheduled",   color:"#6B3FA0" },
  { id:"published",  label:"🚀 Published",   color:"#2D5016" },
];
const CONTENT_COLOR = "#8B2FC9";
const CONTENT_LIGHT = "#F3E8FF";

function ContentStrategy({ ctx }) {
  const C = useC();
  const { contentPillars, setContentPillars, contentPosts, setContentPosts, contentStrategy, setContentStrategy,
          contentStrategies, addContentStrategy2, updateContentStrategy2, deleteContentStrategy2 } = ctx;
  const [tab, setTab] = useState("social");

  const tabs = [
    { id:"social",    label:"🔗 Social Hub"  },
    { id:"scheduler", label:"📅 Scheduler"   },
    { id:"board",     label:"🗂 Board"       },
    { id:"engagement",label:"📊 Engagement"  },
    { id:"dms",       label:"💬 DM Engine"   },
    { id:"viral",     label:"🚀 Viral Engine"},
    { id:"strategy",  label:"🎯 Strategies"  },
    { id:"pillars",   label:"🏛 Pillars"     },
  ];

  return (
    <div>
      <div className="ta-row ta-between" style={{alignItems:"flex-end",marginBottom:20,flexWrap:"wrap",gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Knowledge · Plan · Create · Publish · Grow</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Say it <em>well</em>.</h1>
        </div>
        <div style={{display:"flex",gap:4,background:"var(--ta-surface-low)",borderRadius:"var(--r-md)",padding:4,flexWrap:"wrap"}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              background:tab===t.id?CONTENT_COLOR:"transparent",
              color:tab===t.id?"#fff":C.muted,
              border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",
              fontFamily:FONT,fontSize:12,fontWeight:tab===t.id?"bold":"normal",
              whiteSpace:"nowrap"
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {tab==="social"    && <SocialHubTab     ctx={ctx} />}
      {tab==="scheduler" && <SchedulerTab      ctx={ctx} />}
      {tab==="board"     && <BoardTab posts={contentPosts} setPosts={setContentPosts} pillars={contentPillars} onAddPost={ctx.addContentPost} onUpdatePost={ctx.updateContentPost} onDeletePost={ctx.deleteContentPost} />}
      {tab==="engagement"&& <EngagementTab     ctx={ctx} />}
      {tab==="dms"       && <DMEngineTab       ctx={ctx} />}
      {tab==="viral"     && <ViralEngineTab    ctx={ctx} />}
      {tab==="strategy"  && <MultiStrategyTab strategies={contentStrategies} onAdd={addContentStrategy2} onUpdate={updateContentStrategy2} onDelete={deleteContentStrategy2} posts={contentPosts} />}
      {tab==="pillars"   && <PillarsTab pillars={contentPillars} setPillars={setContentPillars} posts={contentPosts} />}

    </div>
  );
}

// ── Multi-Strategy Tab: multiple named content strategies ────────────────────
function MultiStrategyTab({ strategies, onAdd, onUpdate, onDelete, posts }) {
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew]   = useState(false);
  const [newName, setNewName]   = useState("");

  const MIX_OPTIONS = ["Educational","Entertainment","Promotional","Storytelling","Engagement","Behind-the-Scenes"];
  const activeStrat = (strategies||[]).find(s=>s.id===selected);

  const createStrat = () => {
    if (!newName.trim()) return;
    onAdd({ name: newName.trim(), brand_voice:"", target_audience:"", monthly_goal:"", content_mix:[] });
    setNewName(""); setShowNew(false);
  };

  return (
    <div style={{display:"flex",gap:16}}>
      {/* Sidebar list */}
      <div style={{width:220,flexShrink:0}}>
        <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",background:CONTENT_COLOR,color:"#fff",fontWeight:700,fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>📋 Strategies</span>
            <button onClick={()=>setShowNew(true)} style={{background:"rgba(255,255,255,.2)",border:"none",color:"#fff",borderRadius:6,padding:"2px 8px",cursor:"pointer",fontSize:12}}>+ New</button>
          </div>
          {showNew && (
            <div style={{padding:"8px 10px",borderBottom:`1px solid ${C.border}`,background:C.surfaceAlt}}>
              <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&createStrat()} placeholder="Strategy name..." style={{...inp(),width:"100%",fontSize:12,marginBottom:6}} autoFocus/>
              <div style={{display:"flex",gap:5}}>
                <button onClick={createStrat} style={{...btn(CONTENT_COLOR),fontSize:11,padding:"4px 10px",flex:1}}>Create</button>
                <button onClick={()=>{setShowNew(false);setNewName("");}} style={{...btn("#999"),fontSize:11,padding:"4px 8px"}}>✕</button>
              </div>
            </div>
          )}
          {!(strategies||[]).length && <div style={{padding:"20px 14px",textAlign:"center",color:C.light,fontSize:12}}>No strategies yet.<br/>Click + New to start.</div>}
          {(strategies||[]).map(s=>(
            <div key={s.id} onClick={()=>setSelected(s.id)}
              style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:selected===s.id?CONTENT_LIGHT:"transparent",borderLeft:selected===s.id?`3px solid ${CONTENT_COLOR}`:"3px solid transparent",transition:"background .1s"}}>
              <div style={{fontWeight:600,fontSize:13,color:selected===s.id?CONTENT_COLOR:C.text}}>{s.name}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{(s.content_mix||[]).length} mix items</div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      <div style={{flex:1}}>
        {!activeStrat ? (
          <div style={{textAlign:"center",padding:"60px 20px",color:C.muted}}>
            <div style={{fontSize:48,marginBottom:12}}>📋</div>
            <div style={{fontSize:14}}>Select or create a content strategy to edit it.</div>
          </div>
        ) : (
          <div style={{display:"grid",gap:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h3 style={{margin:0,color:CONTENT_COLOR,fontSize:18}}>{activeStrat.name}</h3>
              <button onClick={()=>{if(window.confirm(`Delete strategy "${activeStrat.name}"?`)){onDelete(activeStrat.id);setSelected(null);}}} style={{...btn(C.red),fontSize:11,padding:"5px 10px"}}>🗑 Delete</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <Card title="🎤 Brand Voice & Tone" color={CONTENT_COLOR}>
                <textarea value={activeStrat.brand_voice||""} onChange={e=>onUpdate(activeStrat.id,{brand_voice:e.target.value})}
                  placeholder={"Describe how you sound:\n• Tone (e.g. professional but warm)\n• Language style\n• Personality traits"}
                  style={ta(130)}/>
              </Card>
              <Card title="🎯 Target Audience" color={C.blue}>
                <textarea value={activeStrat.target_audience||""} onChange={e=>onUpdate(activeStrat.id,{target_audience:e.target.value})}
                  placeholder={"Who are you talking to:\n• Role / job title\n• Pain points\n• Where they spend time online"}
                  style={ta(130)}/>
              </Card>
              <Card title="🗓 Monthly Content Goal" color={C.amber}>
                <textarea value={activeStrat.monthly_goal||""} onChange={e=>onUpdate(activeStrat.id,{monthly_goal:e.target.value})}
                  placeholder={"e.g. 20 posts/month across LinkedIn + Newsletter\n- 3x LinkedIn per week\n- 1x Newsletter per week"}
                  style={ta(90)}/>
              </Card>
              <Card title="🔀 Content Mix" color={C.teal}>
                <div style={{marginBottom:8,fontSize:12,color:C.muted}}>Select your ideal content mix:</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {MIX_OPTIONS.map(opt => {
                    const sel = (activeStrat.content_mix||[]).includes(opt);
                    return (
                      <button key={opt} onClick={()=>onUpdate(activeStrat.id,{content_mix:sel?(activeStrat.content_mix||[]).filter(x=>x!==opt):[...(activeStrat.content_mix||[]),opt]})}
                        style={{padding:"4px 10px",borderRadius:14,border:`1.5px solid ${sel?C.teal:C.border}`,background:sel?C.tealL:C.surface,
                          color:sel?C.teal:C.muted,cursor:"pointer",fontFamily:FONT,fontSize:11,fontWeight:sel?"bold":"normal"}}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pillars Tab: content pillars / themes ────────────────────────────────────
function PillarsTab({ pillars, setPillars, posts }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:"", description:"", color:CONTENT_COLOR, icon:"" });

  
  const add = () => {
    if (!form.name.trim()) return;
    setPillars(p => [...p, { ...form }]);
    setForm({ name:"", description:"", color:CONTENT_COLOR, icon:"" });
    setShowForm(false);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:13,color:C.muted,maxWidth:600}}>
            Content pillars are the 3–6 core themes your content always comes back to. They define your expertise and give your audience a reason to follow you.
          </div>
        </div>
        <button onClick={()=>setShowForm(true)} style={btn(CONTENT_COLOR)}>+ Add Pillar</button>
      </div>

      {showForm && (
        <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:18,marginBottom:16}}>
          <h4 style={{margin:"0 0 12px",color:CONTENT_COLOR}}>New Content Pillar</h4>
          <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:10,alignItems:"start",marginBottom:10}}>
            <div>
              <label style={lbl}>Icon</label>
              <input value={form.icon} onChange={e=>setForm(p=>({...p,icon:e.target.value}))} placeholder="🎯" style={{...inp(),width:56,textAlign:"center",fontSize:20}}/>
            </div>
            <div>
              <label style={lbl}>Pillar Name *</label>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Leadership Insights, AI & Tech, Personal Stories..." style={{...inp(),width:"100%"}}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={lbl}>What this pillar is about</label>
            <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
              placeholder="Describe the topics, angles and value this pillar gives your audience..." style={ta(70)}/>
          </div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Pillar Colour</label>
            <div style={{display:"flex",gap:6}}>
              {PILLAR_COLORS.map(col=>(
                <button key={col} onClick={()=>setForm(p=>({...p,color:col}))}
                  style={{width:28,height:28,borderRadius:"50%",background:col,border:`3px solid ${form.color===col?"#fff":"transparent"}`,outline:`2px solid ${form.color===col?col:"transparent"}`,cursor:"pointer"}}/>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={add} style={btn(CONTENT_COLOR)}>💾 Save Pillar</button>
            <button onClick={()=>setShowForm(false)} style={btn("#999")}>Cancel</button>
          </div>
        </div>
      )}

      {pillars.length===0 && <div style={{textAlign:"center",padding:"40px 20px",color:C.light,fontSize:13}}>
        <div style={{fontSize:40,marginBottom:10}}>🏛</div>
        No content pillars yet. Add 3–6 core themes that define your expertise.
      </div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
        {pillars.map(pil => {
          const pilPosts = posts.filter(p=>(p.pillarId||p.pillar_id)===pil.id);
          const pubPosts = pilPosts.filter(p=>p.status==="published").length;
          return (
            <div key={pil.id} style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`4px solid ${pil.color}`,borderRadius:10,padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {pil.icon && <span style={{fontSize:22}}>{pil.icon}</span>}
                  <div>
                    <div style={{fontWeight:"bold",color:pil.color,fontSize:14}}>{pil.name}</div>
                    <div style={{fontSize:10,color:C.muted}}>{pilPosts.length} posts · {pubPosts} published</div>
                  </div>
                </div>
                <button onClick={()=>setPillars(p=>p.filter(x=>x.id!==pil.id))} style={{background:"none",border:"none",cursor:"pointer",color:C.light,fontSize:14,padding:2}}>🗑</button>
              </div>
              {pil.description && <p style={{margin:"0 0 10px",fontSize:12,color:C.muted,lineHeight:1.5}}>{pil.description}</p>}
              {pilPosts.length>0 && (
                <div style={{background:C.surfaceAlt,borderRadius:6,padding:"4px 0"}}>
                  <div style={{height:6,borderRadius:4,background:C.border,margin:"4px 8px"}}>
                    <div style={{width:`${pubPosts/pilPosts.length*100}%`,background:pil.color,height:"100%",borderRadius:4}}/>
                  </div>
                  <div style={{fontSize:10,color:C.muted,textAlign:"center"}}>{pubPosts}/{pilPosts.length} published</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Shared: New Post Form ────────────────────────────────────────────────────
function PostForm({ pillars, onSave, onCancel, initial }) {
  const blank = { title:"", pillarId:"", platform:"LinkedIn", type:"Educational", status:"idea", scheduledDate:"", notes:"", hook:"", cta:"", tags:"" };
  const [form, setForm] = useState(initial || blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  return (
    <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:18,marginBottom:16}}>
      <h4 style={{margin:"0 0 14px",color:CONTENT_COLOR}}>{initial?"Edit":"New"} Content</h4>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
        <div style={{gridColumn:"1/-1"}}>
          <label style={lbl}>Content Title / Topic *</label>
          <input value={form.title} onChange={e=>upd("title",e.target.value)} placeholder="What is this piece of content about?" style={{...inp(),width:"100%",fontSize:14}}/>
        </div>
        <div>
          <label style={lbl}>Content Pillar</label>
          <select value={form.pillarId} onChange={e=>upd("pillarId",e.target.value)} style={{...inp(),width:"100%"}}>
            <option value="">— No Pillar —</option>
            {pillars.map(p=><option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Platform</label>
          <select value={form.platform} onChange={e=>upd("platform",e.target.value)} style={{...inp(),width:"100%"}}>
            {PLATFORMS.map(p=><option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Content Type</label>
          <select value={form.type} onChange={e=>upd("type",e.target.value)} style={{...inp(),width:"100%"}}>
            {POST_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Status</label>
          <select value={form.status} onChange={e=>upd("status",e.target.value)} style={{...inp(),width:"100%"}}>
            {POST_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Scheduled / Publish Date</label>
          <input type="date" value={form.scheduledDate} onChange={e=>upd("scheduledDate",e.target.value)} style={{...inp(),width:"100%"}}/>
        </div>
        <div>
          <label style={lbl}>Tags (comma separated)</label>
          <input value={form.tags} onChange={e=>upd("tags",e.target.value)} placeholder="leadership, ai, story..." style={{...inp(),width:"100%"}}/>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <label style={lbl}>Hook / Opening Line</label>
          <input value={form.hook} onChange={e=>upd("hook",e.target.value)} placeholder="The first line that stops the scroll..." style={{...inp(),width:"100%"}}/>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <label style={lbl}>CTA (Call to Action)</label>
          <input value={form.cta} onChange={e=>upd("cta",e.target.value)} placeholder="What do you want people to do after reading?" style={{...inp(),width:"100%"}}/>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <label style={lbl}>Notes / Draft / Key Points</label>
          <textarea value={form.notes} onChange={e=>upd("notes",e.target.value)} placeholder="Write your key points, draft, or any notes here..." style={ta(100)}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>{ if(!(form.title||"").trim())return; onSave({...form,id:initial?.id||undefined,tags:(form.tags||"").split(",").map(t=>t.trim()).filter(Boolean)}); }} style={btn(CONTENT_COLOR)}>💾 Save</button>
        <button onClick={onCancel} style={btn("#999")}>Cancel</button>
      </div>
    </div>
  );
}

// ── Calendar Tab: monthly grid view ─────────────────────────────────────────
function CalendarTab({ posts, setPosts, pillars, onAddPost, onUpdatePost, onDeletePost }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [showForm, setShowForm] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [prefillDate, setPrefillDate] = useState("");

  const mk = `${year}-${String(month+1).padStart(2,"0")}`;
  // Monday-first: shift Sun(0) → pos 6
  const _cdow=new Date(year,month,1).getDay();
  const firstDow=(_cdow+6)%7;
  const daysInM  = new Date(year,month+1,0).getDate();
  const dates = Array.from({length:daysInM},(_,i)=>fmt(new Date(year,month,i+1)));

  const postsByDate = {};
  posts.forEach(p=>{
    const sd = p.scheduledDate || p.scheduled_date;
    if(!sd || !sd.startsWith(mk)) return;
    if(!postsByDate[sd]) postsByDate[sd]=[];
    postsByDate[sd].push({...p, scheduledDate: sd});
  });

  const savePost = (p) => {
    if (editPost && p.id) {
      // Update existing post
      if (onUpdatePost) onUpdatePost(p.id, p);
      else setPosts(prev=>prev.map(x=>x.id===p.id?p:x));
    } else {
      // New post — strip id so DB generates UUID
      const {id,...rest} = p;
      if (onAddPost) onAddPost(rest);
      else setPosts(prev=>[...prev,rest]);
    }
    setShowForm(false); setEditPost(null); setPrefillDate("");
  };

  const statusOf = (p) => POST_STATUSES.find(s=>s.id===p.status)||POST_STATUSES[0]; // eslint-disable-line no-unused-vars

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <h3 style={{margin:0,color:CONTENT_COLOR,flex:1}}>📅 {MONTH_NAMES[month]} {year}</h3>
        <button onClick={()=>{if(month===0){setYear(y=>y-1);setMonth(11);}else setMonth(m=>m-1);}} style={btn("#888")}>← Prev</button>
        <button onClick={()=>{if(month===11){setYear(y=>y+1);setMonth(0);}else setMonth(m=>m+1);}} style={btn("#888")}>Next →</button>
        <button onClick={()=>{setPrefillDate("");setEditPost(null);setShowForm(true);}} style={btn(CONTENT_COLOR)}>+ Add Content</button>
      </div>

      {(showForm||editPost) && (
        <PostForm pillars={pillars} initial={editPost?{...editPost,tags:(editPost.tags||[]).join(", ")}:{scheduledDate:prefillDate}}
          onSave={savePost} onCancel={()=>{setShowForm(false);setEditPost(null);setPrefillDate("");}}/>
      )}

      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:12,padding:10}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6}}>
          {MON_NAMES.map(d=><div key={d} style={{textAlign:"center",fontSize:10,fontWeight:"bold",color:C.muted,padding:"4px 0"}}>{d}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {Array.from({length:firstDow},(_,i)=><div key={`e${i}`}/>)}
          {dates.map(d=>{
            const dn=new Date(d+"T00:00:00").getDate();
            const isToday=d===TODAY;
            const dayPosts=postsByDate[d]||[];
            return(
              <div key={d} onClick={()=>{setPrefillDate(d);setEditPost(null);setShowForm(true);}}
                style={{border:`1px solid ${isToday?CONTENT_COLOR:C.border}`,borderRadius:6,padding:"5px 4px",minHeight:72,
                  background:isToday?CONTENT_LIGHT:C.bg,cursor:"pointer",transition:"background .1s"}}
                onMouseEnter={e=>e.currentTarget.style.background=isToday?CONTENT_LIGHT:C.surfaceAlt}
                onMouseLeave={e=>e.currentTarget.style.background=isToday?CONTENT_LIGHT:C.bg}>
                <div style={{fontWeight:"bold",fontSize:11,color:isToday?CONTENT_COLOR:C.muted,marginBottom:3}}>{dn}</div>
                {dayPosts.slice(0,3).map(p=>{
                  const pil=pillars.find(x=>x.id===(p.pillarId||p.pillar_id));
                  return(
                    <div key={p.id} onClick={e=>{e.stopPropagation();setEditPost({...p,scheduledDate:p.scheduledDate||p.scheduled_date,tags:(p.tags||[]).join(", ")});setShowForm(true);}}
                      style={{fontSize:9,background:pil?pil.color:CONTENT_COLOR,color:"#fff",borderRadius:3,padding:"1px 4px",
                        marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",cursor:"pointer"}}>
                      {(p.platform||"?")[0]} · {p.title}
                    </div>
                  );
                })}
                {dayPosts.length>3&&<div style={{fontSize:8,color:C.muted}}>+{dayPosts.length-3} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* This month's posts list */}
      <div style={{marginTop:14}}>
        <h4 style={{color:CONTENT_COLOR,margin:"0 0 10px"}}>Content this month ({posts.filter(p=>(p.scheduledDate||p.scheduled_date)?.startsWith(mk)).length} pieces)</h4>
        {posts.filter(p=>(p.scheduledDate||p.scheduled_date)?.startsWith(mk)).map(p=>({...p,scheduledDate:p.scheduledDate||p.scheduled_date})).sort((a,b)=>(a.scheduledDate||"").localeCompare(b.scheduledDate||"")).map(p=>(
          <ContentRow key={p.id} p={p} pillars={pillars} onEdit={()=>{setEditPost({...p,tags:(p.tags||[]).join(", "),scheduledDate:p.scheduledDate||p.scheduled_date});setShowForm(true);}} onDelete={()=>{ if(onDeletePost) onDeletePost(p.id); else setPosts(prev=>prev.filter(x=>x.id!==p.id)); }} onStatus={s=>{ if(onUpdatePost) onUpdatePost(p.id,{status:s}); else setPosts(prev=>prev.map(x=>x.id===p.id?{...x,status:s}:x)); }}/>
        ))}
      </div>
    </div>
  );
}

// ── Board Tab: Kanban-style by status ────────────────────────────────────────
function BoardTab({ posts, setPosts, pillars, onAddPost, onUpdatePost, onDeletePost }) {
  const [showForm, setShowForm] = useState(false);
  const [editPost, setEditPost] = useState(null);

  const savePost = (p) => {
    if (editPost && p.id) {
      if (onUpdatePost) onUpdatePost(p.id, p); else setPosts(prev=>prev.map(x=>x.id===p.id?p:x));
    } else {
      const {id,...rest} = p;
      if (onAddPost) onAddPost(rest); else setPosts(prev=>[...prev,rest]);
    }
    setShowForm(false); setEditPost(null);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{margin:0,color:CONTENT_COLOR}}>🗂 Content Board</h3>
        <button onClick={()=>{setEditPost(null);setShowForm(true);}} style={btn(CONTENT_COLOR)}>+ Add Content</button>
      </div>

      {(showForm||editPost) && (
        <PostForm pillars={pillars} initial={editPost?{...editPost,tags:(editPost.tags||[]).join(", ")}:null}
          onSave={savePost} onCancel={()=>{setShowForm(false);setEditPost(null);}}/>
      )}

      <div style={{display:"grid",gridTemplateColumns:`repeat(${POST_STATUSES.length},1fr)`,gap:8,overflowX:"auto"}}>
        {POST_STATUSES.map(st=>{
          const colPosts = posts
            .filter(p=>p.status===st.id)
            .sort((a,b)=>(a.position??999)-(b.position??999));

          const handleBoardDrop = (e, targetId) => {
            e.preventDefault(); e.stopPropagation();
            const pid = e.dataTransfer.getData("postId");
            const fromSt = e.dataTransfer.getData("postStatus");
            if (!pid) return;
            if (fromSt !== st.id) {
              if(onUpdatePost) onUpdatePost(pid,{status:st.id,position:colPosts.length});
              else setPosts(prev=>prev.map(x=>x.id===pid?{...x,status:st.id,position:colPosts.length}:x));
            } else if (targetId && targetId !== pid) {
              const dragIdx = colPosts.findIndex(x=>x.id===pid);
              const dropIdx = colPosts.findIndex(x=>x.id===targetId);
              if (dragIdx===-1||dropIdx===-1) return;
              const reordered = [...colPosts];
              const [moved] = reordered.splice(dragIdx,1);
              reordered.splice(dropIdx,0,moved);
              reordered.forEach((p,i)=>{
                if(onUpdatePost) onUpdatePost(p.id,{position:i});
                else setPosts(prev=>prev.map(x=>x.id===p.id?{...x,position:i}:x));
              });
            }
          };

          return(
            <div key={st.id} style={{minWidth:200}}>
              <div style={{background:st.color,color:"#fff",borderRadius:"8px 8px 0 0",padding:"8px 12px",fontSize:12,fontWeight:"bold",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{st.label}</span>
                <span style={{background:"rgba(255,255,255,.25)",borderRadius:10,padding:"0 6px",fontSize:10}}>{colPosts.length}</span>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:"0 0 8px 8px",padding:8,minHeight:200,display:"flex",flexDirection:"column",gap:6}}
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>handleBoardDrop(e,null)}>
                {colPosts.map((p,idx)=>{
                  const pil=pillars.find(x=>x.id===(p.pillarId||p.pillar_id));
                  return(
                    <div key={p.id}
                      draggable
                      onDragStart={e=>{e.dataTransfer.setData("postId",p.id);e.dataTransfer.setData("postStatus",st.id);e.currentTarget.style.opacity="0.5";}}
                      onDragEnd={e=>{e.currentTarget.style.opacity="1";}}
                      onDragOver={e=>{e.preventDefault();e.stopPropagation();e.currentTarget.style.borderTop=`2px solid ${st.color}`;}}
                      onDragLeave={e=>{e.currentTarget.style.borderTop="";}}
                      onDrop={e=>{e.currentTarget.style.borderTop="";handleBoardDrop(e,p.id);}}
                      style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`3px solid ${pil?pil.color:CONTENT_COLOR}`,borderRadius:6,padding:"8px 10px",cursor:"grab"}}
                      onClick={()=>{setEditPost({...p,tags:(p.tags||[]).join(", "),scheduledDate:p.scheduledDate||p.scheduled_date});setShowForm(true);}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                        <span style={{fontSize:9,fontWeight:800,color:st.color,background:st.color+"22",borderRadius:4,padding:"1px 5px",flexShrink:0}}>P{idx+1}</span>
                        <span style={{fontSize:12,fontWeight:"bold",color:C.text,lineHeight:1.3,flex:1}}>{p.title}</span>
                      </div>
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:4}}>
                        <span style={{fontSize:9,background:CONTENT_LIGHT,color:CONTENT_COLOR,borderRadius:8,padding:"1px 5px"}}>{p.platform}</span>
                        <span style={{fontSize:9,background:C.surfaceAlt,color:C.muted,borderRadius:8,padding:"1px 5px"}}>{p.type}</span>
                      </div>
                      {pil&&<span style={{fontSize:9,color:pil.color,fontWeight:"bold"}}>{pil.icon} {pil.name}</span>}
                      {(p.scheduledDate||p.scheduled_date)&&<div style={{fontSize:9,color:C.muted,marginTop:3}}>📅 {p.scheduledDate||p.scheduled_date}</div>}
                      <div style={{display:"flex",justifyContent:"flex-end",marginTop:4,gap:4}} onClick={e=>e.stopPropagation()}>
                        {POST_STATUSES.map(ns=>ns.id!==p.status&&(
                          <button key={ns.id} onClick={e=>{e.stopPropagation();if(onUpdatePost)onUpdatePost(p.id,{status:ns.id});else setPosts(prev=>prev.map(x=>x.id===p.id?{...x,status:ns.id}:x));}}
                            title={`Move to ${ns.label}`}
                            style={{background:ns.color,border:"none",color:"#fff",borderRadius:4,padding:"1px 5px",fontSize:8,cursor:"pointer"}}>→{ns.id[0].toUpperCase()}</button>
                        ))}
                        <button onClick={e=>{e.stopPropagation();if(onDeletePost)onDeletePost(p.id);else setPosts(prev=>prev.filter(x=>x.id!==p.id));}} style={{background:"none",border:"none",cursor:"pointer",color:C.light,fontSize:10}}>🗑</button>
                      </div>
                    </div>
                  );
                })}
                {colPosts.length===0&&<div style={{textAlign:"center",color:C.light,fontSize:11,padding:"20px 8px",fontStyle:"italic"}}>No content here</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Metrics Tab: performance tracking per post ───────────────────────────────
function MetricsTab({ posts, setPosts, pillars, onUpdatePost }) {
  const published = posts.filter(p=>p.status==="published");
  const [expandedId, setExpandedId] = useState(null);

  const updateMetrics = (id, key, val) => {
    const post = posts.find(p=>p.id===id); if(!post)return;
    const newMetrics = {...(post.metrics||{}),[key]:Number(val)||0};
    if (onUpdatePost) onUpdatePost(id, {metrics: newMetrics});
    else setPosts(prev=>prev.map(p=>p.id!==id?p:{...p,metrics:newMetrics}));
  };

  const totalReach = published.reduce((s,p)=>(p.metrics?.impressions||0)+s, 0);
  const totalEng   = published.reduce((s,p)=>(p.metrics?.likes||0)+(p.metrics?.comments||0)+(p.metrics?.shares||0)+s, 0);
  const avgEngRate = published.length>0 ? (totalEng/Math.max(1,totalReach)*100).toFixed(1) : "0";

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[
          {label:"Published",       val:published.length,  color:C.green},
          {label:"Total Reach",     val:totalReach.toLocaleString(), color:CONTENT_COLOR,raw:true},
          {label:"Total Engagement",val:totalEng.toLocaleString(),   color:C.blue,raw:true},
          {label:"Avg Eng. Rate",   val:`${avgEngRate}%`,  color:C.amber,raw:true},
        ].map(({label,val,color,raw})=>(
          <div key={label} style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${color}`,borderRadius:9,padding:14,textAlign:"center"}}>
            <div style={{fontSize:10,color:C.muted}}>{label}</div>
            <div style={{fontSize:raw?20:26,fontWeight:"bold",color}}>{val}</div>
          </div>
        ))}
      </div>

      {published.length===0 && <p style={{color:C.light,textAlign:"center",padding:30}}>No published content yet. Mark posts as published to track metrics.</p>}

      <div style={{display:"grid",gap:8}}>
        {published.sort((a,b)=>(b.scheduledDate||b.scheduled_date||"").localeCompare(a.scheduledDate||a.scheduled_date||"")).map(p=>{
          const pil=pillars.find(x=>x.id===(p.pillarId||p.pillar_id));
          const m=p.metrics||{};
          const eng=(m.likes||0)+(m.comments||0)+(m.shares||0);
          const rate=m.impressions>0?(eng/m.impressions*100).toFixed(1):"—";
          const isOpen=expandedId===p.id;
          return(
            <div key={p.id} style={{background:C.surface,border:`1.5px solid ${C.border}`,borderLeft:`4px solid ${pil?pil.color:CONTENT_COLOR}`,borderRadius:9,padding:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setExpandedId(isOpen?null:p.id)}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:"bold"}}>{p.title}</div>
                  <div style={{display:"flex",gap:8,fontSize:10,color:C.muted,marginTop:2}}>
                    <span>{p.platform}</span>
                    {(p.scheduledDate||p.scheduled_date)&&<span>📅 {p.scheduledDate||p.scheduled_date}</span>}
                    {pil&&<span style={{color:pil.color}}>{pil.icon} {pil.name}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  {[{label:"👀",key:"impressions"},{label:"❤️",key:"likes"},{label:"💬",key:"comments"},{label:"🔁",key:"shares"}].map(({label,key})=>(
                    <div key={key} style={{textAlign:"center"}}>
                      <div style={{fontSize:10,color:C.muted}}>{label}</div>
                      <div style={{fontSize:14,fontWeight:"bold",color:C.text}}>{m[key]||0}</div>
                    </div>
                  ))}
                  <div style={{textAlign:"center",minWidth:50}}>
                    <div style={{fontSize:10,color:C.muted}}>Eng%</div>
                    <div style={{fontSize:14,fontWeight:"bold",color:CONTENT_COLOR}}>{rate}</div>
                  </div>
                  <span style={{color:C.muted,fontSize:12}}>{isOpen?"▲":"▼"}</span>
                </div>
              </div>
              {isOpen&&(
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`,display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
                  {[{label:"Impressions / Reach",key:"impressions"},{label:"Likes / Reactions",key:"likes"},{label:"Comments",key:"comments"},{label:"Shares / Reposts",key:"shares"},{label:"Clicks",key:"clicks"},{label:"Saves / Bookmarks",key:"saves"},{label:"New Followers",key:"followers"},{label:"Link Clicks",key:"linkClicks"}].map(({label,key})=>(
                    <div key={key}>
                      <label style={lbl}>{label}</label>
                      <input type="number" min={0} value={m[key]||""} onChange={e=>updateMetrics(p.id,key,e.target.value)}
                        placeholder="0" style={{...inp(),width:"100%"}}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Shared: content row used in calendar ─────────────────────────────────────
function ContentRow({ p, pillars, onEdit, onDelete, onStatus }) {
  const st  = POST_STATUSES.find(s=>s.id===p.status)||POST_STATUSES[0];
  const pil = pillars.find(x=>x.id===(p.pillarId||p.pillar_id));
  return(
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:C.surface,border:`1px solid ${C.border}`,borderLeft:`4px solid ${pil?pil.color:CONTENT_COLOR}`,borderRadius:8,marginBottom:6}}>
      <div style={{flex:1}}>
        <div style={{fontWeight:"bold",fontSize:13}}>{p.title}</div>
        <div style={{display:"flex",gap:6,marginTop:3,flexWrap:"wrap"}}>
          <span style={{fontSize:10,color:C.muted}}>{p.platform}</span>
          <span style={{fontSize:10,color:C.muted}}>·</span>
          <span style={{fontSize:10,color:C.muted}}>{p.type}</span>
          {(p.scheduledDate||p.scheduled_date)&&<><span style={{fontSize:10,color:C.muted}}>·</span><span style={{fontSize:10,color:C.muted}}>📅 {p.scheduledDate||p.scheduled_date}</span></>}
          {pil&&<><span style={{fontSize:10,color:C.muted}}>·</span><span style={{fontSize:10,color:pil.color}}>{pil.icon} {pil.name}</span></>}
        </div>
      </div>
      <select value={p.status} onChange={e=>onStatus(e.target.value)}
        style={{...inp(),fontSize:11,padding:"3px 6px",background:st.color,color:"#fff",border:"none",borderRadius:6}}>
        {POST_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <button onClick={onEdit} style={{...btn(C.amber),fontSize:11,padding:"4px 8px"}}>✏️</button>
      <button onClick={onDelete} style={{...btn(C.red),fontSize:11,padding:"4px 8px"}}>🗑</button>
    </div>
  );
}




// ══════════════════════════════════════════════════════════════════════════════
// PROJECTS — Project management with own Kanban task board
// ══════════════════════════════════════════════════════════════════════════════
const PROJ_STATUSES = [
  {id:"todo",     label:"📋 To Do",     col:"#6B7280", bg:"#F3F4F6"},
  {id:"working",  label:"⚡ In Progress",col:"#C8860A", bg:"#FFF3CC"},
  {id:"assigned", label:"👤 Assigned",  col:"#6B3FA0", bg:"#F0E8FF"},
  {id:"review",   label:"👀 Review",    col:"#2563EB", bg:"#DBEAFE"},
  {id:"done",     label:"✅ Done",      col:"#16A34A", bg:"#DCFCE7"},
];
const PROJ_COLORS = ["#2D5A3D","#1D5F8A","#B94040","#6B3FA0","#C8860A","#0E7490","#C8375A","#1A7A6A"];
function ProjectsSection({ ctx }) {
  const C = useC();
  const { projects=[], addProject, updateProject, deleteProject,
          projectTasks=[], addProjectTask, updateProjectTask, deleteProjectTask,
          projectMembers=[], inviteProjectMember, removeProjectMember,
          tasks, addTask, uid, profile } = ctx;
  const [selProj, setSelProj]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showTimeLogs, setShowTimeLogs] = useState(false);
  const [localTimeLogs, setLocalTimeLogs] = useState([]);

  // Load time logs for current project from DB when modal opens
  const loadTimeLogs = React.useCallback(async (projectId) => {
    if (!projectId) {
      // Load all user's time logs
      const { data } = await supabase.from('project_time_logs').select('*')
        .eq('user_id', ctx.uid).order('created_at',{ascending:false});
      setLocalTimeLogs(data||[]);
    } else {
      const { data } = await supabase.from('project_time_logs').select('*')
        .eq('project_id', projectId).order('created_at',{ascending:false});
      setLocalTimeLogs(data||[]);
    }
  }, [ctx.uid]);

  // Listen for new time logs added by ProjectKanban
  React.useEffect(() => {
    const handler = () => loadTimeLogs(selProj);
    window.addEventListener('aspreo_timelog_added', handler);
    return () => window.removeEventListener('aspreo_timelog_added', handler);
  }, [selProj, loadTimeLogs]);
  const [taskModal, setTaskModal] = useState(null);
  // logModal removed — history is now a dedicated column in ProjectKanban
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [ownerProfiles, setOwnerProfiles] = useState({}); // projectId → {id,full_name,avatar_url}
  const [form, setForm] = useState({name:"",description:"",color:PROJ_COLORS[0]});
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const activeProj = projects.find(p=>p.id===selProj);
  const projTasks  = projectTasks.filter(t=>t.project_id===selProj);
  const projMembers = projectMembers.filter(m=>m.project_id===selProj);
  const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);

  // Fetch owner profiles for projects where current user is a member (not owner)
  useEffect(() => {
    const memberProjects = projects.filter(p => p.user_id !== uid);
    if (!memberProjects.length) return;
    const missing = memberProjects.filter(p => !ownerProfiles[p.id]);
    if (!missing.length) return;
    const ownerIds = [...new Set(missing.map(p => p.user_id).filter(Boolean))];
    Promise.resolve().then(() => {
      supabase.from('profiles').select('id,full_name,avatar_url').in('id', ownerIds).then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(pr => { map[pr.id] = pr; });
        setOwnerProfiles(prev => {
          const next = { ...prev };
          missing.forEach(p => { if (map[p.user_id]) next[p.id] = map[p.user_id]; });
          return next;
        });
      });
    }).catch(() => {});
  }, [projects.length, uid]); // eslint-disable-line

  // Build full assignable people list for current project:
  // owner (could be self or another user) + all accepted members
  const buildAssigneeList = (proj) => {
    if (!proj) return [];
    const list = [];
    const isMine = proj.user_id === uid;
    // Owner
    const ownerProfile = isMine ? (profile || { id: uid, full_name: 'Me (Owner)' }) : ownerProfiles[proj.id];
    if (ownerProfile) list.push({ id: proj.user_id, name: ownerProfile.full_name || 'Project Owner', badge: '👑' });
    // Accepted members
    const members = projectMembers.filter(m => m.project_id === proj.id && m.status === 'accepted');
    members.forEach(m => {
      const name = m.profiles?.full_name || (m.invited_email||'').split('@')[0];
      if (m.user_id && m.user_id !== proj.user_id) list.push({ id: m.user_id, name, badge: '👤' });
    });
    return list;
  };

  // Logs every task change (status/assignee) to project_task_logs and drives
  // per-user time tracking. Lives here (not inside ProjectKanban) so both the
  // Kanban board (drag/drop, inline dropdowns) AND the task edit modal funnel
  // through the exact same logic — the edit modal used to call updateProjectTask
  // directly, which silently skipped both history logging and time tracking.
  //
  // Time-tracking rule: entering "working" starts a timer under whoever the
  // task is ALREADY assigned to (assignment itself only ever changes via an
  // explicit reassignment — the dropdown/edit-modal — never automatically);
  // leaving "working" to any other status closes that timer and logs the
  // elapsed time against whoever was assigned during that stint. So a task
  // handed back and forth (assigned to B → working by B → review, reassigned
  // to A → working by A → done) tracks each person's own time separately,
  // driven entirely by who it's assigned to at the time, not who clicked.
  const logAndUpdate = (taskId, updates, oldTask) => {
    const myName = profile?.full_name || (profile?.email ? profile.email.split('@')[0] : '') || 'Someone';
    const taskTitle = oldTask?.title || 'Task';
    const projId = oldTask?.project_id || selProj;

    // ── Time tracking ────────────────────────────────────────────────────────
    let timeUpdates = {};
    const enteringWorking = updates.status === 'working' && oldTask?.status !== 'working';
    const leavingWorking  = 'status' in updates && updates.status !== 'working' && oldTask?.status === 'working';

    if (enteringWorking) {
      timeUpdates = { started_at: new Date().toISOString() };
    }
    if (leavingWorking && oldTask?.started_at) {
      const now = new Date().toISOString();
      const startMs = new Date(oldTask.started_at).getTime();
      const durMins = Math.max(1, Math.round((Date.now() - startMs) / 60000));
      timeUpdates = { ...timeUpdates, started_at: null, time_spent_mins: (oldTask.time_spent_mins||0) + durMins };
      try {
        supabase.from('project_time_logs').insert({
          user_id: oldTask.assignee_id || uid,
          task_id: taskId, project_id: oldTask.project_id || selProj,
          task_title: oldTask.title, username: oldTask.assignee_name || myName,
          started_at: oldTask.started_at, ended_at: now, duration_mins: durMins,
        }).then(()=>{ window.dispatchEvent(new CustomEvent('aspreo_timelog_added')); })
          .catch(e=>console.warn('[timelog insert]',e));
      } catch(e) { console.warn('[timelog]', e); }
    }
    const finalUpdates = { ...updates, ...timeUpdates };
    updateProjectTask(taskId, finalUpdates);

    // Build log entries for each change — checked against finalUpdates (not the
    // caller's raw updates) so auto-claim-on-entering-working also shows up as
    // a real "assigned" history entry, not just a silent status change.
    const logs = [];
    if ('status' in finalUpdates && finalUpdates.status !== oldTask?.status) {
      const fromLabel = PROJ_STATUSES.find(s => s.id === (oldTask?.status || 'todo'))?.label || oldTask?.status;
      const toLabel   = PROJ_STATUSES.find(s => s.id === finalUpdates.status)?.label || finalUpdates.status;
      logs.push({ action: 'status_changed', from_val: fromLabel, to_val: toLabel, note: null });
    }
    if ('assignee_id' in finalUpdates && finalUpdates.assignee_id !== oldTask?.assignee_id) {
      const toName = finalUpdates.assignee_name || finalUpdates.assignee_id || 'Unassigned';
      logs.push({ action: 'assigned', from_val: oldTask?.assignee_name || null, to_val: toName, note: null });
    }

    logs.forEach(logEntry => {
      const entry = {
        id: `tmp-${Date.now()}-${Math.random()}`,
        task_id: taskId,
        project_id: projId,
        user_id: uid,
        user_name: myName,
        task_title: taskTitle,
        ...logEntry,
        created_at: new Date().toISOString(),
      };
      // Optimistic UI — dispatch immediately
      window.dispatchEvent(new CustomEvent('proj_log', { detail: entry }));
      // Persist to DB — include task_title so history shows after refresh
      supabase.from('project_task_logs').insert({
        task_id: taskId,
        project_id: projId,
        user_id: uid,
        user_name: myName,
        task_title: taskTitle,
        action: logEntry.action,
        from_val: logEntry.from_val,
        to_val: logEntry.to_val,
        note: logEntry.note,
      }).then(({ error }) => {
        if (error) console.warn('[proj_log] insert failed:', error.message, error.code);
      });
    });
  };

  const saveProject = () => {
    if (!form.name.trim()) return;
    addProject({name:form.name,description:form.description,color:form.color,status:"active"});
    setForm({name:"",description:"",color:PROJ_COLORS[0]}); setShowForm(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true); setInviteMsg('');
    try {
      await inviteProjectMember(selProj, inviteEmail);
      setInviteMsg('✅ Invite sent to ' + inviteEmail);
      setInviteEmail('');
    } catch(e) { setInviteMsg('❌ ' + (e.message||'Error')); }
    setInviting(false);
  };

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      <SectionMasthead
        eyebrow={`Execution · ${projects.length} project${projects.length!==1?"s":""} · Collaborative task boards`}
        title={<>The work, in <em>motion</em>.</>}
        actions={<>
          <button onClick={()=>{setShowTimeLogs(true);loadTimeLogs(selProj);}} className="ta-btn ta-btn-ghost">Time logs</button>
          <button onClick={()=>setShowForm(v=>!v)} className="ta-btn ta-btn-primary">+ New project</button>
        </>}
      />
      {showTimeLogs && (
        <div style={{position:'fixed',inset:0,zIndex:3000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setShowTimeLogs(false)}>
          <div style={{background:C.surface,borderRadius:16,padding:24,width:'100%',maxWidth:720,maxHeight:'80vh',overflowY:'auto',boxShadow:'0 24px 80px rgba(0,0,0,.3)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontWeight:800,fontSize:17,color:'#1D5F8A'}}>⏱ Project Time Logs {activeProj?`— ${activeProj.name}`:''}</div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>{
                  const logs=localTimeLogs;
                  if(!logs.length){alert('No time logs yet.');return;}
                  const rows=[['Task','User','Started','Ended','Duration (mins)'],...logs.map(l=>[l.task_title||'',l.username||'',l.started_at||'',l.ended_at||'',l.duration_mins||0])];
                  const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
                  const a=document.createElement('a');a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);a.download=`timelogs-${activeProj?.name||'all'}-${new Date().toISOString().slice(0,10)}.csv`;a.click();
                }} style={{fontSize:12,padding:'6px 14px',borderRadius:8,border:'none',background:'#16A34A',color:'#fff',cursor:'pointer',fontWeight:700}}>📥 Export CSV</button>
                <button onClick={()=>setShowTimeLogs(false)} style={{fontSize:12,padding:'6px 12px',borderRadius:8,border:'none',background:C.border,cursor:'pointer',color:C.text}}>✕</button>
              </div>
            </div>
            {localTimeLogs.length===0
              ?<div style={{textAlign:'center',color:C.muted,padding:40,fontSize:14}}>No time logs yet.<br/><span style={{fontSize:12,color:C.light}}>Time is tracked automatically: a timer starts when a task is moved to In Progress (for whoever moved it there) and stops — logged below — the moment it leaves In Progress, however many times it gets handed back and forth.</span></div>
              :<>
                <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
                  {Object.values(localTimeLogs.reduce((acc,l)=>{
                    const key=l.user_id||l.username||'—';
                    if(!acc[key])acc[key]={name:l.username||'—',mins:0};
                    acc[key].mins+=l.duration_mins||0;
                    return acc;
                  },{})).sort((a,b)=>b.mins-a.mins).map((u,i)=>(
                    <div key={i} style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',fontSize:12}}>
                      <span style={{color:C.muted}}>{u.name}</span>{' '}
                      <strong style={{color:'#1D5F8A'}}>{Math.floor(u.mins/60)}h {Math.round(u.mins%60)}m</strong>
                    </div>
                  ))}
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr>{['Task','User','Date','Duration'].map(h=><th key={h} style={{textAlign:'left',padding:'8px 12px',borderBottom:`2px solid ${C.border}`,fontWeight:700,color:C.muted,fontSize:11,textTransform:'uppercase',letterSpacing:'.05em'}}>{h}</th>)}</tr></thead>
                  <tbody>{localTimeLogs.map((l,i)=>(
                    <tr key={l.id||i} style={{background:i%2===0?C.surfaceAlt:C.surface}}>
                      <td style={{padding:'9px 12px',borderBottom:`1px solid ${C.border}`}}>{l.task_title||'—'}</td>
                      <td style={{padding:'9px 12px',borderBottom:`1px solid ${C.border}`,color:C.muted}}>{l.username||'—'}</td>
                      <td style={{padding:'9px 12px',borderBottom:`1px solid ${C.border}`,color:C.muted}}>{l.started_at?new Date(l.started_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})+' at '+new Date(l.started_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
                      <td style={{padding:'9px 12px',borderBottom:`1px solid ${C.border}`,fontWeight:700,color:'#1D5F8A'}}>{l.duration_mins!=null?`${Math.floor(l.duration_mins/60)}h ${Math.round(l.duration_mins%60)}m`:'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </>
            }
          </div>
        </div>
      )}

      {showForm && (
        <div style={{background:C.surface,border:"1.5px solid #1D5F8A",borderRadius:12,padding:18,marginBottom:16}}>
          <h4 style={{margin:"0 0 12px",color:"#1D5F8A"}}>New Project</h4>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Name *</label><input value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. Aspreo Innovation" style={{...inp(),width:"100%"}} autoFocus/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>Description</label><textarea value={form.description} onChange={e=>upd("description",e.target.value)} placeholder="What is this project about?" style={ta(60)}/></div>
            <div>
              <label style={lbl}>Color</label>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {PROJ_COLORS.map(col=><button key={col} onClick={()=>upd("color",col)} style={{width:28,height:28,borderRadius:"50%",background:col,border:`3px solid ${form.color===col?"#fff":"transparent"}`,outline:`2px solid ${form.color===col?col:"transparent"}`,cursor:"pointer"}}/>)}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveProject} style={btn("#1D5F8A")}>💾 Create Project</button>
            <button onClick={()=>setShowForm(false)} style={btn("#999")}>Cancel</button>
          </div>
        </div>
      )}

      {projects.length===0 && !showForm && (
        <div style={{textAlign:"center",padding:"60px 20px",color:C.muted}}>
          <div style={{fontSize:52,marginBottom:12}}>🗂</div>
          <h3 style={{color:C.text,margin:"0 0 8px"}}>No Projects Yet</h3>
          <p style={{fontSize:13,marginBottom:20}}>Create a project and invite team members to collaborate.</p>
          <button onClick={()=>setShowForm(true)} style={{...btn("#1D5F8A"),fontSize:14,padding:"10px 24px"}}>Create First Project</button>
        </div>
      )}

      {/* Project list */}
      {!selProj && projects.length>0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
          {projects.map(p=>{
            const ptasks = projectTasks.filter(t=>t.project_id===p.id);
            const done = ptasks.filter(t=>t.status==="done").length;
            const pct = ptasks.length>0 ? Math.round(done/ptasks.length*100) : 0;
            const members = projectMembers.filter(m=>m.project_id===p.id && m.status==="accepted");
            return (
              <div key={p.id} onClick={()=>setSelProj(p.id)}
                style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`4px solid ${p.color||"#1D5F8A"}`,borderRadius:12,padding:16,cursor:"pointer",transition:"all .15s"}}
                onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=C.shadowMd;}}
                onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{fontWeight:700,fontSize:15,color:C.text}}>{p.name}</div>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:p.status==="active"?C.greenM:C.surfaceAlt,color:p.status==="active"?C.green:C.muted}}>{p.status==="active"?"Active":"Archived"}</span>
                </div>
                {p.description&&<div style={{fontSize:12,color:C.muted,marginBottom:8,lineHeight:1.4}}>{p.description}</div>}
                {members.length>0 && (
                  <div style={{display:"flex",marginBottom:8}}>
                    {members.slice(0,5).map((m,i)=>(
                      <div key={m.id} title={m.profiles?.full_name||m.invited_email||''} style={{width:22,height:22,borderRadius:"50%",background:"#1D5F8A",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,border:`2px solid ${C.surface}`,marginLeft:i>0?-6:0}}>
                        {initials(m.profiles?.full_name||m.invited_email||'?')}
                      </div>
                    ))}
                    {members.length>5&&<div style={{width:22,height:22,borderRadius:"50%",background:C.surfaceAlt,color:C.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,border:`2px solid ${C.surface}`,marginLeft:-6}}>+{members.length-5}</div>}
                  </div>
                )}
                <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{ptasks.length} tasks · {done} done · {members.length} member{members.length!==1?"s":""}</div>
                {ptasks.length>0&&<div style={{background:C.border,borderRadius:4,height:5,overflow:"hidden",marginBottom:10}}><div style={{width:`${pct}%`,background:p.color||"#1D5F8A",height:"100%",borderRadius:4,transition:"width .4s"}}/></div>}
                <div style={{display:"flex",gap:6,marginTop:4}}>
                  <button onClick={e=>{e.stopPropagation();setSelProj(p.id);}} style={{...btn(p.color||"#1D5F8A"),flex:1,fontSize:12,padding:"5px"}}>Open →</button>
                  <button onClick={e=>{e.stopPropagation();if(window.confirm(`Delete project "${p.name}" and all tasks?`))deleteProject(p.id);}} style={{...btn(C.red),fontSize:12,padding:"5px 8px"}}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Project detail */}
      {selProj && activeProj && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
            <button onClick={()=>{setSelProj(null);setShowInvite(false);}} style={{...btn("#888"),fontSize:12,padding:"5px 10px"}}>← Back</button>
            <div style={{width:14,height:14,borderRadius:"50%",background:activeProj.color||"#1D5F8A",flexShrink:0}}/>
            <h3 style={{margin:0,color:activeProj.color||"#1D5F8A",fontSize:17,fontWeight:800,flex:1}}>{activeProj.name}</h3>
            {projMembers.filter(m=>m.status==="accepted").map(m=>(
              <div key={m.id} title={`${m.profiles?.full_name||m.invited_email} — click to remove`}
                onClick={()=>{if(window.confirm(`Remove ${m.profiles?.full_name||m.invited_email}?`))removeProjectMember(m.id);}}
                style={{display:"flex",alignItems:"center",gap:4,background:C.surfaceAlt,borderRadius:20,padding:"3px 8px",fontSize:10,color:C.muted,cursor:"pointer",border:`1px solid ${C.border}`}}>
                <div style={{width:16,height:16,borderRadius:"50%",background:activeProj.color||"#1D5F8A",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:700}}>
                  {initials(m.profiles?.full_name||m.invited_email||'?')}
                </div>
                {m.profiles?.username?`@${m.profiles.username}`:(m.invited_email||'').split('@')[0]}
                <span style={{color:C.green,fontSize:9}}>●</span>
              </div>
            ))}
            {projMembers.filter(m=>m.status==="pending").map(m=>(
              <div key={m.id} style={{background:C.amberL,borderRadius:20,padding:"3px 8px",fontSize:10,color:C.amber}}>⏳ {(m.invited_email||'').split('@')[0]}</div>
            ))}
            <button onClick={()=>setShowInvite(v=>!v)} style={{...btn(activeProj.color||"#1D5F8A"),fontSize:11,padding:"5px 10px"}}>👥 Invite</button>
            <button onClick={()=>setTaskModal({mode:"new"})} style={{...btn(activeProj.color||"#1D5F8A"),fontSize:12}}>+ Task</button>
          </div>

          {showInvite && (
            <div style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:8}}>👥 Invite Member by Email</div>
              <div style={{display:"flex",gap:8}}>
                <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleInvite()} placeholder="teammate@example.com" style={{...inp(),flex:1}}/>
                <button onClick={handleInvite} disabled={inviting||!inviteEmail.trim()} style={{...btn(activeProj.color||"#1D5F8A"),opacity:inviting||!inviteEmail.trim()?0.5:1}}>{inviting?"Sending...":"Send Invite"}</button>
              </div>
              {inviteMsg&&<div style={{fontSize:12,marginTop:6,color:inviteMsg.startsWith("✅")?C.green:C.red}}>{inviteMsg}</div>}
              <div style={{fontSize:11,color:C.muted,marginTop:6}}>Members must have an Aspreo Life account with the same email.</div>
            </div>
          )}

          {activeProj.description&&<p style={{fontSize:13,color:C.muted,margin:"0 0 14px"}}>{activeProj.description}</p>}

          <ProjectKanban
            projTasks={projTasks}
            projMembers={projMembers}
            activeProj={activeProj}
            uid={uid}
            profile={profile}
            updateProjectTask={updateProjectTask}
            deleteProjectTask={deleteProjectTask}
            setTaskModal={setTaskModal}
            buildAssigneeList={buildAssigneeList}
            selProj={selProj}
            logAndUpdate={logAndUpdate}
          />
        </div>
      )}

      {taskModal&&(
        <ProjectTaskModal
          task={taskModal.task}
          projectId={selProj}
          projectColor={activeProj?.color||"#1D5F8A"}
          projectMembers={projMembers}
          allAssignees={buildAssigneeList(activeProj)}
          tasks={tasks}
          onSave={d=>{
            if(taskModal.mode==="edit")logAndUpdate(taskModal.task.id,d,taskModal.task);
            else addProjectTask({...d,project_id:selProj});
            if(d._addToDaily&&d.due_date)addTask(d.due_date,`[${activeProj?.name||"Project"}] ${d.title}`,d.due_date&&d.due_time?`${d.due_date}T${d.due_time}`:undefined,{status:"new",priority:d.priority||"medium"});
            setTaskModal(null);
          }}
          onClose={()=>setTaskModal(null)}
        />
      )}

    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// PROJECT KANBAN — 5 status columns + History column
// History column shows a live feed of all task activity in this project.
// All members see the same board — status and assignee are real-time.
// ══════════════════════════════════════════════════════════════════════════════
function ProjectKanban({ projTasks, projMembers, activeProj, uid, profile, updateProjectTask, deleteProjectTask, setTaskModal, buildAssigneeList, selProj, logAndUpdate }) {
  const C = useC();
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Load ALL project history on mount and when project changes
  useEffect(() => {
    if (!selProj) return;
    setHistLoading(true);
    setHistory([]); // clear on project switch
    supabase.from('project_task_logs')
      .select('*')
      .eq('project_id', selProj)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.warn('[history]', error.message);
        setHistory(data || []);
        setHistLoading(false);
      });
  }, [selProj]);

  // Listen for new logs added in same session — deduplicate by id
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.project_id === selProj) {
        setHistory(prev => {
          // Avoid duplicates — optimistic entry has tmp- prefix, DB entry has real UUID
          // Replace tmp entry if a real one comes in for same action+task+user within 5s
          const entry = e.detail;
          const exists = prev.some(p =>
            p.id === entry.id ||
            (p.task_id === entry.task_id && p.action === entry.action &&
             p.user_id === entry.user_id &&
             Math.abs(new Date(p.created_at) - new Date(entry.created_at)) < 5000)
          );
          if (exists) return prev;
          return [entry, ...prev];
        });
      }
    };
    window.addEventListener('proj_log', handler);
    return () => window.removeEventListener('proj_log', handler);
  }, [selProj]);

  // logAndUpdate now lives in the parent ProjectsSection (shared with the task
  // edit modal) and is passed down as a prop — see its definition there for why.

  // Resolve a log's user_id to a live display name (current member list) so
  // history entries stay correct even if the name stored at write-time was
  // wrong or missing — falls back to whatever was stored, then "Someone".
  const nameById = React.useMemo(() => {
    const map = {};
    buildAssigneeList(activeProj).forEach(p => { map[p.id] = p.name; });
    return map;
  }, [activeProj, projMembers]); // eslint-disable-line

  const historyLabel = (log) => {
    const who = nameById[log.user_id] || log.user_name || 'Someone';
    const task = log.task_title ? `"${log.task_title}"` : 'a task';
    switch (log.action) {
      case 'created':        return `${who} created ${task}`;
      case 'status_changed': return `${who} moved ${task} → ${log.to_val}`;
      case 'assigned':       return log.to_val && log.to_val !== 'Unassigned'
                               ? `${who} assigned ${task} to ${log.to_val}`
                               : `${who} unassigned ${task}`;
      case 'updated':        return `${who} updated ${task}`;
      default:               return `${who} ${log.action} ${task}`;
    }
  };

  // Actual date+time, not "1h ago" — the relative version drifts stale the
  // moment you stop looking at the screen and doesn't say which day it was.
  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
  };

  const actionColor = (action) => {
    switch(action) {
      case 'created':        return '#6366f1';
      case 'status_changed': return '#d97706';
      case 'assigned':       return '#6b3fa0';
      case 'updated':        return '#0d9488';
      default:               return '#888';
    }
  };

  return (
    <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr) 220px', gap:8, overflowX:'auto', minWidth:700}}>
      {/* ── 5 status columns ── */}
      {PROJ_STATUSES.map(st => {
        const cols = projTasks.filter(t => (t.status||'todo') === st.id).sort((a,b) => (a.position??999)-(b.position??999));
        const handleColDrop = (e, targetId) => {
          e.preventDefault(); e.currentTarget.style.outline='none';
          const tid = e.dataTransfer.getData('ptaskId');
          const fromStatus = e.dataTransfer.getData('ptaskStatus');
          if (!tid) return;
          const draggedTask = projTasks.find(t => t.id === tid);
          if (fromStatus !== st.id) {
            logAndUpdate(tid, {status: st.id, position: cols.length}, draggedTask);
          } else if (targetId && targetId !== tid) {
            const di = cols.findIndex(t => t.id === tid), ti = cols.findIndex(t => t.id === targetId);
            if (di === -1 || ti === -1) return;
            const r = [...cols]; const [m] = r.splice(di, 1); r.splice(ti, 0, m);
            r.forEach((t, i) => updateProjectTask(t.id, {position: i}));
          }
        };
        return (
          <div key={st.id}
            style={{background: 'var(--ta-surface-low)', borderRadius:'var(--r-lg)', padding:10, minHeight:160}}
            onDragOver={e=>{e.preventDefault(); e.currentTarget.style.outline=`2px dashed ${st.col}`;}}
            onDragLeave={e=>{e.currentTarget.style.outline='none';}}
            onDrop={e=>handleColDrop(e, null)}>
            <div className="ta-mono" style={{fontSize:10, fontWeight:600, color:'var(--ta-on-surface-muted)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8, display:'flex', alignItems:'center', gap:6}}>
              {st.label}
              <span style={{background:'var(--ta-surface-high)', color:st.col, borderRadius:'var(--r-sm)', padding:'1px 7px', fontSize:10}}>{cols.length}</span>
            </div>
            {cols.map((t, idx) => {
              const assigneeName = t.assignee_name || (projMembers.find(m=>m.user_id===t.assignee_id)?.profiles?.full_name||'');
              return (
                <div key={t.id}
                  draggable
                  onDragStart={e=>{e.dataTransfer.setData('ptaskId', t.id); e.dataTransfer.setData('ptaskStatus', st.id); e.currentTarget.style.opacity='0.5';}}
                  onDragEnd={e=>{e.currentTarget.style.opacity='1';}}
                  onDragOver={e=>{e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderTop=`2px solid ${st.col}`;}}
                  onDragLeave={e=>{e.currentTarget.style.borderTop='';}}
                  onDrop={e=>{e.currentTarget.style.borderTop=''; handleColDrop(e, t.id);}}
                  style={{background:'var(--ta-surface-lowest)', borderLeft:`3px solid ${t.priority==='high'?'var(--ta-negative)':t.priority==='critical'?'var(--ta-negative)':st.col}`, borderRadius:'var(--r-md)', padding:'8px 10px', marginBottom:7, fontSize:12, cursor:'grab', boxShadow:'0 1px 2px rgba(26,28,28,.05)'}}>
                  <div style={{display:'flex', alignItems:'center', gap:4, marginBottom:3}}>
                    <span className="ta-mono" style={{fontSize:9, fontWeight:700, color:st.col, background:'var(--ta-surface-high)', borderRadius:'var(--r-sm)', padding:'1px 5px', flexShrink:0}}>P{idx+1}</span>
                    <span style={{fontWeight:600, color:'var(--ta-on-surface)', lineHeight:1.3, flex:1, fontSize:11}}>{t.title}</span>
                  </div>
                  {assigneeName && (
                    <div style={{display:'flex', alignItems:'center', gap:4, marginTop:2}}>
                      <div style={{width:14, height:14, borderRadius:'50%', background:activeProj?.color||'#1D5F8A', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:6, fontWeight:700}}>
                        {assigneeName.slice(0,2).toUpperCase()}
                      </div>
                      <span style={{fontSize:9, color:C.muted}}>{assigneeName}</span>
                    </div>
                  )}
                  {t.due_date && <div style={{fontSize:10, color:C.muted, marginTop:2}}>📅 {t.due_date}</div>}
                  {t.notes && <div style={{fontSize:10, color:C.muted, marginTop:3, lineHeight:1.4, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>{t.notes}</div>}
                  <div style={{display:'flex', gap:3, marginTop:5, alignItems:'center'}}>
                    <select value={t.priority||'medium'} onChange={e=>updateProjectTask(t.id,{priority:e.target.value})}
                      style={{fontSize:9, padding:'1px 3px', border:`1px solid ${C.border}`, borderRadius:4, background:C.surface, color:C.text, cursor:'pointer'}}>
                      <option value="critical">🔴</option><option value="high">🟠</option><option value="medium">🟡</option><option value="low">🟢</option>
                    </select>
                    <select value={t.assignee_id||''} onChange={e=>{
                        const person = buildAssigneeList(activeProj).find(x=>x.id===e.target.value);
                        const newStatus = e.target.value ? 'assigned' : (t.status==='assigned'?'todo':t.status);
                        logAndUpdate(t.id, {
                          assignee_id: e.target.value||null,
                          assignee_name: person?.name||'',
                          status: newStatus,
                        }, t);
                      }}
                      style={{fontSize:9, padding:'1px 3px', border:`1px solid ${t.assignee_id?'#6B3FA0':C.border}`, borderRadius:4, background:t.assignee_id?'#F0E8FF':C.surface, color:t.assignee_id?'#6B3FA0':C.text, cursor:'pointer', flex:1, maxWidth:100}}>
                      <option value="">👤 Assign</option>
                      {buildAssigneeList(activeProj).map(p=>(
                        <option key={p.id} value={p.id}>{p.badge} {p.name.slice(0,16)}</option>
                      ))}
                    </select>
                    <button onClick={()=>setTaskModal({mode:'edit', task:t})} style={{background:'none', border:'none', cursor:'pointer', color:C.amber, fontSize:11, padding:0}}>✏️</button>
                    <button onClick={()=>{if(window.confirm('Delete this task?')) deleteProjectTask(t.id);}} style={{background:'none', border:'none', cursor:'pointer', color:C.light, fontSize:11, padding:0, marginLeft:'auto'}}>✕</button>
                  </div>
                </div>
              );
            })}
            {cols.length===0 && <p style={{fontSize:10, color:C.light, textAlign:'center', margin:'10px 0', fontStyle:'italic'}}>Drop here</p>}
          </div>
        );
      })}

      {/* ── History column ── */}
      <div style={{background: 'var(--ta-surface-low)', borderRadius:'var(--r-lg)', padding:10, minHeight:160, display:'flex', flexDirection:'column'}}>
        <div className="ta-mono" style={{fontSize:10, fontWeight:600, color:'var(--ta-on-surface-muted)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8, display:'flex', alignItems:'center', gap:6, flexShrink:0}}>
          📜 History
        </div>
        <div style={{flex:1, overflowY:'auto', maxHeight:500}}>
          {histLoading && <div style={{fontSize:10, color:'var(--ta-on-surface-muted)', textAlign:'center', padding:12}}>Loading...</div>}
          {!histLoading && history.length===0 && (
            <div style={{fontSize:10, color:'var(--ta-on-surface-muted)', textAlign:'center', padding:16, lineHeight:1.6}}>
              No activity yet.<br/>Changes will appear here.
            </div>
          )}
          {!histLoading && history.map((log, i) => (
            <div key={log.id||i} style={{marginBottom:8, paddingBottom:8, borderBottom:'1px solid var(--ta-outline-variant)', position:'relative', paddingLeft:10, borderLeft:`2px solid ${actionColor(log.action)}`}}>
              <div style={{fontSize:10, color:'var(--ta-on-surface)', lineHeight:1.5, fontWeight:500}}>{historyLabel(log)}</div>
              <div className="ta-mono" style={{fontSize:9, color:'var(--ta-on-surface-muted)', marginTop:2}}>{fmtTime(log.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── TaskLogModal: shows activity history for a project task ──────────────────
function TaskLogModal({ taskId, taskTitle, onClose }) {
  const C = useC();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId) return;
    supabase.from('project_task_logs')
      .select('*').eq('task_id', taskId)
      .order('created_at', { ascending: false }).limit(50)
      .then(({ data }) => { setLogs(data||[]); setLoading(false); });
  }, [taskId]);

  // Format: "User A changed status of 'Task Name' to Working"
  const actionLabel = (log) => {
    const who  = log.user_name || 'Someone';
    const task = `"${taskTitle}"`;
    switch(log.action) {
      case 'created':
        return `${who} created task ${task}`;
      case 'status_changed':
        return `${who} changed status of ${task} from ${log.from_val||'?'} → ${log.to_val||'?'}`;
      case 'assigned':
        return log.to_val
          ? `${who} assigned ${task} to ${log.to_val}`
          : `${who} unassigned ${task}`;
      case 'updated':
        return `${who} updated ${task}${log.note ? ` (${log.note})` : ''}`;
      default:
        return `${who} ${log.action} ${task}`;
    }
  };

  const actionIcon = (action) => {
    switch(action) {
      case 'created':        return { icon:'✨', color:'#6366f1' };
      case 'status_changed': return { icon:'🔄', color:'#d97706' };
      case 'assigned':       return { icon:'👤', color:'#6b3fa0' };
      case 'updated':        return { icon:'✏️', color:'#0d9488' };
      default:               return { icon:'📌', color:'#888' };
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day:'numeric', month:'short' }) +
      ' at ' + d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:4000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={onClose}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:24,width:'100%',maxWidth:500,maxHeight:'75vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 80px rgba(0,0,0,.35)'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexShrink:0}}>
          <div>
            <h3 style={{margin:'0 0 4px',color:C.text,fontSize:15,fontWeight:700}}>📋 Activity Log</h3>
            <div style={{fontSize:12,color:C.indigo,fontWeight:600}}>{taskTitle}</div>
          </div>
          <button onClick={onClose} style={{...btn('#999'),padding:'4px 10px',fontSize:13}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {loading && (
            <div style={{textAlign:'center',padding:32,color:C.muted,fontSize:13}}>Loading activity...</div>
          )}
          {!loading && logs.length===0 && (
            <div style={{textAlign:'center',padding:32,color:C.muted,fontSize:13}}>
              <div style={{fontSize:28,marginBottom:8}}>📭</div>
              No activity recorded yet.<br/>
              <span style={{fontSize:11,opacity:.7}}>Changes to status, assignments, and title will appear here.</span>
            </div>
          )}
          {!loading && logs.map((log, i) => {
            const {icon, color} = actionIcon(log.action);
            return (
              <div key={log.id} style={{display:'flex',gap:12,marginBottom:16,position:'relative'}}>
                {/* Timeline line */}
                {i < logs.length-1 && <div style={{position:'absolute',left:15,top:30,bottom:-16,width:2,background:C.border}}/>}
                {/* Icon */}
                <div style={{width:30,height:30,borderRadius:'50%',background:color+'22',border:`2px solid ${color}40`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,flexShrink:0,zIndex:1}}>
                  {icon}
                </div>
                {/* Content */}
                <div style={{flex:1,paddingTop:3}}>
                  <div style={{fontSize:12,color:C.text,lineHeight:1.6}}>{actionLabel(log)}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>{fmtTime(log.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProjectTaskModal({ task, projectId, projectColor, projectMembers=[], allAssignees=[], tasks, onSave, onClose }) {
  const C = useC();
  const blank = {title:"",status:"todo",priority:"medium",due_date:"",due_time:"",notes:"",assignee_id:"",assignee_name:"",_addToDaily:false};
  const [form, setForm] = useState(task?{...blank,...task,_addToDaily:false}:blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  return (
    <div style={{position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:24,width:"100%",maxWidth:480,boxShadow:"0 24px 80px rgba(0,0,0,.3)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{margin:0,color:projectColor,fontSize:16}}>{task?"Edit":"New"} Project Task</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>
        <div style={{marginBottom:10}}><label style={lbl}>Task Title *</label><input value={form.title} onChange={e=>upd("title",e.target.value)} style={{...inp(),width:"100%"}} autoFocus/></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><label style={lbl}>Status</label>
            <select value={form.status} onChange={e=>upd("status",e.target.value)} style={{...inp(),width:"100%"}}>
              {PROJ_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Priority</label>
            <select value={form.priority} onChange={e=>upd("priority",e.target.value)} style={{...inp(),width:"100%"}}>
              <option value="critical">🔴 Critical</option><option value="high">🟠 High</option>
              <option value="medium">🟡 Medium</option><option value="low">🟢 Low</option>
            </select>
          </div>
          <div><label style={lbl}>📅 Due Date</label><input type="date" value={form.due_date} onChange={e=>upd("due_date",e.target.value)} style={{...inp(),width:"100%"}}/></div>
          <div><label style={lbl}>⏰ Due Time</label><input type="time" value={form.due_time} onChange={e=>upd("due_time",e.target.value)} style={{...inp(),width:"100%"}}/></div>
          {projectMembers.filter(m=>m.status==="accepted").length>0 && (
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>👤 Assign To</label>
              <select value={form.assignee_id||""} onChange={e=>{
                const pid=projectId;
                const proj=projectMembers?.[0]?.project_id; // context
                const person=allAssignees.find(x=>x.id===e.target.value);
                upd("assignee_id",e.target.value);
                upd("assignee_name",person?.name||'');
              }} style={{...inp(),width:"100%"}}>
                <option value="">— Unassigned —</option>
                {allAssignees.map(p=><option key={p.id} value={p.id}>{p.badge} {p.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{marginBottom:12}}><label style={lbl}>Notes</label><textarea value={form.notes} onChange={e=>upd("notes",e.target.value)} placeholder="Task details, links, resources..." style={{...ta(70),width:"100%"}}/></div>
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:C.surfaceAlt,borderRadius:8,marginBottom:12}}>
          <input type="checkbox" id="addToDaily" checked={form._addToDaily} onChange={e=>upd("_addToDaily",e.target.checked)} style={{accentColor:projectColor}}/>
          <label htmlFor="addToDaily" style={{fontSize:12,color:C.text,cursor:"pointer"}}>{task ? "Add to Daily Tasks board (on due date)" : "Also add to Daily Tasks board (on due date)"}</label>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{...btn("#999"),padding:"8px 18px"}}>Cancel</button>
          <button onClick={()=>{if(!form.title.trim())return;const{_addToDaily,...rest}=form;onSave({...rest,_addToDaily:form._addToDaily});}} style={{...btn(projectColor),padding:"8px 18px"}}>💾 Save Task</button>
        </div>
      </div>
    </div>
  );
}

// ── FeedSection — Social feed with streaks, books, goals ──────────────────────
function FeedSection({ ctx }) {
  const C = useC();
  const { feedPosts=[], addFeedPost, deleteFeedPost, toggleFeedLike,
          fellows=[], sendFellowRequest, respondFellowRequest, removeFellow, searchUserByUsername,
          goals=[], habits=[], habitLog={}, books=[], uid } = ctx;
  const [tab, setTab] = useState('feed');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { supabase: _sb } = ctx;

  const myPosts = feedPosts.filter(p=>p.user_id===uid);
  const fellowIds = fellows.filter(f=>f.status==="accepted").map(f=>f.requester_id===uid?f.addressee_id:f.requester_id);
  const fellowPosts = feedPosts.filter(p=>fellowIds.includes(p.user_id));
  const allVisible = [...myPosts, ...fellowPosts].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
  const pendingReqs = fellows.filter(f=>f.status==="pending"&&f.addressee_id===uid);

  const shareStreak = async () => {
    if (!addFeedPost) { ctx.setAlert?.('❌ Feed not available yet.'); return; }
    // Calculate genuine streak — consecutive days up to today
    const today = new Date();
    let streak = 0;
    const d = new Date(today);
    const fmtD = (dt) => { const y=dt.getFullYear(),m=String(dt.getMonth()+1).padStart(2,'0'),dd=String(dt.getDate()).padStart(2,'0'); return `${y}-${m}-${dd}`; };
    while (true) {
      const dateStr = fmtD(d);
      const dayDone = habits.some(h => habitLog[`${dateStr}:${h.id}`]);
      if (!dayDone) break;
      streak++;
      d.setDate(d.getDate() - 1);
      if (streak > 365) break;
    }
    const topHabit = habits[0]?.name || 'habits';
    const msg = streak > 0
      ? `🔥 ${streak}-day streak on ${topHabit}! Staying consistent!`
      : `💪 Started tracking ${topHabit} today!`;
    await addFeedPost('streak', { streak, habit: topHabit, message: msg });
    ctx.setAlert?.('📤 Streak shared to your feed!');
  };
  const shareBook = async (book) => {
    await addFeedPost('book', { title: book.title, author: book.author, rating: book.rating, status: book.status, cover_image: book.cover_image });
    ctx.setAlert?.('📤 Book shared to your feed!');
  };
  const shareGoal = async (goal) => {
    const ch = Object.values(goal.checkinHistory||{}).filter(Boolean).length;
    await addFeedPost('goal', { title: goal.title, checkins: ch, priority: goal.priority });
    ctx.setAlert?.('📤 Goal shared to your feed!');
  };

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const result = await searchUserByUsername(searchQ);
      const d = result?.data || result;
      setSearchResults(Array.isArray(d) ? d : d ? [d] : []);
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  const formatTime = (ts) => {
    const d = new Date(ts); const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  const TABS = [['feed','📰 Feed'],['share','📤 Share'],['fellows','👥 Fellows']];

  return (
    <div style={{fontFamily:FONT,color:C.text,maxWidth:700,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 style={{margin:0,color:C.indigo,fontSize:22,fontWeight:800}}>📰 Feed</h2>
        {pendingReqs.length>0&&<span style={{background:C.red,color:"#fff",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:700}}>{pendingReqs.length} fellow request{pendingReqs.length>1?"s":""}</span>}
      </div>

      <div style={{display:"flex",gap:0,marginBottom:20,background:C.surfaceAlt,borderRadius:10,padding:3}}>
        {TABS.map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:tab===id?C.surface:"transparent",color:tab===id?C.indigo:C.muted,fontWeight:tab===id?700:400,cursor:"pointer",fontSize:12,fontFamily:FONT,boxShadow:tab===id?C.shadowSm:""}}>{label}</button>
        ))}
      </div>

      {/* ── FEED TAB ── */}
      {tab==="feed" && (
        <div>
          {allVisible.length===0 ? (
            <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
              <div style={{fontSize:40,marginBottom:10}}>📭</div>
              <div style={{fontSize:14,marginBottom:6}}>Your feed is empty.</div>
              <div style={{fontSize:12}}>Share your streaks, books, or goals. Add fellows to see their posts.</div>
            </div>
          ) : allVisible.map(post=>(
            <div key={post.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:16,marginBottom:12,boxShadow:C.shadowSm}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:C.indigo,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,flexShrink:0}}>
                  {(post.profiles?.full_name||post.profiles?.username||'U').slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.text}}>{post.profiles?.full_name || (post.profiles?.username ? `@${post.profiles.username}` : 'You')}</div>
                  <div style={{fontSize:11,color:C.muted}}>{formatTime(post.created_at)}</div>
                </div>
                {post.user_id===uid&&<button onClick={()=>deleteFeedPost(post.id)} style={{background:"none",border:"none",cursor:"pointer",color:C.light,fontSize:12}}>✕</button>}
              </div>
              {/* Post content by type */}
              {post.type==="streak"&&<div style={{background:C.isDark?"rgba(245,158,11,.15)":C.amberL,borderRadius:10,padding:"10px 14px",fontSize:14,fontWeight:600,color:C.amber}}>🔥 {post.data?.message||`${post.data?.streak} streaks logged`}</div>}
              {post.type==="book"&&(
                <div style={{display:"flex",gap:12,alignItems:"center",background:C.surfaceAlt,borderRadius:10,padding:"10px 14px"}}>
                  {post.data?.cover_image?<img src={post.data.cover_image} alt="cover" style={{width:40,height:55,objectFit:"cover",borderRadius:4}}/>:<div style={{width:40,height:55,background:C.border,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>📚</div>}
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:C.text}}>{post.data?.title}</div>
                    <div style={{fontSize:11,color:C.muted}}>{post.data?.author}</div>
                    {post.data?.rating>0&&<div style={{fontSize:12,marginTop:2}}>{"⭐".repeat(post.data.rating)}</div>}
                  </div>
                </div>
              )}
              {post.type==="goal"&&<div style={{background:C.isDark?"rgba(34,197,94,.1)":C.greenM,borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontWeight:700,fontSize:13,color:C.green}}>🎯 {post.data?.title}</div>
                <div style={{fontSize:11,color:C.muted,marginTop:3}}>{post.data?.checkins} check-ins logged</div>
              </div>}
              {/* Like button */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                <button onClick={()=>toggleFeedLike(post.id)} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:4,color:post.liked_by_me?C.red:C.muted,fontSize:13,padding:0}}>
                  {post.liked_by_me?"❤️":"🤍"} <span style={{fontSize:12}}>{post.likes_count||0}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SHARE TAB ── */}
      {tab==="share" && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
            <div style={{fontWeight:700,color:C.amber,marginBottom:4}}>🔥 Share Habit Streak</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Pick a habit to share its streak to your feed.</div>
            {habits.filter(h=>!h.archived).length===0
              ? <p style={{fontSize:12,color:C.muted,margin:0}}>No habits tracked yet.</p>
              : <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {habits.filter(h=>!h.archived).map(h=>{
                    // Calculate streak for this specific habit
                    let s=0, d=new Date();
                    const fmtD2=(dt)=>{const y=dt.getFullYear(),m=String(dt.getMonth()+1).padStart(2,'0'),dd=String(dt.getDate()).padStart(2,'0');return `${y}-${m}-${dd}`;};
                    while(s<=365){const ds=fmtD2(d);if(!habitLog[`${ds}:${h.id}`])break;s++;d.setDate(d.getDate()-1);}
                    return (
                      <div key={h.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:C.surfaceAlt,borderRadius:8,border:`1px solid ${C.border}`}}>
                        <span style={{fontSize:18}}>{h.icon||'🔷'}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:C.text}}>{h.name}</div>
                          <div style={{fontSize:11,color:s>0?C.amber:C.muted}}>
                            {s>0 ? `🔥 ${s}-day streak` : 'No streak yet today'}
                          </div>
                        </div>
                        <button
                          onClick={async()=>{
                            const msg = s>0
                              ? `🔥 ${s}-day streak on "${h.name}"! Staying consistent!`
                              : `💪 Just checked in "${h.name}" — starting a new streak!`;
                            await addFeedPost('streak',{streak:s,habit:h.name,message:msg});
                            ctx.setAlert?.('📤 Shared "'+h.name+'" streak to your feed!');
                          }}
                          style={{...btn(C.amber),fontSize:11,padding:'5px 10px',flexShrink:0}}>
                          📤 Share
                        </button>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
          {books.filter(b=>b.status==="read"||b.status==="reading").slice(0,5).map(b=>(
            <div key={b.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,display:"flex",alignItems:"center",gap:12}}>
              {b.cover_image?<img src={b.cover_image} alt="cover" style={{width:36,height:50,objectFit:"cover",borderRadius:4}}/>:<div style={{width:36,height:50,background:C.surfaceAlt,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}>📚</div>}
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:C.text}}>{b.title}</div>
                <div style={{fontSize:11,color:C.muted}}>{b.status}</div>
              </div>
              <button onClick={()=>shareBook(b)} style={{...btn(C.indigo),fontSize:11,padding:"5px 10px"}}>📤 Share</button>
            </div>
          ))}
          {goals.slice(0,4).map(g=>(
            <div key={g.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14,display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:C.green}}>🎯 {g.title}</div>
                <div style={{fontSize:11,color:C.muted}}>{Object.values(g.checkinHistory||{}).filter(Boolean).length} check-ins</div>
              </div>
              <button onClick={()=>shareGoal(g)} style={{...btn(C.green),fontSize:11,padding:"5px 10px"}}>📤 Share</button>
            </div>
          ))}
        </div>
      )}

      {/* ── FELLOWS TAB ── */}
      {tab==="fellows" && (
        <div>
          {/* Pending requests */}
          {pendingReqs.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.amber,marginBottom:8}}>⏳ Pending Requests ({pendingReqs.length})</div>
              {pendingReqs.map(f=>(
                <div key={f.id} style={{background:C.surface,border:`1px solid ${C.amber}40`,borderRadius:10,padding:"10px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:C.indigo,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>
                    {(f.requester?.full_name||f.requester?.username||'?').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,color:C.text}}>{f.requester?.full_name||`@${f.requester?.username}`}</div>
                    <div style={{fontSize:11,color:C.muted}}>Wants to be your accountability fellow</div>
                  </div>
                  <button onClick={()=>respondFellowRequest(f.id,true)} style={{...btn(C.green),fontSize:11,padding:"5px 10px"}}>✓ Accept</button>
                  <button onClick={()=>respondFellowRequest(f.id,false)} style={{...btn("#999"),fontSize:11,padding:"5px 8px"}}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* My fellows */}
          {fellows.filter(f=>f.status==="accepted").length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>👥 My Fellows</div>
              {fellows.filter(f=>f.status==="accepted").map(f=>{
                const partner=f.requester_id===uid?f.addressee:f.requester;
                return (
                  <div key={f.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:C.green,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>
                      {(partner?.full_name||partner?.username||'?').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:13,color:C.text}}>{partner?.full_name||`@${partner?.username}`}</div>
                      <div style={{fontSize:11,color:C.muted}}>Accountability Fellow</div>
                    </div>
                    <button onClick={()=>{if(window.confirm("Remove this fellow?"))removeFellow(f.id);}} style={{...btn("#999"),fontSize:11,padding:"5px 8px"}}>Remove</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Find fellows */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
            <div style={{fontWeight:700,color:C.text,marginBottom:10}}>🔍 Find Accountability Fellows</div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSearch()} placeholder="Search by @username..." style={{...inp(),flex:1}}/>
              <button onClick={handleSearch} disabled={searching||!searchQ.trim()} style={{...btn(C.indigo),opacity:searching||!searchQ.trim()?0.5:1}}>{searching?"...":"Search"}</button>
            </div>
            {searchResults.map(u=>(
              <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:`1px solid ${C.border}`}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:C.indigo,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>
                  {(u.full_name||u.username||'?').slice(0,2).toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:13,color:C.text}}>{u.full_name||u.username}</div>
                  {u.username&&<div style={{fontSize:11,color:C.muted}}>@{u.username}</div>}
                </div>
                <button onClick={()=>{sendFellowRequest(u.id);ctx.setAlert?.("✅ Fellow request sent!");}} style={{...btn(C.indigo),fontSize:11,padding:"5px 10px"}}>+ Connect</button>
              </div>
            ))}
            {searchResults.length===0&&searchQ&&!searching&&<div style={{fontSize:12,color:C.muted,textAlign:"center",padding:"10px 0"}}>No users found for "@{searchQ}"</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LINK VAULT — Secure Link & Password Manager (integrated from LinkVault app)
// ══════════════════════════════════════════════════════════════════════════════
const VLT = "#6c63ff";
const VLTL = "rgba(108,99,255,.12)";
const SSO_LIST_V = [
  {id:"google",n:"Google",e:"🟡"},{id:"github",n:"GitHub",e:"⬛"},
  {id:"microsoft",n:"Microsoft",e:"🔷"},{id:"facebook",n:"Facebook",e:"🔵"},
  {id:"linkedin",n:"LinkedIn",e:"💼"},{id:"discord",n:"Discord",e:"🟣"},
  {id:"twitter",n:"Twitter/X",e:"🐦"},{id:"apple",n:"Apple",e:"🍎"},
  {id:"slack",n:"Slack",e:"🟩"},{id:"notion",n:"Notion",e:"📄"},
  {id:"other",n:"Other",e:"🔐"},
];
const VAULT_EMOJIS = ["📁","🗂","📚","🎓","💼","🔒","🌐","⚡","🛠","🎯","📊","🔬","🎨","🚀","💡","📱","🖥","🔧","📝","🌟","🏠","🔑","🌍","🎮"];
const VAULT_COLORS = ["#6366f1","#10b981","#f43f5e","#f59e0b","#3b82f6","#8b5cf6","#ec4899","#14b8a6"];

function vaultFavicon(url) {
  if (!url) return "🔗";
  try {
    const d = new URL(url).hostname.toLowerCase();
    const m = {"github.com":"🐙","google.com":"🟡","youtube.com":"📹","linkedin.com":"💼",
      "twitter.com":"🐦","x.com":"🐦","facebook.com":"🔵","instagram.com":"📸",
      "discord.com":"🟣","notion.so":"📄","slack.com":"🟩","microsoft.com":"🔷",
      "apple.com":"🍎","udemy.com":"🎓","figma.com":"🎨","vercel.com":"▲",
      "gitlab.com":"🦊","stackoverflow.com":"💬","amazon.com":"📦","netflix.com":"🎬"};
    for (const [k,v] of Object.entries(m)) if (d.includes(k)) return v;
    return "🌐";
  } catch { return "🌐"; }
}

function LinkVault({ ctx }) {
  const C = useC();
  const { vaultFolders=[], addVaultFolder, updateVaultFolder, deleteVaultFolder,
          vaultLinks=[], addVaultLink, updateVaultLink, deleteVaultLink } = ctx;
  // Pre-read cache from localStorage (persists across page revisits, 4hr TTL)
  const _vaultCache = (() => {
    try {
      const c = localStorage.getItem('vault_mfa_cache');
      if (!c) return null;
      const parsed = JSON.parse(c);
      // Expire after 4 hours
      if (parsed.ts && Date.now() - parsed.ts > 4 * 60 * 60 * 1000) {
        localStorage.removeItem('vault_mfa_cache');
        return null;
      }
      return parsed;
    } catch { return null; }
  })();
  const [vaultUnlocked, setVaultUnlocked] = useState(_vaultCache ? (!_vaultCache.hasMFA || _vaultCache.unlocked) : false);
  const [vaultCode, setVaultCode] = useState("");
  const [vaultErr, setVaultErr] = useState("");
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultChecked, setVaultChecked] = useState(!!_vaultCache);
  const [hasMFA, setHasMFA] = useState(_vaultCache?.hasMFA || false);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Get or create a stable device token for this browser
  const getDeviceToken = () => {
    let t = localStorage.getItem('vault_device_token');
    if (!t) { t = `dt_${Date.now()}_${Math.random().toString(36).slice(2,10)}`; localStorage.setItem('vault_device_token', t); }
    return t;
  };

  // On mount: check MFA enrollment & whether this device is already remembered
  // Cache result in sessionStorage so revisiting vault in same session is instant
  useEffect(() => {
    const SESSION_KEY = 'vault_mfa_cache';
    const cached = localStorage.getItem(SESSION_KEY);
    if (cached) {
      const { hasMFA: cachedMFA, unlocked } = JSON.parse(cached);
      setHasMFA(cachedMFA);
      if (!cachedMFA || unlocked) setVaultUnlocked(true);
      setVaultChecked(true);
      return;
    }
    (async () => {
      try {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const enrolled = !!(factors?.totp?.[0]?.factor_type === "totp" || factors?.totp?.length > 0);
        setHasMFA(enrolled);
        if (!enrolled) {
          setVaultUnlocked(true);
          setVaultChecked(true);
          localStorage.setItem(SESSION_KEY, JSON.stringify({ hasMFA: false, unlocked: true, ts: Date.now() }));
          return;
        }

        // Check if this device has a valid remembered session
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const token = getDeviceToken();
          const { data: sess } = await supabase.from('vault_sessions')
            .select('*').eq('user_id', user.id).eq('device_token', token)
            .gt('expires_at', new Date().toISOString()).maybeSingle();
          if (sess) {
            setVaultUnlocked(true);
            localStorage.setItem(SESSION_KEY, JSON.stringify({ hasMFA: true, unlocked: true, ts: Date.now() }));
          } else {
            localStorage.setItem(SESSION_KEY, JSON.stringify({ hasMFA: true, unlocked: false, ts: Date.now() }));
          }
        }
      } catch(e) { /* silent */ }
      setVaultChecked(true);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlockVault = async () => {
    setVaultLoading(true); setVaultErr("");
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = factors?.totp?.[0];
      if (!totp) { setVaultUnlocked(true); setVaultLoading(false); return; }
      const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId: totp.id });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: ch.id, code: vaultCode });
      if (vErr) throw new Error("Invalid code — check your authenticator app.");
      setVaultUnlocked(true);
      // Update cache to mark vault as unlocked
      try { localStorage.setItem('vault_mfa_cache', JSON.stringify({ hasMFA: true, unlocked: true, ts: Date.now() })); } catch{}

      // Save device session if "remember this device" was checked
      if (rememberDevice) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const token = getDeviceToken();
          const expires = new Date(); expires.setDate(expires.getDate() + 30);
          await supabase.from('vault_sessions').upsert(
            { user_id: user.id, device_token: token, expires_at: expires.toISOString() },
            { onConflict: 'user_id,device_token' }
          );
        }
      }
    } catch(e) { setVaultErr(e.message); }
    setVaultLoading(false);
  };

  const [curFolder, setCurFolder] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);

  if (!vaultChecked) { setVaultChecked(true); }

  if (hasMFA && !vaultUnlocked) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:380,padding:32,textAlign:"center",fontFamily:FONT}}>
      <div style={{fontSize:52,marginBottom:12}}>🔐</div>
      <h3 style={{margin:"0 0 6px",color:C.text,fontSize:18,fontWeight:700}}>Vault is Protected</h3>
      <p style={{margin:"0 0 20px",color:C.muted,fontSize:14,maxWidth:320}}>Enter your authenticator code. You only need to do this once per login session.</p>
      <input value={vaultCode} onChange={e=>setVaultCode(e.target.value.replace(/\D/g,"").slice(0,6))}
        placeholder="000000" maxLength={6} autoFocus
        onKeyDown={e=>e.key==="Enter"&&vaultCode.length===6&&unlockVault()}
        style={{textAlign:"center",fontSize:24,letterSpacing:"0.4em",padding:"12px 20px",borderRadius:12,border:`2px solid ${C.indigo}`,outline:"none",marginBottom:14,width:170,fontFamily:FONT,background:C.surface,color:C.text,fontWeight:700}}/>
      {vaultErr && <div style={{color:C.red,fontSize:13,marginBottom:12,background:"#fee2e2",borderRadius:8,padding:"6px 14px"}}>{vaultErr}</div>}
      {/* Remember device option */}
      <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.muted,cursor:"pointer",marginBottom:18,userSelect:"none"}}>
        <input type="checkbox" checked={rememberDevice} onChange={e=>setRememberDevice(e.target.checked)} style={{accentColor:C.indigo,width:15,height:15}}/>
        Remember this device for 30 days
      </label>
      <button onClick={unlockVault} disabled={vaultLoading||vaultCode.length<6}
        style={{...btn(vaultLoading||vaultCode.length<6?C.light:C.indigo),padding:"11px 36px",fontSize:14}}>
        {vaultLoading?"Verifying...":"Unlock Vault →"}
      </button>
      <div style={{marginTop:14,fontSize:11,color:C.light}}>
        Code refreshes every 30 seconds in your authenticator app
      </div>
    </div>
  );

  const childFolders = pid => vaultFolders.filter(f=>(f.parent_id||null)===pid);
  const folderLinks  = fid => vaultLinks.filter(l=>l.folder_id===fid);

  const getPath = fid => {
    const path=[]; let _node=vaultFolders.find(x=>x.id===fid);
    while(_node){ const _n=_node; path.unshift(_n); _node=_n.parent_id?vaultFolders.find(x=>x.id===_n.parent_id):null; } // eslint-disable-line no-loop-func
    return path;
  };

  const subFolders = childFolders(curFolder);
  const curLinks   = curFolder ? folderLinks(curFolder) : [];
  const path       = curFolder ? getPath(curFolder) : [];

  const searchHits = search.trim()
    ? vaultLinks.filter(l=>[l.title,l.url,l.desc,l.descp,...(l.tags||[])].some(v=>(v||"").toLowerCase().includes(search.toLowerCase())))
    : null;

  // Delete folder recursively in state
  const doDeleteFolder = (fid) => {
    const nuke = id => {
      childFolders(id).forEach(c=>nuke(c.id));
      vaultLinks.filter(l=>l.folder_id===id).forEach(l=>deleteVaultLink(l.id));
      deleteVaultFolder(id);
    };
    if(curFolder===fid) setCurFolder(null);
    nuke(fid);
  };

  return (
    <div>
      <SectionMasthead
        eyebrow={`System · ${vaultFolders.length} folders · ${vaultLinks.length} links saved`}
        title={<>Nothing <em>important</em> gets lost.</>}
        actions={<>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search links…" className="ta-input" style={{width:190}} />
          <button onClick={()=>setModal({type:"folder",parentId:curFolder})} className="ta-btn ta-btn-secondary">New folder</button>
          <button onClick={()=>setModal({type:"link",folderId:curFolder})} className="ta-btn ta-btn-primary">+ Add link</button>
        </>}
      />

      {/* Breadcrumb */}
      {!searchHits && (
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14,fontSize:13,flexWrap:"wrap"}}>
          <span onClick={()=>setCurFolder(null)} style={{cursor:"pointer",color:VLT,fontWeight:600,padding:"3px 10px",background:curFolder?C.surface:VLTL,borderRadius:20,border:`1px solid ${curFolder?C.border:VLT}`}}>🏠 All Folders</span>
          {path.map(f=>(
            <span key={f.id} style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{color:C.light}}>›</span>
              <span onClick={()=>setCurFolder(f.id)} style={{cursor:"pointer",color:VLT,fontWeight:600,padding:"3px 10px",background:curFolder===f.id?VLTL:C.surface,borderRadius:20,border:`1px solid ${curFolder===f.id?VLT:C.border}`}}>
                {f.icon||"📁"} {f.name}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Search Results */}
      {searchHits ? (
        <div>
          <div style={{fontSize:13,color:C.muted,marginBottom:12}}>{searchHits.length} result{searchHits.length!==1?"s":""} for "{search}" <button onClick={()=>setSearch("")} style={{...btn("#888"),fontSize:11,padding:"2px 8px",marginLeft:6}}>✕ Clear</button></div>
          {searchHits.length===0 ? <p style={{color:C.light}}>No links found.</p> :
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:12}}>
              {searchHits.map(l=><VaultLinkCard key={l.id} link={l} onEdit={()=>setModal({type:"link",existing:l,folderId:l.folder_id})} onDelete={()=>deleteVaultLink(l.id)} onCreds={()=>setModal({type:"creds",link:l})}/>)}
            </div>}
        </div>
      ) : (
        <>
          {/* Folders grid */}
          {subFolders.length > 0 && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>📁 FOLDERS</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:10}}>
                {subFolders.map((f,i)=>{
                  const fl=folderLinks(f.id).length, fc=childFolders(f.id).length, col=VAULT_COLORS[i%VAULT_COLORS.length];
                  return (
                    <div key={f.id} onClick={()=>setCurFolder(f.id)}
                      draggable
                      onDragStart={e=>{e.dataTransfer.setData('folderDragId',f.id);e.currentTarget.style.opacity='0.5';}}
                      onDragEnd={e=>{e.currentTarget.style.opacity='1';}}
                      onDragOver={e=>{e.preventDefault();e.currentTarget.style.outline=`2px dashed ${col}`;}}
                      onDragLeave={e=>{e.currentTarget.style.outline='none';}}
                      onDrop={e=>{e.preventDefault();e.currentTarget.style.outline='none';const dragId=e.dataTransfer.getData('folderDragId');if(!dragId||dragId===f.id)return;const draggedFolder=vaultFolders.find(x=>x.id===dragId);if(!draggedFolder)return;updateVaultFolder(dragId,{...draggedFolder,parent_id:f.id});setCurFolder(f.id);}}
                      style={{background:C.surface,border:`1.5px solid ${C.border}`,borderTop:`3px solid ${col}`,borderRadius:10,padding:14,cursor:"grab",position:"relative",transition:"all .15s"}}
                      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=col;}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.borderColor=C.border;}}>
                      <div style={{fontSize:26,marginBottom:8}}>{f.icon||"📁"}</div>
                      <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:3}}>{f.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{fc} subfolder{fc!==1?"s":""} · {fl} link{fl!==1?"s":""}</div>
                      {(f.desc||f.descp) && <div style={{fontSize:11,color:C.light,marginTop:4,display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{f.desc||f.descp}</div>}
                      <div style={{position:"absolute",top:8,right:8,display:"flex",gap:4,opacity:0,transition:"opacity .15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.opacity=1;}} onMouseLeave={e=>{e.currentTarget.style.opacity=0;}}
                        onClick={e=>e.stopPropagation()}>
                        <button onClick={()=>setModal({type:"folder",existing:f,parentId:f.parent_id})} style={{...btn("#888"),padding:"3px 7px",fontSize:11}}>✏️</button>
                        <button onClick={()=>{if(window.confirm(`Delete "${f.name}" and all contents?`))doDeleteFolder(f.id);}} style={{...btn(C.red),padding:"3px 7px",fontSize:11}}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Links grid */}
          {curFolder && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>🔗 LINKS</div>
              {curLinks.length===0
                ? <div style={{textAlign:"center",padding:"30px 20px",color:C.light,fontSize:13}}>
                    No links in this folder.{" "}
                    <button onClick={()=>setModal({type:"link",folderId:curFolder})} style={{...btn(VLT),fontSize:11,padding:"4px 10px"}}>+ Add Link</button>
                  </div>
                : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:12}}>
                    {curLinks.map(l=><VaultLinkCard key={l.id} link={l}
                      onEdit={()=>setModal({type:"link",existing:l,folderId:l.folder_id})}
                      onDelete={()=>deleteVaultLink(l.id)}
                      onCreds={()=>setModal({type:"creds",link:l})}/>)}
                  </div>
              }
            </div>
          )}

          {/* Empty root */}
          {!curFolder && subFolders.length===0 && (
            <div style={{textAlign:"center",padding:"60px 20px",color:C.muted}}>
              <div style={{fontSize:52,marginBottom:14}}>🔐</div>
              <h3 style={{color:C.text,margin:"0 0 8px",fontSize:18}}>Welcome to Link Vault</h3>
              <p style={{fontSize:13,marginBottom:20}}>Organize all your links and passwords in secure folders. Start by creating your first folder.</p>
              <button onClick={()=>setModal({type:"folder",parentId:null})} style={{...btn(VLT),fontSize:14,padding:"10px 24px"}}>📁 Create First Folder</button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {modal?.type==="folder" && (
        <VaultFolderModal
          existing={modal.existing}
          parentId={modal.parentId}
          onSave={d=>{
            if(modal.existing) updateVaultFolder(modal.existing.id,d);
            else addVaultFolder({...d,parent_id:modal.parentId});
            setModal(null);
          }}
          onClose={()=>setModal(null)}
        />
      )}
      {modal?.type==="link" && ReactDOM.createPortal(
        <VaultLinkModal
          existing={modal.existing}
          folderId={modal.folderId}
          onSave={d=>{
            if(modal.existing) updateVaultLink(modal.existing.id,d);
            else addVaultLink({...d,folder_id:modal.folderId});
            setModal(null);
          }}
          onClose={()=>setModal(null)}
        />,
        document.body
      )}
      {modal?.type==="creds" && ReactDOM.createPortal(
        <VaultCredsModal link={modal.link} onClose={()=>setModal(null)}/>,
        document.body
      )}
    </div>
  );
}

function VaultLinkCard({link:l, onEdit, onDelete, onCreds}) {
  const authBadge = {
    none: <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:C.surfaceAlt,color:C.muted}}>Public</span>,
    up:   <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:VLTL,color:VLT}}>🔑 User/Pass</span>,
    sso:  <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:C.greenM,color:C.green}}>🌐 SSO</span>,
    otp:  <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:C.amberL,color:C.amber}}>📱 OTP</span>,
  }[l.auth_type||"none"];
  return (
    <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:10,padding:14,transition:"border-color .15s"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor=VLT}
      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
      <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
        <div style={{width:36,height:36,borderRadius:8,background:VLTL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,flexShrink:0}}>{vaultFavicon(l.url)}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.text}}>{l.title}</div>
          <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.url}</div>
        </div>
        {authBadge}
      </div>
      {(l.desc||l.descp) && <div style={{fontSize:12,color:C.muted,marginBottom:8,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{l.desc||l.descp}</div>}
      {(l.tags||[]).length>0 && <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>{(l.tags||[]).map(t=><span key={t} style={{fontSize:10,padding:"2px 7px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,color:C.muted}}>#{t}</span>)}</div>}
      <div style={{display:"flex",gap:6}}>
        <button onClick={()=>window.open(l.url,"_blank")} style={{...btn(C.green),flex:1,padding:"6px 4px",fontSize:11}}>🚀 Visit</button>
        {l.auth_type&&l.auth_type!=="none"&&<button onClick={onCreds} style={{...btn(VLT),flex:1,padding:"6px 4px",fontSize:11}}>👁 Creds</button>}
        <button onClick={onEdit} style={{...btn(C.amber),flex:1,padding:"6px 4px",fontSize:11}}>✏️</button>
        <button onClick={()=>{if(window.confirm("Delete this link?"))onDelete();}} style={{...btn(C.red),flex:1,padding:"6px 4px",fontSize:11}}>🗑</button>
      </div>
    </div>
  );
}

function VaultFolderModal({existing, onSave, onClose}) {
  const [name,setName]=useState(existing?.name||"");
  const [desc,setDesc]=useState(existing?.desc||"");
  const [icon,setIcon]=useState(existing?.icon||"📁");
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:28,width:"100%",maxWidth:460,boxShadow:"0 24px 80px rgba(0,0,0,.35)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <h3 style={{margin:0,color:VLT,fontSize:16}}>{existing?"Edit":"New"} Folder</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>
        <div style={{marginBottom:12}}><label style={lbl}>Name *</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Work Tools" style={{...inp(),width:"100%"}} autoFocus/></div>
        <div style={{marginBottom:12}}><label style={lbl}>Description</label><input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Optional" style={{...inp(),width:"100%"}}/></div>
        <div style={{marginBottom:18}}>
          <label style={lbl}>Icon</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {VAULT_EMOJIS.map(e=><button key={e} onClick={()=>setIcon(e)} style={{width:34,height:34,borderRadius:7,border:`2px solid ${icon===e?VLT:"transparent"}`,background:icon===e?VLTL:C.bg,fontSize:17,cursor:"pointer"}}>{e}</button>)}
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{...btn("#999"),padding:"8px 18px"}}>Cancel</button>
          <button onClick={()=>{if(!name.trim())return;onSave({name,desc,icon});}} style={{...btn(VLT),padding:"8px 18px"}}>💾 Save</button>
        </div>
      </div>
    </div>
  );
}

function VaultLinkModal({existing, onSave, onClose}) {
  const blank={title:"",url:"",desc:"",auth_type:"none",tags:"",username:"",password:"",sso_id:"",sso_email:"",sso_password:"",otp_type:"phone",otp_value:""};
  const [form,setForm]=useState(existing?{...blank,...existing,tags:(existing.tags||[]).join(", ")}:blank);
  const [showPw,setShowPw]=useState(false);
  const upd=(k,v)=>setForm(p=>({...p,[k]:v}));
  const AUTH=[{id:"none",l:"🔓 None"},{id:"up",l:"🔑 User/Pass"},{id:"sso",l:"🌐 SSO"},{id:"otp",l:"📱 OTP"}];
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)",overflowY:"auto"}} onClick={e=>{e.stopPropagation();if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:28,width:"100%",maxWidth:540,boxShadow:"0 24px 80px rgba(0,0,0,.35)",margin:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <h3 style={{margin:0,color:C.green,fontSize:16}}>{existing?"Edit":"Add"} Link</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"1/-1"}}><label style={lbl}>Title *</label><input value={form.title} onChange={e=>upd("title",e.target.value)} placeholder="e.g. GitHub" style={{...inp(),width:"100%"}} autoFocus/></div>
          <div style={{gridColumn:"1/-1"}}><label style={lbl}>URL *</label><input value={form.url} onChange={e=>upd("url",e.target.value)} placeholder="https://..." style={{...inp(),width:"100%"}}/></div>
          <div style={{gridColumn:"1/-1"}}><label style={lbl}>Description</label><input value={form.desc} onChange={e=>upd("desc",e.target.value)} placeholder="Optional note" style={{...inp(),width:"100%"}}/></div>
          <div style={{gridColumn:"1/-1"}}><label style={lbl}>Tags (comma separated)</label><input value={form.tags} onChange={e=>upd("tags",e.target.value)} placeholder="work, dev, tools" style={{...inp(),width:"100%"}}/></div>
        </div>
        <div style={{marginBottom:10}}>
          <label style={lbl}>Auth Type</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {AUTH.map(a=><button key={a.id} onClick={()=>upd("auth_type",a.id)} style={{padding:"5px 12px",borderRadius:7,border:`1.5px solid ${form.auth_type===a.id?VLT:C.border}`,background:form.auth_type===a.id?VLTL:C.bg,color:form.auth_type===a.id?VLT:C.muted,cursor:"pointer",fontSize:12,fontWeight:600}}>{a.l}</button>)}
          </div>
        </div>
        {form.auth_type==="up"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><label style={lbl}>Username</label><input value={form.username} onChange={e=>upd("username",e.target.value)} placeholder="user@email.com" style={{...inp(),width:"100%"}}/></div>
            <div><label style={lbl}>Password</label>
              <div style={{position:"relative"}}>
                <input type={showPw?"text":"password"} value={form.password} onChange={e=>upd("password",e.target.value)} placeholder="••••••••" style={{...inp(),width:"100%",paddingRight:34}}/>
                <button onClick={()=>setShowPw(p=>!p)} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:14}}>{showPw?"🙈":"👁"}</button>
              </div>
            </div>
          </div>
        )}
        {form.auth_type==="sso"&&(
          <div style={{marginBottom:10}}>
            <label style={lbl}>Provider</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
              {SSO_LIST_V.map(p=><button key={p.id} onClick={()=>upd("sso_id",p.id)} style={{padding:"4px 10px",borderRadius:7,border:`1.5px solid ${form.sso_id===p.id?VLT:C.border}`,background:form.sso_id===p.id?VLTL:C.bg,color:form.sso_id===p.id?VLT:C.muted,cursor:"pointer",fontSize:11}}>{p.e} {p.n}</button>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label style={lbl}>Email</label><input value={form.sso_email} onChange={e=>upd("sso_email",e.target.value)} placeholder="you@email.com" style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Password</label><input type="password" value={form.sso_password} onChange={e=>upd("sso_password",e.target.value)} placeholder="••••••••" style={{...inp(),width:"100%"}}/></div>
            </div>
          </div>
        )}
        {form.auth_type==="otp"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><label style={lbl}>Via</label>
              <select value={form.otp_type} onChange={e=>upd("otp_type",e.target.value)} style={{...inp(),width:"100%"}}>
                <option value="phone">📱 Phone</option><option value="email">📧 Email</option>
              </select>
            </div>
            <div><label style={lbl}>Contact</label><input value={form.otp_value} onChange={e=>upd("otp_value",e.target.value)} placeholder="+91..." style={{...inp(),width:"100%"}}/></div>
          </div>
        )}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
          <button onClick={onClose} style={{...btn("#999"),padding:"8px 18px"}}>Cancel</button>
          <button onClick={()=>{
            if(!form.title.trim()||!form.url.trim())return;
            onSave({...form,tags:(form.tags||"").split(",").map(t=>t.trim()).filter(Boolean)});
          }} style={{...btn(VLT),padding:"8px 18px"}}>💾 Save Link</button>
        </div>
      </div>
    </div>
  );
}

function VaultCredsModal({link:l, onClose}) {
  const [revealed,setRevealed]=useState({});
  const copy=(val,k)=>{
    navigator.clipboard.writeText(val).then(()=>{
      setRevealed(p=>({...p,[k+"_cp"]:true}));
      setTimeout(()=>setRevealed(p=>({...p,[k+"_cp"]:false})),1400);
    });
  };
  const Row=({label,val,masked,ck})=>{
    if(!val)return null;
    const show=revealed[ck];
    return(
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 0",borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:11,color:C.muted,width:88,flexShrink:0}}>{label}</span>
        <span style={{flex:1,fontSize:13,color:C.text,wordBreak:"break-all"}}>{masked&&!show?"••••••••":val}</span>
        {masked&&<button onClick={()=>setRevealed(p=>({...p,[ck]:!p[ck]}))} style={{...btn("#888"),padding:"3px 8px",fontSize:11}}>{show?"Hide":"Show"}</button>}
        <button onClick={()=>copy(val,ck)} style={{...btn(VLT),padding:"3px 8px",fontSize:11}}>{revealed[ck+"_cp"]?"✓":"Copy"}</button>
      </div>
    );
  };
  return(
    <div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:28,width:"100%",maxWidth:460,boxShadow:"0 24px 80px rgba(0,0,0,.35)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <h3 style={{margin:0,fontSize:16,color:C.text}}>🔐 {l.title}</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>
        <Row label="URL" val={l.url} ck="url"/>
        {l.auth_type==="up"&&<><Row label="Username" val={l.username} ck="user"/><Row label="Password" val={l.password} masked ck="pass"/></>}
        {l.auth_type==="sso"&&<><Row label="Provider" val={SSO_LIST_V.find(x=>x.id===l.sso_id)?.n||l.sso_id} ck="prov"/><Row label="Email" val={l.sso_email} ck="smail"/><Row label="Password" val={l.sso_password} masked ck="spw"/></>}
        {l.auth_type==="otp"&&<><Row label="Via" val={l.otp_type==="phone"?"📱 Phone":"📧 Email"} ck="otyp"/><Row label="Contact" val={l.otp_value} ck="oval"/></>}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <button onClick={()=>window.open(l.url,"_blank")} style={{...btn(C.green),padding:"8px 18px"}}>🚀 Open Link</button>
          <button onClick={onClose} style={{...btn("#999"),padding:"8px 18px"}}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SPECIAL DATES MANAGER — embedded modal accessible from dashboard
// ══════════════════════════════════════════════════════════════════════════════
export function SpecialDatesModal({ specialDates=[], addSpecialDate, deleteSpecialDate, onClose }) {
  const blank = {name:"",type:"birthday",day:1,month:1,year:"",notes:""};
  const [form, setForm] = useState(blank);
  const [showing, setShowing] = useState(false);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const save = () => {
    if (!form.name.trim()) return;
    addSpecialDate({
      name: form.name.trim(),
      type: form.type,
      day: Number(form.day),
      month: Number(form.month),
      year: form.year ? Number(form.year) : null,
      notes: form.notes,
    });
    setForm(blank); setShowing(false);
  };

  const grouped = {};
  (specialDates||[]).forEach(d=>{
    const k=`${String(d.month).padStart(2,"0")}-${String(d.day).padStart(2,"0")}`;
    if(!grouped[k]) grouped[k]=[];
    grouped[k].push(d);
  });

  return (
    <div style={{position:"fixed",inset:0,zIndex:5000,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,padding:28,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,.35)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <h3 style={{margin:0,color:"#C8375A",fontSize:16}}>🎂 Birthdays & Anniversaries</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>
        <p style={{fontSize:12,color:C.muted,margin:"0 0 14px"}}>These repeat every year automatically. You'll see a corner banner on the day.</p>

        <button onClick={()=>setShowing(v=>!v)} style={{...btn("#C8375A"),marginBottom:14}}>+ Add Date</button>

        {showing && (
          <div style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div style={{gridColumn:"1/-1"}}><label style={lbl}>Name *</label><input value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. Mom, Priya, Wedding Anniversary" style={{...inp(),width:"100%"}} autoFocus/></div>
              <div><label style={lbl}>Type</label>
                <select value={form.type} onChange={e=>upd("type",e.target.value)} style={{...inp(),width:"100%"}}>
                  <option value="birthday">🎂 Birthday</option>
                  <option value="anniversary">💍 Anniversary</option>
                  <option value="other">⭐ Other</option>
                </select>
              </div>
              <div><label style={lbl}>Year (optional)</label><input type="number" value={form.year} onChange={e=>upd("year",e.target.value)} placeholder="e.g. 1990" style={{...inp(),width:"100%"}}/></div>
              <div><label style={lbl}>Month</label>
                <select value={form.month} onChange={e=>upd("month",e.target.value)} style={{...inp(),width:"100%"}}>
                  {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Day</label>
                <select value={form.day} onChange={e=>upd("day",e.target.value)} style={{...inp(),width:"100%"}}>
                  {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={save} style={btn("#C8375A")}>💾 Save</button>
              <button onClick={()=>setShowing(false)} style={btn("#999")}>Cancel</button>
            </div>
          </div>
        )}

        {Object.keys(grouped).sort().map(k=>{
          const [mo,dy] = k.split("-");
          return (
            <div key={k} style={{marginBottom:10,padding:"10px 14px",background:C.surfaceAlt,borderRadius:10,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8}}>{MONTHS[Number(mo)-1]} {Number(dy)}</div>
              {grouped[k].map(d=>(
                <div key={d.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontSize:18}}>{(d.type||"birthday")==="birthday"?"🎂":(d.type)==="anniversary"?"💍":"⭐"}</span>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,color:C.text}}>{d.name}</div>
                    {d.year&&<div style={{fontSize:10,color:C.muted}}>Since {d.year}</div>}
                  </div>
                  <button onClick={()=>{if(window.confirm(`Delete "${d.name}"?`))deleteSpecialDate(d.id);}} style={{...btn(C.red),padding:"3px 8px",fontSize:11}}>🗑</button>
                </div>
              ))}
            </div>
          );
        })}

        {!(specialDates||[]).length && !showing && (
          <div style={{textAlign:"center",padding:"30px 20px",color:C.muted}}>
            <div style={{fontSize:36,marginBottom:8}}>🎂</div>
            <div style={{fontSize:13}}>No birthdays or anniversaries saved yet.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SOCIAL SHARE CARDS — shareable achievement posters for social media
// ══════════════════════════════════════════════════════════════════════════════

const SHARE_PLATFORMS = [
  { id:"copy",      label:"📋 Copy Caption", color:"#6B7280" },
  { id:"download",  label:"⬇ Save Card",    color:"#374151" },
  { id:"linkedin",  label:"💼 LinkedIn",     color:"#0A66C2" },
  { id:"twitter",   label:"🐦 X / Twitter",  color:"#1DA1F2" },
  { id:"whatsapp",  label:"💬 WhatsApp",     color:"#25D366" },
  { id:"instagram", label:"📸 Instagram",    color:"#E1306C" },
  { id:"facebook",  label:"📘 Facebook",     color:"#1877F2" },
  { id:"threads",   label:"🧵 Threads",      color:"#000000" },
];

const ASPREO_URL = 'https://aspreo.com';

// Platform-specific caption builder
function buildCaption(platform, type, data) {
  const tag = `\n\n🚀 Track yours free → ${ASPREO_URL}`;
  const hashtags = {
    linkedin: type==="book"
      ? `\n\n#Reading #BookNotes #ContinuousLearning #PersonalDevelopment`
      : `\n\n#Goals #PersonalGrowth #Productivity #GrowthMindset`,
    twitter: type==="book"
      ? `\n\n#BookTwt #Reading #Learning`
      : `\n\n#Goals #Habits #GrowthMindset`,
    instagram: type==="book"
      ? `\n\n#bookstagram #readinglife #booknotes #learning #selfimprovement`
      : `\n\n#goals #habittracker #selfimprovement #growthmindset #motivation`,
    threads: type==="book"
      ? `\n\n#bookstagram #reading #booknotes`
      : `\n\n#goals #habits #motivation`,
    whatsapp: '',
    facebook: '',
    copy: '',
    download: '',
  };
  const ht = hashtags[platform] || '';

  if (type==="habit") {
    if (platform==="linkedin")
      return `🔥 ${data.streak||0}-Day Streak on "${data.habitName}"\n\nConsistency is the foundation of every great achievement. ${data.streak} days in, and this habit has become part of who I am.\n\nWhat habit are you building?${tag}${ht}`;
    if (platform==="twitter")
      return `🔥 ${data.streak} day streak — "${data.habitName}" 💪\n\nShowing up every single day.${tag}${ht}`;
    if (platform==="instagram")
      return `🔥 ${data.streak} day streak on "${data.habitName}" 💪\n\nEvery day counts. Every rep counts. Keep showing up.${tag}${ht}`;
    return `🔥 ${data.streak||0} day streak on "${data.habitName}" — building consistent habits every single day!${tag}${ht}`;
  }
  if (type==="goal") {
    if (platform==="linkedin")
      return `🎯 Goal Update: "${data.title}" — ${data.pct||0}% Complete\n\nProgress isn't always fast, but it's always forward. Sharing this milestone to stay accountable.\n\nWhat goal are you working toward?${tag}${ht}`;
    if (platform==="twitter")
      return `🎯 ${data.pct||0}% done on: "${data.title}" 🚀\n\nStaying focused, one step at a time.${tag}${ht}`;
    return `🎯 ${data.pct||0}% progress on my goal: "${data.title}" — staying focused and making it happen!${tag}${ht}`;
  }
  if (type==="book") {
    const topNotes = (data.entries||[]).slice(0,3).map(e=>`• "${e.text}"`).join('\n');
    if (platform==="linkedin")
      return `📚 Currently Reading: "${data.title}"${data.author?` by ${data.author}`:''}\n\n${data.rating>0?`My rating: ${'⭐'.repeat(data.rating)}\n\n`:''}${topNotes?`Key takeaways so far:\n${topNotes}\n\n`:''}Every book is an investment in yourself. What are you reading?${tag}${ht}`;
    if (platform==="instagram")
      return `📚 "${data.title}"${data.author?` by ${data.author}`:''} ${data.rating>0?'⭐'.repeat(data.rating):''}\n\n${topNotes?`My notes:\n${topNotes}\n\n`:''}Drop your current read below 👇${tag}${ht}`;
    if (platform==="twitter")
      return `📚 Reading "${data.title}"${data.author?` by ${data.author}`:''} ${data.rating>0?'⭐'.repeat(data.rating):''}\n\n${topNotes?topNotes+'\n\n':''}${tag}${ht}`;
    return `📚 Reading "${data.title}"${data.author?` by ${data.author}`:''}${data.rating?` — ${'⭐'.repeat(data.rating)}`:''}${topNotes?`\n\n${topNotes}`:''}${tag}${ht}`;
  }
  if (type==="checkin") {
    return `✅ Completed ${data.count} daily check-ins — showing up every day!${tag}${ht}`;
  }
  return `I'm building better habits with Aspreo Life! 🚀${tag}`;
}

function ShareCardModal({ type, data, onClose }) {
  const [copied, setCopied] = useState(false);
  const [activePlatform, setActivePlatform] = useState('whatsapp');
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  const caption = buildCaption(activePlatform, type, data);

  // Load html2canvas once on mount
  useEffect(() => {
    if (!window.html2canvas) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      document.head.appendChild(s);
    }
  }, []);

  // Capture the card as a canvas → returns a Promise<{canvas, blob, file}>
  const captureCard = () => new Promise((resolve, reject) => {
    const card = cardRef.current;
    if (!card) { reject(new Error('Card not ready')); return; }
    const doCapture = () => {
      window.html2canvas(card, { scale:2, useCORS:true, backgroundColor:null })
        .then(canvas => {
          canvas.toBlob(blob => {
            if (!blob) { reject(new Error('Blob failed')); return; }
            const file = new File([blob], `aspreo-${type}.png`, { type:'image/png' });
            resolve({ canvas, blob, file });
          }, 'image/png');
        }).catch(reject);
    };
    if (window.html2canvas) doCapture();
    else {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      s.onload = doCapture;
      s.onerror = () => reject(new Error('html2canvas failed to load'));
      document.head.appendChild(s);
    }
  });

  const handleShare = async (platform) => {
    setActivePlatform(platform);
    const text = buildCaption(platform, type, data);
    const encodedText = encodeURIComponent(text);
    const encodedUrl  = encodeURIComponent(ASPREO_URL);

    if (platform === 'copy') {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
      return;
    }

    if (platform === 'download') {
      try {
        const { canvas } = await captureCard();
        const link = document.createElement('a');
        link.download = `aspreo-${type}-share.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch { navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),2500); }
      return;
    }

    // ── Native share (mobile/PWA) — shares card image + text + link in one tap ──
    if (platform === 'native') {
      if (!navigator.share) {
        // Fallback: copy text
        navigator.clipboard.writeText(`${text}\n${ASPREO_URL}`);
        setCopied(true); setTimeout(()=>setCopied(false),2500);
        return;
      }
      setSharing(true);
      try {
        const shareData = { title: 'My Aspreo Achievement', text, url: ASPREO_URL };
        // Try with image file first (supported on most mobile browsers)
        try {
          const { file } = await captureCard();
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'My Aspreo Achievement', text, url: ASPREO_URL });
          } else {
            await navigator.share(shareData);
          }
        } catch {
          // If file sharing fails, share text+url only
          await navigator.share(shareData);
        }
      } catch(e) { if (e.name !== 'AbortError') { navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),2500); } }
      setSharing(false);
      return;
    }

    // ── Instagram / Threads — copy + instruct ──
    if (platform === 'instagram' || platform === 'threads') {
      // Try to also download card for easy attach
      try { const {canvas}=await captureCard(); const l=document.createElement('a');l.download=`aspreo-${type}.png`;l.href=canvas.toDataURL('image/png');l.click(); } catch{}
      navigator.clipboard.writeText(text).catch(()=>{});
      setCopied(true); setTimeout(()=>setCopied(false),2500);
      return;
    }

    // ── URL-based sharing for desktop platforms ──
    const links = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedText}`,
      twitter:  `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + '\n' + ASPREO_URL)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    };
    if (links[platform]) window.open(links[platform], '_blank', 'width=600,height=500');
    navigator.clipboard.writeText(text).catch(()=>{});
    setCopied(true); setTimeout(()=>setCopied(false),2500);
  };

  const CardVisual = () => {
    const gradients = {
      habit:   "linear-gradient(135deg,#DC2626,#7F1D1D)",
      goal:    "linear-gradient(135deg,#16A34A,#064E3B)",
      book:    "linear-gradient(135deg,#1e3a8a,#312e81)",
      checkin: "linear-gradient(135deg,#7C3AED,#3B0764)",
    };
    const bg = gradients[type] || "linear-gradient(135deg,#4F46E5,#1E1B4B)";
    return (
      <div ref={cardRef} style={{background:bg, borderRadius:18, padding:"26px 24px 20px", color:"#fff", position:"relative", overflow:"hidden", fontFamily:FONT, boxShadow:"0 8px 40px rgba(0,0,0,.25)"}}>
        <div style={{position:"absolute",top:-25,right:-25,width:110,height:110,borderRadius:"50%",background:"rgba(255,255,255,.07)"}}/>
        <div style={{position:"absolute",bottom:-15,left:-15,width:70,height:70,borderRadius:"50%",background:"rgba(255,255,255,.05)"}}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
          <a href={ASPREO_URL} target="_blank" rel="noreferrer" style={{fontSize:10,opacity:.65,letterSpacing:".08em",fontWeight:700,color:"#fff",textDecoration:"none",background:"rgba(255,255,255,.15)",borderRadius:20,padding:"2px 9px"}}>ASPREO.COM</a>
          <div style={{fontSize:26}}>{type==="habit"?"🔥":type==="goal"?"🎯":type==="book"?"📚":"✅"}</div>
        </div>

        {type==="habit" && <>
          <div style={{fontSize:52,fontWeight:800,lineHeight:1}}>{data.streak||0}</div>
          <div style={{fontSize:13,opacity:.75,marginTop:2}}>day streak</div>
          <div style={{fontSize:17,fontWeight:700,marginTop:8,lineHeight:1.3}}>{data.habitName}</div>
          <div style={{fontSize:11,opacity:.6,marginTop:4}}>Consistent · Building · Growing</div>
        </>}

        {type==="goal" && <>
          <div style={{fontSize:52,fontWeight:800,lineHeight:1}}>{data.pct||0}%</div>
          <div style={{fontSize:13,opacity:.75,marginTop:2}}>progress achieved</div>
          <div style={{fontSize:17,fontWeight:700,marginTop:8,lineHeight:1.3}}>{data.title}</div>
          <div style={{marginTop:10,background:"rgba(255,255,255,.2)",borderRadius:99,height:7}}>
            <div style={{width:`${Math.min(data.pct||0,100)}%`,background:"#fff",height:"100%",borderRadius:99}}/>
          </div>
        </>}

        {type==="book" && (
          <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
            {data.cover_image
              ? <img src={data.cover_image} alt="cover" style={{width:72,height:100,objectFit:"cover",borderRadius:8,flexShrink:0,boxShadow:"0 4px 16px rgba(0,0,0,.4)"}}/>
              : <div style={{width:72,height:100,borderRadius:8,background:"rgba(255,255,255,.15)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>📖</div>
            }
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:16,fontWeight:800,lineHeight:1.3,marginBottom:4}}>{data.title}</div>
              {data.author&&<div style={{fontSize:11,opacity:.7,marginBottom:6}}>by {data.author}</div>}
              {data.rating>0&&<div style={{fontSize:14,marginBottom:8}}>{"⭐".repeat(data.rating)}</div>}
              {(data.entries||[]).slice(0,2).map((e,i)=>(
                <div key={i} style={{marginBottom:6,background:"rgba(255,255,255,.12)",borderRadius:7,padding:"6px 9px",fontSize:10,lineHeight:1.5,fontStyle:"italic"}}>"{e.text}"</div>
              ))}
            </div>
          </div>
        )}

        {type==="checkin" && <>
          <div style={{fontSize:52,fontWeight:800,lineHeight:1}}>{data.count||0}</div>
          <div style={{fontSize:13,opacity:.75,marginTop:2}}>check-ins today</div>
          <div style={{fontSize:12,opacity:.6,marginTop:8}}>Showing up every single day</div>
        </>}

        <div style={{marginTop:16,paddingTop:10,borderTop:"1px solid rgba(255,255,255,.15)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:9,opacity:.45}}>{new Date().toLocaleDateString()}</span>
          <a href={ASPREO_URL} target="_blank" rel="noreferrer" style={{fontSize:9,opacity:.55,color:"#fff",textDecoration:"none"}}>Track yours free →</a>
        </div>
      </div>
    );
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:9000,background:"rgba(0,0,0,.75)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(6px)"}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:20,padding:24,width:"100%",maxWidth:480,boxShadow:"0 24px 80px rgba(0,0,0,.4)",maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{margin:0,color:C.text,fontSize:15,fontWeight:700}}>📤 Share Achievement</h3>
          <button onClick={onClose} style={{...btn("#999"),padding:"4px 8px",fontSize:13}}>✕</button>
        </div>

        {/* Card preview */}
        <div style={{marginBottom:14}}><CardVisual/></div>

        {/* Platform tabs */}
        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
          {[{id:"linkedin",l:"💼",c:"#0A66C2"},{id:"twitter",l:"🐦",c:"#1DA1F2"},{id:"instagram",l:"📸",c:"#E1306C"},{id:"threads",l:"🧵",c:"#000"},{id:"whatsapp",l:"💬",c:"#25D366"},{id:"facebook",l:"📘",c:"#1877F2"}].map(p=>(
            <button key={p.id} onClick={()=>setActivePlatform(p.id)}
              style={{padding:"5px 10px",borderRadius:20,border:`2px solid ${activePlatform===p.id?p.c:'#e2e8f0'}`,background:activePlatform===p.id?p.c+'18':'#f8fafc',color:activePlatform===p.id?p.c:'#64748b',cursor:"pointer",fontSize:11,fontWeight:activePlatform===p.id?700:400,transition:"all .15s"}}>
              {p.l}
            </button>
          ))}
        </div>

        {/* Caption preview for selected platform */}
        <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:11,color:"#374151",lineHeight:1.7,maxHeight:120,overflowY:"auto",whiteSpace:"pre-wrap"}}>{caption}</div>

        {/* Native share button — shown on mobile/PWA where navigator.share is available */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={()=>handleShare('native')}
            disabled={sharing}
            style={{...btn('#2b1dc9'),width:'100%',padding:'11px',borderRadius:10,fontSize:13,fontWeight:700,marginBottom:10,display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:sharing?0.7:1}}>
            {sharing ? '⏳ Opening...' : '🚀 Share Now (Card + Text + Link)'}
          </button>
        )}

        {/* Platform buttons */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:7,marginBottom:10}}>
          {[
            {id:'whatsapp', label:'💬 WhatsApp', color:'#25D366'},
            {id:'twitter',  label:'🐦 X / Twitter', color:'#1DA1F2'},
            {id:'linkedin', label:'💼 LinkedIn', color:'#0A66C2'},
            {id:'telegram', label:'✈️ Telegram', color:'#229ED9'},
            {id:'facebook', label:'📘 Facebook', color:'#1877F2'},
            {id:'instagram',label:'📸 Instagram', color:'#E1306C'},
          ].map(p=>(
            <button key={p.id} onClick={()=>handleShare(p.id)}
              style={{...btn(p.color),fontSize:10,padding:'7px 4px',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center'}}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Utility buttons */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:7}}>
          <button onClick={()=>handleShare('copy')} style={{...btn('#6B7280'),fontSize:11,padding:'9px',borderRadius:8,fontWeight:600}}>📋 Copy Caption</button>
          <button onClick={()=>handleShare('download')} style={{...btn('#374151'),fontSize:11,padding:'9px',borderRadius:8,fontWeight:600}}>⬇ Save Card</button>
        </div>

        {copied && <div style={{marginTop:10,textAlign:'center',fontSize:12,color:C.green,fontWeight:600,background:'#f0fdf4',borderRadius:8,padding:'8px'}}>✅ Caption copied to clipboard!</div>}

        {/* Instagram/Threads tip */}
        {(activePlatform==='instagram'||activePlatform==='threads') && (
          <div style={{marginTop:10,background:'#fef3c7',borderRadius:8,padding:'8px 12px',fontSize:11,color:'#92400e',lineHeight:1.6}}>
            💡 Card saved + caption copied! Open Instagram → create post → attach the saved image → paste caption.
          </div>
        )}
      </div>
    </div>
  );
}

function ShareButton({ type, data, style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={e=>{e.stopPropagation();setOpen(true);}} style={{...btn("#6B7280"),fontSize:11,padding:"4px 9px",...style}}>📤 Share</button>
      {open && ReactDOM.createPortal(
        <ShareCardModal type={type} data={data} onClose={()=>setOpen(false)}/>,
        document.body
      )}
    </>
  );
}
const BIZ_COLOR  = '#0D9488'; // teal
const BIZ_LIGHT  = '#F0FDFA';

const LEAD_STATUSES = [
  { id:'new',       label:'🆕 New',        color:'#6B7280' },
  { id:'contacted', label:'📞 Contacted',  color:'#2563EB' },
  { id:'qualified', label:'✅ Qualified',  color:'#D97706' },
  { id:'proposal',  label:'📄 Proposal',   color:'#7C3AED' },
  { id:'converted', label:'🎉 Converted',  color:'#16A34A' },
  { id:'lost',      label:'❌ Lost',       color:'#DC2626' },
];
const INV_STATUSES = [
  { id:'draft',     label:'✏️ Draft',      color:'#6B7280' },
  { id:'sent',      label:'📤 Sent',       color:'#2563EB' },
  { id:'paid',      label:'✅ Paid',       color:'#16A34A' },
  { id:'overdue',   label:'⚠️ Overdue',    color:'#DC2626' },
  { id:'cancelled', label:'❌ Cancelled',  color:'#9CA3AF' },
];
const PROP_STATUSES = [
  { id:'draft',    label:'✏️ Draft',    color:'#6B7280' },
  { id:'sent',     label:'📤 Sent',     color:'#2563EB' },
  { id:'accepted', label:'✅ Accepted', color:'#16A34A' },
  { id:'rejected', label:'❌ Rejected', color:'#DC2626' },
  { id:'expired',  label:'⏰ Expired',  color:'#9CA3AF' },
];

function BusinessModule({ ctx }) {
  const { leads=[], clients=[], proposals=[], invoices=[], sheetLinks=[], userPlan, workspaceMembers=[], uid, profile } = ctx;
  const isAgencyPlan = profile?.role === 'admin' || userPlan?.plan_id === 'agency';

  // Check if this user is a workspace MEMBER (not owner)
  // Match by member_id (set after accept) OR by presence in combined array from email lookup
  const myMembership = workspaceMembers.find(m =>
    m.status === 'active' &&
    m.owner_id !== uid &&
    (m.member_id === uid || m.member_id === null)  // null = accepted but member_id not yet set
  );

  // If member: they see the OWNER's data (owner_id), not their own
  const effectiveUid = myMembership ? myMembership.owner_id : uid;

  // Filter the data to show only the owner's records for workspace members
  const filteredLeads     = myMembership ? leads.filter(l => l.user_id === effectiveUid)     : leads;
  const filteredClients   = myMembership ? clients.filter(c => c.user_id === effectiveUid)   : clients;
  const filteredInvoices  = myMembership ? invoices.filter(i => i.user_id === effectiveUid)  : invoices;
  const filteredProposals = myMembership ? proposals.filter(p => p.user_id === effectiveUid) : proposals;

  const allowedTabs = myMembership
    ? (typeof myMembership.access_modules === 'string' ? JSON.parse(myMembership.access_modules) : (myMembership.access_modules || ['crm']))
    : null; // null = no restriction (owner)

  const ALL_TABS = [
    { id:'setup',        label:'⚙️ Setup'            },
    { id:'services',     label:'📦 Services'         },
    { id:'crm',          label:'🏆 Leads / CRM'     },
    { id:'clients',      label:'👤 Clients'          },
    { id:'invoices',     label:'🧾 Invoices'         },
    { id:'proposals',    label:'📄 Proposals'        },
    { id:'consultants',  label:'🧑‍💼 Consultants'      },
    { id:'biz_expenses', label:'💰 Expenses'         },
    { id:'whatsapp',     label:'💬 WhatsApp'         },
    { id:'promotion',    label:'📣 Promotion'        },
    { id:'sheets',       label:'📊 Google Sheets'    },
    ...(isAgencyPlan && !myMembership ? [{ id:'workspace', label:'🏢 Team Workspace' }] : []),
  ];

  // Filter tabs based on membership access (or show all if owner)
  const TABS = allowedTabs ? ALL_TABS.filter(t => allowedTabs.includes(t.id)) : ALL_TABS;

  const [tab, setTab] = useState(TABS[0]?.id || 'crm');

  // Summary numbers
  const totalRevenue = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(parseFloat(i.total)||0),0);
  const pendingRev   = invoices.filter(i=>i.status==='sent'||i.status==='overdue').reduce((s,i)=>s+(parseFloat(i.total)||0),0);
  const activeLeads  = leads.filter(l=>!['converted','lost'].includes(l.status)).length;

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      <SectionMasthead eyebrow="System · CRM · Clients · Invoices · Proposals" title={<>Run the <em>business</em>.</>} />

      {/* KPI Cards */}
      <div className="ta-grid-4" style={{marginBottom:20}}>
        {[
          { label:'Active Leads',     value:activeLeads,                                        color:'var(--ta-primary)', icon:'🎯' },
          { label:'Total Clients',    value:clients.filter(c=>c.status==='active').length,        color:'var(--ta-primary)', icon:'👤' },
          { label:'Revenue (Paid)',   value:`₹${totalRevenue.toLocaleString('en-IN')}`,           color:'var(--ta-positive)', icon:'💰' },
          { label:'Pending Revenue',  value:`₹${pendingRev.toLocaleString('en-IN')}`,             color:'var(--ta-notice)', icon:'⏳' },
        ].map(k=>(
          <div key={k.label} className="ta-card">
            <div className="ta-section-head-meta" style={{marginBottom:8}}>{k.label}</div>
            <div className="ta-serif tnum" style={{fontSize:26,fontWeight:400,color:k.color}}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',padding:4,marginBottom:16,overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            background:tab===t.id?BIZ_COLOR:'transparent',
            color:tab===t.id?'#fff':C.muted,
            border:'none',borderRadius:7,padding:'6px 14px',cursor:'pointer',
            fontFamily:FONT,fontSize:12,fontWeight:tab===t.id?'bold':'normal',
            whiteSpace:'nowrap'
          }}>{t.label}</button>
        ))}
      </div>

      {(() => {
        const bizCtx = myMembership ? {
          ...ctx,
          uid:       effectiveUid,
          leads:     filteredLeads,
          clients:   filteredClients,
          invoices:  filteredInvoices,
          proposals: filteredProposals,
          // Wrap write ops to use owner's uid (effectiveUid)
          addLead:    (lead)     => ctx.addLead(lead, effectiveUid),
          addClient:  (client)   => ctx.addClient({...client, user_id: effectiveUid}),
          updateBizProfile: ()=>{}, // members cannot change owner's settings
        } : ctx;
        return <>
          {tab==='setup'      && <BusinessSetupTab ctx={ctx}    />}
          {tab==='services'   && <ServicesTab      ctx={bizCtx} />}
          {tab==='crm'        && <LeadsTab         ctx={bizCtx} />}
          {tab==='clients'    && <ClientsTab       ctx={bizCtx} />}
          {tab==='invoices'   && <InvoicesTab      ctx={bizCtx} />}
          {tab==='proposals'  && <ProposalsTab     ctx={bizCtx} />}
          {tab==='consultants'&& <ConsultantsTab   ctx={bizCtx} />}
          {tab==='whatsapp'   && <WhatsAppTab      ctx={bizCtx} />}
          {tab==='promotion'  && <PromotionTab     ctx={bizCtx} />}
          {tab==='sheets'     && <SheetsTab        ctx={bizCtx} />}
          {tab==='workspace'  && <WorkspaceTab     ctx={ctx}    />}
          {tab==='biz_expenses' && <BusinessExpenseTab ctx={ctx} />}
          {myMembership && (
            <div style={{position:'fixed',bottom:16,right:16,background:'#2B1DC9',color:'#fff',borderRadius:10,padding:'8px 16px',fontSize:12,fontWeight:600,boxShadow:'0 4px 16px rgba(43,29,201,.4)',zIndex:9999,display:'flex',alignItems:'center',gap:8}}>
              <span>🏢</span>
              <span>Team member · <strong style={{textTransform:'capitalize'}}>{myMembership.role}</strong></span>
              <button onClick={async()=>{
                // Reload owner's leads/clients fresh from DB
                const ownerUid = myMembership.owner_id;
                const [ld,cl,inv,pr] = await Promise.all([
                  supabase.from('leads').select('*').eq('user_id',ownerUid),
                  supabase.from('clients').select('*').eq('user_id',ownerUid),
                  supabase.from('invoices').select('*').eq('user_id',ownerUid),
                  supabase.from('proposals').select('*').eq('user_id',ownerUid),
                ]);
                // Replace owner's records in state completely
                ctx.setLeads?.(p=>{const mine=p.filter(x=>x.user_id!==ownerUid);return[...mine,...(ld.data||[])];});
                ctx.setClients?.(p=>{const mine=p.filter(x=>x.user_id!==ownerUid);return[...mine,...(cl.data||[])];});
                ctx.setInvoices?.(p=>{const mine=p.filter(x=>x.user_id!==ownerUid);return[...mine,...(inv.data||[])];});
                ctx.setProposals?.(p=>{const mine=p.filter(x=>x.user_id!==ownerUid);return[...mine,...(pr.data||[])];});
              }} style={{background:'rgba(255,255,255,.2)',border:'none',color:'#fff',borderRadius:6,padding:'3px 8px',cursor:'pointer',fontSize:11,fontWeight:700,fontFamily:FONT}}>
                ↻ Refresh
              </button>
            </div>
          )}
        </>;
      })()}
    </div>
  );
}

// ── LEADS / CRM TAB ───────────────────────────────────────────────────────────
function LeadsTab({ ctx }) {
  const { leads=[], addLead, updateLead, deleteLead, convertLeadToClient } = ctx;
  const [showForm,    setShowForm]    = useState(false);
  const [editLead,    setEditLead]    = useState(null);
  const [filterStatus,setFilterStatus]= useState('all');
  const [activeTab,   setActiveTab]   = useState('leads'); // 'leads' | 'campaigns'
  const [campaigns,   setCampaigns]   = useState(() => {
    try { return JSON.parse(localStorage.getItem(`aspreo_campaigns_${ctx.uid}`)||'[]'); } catch{ return []; }
  });
  const [showCampForm,setShowCampForm]= useState(false);
  const [campForm,    setCampForm]    = useState({name:'',source:'Facebook Lead Ad',sheet_url:'',notes:''});
  const saveCampaign = () => {
    if(!campForm.name.trim()) return;
    const newCamp = { ...campForm, id: Date.now(), created_at: new Date().toISOString(),
      webhook_key: ctx.uid?.slice(0,8)+'-'+Math.random().toString(36).slice(2,8) };
    const updated = [...campaigns, newCamp];
    setCampaigns(updated);
    localStorage.setItem(`aspreo_campaigns_${ctx.uid}`, JSON.stringify(updated));
    setCampForm({name:'',source:'Facebook Lead Ad',sheet_url:'',notes:''});
    setShowCampForm(false);
  };
  const deleteCampaign = (id) => {
    const updated = campaigns.filter(c=>c.id!==id);
    setCampaigns(updated);
    localStorage.setItem(`aspreo_campaigns_${ctx.uid}`, JSON.stringify(updated));
  };

  const blank = { name:'', email:'', phone:'', company:'', source:'', status:'new', value:'', notes:'', next_follow_up:'' };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.name.trim()) return;
    const payload = { ...form, value: parseFloat(form.value)||0 };
    if (editLead) { updateLead(editLead.id, payload); }
    else { await addLead(payload); }
    setForm(blank); setShowForm(false); setEditLead(null);
  };

  const openEdit = (l) => { setEditLead(l); setForm({...l,value:l.value||''}); setShowForm(true); };

  const filtered = filterStatus==='all' ? leads : leads.filter(l=>l.status===filterStatus);
  const totalValue = filtered.reduce((s,l)=>s+(parseFloat(l.value)||0),0);

  const CAMP_SOURCES = ['Facebook Lead Ad','Instagram Ad','LinkedIn Lead Gen','Google Ads','WhatsApp','Referral','Website','Manual','Other'];

  if (activeTab === 'campaigns') return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>setActiveTab('leads')} style={{...btn('#888'),fontSize:12}}>← All Leads</button>
          <span style={{fontWeight:800,fontSize:16,color:'#1D5F8A',alignSelf:'center'}}>📢 Lead Campaigns</span>
        </div>
        <button onClick={()=>setShowCampForm(v=>!v)} style={btn('#1D5F8A')}>+ New Campaign</button>
      </div>
      {showCampForm&&(
        <div style={{background:'var(--surface)',border:'1.5px solid #1D5F8A',borderRadius:12,padding:16,marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:14,color:'#1D5F8A',marginBottom:12}}>New Lead Campaign</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <div style={{gridColumn:'1/-1'}}><label style={lbl}>Campaign Name *</label><input value={campForm.name} onChange={e=>setCampForm(p=>({...p,name:e.target.value}))} placeholder="e.g. April Facebook Leads" style={{...inp(),width:'100%'}} autoFocus/></div>
            <div><label style={lbl}>Source</label>
              <select value={campForm.source} onChange={e=>setCampForm(p=>({...p,source:e.target.value}))} style={{...inp(),width:'100%'}}>
                {CAMP_SOURCES.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Google Sheet URL (optional)</label><input value={campForm.sheet_url} onChange={e=>setCampForm(p=>({...p,sheet_url:e.target.value}))} placeholder="https://docs.google.com/..." style={{...inp(),width:'100%'}}/></div>
            <div style={{gridColumn:'1/-1'}}><label style={lbl}>Notes</label><textarea value={campForm.notes} onChange={e=>setCampForm(p=>({...p,notes:e.target.value}))} style={{...ta(60),width:'100%'}} placeholder="Campaign details, budget, targeting..."/></div>
          </div>
          <div style={{display:'flex',gap:6}}>
            <button onClick={saveCampaign} style={btn('#1D5F8A')}>💾 Save Campaign</button>
            <button onClick={()=>setShowCampForm(false)} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}
      {campaigns.length===0&&<div style={{textAlign:'center',color:'var(--muted)',padding:40}}>No campaigns yet.<br/>Create campaigns to manage separate Facebook/LinkedIn/Instagram lead flows.</div>}
      {campaigns.map(c=>{
        const campLeads = leads.filter(l=>(l.source||'')===c.source || (l.notes||'').includes(c.name));
        const webhookUrl = `https://www.aspreo.com/api/webhooks/zapier-lead`;
        return (
          <div key={c.id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
              <div>
                <div style={{fontWeight:700,fontSize:15,color:'var(--text)'}}>{c.name}</div>
                <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>📢 {c.source} · {campLeads.length} leads · Created {new Date(c.created_at).toLocaleDateString('en-IN')}</div>
              </div>
              <button onClick={()=>{if(window.confirm('Delete campaign?'))deleteCampaign(c.id);}} style={{...btn('#DC2626'),fontSize:11,padding:'4px 8px'}}>🗑</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
              <div style={{background:'#EFF6FF',borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,fontWeight:700,color:'#1D5F8A',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>🔗 Zapier/Make Webhook URL</div>
                <div style={{fontSize:11,wordBreak:'break-all',color:'#1D4ED8',marginBottom:6,fontFamily:'monospace'}}>{webhookUrl}</div>
                <div style={{fontSize:10,color:'#64748B',marginBottom:6}}>Header: <code>x-aspreo-user-key: {c.webhook_key}</code></div>
                <button onClick={()=>{navigator.clipboard.writeText(webhookUrl);alert('Webhook URL copied!');}} style={{...btn('#1D5F8A'),fontSize:10,padding:'3px 8px'}}>📋 Copy URL</button>
                <button onClick={()=>{navigator.clipboard.writeText(c.webhook_key);alert('Key copied!');}} style={{...btn('#64748B'),fontSize:10,padding:'3px 8px',marginLeft:4}}>📋 Copy Key</button>
              </div>
              {c.sheet_url&&(
                <div style={{background:'#F0FDF4',borderRadius:8,padding:'10px 12px'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#16A34A',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>📊 Google Sheet</div>
                  <a href={c.sheet_url} target="_blank" rel="noreferrer" style={{fontSize:12,color:'#16A34A',wordBreak:'break-all'}}>Open Sheet →</a>
                  <div style={{fontSize:10,color:'#64748B',marginTop:4}}>Connect in Business → Sheets tab to auto-import leads</div>
                </div>
              )}
            </div>
            {campLeads.length>0&&(
              <div style={{borderTop:'1px solid var(--border)',paddingTop:10,marginTop:4}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',marginBottom:6}}>Recent Leads from this Campaign</div>
                {campLeads.slice(0,3).map(l=>(
                  <div key={l.id} style={{display:'flex',gap:8,alignItems:'center',padding:'4px 0',fontSize:12}}>
                    <span style={{fontWeight:600,color:'var(--text)'}}>{l.name}</span>
                    <span style={{color:'var(--muted)'}}>{l.email}</span>
                    <span style={{marginLeft:'auto',fontSize:10,padding:'1px 6px',borderRadius:8,background:'#DCFCE7',color:'#16A34A'}}>{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
          {['all',...LEAD_STATUSES.map(s=>s.id)].map(s=>(
            <button key={s} onClick={()=>setFilterStatus(s)} style={{
              fontSize:10,padding:'3px 10px',borderRadius:20,cursor:'pointer',border:'none',
              background:filterStatus===s?BIZ_COLOR:'#e5e7eb',
              color:filterStatus===s?'#fff':C.muted,fontWeight:filterStatus===s?700:400
            }}>{s==='all'?`All (${leads.length})`:(LEAD_STATUSES.find(x=>x.id===s)?.label||s)+` (${leads.filter(l=>l.status===s).length})`}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={()=>setActiveTab('campaigns')} style={{...btn('#64748B'),fontSize:12}}>📢 Campaigns</button>
          <button onClick={()=>{setEditLead(null);setForm(blank);setShowForm(true);}} style={btn(BIZ_COLOR)}>+ Add Lead</button>
        </div>
      </div>

      {/* Pipeline Value */}
      {filtered.length > 0 && (
        <div style={{background:BIZ_LIGHT,border:`1px solid ${BIZ_COLOR}30`,borderRadius:8,padding:'8px 14px',marginBottom:12,fontSize:12,color:BIZ_COLOR,fontWeight:600}}>
          💰 Pipeline value: ₹{totalValue.toLocaleString('en-IN')}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={{background:C.surface,border:`1.5px solid ${BIZ_COLOR}`,borderRadius:12,padding:16,marginBottom:14}}>
          <h4 style={{margin:'0 0 12px',color:BIZ_COLOR}}>{editLead?'Edit Lead':'New Lead'}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.name}    onChange={e=>upd('name',e.target.value)}    placeholder="Full Name *"    style={{...inp(),width:'100%'}}/>
            <input value={form.email}   onChange={e=>upd('email',e.target.value)}   placeholder="Email"          style={{...inp(),width:'100%'}}/>
            <input value={form.phone}   onChange={e=>upd('phone',e.target.value)}   placeholder="Phone"          style={{...inp(),width:'100%'}}/>
            <input value={form.company} onChange={e=>upd('company',e.target.value)} placeholder="Company"        style={{...inp(),width:'100%'}}/>
            <input value={form.value}   onChange={e=>upd('value',e.target.value)}   placeholder="Deal Value (₹)" style={{...inp(),width:'100%'}} type="number"/>
            <select value={form.source} onChange={e=>upd('source',e.target.value)} style={{...inp(),width:'100%'}}>
              <option value="">Source</option>
              {['Instagram','LinkedIn','Referral','Cold Outreach','Website','Other'].map(s=><option key={s} value={s.toLowerCase()}>{s}</option>)}
            </select>
            <select value={form.status} onChange={e=>upd('status',e.target.value)} style={{...inp(),width:'100%'}}>
              {LEAD_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <input value={form.next_follow_up} onChange={e=>upd('next_follow_up',e.target.value)} type="date" style={{...inp(),width:'100%'}}/>
          </div>
          <textarea value={form.notes} onChange={e=>upd('notes',e.target.value)} placeholder="Notes..." style={{...ta(60),width:'100%',marginBottom:8}}/>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(BIZ_COLOR)}>💾 {editLead?'Update':'Save'}</button>
            <button onClick={()=>{setShowForm(false);setEditLead(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(6,minmax(160px,1fr))',gap:8,overflowX:'auto',minWidth:700}}>
        {LEAD_STATUSES.filter(st=>filterStatus==='all'||filterStatus===st.id).map(st=>{
          const stLeads = leads.filter(l=>l.status===st.id);
          return (
            <div key={st.id} style={{background:C.surfaceAlt,borderRadius:10,overflow:'hidden',minHeight:200}}>
              <div style={{background:st.color,color:'#fff',padding:'8px 12px',fontSize:11,fontWeight:700,display:'flex',justifyContent:'space-between'}}>
                <span>{st.label}</span>
                <span style={{background:'rgba(255,255,255,.25)',borderRadius:10,padding:'0 6px'}}>{stLeads.length}</span>
              </div>
              <div style={{padding:6,display:'flex',flexDirection:'column',gap:5}}>
                {stLeads.map(l=>(
                  <div key={l.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px'}}>
                    <div style={{fontWeight:600,fontSize:12,color:C.text,marginBottom:2}}>{l.name}</div>
                    {l.company&&<div style={{fontSize:10,color:C.muted}}>{l.company}</div>}
                    {l.value>0&&<div style={{fontSize:10,color:'#16A34A',fontWeight:600,marginTop:2}}>₹{parseFloat(l.value).toLocaleString('en-IN')}</div>}
                    {l.next_follow_up&&<div style={{fontSize:9,color:'#D97706',marginTop:2}}>📅 Follow up: {l.next_follow_up}</div>}
                    <div style={{display:'flex',gap:4,marginTop:6,flexWrap:'wrap'}}>
                      {LEAD_STATUSES.filter(ns=>ns.id!==st.id).map(ns=>(
                        <button key={ns.id} onClick={()=>updateLead(l.id,{status:ns.id})}
                          style={{background:ns.color,border:'none',color:'#fff',borderRadius:4,padding:'1px 5px',fontSize:8,cursor:'pointer'}}>
                          →{ns.label.split(' ')[1]||ns.id}
                        </button>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:4,marginTop:4}}>
                      <button onClick={()=>openEdit(l)} style={{background:'none',border:'none',cursor:'pointer',fontSize:11,color:C.amber}}>✏️</button>
                      {st.id!=='converted'&&(
                        <button onClick={()=>{if(window.confirm(`Convert "${l.name}" to client?`))convertLeadToClient(l.id);}}
                          style={{background:'none',border:'none',cursor:'pointer',fontSize:10,color:'#16A34A'}}>🎉 Convert</button>
                      )}
                      <button onClick={()=>{if(window.confirm('Delete this lead?'))deleteLead(l.id);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:11,color:C.light,marginLeft:'auto'}}>✕</button>
                    </div>
                  </div>
                ))}
                {stLeads.length===0&&<div style={{textAlign:'center',color:C.light,fontSize:10,padding:16,fontStyle:'italic'}}>Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── CLIENTS TAB ───────────────────────────────────────────────────────────────
function ClientsTab({ ctx }) {
  const { clients=[], invoices=[], addClient, updateClient, deleteClient } = ctx;
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [selected, setSelected] = useState(null);
  const blank = { name:'', email:'', phone:'', company:'', address:'', gstin:'', notes:'' };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.name.trim()) return;
    if (editClient) updateClient(editClient.id, form);
    else await addClient(form);
    setForm(blank); setShowForm(false); setEditClient(null);
  };

  const activeClients = clients.filter(c=>c.status==='active');
  const selClient = clients.find(c=>c.id===selected);
  const clientInvoices = invoices.filter(i=>i.client_id===selected);
  const clientRevenue = clientInvoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(parseFloat(i.total)||0),0);

  return (
    <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:14}}>
      {/* Client List */}
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:700,color:C.text}}>Clients ({activeClients.length})</span>
          <button onClick={()=>{setEditClient(null);setForm(blank);setShowForm(true);}} style={{...btn(BIZ_COLOR),padding:'5px 10px',fontSize:12}}>+ Add</button>
        </div>
        {showForm&&(
          <div style={{background:C.surface,border:`1.5px solid ${BIZ_COLOR}`,borderRadius:10,padding:12,marginBottom:10}}>
            <input value={form.name}    onChange={e=>upd('name',e.target.value)}    placeholder="Name *"    style={{...inp(),width:'100%',marginBottom:6}}/>
            <input value={form.email}   onChange={e=>upd('email',e.target.value)}   placeholder="Email"     style={{...inp(),width:'100%',marginBottom:6}}/>
            <input value={form.phone}   onChange={e=>upd('phone',e.target.value)}   placeholder="Phone"     style={{...inp(),width:'100%',marginBottom:6}}/>
            <input value={form.company} onChange={e=>upd('company',e.target.value)} placeholder="Company"   style={{...inp(),width:'100%',marginBottom:6}}/>
            <input value={form.gstin}   onChange={e=>upd('gstin',e.target.value)}   placeholder="GSTIN"     style={{...inp(),width:'100%',marginBottom:6}}/>
            <input value={form.address} onChange={e=>upd('address',e.target.value)} placeholder="Address"   style={{...inp(),width:'100%',marginBottom:8}}/>
            <div style={{display:'flex',gap:6}}>
              <button onClick={save} style={{...btn(BIZ_COLOR),flex:1,fontSize:12}}>💾 {editClient?'Update':'Save'}</button>
              <button onClick={()=>{setShowForm(false);setEditClient(null);setForm(blank);}} style={{...btn('#999'),fontSize:12}}>✕</button>
            </div>
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {clients.length===0&&<div style={{textAlign:'center',color:C.light,padding:24,fontSize:13}}>No clients yet.<br/>Convert a lead or add one.</div>}
          {clients.map(c=>(
            <div key={c.id} onClick={()=>setSelected(c.id===selected?null:c.id)}
              style={{background:c.id===selected?BIZ_LIGHT:C.surface,border:`1px solid ${c.id===selected?BIZ_COLOR:C.border}`,borderRadius:10,padding:'10px 12px',cursor:'pointer',transition:'all .15s'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:C.text}}>{c.name}</div>
                  {c.company&&<div style={{fontSize:11,color:C.muted}}>{c.company}</div>}
                  {c.email&&<div style={{fontSize:10,color:C.muted}}>{c.email}</div>}
                </div>
                <span style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:c.status==='active'?'#DCFCE7':'#F3F4F6',color:c.status==='active'?'#16A34A':'#6B7280'}}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Detail */}
      <div>
        {!selClient ? (
          <div style={{textAlign:'center',padding:60,color:C.light}}>
            <div style={{fontSize:48,marginBottom:12}}>👤</div>
            <div>Select a client to view details</div>
          </div>
        ) : (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
              <div>
                <h3 style={{margin:0,color:BIZ_COLOR,fontSize:18}}>{selClient.name}</h3>
                {selClient.company&&<div style={{color:C.muted,fontSize:13}}>{selClient.company}</div>}
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>{setEditClient(selClient);setForm({...selClient});setShowForm(true);}} style={{...btn(C.amber),fontSize:12,padding:'5px 10px'}}>✏️ Edit</button>
                <button onClick={()=>{if(window.confirm('Delete client?')){deleteClient(selClient.id);setSelected(null);}}} style={{...btn(C.red),fontSize:12,padding:'5px 10px'}}>🗑 Delete</button>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              {[
                ['📧 Email', selClient.email],
                ['📱 Phone', selClient.phone],
                ['📍 Address', selClient.address],
                ['🏛 GSTIN', selClient.gstin],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l} style={{background:C.surfaceAlt,borderRadius:8,padding:'8px 12px'}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:2}}>{l}</div>
                  <div style={{fontSize:13,color:C.text}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:16}}>
              <div style={{background:'#F0FDF4',borderRadius:8,padding:'10px',textAlign:'center'}}>
                <div style={{fontSize:18,fontWeight:800,color:'#16A34A'}}>₹{clientRevenue.toLocaleString('en-IN')}</div>
                <div style={{fontSize:10,color:C.muted}}>Total Paid</div>
              </div>
              <div style={{background:BIZ_LIGHT,borderRadius:8,padding:'10px',textAlign:'center'}}>
                <div style={{fontSize:18,fontWeight:800,color:BIZ_COLOR}}>{clientInvoices.length}</div>
                <div style={{fontSize:10,color:C.muted}}>Invoices</div>
              </div>
              <div style={{background:'#FFFBEB',borderRadius:8,padding:'10px',textAlign:'center'}}>
                <div style={{fontSize:18,fontWeight:800,color:'#D97706'}}>{clientInvoices.filter(i=>i.status==='sent'||i.status==='overdue').length}</div>
                <div style={{fontSize:10,color:C.muted}}>Pending</div>
              </div>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>Recent Invoices</div>
              {clientInvoices.slice(0,5).map(inv=>(
                <div key={inv.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:`1px solid ${C.border}`}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600}}>{inv.invoice_number}</div>
                    <div style={{fontSize:10,color:C.muted}}>{inv.due_date}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:12,fontWeight:700}}>₹{parseFloat(inv.total||0).toLocaleString('en-IN')}</div>
                    <span style={{fontSize:9,padding:'1px 6px',borderRadius:10,background:INV_STATUSES.find(s=>s.id===inv.status)?.color+'22',color:INV_STATUSES.find(s=>s.id===inv.status)?.color}}>
                      {INV_STATUSES.find(s=>s.id===inv.status)?.label||inv.status}
                    </span>
                  </div>
                </div>
              ))}
              {clientInvoices.length===0&&<div style={{color:C.light,fontSize:12,textAlign:'center',padding:12}}>No invoices yet</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONSULTANT MANAGEMENT — add/search consultants, upload resume + photo,
// export a clean client-facing profile.
// ══════════════════════════════════════════════════════════════════════════════
const CONSULTANT_STATUSES = [
  { id:'active',   label:'Active',   color:'#16A34A' },
  { id:'bench',    label:'Bench',    color:'#D97706' },
  { id:'placed',   label:'Placed',   color:'#2563EB' },
  { id:'inactive', label:'Inactive', color:'#6B7280' },
];

function ConsultantsTab({ ctx }) {
  const C = useC();
  const { consultants=[], addConsultant, updateConsultant, deleteConsultant, uid, bizProfile={}, userPlan } = ctx;
  const [view, setView] = React.useState('list');
  const [editCons, setEditCons] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [minExp, setMinExp] = React.useState('');
  const [exportCons, setExportCons] = React.useState(null);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [uploadingResume, setUploadingResume] = React.useState(false);

  const blank = {
    first_name:'', last_name:'', mobile:'', email:'', title:'',
    skills:[], certifications:[], experience_years:'', about:'',
    resume_url:'', resume_filename:'', photo_url:'', status:'active',
  };
  const [form, setForm] = React.useState({...blank});
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const openNew = () => { setEditCons(null); setForm({...blank, _tmpId:Date.now()}); setView('form'); };
  const openEdit = (c) => { setEditCons(c); setForm({...blank, ...c, skills:c.skills||[], certifications:c.certifications||[]}); setView('form'); };

  const save = async () => {
    if (!form.first_name.trim()) return;
    const { _tmpId, ...payload } = form;
    if (editCons) updateConsultant(editCons.id, payload);
    else await addConsultant(payload);
    setView('list'); setForm({...blank});
  };

  const uploadFile = async (file, kind) => {
    const setBusy = kind==='photo' ? setUploadingPhoto : setUploadingResume;
    setBusy(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${uid}/${editCons?.id || form._tmpId}-${kind}.${ext}`;
      const { data, error } = await supabase.storage.from('consultant-files').upload(path, file, { upsert:true, contentType:file.type });
      if (!error) {
        const { data: pub } = supabase.storage.from('consultant-files').getPublicUrl(data.path);
        if (kind==='photo') upd('photo_url', pub.publicUrl);
        else { upd('resume_url', pub.publicUrl); upd('resume_filename', file.name); }
      } else {
        alert(`${kind==='photo'?'Photo':'Resume'} upload failed: ` + error.message);
      }
    } catch(e) { console.warn(`${kind} upload:`, e); alert(`${kind==='photo'?'Photo':'Resume'} upload failed: ` + e.message); }
    setBusy(false);
  };

  // ── Search: name, title, skills, certifications; optional min-experience filter ──
  const filtered = consultants.filter(c => {
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [c.first_name, c.last_name, c.title, ...(c.skills||[]), ...(c.certifications||[])].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (minExp && (parseFloat(c.experience_years)||0) < parseFloat(minExp)) return false;
    return true;
  });

  const selCons = consultants.find(c=>c.id===selected);

  if (view==='form') {
    return <ConsultantEditor form={form} upd={upd} editCons={editCons} onSave={save}
      onCancel={()=>{setView('list');setForm({...blank});}}
      uploadFile={uploadFile} uploadingPhoto={uploadingPhoto} uploadingResume={uploadingResume} C={C}/>;
  }

  return (
    <div style={{display:'grid',gridTemplateColumns:'320px 1fr',gap:14}}>
      {/* ── List + search ── */}
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:700,color:C.text}}>Consultants ({consultants.length})</span>
          <button onClick={openNew} style={{...btn(BIZ_COLOR),padding:'5px 10px',fontSize:12}}>+ Add</button>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔎 Search name, skill, certification…" style={{...inp(),width:'100%',marginBottom:6}}/>
        <input value={minExp} onChange={e=>setMinExp(e.target.value)} type="number" min="0" placeholder="Min. years experience" style={{...inp(),width:'100%',marginBottom:10}}/>
        <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:560,overflowY:'auto'}}>
          {filtered.length===0&&<div style={{textAlign:'center',color:C.light,padding:24,fontSize:13}}>No consultants match.</div>}
          {filtered.map(c=>{
            const st = CONSULTANT_STATUSES.find(s=>s.id===c.status)||CONSULTANT_STATUSES[0];
            return (
              <div key={c.id} onClick={()=>setSelected(c.id===selected?null:c.id)}
                style={{background:c.id===selected?BIZ_LIGHT:C.surface,border:`1px solid ${c.id===selected?BIZ_COLOR:C.border}`,borderRadius:10,padding:'10px 12px',cursor:'pointer',display:'flex',alignItems:'center',gap:10,transition:'all .15s'}}>
                <Avatar src={c.photo_url} name={`${c.first_name} ${c.last_name||''}`} size={36}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:13,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.first_name} {c.last_name}</div>
                  {c.title&&<div style={{fontSize:11,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.title}</div>}
                </div>
                <span style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:st.color+'22',color:st.color,flexShrink:0}}>{st.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Detail ── */}
      <div>
        {!selCons ? (
          <div style={{textAlign:'center',padding:60,color:C.light}}>
            <div style={{fontSize:48,marginBottom:12}}>🧑‍💼</div>
            <div>Select a consultant to view their profile</div>
          </div>
        ) : (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,gap:12}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <Avatar src={selCons.photo_url} name={`${selCons.first_name} ${selCons.last_name||''}`} size={56}/>
                <div>
                  <h3 style={{margin:0,color:BIZ_COLOR,fontSize:18}}>{selCons.first_name} {selCons.last_name}</h3>
                  {selCons.title&&<div style={{color:C.muted,fontSize:13}}>{selCons.title}</div>}
                </div>
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0}}>
                <button onClick={()=>setExportCons(selCons)} style={{...btn('#2563EB'),fontSize:12,padding:'5px 10px'}}>📄 Export Profile</button>
                <button onClick={()=>openEdit(selCons)} style={{...btn(C.amber),fontSize:12,padding:'5px 10px'}}>✏️ Edit</button>
                <button onClick={()=>{if(window.confirm('Delete consultant?')){deleteConsultant(selCons.id);setSelected(null);}}} style={{...btn(C.red),fontSize:12,padding:'5px 10px'}}>🗑</button>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              {[
                ['📧 Email', selCons.email],
                ['📱 Mobile', selCons.mobile],
                ['💼 Experience', selCons.experience_years?`${selCons.experience_years} years`:null],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l} style={{background:C.surfaceAlt,borderRadius:8,padding:'8px 12px'}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:2}}>{l}</div>
                  <div style={{fontSize:13,color:C.text}}>{v}</div>
                </div>
              ))}
              {selCons.resume_url&&(
                <div style={{background:C.surfaceAlt,borderRadius:8,padding:'8px 12px'}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:2}}>📄 Resume</div>
                  <a href={selCons.resume_url} target="_blank" rel="noreferrer" style={{fontSize:13,color:BIZ_COLOR,fontWeight:600}}>{selCons.resume_filename||'View resume'}</a>
                </div>
              )}
            </div>

            {selCons.skills?.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>Skills</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {selCons.skills.map(s=><span key={s} style={{fontSize:11,padding:'3px 10px',borderRadius:20,background:BIZ_LIGHT,color:BIZ_COLOR,fontWeight:600}}>{s}</span>)}
                </div>
              </div>
            )}

            {selCons.certifications?.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>Certifications</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {selCons.certifications.map(s=><span key={s} style={{fontSize:11,padding:'3px 10px',borderRadius:20,background:'#FFFBEB',color:'#D97706',fontWeight:600}}>🎖 {s}</span>)}
                </div>
              </div>
            )}

            {selCons.about&&(
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>About</div>
                <div style={{fontSize:13,color:C.text,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{selCons.about}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {exportCons && ReactDOM.createPortal(
        <ConsultantExportModal consultant={exportCons} bizProfile={bizProfile} userPlan={userPlan} onClose={()=>setExportCons(null)}/>,
        document.body
      )}
    </div>
  );
}

function ConsultantEditor({ form, upd, editCons, onSave, onCancel, uploadFile, uploadingPhoto, uploadingResume, C }) {
  const photoRef = React.useRef();
  const resumeRef = React.useRef();
  const [skillsText, setSkillsText] = React.useState((form.skills||[]).join(', '));
  const [certsText, setCertsText] = React.useState((form.certifications||[]).join(', '));

  const commitSkills = (txt) => upd('skills', txt.split(',').map(s=>s.trim()).filter(Boolean));
  const commitCerts  = (txt) => upd('certifications', txt.split(',').map(s=>s.trim()).filter(Boolean));

  return (
    <div style={{maxWidth:720}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{fontWeight:800,fontSize:15,color:BIZ_COLOR}}>{editCons?'Edit Consultant':'New Consultant'}</div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={onSave} style={{...btn(BIZ_COLOR),fontSize:12}}>💾 Save</button>
          <button onClick={onCancel} style={{...btn('#999'),fontSize:12}}>Cancel</button>
        </div>
      </div>

      {/* Photo */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14,display:'flex',alignItems:'center',gap:16}}>
        <div onClick={()=>photoRef.current?.click()} style={{cursor:'pointer'}}>
          <Avatar src={form.photo_url} name={`${form.first_name||'?'} ${form.last_name||''}`} size={72}/>
        </div>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>Profile Photo</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Shows initials until you upload one</div>
          <button onClick={()=>photoRef.current?.click()} disabled={uploadingPhoto} style={{...btn(BIZ_COLOR),fontSize:12,padding:'6px 14px'}}>{uploadingPhoto?'⏳ Uploading…':'📤 Upload Photo'}</button>
        </div>
        <input ref={photoRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>{const f=e.target.files?.[0]; if(f) uploadFile(f,'photo');}}/>
      </div>

      {/* Personal */}
      <div style={{fontSize:11,fontWeight:700,color:BIZ_COLOR,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Personal</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
        <input value={form.first_name} onChange={e=>upd('first_name',e.target.value)} placeholder="First name *" style={{...inp(),width:'100%'}}/>
        <input value={form.last_name}  onChange={e=>upd('last_name',e.target.value)}  placeholder="Last name"    style={{...inp(),width:'100%'}}/>
        <input value={form.mobile}     onChange={e=>upd('mobile',e.target.value)}     placeholder="Mobile"       style={{...inp(),width:'100%'}}/>
        <input value={form.email}      onChange={e=>upd('email',e.target.value)}      placeholder="Email" type="email" style={{...inp(),width:'100%'}}/>
      </div>

      {/* Professional */}
      <div style={{fontSize:11,fontWeight:700,color:BIZ_COLOR,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Professional</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
        <input value={form.title} onChange={e=>upd('title',e.target.value)} placeholder="Professional title, e.g. Senior DevOps Consultant" style={{...inp(),width:'100%',gridColumn:'1/-1'}}/>
        <input value={form.experience_years} onChange={e=>upd('experience_years',e.target.value)} type="number" min="0" step="0.5" placeholder="Years of experience" style={{...inp(),width:'100%'}}/>
        <select value={form.status} onChange={e=>upd('status',e.target.value)} style={{...inp(),width:'100%'}}>
          {CONSULTANT_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div style={{marginBottom:10}}>
        <input value={skillsText} onChange={e=>{setSkillsText(e.target.value);commitSkills(e.target.value);}} placeholder="Skills, comma separated — e.g. React, Node.js, AWS" style={{...inp(),width:'100%'}}/>
        {form.skills?.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:6}}>{form.skills.map(s=><span key={s} style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:BIZ_LIGHT,color:BIZ_COLOR,fontWeight:600}}>{s}</span>)}</div>}
      </div>
      <div style={{marginBottom:16}}>
        <input value={certsText} onChange={e=>{setCertsText(e.target.value);commitCerts(e.target.value);}} placeholder="Certifications, comma separated — e.g. AWS Certified Solutions Architect, PMP" style={{...inp(),width:'100%'}}/>
        {form.certifications?.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:6}}>{form.certifications.map(s=><span key={s} style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:'#FFFBEB',color:'#D97706',fontWeight:600}}>🎖 {s}</span>)}</div>}
      </div>
      <div style={{marginBottom:16}}>
        <textarea value={form.about} onChange={e=>upd('about',e.target.value)} placeholder="About — background, strengths, notable projects…" style={{...ta(90),width:'100%'}}/>
      </div>

      {/* Resume */}
      <div style={{fontSize:11,fontWeight:700,color:BIZ_COLOR,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Resume</div>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,display:'flex',alignItems:'center',gap:14}}>
        <button onClick={()=>resumeRef.current?.click()} disabled={uploadingResume} style={{...btn(BIZ_COLOR),fontSize:12,padding:'6px 14px'}}>{uploadingResume?'⏳ Uploading…':'📤 Upload Resume'}</button>
        {form.resume_filename&&<span style={{fontSize:12,color:C.muted}}>📄 {form.resume_filename}</span>}
        <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" style={{display:'none'}} onChange={e=>{const f=e.target.files?.[0]; if(f) uploadFile(f,'resume');}}/>
      </div>
    </div>
  );
}

// ── Clean, print-ready export of a consultant profile — same visual language as the invoice-box template ──
function ConsultantExportModal({ consultant: c, bizProfile, userPlan, onClose }) {
  const printPDF = () => {
    printHtml(`<!DOCTYPE html><html><head><title>${c.first_name} ${c.last_name||''} — Profile</title><style>body{margin:0;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif}@media print{.no-print{display:none}}</style></head><body>${document.getElementById('cons-export-area').innerHTML}</body></html>`);
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:4500,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)',overflowY:'auto'}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{maxWidth:840,width:'100%',margin:'24px 0'}}>
        <div className="no-print" style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:10}}>
          <button onClick={printPDF} style={{background:'#2B1DC9',color:'#fff',border:'none',borderRadius:9,padding:'10px 20px',cursor:'pointer',fontSize:14,fontWeight:700}}>⬇ Export / Print</button>
          <button onClick={onClose} style={{background:'#fff',color:'#333',border:'1px solid #ddd',borderRadius:9,padding:'10px 16px',cursor:'pointer',fontSize:14,fontWeight:700}}>✕ Close</button>
        </div>
        <div id="cons-export-area" className="invoice-box" style={{padding:36,border:'1px solid #eee',boxShadow:'0 0 10px rgba(0,0,0,.15)',fontSize:14,lineHeight:1.6,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",color:'#555',background:'#fff'}}>
          {/* Header: business branding left, consultant photo/name right */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',paddingBottom:24,borderBottom:'1px solid #eee',marginBottom:24,flexWrap:'wrap',gap:16}}>
            <div>
              {bizProfile?.biz_logo_url
                ? <img src={bizProfile.biz_logo_url} alt="logo" style={{maxWidth:180,maxHeight:50,objectFit:'contain'}}/>
                : <div style={{fontSize:22,color:'#333',fontWeight:700}}>{bizProfile?.biz_name||'Consultant Profile'}</div>}
              <div style={{fontSize:11,color:'#999',marginTop:4,textTransform:'uppercase',letterSpacing:'.08em'}}>Consultant Profile</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div>
                <div style={{fontSize:24,fontWeight:700,color:'#333',textAlign:'right'}}>{c.first_name} {c.last_name}</div>
                {c.title&&<div style={{fontSize:14,color:'#777',textAlign:'right'}}>{c.title}</div>}
              </div>
              {c.photo_url
                ? <img src={c.photo_url} alt={c.first_name} style={{width:64,height:64,borderRadius:'50%',objectFit:'cover',border:'1px solid #eee'}}/>
                : <div style={{width:64,height:64,borderRadius:'50%',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:22}}>
                    {(c.first_name?.[0]||'')+(c.last_name?.[0]||'')}
                  </div>}
            </div>
          </div>

          {/* Contact + experience strip */}
          <div style={{display:'flex',gap:24,flexWrap:'wrap',marginBottom:24,fontSize:13}}>
            {c.email&&<div>📧 {c.email}</div>}
            {c.mobile&&<div>📱 {c.mobile}</div>}
            {c.experience_years&&<div>💼 {c.experience_years} years experience</div>}
          </div>

          {c.about&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,fontWeight:700,color:'#333',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>About</div>
              <div style={{fontSize:13,lineHeight:1.7,whiteSpace:'pre-wrap'}}>{c.about}</div>
            </div>
          )}

          {c.skills?.length>0&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,fontWeight:700,color:'#333',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Skills</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {c.skills.map(s=><span key={s} style={{fontSize:12,padding:'4px 12px',borderRadius:20,background:'#eee',color:'#333'}}>{s}</span>)}
              </div>
            </div>
          )}

          {c.certifications?.length>0&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,fontWeight:700,color:'#333',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Certifications</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {c.certifications.map(s=><span key={s} style={{fontSize:12,padding:'4px 12px',borderRadius:20,background:'#FFFBEB',color:'#A06014'}}>🎖 {s}</span>)}
              </div>
            </div>
          )}

          {(!userPlan?.plan_id||userPlan.plan_id==="hobby")&&(
            <div style={{fontSize:10,color:'#aaa',textAlign:'center',marginTop:32,borderTop:'1px solid #eee',paddingTop:14}}>
              Prepared by {bizProfile?.biz_name||'—'} · Created with <strong>Aspreo</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── INVOICE / PROPOSAL ITEM EDITOR (shared) ───────────────────────────────────
function WhatsAppTab({ ctx }) {
  const C = useC();
  const { clients=[], invoices=[], uid, profile, bizProfile={}, updateInvoice } = ctx;
  const [to,       setTo]       = useState('');
  const [name,     setName]     = useState('');
  const [msg,      setMsg]      = useState('');
  const [logs,     setLogs]     = useState([]);
  const [selInv,   setSelInv]   = useState('');
  const [sending,  setSending]  = useState(false);
  const [waStatus, setWaStatus] = useState(null); // null | 'connecting' | 'connected' | 'error'
  const [waPhone,  setWaPhone]  = useState('');
  const [waToken,  setWaToken]  = useState('');

  // Load existing WA connection from profile on mount
  React.useEffect(() => {
    if (profile?.wa_phone_number) {
      setWaPhone(profile.wa_phone_number);
      setWaToken(profile.wa_access_token || '');
      setWaStatus('connected');
    }
  }, [profile]);

  // ── Meta Embedded Signup (WhatsApp-only app, no Facebook Login product needed) ──
  // Loads the Facebook JS SDK dynamically, then calls window.FB.login() with
  // the Embedded Signup config. Meta handles the entire WABA selection UI inside
  // their own popup. On success we get a code we exchange for an access token.
  const connectWhatsApp = () => {
    const appId = process.env.REACT_APP_META_APP_ID || '';
    if (!appId) {
      alert('REACT_APP_META_APP_ID not set in Vercel environment variables.');
      return;
    }
    setWaStatus('connecting');

    const launchFB = () => {
      window.FB.login(
        (response) => {
          if (response.authResponse?.code) {
            // Exchange code for token via our edge function
            fetch('/api/sb/functions/v1/whatsapp', {
              method: 'POST',
              headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}`,
              },
              body: JSON.stringify({
                action:       'oauth',
                code:         response.authResponse.code,
                uid,
                redirect_uri: 'https://www.aspreo.com/',
              }),
            })
            .then(r => r.json())
            .then(data => {
              if (data.ok) {
                setWaPhone(data.phone_number || '');
                setWaToken('saved');
                setWaStatus('connected');
              } else {
                console.error('[wa embedded signup]', data);
                setWaStatus('error');
                alert('WhatsApp connection failed: ' + (data.error || 'Unknown error'));
              }
            })
            .catch(e => {
              console.error('[wa embedded signup]', e);
              setWaStatus('error');
            });
          } else {
            // User cancelled or closed popup
            setWaStatus(null);
          }
        },
        {
          config_id:    process.env.REACT_APP_META_CONFIG_ID || '',
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: '',
            sessionInfoVersion: '3',
          },
        }
      );
    };

    // Load FB SDK if not already loaded
    if (window.FB) {
      launchFB();
      return;
    }
    // Inject SDK script
    const existingScript = document.getElementById('facebook-jssdk');
    if (!existingScript) {
      window.fbAsyncInit = () => {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: 'v18.0' });
        launchFB();
      };
      const s = document.createElement('script');
      s.id  = 'facebook-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk.js';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    } else {
      // Script tag exists but FB not ready yet — wait
      const wait = setInterval(() => {
        if (window.FB) { clearInterval(wait); launchFB(); }
      }, 200);
    }
  };

  const disconnectWhatsApp = async () => {
    if (!window.confirm('Disconnect your WhatsApp Business number?')) return;
    await supabase.from('profiles').update({ wa_access_token: null, wa_phone_number: null, wa_waba_id: null }).eq('id', uid);
    setWaPhone(''); setWaToken(''); setWaStatus(null);
  };

  // ── Send via Meta Cloud API (edge function) or wa.me fallback ─────────────
  const sendWA = async (toNum, toName, message) => {
    const entry = { id: Date.now(), to: toNum, name: toName, message, status: 'pending', time: new Date().toLocaleTimeString() };
    setLogs(p => [entry, ...p]);
    setSending(true);
    try {
      if (waStatus === 'connected' && waToken) {
        // Real Meta Cloud API send via Supabase edge function
        const res = await fetch('/api/sb/functions/v1/whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` },
          body: JSON.stringify({ action: 'send', to: toNum.replace(/[^0-9]/g,''), message, uid }),
        });
        const data = await res.json();
        if (res.ok && data.messages?.[0]?.id) {
          setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '✅ Sent' } : l));
        } else {
          setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '❌ ' + (data.error?.message || 'Failed') } : l));
        }
      } else {
        // Fallback: open wa.me deep link
        const num = toNum.replace(/[^0-9]/g,'');
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(message)}`, '_blank');
        setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '📲 Opened in WhatsApp' } : l));
      }
    } catch (e) {
      setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '❌ ' + e.message } : l));
    }
    setSending(false);
  };

  const signature = bizProfile?.biz_name || profile?.full_name || 'us';
  // Invoices/proforma both live in the same `invoices` table (doc_type tells
  // them apart) and both get a real public link once shared — this was simply
  // never interpolated into the message text before.
  const invoiceLink = (inv) => inv?.share_token ? `${window.location.origin}/invoice/${inv.share_token}` : '';

  const TEMPLATES = [
    { id:'invoice',  label:'📧 Invoice',       build: (inv, cl) => `Hi ${cl?.name||'there'} 👋\n\nPlease find your ${inv?.doc_type==='proforma'?'proforma invoice':'invoice'} ${inv?.invoice_number||''} for ₹${parseFloat(inv?.total||0).toLocaleString('en-IN')} below.\n\nDue date: ${inv?.due_date||'—'}${invoiceLink(inv)?`\nView it here: ${invoiceLink(inv)}`:''}\n\nThank you for your business!\n— ${signature}` },
    { id:'followup', label:'🔄 Follow Up',      build: (_,cl)   => `Hi ${cl?.name||'there'} 👋\n\nJust following up on our previous conversation. Would love to connect!\n\nLet me know a good time. 🙏\n— ${signature}` },
    { id:'proposal', label:'📄 Proposal Ready', build: (_,cl)   => `Hi ${cl?.name||'there'} 👋\n\nYour proposal is ready! Let me know when you'd like to review it. 🚀\n— ${signature}` },
    { id:'payment',  label:'💰 Payment Reminder',build: (inv,cl) => `Hi ${cl?.name||'there'} 👋\n\nGentle reminder: ${inv?.doc_type==='proforma'?'proforma invoice':'invoice'} ${inv?.invoice_number||''} for ₹${parseFloat(inv?.total||0).toLocaleString('en-IN')} is due on ${inv?.due_date||'—'}.${invoiceLink(inv)?`\nView it here: ${invoiceLink(inv)}`:''}\n\nThank you!\n— ${signature}` },
  ];

  const selClient  = clients.find(c => c.phone === to || c.email === to);
  const selInvoice = invoices.find(i => i.id === selInv);
  const WA_GREEN   = '#25D366';

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:16}}>
      <div>
        {/* ── Connection Status Card ── */}
        <div style={{background:C.surface,border:`1.5px solid ${waStatus==='connected'?WA_GREEN:waStatus==='connecting'?C.amber:C.border}`,borderRadius:12,padding:16,marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:44,height:44,borderRadius:12,background:waStatus==='connected'?'#DCFCE7':'#F0F0F5',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,flexShrink:0}}>
              {waStatus==='connected'?'✅':waStatus==='connecting'?'⏳':'💬'}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:13,color:waStatus==='connected'?WA_GREEN:C.text}}>
                {waStatus==='connected' ? `Connected: ${waPhone||'WhatsApp Business'}` : waStatus==='connecting' ? 'Connecting…' : 'WhatsApp Business'}
              </div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                {waStatus==='connected'
                  ? 'Messages send directly from your WhatsApp Business number via Meta Cloud API.'
                  : 'Connect your WhatsApp Business account to send messages directly from Aspreo.'}
              </div>
            </div>
            {waStatus==='connected'
              ? <button onClick={disconnectWhatsApp} style={{fontSize:11,padding:'5px 12px',borderRadius:7,border:`1px solid ${C.red}`,background:'transparent',color:C.red,cursor:'pointer',fontWeight:600,flexShrink:0}}>Disconnect</button>
              : <button onClick={connectWhatsApp} disabled={waStatus==='connecting'} style={{display:'flex',alignItems:'center',gap:8,padding:'9px 18px',borderRadius:9,border:'none',background:waStatus==='connecting'?C.border:WA_GREEN,color:'#fff',cursor:waStatus==='connecting'?'not-allowed':'pointer',fontWeight:700,fontSize:13,flexShrink:0,opacity:waStatus==='connecting'?0.7:1}}>
                  {waStatus==='connecting'?'Connecting…':'🔗 Connect WhatsApp Business'}
                </button>
            }
          </div>
          {waStatus!=='connected'&&(
            <div style={{marginTop:12,padding:'10px 12px',background:C.surfaceAlt,borderRadius:8,fontSize:11,color:C.muted,lineHeight:1.6}}>
              <strong style={{color:C.text}}>How it works:</strong> Click "Connect WhatsApp Business" → a Facebook popup opens → log in with your Facebook account → select your WhatsApp Business number → click Allow → done. Messages send from <em>your</em> number.
            </div>
          )}
        </div>

        {/* ── Compose Message ── */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14}}>
          <h4 style={{margin:'0 0 12px',color:WA_GREEN}}>💬 Send Message</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>To (Phone)</div>
              <input value={to} onChange={e=>setTo(e.target.value)} placeholder="+91 98765 43210" style={{...inp(),width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Name</div>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Recipient name" style={{...inp(),width:'100%'}}/>
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Quick Templates</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {TEMPLATES.map(t=>(
                <button key={t.id} onClick={()=>setMsg(t.build(selInvoice, selClient||{name}))} style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1px solid ${WA_GREEN}40`,background:'#F0FDF4',color:'#16A34A',cursor:'pointer'}}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {invoices.length>0&&(
            <div style={{marginBottom:8}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Link Invoice / Proforma (optional)</div>
              <select value={selInv} onChange={e=>{
                  const id = e.target.value;
                  setSelInv(id);
                  // Sharing must be on for the link to actually resolve — turn it on
                  // the moment an invoice is picked here, so the link always works.
                  const inv = invoices.find(i=>i.id===id);
                  if (inv && !inv.is_shared) updateInvoice?.(inv.id, { is_shared: true });
                }} style={{...inp(),width:'100%'}}>
                <option value="">None</option>
                {invoices.map(i=><option key={i.id} value={i.id}>{i.doc_type==='proforma'?'Proforma ':''}{i.invoice_number} — ₹{parseFloat(i.total||0).toLocaleString('en-IN')}</option>)}
              </select>
            </div>
          )}
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Type your message..." style={{...ta(100),width:'100%',marginBottom:10}}/>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button onClick={()=>{ if(!to.trim()||!msg.trim())return; sendWA(to,name||to,msg); setMsg(''); }}
              disabled={!to.trim()||!msg.trim()||sending}
              style={{...btn(WA_GREEN),opacity:(!to.trim()||!msg.trim()||sending)?0.5:1,flex:1}}>
              {sending?'Sending…': waStatus==='connected'?'📤 Send Message':'📲 Open in WhatsApp'}
            </button>
            {waStatus!=='connected'&&to.trim()&&msg.trim()&&(
              <button onClick={()=>{ const num=to.replace(/[^0-9]/g,''); window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`,'_blank'); }}
                style={{...btn('#25D366'),fontSize:12,padding:'8px 14px'}}>📲 wa.me</button>
            )}
          </div>
          {waStatus!=='connected'&&<div style={{fontSize:10,color:C.muted,marginTop:6}}>Not connected — clicking will open WhatsApp Web with the message pre-filled.</div>}
        </div>

        {/* ── Quick Send from Clients ── */}
        {clients.length>0&&(
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Quick Send to Client</div>
            {clients.filter(c=>c.phone||c.email).map(c=>(
              <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:`1px solid ${C.border}`}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>{c.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{c.phone||c.email}</div>
                </div>
                <button onClick={()=>{setTo(c.phone||c.email);setName(c.name);}} style={{...btn(WA_GREEN),fontSize:11,padding:'4px 10px'}}>Select</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Message Log ── */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:WA_GREEN}}>📋 Message Log</div>
        {logs.length===0&&<div style={{color:C.light,fontSize:12,textAlign:'center',padding:24}}>No messages sent yet.</div>}
        <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:600,overflowY:'auto'}}>
          {logs.map(l=>(
            <div key={l.id} style={{background:'#F0FDF4',border:'1px solid #86EFAC',borderRadius:8,padding:'8px 10px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                <span style={{fontSize:11,fontWeight:600,color:'#16A34A'}}>→ {l.name||l.to}</span>
                <span style={{fontSize:10,color:C.muted}}>{l.time}</span>
              </div>
              <div style={{fontSize:11,color:C.text,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{l.message.slice(0,120)}{l.message.length>120?'...':''}</div>
              <div style={{fontSize:10,color:l.status?.startsWith('✅')?'#16A34A':'#D97706',marginTop:4}}>{l.status}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── PROMOTION TAB — sends a WhatsApp promo message built from the company's
// "About" section (Business > Setup) so the user isn't writing pitch copy
// from scratch every time. Reuses the same send-or-wa.me-fallback mechanism
// as WhatsAppTab, kept separate here since each tab mounts independently. ──
function PromotionTab({ ctx }) {
  const C = useC();
  const { clients=[], uid, profile, bizProfile={} } = ctx;
  const [to, setTo] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const WA_GREEN = '#25D366';
  const waConnected = !!profile?.wa_phone_number;

  const defaultMsg = React.useCallback(() => {
    const biz = bizProfile?.biz_name || 'us';
    const about = bizProfile?.biz_about?.trim();
    return about
      ? `Hi ${name||'there'} 👋\n\n${about}\n\nWe'd love to help you too — reply to this message or let us know if you have any questions!\n— ${biz}`
      : `Hi ${name||'there'} 👋\n\nWe wanted to reach out and share what ${biz} can do for you.\n\n(Add an "About" description in Business > Setup to personalize this message with your real services.)\n— ${biz}`;
  }, [bizProfile?.biz_name, bizProfile?.biz_about, name]);

  const [msg, setMsg] = useState(defaultMsg);
  useEffect(() => { setMsg(defaultMsg()); }, [defaultMsg]); // eslint-disable-line

  const send = async (toNum, toName, message) => {
    const entry = { id: Date.now(), to: toNum, name: toName, message, status: 'pending', time: new Date().toLocaleTimeString() };
    setLogs(p => [entry, ...p]);
    setSending(true);
    try {
      if (waConnected) {
        const res = await fetch('/api/sb/functions/v1/whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` },
          body: JSON.stringify({ action: 'send', to: toNum.replace(/[^0-9]/g,''), message, uid }),
        });
        const data = await res.json();
        setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: (res.ok && data.messages?.[0]?.id) ? '✅ Sent' : '❌ ' + (data.error?.message || 'Failed') } : l));
      } else {
        const num = toNum.replace(/[^0-9]/g,'');
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(message)}`, '_blank');
        setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '📲 Opened in WhatsApp' } : l));
      }
    } catch (e) {
      setLogs(p => p.map(l => l.id === entry.id ? { ...l, status: '❌ ' + e.message } : l));
    }
    setSending(false);
  };

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:16}}>
      <div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14}}>
          <h4 style={{margin:'0 0 6px',color:WA_GREEN}}>📣 Send Promotional Message</h4>
          <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Built from your company's About section in Business &gt; Setup.</div>
          {!bizProfile?.biz_about?.trim() && (
            <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#92400E',marginBottom:12}}>
              ⚠️ Add an "About" description in Business &gt; Setup so this message reflects your real company &amp; services.
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <div><div style={{fontSize:11,color:C.muted,marginBottom:4}}>To (Phone)</div><input value={to} onChange={e=>setTo(e.target.value)} placeholder="+91 98765 43210" style={{...inp(),width:'100%'}}/></div>
            <div><div style={{fontSize:11,color:C.muted,marginBottom:4}}>Name</div><input value={name} onChange={e=>setName(e.target.value)} placeholder="Recipient name" style={{...inp(),width:'100%'}}/></div>
          </div>
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} style={{...ta(120),width:'100%',marginBottom:10}}/>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button onClick={()=>{ if(!to.trim()||!msg.trim())return; send(to,name||to,msg); }} disabled={!to.trim()||!msg.trim()||sending}
              style={{...btn(WA_GREEN),opacity:(!to.trim()||!msg.trim()||sending)?0.5:1,flex:1}}>
              {sending?'Sending…':waConnected?'📤 Send Message':'📲 Open in WhatsApp'}
            </button>
            <button onClick={()=>setMsg(defaultMsg())} style={{...btn('#999'),fontSize:12}}>↺ Reset to Default</button>
          </div>
          {!waConnected && <div style={{fontSize:10,color:C.muted,marginTop:6}}>Not connected — clicking will open WhatsApp Web with the message pre-filled. Connect in the WhatsApp tab to send directly.</div>}
        </div>
      </div>
      <div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Quick Send to Client</div>
          {clients.filter(c=>c.phone||c.email).length===0 && <div style={{fontSize:12,color:C.muted}}>No clients with phone/email yet.</div>}
          {clients.filter(c=>c.phone||c.email).map(c=>(
            <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:`1px solid ${C.border}`}}>
              <div><div style={{fontSize:13,fontWeight:600}}>{c.name}</div><div style={{fontSize:11,color:C.muted}}>{c.phone||c.email}</div></div>
              <button onClick={()=>{setTo(c.phone||c.email);setName(c.name);}} style={{...btn(WA_GREEN),fontSize:11,padding:'4px 10px'}}>Select</button>
            </div>
          ))}
          {logs.length>0 && (
            <div style={{marginTop:14,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
              <div style={{fontWeight:700,fontSize:12,marginBottom:6}}>Recent</div>
              {logs.slice(0,5).map(l=>(
                <div key={l.id} style={{fontSize:11,color:C.muted,marginBottom:4}}>{l.time} — {l.name}: {l.status}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── GOOGLE SHEETS INTEGRATION TAB ─────────────────────────────────────────────
function SheetsTab({ ctx }) {
  const { sheetLinks=[], addSheetLink, updateSheetLink, deleteSheetLink, addLead, addClient } = ctx;
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing]   = useState(null);
  const [preview, setPreview]   = useState(null);
  const blank = { name:'', sheet_id:'', sheet_url:'', tab_name:'Sheet1', target_module:'leads', column_map: JSON.stringify({A:'name',B:'email',C:'phone',D:'company',E:'notes'}, null, 2) };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  // Extract sheet ID from URL
  const extractSheetId = (url) => {
    const m = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return m ? m[1] : url;
  };

  const save = async () => {
    if (!form.name.trim()||!form.sheet_url.trim()) return;
    const sheet_id = extractSheetId(form.sheet_url);
    let colMap = {};
    try { colMap = JSON.parse(form.column_map); } catch { colMap = {A:'name',B:'email'}; }
    await addSheetLink({ ...form, sheet_id, column_map: colMap });
    setForm(blank); setShowForm(false);
  };

  // Real Google Sheets sync using API Key (public sheets only)
  const syncSheet = async (link) => {
    setSyncing(link.id);
    setPreview(null);
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';
      const sheetId = link.sheet_id;
      const tabName = encodeURIComponent(link.tab_name || 'Sheet1');
      let url;
      if (apiKey) {
        // Public sheet via API Key — no OAuth needed
        url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${tabName}?key=${apiKey}`;
      } else {
        // Fallback: published CSV export (works if sheet is published to web)
        url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&sheet=${tabName}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Sheet fetch failed: ${res.status}. Make sure the sheet is shared as "Anyone with the link can view".`);
      let rows = [];
      if (apiKey) {
        const data = await res.json();
        const values = data.values || [];
        if (values.length < 2) { alert('Sheet appears empty or has no data rows.'); setSyncing(null); return; }
        const headers = values[0]; // first row = headers
        const colMap = link.column_map || {};
        // Map by position (A=0, B=1, ...) or by header name
        rows = values.slice(1).map(row => {
          const obj = {};
          Object.entries(colMap).forEach(([col, field]) => {
            const idx = col.charCodeAt(0) - 65; // A→0, B→1...
            if (row[idx] !== undefined) obj[field] = row[idx];
          });
          return obj;
        }).filter(r => r.name || r.email);
      } else {
        // Parse CSV fallback
        const text = await res.text();
        const lines = text.split('\n').filter(l=>l.trim());
        const colMap = link.column_map || {};
        rows = lines.slice(1).map(line => {
          const cols = line.split(',').map(c=>c.replace(/^"|"$/g,'').trim());
          const obj = {};
          Object.entries(colMap).forEach(([col, field]) => {
            const idx = col.charCodeAt(0) - 65;
            if (cols[idx]) obj[field] = cols[idx];
          });
          return obj;
        }).filter(r => r.name || r.email);
      }
      if (!rows.length) { alert('No matching rows found. Check your column mapping.'); setSyncing(null); return; }
      setPreview({ link, rows });
    } catch(e) {
      alert('Sync failed: ' + e.message);
    }
    setSyncing(null);
  };

  // Auto-sync interval (set up once per session if enabled)
  const autoSyncRef = React.useRef(null);
  const [autoSync, setAutoSync] = React.useState(false);
  React.useEffect(() => {
    if (autoSync && sheetLinks.length) {
      autoSyncRef.current = setInterval(() => sheetLinks.forEach(l => syncSheet(l)), 30 * 60 * 1000);
    }
    return () => clearInterval(autoSyncRef.current);
  }, [autoSync, sheetLinks.length]); // eslint-disable-line

  const importRows = async (link, rows) => {
    for (const row of rows) {
      if (link.target_module === 'leads') await addLead({ ...row, status:'new', source:'google_sheets' });
      else if (link.target_module === 'clients') await addClient({ ...row, status:'active' });
    }
    setPreview(null);
    updateSheetLink(link.id, { last_synced: new Date().toISOString() });
    alert(`✅ Imported ${rows.length} ${link.target_module} successfully!`);
  };

  return (
    <div>
      <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#1D4ED8'}}>
        <strong>📊 Google Sheets Integration:</strong> Link any public Google Sheet to automatically import leads or clients. Sheet must be shared as &quot;Anyone with the link can view&quot;.
        {!process.env.REACT_APP_GOOGLE_API_KEY&&<span style={{color:'#D97706'}}> For best results, add <code>REACT_APP_GOOGLE_API_KEY</code> to Vercel env vars (Google Cloud → Sheets API → Credentials).</span>}
        <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
          <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:12}}>
            <input type="checkbox" checked={autoSync} onChange={e=>setAutoSync(e.target.checked)} style={{accentColor:'#1D4ED8'}}/>
            Auto-sync every 30 minutes (this session)
          </label>
        </div>
      </div>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <span style={{fontSize:14,fontWeight:700}}>Linked Sheets ({sheetLinks.length})</span>
        <button onClick={()=>setShowForm(true)} style={btn(BIZ_COLOR)}>+ Link Sheet</button>
      </div>

      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${BIZ_COLOR}`,borderRadius:12,padding:16,marginBottom:14}}>
          <h4 style={{margin:'0 0 12px',color:BIZ_COLOR}}>Link Google Sheet</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.name}      onChange={e=>upd('name',e.target.value)}      placeholder="Sheet name (e.g. Instagram Leads)" style={{...inp(),width:'100%'}}/>
            <select value={form.target_module} onChange={e=>upd('target_module',e.target.value)} style={{...inp(),width:'100%'}}>
              <option value="leads">Import as Leads</option>
              <option value="clients">Import as Clients</option>
              <option value="content_posts">Import as Content Posts</option>
            </select>
          </div>
          <input value={form.sheet_url} onChange={e=>{upd('sheet_url',e.target.value);upd('sheet_id',extractSheetId(e.target.value));}} placeholder="Google Sheet URL (share → copy link)" style={{...inp(),width:'100%',marginBottom:8}}/>
          <input value={form.tab_name} onChange={e=>upd('tab_name',e.target.value)} placeholder="Tab name (e.g. Sheet1)" style={{...inp(),width:'100%',marginBottom:8}}/>
          <div style={{marginBottom:4,fontSize:12,color:C.muted}}>Column mapping (column letter → field name):</div>
          <textarea value={form.column_map} onChange={e=>upd('column_map',e.target.value)} style={{...ta(100),width:'100%',fontFamily:'monospace',fontSize:12,marginBottom:8}}/>
          <div style={{fontSize:11,color:C.muted,marginBottom:10}}>Available fields: name, email, phone, company, notes (for leads/clients)</div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(BIZ_COLOR)}>💾 Save</button>
            <button onClick={()=>setShowForm(false)} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Preview / Import modal */}
      {preview && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,zIndex:4000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setPreview(null)}>
          <div style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:560,maxHeight:'80vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:'0 0 12px',color:BIZ_COLOR}}>Preview: {preview.rows.length} rows found</h3>
            <div style={{overflowX:'auto',marginBottom:16}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:BIZ_COLOR,color:'#fff'}}>
                  {Object.keys(preview.rows[0]||{}).map(k=><th key={k} style={{padding:'6px 10px',textAlign:'left'}}>{k}</th>)}
                </tr></thead>
                <tbody>{preview.rows.map((r,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid #e5e7eb'}}>
                    {Object.values(r).map((v,j)=><td key={j} style={{padding:'5px 10px'}}>{v}</td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>importRows(preview.link, preview.rows)} style={btn(BIZ_COLOR)}>✅ Import {preview.rows.length} rows</button>
              <button onClick={()=>setPreview(null)} style={btn('#999')}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Sheet links list */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {sheetLinks.length===0&&<div style={{textAlign:'center',color:C.light,padding:40}}>No sheets linked yet.</div>}
        {sheetLinks.map(link=>(
          <div key={link.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:13,color:C.text}}>📊 {link.name}</div>
              <div style={{fontSize:11,color:C.muted}}>Target: {link.target_module} · Tab: {link.tab_name||'Sheet1'}</div>
              {link.last_synced&&<div style={{fontSize:10,color:'#16A34A'}}>Last synced: {new Date(link.last_synced).toLocaleString('en-IN')}</div>}
            </div>
            <div style={{display:'flex',gap:6}}>
              <button onClick={()=>syncSheet(link)} disabled={syncing===link.id} style={{...btn(BIZ_COLOR),fontSize:11,padding:'5px 10px',opacity:syncing===link.id?0.6:1}}>
                {syncing===link.id?'⏳ Syncing...':'🔄 Sync Now'}
              </button>
              <a href={link.sheet_url} target="_blank" rel="noreferrer" style={{...btn('#6B7280'),fontSize:11,padding:'5px 10px',textDecoration:'none'}}>🔗 Open</a>
              <button onClick={()=>{if(window.confirm('Remove this sheet link?'))deleteSheetLink(link.id);}} style={{...btn(C.red),fontSize:11,padding:'5px 8px'}}>🗑</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 1 — SOCIAL HUB: Connect & manage social accounts
// ══════════════════════════════════════════════════════════════════════════════
const PLATFORM_CONFIG = {
  instagram: { label:'Instagram',  icon:'📸', color:'#E1306C', bg:'#FEF2F6', scope:'Business accounts only', mock:true },
  linkedin:  { label:'LinkedIn',   icon:'💼', color:'#0A66C2', bg:'#EFF6FF', scope:'Personal + Company pages', mock:true },
  facebook:  { label:'Facebook',   icon:'📘', color:'#1877F2', bg:'#EFF6FF', scope:'Pages only (Phase 2)', mock:true },
  twitter:   { label:'X / Twitter',icon:'🐦', color:'#1DA1F2', bg:'#EFF6FF', scope:'Paid API (Phase 2)', mock:true },
  youtube:   { label:'YouTube',    icon:'▶️',  color:'#FF0000', bg:'#FEF2F2', scope:'Phase 2', mock:true },
};

function SocialHubTab({ ctx }) {
  const { socialAccounts=[], addSocialAccount, updateSocialAccount, deleteSocialAccount } = ctx;
  const [connecting, setConnecting] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ platform:'instagram', account_name:'', account_handle:'', account_type:'business', followers_count:'' });
  const upd = (k,v) => setManualForm(p=>({...p,[k]:v}));

  // Mock OAuth connect — replace body with real OAuth redirect in production
  const connectPlatform = async (platform) => {
    setConnecting(platform);
    // MOCK: In production → window.location.href = `/api/social/connect?platform=${platform}`
    await new Promise(r => setTimeout(r, 1000));
    setConnecting(null);
    setManualForm(p=>({...p, platform}));
    setShowManual(true);
  };

  const saveManual = async () => {
    if (!manualForm.account_name.trim()) return;
    await addSocialAccount({
      ...manualForm,
      followers_count: parseInt(manualForm.followers_count)||0,
      is_active: true,
    });
    setShowManual(false);
    setManualForm({ platform:'instagram', account_name:'', account_handle:'', account_type:'business', followers_count:'' });
  };

  const platformAccounts = (p) => socialAccounts.filter(a => a.platform === p);
  const totalFollowers = socialAccounts.reduce((s,a) => s + (parseInt(a.followers_count)||0), 0);

  return (
    <div>
      <div style={{background:'#FEF9C3',border:'1px solid #D97706',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#92400E'}}>
        <strong>🔧 MVP Mode:</strong> OAuth connections are mocked. In production, connect your Meta App ID and LinkedIn Client ID to enable real OAuth. Click "Connect" to manually add an account for now.
      </div>

      {/* Summary */}
      {socialAccounts.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
          {[
            { label:'Connected Accounts', value:socialAccounts.length, icon:'🔗', color:CONTENT_COLOR },
            { label:'Total Followers',    value:totalFollowers.toLocaleString('en-IN'), icon:'👥', color:'#16A34A' },
            { label:'Active Platforms',   value:new Set(socialAccounts.map(a=>a.platform)).size, icon:'📱', color:'#2563EB' },
          ].map(k=>(
            <div key={k.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:20,marginBottom:4}}>{k.icon}</div>
              <div style={{fontSize:20,fontWeight:800,color:k.color}}>{k.value}</div>
              <div style={{fontSize:11,color:C.muted}}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Manual add form */}
      {showManual && (
        <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:'0 0 12px',color:CONTENT_COLOR}}>Add Account — {PLATFORM_CONFIG[manualForm.platform]?.label}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <select value={manualForm.platform} onChange={e=>upd('platform',e.target.value)} style={{...inp(),width:'100%'}}>
              {Object.entries(PLATFORM_CONFIG).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select value={manualForm.account_type} onChange={e=>upd('account_type',e.target.value)} style={{...inp(),width:'100%'}}>
              <option value="personal">Personal</option>
              <option value="business">Business</option>
              <option value="creator">Creator</option>
              <option value="page">Page</option>
            </select>
            <input value={manualForm.account_name}    onChange={e=>upd('account_name',e.target.value)}    placeholder="Account Name" style={{...inp(),width:'100%'}}/>
            <input value={manualForm.account_handle}  onChange={e=>upd('account_handle',e.target.value)}  placeholder="@handle (optional)" style={{...inp(),width:'100%'}}/>
            <input value={manualForm.followers_count} onChange={e=>upd('followers_count',e.target.value)} placeholder="Followers count" type="number" style={{...inp(),width:'100%'}}/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={saveManual} style={btn(CONTENT_COLOR)}>✅ Save Account</button>
            <button onClick={()=>setShowManual(false)} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Platform grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => {
          const accs = platformAccounts(key);
          return (
            <div key={key} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,position:'relative'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <div style={{width:36,height:36,borderRadius:10,background:cfg.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>{cfg.icon}</div>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:C.text}}>{cfg.label}</div>
                  <div style={{fontSize:10,color:C.muted}}>{cfg.scope}</div>
                </div>
              </div>
              {accs.length === 0 ? (
                <button onClick={()=>connectPlatform(key)} disabled={connecting===key}
                  style={{...btn(cfg.color),width:'100%',fontSize:12,opacity:connecting===key?0.6:1}}>
                  {connecting===key ? '⏳ Connecting...' : '🔗 Connect'}
                </button>
              ) : (
                <div>
                  {accs.map(acc=>(
                    <div key={acc.id} style={{background:cfg.bg,borderRadius:8,padding:'8px 10px',marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:cfg.color}}>{acc.account_name}</div>
                        {acc.account_handle&&<div style={{fontSize:10,color:C.muted}}>@{acc.account_handle.replace('@','')}</div>}
                        {acc.followers_count>0&&<div style={{fontSize:10,color:C.muted}}>{parseInt(acc.followers_count).toLocaleString('en-IN')} followers</div>}
                        <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,background:acc.is_active?'#DCFCE7':'#FEF2F2',color:acc.is_active?'#16A34A':'#DC2626'}}>
                          {acc.is_active?'● Active':'○ Inactive'}
                        </span>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        <button onClick={()=>updateSocialAccount(acc.id,{is_active:!acc.is_active})} style={{...btn(acc.is_active?'#D97706':'#16A34A'),fontSize:9,padding:'2px 6px'}}>
                          {acc.is_active?'Pause':'Resume'}
                        </button>
                        <button onClick={()=>{if(window.confirm('Disconnect this account?'))deleteSocialAccount(acc.id);}} style={{...btn(C.red),fontSize:9,padding:'2px 6px'}}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>connectPlatform(key)} style={{...btn(cfg.color),width:'100%',fontSize:11,padding:'5px',marginTop:4}}>+ Add Another</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* API Guide */}
      <div style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
        <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:8}}>🔑 Connect Real OAuth (Production)</div>
        {[
          ['Instagram/Facebook','1. Create Meta Developer App → 2. Add Instagram Basic Display API → 3. Set callback: /api/social/callback?platform=instagram → 4. Add META_APP_ID + META_APP_SECRET to .env'],
          ['LinkedIn','1. Create LinkedIn Developer App → 2. Enable Marketing API → 3. Set callback: /api/social/callback?platform=linkedin → 4. Add LINKEDIN_CLIENT_ID + LINKEDIN_CLIENT_SECRET to .env'],
        ].map(([plat, guide]) => (
          <div key={plat} style={{marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:600,color:CONTENT_COLOR,marginBottom:2}}>{plat}</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>{guide}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 2 — CONTENT SCHEDULER: Compose + schedule posts
// ══════════════════════════════════════════════════════════════════════════════
const CHAR_LIMITS = { LinkedIn:3000, Instagram:2200, 'Twitter/X':280, Facebook:63206, YouTube:5000, Newsletter:100000, Blog:100000, TikTok:2200, Podcast:5000 };

function SchedulerTab({ ctx }) {
  const { contentPosts=[], contentPillars=[], socialAccounts=[], addContentPost, updateContentPost, deleteContentPost } = ctx;
  const [showComposer, setShowComposer] = useState(false);
  const [editPost,     setEditPost]     = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  const SCHED_STATUSES = ['idea','draft','ready','scheduled','published','failed'];
  const blank = {
    title:'', body:'', platform:'LinkedIn', status:'draft',
    scheduled_at:'', pillarId:'', type:'Educational',
    hook:'', cta:'', tags:'', media_urls:[],
    social_account_id:'',
  };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const charLimit = CHAR_LIMITS[form.platform] || 3000;
  const bodyLen   = (form.body||'').length;
  const charPct   = Math.round((bodyLen/charLimit)*100);

  const save = async () => {
    if (!form.title.trim() && !form.body.trim()) return;
    const title = form.title || form.body.slice(0,60);
    const tags  = typeof form.tags === 'string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : (form.tags||[]);
    const payload = { ...form, title, tags, char_count: bodyLen };
    if (editPost) updateContentPost(editPost.id, payload);
    else await addContentPost(payload);
    setForm(blank); setShowComposer(false); setEditPost(null);
  };

  const openEdit = (p) => {
    setEditPost(p);
    setForm({ ...blank, ...p, tags: (p.tags||[]).join(', '), scheduled_at: p.scheduled_at||p.scheduledDate||'' });
    setShowComposer(true);
  };

  // Filtering
  let filtered = contentPosts;
  if (filterStatus !== 'all')   filtered = filtered.filter(p => p.status === filterStatus);
  if (filterPlatform !== 'all') filtered = filtered.filter(p => p.platform === filterPlatform);
  // Sort: scheduled first by date, then by created_at
  filtered = [...filtered].sort((a,b) => {
    const da = a.scheduled_at||a.scheduledDate||a.created_at||'';
    const db = b.scheduled_at||b.scheduledDate||b.created_at||'';
    return da.localeCompare(db);
  });

  const accsForPlatform = socialAccounts.filter(a => a.platform === (form.platform||'').toLowerCase() && a.is_active);
  const platforms = [...new Set(contentPosts.map(p=>p.platform).filter(Boolean))];

  return (
    <div>
      {/* Filter + Add bar */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{...inp(),width:'auto',fontSize:11,padding:'4px 8px'}}>
            <option value="all">All Status</option>
            {SCHED_STATUSES.map(s=><option key={s} value={s}>{POST_STATUSES.find(x=>x.id===s)?.label||s}</option>)}
          </select>
          <select value={filterPlatform} onChange={e=>setFilterPlatform(e.target.value)} style={{...inp(),width:'auto',fontSize:11,padding:'4px 8px'}}>
            <option value="all">All Platforms</option>
            {platforms.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <button onClick={()=>{setEditPost(null);setForm(blank);setShowComposer(true);}} style={btn(CONTENT_COLOR)}>✏️ Compose Post</button>
      </div>

      {/* Composer */}
      {showComposer && (
        <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:'0 0 12px',color:CONTENT_COLOR}}>{editPost?'Edit Post':'✏️ Compose Post'}</h4>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
            <select value={form.platform} onChange={e=>upd('platform',e.target.value)} style={{...inp(),width:'100%'}}>
              {PLATFORMS.map(p=><option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.status} onChange={e=>upd('status',e.target.value)} style={{...inp(),width:'100%'}}>
              {POST_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select value={form.type} onChange={e=>upd('type',e.target.value)} style={{...inp(),width:'100%'}}>
              {POST_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Hook */}
          <input value={form.hook} onChange={e=>upd('hook',e.target.value)} placeholder="🪝 Hook — the first line that stops the scroll" style={{...inp(),width:'100%',marginBottom:8}}/>

          {/* Body */}
          <div style={{position:'relative',marginBottom:8}}>
            <textarea value={form.body} onChange={e=>upd('body',e.target.value)}
              placeholder={`Write your ${form.platform} post here...\n\n• Add value\n• Tell a story\n• Be specific`}
              style={{...ta(160),width:'100%',fontFamily:FONT,fontSize:13,lineHeight:1.7}}/>
            <div style={{position:'absolute',bottom:8,right:10,fontSize:10,
              color:charPct>90?C.red:charPct>75?C.amber:C.muted}}>
              {bodyLen}/{charLimit} {charPct>100?'⚠️ OVER LIMIT':''}
            </div>
          </div>

          {/* CTA */}
          <input value={form.cta} onChange={e=>upd('cta',e.target.value)} placeholder="📣 CTA — what should readers do after reading?" style={{...inp(),width:'100%',marginBottom:8}}/>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.scheduled_at} onChange={e=>upd('scheduled_at',e.target.value)} type="datetime-local" style={{...inp(),width:'100%'}} title="Schedule date & time"/>
            {contentPillars.length > 0 && (
              <select value={form.pillarId||''} onChange={e=>upd('pillarId',e.target.value)} style={{...inp(),width:'100%'}}>
                <option value="">No Pillar</option>
                {contentPillars.map(p=><option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
              </select>
            )}
            {accsForPlatform.length > 0 && (
              <select value={form.social_account_id||''} onChange={e=>upd('social_account_id',e.target.value)} style={{...inp(),width:'100%'}}>
                <option value="">Select Account</option>
                {accsForPlatform.map(a=><option key={a.id} value={a.id}>{a.account_name}</option>)}
              </select>
            )}
          </div>

          <input value={form.tags} onChange={e=>upd('tags',e.target.value)} placeholder="Tags (comma separated)" style={{...inp(),width:'100%',marginBottom:10}}/>

          {/* Platform formatting tips */}
          <div style={{background:C.surfaceAlt,borderRadius:8,padding:'8px 12px',marginBottom:10,fontSize:11,color:C.muted}}>
            💡 <strong>{form.platform}:</strong> {
              form.platform==='LinkedIn' ? 'Start with a bold hook. Use line breaks. End with a question to drive comments.' :
              form.platform==='Instagram' ? 'First 125 chars are shown before "More". Put key message upfront. Use 5-10 hashtags.' :
              form.platform==='Twitter/X' ? `${280-bodyLen} characters remaining. Short, punchy, or use thread format.` :
              'Keep it valuable and authentic.'
            }
          </div>

          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button onClick={save} style={btn(CONTENT_COLOR)}>💾 {editPost?'Update':'Save'}</button>
            <button onClick={()=>{if(form.status!=='scheduled')upd('status','scheduled');save();}} style={btn('#6B3FA0')}>📅 Schedule</button>
            <button onClick={()=>{setShowComposer(false);setEditPost(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
            {form.status==='scheduled'&&form.scheduled_at&&(
              <span style={{fontSize:11,color:'#6B3FA0',marginLeft:8}}>🕐 Will publish: {new Date(form.scheduled_at).toLocaleString('en-IN')}</span>
            )}
          </div>
        </div>
      )}

      {/* Stats row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:14}}>
        {POST_STATUSES.map(st=>{
          const count = contentPosts.filter(p=>p.status===st.id).length;
          return (
            <div key={st.id} onClick={()=>setFilterStatus(st.id===filterStatus?'all':st.id)}
              style={{background:filterStatus===st.id?st.color:C.surface,border:`1px solid ${filterStatus===st.id?st.color:C.border}`,borderRadius:8,padding:'8px',textAlign:'center',cursor:'pointer'}}>
              <div style={{fontSize:16,fontWeight:800,color:filterStatus===st.id?'#fff':st.color}}>{count}</div>
              <div style={{fontSize:10,color:filterStatus===st.id?'rgba(255,255,255,.8)':C.muted}}>{st.label}</div>
            </div>
          );
        })}
      </div>

      {/* Post list */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {filtered.length===0&&<div style={{textAlign:'center',color:C.light,padding:40}}>No posts yet. Compose your first post!</div>}
        {filtered.map(post=>{
          const st = POST_STATUSES.find(s=>s.id===post.status)||POST_STATUSES[0];
          const pil = contentPillars.find(p=>p.id===(post.pillarId||post.pillar_id));
          const schedDate = post.scheduled_at||post.scheduledDate;
          return (
            <div key={post.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`3px solid ${st.color}`,borderRadius:10,padding:'12px 16px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',marginBottom:4}}>
                    <span style={{fontWeight:700,fontSize:13,color:C.text}}>{post.title}</span>
                    <span style={{fontSize:9,padding:'2px 7px',borderRadius:10,background:st.color+'22',color:st.color,fontWeight:600}}>{st.label}</span>
                    <span style={{fontSize:9,padding:'2px 7px',borderRadius:10,background:CONTENT_LIGHT,color:CONTENT_COLOR}}>{post.platform}</span>
                    {pil&&<span style={{fontSize:9,color:pil.color,fontWeight:600}}>{pil.icon} {pil.name}</span>}
                  </div>
                  {post.hook&&<div style={{fontSize:12,color:C.text,fontWeight:500,marginBottom:3,lineHeight:1.4}}>🪝 {post.hook}</div>}
                  {post.body&&<div style={{fontSize:12,color:C.muted,lineHeight:1.5,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{post.body}</div>}
                  {schedDate&&<div style={{fontSize:11,color:'#6B3FA0',marginTop:4}}>📅 {new Date(schedDate).toLocaleString('en-IN')}</div>}
                  {post.error_message&&<div style={{fontSize:11,color:C.red,marginTop:4}}>⚠️ {post.error_message}</div>}
                </div>
                <div style={{display:'flex',gap:5,marginLeft:12,flexShrink:0}}>
                  <select value={post.status} onChange={e=>updateContentPost(post.id,{status:e.target.value})} style={{fontSize:10,padding:'2px 5px',border:`1px solid ${C.border}`,borderRadius:5,background:C.surface,cursor:'pointer'}}>
                    {POST_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <button onClick={()=>openEdit(post)} style={{...btn(C.amber),fontSize:10,padding:'4px 8px'}}>✏️</button>
                  <button onClick={()=>{if(window.confirm('Delete?'))deleteContentPost(post.id);}} style={{...btn(C.red),fontSize:10,padding:'4px 8px'}}>🗑</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 3 — ENGAGEMENT DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function DMEngineTab({ ctx }) {
  const { dmTemplates=[], addDMTemplate, updateDMTemplate, deleteDMTemplate } = ctx;
  const [showForm,  setShowForm]  = useState(false);
  const [editTmpl,  setEditTmpl]  = useState(null);
  const [copied,    setCopied]    = useState(null);
  const blank = { name:'', platform:'instagram', trigger_type:'manual', trigger_keyword:'', message:'', is_active:true };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const TRIGGER_TYPES = [
    { id:'manual',       label:'✋ Manual — you send it yourself' },
    { id:'keyword',      label:'🔤 Keyword — someone comments a word' },
    { id:'new_follower', label:'👋 New Follower — welcome message' },
  ];

  const save = async () => {
    if (!form.name.trim()||!form.message.trim()) return;
    if (editTmpl) updateDMTemplate(editTmpl.id, form);
    else await addDMTemplate(form);
    setForm(blank); setShowForm(false); setEditTmpl(null);
  };

  const copyToClipboard = (tmpl) => {
    navigator.clipboard.writeText(tmpl.message).then(()=>{
      setCopied(tmpl.id); setTimeout(()=>setCopied(null),2000);
      updateDMTemplate(tmpl.id, { use_count: (tmpl.use_count||0)+1 });
    });
  };

  const STARTER_TEMPLATES = [
    { name:'Lead Magnet Delivery', platform:'instagram', trigger_type:'keyword', trigger_keyword:'GUIDE',
      message:'Hey! 👋 Thanks for commenting! Here\'s the free guide you requested:\n\n🔗 [YOUR LINK HERE]\n\nLet me know if you have any questions! 🙌' },
    { name:'New Follower Welcome', platform:'instagram', trigger_type:'new_follower',
      message:'Hey [Name]! 👋 Thanks for following!\n\nI share tips on [YOUR NICHE] every week.\n\nWhat brought you here today? I\'d love to know! 😊' },
    { name:'LinkedIn Connection', platform:'linkedin', trigger_type:'new_follower',
      message:'Hi [Name], thanks for connecting! 🙏\n\nI help [TARGET AUDIENCE] achieve [OUTCOME].\n\nWould love to learn more about what you\'re working on. What\'s your biggest challenge right now?' },
    { name:'Proposal Follow-Up', platform:'instagram', trigger_type:'manual',
      message:'Hey! 👋 Just following up on the proposal I sent over.\n\nHappy to jump on a quick call to walk you through it — totally free, no pressure.\n\nWhen works best for you? 📅' },
  ];

  return (
    <div>
      <div style={{background:'#FEF9C3',border:'1px solid #D97706',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#92400E'}}>
        <strong>⚠️ Compliance Notice:</strong> This is an "assisted" DM tool. Messages are NEVER sent automatically — you copy and send them manually. This keeps your accounts 100% safe from platform bans. Full Instagram DM automation violates Meta's policies.
      </div>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontSize:14,fontWeight:700,color:C.text}}>DM Templates ({dmTemplates.length})</span>
        <div style={{display:'flex',gap:8}}>
          <button onClick={async()=>{ for(const t of STARTER_TEMPLATES){ if(!dmTemplates.find(x=>x.name===t.name)) await addDMTemplate(t); } }}
            style={{...btn('#6B7280'),fontSize:11,padding:'5px 12px'}}>📦 Load Starters</button>
          <button onClick={()=>{setEditTmpl(null);setForm(blank);setShowForm(true);}} style={btn(CONTENT_COLOR)}>+ New Template</button>
        </div>
      </div>

      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:'0 0 12px',color:CONTENT_COLOR}}>{editTmpl?'Edit Template':'New DM Template'}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="Template name *" style={{...inp(),width:'100%'}}/>
            <select value={form.platform} onChange={e=>upd('platform',e.target.value)} style={{...inp(),width:'100%'}}>
              {['instagram','linkedin','all'].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Trigger Type</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {TRIGGER_TYPES.map(t=>(
                <button key={t.id} onClick={()=>upd('trigger_type',t.id)}
                  style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1px solid ${form.trigger_type===t.id?CONTENT_COLOR:C.border}`,background:form.trigger_type===t.id?CONTENT_LIGHT:'transparent',color:form.trigger_type===t.id?CONTENT_COLOR:C.muted,cursor:'pointer'}}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {form.trigger_type==='keyword'&&(
            <input value={form.trigger_keyword} onChange={e=>upd('trigger_keyword',e.target.value)} placeholder='Trigger keyword (e.g. "GUIDE", "FREE", "LINK")' style={{...inp(),width:'100%',marginBottom:8}}/>
          )}
          <div style={{marginBottom:4,fontSize:11,color:C.muted}}>Message (use [Name] as placeholder)</div>
          <textarea value={form.message} onChange={e=>upd('message',e.target.value)} placeholder="Hi [Name]! 👋 ..." style={{...ta(120),width:'100%',marginBottom:8}}/>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(CONTENT_COLOR)}>💾 {editTmpl?'Update':'Save'}</button>
            <button onClick={()=>{setShowForm(false);setEditTmpl(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Templates grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
        {dmTemplates.length===0&&(
          <div style={{gridColumn:'1/-1',textAlign:'center',color:C.light,padding:40}}>
            No templates yet. Click "Load Starters" to add pre-written templates.
          </div>
        )}
        {dmTemplates.map(tmpl=>{
          const ttype = TRIGGER_TYPES.find(t=>t.id===tmpl.trigger_type);
          const platCfg = PLATFORM_CONFIG[tmpl.platform];
          return (
            <div key={tmpl.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:C.text}}>{tmpl.name}</div>
                  <div style={{display:'flex',gap:5,marginTop:3,flexWrap:'wrap'}}>
                    <span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:CONTENT_LIGHT,color:CONTENT_COLOR}}>{platCfg?.icon||'📱'} {tmpl.platform}</span>
                    <span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:C.surfaceAlt,color:C.muted}}>{ttype?.label||tmpl.trigger_type}</span>
                    {tmpl.trigger_keyword&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:'#DCFCE7',color:'#16A34A'}}>keyword: {tmpl.trigger_keyword}</span>}
                    {tmpl.use_count>0&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:'#FEF9C3',color:'#D97706'}}>used {tmpl.use_count}×</span>}
                  </div>
                </div>
                <div style={{display:'flex',gap:4}}>
                  <button onClick={()=>{setEditTmpl(tmpl);setForm({...tmpl});setShowForm(true);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:C.amber}}>✏️</button>
                  <button onClick={()=>{if(window.confirm('Delete?'))deleteDMTemplate(tmpl.id);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:C.light}}>✕</button>
                </div>
              </div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:10,background:C.surfaceAlt,borderRadius:6,padding:'8px 10px',whiteSpace:'pre-wrap'}}>
                {tmpl.message.slice(0,200)}{tmpl.message.length>200?'...':''}
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>copyToClipboard(tmpl)} style={{...btn(copied===tmpl.id?'#16A34A':'#6B7280'),flex:1,fontSize:11}}>
                  {copied===tmpl.id?'✅ Copied!':'📋 Copy'}
                </button>
                {tmpl.platform==='instagram'&&(
                  <button onClick={()=>{
                    const igInteg = (ctx.integrations||[]).find(i=>i.type==='instagram'&&i.is_active);
                    if(!igInteg){alert('Connect Instagram first in Integrations tab');return;}
                    const recipId = prompt('Enter the Instagram User ID of the recipient:\n(You can find it by going to their profile and using a tool like Find Instagram User ID)');
                    if(!recipId?.trim())return;
                    fetch('/api/sb/functions/v1/instagram-send-dm',{
                      method:'POST',
                      headers:{'Content-Type':'application/json','Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}`},
                      body:JSON.stringify({uid:ctx.uid,recipient_id:recipId.trim(),message:tmpl.message,template_id:tmpl.id})
                    }).then(r=>r.json()).then(d=>{
                      if(d.ok)ctx.setAlert?.('✅ DM sent via Instagram!');
                      else ctx.setAlert?.('❌ '+( d.error||'Send failed'));
                    }).catch(e=>ctx.setAlert?.('❌ '+e.message));
                  }} style={{...btn('#E1306C'),fontSize:11,padding:'7px 10px'}}>📤 Send DM</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 5 — VIRAL ENGINE: Shareable links + CTA builder + lead capture
// ══════════════════════════════════════════════════════════════════════════════
function ViralEngineTab({ ctx }) {
  const { viralLinks=[], contentPosts=[], addViralLink, updateViralLink, deleteViralLink } = ctx;
  const [showForm, setShowForm] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [copied,   setCopied]   = useState(null);
  const blank = { title:'', cta_keyword:'', lead_magnet:'', destination_url:'', slug:'', hook_keywords:[] };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.title.trim()) return;
    const slug = form.slug || form.cta_keyword.toLowerCase().replace(/\s+/g,'-') || `link-${Date.now().toString(36)}`;
    if (editLink) updateViralLink(editLink.id, { ...form, slug });
    else await addViralLink({ ...form, slug });
    setForm(blank); setShowForm(false); setEditLink(null);
  };

  const copyLink = (link) => {
    const url = `https://www.aspreo.com/l/${link.slug}`;
    navigator.clipboard.writeText(url).then(()=>{
      setCopied(link.id); setTimeout(()=>setCopied(null),2000);
    });
  };

  const copyCaption = (link) => {
    const caption = `Comment "${link.cta_keyword}" below and I'll send you ${link.lead_magnet} directly! 👇`;
    navigator.clipboard.writeText(caption).then(()=>{
      setCopied(link.id+'-cap'); setTimeout(()=>setCopied(null),2000);
    });
  };

  const totalClicks = viralLinks.reduce((s,l)=>s+(parseInt(l.clicks)||0),0);
  const totalLeads  = viralLinks.reduce((s,l)=>s+(parseInt(l.leads_captured)||0),0);

  return (
    <div>
      {/* KPIs */}
      {viralLinks.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
          {[
            { label:'Active Links',    value:viralLinks.filter(l=>l.is_active).length, color:CONTENT_COLOR, icon:'🔗' },
            { label:'Total Clicks',    value:totalClicks.toLocaleString(),              color:'#2563EB',     icon:'👆' },
            { label:'Leads Captured',  value:totalLeads.toLocaleString(),               color:'#16A34A',     icon:'📧' },
          ].map(k=>(
            <div key={k.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px',textAlign:'center'}}>
              <div style={{fontSize:22,marginBottom:4}}>{k.icon}</div>
              <div style={{fontSize:22,fontWeight:800,color:k.color}}>{k.value}</div>
              <div style={{fontSize:11,color:C.muted}}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontSize:14,fontWeight:700}}>Viral Links & CTAs ({viralLinks.length})</span>
        <button onClick={()=>{setEditLink(null);setForm(blank);setShowForm(true);}} style={btn(CONTENT_COLOR)}>+ New Link</button>
      </div>

      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${CONTENT_COLOR}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:'0 0 12px',color:CONTENT_COLOR}}>{editLink?'Edit Viral Link':'Create Viral Link'}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.title} onChange={e=>upd('title',e.target.value)} placeholder="Link name (internal) *" style={{...inp(),width:'100%'}}/>
            <input value={form.cta_keyword} onChange={e=>upd('cta_keyword',e.target.value)} placeholder='CTA keyword (e.g. "GUIDE", "FREE")' style={{...inp(),width:'100%'}}/>
            <input value={form.lead_magnet} onChange={e=>upd('lead_magnet',e.target.value)} placeholder='What they get (e.g. "my free PDF guide")' style={{...inp(),width:'100%'}}/>
            <input value={form.destination_url} onChange={e=>upd('destination_url',e.target.value)} placeholder="Destination URL (Notion, Drive, Gumroad...)" style={{...inp(),width:'100%'}}/>
            <input value={form.slug} onChange={e=>upd('slug',e.target.value)} placeholder="Custom slug (auto-generated if blank)" style={{...inp(),width:'100%'}}/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(CONTENT_COLOR)}>💾 {editLink?'Update':'Create'}</button>
            <button onClick={()=>{setShowForm(false);setEditLink(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {/* How it works */}
      {viralLinks.length===0&&(
        <div style={{background:C.surfaceAlt,borderRadius:12,padding:20,marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:12}}>🚀 How the Viral Engine Works</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
            {[
              { step:'1', title:'Create a CTA Link', desc:'Set a keyword like "GUIDE" and what people receive (your PDF, video, etc.)' },
              { step:'2', title:'Post on Social Media', desc:'Copy the generated caption: "Comment GUIDE and I\'ll send it to you!" — this drives comment engagement' },
              { step:'3', title:'DM the link manually', desc:'When someone comments, use the DM Engine to copy and send them the link — fully compliant!' },
            ].map(s=>(
              <div key={s.step} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14,textAlign:'center'}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:CONTENT_COLOR,color:'#fff',fontSize:16,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 8px'}}>{s.step}</div>
                <div style={{fontWeight:700,fontSize:12,color:C.text,marginBottom:4}}>{s.title}</div>
                <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Links list */}
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {viralLinks.map(link=>(
          <div key={link.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.text}}>{link.title}</div>
                {link.cta_keyword&&<div style={{fontSize:12,color:CONTENT_COLOR,fontWeight:600,marginTop:2}}>Keyword: "{link.cta_keyword}"</div>}
                {link.lead_magnet&&<div style={{fontSize:12,color:C.muted}}>Delivers: {link.lead_magnet}</div>}
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>{setEditLink(link);setForm({...link});setShowForm(true);}} style={{...btn(C.amber),fontSize:11,padding:'4px 8px'}}>✏️</button>
                <button onClick={()=>{if(window.confirm('Delete?'))deleteViralLink(link.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>🗑</button>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
              {[['👆','Clicks',link.clicks||0],['📧','Leads',link.leads_captured||0]].map(([i,l,v])=>(
                <div key={l} style={{background:C.surfaceAlt,borderRadius:6,padding:'6px',textAlign:'center'}}>
                  <div style={{fontSize:14}}>{i}</div>
                  <div style={{fontSize:16,fontWeight:800,color:CONTENT_COLOR}}>{v}</div>
                  <div style={{fontSize:9,color:C.muted}}>{l}</div>
                </div>
              ))}
              <div style={{background:C.surfaceAlt,borderRadius:6,padding:'6px',textAlign:'center'}}>
                <div style={{fontSize:14}}>📎</div>
                <div style={{fontSize:11,color:CONTENT_COLOR,fontWeight:600,wordBreak:'break-all'}}>aspreo.com/l/{link.slug}</div>
                <div style={{fontSize:9,color:C.muted}}>Short URL</div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
              <button onClick={()=>copyLink(link)} style={{...btn(copied===link.id?'#16A34A':CONTENT_COLOR),fontSize:11,padding:'6px'}}>
                {copied===link.id?'✅ Copied!':'📋 Copy Link'}
              </button>
              {link.cta_keyword&&(
                <button onClick={()=>copyCaption(link)} style={{...btn(copied===link.id+'-cap'?'#16A34A':'#6B3FA0'),fontSize:11,padding:'6px'}}>
                  {copied===link.id+'-cap'?'✅ Copied!':'📣 Copy Caption'}
                </button>
              )}
              {link.destination_url&&(
                <a href={link.destination_url} target="_blank" rel="noreferrer" style={{...btn('#6B7280'),fontSize:11,padding:'6px',textDecoration:'none',textAlign:'center'}}>
                  🔗 Open Destination
                </a>
              )}
            </div>

            {/* Generated caption preview */}
            {link.cta_keyword&&link.lead_magnet&&(
              <div style={{marginTop:10,background:'#F5F3FF',border:'1px solid #DDD6FE',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#5B21B6',lineHeight:1.6}}>
                <div style={{fontSize:10,fontWeight:700,marginBottom:4,color:CONTENT_COLOR}}>📋 SUGGESTED CAPTION (copy to your post):</div>
                Comment <strong>"{link.cta_keyword}"</strong> below and I'll send you {link.lead_magnet} directly! 👇<br/>
                ⬇️ Drop it in the comments right now!
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 6 — MEETING SCHEDULER (Calendly Clone)
// ══════════════════════════════════════════════════════════════════════════════
const MTG_COLOR  = '#0D9488';
const MTG_LIGHT  = '#F0FDFA';
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DAYS_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function MeetingScheduler({ ctx }) {
  const { meetingTypes=[], addMeetingType, updateMeetingType, deleteMeetingType,
          availability=[], saveAvailability,
          meetings=[], addMeeting, updateMeeting, deleteMeeting,
          uid } = ctx;
  const [tab, setTab] = useState('overview');

  const TABS = [
    { id:'overview',    label:'📋 Overview'     },
    { id:'types',       label:'🎯 Meeting Types' },
    { id:'availability',label:'🕐 Availability'  },
    { id:'bookings',    label:'📅 Bookings'      },
    { id:'booking_page',label:'🔗 Booking Page'  },
    { id:'setup',       label:'⚙️ Setup'          },
  ];

  const today = new Date();
  const upcoming   = meetings.filter(m => m.status==='confirmed' && new Date(m.start_time) >= today);
  const thisWeek   = upcoming.filter(m => {
    const d = new Date(m.start_time);
    const diff = (d - today) / 86400000;
    return diff <= 7;
  });
  const totalRevenue = meetings.filter(m=>m.status==='confirmed').reduce((s,m)=>{
    const mt = meetingTypes.find(t=>t.id===m.meeting_type_id);
    return s + (parseFloat(mt?.price)||0);
  },0);

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      <SectionMasthead eyebrow="System · Your booking page, built in" title={<>Time, <em>scheduled</em>.</>} />

      {/* Tabs */}
      <div style={{display:'flex',gap:4,background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',padding:4,marginBottom:16,overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            background:tab===t.id?MTG_COLOR:'transparent',color:tab===t.id?'#fff':C.muted,
            border:'none',borderRadius:7,padding:'6px 14px',cursor:'pointer',
            fontFamily:FONT,fontSize:12,fontWeight:tab===t.id?'bold':'normal',whiteSpace:'nowrap'
          }}>{t.label}</button>
        ))}
      </div>

      {tab==='overview'     && <MtgOverview     meetings={meetings} meetingTypes={meetingTypes} upcoming={upcoming} thisWeek={thisWeek} totalRevenue={totalRevenue} updateMeeting={updateMeeting} deleteMeeting={deleteMeeting} />}
      {tab==='types'        && <MtgTypesTab     meetingTypes={meetingTypes} addMeetingType={addMeetingType} updateMeetingType={updateMeetingType} deleteMeetingType={deleteMeetingType} />}
      {tab==='availability' && <AvailabilityTab availability={availability} saveAvailability={saveAvailability} />}
      {tab==='bookings'     && <BookingsTab     meetings={meetings} meetingTypes={meetingTypes} updateMeeting={updateMeeting} deleteMeeting={deleteMeeting} />}
      {tab==='booking_page' && <BookingPageTab  meetingTypes={meetingTypes} availability={availability} addMeeting={addMeeting} uid={uid} />}
      {tab==='setup'        && <MeetingSetupTab  ctx={ctx} />}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────
function MtgOverview({ meetings, meetingTypes, upcoming, thisWeek, totalRevenue, updateMeeting, deleteMeeting }) {
  return (
    <div>
      <div className="ta-grid-4" style={{marginBottom:20}}>
        {[
          { label:'Total Meetings',  value:meetings.length,               color:'var(--ta-primary)' },
          { label:'Upcoming',         value:upcoming.length,               color:'var(--ta-primary)' },
          { label:'This Week',        value:thisWeek.length,               color:'var(--ta-notice)' },
          { label:'Revenue',          value:`₹${totalRevenue.toLocaleString('en-IN')}`, color:'var(--ta-positive)' },
        ].map(k=>(
          <div key={k.label} className="ta-card">
            <div className="ta-section-head-meta" style={{marginBottom:8}}>{k.label}</div>
            <div className="ta-serif tnum" style={{fontSize:26,fontWeight:400,color:k.color}}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>Upcoming Meetings</div>
      {upcoming.length===0 && <div style={{textAlign:'center',color:C.light,padding:32}}>No upcoming meetings. Share your booking link to get started!</div>}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {upcoming.slice(0,10).map(m=>{
          const mt = meetingTypes.find(t=>t.id===m.meeting_type_id);
          const start = new Date(m.start_time);
          return (
            <div key={m.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`3px solid ${mt?.color||MTG_COLOR}`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
              <div style={{textAlign:'center',background:MTG_LIGHT,borderRadius:8,padding:'8px 12px',minWidth:52}}>
                <div style={{fontSize:18,fontWeight:800,color:MTG_COLOR}}>{start.getDate()}</div>
                <div style={{fontSize:10,color:C.muted}}>{start.toLocaleDateString('en-IN',{month:'short'})}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13}}>{m.guest_name}</div>
                <div style={{fontSize:12,color:C.muted}}>{m.guest_email}</div>
                <div style={{fontSize:11,color:MTG_COLOR,marginTop:2}}>
                  {mt?.title||'Meeting'} · {start.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                  {mt?.duration_mins && ` · ${mt.duration_mins} min`}
                </div>
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>updateMeeting(m.id,{status:'completed'})} style={{...btn('#16A34A'),fontSize:11,padding:'4px 8px'}}>✅ Done</button>
                <button onClick={()=>updateMeeting(m.id,{status:'cancelled'})} style={{...btn('#DC2626'),fontSize:11,padding:'4px 8px'}}>✕ Cancel</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Meeting Types ─────────────────────────────────────────────────────────────
function MtgTypesTab({ meetingTypes, addMeetingType, updateMeetingType, deleteMeetingType }) {
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { title:'', description:'', duration_mins:30, price:0, color:MTG_COLOR, buffer_mins:15, questions:[] };
  const [form, setForm] = useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const [newQ, setNewQ] = useState('');
  const addQ  = () => { if(!newQ.trim()) return; upd('questions',[...form.questions,{id:Date.now(),label:newQ.trim(),required:false}]); setNewQ(''); };
  const remQ  = (id) => upd('questions', form.questions.filter(q=>q.id!==id));

  const save = async () => {
    if (!form.title.trim()) return;
    if (edit) updateMeetingType(edit.id, form);
    else await addMeetingType(form);
    setForm(blank); setShowForm(false); setEdit(null);
  };

  const DURATIONS = [15,20,30,45,60,90,120];
  const STARTER_TYPES = [
    { title:'Discovery Call', description:'A free 30-min call to explore how I can help you.', duration_mins:30, price:0, color:'#2563EB', buffer_mins:15, questions:[{id:1,label:"What's your main challenge right now?",required:true}] },
    { title:'Coaching Session', description:'Deep-dive 1:1 coaching to accelerate your growth.', duration_mins:60, price:2999, color:MTG_COLOR, buffer_mins:15, questions:[{id:2,label:"What outcome do you want from this session?",required:true},{id:3,label:"Any specific topics to cover?",required:false}] },
    { title:'Consultation', description:'90-min strategy consultation for your business.', duration_mins:90, price:4999, color:'#7C3AED', buffer_mins:30, questions:[{id:4,label:"Describe your current situation",required:true}] },
  ];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontWeight:700,fontSize:14}}>Meeting Types ({meetingTypes.length})</span>
        <div style={{display:'flex',gap:8}}>
          {meetingTypes.length===0&&(
            <button onClick={async()=>{ for(const t of STARTER_TYPES) await addMeetingType(t); }}
              style={{...btn('#6B7280'),fontSize:12,padding:'5px 12px'}}>📦 Load Starters</button>
          )}
          <button onClick={()=>{setEdit(null);setForm(blank);setShowForm(true);}} style={btn(MTG_COLOR)}>+ New Type</button>
        </div>
      </div>

      {showForm && (
        <div style={{background:C.surface,border:`1.5px solid ${MTG_COLOR}`,borderRadius:12,padding:16,marginBottom:16}}>
          <h4 style={{margin:'0 0 12px',color:MTG_COLOR}}>{edit?'Edit Meeting Type':'New Meeting Type'}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.title} onChange={e=>upd('title',e.target.value)} placeholder="Title (e.g. Discovery Call) *" style={{...inp(),width:'100%'}}/>
            <select value={form.duration_mins} onChange={e=>upd('duration_mins',parseInt(e.target.value))} style={{...inp(),width:'100%'}}>
              {DURATIONS.map(d=><option key={d} value={d}>{d} minutes</option>)}
            </select>
            <input value={form.price} onChange={e=>upd('price',parseFloat(e.target.value)||0)} type="number" placeholder="Price (₹0 = free)" style={{...inp(),width:'100%'}}/>
            <select value={form.buffer_mins} onChange={e=>upd('buffer_mins',parseInt(e.target.value))} style={{...inp(),width:'100%'}}>
              {[0,10,15,20,30].map(b=><option key={b} value={b}>{b===0?'No buffer':`${b} min buffer`}</option>)}
            </select>
            <input value={form.color} onChange={e=>upd('color',e.target.value)} type="color" style={{...inp(),width:'100%',height:38,padding:4}}/>
          </div>
          <textarea value={form.description} onChange={e=>upd('description',e.target.value)} placeholder="Description shown on booking page..." style={{...ta(60),width:'100%',marginBottom:10}}/>

          {/* Custom Questions */}
          <div style={{marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,marginBottom:6}}>Custom Questions (shown to guest on booking)</div>
            {form.questions.map(q=>(
              <div key={q.id} style={{display:'flex',gap:6,alignItems:'center',marginBottom:5}}>
                <div style={{flex:1,fontSize:12,background:C.surfaceAlt,padding:'6px 10px',borderRadius:6}}>{q.label}</div>
                <button onClick={()=>remQ(q.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.light}}>✕</button>
              </div>
            ))}
            <div style={{display:'flex',gap:6}}>
              <input value={newQ} onChange={e=>setNewQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addQ()} placeholder="Add a question..." style={{...inp(),flex:1,fontSize:12}}/>
              <button onClick={addQ} style={btn(MTG_COLOR)}>+ Add</button>
            </div>
          </div>

          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(MTG_COLOR)}>💾 {edit?'Update':'Save'}</button>
            <button onClick={()=>{setShowForm(false);setEdit(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
        {meetingTypes.map(mt=>(
          <div key={mt.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderLeft:`4px solid ${mt.color||MTG_COLOR}`,borderRadius:10,padding:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.text}}>{mt.title}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{mt.description}</div>
              </div>
              <div style={{display:'flex',gap:4}}>
                <button onClick={()=>{setEdit(mt);setForm({...mt,questions:mt.questions||[]});setShowForm(true);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:C.amber}}>✏️</button>
                <button onClick={()=>{if(window.confirm('Delete?'))deleteMeetingType(mt.id);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:C.light}}>🗑</button>
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:mt.color+'22',color:mt.color,fontWeight:600}}>⏱ {mt.duration_mins} min</span>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:'#F0FDF4',color:'#16A34A',fontWeight:600}}>{mt.price>0?`₹${mt.price}`:'Free'}</span>
              {mt.buffer_mins>0&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:C.surfaceAlt,color:C.muted}}>{mt.buffer_mins}m buffer</span>}
              {(mt.questions||[]).length>0&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:'#EFF6FF',color:'#2563EB'}}>{mt.questions.length} questions</span>}
            </div>
          </div>
        ))}
        {meetingTypes.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',color:C.light,padding:40}}>No meeting types yet. Click "Load Starters" or create your own.</div>}
      </div>
    </div>
  );
}

// ── Availability ──────────────────────────────────────────────────────────────
function AvailabilityTab({ availability, saveAvailability }) {
  const getSlot = (day) => availability.find(s=>s.day_of_week===day) || { day_of_week:day, start_time:'09:00', end_time:'18:00', is_active:false };
  const [slots, setSlots] = useState(() => [0,1,2,3,4,5,6].map(d=>getSlot(d)));
  const [saving, setSaving] = useState(false);

  // Sync when availability loads from DB
  React.useEffect(() => {
    if (availability.length > 0) {
      setSlots([0,1,2,3,4,5,6].map(d => availability.find(s=>s.day_of_week===d) || { day_of_week:d, start_time:'09:00', end_time:'18:00', is_active:false }));
    }
  }, [availability.length]);

  const updSlot = (day, field, val) => setSlots(p => p.map(s => s.day_of_week===day ? {...s,[field]:val} : s));

  const save = async () => {
    setSaving(true);
    await saveAvailability(slots.filter(s=>s.is_active));
    setSaving(false);
    alert('✅ Availability saved!');
  };

  const PRESET_HOURS = ['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontWeight:700,fontSize:14}}>Working Hours</div>
          <div style={{fontSize:12,color:C.muted}}>Set when you're available for meetings each day</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>setSlots(p=>p.map(s=>s.day_of_week>=1&&s.day_of_week<=5?{...s,is_active:true,start_time:'09:00',end_time:'18:00'}:s))} style={{...btn('#6B7280'),fontSize:12}}>Set Mon–Fri</button>
          <button onClick={save} disabled={saving} style={{...btn(MTG_COLOR),opacity:saving?0.6:1}}>{saving?'Saving...':'💾 Save'}</button>
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {slots.map(slot=>(
          <div key={slot.day_of_week} style={{display:'flex',alignItems:'center',gap:12,background:C.surface,border:`1px solid ${slot.is_active?MTG_COLOR:C.border}`,borderRadius:10,padding:'10px 16px'}}>
            <div style={{width:80,fontWeight:600,fontSize:13,color:slot.is_active?MTG_COLOR:C.muted}}>{DAYS_FULL[slot.day_of_week]}</div>
            <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer'}}>
              <input type="checkbox" checked={slot.is_active} onChange={e=>updSlot(slot.day_of_week,'is_active',e.target.checked)} style={{accentColor:MTG_COLOR,width:16,height:16}}/>
              <span style={{fontSize:12,color:C.muted}}>{slot.is_active?'Available':'Off'}</span>
            </label>
            {slot.is_active && (
              <>
                <select value={slot.start_time} onChange={e=>updSlot(slot.day_of_week,'start_time',e.target.value)} style={{...inp(),width:100,fontSize:12}}>
                  {PRESET_HOURS.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{color:C.muted,fontSize:12}}>to</span>
                <select value={slot.end_time} onChange={e=>updSlot(slot.day_of_week,'end_time',e.target.value)} style={{...inp(),width:100,fontSize:12}}>
                  {PRESET_HOURS.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{fontSize:12,color:C.muted}}>
                  ({Math.round((parseInt(slot.end_time)-parseInt(slot.start_time)))} hrs)
                </span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bookings List ─────────────────────────────────────────────────────────────
function BookingsTab({ meetings, meetingTypes, updateMeeting, deleteMeeting }) {
  const [filter, setFilter] = useState('all');
  const STATUS_COLORS = { confirmed:'#16A34A', cancelled:'#DC2626', completed:'#2563EB', rescheduled:'#D97706' };
  const filtered = filter==='all' ? meetings : meetings.filter(m=>m.status===filter);
  const sorted = [...filtered].sort((a,b)=>new Date(b.start_time)-new Date(a.start_time));

  return (
    <div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {['all','confirmed','completed','cancelled'].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{fontSize:11,padding:'4px 12px',borderRadius:20,border:'none',cursor:'pointer',
            background:filter===s?MTG_COLOR:'#e5e7eb',color:filter===s?'#fff':C.muted,fontWeight:filter===s?700:400}}>
            {s==='all'?`All (${meetings.length})`:s.charAt(0).toUpperCase()+s.slice(1)+` (${meetings.filter(m=>m.status===s).length})`}
          </button>
        ))}
      </div>

      {sorted.length===0&&<div style={{textAlign:'center',color:C.light,padding:40}}>No meetings found.</div>}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {sorted.map(m=>{
          const mt = meetingTypes.find(t=>t.id===m.meeting_type_id);
          const start = new Date(m.start_time);
          const sc = STATUS_COLORS[m.status]||'#6B7280';
          return (
            <div key={m.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',display:'flex',gap:12,alignItems:'center'}}>
              <div style={{textAlign:'center',background:sc+'18',borderRadius:8,padding:'8px',minWidth:48}}>
                <div style={{fontSize:16,fontWeight:800,color:sc}}>{start.getDate()}</div>
                <div style={{fontSize:10,color:C.muted}}>{start.toLocaleDateString('en-IN',{month:'short'})}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13}}>{m.guest_name}
                  <span style={{fontSize:10,padding:'1px 6px',borderRadius:10,background:sc+'22',color:sc,marginLeft:8,fontWeight:600}}>{m.status}</span>
                </div>
                <div style={{fontSize:12,color:C.muted}}>{m.guest_email}</div>
                <div style={{fontSize:11,color:MTG_COLOR,marginTop:2}}>
                  {mt?.title} · {start.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} — {new Date(m.end_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                </div>
                {m.notes&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>📝 {m.notes}</div>}
              </div>
              <div style={{display:'flex',gap:5}}>
                {m.status==='confirmed'&&<>
                  <button onClick={()=>updateMeeting(m.id,{status:'completed'})} style={{...btn('#16A34A'),fontSize:10,padding:'3px 7px'}}>✓ Done</button>
                  <button onClick={()=>updateMeeting(m.id,{status:'cancelled'})} style={{...btn('#DC2626'),fontSize:10,padding:'3px 7px'}}>✕</button>
                </>}
                <button onClick={()=>{if(window.confirm('Delete permanently?'))deleteMeeting(m.id);}} style={{...btn('#9CA3AF'),fontSize:10,padding:'3px 7px'}}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Booking Page (Live Preview + Demo) ───────────────────────────────────────
function BookingPageTab({ meetingTypes, availability, addMeeting }) {
  const [step,         setStep]         = useState(0);   // 0=type, 1=date, 2=slot, 3=form, 4=confirm
  const [selType,      setSelType]      = useState(null);
  const [selDate,      setSelDateB]     = useState('');
  const [selSlot,      setSelSlot]      = useState(null);
  const [guestForm,    setGuestForm]    = useState({ guest_name:'', guest_email:'', notes:'' });
  const [answers,      setAnswers]      = useState({});
  const [booking,      setBooking]      = useState(false);
  const [confirmed,    setConfirmed]    = useState(null);
  const upd = (k,v) => setGuestForm(p=>({...p,[k]:v}));

  const activeMeetingTypes = meetingTypes.filter(t=>t.is_active);

  // Generate time slots for a date based on availability
  const getSlotsForDate = (dateStr, meetingType) => {
    if (!dateStr || !meetingType) return [];
    const d = new Date(dateStr);
    const dow = d.getDay();
    const avail = availability.find(a=>a.day_of_week===dow);
    if (!avail || !avail.is_active) return [];
    const slots = [];
    const [sh,sm] = avail.start_time.split(':').map(Number);
    const [eh,em] = avail.end_time.split(':').map(Number);
    let cur = sh*60+sm;
    const end = eh*60+em;
    const dur = meetingType.duration_mins + (meetingType.buffer_mins||0);
    while (cur + meetingType.duration_mins <= end) {
      const hh = String(Math.floor(cur/60)).padStart(2,'0');
      const mm = String(cur%60).padStart(2,'0');
      slots.push(`${hh}:${mm}`);
      cur += dur;
    }
    return slots;
  };

  // Next 14 available days
  const availableDays = React.useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i=1; i<=30 && days.length<14; i++) {
      const d = new Date(now); d.setDate(now.getDate()+i);
      const dow = d.getDay();
      const avail = availability.find(a=>a.day_of_week===dow);
      if (avail?.is_active) days.push(d.toISOString().split('T')[0]);
    }
    return days;
  }, [availability]);

  const slots = getSlotsForDate(selDate, selType);

  const confirm = async () => {
    if (!selType||!selDate||!selSlot||!guestForm.guest_name.trim()||!guestForm.guest_email.trim()) return;
    setBooking(true);
    const [h,m] = selSlot.split(':').map(Number);
    const start = new Date(`${selDate}T${selSlot}:00`);
    const end   = new Date(start.getTime() + selType.duration_mins*60000);
    const meeting = {
      meeting_type_id: selType.id,
      guest_name:      guestForm.guest_name,
      guest_email:     guestForm.guest_email,
      guest_answers:   answers,
      notes:           guestForm.notes,
      start_time:      start.toISOString(),
      end_time:        end.toISOString(),
      status:          'confirmed',
    };
    const result = await addMeeting(meeting);
    const booked = result || meeting;
    setConfirmed(booked);

    // Auto-invoice if meeting type has a price
    if (selType.price > 0 && booked.id) {
      try {
        const shareToken = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
        const invPayload = {
          issue_date:      start.toISOString().split('T')[0],
          due_date:        start.toISOString().split('T')[0],
          items:           [{ name: selType.title, description: `Meeting on ${start.toLocaleDateString('en-IN')}`, qty: 1, rate: selType.price, amount: selType.price }],
          subtotal:        selType.price,
          tax_pct:         0,
          tax_amount:      0,
          total:           selType.price,
          status:          'sent',
          notes:           `Auto-generated invoice for booking: ${selType.title}`,
          share_token:     shareToken,
          is_shared:       true,
          // Store guest details as client info in notes
          biz_client_name:  guestForm.guest_name,
          biz_client_email: guestForm.guest_email,
          meeting_id:       booked.id,
        };
        // addInvoice is not directly available here — use supabase directly
        await supabase.from('invoices').insert({ ...invPayload, status: 'sent' }).select();
      } catch(e) { console.warn('[auto-invoice booking]', e); }
    }

    setBooking(false);
    setStep(4);
  };

  return (
    <div>
      <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#1D4ED8'}}>
        🔗 <strong>Your booking page:</strong> Once deployed, guests can book at <strong>aspreo.com/book/[your-username]</strong>. Below is a live preview of the booking experience.
      </div>

      {/* Booking widget */}
      <div style={{background:C.surface,border:`1.5px solid ${MTG_COLOR}`,borderRadius:16,padding:24,maxWidth:520,margin:'0 auto'}}>
        {/* Progress */}
        <div style={{display:'flex',justifyContent:'center',gap:6,marginBottom:20}}>
          {['Type','Date','Time','Details','Done'].map((s,i)=>(
            <div key={s} style={{display:'flex',alignItems:'center',gap:6}}>
              <div style={{width:22,height:22,borderRadius:'50%',background:i<=step?MTG_COLOR:'#e5e7eb',color:i<=step?'#fff':'#9ca3af',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>{i+1}</div>
              {i<4&&<div style={{width:20,height:2,background:i<step?MTG_COLOR:'#e5e7eb'}}/>}
            </div>
          ))}
        </div>

        {/* Step 0: Select Type */}
        {step===0&&(
          <div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:14,textAlign:'center',color:MTG_COLOR}}>Select Meeting Type</div>
            {activeMeetingTypes.length===0&&<div style={{textAlign:'center',color:C.light,padding:24}}>Create meeting types first in the "Meeting Types" tab.</div>}
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {activeMeetingTypes.map(mt=>(
                <div key={mt.id} onClick={()=>{setSelType(mt);setStep(1);}}
                  style={{background:C.surfaceAlt,border:`2px solid ${selType?.id===mt.id?mt.color:C.border}`,borderRadius:10,padding:'12px 16px',cursor:'pointer',transition:'border .15s'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:mt.color||MTG_COLOR}}>{mt.title}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:2}}>{mt.description}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:12,fontWeight:700}}>{mt.duration_mins} min</div>
                      <div style={{fontSize:11,color:'#16A34A'}}>{mt.price>0?`₹${mt.price}`:'Free'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Select Date */}
        {step===1&&(
          <div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:14,textAlign:'center',color:MTG_COLOR}}>Select a Date</div>
            {availableDays.length===0&&<div style={{textAlign:'center',color:C.light,padding:24}}>Set your availability first in the "Availability" tab.</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6}}>
              {availableDays.map(d=>{
                const dt = new Date(d+'T12:00:00');
                return (
                  <div key={d} onClick={()=>{setSelDateB(d);setStep(2);}}
                    style={{background:selDate===d?MTG_COLOR:C.surfaceAlt,border:`1px solid ${selDate===d?MTG_COLOR:C.border}`,borderRadius:8,padding:'8px',textAlign:'center',cursor:'pointer'}}>
                    <div style={{fontSize:10,color:selDate===d?'rgba(255,255,255,.8)':C.muted}}>{DAYS_SHORT[dt.getDay()]}</div>
                    <div style={{fontSize:16,fontWeight:800,color:selDate===d?'#fff':C.text}}>{dt.getDate()}</div>
                    <div style={{fontSize:10,color:selDate===d?'rgba(255,255,255,.7)':C.muted}}>{dt.toLocaleDateString('en-IN',{month:'short'})}</div>
                  </div>
                );
              })}
            </div>
            <button onClick={()=>setStep(0)} style={{...btn('#6B7280'),marginTop:12,fontSize:12}}>← Back</button>
          </div>
        )}

        {/* Step 2: Select Slot */}
        {step===2&&(
          <div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:4,textAlign:'center',color:MTG_COLOR}}>Select a Time</div>
            <div style={{textAlign:'center',color:C.muted,fontSize:12,marginBottom:14}}>{new Date(selDate+'T12:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</div>
            {slots.length===0&&<div style={{textAlign:'center',color:C.light,padding:24}}>No slots available for this day.</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:6}}>
              {slots.map(s=>(
                <div key={s} onClick={()=>{setSelSlot(s);setStep(3);}}
                  style={{background:selSlot===s?MTG_COLOR:C.surfaceAlt,border:`1px solid ${selSlot===s?MTG_COLOR:C.border}`,borderRadius:8,padding:'8px',textAlign:'center',cursor:'pointer',color:selSlot===s?'#fff':C.text,fontWeight:600,fontSize:13}}>
                  {s}
                </div>
              ))}
            </div>
            <button onClick={()=>setStep(1)} style={{...btn('#6B7280'),marginTop:12,fontSize:12}}>← Back</button>
          </div>
        )}

        {/* Step 3: Guest Details */}
        {step===3&&(
          <div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:4,textAlign:'center',color:MTG_COLOR}}>Your Details</div>
            <div style={{textAlign:'center',fontSize:11,color:C.muted,marginBottom:14}}>{selType?.title} · {selDate} at {selSlot}</div>
            <input value={guestForm.guest_name}  onChange={e=>upd('guest_name',e.target.value)}  placeholder="Full Name *" style={{...inp(),width:'100%',marginBottom:8}}/>
            <input value={guestForm.guest_email} onChange={e=>upd('guest_email',e.target.value)} placeholder="Email *"     style={{...inp(),width:'100%',marginBottom:8}}/>
            {(selType?.questions||[]).map(q=>(
              <div key={q.id} style={{marginBottom:8}}>
                <div style={{fontSize:12,color:C.text,marginBottom:4}}>{q.label}{q.required&&<span style={{color:C.red}}> *</span>}</div>
                <input value={answers[q.id]||''} onChange={e=>setAnswers(p=>({...p,[q.id]:e.target.value}))} style={{...inp(),width:'100%'}}/>
              </div>
            ))}
            <textarea value={guestForm.notes} onChange={e=>upd('notes',e.target.value)} placeholder="Any notes or questions? (optional)" style={{...ta(60),width:'100%',marginBottom:12}}/>
            <button onClick={confirm} disabled={!guestForm.guest_name.trim()||!guestForm.guest_email.trim()||booking}
              style={{...btn(MTG_COLOR),width:'100%',fontSize:13,padding:'10px',opacity:booking?0.6:1}}>
              {booking?'Booking...':'✅ Confirm Booking'}
            </button>
            <button onClick={()=>setStep(2)} style={{...btn('#6B7280'),width:'100%',marginTop:8,fontSize:12}}>← Back</button>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step===4&&(
          <div style={{textAlign:'center',padding:'20px 0'}}>
            <div style={{fontSize:48,marginBottom:12}}>🎉</div>
            <div style={{fontSize:18,fontWeight:800,color:MTG_COLOR,marginBottom:6}}>Booking Confirmed!</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:16}}>
              A confirmation email will be sent to {guestForm.guest_email}
            </div>
            <div style={{background:MTG_LIGHT,border:`1px solid ${MTG_COLOR}30`,borderRadius:10,padding:'12px 16px',marginBottom:16,textAlign:'left'}}>
              <div style={{fontWeight:700,color:MTG_COLOR}}>{selType?.title}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>{selDate} at {selSlot}</div>
              <div style={{fontSize:12,color:C.muted}}>{guestForm.guest_name} · {guestForm.guest_email}</div>
            </div>
            <button onClick={()=>{setStep(0);setSelType(null);setSelDateB('');setSelSlot(null);setGuestForm({guest_name:'',guest_email:'',notes:''});setAnswers({});setConfirmed(null);}}
              style={btn(MTG_COLOR)}>Book Another Meeting</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE 7 — UNIFIED CALENDAR
// ══════════════════════════════════════════════════════════════════════════════
function UnifiedCalendar({ ctx }) {
  const { tasks={}, contentPosts=[], meetings=[], meetingTypes=[], setSelDate, setView: ctxSetView } = ctx;
  const [viewMode,  setViewMode]  = useState('month'); // month | week
  const [curDate,   setCurDate]   = useState(new Date());
  const [filters,   setFilters]   = useState({ tasks:true, posts:true, meetings:true });
  const [selDay,    setSelDay]    = useState(null);

  const toggleFilter = (k) => setFilters(p=>({...p,[k]:!p[k]}));

  // Build a flat event list from all sources
  const allEvents = React.useMemo(() => {
    const evts = [];
    // Tasks
    if (filters.tasks) {
      Object.entries(tasks).forEach(([date, arr]) => {
        (arr||[]).filter(t=>!t.done).forEach(t => {
          evts.push({ id:`t-${t.id}`, type:'task', date, title:t.text, color:'#D97706', bg:'#FFFBEB', time:t.task_time||null });
        });
      });
    }
    // Content posts
    if (filters.posts) {
      contentPosts.filter(p=>p.scheduledDate||p.scheduled_date||p.scheduled_at).forEach(p => {
        const raw = p.scheduledDate||p.scheduled_date||p.scheduled_at||'';
        const date = raw.split('T')[0];
        if (!date) return;
        evts.push({ id:`p-${p.id}`, type:'post', date, title:p.title, platform:p.platform, color:CONTENT_COLOR, bg:CONTENT_LIGHT });
      });
    }
    // Meetings
    if (filters.meetings) {
      meetings.filter(m=>m.status==='confirmed'||m.status==='completed').forEach(m => {
        const date = m.start_time?.split('T')[0];
        if (!date) return;
        const mt = meetingTypes.find(t=>t.id===m.meeting_type_id);
        const timeStr = new Date(m.start_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
        evts.push({ id:`m-${m.id}`, type:'meeting', date, title:`${m.guest_name} — ${mt?.title||'Meeting'}`, color:MTG_COLOR, bg:MTG_LIGHT, time:timeStr });
      });
    }
    return evts;
  }, [tasks, contentPosts, meetings, filters]);

  const getEventsForDate = (dateStr) => allEvents.filter(e=>e.date===dateStr);

  // Month view helpers
  const year  = curDate.getFullYear();
  const month = curDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const todayStr = new Date().toISOString().split('T')[0];

  const prevMonth = () => setCurDate(new Date(year, month-1, 1));
  const nextMonth = () => setCurDate(new Date(year, month+1, 1));

  // Week view helpers
  const startOfWeek = (d) => { const s = new Date(d); s.setDate(d.getDate() - d.getDay()); return s; };
  const weekStart = startOfWeek(curDate);
  const weekDays = [...Array(7)].map((_,i) => { const d=new Date(weekStart); d.setDate(weekStart.getDate()+i); return d; });

  const prevWeek = () => setCurDate(d => { const n=new Date(d); n.setDate(d.getDate()-7); return n; });
  const nextWeek = () => setCurDate(d => { const n=new Date(d); n.setDate(d.getDate()+7); return n; });

  const EVENT_TYPE_COLORS = { task:'#D97706', post:CONTENT_COLOR, meeting:MTG_COLOR };

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <div>
          <h2 style={{margin:'0 0 2px',color:'#2563EB'}}>🗓 Unified Calendar</h2>
          <div style={{fontSize:12,color:C.muted}}>Tasks · Content · Meetings — all in one view</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {/* Filters */}
          {[
            { key:'tasks',    label:'📋 Tasks',    color:'#D97706' },
            { key:'posts',    label:'✍️ Posts',    color:CONTENT_COLOR },
            { key:'meetings', label:'📆 Meetings', color:MTG_COLOR },
          ].map(f=>(
            <button key={f.key} onClick={()=>toggleFilter(f.key)} style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1.5px solid ${filters[f.key]?f.color:C.border}`,background:filters[f.key]?f.color+'18':'transparent',color:filters[f.key]?f.color:C.muted,cursor:'pointer',fontWeight:filters[f.key]?700:400}}>
              {f.label}
            </button>
          ))}
          {/* View toggle */}
          <div style={{display:'flex',background:C.surfaceAlt,borderRadius:8,padding:3,gap:2}}>
            {['month','week'].map(v=>(
              <button key={v} onClick={()=>setViewMode(v)} style={{fontSize:11,padding:'4px 10px',borderRadius:6,border:'none',background:viewMode===v?'#2563EB':'transparent',color:viewMode===v?'#fff':C.muted,cursor:'pointer',fontWeight:viewMode===v?700:400}}>
                {v.charAt(0).toUpperCase()+v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <button onClick={viewMode==='month'?prevMonth:prevWeek} style={{...btn('#6B7280'),padding:'6px 12px',fontSize:13}}>‹ Prev</button>
        <div style={{fontWeight:700,fontSize:15,color:C.text}}>
          {viewMode==='month'
            ? curDate.toLocaleDateString('en-IN',{month:'long',year:'numeric'})
            : `${weekDays[0].toLocaleDateString('en-IN',{day:'numeric',month:'short'})} – ${weekDays[6].toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}`
          }
        </div>
        <button onClick={viewMode==='month'?nextMonth:nextWeek} style={{...btn('#6B7280'),padding:'6px 12px',fontSize:13}}>Next ›</button>
      </div>

      {/* Event count summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:14}}>
        {[
          { label:'Tasks this view',    value:allEvents.filter(e=>e.type==='task').length,    color:'#D97706' },
          { label:'Posts this view',    value:allEvents.filter(e=>e.type==='post').length,    color:CONTENT_COLOR },
          { label:'Meetings this view', value:allEvents.filter(e=>e.type==='meeting').length, color:MTG_COLOR },
        ].map(k=>(
          <div key={k.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 12px',display:'flex',gap:8,alignItems:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:k.color}}>{k.value}</div>
            <div style={{fontSize:11,color:C.muted}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* MONTH VIEW */}
      {viewMode==='month'&&(
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
          {/* Day headers */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'#2563EB'}}>
            {DAYS_SHORT.map(d=><div key={d} style={{padding:'8px',textAlign:'center',fontSize:11,fontWeight:700,color:'#fff'}}>{d}</div>)}
          </div>
          {/* Grid */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
            {/* Empty cells before month starts */}
            {[...Array(firstDay)].map((_,i)=><div key={`e-${i}`} style={{minHeight:90,background:'#FAFAFA',borderRight:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`}}/>)}
            {/* Day cells */}
            {[...Array(daysInMonth)].map((_,i)=>{
              const dayNum = i+1;
              const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
              const dayEvts = getEventsForDate(dateStr);
              const isToday = dateStr===todayStr;
              const isSel   = dateStr===selDay;
              return (
                <div key={dayNum} onClick={()=>setSelDay(isSel?null:dateStr)}
                  style={{minHeight:90,padding:4,border:`1px solid ${C.border}`,cursor:'pointer',background:isSel?'#EFF6FF':isToday?'#FFFBEB':'transparent',position:'relative',transition:'background .1s'}}>
                  <div style={{width:22,height:22,borderRadius:'50%',background:isToday?'#2563EB':'transparent',color:isToday?'#fff':C.text,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:isToday?800:400,marginBottom:2}}>
                    {dayNum}
                  </div>
                  {dayEvts.slice(0,3).map(e=>(
                    <div key={e.id} style={{fontSize:9,padding:'1px 4px',borderRadius:3,background:e.color,color:'#fff',marginBottom:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',lineHeight:1.5}}>
                      {e.time&&<span style={{opacity:.8}}>{e.time} </span>}{e.title}
                    </div>
                  ))}
                  {dayEvts.length>3&&<div style={{fontSize:9,color:C.muted,padding:'0 2px'}}>+{dayEvts.length-3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode==='week'&&(
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'#2563EB'}}>
            {weekDays.map(d=>{
              const ds = d.toISOString().split('T')[0];
              const isT = ds===todayStr;
              return (
                <div key={ds} style={{padding:'10px 6px',textAlign:'center',borderRight:`1px solid rgba(255,255,255,.1)`}}>
                  <div style={{fontSize:10,color:'rgba(255,255,255,.7)',fontWeight:600}}>{DAYS_SHORT[d.getDay()]}</div>
                  <div style={{width:28,height:28,borderRadius:'50%',background:isT?'#fff':'transparent',color:isT?'#2563EB':'#fff',fontSize:15,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',margin:'2px auto 0'}}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',minHeight:300}}>
            {weekDays.map(d=>{
              const ds = d.toISOString().split('T')[0];
              const dayEvts = getEventsForDate(ds);
              return (
                <div key={ds} style={{padding:6,borderRight:`1px solid ${C.border}`,minHeight:300}}>
                  {dayEvts.map(e=>(
                    <div key={e.id} style={{fontSize:10,padding:'4px 6px',borderRadius:5,background:e.bg,borderLeft:`3px solid ${e.color}`,marginBottom:5,lineHeight:1.4}}>
                      {e.time&&<div style={{fontSize:9,color:e.color,fontWeight:700}}>{e.time}</div>}
                      <div style={{fontWeight:600,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.title}</div>
                      {e.platform&&<div style={{fontSize:9,color:C.muted}}>{e.platform}</div>}
                    </div>
                  ))}
                  {dayEvts.length===0&&<div style={{color:C.light,fontSize:10,textAlign:'center',marginTop:20}}>—</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected day detail panel */}
      {selDay&&(()=>{
        const dayEvts = getEventsForDate(selDay);
        return (
          <div style={{marginTop:12,background:C.surface,border:`1px solid ${'#2563EB'}30`,borderRadius:10,padding:14}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontWeight:700,fontSize:13,color:'#2563EB'}}>{new Date(selDay+'T12:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</div>
              <button onClick={()=>setSelDay(null)} style={{background:'none',border:'none',cursor:'pointer',color:C.muted}}>✕</button>
            </div>
            {dayEvts.length===0&&<div style={{color:C.light,fontSize:12}}>Nothing scheduled on this day.</div>}
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {[...dayEvts].sort((a,b)=>(a.time||'').localeCompare(b.time||'')).map(e=>(
                <div key={e.id} style={{display:'flex',gap:10,alignItems:'center',padding:'7px 10px',background:e.bg,borderRadius:7,borderLeft:`3px solid ${e.color}`}}>
                  <span style={{fontSize:11,color:e.color,fontWeight:700,minWidth:40}}>{e.time||'—'}</span>
                  <span style={{fontSize:12,color:C.text,flex:1}}>{e.title}</span>
                  <span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:e.color+'22',color:e.color}}>{e.type}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MEETING SETUP TAB — Booking page config + Google Calendar connect
// ══════════════════════════════════════════════════════════════════════════════
function MeetingSetupTab({ ctx }) {
  const C = useC();
  const { uid } = ctx;
  const [profile,  setProfile]  = React.useState({ booking_username:'', booking_headline:'', booking_bio:'', booking_enabled:false });
  const [saving,   setSaving]   = React.useState(false);
  const [saved,    setSaved]    = React.useState(false);
  const [gcalConn, setGcalConn] = React.useState(false);
  const upd = (k,v) => setProfile(p=>({...p,[k]:v}));

  React.useEffect(() => {
    if (!uid) return;
    supabase.from('profiles').select('booking_username,booking_headline,booking_bio,booking_enabled,google_refresh_token').eq('id',uid).maybeSingle()
      .then(({data}) => { if (data) { setProfile(p=>({...p,...data})); setGcalConn(!!data.google_refresh_token); } });
  }, [uid]);

  const save = async () => {
    setSaving(true);
    await supabase.from('profiles').update({
      booking_username: profile.booking_username?.trim().toLowerCase().replace(/[^a-z0-9_-]/g,''),
      booking_headline: profile.booking_headline,
      booking_bio:      profile.booking_bio,
      booking_enabled:  profile.booking_enabled,
    }).eq('id', uid);
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false), 2500);
  };

  const bookingUrl = `https://www.aspreo.com/book/${profile.booking_username||'your-username'}`;

  // Google OAuth connect — redirect to Google, then callback stores refresh_token
  const connectGoogle = () => {
    const scope    = encodeURIComponent('https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly');
    const redirect = encodeURIComponent(`https://www.aspreo.com/api/auth/google-callback`);
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
    const state    = encodeURIComponent(JSON.stringify({ uid, return_to: '/meetings' }));
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirect}&response_type=code&scope=${scope}&access_type=offline&prompt=consent&state=${state}`;
  };

  return (
    <div style={{display:'grid',gap:16}}>
      {/* Booking Page Setup */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontWeight:700,fontSize:14,color:MTG_COLOR,marginBottom:14}}>🔗 Booking Page Setup</div>

        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>Your Booking URL</div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1,background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:'9px 12px',fontSize:13,color:C.text,fontWeight:600}}>
              aspreo.com/book/<span style={{color:MTG_COLOR}}>{profile.booking_username||'your-username'}</span>
            </div>
            <button onClick={()=>navigator.clipboard.writeText(bookingUrl)} style={{...btn(MTG_COLOR),fontSize:12,padding:'9px 14px',whiteSpace:'nowrap'}}>📋 Copy</button>
          </div>
          {profile.booking_username && (
            <a href={bookingUrl} target="_blank" rel="noreferrer" style={{display:'block',marginTop:6,fontSize:11,color:MTG_COLOR}}>🔗 Open in new tab to test →</a>
          )}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <div>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>Username (URL slug) *</div>
            <input value={profile.booking_username||''} onChange={e=>upd('booking_username',e.target.value)} placeholder="e.g. johncreator" style={{...inp(),width:'100%'}}/>
            <div style={{fontSize:10,color:C.muted,marginTop:3}}>Letters, numbers, - and _ only</div>
          </div>
          <div style={{display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
            <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'10px 14px',background:profile.booking_enabled?MTG_LIGHT:C.surfaceAlt,border:`1px solid ${profile.booking_enabled?MTG_COLOR:C.border}`,borderRadius:9,transition:'all .15s'}}>
              <input type="checkbox" checked={!!profile.booking_enabled} onChange={e=>upd('booking_enabled',e.target.checked)} style={{accentColor:MTG_COLOR,width:16,height:16}}/>
              <span style={{fontSize:13,fontWeight:600,color:profile.booking_enabled?MTG_COLOR:C.muted}}>
                {profile.booking_enabled ? '✅ Page is Live' : '⏸ Page is Hidden'}
              </span>
            </label>
          </div>
        </div>

        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>Headline</div>
          <input value={profile.booking_headline||''} onChange={e=>upd('booking_headline',e.target.value)} placeholder="e.g. Book a 1:1 call with me" style={{...inp(),width:'100%'}}/>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>Bio</div>
          <textarea value={profile.booking_bio||''} onChange={e=>upd('booking_bio',e.target.value)} placeholder="A short intro shown on your booking page..." style={{...ta(70),width:'100%'}}/>
        </div>

        <button onClick={save} disabled={saving} style={{...btn(MTG_COLOR),opacity:saving?0.6:1}}>
          {saving ? '⏳ Saving...' : saved ? '✅ Saved!' : '💾 Save Settings'}
        </button>
      </div>

      {/* Google Calendar Connect */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontWeight:700,fontSize:14,color:'#4285F4',marginBottom:4}}>📅 Google Calendar Sync</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>When someone books a meeting, it automatically appears in your Google Calendar with a Google Meet link.</div>
        {gcalConn ? (
          <div style={{display:'flex',alignItems:'center',gap:10,background:'#F0FDF4',border:'1px solid #16A34A',borderRadius:10,padding:'12px 16px'}}>
            <span style={{fontSize:20}}>✅</span>
            <div>
              <div style={{fontWeight:700,color:'#16A34A',fontSize:13}}>Google Calendar Connected</div>
              <div style={{fontSize:11,color:C.muted}}>New bookings will sync automatically</div>
            </div>
            <button onClick={async()=>{ await supabase.from('profiles').update({google_refresh_token:null}).eq('id',uid); setGcalConn(false); }} style={{...btn(C.red),fontSize:11,padding:'5px 10px',marginLeft:'auto'}}>Disconnect</button>
          </div>
        ) : (
          <div>
            <button onClick={connectGoogle} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 20px',borderRadius:9,border:'1.5px solid #4285F4',background:'#fff',cursor:'pointer',fontFamily:FONT,fontSize:13,fontWeight:600,color:'#4285F4',transition:'all .15s'}}
              onMouseEnter={e=>e.currentTarget.style.background='#EFF6FF'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M47.5 24.5c0-1.6-.1-3.2-.4-4.8H24v9.1h13.2c-.6 3-2.3 5.5-4.9 7.2v6h7.9c4.6-4.2 7.3-10.5 7.3-17.5z"/><path fill="#34A853" d="M24 48c6.6 0 12.2-2.2 16.2-5.9l-7.9-6c-2.2 1.5-5 2.3-8.3 2.3-6.4 0-11.8-4.3-13.7-10.1H2.1v6.2C6.1 42.5 14.5 48 24 48z"/><path fill="#FBBC04" d="M10.3 28.3c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6v-6.2H2.1C.7 15.9 0 19.9 0 24s.7 8.1 2.1 11.1l8.2-6.8z"/><path fill="#EA4335" d="M24 9.5c3.6 0 6.8 1.2 9.3 3.6l7-7C36.2 2.2 30.6 0 24 0 14.5 0 6.1 5.5 2.1 13.7l8.2 6.2C12.2 13.8 17.6 9.5 24 9.5z"/></svg>
              Connect Google Calendar
            </button>
            <div style={{marginTop:10,background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#1D4ED8'}}>
              <strong>Setup:</strong> Add <code>REACT_APP_GOOGLE_CLIENT_ID</code> to your Vercel env vars, then deploy the <code>google-calendar-sync</code> edge function. The callback URL to set in Google Console is: <code>https://www.aspreo.com/api/auth/google-callback</code>
            </div>
          </div>
        )}
      </div>

      {/* Email setup status */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontWeight:700,fontSize:14,color:'#0EA5E9',marginBottom:4}}>📧 Booking Email Notifications</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Automatic emails are sent to both you and your guest on every booking.</div>
        <div style={{background:C.surfaceAlt,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.text}}>
          Deploy the <strong>send-booking-email</strong> Supabase Edge Function and set <strong>RESEND_API_KEY</strong> in your Supabase project secrets.
          <div style={{marginTop:6,fontFamily:'monospace',fontSize:11,color:MTG_COLOR}}>supabase functions deploy send-booking-email</div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ENGAGEMENT TAB — Real-time post tracking + Webhook infrastructure
// ══════════════════════════════════════════════════════════════════════════════
// Replace the existing EngagementTab with this enhanced version
function EngagementTab({ ctx }) {
  const C = useC();
  const { contentPosts=[], engagement=[], saveEngagement, socialAccounts=[] } = ctx;
  const [engTab,    setEngTab]    = React.useState('metrics');
  const [editPostId,setEditPostId]= React.useState(null);
  const [metricForm,setMetricForm]= React.useState({likes:0,comments:0,shares:0,reach:0,impressions:0,clicks:0,saves:0});
  const [syncing,   setSyncing]   = React.useState(false);
  const upd = (k,v) => setMetricForm(p=>({...p,[k]:v}));

  const published  = contentPosts.filter(p=>p.status==='published');
  const getEng     = (pid) => engagement.find(e=>e.post_id===pid) || {};

  const totals = published.reduce((acc,post)=>{
    const e = getEng(post.id);
    return { likes:acc.likes+(parseInt(e.likes)||0), comments:acc.comments+(parseInt(e.comments)||0), shares:acc.shares+(parseInt(e.shares)||0), reach:acc.reach+(parseInt(e.reach)||0) };
  },{ likes:0, comments:0, shares:0, reach:0 });

  const engRate = totals.reach > 0 ? (((totals.likes+totals.comments+totals.shares)/totals.reach)*100).toFixed(2) : '—';

  const topPost = [...published].sort((a,b)=>{
    const ea=getEng(a.id); const eb=getEng(b.id);
    return ((parseInt(eb.likes)||0)+(parseInt(eb.comments)||0))-((parseInt(ea.likes)||0)+(parseInt(ea.comments)||0));
  })[0];

  // Mock real-time refresh — in production calls platform APIs
  const refreshMetrics = async (post) => {
    setSyncing(post.id);
    try {
      const igInteg = (ctx.integrations||[]).find(i=>i.type==='instagram'&&i.is_active);
      if(!igInteg){
        alert('Connect Instagram in Integrations tab first to enable real metric sync.');
        setSyncing(null);return;
      }
      // Trigger sync for this specific user
      const res = await fetch('/api/sb/functions/v1/instagram-sync-metrics',{
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}`},
        body:JSON.stringify({uid:ctx.uid})
      });
      const data = await res.json();
      if(data.ok){
        ctx.setAlert?.(`✅ Synced ${data.synced||0} post${data.synced!==1?'s':''} from Instagram`);
        // Reload engagement data
        if(ctx.loadAll) ctx.loadAll();
      } else {
        ctx.setAlert?.('❌ Sync failed: '+(data.error||'Unknown error'));
      }
    } catch(e){
      ctx.setAlert?.('❌ '+e.message);
    }
    setSyncing(null);
  };

  const ENG_TABS = [
    { id:'metrics',  label:'📊 Metrics'   },
    { id:'realtime', label:'🔴 Real-Time'  },
    { id:'webhooks', label:'🔌 Webhooks'   },
  ];

  return (
    <div>
      <div style={{display:'flex',gap:4,background:C.surfaceAlt,borderRadius:10,padding:4,marginBottom:16}}>
        {ENG_TABS.map(t=>(
          <button key={t.id} onClick={()=>setEngTab(t.id)} style={{background:engTab===t.id?CONTENT_COLOR:'transparent',color:engTab===t.id?'#fff':C.muted,border:'none',borderRadius:7,padding:'6px 14px',cursor:'pointer',fontFamily:FONT,fontSize:12,fontWeight:engTab===t.id?'bold':'normal'}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* METRICS TAB */}
      {engTab==='metrics' && (
        <div>
          {/* KPI summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
            {[
              {icon:'🚀',label:'Published',value:published.length,color:CONTENT_COLOR},
              {icon:'❤️',label:'Likes',value:totals.likes.toLocaleString(),color:'#E1306C'},
              {icon:'💬',label:'Comments',value:totals.comments.toLocaleString(),color:'#0A66C2'},
              {icon:'🔄',label:'Shares',value:totals.shares.toLocaleString(),color:'#16A34A'},
              {icon:'📈',label:'Eng. Rate',value:`${engRate}%`,color:'#D97706'},
            ].map(k=>(
              <div key={k.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px',textAlign:'center'}}>
                <div style={{fontSize:18,marginBottom:2}}>{k.icon}</div>
                <div style={{fontSize:18,fontWeight:800,color:k.color}}>{k.value}</div>
                <div style={{fontSize:10,color:C.muted}}>{k.label}</div>
              </div>
            ))}
          </div>

          {topPost && (
            <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:10,padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontSize:24}}>🏆</span>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#D97706'}}>Top Performing Post</div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{topPost.title}</div>
                <div style={{fontSize:11,color:C.muted}}>{topPost.platform} · {getEng(topPost.id).likes||0} likes · {getEng(topPost.id).comments||0} comments</div>
              </div>
            </div>
          )}

          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {published.length===0 && <div style={{textAlign:'center',color:C.muted,padding:32}}>No published posts yet.</div>}
            {published.map(post=>{
              const e = getEng(post.id);
              return (
                <div key={post.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:13}}>{post.title}</div>
                      <div style={{fontSize:11,color:C.muted}}>{post.platform} · {post.scheduled_at?new Date(post.scheduled_at).toLocaleDateString('en-IN'):''}</div>
                    </div>
                    <div style={{display:'flex',gap:6}}>
                      <button onClick={()=>refreshMetrics(post)} disabled={syncing===post.id} style={{...btn('#6B7280'),fontSize:11,padding:'4px 8px',opacity:syncing===post.id?0.6:1}}>
                        {syncing===post.id?'⏳':'🔄'} Refresh
                      </button>
                      <button onClick={()=>{setEditPostId(post.id);setMetricForm({likes:e.likes||0,comments:e.comments||0,shares:e.shares||0,reach:e.reach||0,impressions:e.impressions||0,clicks:e.clicks||0,saves:e.saves||0});}}
                        style={{...btn(CONTENT_COLOR),fontSize:11,padding:'4px 8px'}}>✏️ Update</button>
                    </div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:5}}>
                    {[['❤️','Likes',e.likes||0],['💬','Comments',e.comments||0],['🔄','Shares',e.shares||0],['👁','Reach',e.reach||0],['📢','Impr.',e.impressions||0],['🖱','Clicks',e.clicks||0],['📌','Saves',e.saves||0]].map(([ico,lbl,val])=>(
                      <div key={lbl} style={{textAlign:'center',background:C.surfaceAlt,borderRadius:6,padding:'6px 4px'}}>
                        <div style={{fontSize:13}}>{ico}</div>
                        <div style={{fontSize:12,fontWeight:700,color:C.text}}>{parseInt(val).toLocaleString()}</div>
                        <div style={{fontSize:9,color:C.muted}}>{lbl}</div>
                      </div>
                    ))}
                  </div>
                  {editPostId===post.id&&(
                    <div style={{marginTop:10,padding:10,background:C.surfaceAlt,borderRadius:8}}>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,marginBottom:8}}>
                        {[['likes','Likes'],['comments','Comments'],['shares','Shares'],['reach','Reach'],['impressions','Impressions'],['clicks','Clicks'],['saves','Saves']].map(([k,l])=>(
                          <div key={k}>
                            <div style={{fontSize:10,color:C.muted,marginBottom:2}}>{l}</div>
                            <input value={metricForm[k]} onChange={e=>upd(k,parseInt(e.target.value)||0)} type="number" style={{...inp(),width:'100%',fontSize:12}}/>
                          </div>
                        ))}
                      </div>
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={async()=>{await saveEngagement(post.id,{...metricForm,platform:post.platform});setEditPostId(null);}} style={btn(CONTENT_COLOR)}>💾 Save</button>
                        <button onClick={()=>setEditPostId(null)} style={btn('#999')}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* REAL-TIME TRACKING TAB */}
      {engTab==='realtime' && (
        <div>
          <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#1D4ED8'}}>
            🔴 <strong>Real-Time Tracking</strong> — Metrics auto-refresh every 6 hours via platform APIs. Instagram requires a Business account with the Instagram Graph API. LinkedIn uses the Marketing API.
          </div>
          {socialAccounts.length===0 ? (
            <div style={{textAlign:'center',padding:40,color:C.muted}}>
              <div style={{fontSize:40,marginBottom:12}}>🔌</div>
              <div style={{fontWeight:700,marginBottom:6}}>No accounts connected</div>
              <div style={{fontSize:12}}>Connect social accounts in the Social Hub tab to enable real-time tracking.</div>
            </div>
          ) : (
            <div>
              <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16}}>
                {socialAccounts.map(acc=>{
                  const accPosts = published.filter(p=>p.platform?.toLowerCase()===acc.platform&&(p.social_account_id===acc.id||!p.social_account_id));
                  const PLAT = { instagram:{color:'#E1306C',icon:'📸'}, linkedin:{color:'#0A66C2',icon:'💼'}, facebook:{color:'#1877F2',icon:'📘'}, twitter:{color:'#1DA1F2',icon:'🐦'} };
                  const pc = PLAT[acc.platform]||{color:CONTENT_COLOR,icon:'📱'};
                  return (
                    <div key={acc.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
                      <div style={{fontSize:24}}>{pc.icon}</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:pc.color}}>{acc.account_name}</div>
                        <div style={{fontSize:11,color:C.muted}}>{acc.platform} · {accPosts.length} published posts tracked</div>
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:acc.is_active?'#DCFCE7':'#FEF2F2',color:acc.is_active?'#16A34A':'#DC2626',fontWeight:600}}>
                          {acc.is_active?'● Active':'○ Paused'}
                        </span>
                        <button onClick={()=>alert('In production: calls Instagram Graph API / LinkedIn API to fetch latest metrics for all posts from this account. Deploy the metrics-sync edge function to enable.')}
                          style={{...btn(pc.color),fontSize:11,padding:'5px 10px'}}>🔄 Sync Now</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.muted}}>
                <strong>Auto-sync schedule:</strong> Every 6 hours via Supabase cron. Deploy <code>supabase/functions/sync-post-metrics</code> and set up pg_cron to run every 6h.
              </div>
            </div>
          )}
        </div>
      )}

      {/* WEBHOOKS TAB */}
      {engTab==='webhooks' && (
        <div>
          <div style={{background:'#FEF9C3',border:'1px solid #D97706',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400E'}}>
            ⚡ <strong>Webhooks are 100% Meta-policy compliant.</strong> We use time-based polling (every 6h) and Meta's official webhook subscriptions for comment/mention events only. No automated DMs are sent — only flagging for your review.
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {/* Instagram Webhook */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                <span style={{fontSize:22}}>📸</span>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:'#E1306C'}}>Instagram Webhook</div>
                  <div style={{fontSize:11,color:C.muted}}>Comment events · Mention events · Story replies</div>
                </div>
                <span style={{marginLeft:'auto',fontSize:10,padding:'2px 8px',borderRadius:10,background:'#FEF9C3',color:'#D97706',fontWeight:700}}>Setup Required</span>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:'10px 12px',fontSize:12,color:C.muted,lineHeight:1.8}}>
                <strong style={{color:C.text}}>Webhook URL:</strong> <code style={{color:'#E1306C'}}>https://www.aspreo.com/api/webhooks/instagram</code><br/>
                <strong style={{color:C.text}}>Verify Token:</strong> Set <code>INSTAGRAM_WEBHOOK_VERIFY_TOKEN</code> in Supabase secrets<br/>
                <strong style={{color:C.text}}>Subscribe to:</strong> comments, mentions, messages (flagging only — not auto-reply)<br/>
                <strong style={{color:C.text}}>Edge fn:</strong> <code>supabase functions deploy instagram-webhook</code>
              </div>
            </div>

            {/* LinkedIn Webhook */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                <span style={{fontSize:22}}>💼</span>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:'#0A66C2'}}>LinkedIn API</div>
                  <div style={{fontSize:11,color:C.muted}}>Post metrics · Comment tracking · Share counts</div>
                </div>
                <span style={{marginLeft:'auto',fontSize:10,padding:'2px 8px',borderRadius:10,background:'#EFF6FF',color:'#2563EB',fontWeight:700}}>Polling</span>
              </div>
              <div style={{background:C.surfaceAlt,borderRadius:8,padding:'10px 12px',fontSize:12,color:C.muted,lineHeight:1.8}}>
                LinkedIn uses polling (no webhook support for personal posts).<br/>
                <strong style={{color:C.text}}>API:</strong> <code>GET /v2/socialActions/{'{'}ugcPostUrn{'}'}</code><br/>
                <strong style={{color:C.text}}>Schedule:</strong> Every 6h via pg_cron<br/>
                <strong style={{color:C.text}}>Edge fn:</strong> <code>supabase functions deploy sync-linkedin-metrics</code>
              </div>
            </div>

            {/* Time-based automation */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                <span style={{fontSize:22}}>⏰</span>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:CONTENT_COLOR}}>Time-Based Automation (Meta Compliant)</div>
                  <div style={{fontSize:11,color:C.muted}}>Trigger actions on schedules — no policy violations</div>
                </div>
                <span style={{marginLeft:'auto',fontSize:10,padding:'2px 8px',borderRadius:10,background:'#F0FDF4',color:'#16A34A',fontWeight:700}}>✅ Safe</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {[
                  {trigger:'Every 6h',action:'Fetch engagement metrics from Instagram + LinkedIn',safe:true},
                  {trigger:'1h after post',action:'Check if post needs boosting (low reach alert)',safe:true},
                  {trigger:'On comment keyword',action:'Flag comment → show in DM Engine for manual review',safe:true},
                  {trigger:'24h before meeting',action:'Send reminder email to guest + host',safe:true},
                  {trigger:'On new follower',action:'Add to "New Followers" queue — you review + send DM manually',safe:true},
                ].map((a,i)=>(
                  <div key={i} style={{display:'flex',gap:10,alignItems:'center',padding:'8px 12px',background:C.surfaceAlt,borderRadius:8}}>
                    <span style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#F0FDF4',color:'#16A34A',fontWeight:700,whiteSpace:'nowrap'}}>✓ SAFE</span>
                    <div style={{flex:1}}>
                      <span style={{fontSize:11,fontWeight:700,color:CONTENT_COLOR}}>{a.trigger}</span>
                      <span style={{fontSize:11,color:C.muted}}> → {a.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MONTHLY UNIFIED VIEW — Tasks + Posts + Meetings in monthly calendar
// ══════════════════════════════════════════════════════════════════════════════
function MonthlyUnifiedView({ ctx, year, month, setYear, setMonth }) {
  const C = useC();
  const { tasks={}, contentPosts=[], meetings=[], meetingTypes=[] } = ctx;
  const [filters, setFilters] = React.useState({ tasks:true, posts:true, meetings:true });
  const toggle = k => setFilters(p=>({...p,[k]:!p[k]}));

  const mk = `${year}-${String(month+1).padStart(2,'0')}`;
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon-first
  const daysInM  = new Date(year, month+1, 0).getDate();
  const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const TODAY_STR = fmtIST(new Date());

  const getEventsForDate = (d) => {
    const evts = [];
    if (filters.tasks) (tasks[d]||[]).filter(t=>!t.done).forEach(t=>evts.push({type:'task',title:t.text,color:'#D97706',bg:'#FFFBEB'}));
    if (filters.tasks) (ctx.recurringTasks||[]).filter(rt=>{
      if(!rt.start_date||rt.start_date>d) return false;
      if(rt.end_date&&rt.end_date<d) return false;
      const dt=new Date(d+'T12:00:00'); const dow=dt.getDay()||7;
      if(rt.repeat_type==='daily') return true;
      if(rt.repeat_type==='weekly') return (rt.repeat_days||[]).includes(dow);
      if(rt.repeat_type==='monthly') return rt.start_date?.slice(8)===d.slice(8);
      return false;
    }).forEach(rt=>evts.push({type:'recurring',title:'🔁 '+rt.title,color:'#6B7280',bg:'#F3F4F6',time:rt.start_time||''}));
    // Recurring tasks
    if (filters.posts) contentPosts.filter(p=>(p.scheduledDate||p.scheduled_date||p.scheduled_at||'').startsWith(d))
      .forEach(p=>evts.push({type:'post',title:p.title,color:'#8B2FC9',bg:'#F3E8FF',platform:p.platform}));
    if (filters.meetings) meetings.filter(m=>m.status==='confirmed'&&m.start_time?.startsWith(d))
      .forEach(m=>{const mt=meetingTypes.find(t=>t.id===m.meeting_type_id); evts.push({type:'meeting',title:`${m.guest_name}${mt?` · ${mt.title}`:''}`,color:'#0D9488',bg:'#F0FDFA',time:new Date(m.start_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})});});
    return evts;
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',gap:6}}>
          {[{k:'tasks',l:'📋 Tasks',c:'#D97706'},{k:'posts',l:'✍️ Posts',c:'#8B2FC9'},{k:'meetings',l:'📆 Meetings',c:'#0D9488'}].map(f=>(
            <button key={f.k} onClick={()=>toggle(f.k)} style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1.5px solid ${filters[f.k]?f.c:C.border}`,background:filters[f.k]?f.c+'18':'transparent',color:filters[f.k]?f.c:C.muted,cursor:'pointer',fontWeight:filters[f.k]?700:400}}>{f.l}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={()=>{if(month===0){setYear(y=>y-1);setMonth(11);}else setMonth(m=>m-1);}} style={{...btn('#94a3b8'),padding:'6px 10px'}}>←</button>
          <span style={{fontWeight:700,fontSize:14,alignSelf:'center'}}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month]} {year}</span>
          <button onClick={()=>{if(month===11){setYear(y=>y+1);setMonth(0);}else setMonth(m=>m+1);}} style={{...btn('#94a3b8'),padding:'6px 10px'}}>→</button>
        </div>
      </div>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'#2B1DC9'}}>
          {DOW.map((d,i)=><div key={d} style={{padding:'8px',textAlign:'center',fontSize:11,fontWeight:700,color:i>=5?'#fca5a5':'#fff'}}>{d}</div>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
          {Array.from({length:firstDow},(_,i)=><div key={`e${i}`} style={{minHeight:88,background:'#FAFAFA',borderRight:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`}}/>)}
          {Array.from({length:daysInM},(_,i)=>{
            const dayNum = i+1;
            const d = `${year}-${String(month+1).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
            const evts = getEventsForDate(d);
            const isToday = d===TODAY_STR;
            const dow = (firstDow+i)%7;
            return (
              <div key={d} style={{minHeight:88,padding:4,border:`1px solid ${C.border}`,background:isToday?'#FFF9F0':dow>=5?'#FFFBF5':'transparent'}}>
                <div style={{width:22,height:22,borderRadius:'50%',background:isToday?'#2B1DC9':'transparent',color:isToday?'#fff':dow>=5?'#ef4444':C.text,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:isToday?800:500,marginBottom:2}}>{dayNum}</div>
                {evts.slice(0,3).map((e,ei)=>(
                  <div key={ei} style={{fontSize:9,padding:'1px 4px',borderRadius:3,background:e.color,color:'#fff',marginBottom:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',lineHeight:1.6}}>
                    {e.time&&<span style={{opacity:.8}}>{e.time} </span>}{e.title}
                  </div>
                ))}
                {evts.length>3&&<div style={{fontSize:9,color:C.muted}}>+{evts.length-3} more</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BUSINESS SETUP TAB
// ══════════════════════════════════════════════════════════════════════════════
function BusinessSetupTab({ ctx }) {
  const C = useC();
  const { bizProfile={}, updateBizProfile, uid } = ctx;
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [uploadingLogo, setUploadingLogo] = React.useState(false);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const fileRef = React.useRef();

  React.useEffect(() => { setForm(bizProfile||{}); }, [bizProfile]);

  const save = async () => {
    setSaving(true);
    await updateBizProfile(form);
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop();
      // Path must start with the uid folder segment — the "Users upload own avatar"
      // storage RLS policy checks auth.uid() = (storage.foldername(name))[1].
      const path = `${uid}/logo-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage.from('avatars').upload(path, file, { upsert:true, contentType:file.type });
      if (!error) {
        const { data: pub } = supabase.storage.from('avatars').getPublicUrl(data.path);
        upd('biz_logo_url', pub.publicUrl);
        // Persist immediately — an upload with no follow-up save was silently lost on navigation.
        await updateBizProfile({ ...form, biz_logo_url: pub.publicUrl });
      } else {
        alert('Logo upload failed: ' + error.message);
      }
    } catch(e) { console.warn('logo upload:', e); alert('Logo upload failed: ' + e.message); }
    setUploadingLogo(false);
  };

  const FIELDS = [
    { section:'Business Identity' },
    { k:'biz_name',    l:'Business Name *',  ph:'Aspreo Consulting Pvt. Ltd.' },
    { k:'biz_tagline', l:'Tagline',           ph:'Building the future, one client at a time' },
    { k:'biz_about',   l:'About / Company & Services', ph:'What your company does, the services you offer, and what makes you different — this is used to write your WhatsApp promotional messages.', area:true },
    { section:'Contact Details' },
    { k:'biz_email',   l:'Business Email',   ph:'hello@yourbusiness.com' },
    { k:'biz_phone',   l:'Phone',            ph:'+91 98765 43210' },
    { k:'biz_website', l:'Website',          ph:'https://yourbusiness.com' },
    { section:'Address' },
    { k:'biz_address', l:'Address',          ph:'123, Business Park, Mumbai - 400001', area:true },
    { section:'Tax & Legal' },
    { k:'biz_gstin',   l:'GSTIN',            ph:'22AAAAA0000A1Z5' },
    { k:'biz_pan',     l:'PAN',              ph:'AAAAA0000A' },
    { section:'Bank Details' },
    { k:'biz_bank_name',l:'Bank Name',       ph:'HDFC Bank' },
    { k:'biz_bank_acc', l:'Account Number',  ph:'123456789012' },
    { k:'biz_bank_ifsc',l:'IFSC Code',       ph:'HDFC0001234' },
    { k:'biz_bank_type',l:'Account Type',    ph:'Current', select:['Current','Savings'] },
    { section:'Invoice & Proposal Defaults' },
    { k:'inv_prefix',      l:'Invoice Prefix',  ph:'INV' },
    { k:'proforma_prefix', l:'Proforma Prefix', ph:'PRO' },
    { k:'prop_prefix',     l:'Proposal Prefix', ph:'PROP' },
    { k:'biz_terms',       l:'Default Terms',   ph:'Payment due within 15 days.', area:true },
  ];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontWeight:800,fontSize:16,color:BIZ_COLOR}}>⚙️ Business Setup</div>
          <div style={{fontSize:12,color:C.muted}}>These details appear on all your invoices and proposals</div>
        </div>
        <button onClick={save} disabled={saving} style={{...btn(BIZ_COLOR),opacity:saving?0.6:1}}>{saving?'⏳ Saving…':saved?'✅ Saved!':'💾 Save'}</button>
      </div>

      {/* Logo upload */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16,display:'flex',alignItems:'center',gap:20}}>
        <div onClick={()=>fileRef.current?.click()} style={{width:90,height:90,borderRadius:14,border:`2px dashed ${BIZ_COLOR}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',overflow:'hidden',flexShrink:0,background:C.surfaceAlt}}>
          {form.biz_logo_url
            ? <img src={form.biz_logo_url} alt="logo" style={{width:'100%',height:'100%',objectFit:'contain'}}/>
            : <span style={{fontSize:32}}>🏢</span>}
        </div>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:4}}>Business Logo</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Appears on invoices, proposals, and booking page</div>
          <button onClick={()=>fileRef.current?.click()} disabled={uploadingLogo} style={{...btn(BIZ_COLOR),fontSize:12,padding:'6px 14px'}}>
            {uploadingLogo?'⏳ Uploading…':'📤 Upload Logo'}
          </button>
          {form.biz_logo_url&&<button onClick={()=>upd('biz_logo_url','')} style={{...btn('#DC2626'),fontSize:12,padding:'6px 10px',marginLeft:8}}>Remove</button>}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} style={{display:'none'}}/>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        {FIELDS.map((f,i)=>{
          if (f.section) return (
            <div key={f.section} style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:BIZ_COLOR,textTransform:'uppercase',letterSpacing:'.08em',borderBottom:`1px solid ${BIZ_COLOR}30`,paddingBottom:6,marginTop:i===0?0:8}}>{f.section}</div>
          );
          return (
            <div key={f.k} style={{gridColumn:f.area?'1/-1':'auto'}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>{f.l}</div>
              {f.select
                ? <select value={form[f.k]||''} onChange={e=>upd(f.k,e.target.value)} style={{...inp(),width:'100%'}}>
                    {f.select.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                : f.area
                  ? <textarea value={form[f.k]||''} onChange={e=>upd(f.k,e.target.value)} placeholder={f.ph} style={{...ta(70),width:'100%'}}/>
                  : <input value={form[f.k]||''} onChange={e=>upd(f.k,e.target.value)} placeholder={f.ph} style={{...inp(),width:'100%'}}/>
              }
            </div>
          );
        })}
      </div>
      <div style={{marginTop:16,textAlign:'right'}}>
        <button onClick={save} disabled={saving} style={{...btn(BIZ_COLOR),opacity:saving?0.6:1}}>{saving?'⏳ Saving…':saved?'✅ Saved!':'💾 Save Business Profile'}</button>
      </div>

      {/* ── Sender Profiles ──────────────────────────────────────────────── */}
      <SenderProfilesSection uid={uid} BIZ_COLOR={BIZ_COLOR} ctx={ctx} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SENDER PROFILES — Team members with name+email for invoice/proposal sending
// ══════════════════════════════════════════════════════════════════════════════
function SenderProfilesSection({ uid, BIZ_COLOR, ctx }) {
  const C = useC();
  const senders = ctx?.senderProfiles || [];
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm]         = React.useState({ name:'', email:'', role:'', sort_order:0 });
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = () => {
    if (!form.name.trim() || !form.email.trim()) return;
    ctx?.addSenderProfile?.({ ...form, sort_order: senders.length });
    setForm({ name:'', email:'', role:'', sort_order:0 });
    setShowForm(false);
  };

  const remove = (id) => {
    if (window.confirm('Remove this sender profile?')) ctx?.deleteSenderProfile?.(id);
  };

  return (
    <div style={{marginTop:24,background:C.surface,border:`1.5px solid ${BIZ_COLOR}20`,borderRadius:12,padding:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div style={{fontWeight:800,fontSize:15,color:BIZ_COLOR}}>👤 Sender Profiles</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>When sending invoices or proposals, choose which team member the email comes from. The client sees their name and can reply directly to their email.</div>
        </div>
        <button onClick={()=>setShowForm(v=>!v)} style={{...btn(BIZ_COLOR),fontSize:12}}>+ Add Sender</button>
      </div>

      {showForm && (
        <div style={{background:C.surfaceAlt,borderRadius:10,padding:14,marginBottom:14,display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,alignItems:'end'}}>
          <div><label style={lbl}>Full Name *</label><input value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="e.g. Gajanan Khekade" style={{...inp(),width:'100%'}} autoFocus/></div>
          <div><label style={lbl}>Email *</label><input value={form.email} onChange={e=>upd('email',e.target.value)} placeholder="gajanan@verentix.com" type="email" style={{...inp(),width:'100%'}}/></div>
          <div><label style={lbl}>Role (optional)</label><input value={form.role} onChange={e=>upd('role',e.target.value)} placeholder="e.g. CEO, Sales Manager" style={{...inp(),width:'100%'}}/></div>
          <div style={{gridColumn:'1/-1',display:'flex',gap:8}}>
            <button onClick={save} style={{...btn(BIZ_COLOR),fontSize:12}}>💾 Save Sender</button>
            <button onClick={()=>{setShowForm(false);setForm({name:'',email:'',role:'',});}} style={{...btn('#999'),fontSize:12}}>Cancel</button>
          </div>
        </div>
      )}

      {senders.length === 0 ? (
        <div style={{textAlign:'center',color:C.muted,padding:24,fontSize:13}}>
          No sender profiles yet.<br/>
          <span style={{fontSize:12}}>Add your team members — when sending an invoice you can choose who it comes from.</span>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {senders.map(s => (
            <div key={s.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:C.surfaceAlt,borderRadius:8,border:`1px solid ${C.border}`}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:BIZ_COLOR,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:'#fff',fontSize:14,flexShrink:0}}>
                {(s.name||'?').slice(0,1).toUpperCase()}
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:C.text}}>{s.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{s.email}{s.role && ` · ${s.role}`}</div>
              </div>
              <button onClick={()=>remove(s.id)} style={{...btn('#DC2626'),fontSize:11,padding:'4px 8px'}}>🗑</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SERVICES TAB — Library of services/items used in invoices + proposals
// ══════════════════════════════════════════════════════════════════════════════
function ServicesTab({ ctx }) {
  const C = useC();
  const { services=[], addService, updateService, deleteService } = ctx;
  const [showForm, setShowForm] = React.useState(false);
  const [editSvc, setEditSvc] = React.useState(null);
  const blank = { name:'', description:'', price:'', unit:'service', category:'' };
  const [form, setForm] = React.useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const UNITS = ['service','hour','day','piece','month','year','session','project'];

  const save = async () => {
    if (!form.name.trim()) return;
    const payload = { ...form, price: parseFloat(form.price)||0 };
    if (editSvc) updateService(editSvc.id, payload);
    else await addService(payload);
    setForm(blank); setShowForm(false); setEditSvc(null);
  };

  const categories = [...new Set(services.map(s=>s.category).filter(Boolean))];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:BIZ_COLOR}}>📦 Services & Items</div>
          <div style={{fontSize:12,color:C.muted}}>Reusable items — pick from dropdown when creating invoices & proposals</div>
        </div>
        <button onClick={()=>{setEditSvc(null);setForm(blank);setShowForm(true);}} style={btn(BIZ_COLOR)}>+ Add Service</button>
      </div>

      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${BIZ_COLOR}`,borderRadius:12,padding:16,marginBottom:14}}>
          <h4 style={{margin:'0 0 12px',color:BIZ_COLOR}}>{editSvc?'Edit Service':'New Service / Item'}</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <input value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="Service name *" style={{...inp(),width:'100%'}}/>
            <input value={form.category} onChange={e=>upd('category',e.target.value)} placeholder="Category (e.g. Consulting, Design)" style={{...inp(),width:'100%'}} list="cat-list"/>
            <datalist id="cat-list">{categories.map(c=><option key={c} value={c}/>)}</datalist>
            <input value={form.price} onChange={e=>upd('price',e.target.value)} placeholder="Price (₹)" type="number" style={{...inp(),width:'100%'}}/>
            <select value={form.unit} onChange={e=>upd('unit',e.target.value)} style={{...inp(),width:'100%'}}>
              {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <textarea value={form.description} onChange={e=>upd('description',e.target.value)} placeholder="Description (optional — shown on invoice in italics)" style={{...ta(60),width:'100%',marginBottom:8}}/>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(BIZ_COLOR)}>💾 {editSvc?'Update':'Save'}</button>
            <button onClick={()=>{setShowForm(false);setEditSvc(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {services.length===0&&!showForm&&(
        <div style={{textAlign:'center',padding:48,color:C.muted}}>
          <div style={{fontSize:40,marginBottom:12}}>📦</div>
          <div style={{fontWeight:700,marginBottom:6}}>No services yet</div>
          <div style={{fontSize:12,marginBottom:16}}>Add your services once — select them instantly when creating invoices and proposals.</div>
          <button onClick={()=>setShowForm(true)} style={btn(BIZ_COLOR)}>+ Add Your First Service</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
        {services.map(svc=>(
          <div key={svc.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.text}}>{svc.name}</div>
                {svc.category&&<span style={{fontSize:10,padding:'1px 7px',borderRadius:10,background:BIZ_COLOR+'18',color:BIZ_COLOR,fontWeight:600}}>{svc.category}</span>}
              </div>
              <div style={{display:'flex',gap:4}}>
                <button onClick={()=>{setEditSvc(svc);setForm({...svc,price:svc.price||''});setShowForm(true);}} style={{background:'none',border:'none',cursor:'pointer',color:C.amber,fontSize:14}}>✏️</button>
                <button onClick={()=>{if(window.confirm('Delete this service?'))deleteService(svc.id);}} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:14}}>🗑</button>
              </div>
            </div>
            {svc.description&&<div style={{fontSize:12,color:C.muted,fontStyle:'italic',marginBottom:8,lineHeight:1.5}}>{svc.description}</div>}
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:15,fontWeight:800,color:BIZ_COLOR}}>₹{parseFloat(svc.price||0).toLocaleString('en-IN')}</span>
              <span style={{fontSize:11,color:C.muted}}>/ {svc.unit}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// INVOICE GENERATOR v2 — Professional, 2 templates, themes, PDF, shareable
// ══════════════════════════════════════════════════════════════════════════════
const INV_THEMES = [
  { id:'default',    name:'Default',       primary:'#2B1DC9', accent:'#6366F1', bg:'#F6F6F8' },
  { id:'teal',       name:'Teal',          primary:'#0D9488', accent:'#14B8A6', bg:'#F0FDFA' },
  { id:'indigo',     name:'Indigo',        primary:'#4F46E5', accent:'#818CF8', bg:'#EEF2FF' },
  { id:'purple',     name:'Purple',        primary:'#7C3AED', accent:'#A78BFA', bg:'#F5F3FF' },
  { id:'green',      name:'Green',         primary:'#16A34A', accent:'#4ADE80', bg:'#F0FDF4' },
  { id:'rose',       name:'Rose',          primary:'#E11D48', accent:'#FB7185', bg:'#FFF1F3' },
  { id:'orange',     name:'Vibrant Orange',primary:'#EA580C', accent:'#FB923C', bg:'#FFF7ED' },
  { id:'slate',      name:'Formal Slate',  primary:'#334155', accent:'#64748B', bg:'#F8FAFC' },
  { id:'brown',      name:'Formal Brown',  primary:'#78350F', accent:'#92400E', bg:'#FFFBEB' },
  { id:'navy',       name:'Formal Navy',   primary:'#1E3A5F', accent:'#2563EB', bg:'#EFF6FF' },
];
const PAPER_SIZES = { A4:'A4', A5:'A5', Letter:'Letter' };
const FONTS_LIST  = ['Helvetica','Times New Roman','Courier','Arial'];

// ── Proposal content templates: {{token}} substitution ──────────────────────
// Static default content (executive summary, why-us, etc.) is written once in
// the proposal's Content Template screen and can reference these tokens; they
// get resolved to real values once, when a proposal is saved.
const applyPlaceholders = (text, values) => {
  if (!text) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (m, key) => (values[key] !== undefined && values[key] !== '' ? values[key] : m));
};
const buildPlaceholderValues = (bizProfile, party, extra) => {
  const vals = { client_name: party?.name || '', company_name: bizProfile?.biz_name || '' };
  (bizProfile?.prop_placeholders || []).forEach(p => { if (p?.key && vals[p.key] === undefined) vals[p.key] = p.default_value || ''; });
  return { ...vals, ...(extra || {}) };
};

function InvoicesTab({ ctx }) {
  const C = useC();
  const { invoices=[], clients=[], services=[], bizProfile={}, addInvoice, updateInvoice, deleteInvoice, uid, senderProfiles=[] } = ctx;
  const [view, setView] = React.useState('list'); // list | create | preview
  const [editInv, setEditInv] = React.useState(null);
  const [printInv, setPrintInv] = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState('all');
  const [shareUrl, setShareUrl] = React.useState('');

  const blankInv = {
    client_id:'', items:[], tax_pct:18, due_date:'', notes:'', terms: bizProfile.biz_terms||'Payment due within 15 days.',
    template:'modern', theme:'default', paper:'A4', font:'Helvetica', label_color:'', font_color:'', font_size:10, bg_color:'',
    doc_type:'invoice',
  };
  const [form, setForm] = React.useState({...blankInv});
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const openNew = (docType='invoice') => {
    setEditInv(null);
    setForm({...blankInv, doc_type:docType, terms:bizProfile.biz_terms||(docType==='proforma'?'This proforma is valid for 30 days and does not constitute a demand for payment.':'Payment due within 15 days.'), items:[{id:Date.now(),service_id:'',desc:'',qty:1,rate:'',amount:0,italic_desc:''}]});
    setView('create');
  };
  const openEdit = (inv) => {
    setEditInv(inv);
    setForm({...blankInv,...inv, items:(inv.items||[]).map((it,i)=>({...it,id:it.id||i}))});
    setView('create');
  };

  const save = async () => {
    const payload = { ...form };
    if (editInv) { updateInvoice(editInv.id, payload); }
    else { await addInvoice(payload); }
    setView('list');
  };

  const [shareModal, setShareModal] = React.useState(null); // {item, type:'invoice'|'proposal'}
  const [emailModal, setEmailModal] = React.useState(null); // {to, subject, message, link, type}

  const defaultEmail = bizProfile?.biz_email || '';
  const defaultName  = bizProfile?.biz_name  || '';

  const openEmailModal = (inv) => {
    const link = `${window.location.origin}/invoice/${inv.share_token||''}`;
    setEmailModal({
      to:         (clients||[]).find(c=>c.id===inv.client_id)?.email || '',
      from_email: defaultEmail,
      from_name:  defaultName,
      subject:    `Invoice ${inv.invoice_number} — ₹${parseFloat(inv.total||0).toLocaleString('en-IN')}`,
      message:    `Hi,\n\nPlease find your invoice ${inv.invoice_number} attached.\n\nView & download it here: ${link}\n\nDue date: ${inv.due_date||'—'}\n\nThank you for your business!`,
      link, type: 'invoice',
    });
  };

  const generateShareLink = (inv) => {
    setShareModal({ item: inv, type: 'invoice' });
  };

  const filtered = filterStatus==='all' ? invoices : invoices.filter(i=>i.status===filterStatus);

  return (
    <div>
      {view==='list'&&(
        <>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {['all',...INV_STATUSES.map(s=>s.id)].map(s=>(
                <button key={s} onClick={()=>setFilterStatus(s)} style={{fontSize:10,padding:'3px 10px',borderRadius:20,cursor:'pointer',border:'none',
                  background:filterStatus===s?BIZ_COLOR:'#e5e7eb',color:filterStatus===s?'#fff':C.muted}}>
                  {s==='all'?`All (${invoices.length})`:(INV_STATUSES.find(x=>x.id===s)?.label||s)+` (${invoices.filter(i=>i.status===s).length})`}
                </button>
              ))}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>openNew('proforma')} style={{...btn(BIZ_COLOR),background:'transparent',border:`1px solid ${BIZ_COLOR}`,color:BIZ_COLOR}}>+ New Proforma</button>
              <button onClick={()=>openNew('invoice')} style={btn(BIZ_COLOR)}>+ New Invoice</button>
            </div>
          </div>
          {filtered.length===0&&<div style={{textAlign:'center',color:C.muted,padding:40}}>No invoices yet. Create your first one!</div>}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {filtered.map(inv=>{
              const client=clients.find(c=>c.id===inv.client_id);
              const st=INV_STATUSES.find(s=>s.id===inv.status)||INV_STATUSES[0];
              const theme=INV_THEMES.find(t=>t.id===(inv.theme||'default'))||INV_THEMES[0];
              const isProforma=inv.doc_type==='proforma';
              return(
                <div key={inv.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:6,height:40,borderRadius:3,background:theme.primary,flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{inv.invoice_number}
                      <span style={{marginLeft:8,fontSize:10,padding:'1px 7px',borderRadius:10,background:st.color+'22',color:st.color,fontWeight:600}}>{st.label}</span>
                      {isProforma&&<span style={{marginLeft:6,fontSize:10,padding:'1px 7px',borderRadius:10,background:'#F5EBD9',color:'#A06014',fontWeight:700,letterSpacing:'.03em'}}>PROFORMA</span>}
                    </div>
                    <div style={{fontSize:12,color:C.muted}}>{client?.name||'No client'}{inv.due_date?` · Due: ${inv.due_date}`:''}</div>
                  </div>
                  <div style={{fontSize:18,fontWeight:800,color:BIZ_COLOR}}>₹{parseFloat(inv.total||0).toLocaleString('en-IN')}</div>
                  <div style={{display:'flex',gap:5}}>
                    <select value={inv.status} onChange={e=>updateInvoice(inv.id,{status:e.target.value})} style={{fontSize:11,padding:'3px 6px',border:`1px solid ${C.border}`,borderRadius:6,background:C.surface,cursor:'pointer'}}>
                      {INV_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <button onClick={()=>{setPrintInv(inv);}} style={{...btn('#475569'),fontSize:11,padding:'4px 8px'}}>👁 View</button>
                    <button onClick={()=>openEdit(inv)} style={{...btn(C.amber),fontSize:11,padding:'4px 8px'}}>✏️</button>
                    <button onClick={()=>generateShareLink(inv)} style={{...btn('#7C3AED'),fontSize:11,padding:'4px 8px'}}>🔗 Share</button>
                    <button onClick={()=>{const token=inv.share_token||([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));updateInvoice(inv.id,{is_shared:true,share_token:token});setTimeout(()=>openEmailModal({...inv,share_token:token}),150);}} style={{...btn('#2563EB'),fontSize:11,padding:'4px 8px'}}>📧 Email</button>
                    <button onClick={()=>{if(window.confirm('Delete?'))deleteInvoice(inv.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view==='create'&&(
        <InvoiceEditor
          form={form} upd={upd} clients={clients} services={services} bizProfile={bizProfile}
          editInv={editInv} onSave={save} onCancel={()=>setView('list')} onPreview={()=>setView('preview')}
        />
      )}

      {view==='preview'&&(
        <div>
          <div style={{display:'flex',gap:8,marginBottom:14}}>
            <button onClick={()=>setView('create')} style={btn('#475569')}>← Back to Edit</button>
          </div>
          <InvoicePreview form={form} clients={clients} bizProfile={bizProfile}/>
        </div>
      )}

      {shareModal && ReactDOM.createPortal(
        <ShareLinkModal
          item={shareModal.item}
          type={shareModal.type}
          onShare={async (expires)=>{
            const exp = expires ? new Date(Date.now() + expires).toISOString() : null;
            const token = shareModal.item.share_token ||
              ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
            updateInvoice(shareModal.item.id, {is_shared:true, share_token:token, share_expires_at:exp});
            const url = `${window.location.origin}/invoice/${token}`;
            navigator.clipboard.writeText(url);
            setShareModal(null);
            alert(`🔗 Link copied!\n\n${url}\n\nAnyone with this link can view and download the invoice as PDF.${exp?`\n\nExpires: ${new Date(exp).toLocaleDateString('en-IN')}`:'\n\nNo expiry set.'}`);
          }}
          onClose={()=>setShareModal(null)}
        />, document.body
      )}
      {emailModal && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,zIndex:4500,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setEmailModal(null)}>
          <div style={{background:C.surface,borderRadius:16,padding:24,width:'100%',maxWidth:500,boxShadow:'0 24px 80px rgba(0,0,0,.3)'}}>
            <div style={{fontWeight:800,fontSize:16,marginBottom:16,color:C.text}}>📧 Send Invoice by Email</div>
            {/* Sender selector */}
            <div style={{marginBottom:10}}>
              <label style={lbl}>Send As (Sender / Reply-To)</label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                {/* Default: business */}
                <button onClick={()=>setEmailModal(p=>({...p,from_name:defaultName,from_email:defaultEmail}))}
                  style={{fontSize:11,padding:'5px 12px',borderRadius:20,border:`1.5px solid ${emailModal.from_email===defaultEmail?BIZ_COLOR:C.border}`,background:emailModal.from_email===defaultEmail?BIZ_COLOR+'18':'transparent',color:emailModal.from_email===defaultEmail?BIZ_COLOR:C.muted,cursor:'pointer',fontWeight:emailModal.from_email===defaultEmail?700:400}}>
                  🏢 {defaultName||'Business'}
                </button>
                {senderProfiles.map(s=>(
                  <button key={s.id} onClick={()=>setEmailModal(p=>({...p,from_name:s.name,from_email:s.email}))}
                    style={{fontSize:11,padding:'5px 12px',borderRadius:20,border:`1.5px solid ${emailModal.from_email===s.email?BIZ_COLOR:C.border}`,background:emailModal.from_email===s.email?BIZ_COLOR+'18':'transparent',color:emailModal.from_email===s.email?BIZ_COLOR:C.muted,cursor:'pointer',fontWeight:emailModal.from_email===s.email?700:400}}>
                    👤 {s.name}
                  </button>
                ))}
                {senderProfiles.length===0&&<span style={{fontSize:11,color:C.muted,alignSelf:'center'}}>Add team members in Business → Setup → Sender Profiles</span>}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div><label style={lbl}>Sender Name</label><input value={emailModal.from_name||''} onChange={e=>setEmailModal(p=>({...p,from_name:e.target.value}))} placeholder="Name" style={{...inp(),width:'100%'}}/></div>
                <div><label style={lbl}>Reply-To Email</label><input value={emailModal.from_email||''} onChange={e=>setEmailModal(p=>({...p,from_email:e.target.value}))} placeholder="email@domain.com" type="email" style={{...inp(),width:'100%'}}/></div>
              </div>
            </div>
            <div style={{marginBottom:10}}><label style={lbl}>To (Email) *</label><input value={emailModal.to} onChange={e=>setEmailModal(p=>({...p,to:e.target.value}))} placeholder="client@example.com" style={{...inp(),width:'100%'}}/></div>
            <div style={{marginBottom:10}}><label style={lbl}>Subject</label><input value={emailModal.subject} onChange={e=>setEmailModal(p=>({...p,subject:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
            <div style={{marginBottom:14}}><label style={lbl}>Message</label><textarea value={emailModal.message} onChange={e=>setEmailModal(p=>({...p,message:e.target.value}))} style={{...ta(100),width:'100%'}}/></div>
            <div style={{background:BIZ_COLOR+'10',border:`1px solid ${BIZ_COLOR}30`,borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:11,color:BIZ_COLOR}}>
              📎 Invoice link will be included: <strong>{emailModal.link}</strong>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setEmailModal(null)} style={{...btn('#999'),padding:'8px 18px'}}>Cancel</button>
              <button onClick={async()=>{
                if(!emailModal.to.trim())return;
                try{
                  const res=await fetch('/api/sb/functions/v1/send-booking-email',{
                    method:'POST',
                    headers:{'Content-Type':'application/json','Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}`},
                    body:JSON.stringify({type:'invoice',to_email:emailModal.to,from_email:emailModal.from_email,from_name:emailModal.from_name,subject:emailModal.subject,message:emailModal.message,link:emailModal.link,host_id:uid}),
                  });
                  ctx.setAlert?.('✅ Invoice emailed to '+emailModal.to);
                }catch(e){ctx.setAlert?.('❌ Send failed: '+e.message);}
                setEmailModal(null);
              }} style={{...btn(BIZ_COLOR),padding:'8px 20px',fontWeight:700}}>📤 Send</button>
            </div>
          </div>
        </div>, document.body
      )}
      {printInv && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,zIndex:5000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflowY:'auto'}} onClick={()=>setPrintInv(null)}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:700,maxHeight:'90vh',overflowY:'auto',padding:0}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',gap:8,padding:'12px 16px',borderBottom:'1px solid #e5e7eb',justifyContent:'flex-end'}}>
              <button onClick={()=>{
                const el2 = document.getElementById(`inv-preview-${printInv.id}`);
                if(!el2){return;}
                printHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice</title>
                <style>
                  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900');
                  *{box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
                  body{font-family:'Inter',Arial,sans-serif;margin:0;padding:0;color:#0F0E1A;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
                  img{max-width:100%;} table{border-collapse:collapse;width:100%;}
                  @page{margin:10mm;}
                  @media print{*{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}}
                </style></head><body>${el2.innerHTML}</body></html>`);
                }} style={{background:'#2B1DC9',color:'#fff',border:'none',borderRadius:7,padding:'7px 14px',cursor:'pointer',fontSize:12,fontWeight:700}}>🖨 Print / PDF</button>
              <button onClick={()=>setPrintInv(null)} style={{background:'#e5e7eb',color:'#333',border:'none',borderRadius:7,padding:'7px 12px',cursor:'pointer',fontSize:12}}>✕ Close</button>
            </div>
            <div id={`inv-preview-${printInv.id}`} style={{padding:16}}>
              <InvoicePreview form={printInv} clients={clients} bizProfile={bizProfile}/>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Invoice Editor ────────────────────────────────────────────────────────────
function InvoiceEditor({ form, upd, clients, services, bizProfile, editInv, onSave, onCancel, onPreview }) {
  const C = useC();
  const theme = INV_THEMES.find(t=>t.id===form.theme)||INV_THEMES[0];

  const addItem = (svcId) => {
    const svc = services.find(s=>s.id===svcId);
    const newItem = { id:Date.now(), service_id:svcId||'', desc:svc?.name||'', italic_desc:svc?.description||'', qty:1, rate:svc?.price||'', amount:svc?.price||0 };
    upd('items', [...(form.items||[]), newItem]);
  };

  const addBlankItem = () => upd('items', [...(form.items||[]), { id:Date.now(), service_id:'', desc:'', italic_desc:'', qty:1, rate:'', amount:0 }]);

  const updItem = (id, field, val) => {
    upd('items', (form.items||[]).map(it => {
      if (it.id!==id) return it;
      const updated = {...it, [field]:val};
      if (field==='qty'||field==='rate') updated.amount = (parseFloat(field==='qty'?val:it.qty)||1)*(parseFloat(field==='rate'?val:it.rate)||0);
      return updated;
    }));
  };

  const removeItem = (id) => upd('items', (form.items||[]).filter(it=>it.id!==id));
  const subtotal = (form.items||[]).reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  const taxAmt   = subtotal * ((parseFloat(form.tax_pct)||0)/100);
  const total    = subtotal + taxAmt;

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:16}}>
      {/* Left: Form */}
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div style={{fontWeight:800,fontSize:15,color:BIZ_COLOR}}>{editInv?'Edit ':'New '}{form.doc_type==='proforma'?'Proforma Invoice':'Invoice'}</div>
          <div style={{display:'flex',gap:6}}>
            <button onClick={onPreview} style={{...btn('#475569'),fontSize:12}}>👁 Preview</button>
            <button onClick={onSave} style={{...btn(BIZ_COLOR),fontSize:12}}>💾 Save</button>
            <button onClick={onCancel} style={{...btn('#999'),fontSize:12}}>Cancel</button>
          </div>
        </div>

        {/* Client + Dates */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:10,textTransform:'uppercase',letterSpacing:'.05em'}}>Invoice Details</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Client</div>
              <select value={form.client_id||''} onChange={e=>upd('client_id',e.target.value)} style={{...inp(),width:'100%'}}>
                <option value="">Select Client</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}{c.company?` (${c.company})`:''}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Due Date</div>
              <input type="date" value={form.due_date||''} onChange={e=>upd('due_date',e.target.value)} style={{...inp(),width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>GST %</div>
              <input type="number" value={form.tax_pct} onChange={e=>upd('tax_pct',e.target.value)} placeholder="18" style={{...inp(),width:'100%'}}/>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:10,textTransform:'uppercase',letterSpacing:'.05em'}}>Line Items</div>

          {/* Add from services */}
          {services.length>0&&(
            <div style={{marginBottom:10}}>
              <select onChange={e=>{if(e.target.value)addItem(e.target.value);e.target.value='';}} style={{...inp(),width:'100%',fontSize:12}} defaultValue="">
                <option value="">+ Add from Services Library…</option>
                {services.map(s=><option key={s.id} value={s.id}>{s.name} — ₹{parseFloat(s.price||0).toLocaleString('en-IN')}/{s.unit}</option>)}
              </select>
            </div>
          )}

          {/* Items table */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px 28px',gap:4,marginBottom:6,fontSize:10,color:C.muted,fontWeight:700}}>
            <span>Description</span><span style={{textAlign:'center'}}>Qty</span><span style={{textAlign:'right'}}>Rate (₹)</span><span style={{textAlign:'right'}}>Amount</span><span/>
          </div>
          {(form.items||[]).map(it=>(
            <div key={it.id}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px 28px',gap:4,marginBottom:3,alignItems:'center'}}>
                <input value={it.desc} onChange={e=>updItem(it.id,'desc',e.target.value)} placeholder="Description" style={{...inp(),fontSize:12}}/>
                <input type="number" value={it.qty} onChange={e=>updItem(it.id,'qty',e.target.value)} min="1" style={{...inp(),fontSize:12,textAlign:'center'}}/>
                <input type="number" value={it.rate} onChange={e=>updItem(it.id,'rate',e.target.value)} min="0" style={{...inp(),fontSize:12,textAlign:'right'}}/>
                <div style={{fontSize:12,fontWeight:700,textAlign:'right',color:C.text}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</div>
                <button onClick={()=>removeItem(it.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:13}}>✕</button>
              </div>
              <div style={{gridColumn:'1/-1',marginBottom:6}}>
                <input value={it.italic_desc||''} onChange={e=>updItem(it.id,'italic_desc',e.target.value)} placeholder="Optional italic description…" style={{...inp(),fontSize:11,fontStyle:'italic',width:'calc(100% - 32px)'}}/>
              </div>
            </div>
          ))}
          <button onClick={addBlankItem} style={{...btn('#e5e7eb'),fontSize:11,padding:'5px 12px',color:C.muted,marginTop:4}}>+ Add Line</button>

          {/* Totals */}
          <div style={{borderTop:`1px solid ${C.border}`,marginTop:12,paddingTop:10}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,color:C.muted,marginBottom:4}}>
              <span>Subtotal</span><span style={{fontWeight:600,color:C.text}}>₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {parseFloat(form.tax_pct)>0&&(
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,color:C.muted,marginBottom:4}}>
                <span>GST ({form.tax_pct}%)</span><span style={{fontWeight:600,color:C.text}}>₹{taxAmt.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,color:BIZ_COLOR,borderTop:`2px solid ${BIZ_COLOR}`,paddingTop:8,marginTop:4}}>
              <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:8,textTransform:'uppercase',letterSpacing:'.05em'}}>Notes & Terms</div>
          <textarea value={form.notes||''} onChange={e=>upd('notes',e.target.value)} placeholder="Notes to client…" style={{...ta(50),width:'100%',marginBottom:8}}/>
          <textarea value={form.terms||''} onChange={e=>upd('terms',e.target.value)} placeholder="Terms & Conditions…" style={{...ta(50),width:'100%'}}/>
        </div>
      </div>

      {/* Right: Customization */}
      <div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:14,position:'sticky',top:80}}>
          <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:14,textTransform:'uppercase',letterSpacing:'.05em'}}>Customization</div>

          {/* Template */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6,fontWeight:600}}>Template</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              {[{id:'modern',label:'Modern',desc:'Clean with header bar'},{id:'classic',label:'Classic',desc:'Traditional columns'}].map(t=>(
                <div key={t.id} onClick={()=>upd('template',t.id)}
                  style={{border:`2px solid ${form.template===t.id?theme.primary:C.border}`,borderRadius:8,padding:'8px 10px',cursor:'pointer',background:form.template===t.id?theme.primary+'12':'transparent'}}>
                  <div style={{fontSize:12,fontWeight:700,color:form.template===t.id?theme.primary:C.text}}>{t.label}</div>
                  <div style={{fontSize:10,color:C.muted}}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6,fontWeight:600}}>Theme / Color</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {INV_THEMES.map(t=>(
                <div key={t.id} onClick={()=>upd('theme',t.id)} title={t.name}
                  style={{width:24,height:24,borderRadius:'50%',background:t.primary,border:`2px solid ${form.theme===t.id?'#0F0E1A':'transparent'}`,cursor:'pointer',boxShadow:form.theme===t.id?`0 0 0 2px ${t.primary}`:'none'}}/>
              ))}
            </div>
            <div style={{fontSize:11,color:theme.primary,fontWeight:600,marginTop:4}}>{INV_THEMES.find(t=>t.id===form.theme)?.name||'Default'}</div>
          </div>

          {/* Paper + Orientation */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Paper Size</div>
              <select value={form.paper||'A4'} onChange={e=>upd('paper',e.target.value)} style={{...inp(),width:'100%',fontSize:12}}>
                <option value="A4">A4</option><option value="A5">A5</option><option value="Letter">Letter</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Orientation</div>
              <select value={form.orientation||'portrait'} onChange={e=>upd('orientation',e.target.value)} style={{...inp(),width:'100%',fontSize:12}}>
                <option value="portrait">Portrait</option><option value="landscape">Landscape</option>
              </select>
            </div>
          </div>

          {/* Font */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Font</div>
            <select value={form.font||'Helvetica'} onChange={e=>upd('font',e.target.value)} style={{...inp(),width:'100%',fontSize:12}}>
              {FONTS_LIST.map(f=><option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Colors */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Label Color</div>
              <input type="color" value={form.label_color||theme.primary} onChange={e=>upd('label_color',e.target.value)} style={{width:'100%',height:36,borderRadius:6,border:`1px solid ${C.border}`,cursor:'pointer',padding:2}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Font Color</div>
              <input type="color" value={form.font_color||'#0F0E1A'} onChange={e=>upd('font_color',e.target.value)} style={{width:'100%',height:36,borderRadius:6,border:`1px solid ${C.border}`,cursor:'pointer',padding:2}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Background</div>
              <input type="color" value={form.bg_color||theme.bg} onChange={e=>upd('bg_color',e.target.value)} style={{width:'100%',height:36,borderRadius:6,border:`1px solid ${C.border}`,cursor:'pointer',padding:2}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Font Size</div>
              <select value={form.font_size||10} onChange={e=>upd('font_size',parseInt(e.target.value))} style={{...inp(),width:'100%',fontSize:12}}>
                {[8,9,10,11,12].map(s=><option key={s} value={s}>{s}pt</option>)}
              </select>
            </div>
          </div>

          <button onClick={onPreview} style={{...btn(BIZ_COLOR),width:'100%',marginTop:4}}>👁 Preview Invoice</button>
        </div>
      </div>
    </div>
  );
}

// ── Invoice Preview (Live in-app) ─────────────────────────────────────────────
// ── Clean invoice document — matches the plain, professional reference
// template (white box, grey heading rows, thin borders) instead of the
// old colour-block header. Used for both on-screen preview and PDF export.
function InvoicePreview({ form, clients, bizProfile }) {
  const client = clients.find(c=>c.id===form.client_id);
  const items = form.items||[];
  const subtotal = items.reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  const taxAmt   = subtotal*((parseFloat(form.tax_pct)||0)/100);
  const total    = subtotal+taxAmt;
  const isProforma = form.doc_type==='proforma';
  const docLabel = isProforma ? 'Proforma Invoice' : 'Invoice';
  const hasBank = bizProfile.biz_bank_acc||bizProfile.biz_bank_name;

  return (
    <div className="invoice-box" style={{maxWidth:800,margin:'0 auto',padding:30,border:'1px solid #eee',boxShadow:'0 0 10px rgba(0,0,0,.15)',fontSize:14,lineHeight:1.6,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",color:'#555',background:'#fff'}}>
      {/* Top: logo/name + doc details */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',paddingBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          {bizProfile.biz_logo_url
            ? <img src={bizProfile.biz_logo_url} alt="logo" style={{maxWidth:220,maxHeight:60,objectFit:'contain'}}/>
            : <div style={{fontSize:32,lineHeight:'32px',color:'#333',fontWeight:700}}>{bizProfile.biz_name||'Your Business'}</div>}
        </div>
        <div style={{textAlign:'right',fontSize:13}}>
          {isProforma && <div style={{display:'inline-block',fontSize:10,fontWeight:700,letterSpacing:'.08em',color:'#A06014',background:'#F5EBD9',borderRadius:4,padding:'2px 8px',marginBottom:6}}>PROFORMA — NOT A TAX INVOICE</div>}
          <div>{docLabel} #: {form.invoice_number||'—'}</div>
          <div>Created: {form.created_at?new Date(form.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}):new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
          {form.due_date&&<div>{isProforma?'Valid until':'Due'}: {form.due_date}</div>}
        </div>
      </div>

      {/* From / To */}
      <div style={{display:'flex',justifyContent:'space-between',paddingBottom:30,flexWrap:'wrap',gap:20}}>
        <div style={{fontSize:13,lineHeight:1.6}}>
          <strong>{bizProfile.biz_name||'Your Business'}</strong><br/>
          {bizProfile.biz_address&&<>{bizProfile.biz_address}<br/></>}
          {bizProfile.biz_email&&<>{bizProfile.biz_email}<br/></>}
          {bizProfile.biz_gstin&&<>GSTIN: {bizProfile.biz_gstin}</>}
        </div>
        <div style={{fontSize:13,lineHeight:1.6,textAlign:'right'}}>
          {client ? <>
            <strong>{client.name}</strong><br/>
            {client.company&&<>{client.company}<br/></>}
            {client.email&&<>{client.email}<br/></>}
            {client.gstin&&<>GSTIN: {client.gstin}</>}
          </> : <span style={{color:'#999'}}>No client selected</span>}
        </div>
      </div>

      {/* Payment method row — only if bank details exist */}
      {hasBank && (<>
        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:20}}>
          <thead><tr style={{background:'#eee',borderBottom:'1px solid #ddd'}}>
            <th style={{padding:'6px 5px',textAlign:'left',fontWeight:700,fontSize:13}}>Payment Method</th>
            <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Account</th>
          </tr></thead>
          <tbody><tr>
            <td style={{padding:'6px 5px'}}>{bizProfile.biz_bank_name||'Bank transfer'}</td>
            <td style={{padding:'6px 5px',textAlign:'right'}}>{bizProfile.biz_bank_acc}{bizProfile.biz_bank_ifsc?` · IFSC ${bizProfile.biz_bank_ifsc}`:''}</td>
          </tr></tbody>
        </table>
      </>)}

      {/* Items */}
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr style={{background:'#eee',borderBottom:'1px solid #ddd'}}>
          <th style={{padding:'6px 5px',textAlign:'left',fontWeight:700,fontSize:13}}>Item</th>
          <th style={{padding:'6px 5px',textAlign:'center',fontWeight:700,fontSize:13}}>Qty</th>
          <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Rate</th>
          <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Amount</th>
        </tr></thead>
        <tbody>
          {items.map((it,i)=>(
            <React.Fragment key={it.id||i}>
              <tr style={{borderBottom:(i===items.length-1 && !it.italic_desc)?'none':'1px solid #eee'}}>
                <td style={{padding:'6px 5px'}}>{it.desc||<span style={{color:'#999'}}>—</span>}</td>
                <td style={{padding:'6px 5px',textAlign:'center'}}>{it.qty||1}</td>
                <td style={{padding:'6px 5px',textAlign:'right'}}>₹{parseFloat(it.rate||0).toLocaleString('en-IN')}</td>
                <td style={{padding:'6px 5px',textAlign:'right'}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</td>
              </tr>
              {it.italic_desc&&<tr style={{borderBottom:i===items.length-1?'none':'1px solid #eee'}}>
                <td colSpan={4} style={{padding:'0 5px 6px',fontSize:12,color:'#777',fontStyle:'italic'}}>{it.italic_desc}</td>
              </tr>}
            </React.Fragment>
          ))}
          <tr><td colSpan={3} style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>Subtotal</td><td style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>₹{subtotal.toLocaleString('en-IN')}</td></tr>
          {parseFloat(form.tax_pct)>0&&<tr><td colSpan={3} style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>GST ({form.tax_pct}%)</td><td style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>₹{taxAmt.toLocaleString('en-IN')}</td></tr>}
          <tr>
            <td colSpan={3} style={{textAlign:'right',padding:'8px 5px',borderTop:'2px solid #eee',fontWeight:700}}>Total:</td>
            <td style={{textAlign:'right',padding:'8px 5px',borderTop:'2px solid #eee',fontWeight:700}}>₹{total.toLocaleString('en-IN')}</td>
          </tr>
        </tbody>
      </table>

      {form.notes&&<div style={{fontSize:13,marginTop:20}}><strong>Notes:</strong> {form.notes}</div>}
      {form.terms&&<div style={{fontSize:12,color:'#777',marginTop:form.notes?8:20}}>{form.terms}</div>}
      {isProforma&&<div style={{fontSize:11,color:'#A06014',marginTop:12,fontStyle:'italic'}}>This is a proforma invoice for reference only — it is not a demand for payment.</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROPOSALS TAB v2 — with dynamic business details + proposal editor
// ══════════════════════════════════════════════════════════════════════════════
function ProposalsTab({ ctx }) {
  const C = useC();
  const { proposals=[], clients=[], leads=[], services=[], bizProfile={}, addProposal, updateProposal, deleteProposal, uid, senderProfiles=[], userPlan } = ctx;
  const [view, setView] = React.useState('list');
  const [editProp, setEditProp] = React.useState(null);
  const [previewProp, setPreviewProp] = React.useState(null);
  const [propShareModal,  setPropShareModal]  = React.useState(null);
  const [propEmailModal,  setPropEmailModal]  = React.useState(null);

  const openPropEmailModal = (prop) => {
    const token = prop.share_token || ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
    if (!prop.share_token) updateProposal(prop.id, {is_shared:true, share_token:token});
    const link = `${window.location.origin}/proposal/${token}`;
    setPropEmailModal({
      to:         (clients||[]).find(c=>c.id===prop.client_id)?.email || '',
      from_email: bizProfile?.biz_email || '',
      from_name:  bizProfile?.biz_name  || '',
      subject:    `Proposal: ${prop.title}`,
      message:    `Hi,\n\nPlease find our proposal attached.\n\nView it here: ${link}\n\nValid until: ${prop.valid_until||'—'}\n\nLooking forward to hearing from you!`,
      link, type: 'proposal',
    });
  };

  const blankProp = {
    client_id:'', lead_id:'', title:'Business Proposal',
    items:[], tax_pct:0, valid_until:'', notes:'', terms:bizProfile.biz_terms||'This proposal is valid for 30 days.',
    // Proposal-specific fields
    executive_summary:'', understanding:'', why_us:'', next_steps:'', best_practices:'', acceptance_text:'',
    placeholder_values:{},
    cover_title:'Business Proposal', theme:'default', theme_bg:'', theme_primary:'', theme_font:'',
    services_scope:[], // selected services as scope of work
  };
  const [form, setForm] = React.useState({...blankProp});
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const openNew = () => {
    setEditProp(null);
    setForm({
      ...blankProp,
      terms: bizProfile.biz_terms||'This proposal is valid for 30 days.',
      executive_summary: bizProfile.prop_default_executive_summary||'',
      why_us: bizProfile.prop_default_why_us||'',
      next_steps: bizProfile.prop_default_next_steps||'',
      best_practices: bizProfile.prop_default_best_practices||'',
      theme_bg: bizProfile.prop_theme_bg||'', theme_primary: bizProfile.prop_theme_primary||'', theme_font: bizProfile.prop_theme_font||'',
      items:[{id:Date.now(),service_id:'',desc:'',italic_desc:'',qty:1,rate:'',amount:0}],
    });
    setView('create');
  };
  const openEdit = (p) => { setEditProp(p); setForm({...blankProp,...p,items:(p.items||[]).map((it,i)=>({...it,id:it.id||i}))}); setView('create'); };
  const save = async () => {
    if (!form.title.trim()) return;
    // Resolve {{placeholders}} once at save time — client/company are auto-filled,
    // any custom placeholders come from the per-proposal values the user typed in.
    const party = clients.find(c=>c.id===form.client_id) || leads.find(l=>l.id===form.lead_id);
    const values = buildPlaceholderValues(bizProfile, party, form.placeholder_values);
    const resolved = {
      ...form,
      executive_summary: applyPlaceholders(form.executive_summary, values),
      why_us: applyPlaceholders(form.why_us, values),
      next_steps: applyPlaceholders(form.next_steps, values),
      best_practices: applyPlaceholders(form.best_practices, values),
      terms: applyPlaceholders(form.terms, values),
    };
    if (editProp) updateProposal(editProp.id, resolved);
    else await addProposal(resolved);
    setView('list');
  };

  const filtered = proposals;

  return (
    <div>
      {view==='list'&&(
        <>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <span style={{fontWeight:700,fontSize:15,color:BIZ_COLOR}}>📄 Proposals ({proposals.length})</span>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setView('template')} style={{...btn('#475569'),fontSize:12}}>📐 Content Template</button>
              <button onClick={openNew} style={btn(BIZ_COLOR)}>+ New Proposal</button>
            </div>
          </div>
          {filtered.length===0&&<div style={{textAlign:'center',color:C.muted,padding:40}}>No proposals yet.</div>}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {filtered.map(prop=>{
              const client=clients.find(c=>c.id===prop.client_id)||leads.find(l=>l.id===prop.lead_id);
              const st=PROP_STATUSES.find(s=>s.id===prop.status)||PROP_STATUSES[0];
              const theme=INV_THEMES.find(t=>t.id===(prop.theme||'default'))||INV_THEMES[0];
              const rowPrimary = prop.theme_primary || theme.primary;
              return(
                <div key={prop.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:6,height:40,borderRadius:3,background:rowPrimary,flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13}}>{prop.title}
                      <span style={{marginLeft:8,fontSize:10,padding:'1px 7px',borderRadius:10,background:st.color+'22',color:st.color,fontWeight:600}}>{st.label}</span>
                    </div>
                    <div style={{fontSize:11,color:C.muted}}>{client?.name||'—'}{prop.valid_until?` · Valid: ${prop.valid_until}`:''}</div>
                  </div>
                  <div style={{fontSize:16,fontWeight:800,color:BIZ_COLOR}}>₹{parseFloat(prop.total||0).toLocaleString('en-IN')}</div>
                  <div style={{display:'flex',gap:5}}>
                    <select value={prop.status} onChange={e=>updateProposal(prop.id,{status:e.target.value})} style={{fontSize:11,padding:'3px 6px',border:`1px solid ${C.border}`,borderRadius:6,background:C.surface,cursor:'pointer'}}>
                      {PROP_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <button onClick={()=>setPreviewProp(prop)} style={{...btn('#475569'),fontSize:11,padding:'4px 8px'}}>👁 View</button>
                    <button onClick={()=>openEdit(prop)} style={{...btn(C.amber),fontSize:11,padding:'4px 8px'}}>✏️</button>
                    <button onClick={()=>setPropShareModal({item:prop,type:'proposal'})} style={{...btn('#7C3AED'),fontSize:11,padding:'4px 8px'}}>🔗 Share</button>
                    <button onClick={()=>openPropEmailModal(prop)} style={{...btn('#2563EB'),fontSize:11,padding:'4px 8px'}}>📧 Email</button>
                    <button onClick={()=>{if(window.confirm('Delete?'))deleteProposal(prop.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view==='create'&&(
        <ProposalEditor form={form} upd={upd} clients={clients} leads={leads} services={services} bizProfile={bizProfile}
          editProp={editProp} onSave={save} onCancel={()=>setView('list')} onPreview={()=>setPreviewProp(form)} updateBizProfile={ctx.updateBizProfile}/>
      )}

      {view==='template'&&(
        <ProposalTemplateEditor bizProfile={bizProfile} updateBizProfile={ctx.updateBizProfile} onDone={()=>setView('list')}/>
      )}

      {previewProp && ReactDOM.createPortal(
        <ProposalPreviewModal prop={previewProp} clients={clients} leads={leads} bizProfile={bizProfile} userPlan={userPlan} onClose={()=>setPreviewProp(null)}/>,
        document.body
      )}
      {propEmailModal && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,zIndex:4500,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(4px)'}} onClick={e=>e.target===e.currentTarget&&setPropEmailModal(null)}>
          <div style={{background:C.surface,borderRadius:16,padding:24,width:'100%',maxWidth:500,boxShadow:'0 24px 80px rgba(0,0,0,.3)'}}>
            <div style={{fontWeight:800,fontSize:16,marginBottom:16,color:C.text}}>📧 Send Proposal by Email</div>
            {/* Sender selector */}
            <div style={{marginBottom:10}}>
              <label style={lbl}>Send As (Reply-To)</label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                <button onClick={()=>setPropEmailModal(p=>({...p,from_name:bizProfile?.biz_name||'',from_email:bizProfile?.biz_email||''}))}
                  style={{fontSize:11,padding:'5px 12px',borderRadius:20,border:`1.5px solid ${propEmailModal?.from_email===bizProfile?.biz_email?BIZ_COLOR:C.border}`,background:propEmailModal?.from_email===bizProfile?.biz_email?BIZ_COLOR+'18':'transparent',color:propEmailModal?.from_email===bizProfile?.biz_email?BIZ_COLOR:C.muted,cursor:'pointer',fontWeight:700}}>
                  🏢 {bizProfile?.biz_name||'Business'}
                </button>
                {senderProfiles.map(s=>(
                  <button key={s.id} onClick={()=>setPropEmailModal(p=>({...p,from_name:s.name,from_email:s.email}))}
                    style={{fontSize:11,padding:'5px 12px',borderRadius:20,border:`1.5px solid ${propEmailModal?.from_email===s.email?BIZ_COLOR:C.border}`,background:propEmailModal?.from_email===s.email?BIZ_COLOR+'18':'transparent',color:propEmailModal?.from_email===s.email?BIZ_COLOR:C.muted,cursor:'pointer'}}>
                    👤 {s.name}
                  </button>
                ))}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div><label style={lbl}>Sender Name</label><input value={propEmailModal?.from_name||''} onChange={e=>setPropEmailModal(p=>({...p,from_name:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
                <div><label style={lbl}>Reply-To Email</label><input value={propEmailModal?.from_email||''} onChange={e=>setPropEmailModal(p=>({...p,from_email:e.target.value}))} type="email" style={{...inp(),width:'100%'}}/></div>
              </div>
            </div>
            <div style={{marginBottom:10}}><label style={lbl}>To *</label><input value={propEmailModal?.to||''} onChange={e=>setPropEmailModal(p=>({...p,to:e.target.value}))} placeholder="client@example.com" style={{...inp(),width:'100%'}}/></div>
            <div style={{marginBottom:10}}><label style={lbl}>Subject</label><input value={propEmailModal?.subject||''} onChange={e=>setPropEmailModal(p=>({...p,subject:e.target.value}))} style={{...inp(),width:'100%'}}/></div>
            <div style={{marginBottom:14}}><label style={lbl}>Message</label><textarea value={propEmailModal?.message||''} onChange={e=>setPropEmailModal(p=>({...p,message:e.target.value}))} style={{...ta(100),width:'100%'}}/></div>
            <div style={{background:BIZ_COLOR+'10',border:`1px solid ${BIZ_COLOR}30`,borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:11,color:BIZ_COLOR}}>
              📎 Proposal link: <strong>{propEmailModal?.link}</strong>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setPropEmailModal(null)} style={{...btn('#999'),padding:'8px 18px'}}>Cancel</button>
              <button onClick={async()=>{
                if(!propEmailModal?.to?.trim())return;
                try{
                  await fetch('/api/sb/functions/v1/send-booking-email',{
                    method:'POST',
                    headers:{'Content-Type':'application/json','Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}`},
                    body:JSON.stringify({type:'proposal',to_email:propEmailModal.to,from_email:propEmailModal.from_email,from_name:propEmailModal.from_name,subject:propEmailModal.subject,message:propEmailModal.message,link:propEmailModal.link,host_id:uid}),
                  });
                  ctx.setAlert?.('✅ Proposal emailed to '+propEmailModal.to);
                }catch(e){ctx.setAlert?.('❌ '+e.message);}
                setPropEmailModal(null);
              }} style={{...btn(BIZ_COLOR),padding:'8px 20px',fontWeight:700}}>📤 Send</button>
            </div>
          </div>
        </div>, document.body
      )}
      {propShareModal && ReactDOM.createPortal(
        <ShareLinkModal
          item={propShareModal.item}
          type="proposal"
          onShare={async (expires)=>{
            const exp = expires ? new Date(Date.now() + expires).toISOString() : null;
            // Generate share_token if not already set
            const token = propShareModal.item.share_token ||
              ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
            updateProposal(propShareModal.item.id, {is_shared:true, share_token:token, share_expires_at:exp});
            const url = `${window.location.origin}/proposal/${token}`;
            navigator.clipboard.writeText(url);
            setPropShareModal(null);
            alert(`🔗 Link copied!\n\n${url}\n\nAnyone with this link can view and download the proposal as PDF.${exp?`\n\nExpires: ${new Date(exp).toLocaleDateString('en-IN')}`:'\n\nNo expiry set.'}`);
          }}
          onClose={()=>setPropShareModal(null)}
        />, document.body
      )}
    </div>
  );
}

// ── Reusable proposal content template: edited once, reused on every new proposal ──
function ProposalTemplateEditor({ bizProfile, updateBizProfile, onDone }) {
  const C = useC();
  const [tpl, setTpl] = React.useState({
    prop_default_executive_summary: bizProfile.prop_default_executive_summary||'',
    prop_default_why_us: bizProfile.prop_default_why_us||'',
    prop_default_next_steps: bizProfile.prop_default_next_steps||'',
    prop_default_best_practices: bizProfile.prop_default_best_practices||'',
    prop_placeholders: bizProfile.prop_placeholders?.length ? bizProfile.prop_placeholders : [
      {key:'client_name',label:'Client Name',default_value:''},
      {key:'company_name',label:'Your Company Name',default_value:''},
    ],
  });
  const updTpl = (k,v) => setTpl(p=>({...p,[k]:v}));
  const updPh = (i,field,v) => setTpl(p=>({...p,prop_placeholders:p.prop_placeholders.map((ph,idx)=>idx===i?{...ph,[field]:v}:ph)}));
  const addPh = () => setTpl(p=>({...p,prop_placeholders:[...p.prop_placeholders,{key:'',label:'',default_value:''}]}));
  const remPh = (i) => setTpl(p=>({...p,prop_placeholders:p.prop_placeholders.filter((_,idx)=>idx!==i)}));
  const [saving, setSaving] = React.useState(false);
  const save = async () => {
    setSaving(true);
    await updateBizProfile({...tpl, prop_placeholders: tpl.prop_placeholders.filter(p=>p.key.trim())});
    setSaving(false);
    onDone();
  };

  const FIELDS = [
    {k:'prop_default_executive_summary', l:'Executive Summary'},
    {k:'prop_default_why_us', l:'Why Us'},
    {k:'prop_default_next_steps', l:'Next Steps'},
    {k:'prop_default_best_practices', l:'Our Approach'},
  ];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <div style={{fontWeight:800,fontSize:15,color:BIZ_COLOR}}>📐 Reusable Content Template</div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={save} disabled={saving} style={{...btn(BIZ_COLOR),fontSize:12}}>{saving?'Saving…':'💾 Save'}</button>
          <button onClick={onDone} style={{...btn('#999'),fontSize:12}}>Cancel</button>
        </div>
      </div>
      <div style={{fontSize:12,color:C.muted,marginBottom:16,maxWidth:640,lineHeight:1.6}}>
        This content is copied into every <em>new</em> proposal automatically, so you don't have to retype it each time — you can still edit it per-proposal afterward for anything client-specific.
        Use <code>{'{{token}}'}</code> in the text below; <code>{'{{client_name}}'}</code> and <code>{'{{company_name}}'}</code> fill in automatically, and any other placeholders you define below get asked for once per proposal.
      </div>

      <div style={{display:'grid',gap:12,marginBottom:20}}>
        {FIELDS.map(f=>(
          <div key={f.k}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:4}}>{f.l}</div>
            <textarea value={tpl[f.k]} onChange={e=>updTpl(f.k,e.target.value)} style={{...ta(110),width:'100%'}}/>
          </div>
        ))}
      </div>

      <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>Placeholders</div>
      <div style={{display:'grid',gap:6,marginBottom:10}}>
        {tpl.prop_placeholders.map((ph,i)=>(
          <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 28px',gap:6,alignItems:'center'}}>
            <input value={ph.key} onChange={e=>updPh(i,'key',e.target.value.replace(/[^a-zA-Z0-9_]/g,''))} placeholder="key (e.g. industry)" style={{...inp(),fontSize:12}}/>
            <input value={ph.label} onChange={e=>updPh(i,'label',e.target.value)} placeholder="Label shown to you" style={{...inp(),fontSize:12}}/>
            <input value={ph.default_value} onChange={e=>updPh(i,'default_value',e.target.value)} placeholder="Default value (optional)" style={{...inp(),fontSize:12}}/>
            <button onClick={()=>remPh(i)} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:14}}>✕</button>
          </div>
        ))}
      </div>
      <button onClick={addPh} style={{...btn('#e5e7eb'),fontSize:11,padding:'5px 12px',color:C.muted}}>+ Add Placeholder</button>
    </div>
  );
}

function ProposalEditor({ form, upd, clients, leads, services, bizProfile, editProp, onSave, onCancel, onPreview, updateBizProfile }) {
  const C = useC();
  const theme = INV_THEMES.find(t=>t.id===form.theme)||INV_THEMES[0];
  const addItem = (svcId) => {
    const svc = services.find(s=>s.id===svcId);
    upd('items',[...(form.items||[]),{id:Date.now(),service_id:svcId||'',desc:svc?.name||'',italic_desc:svc?.description||'',qty:1,rate:svc?.price||'',amount:svc?.price||0}]);
  };
  const addBlank = () => upd('items',[...(form.items||[]),{id:Date.now(),service_id:'',desc:'',italic_desc:'',qty:1,rate:'',amount:0}]);
  const updItem = (id,field,val) => upd('items',(form.items||[]).map(it=>{if(it.id!==id)return it;const u={...it,[field]:val};if(field==='qty'||field==='rate')u.amount=(parseFloat(field==='qty'?val:it.qty)||1)*(parseFloat(field==='rate'?val:it.rate)||0);return u;}));
  const remItem = (id) => upd('items',(form.items||[]).filter(it=>it.id!==id));
  const subtotal = (form.items||[]).reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  const taxAmt = subtotal*((parseFloat(form.tax_pct)||0)/100);
  const total = subtotal+taxAmt;

  const clientsAndLeads = [...clients.map(c=>({...c,_type:'client'})),...leads.map(l=>({...l,_type:'lead'}))];
  const selParty = clientsAndLeads.find(x=>x.id===form.client_id||x.id===form.lead_id);

  const [section, setSection] = React.useState('basics');
  const SECTIONS = [{id:'basics',l:'📋 Basics'},{id:'scope',l:'📦 Scope & Pricing'},{id:'content',l:'✏️ Content'},{id:'design',l:'🎨 Design'}];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontWeight:800,fontSize:15,color:BIZ_COLOR}}>{editProp?'Edit Proposal':'New Proposal'}</div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={onPreview} style={{...btn('#475569'),fontSize:12}}>👁 Preview</button>
          <button onClick={onSave} style={{...btn(BIZ_COLOR),fontSize:12}}>💾 Save</button>
          <button onClick={onCancel} style={{...btn('#999'),fontSize:12}}>Cancel</button>
        </div>
      </div>

      {/* Section nav */}
      <div style={{display:'flex',gap:4,background:'#F0F0F5',borderRadius:10,padding:4,marginBottom:14}}>
        {SECTIONS.map(s=>(
          <button key={s.id} onClick={()=>setSection(s.id)} style={{background:section===s.id?BIZ_COLOR:'transparent',color:section===s.id?'#fff':C.muted,border:'none',borderRadius:7,padding:'6px 12px',cursor:'pointer',fontFamily:FONT,fontSize:12,fontWeight:section===s.id?700:400}}>{s.l}</button>
        ))}
      </div>

      {section==='basics'&&(
        <div style={{display:'grid',gap:10}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Proposal Title (Cover)</div>
              <input value={form.cover_title||form.title||''} onChange={e=>{upd('cover_title',e.target.value);upd('title',e.target.value);}} placeholder="Business Proposal" style={{...inp(),width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Client / Lead</div>
              <select value={form.client_id||form.lead_id||''} onChange={e=>{const x=clientsAndLeads.find(c=>c.id===e.target.value);if(x?._type==='lead')upd('lead_id',e.target.value);else upd('client_id',e.target.value);}} style={{...inp(),width:'100%'}}>
                <option value="">Select…</option>
                {clients.map(c=><option key={c.id} value={c.id}>👤 {c.name}</option>)}
                {leads.map(l=><option key={l.id} value={l.id}>🎯 {l.name} (lead)</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Valid Until</div>
              <input type="date" value={form.valid_until||''} onChange={e=>upd('valid_until',e.target.value)} style={{...inp(),width:'100%'}}/>
            </div>
          </div>
        </div>
      )}

      {section==='scope'&&(
        <div>
          {services.length>0&&(
            <select onChange={e=>{if(e.target.value)addItem(e.target.value);e.target.value='';}} style={{...inp(),width:'100%',fontSize:12,marginBottom:10}} defaultValue="">
              <option value="">+ Add from Services Library…</option>
              {services.map(s=><option key={s.id} value={s.id}>{s.name} — ₹{parseFloat(s.price||0).toLocaleString('en-IN')}/{s.unit}</option>)}
            </select>
          )}
          <div style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px 28px',gap:4,marginBottom:6,fontSize:10,color:C.muted,fontWeight:700}}>
            <span>Description</span><span style={{textAlign:'center'}}>Qty</span><span style={{textAlign:'right'}}>Rate (₹)</span><span style={{textAlign:'right'}}>Amount</span><span/>
          </div>
          {(form.items||[]).map(it=>(
            <div key={it.id}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px 28px',gap:4,marginBottom:3,alignItems:'center'}}>
                <input value={it.desc} onChange={e=>updItem(it.id,'desc',e.target.value)} placeholder="Description" style={{...inp(),fontSize:12}}/>
                <input type="number" value={it.qty} onChange={e=>updItem(it.id,'qty',e.target.value)} style={{...inp(),fontSize:12,textAlign:'center'}}/>
                <input type="number" value={it.rate} onChange={e=>updItem(it.id,'rate',e.target.value)} style={{...inp(),fontSize:12,textAlign:'right'}}/>
                <div style={{fontSize:12,fontWeight:700,textAlign:'right'}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</div>
                <button onClick={()=>remItem(it.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:13}}>✕</button>
              </div>
              <input value={it.italic_desc||''} onChange={e=>updItem(it.id,'italic_desc',e.target.value)} placeholder="Optional scope detail (italic)…" style={{...inp(),fontSize:11,fontStyle:'italic',width:'calc(100% - 32px)',marginBottom:6}}/>
            </div>
          ))}
          <button onClick={addBlank} style={{...btn('#e5e7eb'),fontSize:11,padding:'5px 12px',color:C.muted}}>+ Add Line</button>
          <div style={{borderTop:`1px solid ${C.border}`,marginTop:12,paddingTop:10}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,marginBottom:4}}>
              <span style={{color:C.muted}}>Subtotal</span><span style={{fontWeight:600}}>₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,marginBottom:4}}>
              <span style={{color:C.muted}}>Tax / GST % <span style={{fontSize:10,color:C.light}}>(leave 0 for no tax)</span></span>
              <input type="number" value={form.tax_pct||''} onChange={e=>upd('tax_pct',e.target.value)} placeholder="0" style={{...inp(),width:70,textAlign:'right'}}/>
            </div>
            {parseFloat(form.tax_pct)>0&&(
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                <span style={{color:C.muted}}>GST ({form.tax_pct}%)</span><span style={{fontWeight:600,color:C.text}}>₹{taxAmt.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,color:BIZ_COLOR}}>
              <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      )}

      {section==='content'&&(
        <div style={{display:'grid',gap:12}}>
          {(bizProfile.prop_placeholders||[]).filter(p=>p.key&&p.key!=='client_name'&&p.key!=='company_name').length>0&&(
            <div style={{background:'#F5F3FF',border:'1px solid #DDD6FE',borderRadius:8,padding:12}}>
              <div style={{fontSize:11,fontWeight:700,color:'#7C3AED',marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>Fill in this proposal's placeholders</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                {(bizProfile.prop_placeholders||[]).filter(p=>p.key&&p.key!=='client_name'&&p.key!=='company_name').map(p=>(
                  <div key={p.key}>
                    <div style={{fontSize:10,color:C.muted,marginBottom:2}}>{p.label||p.key}</div>
                    <input value={form.placeholder_values?.[p.key]??p.default_value??''} onChange={e=>upd('placeholder_values',{...(form.placeholder_values||{}),[p.key]:e.target.value})} style={{...inp(),width:'100%',fontSize:12}}/>
                  </div>
                ))}
              </div>
            </div>
          )}
          {[
            {k:'executive_summary',l:'Executive Summary',ph:`Dear ${selParty?.name||'[Client Name]'},\n\nThank you for the opportunity to present this proposal...`},
            {k:'understanding',l:'Our Understanding (from discovery call)',ph:'We understand that you are looking to...'},
            {k:'why_us',l:`Why ${bizProfile.biz_name||'Us'}`,ph:'Our experience, approach, and why we are the right partner...'},
            {k:'next_steps',l:'Next Steps',ph:'1. Review proposal\n2. Schedule kickoff call\n3. Sign agreement'},
            {k:'best_practices',l:'Our Approach',ph:'How we approach this kind of engagement — our process, methodology, and what to expect...'},
            {k:'terms',l:'Terms & Conditions',ph:'Payment due within 15 days...'},
            {k:'acceptance_text',l:'Acceptance Clause',ph:`By signing, ${selParty?.name||'[Client]'} accepts this proposal and authorises ${bizProfile.biz_name||'[Your Company]'} to proceed.`},
          ].map(f=>(
            <div key={f.k}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:4}}>{f.l}</div>
              <textarea value={form[f.k]||''} onChange={e=>upd(f.k,e.target.value)} placeholder={f.ph} style={{...ta(90),width:'100%'}}/>
            </div>
          ))}
        </div>
      )}

      {section==='design'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:8}}>Theme Color (presets)</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
              {INV_THEMES.map(t=>(
                <div key={t.id} onClick={()=>upd('theme',t.id)} title={t.name}
                  style={{width:28,height:28,borderRadius:'50%',background:t.primary,border:`2px solid ${form.theme===t.id&&!form.theme_primary?'#000':'transparent'}`,cursor:'pointer',boxShadow:form.theme===t.id&&!form.theme_primary?`0 0 0 3px ${t.primary}40`:''}}/>
              ))}
            </div>
            <div style={{fontSize:13,color:theme.primary,fontWeight:700}}>{INV_THEMES.find(t=>t.id===form.theme)?.name||'Default'}</div>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>Cover Page Title</div>
            <input value={form.cover_title||''} onChange={e=>upd('cover_title',e.target.value)} placeholder="BUSINESS PROPOSAL" style={{...inp(),width:'100%',textTransform:'uppercase'}}/>
          </div>

          <div style={{gridColumn:'1/-1',borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:2}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:10}}>Custom Colors (override the preset above)</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:12}}>
              {[
                {k:'theme_bg',l:'Background Color',fallback:theme.bg},
                {k:'theme_primary',l:'Theme Color',fallback:theme.primary},
                {k:'theme_font',l:'Font Color',fallback:'#0F0E1A'},
              ].map(c=>(
                <div key={c.k}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:4}}>{c.l}</div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <input type="color" value={form[c.k]||c.fallback} onChange={e=>upd(c.k,e.target.value)} style={{width:36,height:30,padding:0,border:`1px solid ${C.border}`,borderRadius:6,cursor:'pointer',background:'none'}}/>
                    <input value={form[c.k]||''} onChange={e=>upd(c.k,e.target.value)} placeholder={c.fallback} style={{...inp(),width:'100%',fontSize:11,fontFamily:'monospace'}}/>
                    {form[c.k]&&<button onClick={()=>upd(c.k,'')} title="Reset to preset" style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:13}}>✕</button>}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={()=>updateBizProfile?.({prop_theme_bg:form.theme_bg||'',prop_theme_primary:form.theme_primary||'',prop_theme_font:form.theme_font||''})}
              style={{...btn('#7C3AED'),fontSize:11,padding:'6px 14px'}}>⭐ Save as My Default Colors</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProposalPreviewModal({ prop, clients, leads, bizProfile, userPlan, onClose }) {
  const theme = INV_THEMES.find(t=>t.id===(prop.theme||'default'))||INV_THEMES[0];
  const primary = prop.theme_primary || theme.primary;
  const pageBg = prop.theme_bg || theme.bg || '#fff';
  const fontColor = prop.theme_font || '#0F0E1A';
  const client = [...(clients||[]),...(leads||[])].find(x=>x.id===prop.client_id||x.id===prop.lead_id);
  const items = prop.items||[];
  const subtotal = items.reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  const taxAmt = subtotal*((parseFloat(prop.tax_pct)||0)/100);
  const total = subtotal+taxAmt;

  const printNow = () => {
    printHtml(`<!DOCTYPE html><html><head><title>${prop.title||'Proposal'}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
      *{box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
      body{font-family:'Inter',Arial,sans-serif;color:${fontColor};background:${pageBg};padding:0;margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .cover{background:${primary} !important;color:#fff !important;padding:80px 60px;text-align:center;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;page-break-after:always;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .cover *{color:#fff !important;}
      .section{padding:40px 60px;page-break-inside:avoid;break-inside:avoid;}
      .section h2{color:${primary};border-bottom:2px solid ${primary};padding-bottom:6px;margin-bottom:16px;font-size:18px;font-family:'Inter',Arial,sans-serif;page-break-after:avoid;break-after:avoid;}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      thead{display:table-header-group;}
      tr{page-break-inside:avoid;break-inside:avoid;}
      th{background:${primary} !important;color:#fff !important;padding:8px 12px;text-align:left;font-size:12px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      td{padding:8px 12px;border-bottom:1px solid #eee;font-size:13px}
      .total-row td{font-weight:800;font-size:15px;color:${primary};border-top:2px solid ${primary}}
      .sig-box{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:40px;page-break-inside:avoid;break-inside:avoid;}
      .sig-line{border-top:1px solid #999;padding-top:8px;font-size:11px;color:#666}
      @page{margin:0;}
      @media print{
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
        .cover{background:${primary} !important;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
        th{background:${primary} !important;-webkit-print-color-adjust:exact !important;}
        .no-print{display:none}
      }
    </style></head><body>
    <div class="cover">
      ${bizProfile.biz_logo_url?`<img src="${bizProfile.biz_logo_url}" style="height:60px;margin-bottom:24px;object-fit:contain">`:''}
      <h1 style="font-size:42px;letter-spacing:-.02em;margin-bottom:8px">${bizProfile.biz_name||'Your Business'}</h1>
      <div style="opacity:.7;font-size:14px;margin-bottom:48px">${bizProfile.biz_tagline||''}</div>
      <div style="background:rgba(255,255,255,.15);padding:4px;border-radius:4px;display:flex;gap:0;margin-bottom:48px;width:200px">
        <div style="height:4px;flex:1;background:#fff"></div><div style="height:4px;flex:1;background:rgba(255,255,255,.5)"></div><div style="height:4px;flex:1;background:rgba(255,255,255,.25)"></div>
      </div>
      <div style="font-size:28px;font-weight:900;letter-spacing:.12em;margin-bottom:32px">${(prop.cover_title||prop.title||'BUSINESS PROPOSAL').toUpperCase()}</div>
      ${client?`<div style="font-size:20px;opacity:.9;margin-bottom:8px">Prepared for: <strong>${client.name}</strong></div>`:''}
      <div style="font-size:13px;opacity:.7">Date: ${new Date().toLocaleDateString('en-IN')}</div>
    </div>
    <div class="section" style="padding-bottom:24px;">
      <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:24px;margin-bottom:20px;border-bottom:1px solid #eee;padding-bottom:20px;">
        <div style="font-size:12px;line-height:1.8;color:#555;">
          <strong style="color:${primary};font-size:14px;">${bizProfile.biz_name||'Your Business'}</strong><br>
          ${bizProfile.biz_address?`${bizProfile.biz_address.replace(/\n/g,'<br>')}<br>`:''}
          ${[bizProfile.biz_email,bizProfile.biz_phone].filter(Boolean).join(' · ')}${bizProfile.biz_email||bizProfile.biz_phone?'<br>':''}
          ${bizProfile.biz_website?`${bizProfile.biz_website}<br>`:''}
          ${bizProfile.biz_gstin?`GSTIN: ${bizProfile.biz_gstin}`:''}
        </div>
        <div style="font-size:12px;color:#555;text-align:right;">
          <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
          ${prop.valid_until?`<div style="margin-top:4px"><strong>Valid Until:</strong> ${new Date(prop.valid_until).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>`:''}
        </div>
      </div>
      <div style="font-size:12px;color:#555;line-height:1.8;margin-bottom:16px;">
        <strong style="color:#333">To,</strong><br>
        ${client?.name||'[Client Name]'}<br>
        ${client?.company?`${client.company}<br>`:''}
        ${client?.address?`${client.address.replace(/\n/g,'<br>')}<br>`:''}
      </div>
      <div style="font-size:13px;color:#333"><strong>Subject:</strong> Proposal for ${prop.title||'Business Services'}</div>
    </div>
    ${prop.executive_summary?`<div class="section"><h2>Executive Summary</h2><p>${prop.executive_summary.replace(/\n/g,'<br>')}</p></div>`:''}
    ${prop.understanding?`<div class="section" style="background:#f9f9f9"><h2>Our Understanding</h2><p>${prop.understanding.replace(/\n/g,'<br>')}</p></div>`:''}
    <div class="section"><h2>Scope & Investment</h2>
      <table><tr><th>Description</th><th>Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amount</th></tr>
        ${items.map((it,i)=>`<tr style="background:${i%2?'#f9f9f9':'transparent'}"><td>${it.desc||''}${it.italic_desc?`<div style="font-style:italic;color:#666;font-size:11px;margin-top:2px">${it.italic_desc}</div>`:''}</td><td style="text-align:center">${it.qty||1}</td><td style="text-align:right">₹${parseFloat(it.rate||0).toLocaleString('en-IN')}</td><td style="text-align:right">₹${parseFloat(it.amount||0).toLocaleString('en-IN')}</td></tr>`).join('')}
        <tr><td colspan="3" style="text-align:right">Subtotal</td><td style="text-align:right">₹${subtotal.toLocaleString('en-IN')}</td></tr>
        ${parseFloat(prop.tax_pct)>0?`<tr><td colspan="3" style="text-align:right">GST (${prop.tax_pct}%)</td><td style="text-align:right">₹${taxAmt.toLocaleString('en-IN')}</td></tr>`:''}
        <tr class="total-row"><td colspan="3" style="text-align:right">TOTAL</td><td style="text-align:right">₹${total.toLocaleString('en-IN')}</td></tr>
      </table>
    </div>
    ${prop.why_us?`<div class="section" style="background:#f9f9f9"><h2>Why ${bizProfile.biz_name||'Us'}</h2><p>${prop.why_us.replace(/\n/g,'<br>')}</p></div>`:''}
    ${prop.next_steps?`<div class="section"><h2>Next Steps</h2><p>${prop.next_steps.replace(/\n/g,'<br>')}</p></div>`:''}
    ${prop.best_practices?`<div class="section" style="background:#f9f9f9"><h2>Our Approach</h2><p>${prop.best_practices.replace(/\n/g,'<br>')}</p></div>`:''}
    ${prop.terms?`<div class="section"><h2>Terms & Conditions</h2><p style="font-size:12px">${prop.terms.replace(/\n/g,'<br>')}</p></div>`:''}
    <div class="section"><h2>Proposal Acceptance</h2>
      <p style="font-size:13px">${prop.acceptance_text||`By signing below, ${client?.name||'[Client]'} accepts this proposal and authorises ${bizProfile.biz_name||'[Your Company]'} to proceed.`}</p>
      <div class="sig-box" style="margin-top:40px">
        <div><div class="sig-line">Authorised Signatory<br>${client?.name||'[Client Name]'}${client?.company?`<br>${client.company}`:''}<br>Date: _____________</div></div>
        <div><div class="sig-line">Authorised Signatory<br>${bizProfile.biz_name||'[Your Company]'}<br>Date: _____________</div></div>
      </div>
    </div>
    <div style="text-align:center;padding:16px 20px;font-size:10px;color:#999;border-top:1px solid #eee;">${bizProfile.biz_name||'Your Business'} · Confidential — for intended recipient only</div>
    ${(!userPlan?.plan_id||userPlan.plan_id==="hobby")?'<div style="text-align:center;padding:0 20px 20px;font-size:11px;color:#aaa">Created with <strong>Aspreo</strong> · aspreo.com</div>':''}
    </body></html>`);
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:5000,background:'rgba(0,0,0,.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflow:'auto'}} onClick={onClose}>
      <div style={{background:pageBg,borderRadius:16,width:'100%',maxWidth:680,maxHeight:'90vh',overflowY:'auto',padding:32,color:fontColor}} onClick={e=>e.stopPropagation()}>
        {/* Cover preview */}
        <div style={{background:primary,color:'#fff',borderRadius:12,padding:40,textAlign:'center',marginBottom:20}}>
          {bizProfile.biz_logo_url&&<img src={bizProfile.biz_logo_url} alt="logo" style={{height:50,marginBottom:12,objectFit:'contain'}}/>}
          <div style={{fontSize:26,fontWeight:900,marginBottom:6}}>{bizProfile.biz_name||'Your Business'}</div>
          {bizProfile.biz_tagline&&<div style={{fontSize:13,opacity:.8,marginBottom:20}}>{bizProfile.biz_tagline}</div>}
          <div style={{fontSize:20,fontWeight:800,letterSpacing:'.08em',marginBottom:12}}>{(prop.cover_title||prop.title||'BUSINESS PROPOSAL').toUpperCase()}</div>
          {client&&<div style={{opacity:.9}}>Prepared for: <strong>{client.name}</strong></div>}
        </div>

        {prop.executive_summary&&<div style={{marginBottom:16}}><h3 style={{color:primary,borderBottom:`2px solid ${primary}`,paddingBottom:6}}>Executive Summary</h3><p style={{lineHeight:1.7,whiteSpace:'pre-wrap'}}>{prop.executive_summary}</p></div>}
        {prop.understanding&&<div style={{background:'#f9f9f9',borderRadius:8,padding:16,marginBottom:16}}><h4 style={{color:primary,margin:'0 0 8px'}}>Our Understanding</h4><p style={{lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{prop.understanding}</p></div>}

        {/* Scope table */}
        {items.length>0&&<div style={{marginBottom:16}}>
          <h3 style={{color:primary,borderBottom:`2px solid ${primary}`,paddingBottom:6}}>Scope & Investment</h3>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{background:primary,color:'#fff'}}><th style={{padding:'8px',textAlign:'left',fontSize:12}}>Service</th><th style={{padding:'8px',textAlign:'center',fontSize:12}}>Qty</th><th style={{padding:'8px',textAlign:'right',fontSize:12}}>Rate</th><th style={{padding:'8px',textAlign:'right',fontSize:12}}>Amount</th></tr></thead>
            <tbody>
              {items.map((it,i)=><React.Fragment key={it.id}>
                <tr style={{borderBottom:'1px solid #eee',background:i%2?'#f9f9f9':'transparent'}}>
                  <td style={{padding:'7px 8px',fontSize:13}}>{it.desc}</td>
                  <td style={{padding:'7px 8px',textAlign:'center',fontSize:13}}>{it.qty||1}</td>
                  <td style={{padding:'7px 8px',textAlign:'right',fontSize:13}}>₹{parseFloat(it.rate||0).toLocaleString('en-IN')}</td>
                  <td style={{padding:'7px 8px',textAlign:'right',fontSize:13,fontWeight:600}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</td>
                </tr>
                {it.italic_desc&&<tr style={{borderBottom:'1px solid #eee'}}><td colSpan={4} style={{padding:'2px 8px 8px',fontStyle:'italic',color:'#666',fontSize:11}}>{it.italic_desc}</td></tr>}
              </React.Fragment>)}
              <tr style={{borderTop:`2px solid ${primary}`}}><td colSpan={3} style={{textAlign:'right',padding:'10px 8px',fontWeight:800,fontSize:15,color:primary}}>TOTAL</td><td style={{textAlign:'right',padding:'10px 8px',fontWeight:800,fontSize:15,color:primary}}>₹{total.toLocaleString('en-IN')}</td></tr>
            </tbody>
          </table>
        </div>}

        {prop.why_us&&<div style={{marginBottom:16}}><h3 style={{color:primary}}>Why {bizProfile.biz_name||'Us'}</h3><p style={{lineHeight:1.7,whiteSpace:'pre-wrap'}}>{prop.why_us}</p></div>}
        {prop.next_steps&&<div style={{background:'#f9f9f9',borderRadius:8,padding:16,marginBottom:16}}><h4 style={{color:primary,margin:'0 0 8px'}}>Next Steps</h4><p style={{lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{prop.next_steps}</p></div>}
        {prop.best_practices&&<div style={{marginBottom:16}}><h3 style={{color:primary}}>Our Approach</h3><p style={{lineHeight:1.7,whiteSpace:'pre-wrap'}}>{prop.best_practices}</p></div>}
        {prop.terms&&<div style={{marginBottom:16,fontSize:12,color:'#666',borderTop:'1px solid #eee',paddingTop:12}}><strong>Terms:</strong> {prop.terms}</div>}

        {/* Signature */}
        <div style={{borderTop:'2px solid '+primary,paddingTop:16,marginBottom:20}}>
          <div style={{fontWeight:700,color:primary,marginBottom:8}}>Proposal Acceptance</div>
          <p style={{fontSize:12,color:'#555',marginBottom:20}}>{prop.acceptance_text||`By signing below, ${client?.name||'[Client]'} accepts this proposal and authorises ${bizProfile.biz_name||'[Your Company]'} to proceed.`}</p>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:32}}>
            {[client?.name||'[Client Name]',bizProfile.biz_name||'[Your Company]'].map((n,i)=>(
              <div key={i} style={{borderTop:'1px solid #999',paddingTop:8,fontSize:11,color:'#666'}}>{n}<br/>Date: _______________</div>
            ))}
          </div>
        </div>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={printNow} style={{...btn(primary),fontSize:13}}>🖨 Download PDF</button>
          <button onClick={onClose} style={{...btn('#999'),fontSize:13}}>✕ Close</button>
        </div>
        {(!userPlan?.plan_id||userPlan.plan_id==="hobby")&&(
          <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:12}}>Created with <strong>Aspreo</strong> · aspreo.com</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SHARE LINK MODAL — with expiry options for invoices and proposals
// ══════════════════════════════════════════════════════════════════════════════
function ShareLinkModal({ item, type, onShare, onClose }) {
  const C = useC();
  const [expiry, setExpiry] = React.useState(0); // 0 = never
  const EXPIRY_OPTIONS = [
    { label: 'Never (permanent link)', value: 0 },
    { label: '24 hours',   value: 24 * 60 * 60 * 1000 },
    { label: '7 days',     value: 7  * 24 * 60 * 60 * 1000 },
    { label: '30 days',    value: 30 * 24 * 60 * 60 * 1000 },
    { label: '90 days',    value: 90 * 24 * 60 * 60 * 1000 },
  ];
  const label = type === 'invoice' ? '🧾 Invoice' : '📄 Proposal';
  const previewUrl = `${window.location.origin}/${type}/${item.share_token || item.id}`;

  return (
    <div style={{position:'fixed',inset:0,zIndex:6000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:C.surface,borderRadius:16,padding:28,width:'100%',maxWidth:460,boxShadow:'0 24px 80px rgba(0,0,0,.3)',fontFamily:FONT}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <div style={{width:44,height:44,borderRadius:12,background:'#7C3AED18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>🔗</div>
          <div>
            <div style={{fontWeight:800,fontSize:16,color:C.text}}>Share {label}</div>
            <div style={{fontSize:12,color:C.muted}}>Anyone with the link can view and download PDF</div>
          </div>
        </div>

        {/* Preview URL */}
        <div style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#7C3AED',fontFamily:'monospace',wordBreak:'break-all'}}>
          {previewUrl}
        </div>

        {/* Expiry */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:8,textTransform:'uppercase',letterSpacing:'.05em'}}>Link Expiry</div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {EXPIRY_OPTIONS.map(opt=>(
              <label key={opt.value} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'8px 12px',borderRadius:8,border:`1.5px solid ${expiry===opt.value?'#7C3AED':C.border}`,background:expiry===opt.value?'#7C3AED12':'transparent',transition:'all .15s'}}>
                <input type="radio" name="expiry" value={opt.value} checked={expiry===opt.value} onChange={()=>setExpiry(opt.value)} style={{accentColor:'#7C3AED',width:15,height:15}}/>
                <span style={{fontSize:13,color:expiry===opt.value?'#7C3AED':C.text,fontWeight:expiry===opt.value?700:400}}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {expiry>0&&(
          <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:8,padding:'8px 12px',marginBottom:16,fontSize:12,color:'#92400E'}}>
            ⚠️ This link will stop working on {new Date(Date.now()+expiry).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </div>
        )}

        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>onShare(expiry||null)} style={{...btn('#7C3AED'),flex:1,fontSize:13}}>
            📋 Copy Link & Share
          </button>
          <button onClick={onClose} style={{...btn('#6B7280'),fontSize:13,padding:'8px 16px'}}>Cancel</button>
        </div>
        <div style={{fontSize:11,color:C.muted,textAlign:'center',marginTop:12}}>
          The link is accessible to anyone — do not share with unintended recipients
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAYMENT AUTOMATION SYSTEM
// Products → Checkout Page → Payment → Automated Delivery
// ══════════════════════════════════════════════════════════════════════════════
const PAY_COLOR  = '#7C3AED';
const PAY_LIGHT  = '#F5F3FF';

const PRODUCT_TYPES = [
  { id:'digital',      icon:'📁', label:'Digital Product',   desc:'eBook, template, file — delivered via link after payment' },
  { id:'course',       icon:'🎓', label:'Online Course',     desc:'Course access link sent automatically after payment' },
  { id:'service',      icon:'🛠', label:'Service / Gig',    desc:'Client pays, you deliver manually or via email' },
  { id:'booking',      icon:'📅', label:'Paid Booking',      desc:'Booking + payment in one flow, meeting auto-created' },
  { id:'subscription', icon:'🔄', label:'Subscription',     desc:'Recurring payment via Razorpay subscription (coming soon)' },
];

const DELIVERY_TYPES = [
  { id:'drive_link',  label:'📁 Google Drive / Dropbox Link', desc:'Paste the link — sent automatically in email after payment' },
  { id:'email',       label:'📧 Custom Email',               desc:'Write a custom email sent to buyer after payment' },
  { id:'course_link', label:'🎓 Course / Member Area Link',  desc:'Send course access URL after payment' },
  { id:'webhook',     label:'🔌 Webhook',                   desc:'POST to your endpoint — integrate with any system' },
  { id:'manual',      label:'✋ Manual',                    desc:'You review and deliver manually — you get notified' },
];

function PaymentAutomation({ ctx }) {
  const C = useC();
  const { products=[], orders=[], automations=[], bizProfile={},
          addProduct, updateProduct, deleteProduct,
          addOrder, updateOrder,
          addAutomation, updateAutomation, deleteAutomation } = ctx;

  const [tab, setTab] = React.useState('products');
  const TABS = [
    { id:'products',    label:'🛍 Products'      },
    { id:'orders',      label:'📦 Orders'        },
    { id:'automations', label:'⚡ Automations'   },
    { id:'settings',    label:'⚙️ Pay Settings'   },
  ];

  // KPIs
  const paidOrders  = orders.filter(o=>o.status==='paid');
  const totalRev    = paidOrders.reduce((s,o)=>s+(parseFloat(o.amount)||0),0);
  const pendingOrds = orders.filter(o=>o.status==='pending').length;

  return (
    <div style={{fontFamily:FONT,color:C.text}}>
      <SectionMasthead eyebrow="System · Sell products · Automate delivery · Track revenue" title={<>Get <em>paid</em>.</>} />

      {/* KPIs */}
      <div className="ta-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Products',     value:products.filter(p=>p.is_active).length, color:'var(--ta-primary)'},
          {label:'Total Orders', value:orders.length,                          color:'var(--ta-primary)'},
          {label:'Revenue',      value:`₹${totalRev.toLocaleString('en-IN')}`, color:'var(--ta-positive)'},
          {label:'Pending',      value:pendingOrds,                            color:'var(--ta-notice)'},
        ].map(k=>(
          <div key={k.label} className="ta-card">
            <div className="ta-section-head-meta" style={{marginBottom:8}}>{k.label}</div>
            <div className="ta-serif tnum" style={{fontSize:26,fontWeight:400,color:k.color}}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',padding:4,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            background:tab===t.id?PAY_COLOR:'transparent',color:tab===t.id?'#fff':C.muted,
            border:'none',borderRadius:7,padding:'6px 14px',cursor:'pointer',
            fontFamily:FONT,fontSize:12,fontWeight:tab===t.id?700:400,whiteSpace:'nowrap'
          }}>{t.label}</button>
        ))}
      </div>

      {tab==='products'    && <ProductsTab    ctx={ctx} />}
      {tab==='orders'      && <OrdersTab      orders={orders} products={products} updateOrder={updateOrder} />}
      {tab==='automations' && <AutomationsTab ctx={ctx} />}
      {tab==='settings'    && <PaySettingsTab ctx={ctx} />}
    </div>
  );
}

// ── PRODUCTS TAB ──────────────────────────────────────────────────────────────
function ProductsTab({ ctx }) {
  const C = useC();
  const { products=[], orders=[], addProduct, updateProduct, deleteProduct, bizProfile={} } = ctx;
  const [showForm, setShowForm] = React.useState(false);
  const [editProd, setEditProd] = React.useState(null);
  const [previewSlug, setPreviewSlug] = React.useState(null);

  const blank = {
    name:'', description:'', type:'digital', price:'', currency:'INR',
    slug:'', cover_image_url:'',
    delivery_type:'drive_link', delivery_content:'', delivery_subject:'',
    checkout_fields:[], max_orders:'',
  };
  const [form, setForm] = React.useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const [newField, setNewField] = React.useState('');

  const save = async () => {
    if (!form.name.trim() || !form.price) return;
    const payload = {
      ...form,
      price: parseFloat(form.price)||0,
      max_orders: form.max_orders ? parseInt(form.max_orders) : null,
    };
    if (editProd) updateProduct(editProd.id, payload);
    else await addProduct(payload);
    setForm(blank); setShowForm(false); setEditProd(null);
  };

  const addField = () => {
    if (!newField.trim()) return;
    upd('checkout_fields', [...(form.checkout_fields||[]), {id:Date.now(),label:newField.trim(),type:'text',required:false}]);
    setNewField('');
  };

  const checkoutUrl = (p) => `${window.location.origin}/buy/${p.slug||p.id}`;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontWeight:700,fontSize:14,color:C.text}}>Products ({products.length})</span>
        <button onClick={()=>{setEditProd(null);setForm(blank);setShowForm(true);}} style={btn(PAY_COLOR)}>+ New Product</button>
      </div>

      {showForm && (
        <div style={{background:C.surface,border:`1.5px solid ${PAY_COLOR}`,borderRadius:12,padding:20,marginBottom:16}}>
          <h4 style={{margin:'0 0 14px',color:PAY_COLOR}}>{editProd?'Edit Product':'New Product'}</h4>

          {/* Product Type */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>Product Type</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6}}>
              {PRODUCT_TYPES.map(t=>(
                <div key={t.id} onClick={()=>upd('type',t.id)}
                  style={{border:`2px solid ${form.type===t.id?PAY_COLOR:C.border}`,borderRadius:8,padding:'8px 6px',cursor:'pointer',textAlign:'center',background:form.type===t.id?PAY_LIGHT:'transparent',transition:'all .15s'}}>
                  <div style={{fontSize:20,marginBottom:4}}>{t.icon}</div>
                  <div style={{fontSize:11,fontWeight:600,color:form.type===t.id?PAY_COLOR:C.text}}>{t.label}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>{PRODUCT_TYPES.find(t=>t.id===form.type)?.desc}</div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Product Name *</div>
              <input value={form.name} onChange={e=>{upd('name',e.target.value);if(!editProd)upd('slug',e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''));}} placeholder="e.g. LinkedIn Mastery Guide" style={{...inp(),width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Price (₹) *</div>
              <input type="number" value={form.price} onChange={e=>upd('price',e.target.value)} placeholder="999" style={{...inp(),width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>URL Slug (checkout URL)</div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:11,color:C.muted,flexShrink:0}}>/buy/</span>
                <input value={form.slug||''} onChange={e=>upd('slug',e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,''))} placeholder="linkedin-mastery-guide" style={{...inp(),flex:1}}/>
              </div>
            </div>
            <div>
              <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Max Orders (leave blank = unlimited)</div>
              <input type="number" value={form.max_orders||''} onChange={e=>upd('max_orders',e.target.value)} placeholder="∞" style={{...inp(),width:'100%'}}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Description</div>
            <textarea value={form.description||''} onChange={e=>upd('description',e.target.value)} placeholder="What does the buyer get?" style={{...ta(70),width:'100%'}}/>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Cover Image URL (optional)</div>
            <input value={form.cover_image_url||''} onChange={e=>upd('cover_image_url',e.target.value)} placeholder="https://..." style={{...inp(),width:'100%'}}/>
          </div>

          {/* Delivery Config */}
          <div style={{background:C.surfaceAlt,borderRadius:10,padding:14,marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:PAY_COLOR,marginBottom:10}}>⚡ Automated Delivery (after payment)</div>
            <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:10}}>
              {DELIVERY_TYPES.map(d=>(
                <label key={d.id} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'8px 10px',borderRadius:8,border:`1.5px solid ${form.delivery_type===d.id?PAY_COLOR:C.border}`,background:form.delivery_type===d.id?PAY_LIGHT:'transparent'}}>
                  <input type="radio" name="delivery" value={d.id} checked={form.delivery_type===d.id} onChange={()=>upd('delivery_type',d.id)} style={{accentColor:PAY_COLOR}}/>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:form.delivery_type===d.id?PAY_COLOR:C.text}}>{d.label}</div>
                    <div style={{fontSize:10,color:C.muted}}>{d.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            {form.delivery_type!=='manual'&&(
              <>
                {(form.delivery_type==='drive_link'||form.delivery_type==='course_link')&&(
                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>
                      {form.delivery_type==='drive_link'?'📁 Google Drive / Dropbox Link':'🎓 Course / Member Area URL'}
                    </div>
                    <input value={form.delivery_content||''} onChange={e=>upd('delivery_content',e.target.value)} placeholder="https://drive.google.com/file/..." style={{...inp(),width:'100%'}}/>
                    <div style={{fontSize:10,color:'#16A34A',marginTop:3}}>✅ This link is sent automatically to the buyer's email right after payment</div>
                  </div>
                )}
                {form.delivery_type==='webhook'&&(
                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Webhook URL</div>
                    <input value={form.delivery_content||''} onChange={e=>upd('delivery_content',e.target.value)} placeholder="https://your-server.com/webhook" style={{...inp(),width:'100%'}}/>
                    <div style={{fontSize:10,color:C.muted,marginTop:3}}>We POST order details as JSON to this URL after payment</div>
                  </div>
                )}
                <div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Delivery Email Subject</div>
                  <input value={form.delivery_subject||''} onChange={e=>upd('delivery_subject',e.target.value)} placeholder={`Your ${form.name||'purchase'} is ready! 🎉`} style={{...inp(),width:'100%'}}/>
                </div>
                {form.delivery_type==='email'&&(
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Email Body (use {`{buyer_name}`}, {`{product_name}`} as placeholders)</div>
                    <textarea value={form.delivery_content||''} onChange={e=>upd('delivery_content',e.target.value)} placeholder={`Hi {buyer_name},\n\nThank you for purchasing {product_name}!\n\nHere are your details:\n...`} style={{...ta(120),width:'100%'}}/>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Checkout custom fields */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>Extra Checkout Fields (optional)</div>
            {(form.checkout_fields||[]).map(f=>(
              <div key={f.id} style={{display:'flex',gap:6,alignItems:'center',marginBottom:5,background:C.surfaceAlt,borderRadius:6,padding:'5px 10px'}}>
                <span style={{flex:1,fontSize:12}}>{f.label}</span>
                <button onClick={()=>upd('checkout_fields',(form.checkout_fields||[]).filter(x=>x.id!==f.id))} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:13}}>✕</button>
              </div>
            ))}
            <div style={{display:'flex',gap:6}}>
              <input value={newField} onChange={e=>setNewField(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addField()} placeholder='Add field (e.g. "Company Name", "Phone")' style={{...inp(),flex:1,fontSize:12}}/>
              <button onClick={addField} style={btn(PAY_COLOR)}>+ Add</button>
            </div>
          </div>

          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(PAY_COLOR)}>💾 {editProd?'Update':'Save Product'}</button>
            <button onClick={()=>{setShowForm(false);setEditProd(null);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {products.length===0&&!showForm&&(
        <div style={{textAlign:'center',padding:48,color:C.muted}}>
          <div style={{fontSize:48,marginBottom:12}}>🛍</div>
          <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>No products yet</div>
          <div style={{fontSize:13,marginBottom:16}}>Create a product and get a checkout link instantly. Payment goes straight to your Razorpay account.</div>
          <button onClick={()=>setShowForm(true)} style={btn(PAY_COLOR)}>+ Create Your First Product</button>
        </div>
      )}

      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {products.map(prod=>{
          const pt = PRODUCT_TYPES.find(t=>t.id===prod.type)||PRODUCT_TYPES[0];
          const prodOrders = orders.filter(o=>o.product_id===prod.id);
          const prodRev = prodOrders.filter(o=>o.status==='paid').reduce((s,o)=>s+(parseFloat(o.amount)||0),0);
          const url = checkoutUrl(prod);
          return (
            <div key={prod.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                  <div style={{width:44,height:44,borderRadius:10,background:PAY_LIGHT,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{pt.icon}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,color:C.text}}>{prod.name}</div>
                    <div style={{fontSize:12,color:C.muted}}>{pt.label}</div>
                    {prod.description&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{prod.description.slice(0,80)}{prod.description.length>80?'...':''}</div>}
                  </div>
                </div>
                <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                  <div style={{fontSize:20,fontWeight:800,color:PAY_COLOR}}>₹{parseFloat(prod.price||0).toLocaleString('en-IN')}</div>
                  <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:prod.is_active?'#DCFCE7':'#F3F4F6',color:prod.is_active?'#16A34A':'#6B7280',fontWeight:600}}>{prod.is_active?'● Active':'○ Inactive'}</span>
                </div>
              </div>

              {/* Stats row */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
                {[['📦','Orders',prodOrders.length],['💰','Revenue',`₹${prodRev.toLocaleString('en-IN')}`],['🚀','Delivery',DELIVERY_TYPES.find(d=>d.id===prod.delivery_type)?.label||'Manual']].map(([i,l,v])=>(
                  <div key={l} style={{background:C.surfaceAlt,borderRadius:6,padding:'8px',textAlign:'center'}}>
                    <div style={{fontSize:14}}>{i}</div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{v}</div>
                    <div style={{fontSize:10,color:C.muted}}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Checkout URL */}
              <div style={{background:PAY_LIGHT,border:`1px solid ${PAY_COLOR}30`,borderRadius:8,padding:'8px 12px',marginBottom:10,display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:11,color:PAY_COLOR,fontFamily:'monospace',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{url}</span>
                <button onClick={()=>navigator.clipboard.writeText(url).then(()=>alert('🔗 Checkout link copied!'))} style={{...btn(PAY_COLOR),fontSize:11,padding:'4px 10px',flexShrink:0}}>📋 Copy</button>
                <a href={url} target="_blank" rel="noreferrer" style={{...btn('#475569'),fontSize:11,padding:'4px 8px',textDecoration:'none',flexShrink:0}}>👁 Preview</a>
              </div>

              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>updateProduct(prod.id,{is_active:!prod.is_active})} style={{...btn(prod.is_active?'#D97706':'#16A34A'),fontSize:11,padding:'5px 10px'}}>{prod.is_active?'⏸ Pause':'▶ Activate'}</button>
                <button onClick={()=>{setEditProd(prod);setForm({...blank,...prod,price:prod.price||'',max_orders:prod.max_orders||''});setShowForm(true);}} style={{...btn(C.amber),fontSize:11,padding:'5px 10px'}}>✏️ Edit</button>
                <button onClick={()=>{if(window.confirm(`Delete "${prod.name}"?`))deleteProduct(prod.id);}} style={{...btn(C.red),fontSize:11,padding:'5px 10px'}}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ORDERS TAB ────────────────────────────────────────────────────────────────
function OrdersTab({ orders=[], products=[], updateOrder }) {
  const C = useC();
  const [filter, setFilter] = React.useState('all');
  const [expand, setExpand] = React.useState(null);
  const filtered = filter==='all' ? orders : orders.filter(o=>o.status===filter);
  const STATUS_C = {paid:'#16A34A',pending:'#D97706',failed:'#DC2626',delivered:'#2563EB',refunded:'#7C3AED'};

  return (
    <div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {['all','pending','paid','delivered','failed'].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{fontSize:11,padding:'3px 12px',borderRadius:20,border:'none',cursor:'pointer',background:filter===s?PAY_COLOR:'#e5e7eb',color:filter===s?'#fff':C.muted,fontWeight:filter===s?700:400}}>
            {s==='all'?`All (${orders.length})`:s.charAt(0).toUpperCase()+s.slice(1)+` (${orders.filter(o=>o.status===s).length})`}
          </button>
        ))}
      </div>

      {filtered.length===0&&<div style={{textAlign:'center',color:C.muted,padding:40}}>No orders found. Share your checkout links to start receiving orders.</div>}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {filtered.map(order=>{
          const prod = products.find(p=>p.product_id===order.product_id) || (order.products);
          const sc = STATUS_C[order.status]||'#6B7280';
          const isExp = expand===order.id;
          return (
            <div key={order.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer'}} onClick={()=>setExpand(isExp?null:order.id)}>
                <div style={{width:8,height:8,borderRadius:'50%',background:sc,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{order.buyer_name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{order.buyer_email} · {order.products?.name||'Product'}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:15,fontWeight:800,color:PAY_COLOR}}>₹{parseFloat(order.amount||0).toLocaleString('en-IN')}</div>
                  <span style={{fontSize:10,padding:'1px 7px',borderRadius:10,background:sc+'22',color:sc,fontWeight:600}}>{order.status}</span>
                </div>
                <div style={{fontSize:12,color:C.muted}}>{new Date(order.created_at).toLocaleDateString('en-IN')}</div>
                <span style={{fontSize:12,color:C.muted}}>{isExp?'▲':'▼'}</span>
              </div>
              {isExp&&(
                <div style={{padding:'0 16px 14px',borderTop:`1px solid ${C.border}`}}>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginTop:10,marginBottom:10}}>
                    {[['📧','Email',order.buyer_email],['📱','Phone',order.buyer_phone||'—'],['💳','Payment',order.payment_method||'—'],['📅','Date',new Date(order.created_at).toLocaleString('en-IN')],['🚀','Delivery',order.delivery_status||'pending'],['🔑','Order ID',order.id?.slice(0,8)+'...']].map(([i,l,v])=>(
                      <div key={l} style={{background:C.surfaceAlt,borderRadius:6,padding:'6px 10px'}}>
                        <div style={{fontSize:10,color:C.muted}}>{i} {l}</div>
                        <div style={{fontSize:12,fontWeight:600,color:C.text,marginTop:1}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {order.buyer_fields&&Object.keys(order.buyer_fields).length>0&&(
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>Custom Fields:</div>
                      {Object.entries(order.buyer_fields).map(([k,v])=>(
                        <div key={k} style={{fontSize:12,color:C.text}}><strong>{k}:</strong> {v}</div>
                      ))}
                    </div>
                  )}
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {order.status==='pending'&&<button onClick={()=>updateOrder(order.id,{status:'paid',paid_at:new Date().toISOString()})} style={{...btn('#16A34A'),fontSize:11,padding:'5px 10px'}}>✅ Mark Paid</button>}
                    {order.status==='paid'&&order.delivery_status!=='sent'&&<button onClick={()=>updateOrder(order.id,{delivery_status:'sent',delivery_sent_at:new Date().toISOString(),status:'delivered'})} style={{...btn('#2563EB'),fontSize:11,padding:'5px 10px'}}>📤 Mark Delivered</button>}
                    <button onClick={()=>updateOrder(order.id,{status:'refunded'})} style={{...btn('#7C3AED'),fontSize:11,padding:'5px 10px'}}>↩ Refund</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AUTOMATIONS TAB ───────────────────────────────────────────────────────────
function AutomationsTab({ ctx }) {
  const C = useC();
  const { automations=[], products=[], addAutomation, updateAutomation, deleteAutomation } = ctx;
  const [showForm, setShowForm] = React.useState(false);
  const blank = { name:'', product_id:'', trigger_type:'payment_success', steps:[], is_active:true };
  const [form, setForm] = React.useState(blank);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const STEP_TYPES = [
    { id:'email',     icon:'📧', label:'Send Email',     fields:['to','subject','body'] },
    { id:'whatsapp',  icon:'💬', label:'WhatsApp',      fields:['to','message'] },
    { id:'webhook',   icon:'🔌', label:'Webhook POST',  fields:['url','payload'] },
  ];

  const addStep = (type) => {
    const templates = {
      email:    { type:'email',    to:'{buyer_email}', subject:'Your purchase is confirmed! 🎉', body:'Hi {buyer_name},\n\nThank you for purchasing {product_name}!\n\nHere is your download link: {delivery_content}\n\nBest,\n{seller_name}' },
      whatsapp: { type:'whatsapp', to:'{buyer_phone}', message:'Hi {buyer_name}! 👋 Your purchase of {product_name} is confirmed. Check your email for the download link.' },
      webhook:  { type:'webhook',  url:'https://your-endpoint.com/webhook', payload:'{"order_id":"{order_id}","buyer":"{buyer_email}","product":"{product_name}"}' },
    };
    upd('steps', [...(form.steps||[]), {...templates[type], id:Date.now()}]);
  };

  const updStep = (id,field,val) => upd('steps',(form.steps||[]).map(s=>s.id===id?{...s,[field]:val}:s));
  const remStep = (id) => upd('steps',(form.steps||[]).filter(s=>s.id!==id));

  const save = async () => {
    if (!form.name.trim()) return;
    await addAutomation(form);
    setForm(blank); setShowForm(false);
  };

  const TRIGGER_LABELS = { payment_success:'💰 After Payment', booking_confirmed:'📅 After Booking', manual:'✋ Manual Trigger' };
  const PLACEHOLDERS = ['{buyer_name}','{buyer_email}','{buyer_phone}','{product_name}','{amount}','{order_id}','{delivery_content}','{seller_name}'];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:C.text}}>⚡ Automation Workflows</div>
          <div style={{fontSize:12,color:C.muted}}>Multi-step automations triggered by payment or booking</div>
        </div>
        <button onClick={()=>setShowForm(true)} style={btn(PAY_COLOR)}>+ New Automation</button>
      </div>

      {showForm&&(
        <div style={{background:C.surface,border:`1.5px solid ${PAY_COLOR}`,borderRadius:12,padding:20,marginBottom:16}}>
          <h4 style={{margin:'0 0 14px',color:PAY_COLOR}}>New Automation</h4>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:12}}>
            <input value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="Automation name" style={{...inp(),width:'100%'}}/>
            <select value={form.trigger_type} onChange={e=>upd('trigger_type',e.target.value)} style={{...inp(),width:'100%'}}>
              <option value="payment_success">💰 After Payment</option>
              <option value="booking_confirmed">📅 After Booking</option>
              <option value="manual">✋ Manual</option>
            </select>
            <select value={form.product_id||''} onChange={e=>upd('product_id',e.target.value||null)} style={{...inp(),width:'100%'}}>
              <option value="">All Products</option>
              {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Steps */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>Steps (run in order)</div>
            {(form.steps||[]).map((step,si)=>{
              const st = STEP_TYPES.find(t=>t.id===step.type);
              return (
                <div key={step.id} style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:12,marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <span style={{fontSize:12,fontWeight:700,color:PAY_COLOR}}>{si+1}. {st?.icon} {st?.label}</span>
                    <button onClick={()=>remStep(step.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.light,fontSize:13}}>✕</button>
                  </div>
                  {step.type==='email'&&(
                    <>
                      <input value={step.to||''} onChange={e=>updStep(step.id,'to',e.target.value)} placeholder="To: {buyer_email}" style={{...inp(),width:'100%',marginBottom:6,fontSize:12}}/>
                      <input value={step.subject||''} onChange={e=>updStep(step.id,'subject',e.target.value)} placeholder="Subject" style={{...inp(),width:'100%',marginBottom:6,fontSize:12}}/>
                      <textarea value={step.body||''} onChange={e=>updStep(step.id,'body',e.target.value)} style={{...ta(90),width:'100%',fontSize:12}}/>
                    </>
                  )}
                  {step.type==='whatsapp'&&(
                    <>
                      <input value={step.to||''} onChange={e=>updStep(step.id,'to',e.target.value)} placeholder="To: {buyer_phone}" style={{...inp(),width:'100%',marginBottom:6,fontSize:12}}/>
                      <textarea value={step.message||''} onChange={e=>updStep(step.id,'message',e.target.value)} style={{...ta(70),width:'100%',fontSize:12}}/>
                    </>
                  )}
                  {step.type==='webhook'&&(
                    <>
                      <input value={step.url||''} onChange={e=>updStep(step.id,'url',e.target.value)} placeholder="https://your-endpoint.com/hook" style={{...inp(),width:'100%',marginBottom:6,fontSize:12}}/>
                      <textarea value={step.payload||''} onChange={e=>updStep(step.id,'payload',e.target.value)} style={{...ta(60),width:'100%',fontSize:11,fontFamily:'monospace'}}/>
                    </>
                  )}
                </div>
              );
            })}
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
              {STEP_TYPES.map(t=>(
                <button key={t.id} onClick={()=>addStep(t.id)} style={{...btn(C.surfaceAlt),color:PAY_COLOR,border:`1px solid ${PAY_COLOR}`,fontSize:11,padding:'5px 12px'}}>{t.icon} Add {t.label}</button>
              ))}
            </div>
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:6,padding:'8px 12px',fontSize:10,color:'#1D4ED8'}}>
              <strong>Available placeholders:</strong> {PLACEHOLDERS.join(' · ')}
            </div>
          </div>

          <div style={{display:'flex',gap:8}}>
            <button onClick={save} style={btn(PAY_COLOR)}>💾 Save Automation</button>
            <button onClick={()=>{setShowForm(false);setForm(blank);}} style={btn('#999')}>Cancel</button>
          </div>
        </div>
      )}

      {automations.length===0&&!showForm&&(
        <div style={{background:C.surfaceAlt,borderRadius:12,padding:32,textAlign:'center'}}>
          <div style={{fontSize:40,marginBottom:12}}>⚡</div>
          <div style={{fontWeight:700,fontSize:14,marginBottom:6}}>No automations yet</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Create workflows that run automatically — send delivery emails, WhatsApp messages, or trigger webhooks after payment or booking.</div>
          <button onClick={()=>setShowForm(true)} style={btn(PAY_COLOR)}>+ Create Automation</button>
        </div>
      )}

      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {automations.map(auto=>(
          <div key={auto.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:'14px 16px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:C.text}}>{auto.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{TRIGGER_LABELS[auto.trigger_type]||auto.trigger_type} · {(auto.steps||[]).length} steps · Run {auto.run_count||0} times</div>
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer'}}>
                  <input type="checkbox" checked={auto.is_active} onChange={e=>updateAutomation(auto.id,{is_active:e.target.checked})} style={{accentColor:PAY_COLOR}}/>
                  <span style={{fontSize:11,color:auto.is_active?'#16A34A':C.muted,fontWeight:600}}>{auto.is_active?'Active':'Paused'}</span>
                </label>
                <button onClick={()=>{if(window.confirm('Delete?'))deleteAutomation(auto.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>🗑</button>
              </div>
            </div>
            <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
              {(auto.steps||[]).map((s,i)=>{
                const st=STEP_TYPES.find(t=>t.id===s.type);
                return <span key={i} style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:PAY_LIGHT,color:PAY_COLOR,fontWeight:600}}>{st?.icon} {st?.label}</span>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PAY SETTINGS TAB ─────────────────────────────────────────────────────────
function PaySettingsTab({ ctx }) {
  const C = useC();
  const { bizProfile={}, updateBizProfile, uid } = ctx;
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  React.useEffect(()=>{ setForm({razorpay_key_id:bizProfile.razorpay_key_id||'', razorpay_key_secret:bizProfile.razorpay_key_secret||''}); },[bizProfile]);

  const save = async () => {
    setSaving(true);
    await updateBizProfile(form);
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const testMode = !bizProfile.razorpay_key_id;

  return (
    <div>
      {/* Razorpay Setup */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:14,color:'#2F6EED',marginBottom:4}}>💳 Razorpay Integration</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Connect your Razorpay account to accept payments directly to your bank account.</div>

        {testMode&&(
          <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400E'}}>
            ⚠️ <strong>Test Mode:</strong> No Razorpay keys connected. Checkout pages will show a demo payment flow. Add your keys below to accept real payments.
          </div>
        )}
        {!testMode&&(
          <div style={{background:'#F0FDF4',border:'1px solid #16A34A',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#14532D'}}>
            ✅ <strong>Razorpay Connected!</strong> Payments go directly to your Razorpay account.
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Razorpay Key ID (rzp_live_...)</div>
            <input value={form.razorpay_key_id||''} onChange={e=>upd('razorpay_key_id',e.target.value)} placeholder="rzp_live_xxxxxxxx" style={{...inp(),width:'100%'}}/>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600}}>Razorpay Key Secret</div>
            <input type="password" value={form.razorpay_key_secret||''} onChange={e=>upd('razorpay_key_secret',e.target.value)} placeholder="••••••••••••••••" style={{...inp(),width:'100%'}}/>
          </div>
        </div>
        <button onClick={save} disabled={saving} style={{...btn('#2F6EED'),opacity:saving?0.6:1}}>{saving?'Saving…':saved?'✅ Saved!':'💾 Save Razorpay Keys'}</button>
        <div style={{fontSize:11,color:C.muted,marginTop:10}}>Get your keys from <a href="https://dashboard.razorpay.com/app/website-app-settings/api-keys" target="_blank" rel="noreferrer" style={{color:'#2F6EED'}}>Razorpay Dashboard → Settings → API Keys</a>. Use Test keys while testing, Live keys for production.</div>
      </div>

      {/* How it works */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontWeight:700,fontSize:14,color:PAY_COLOR,marginBottom:12}}>⚡ How Payment Automation Works</div>
        {[
          {step:'1',title:'Create a Product',desc:'Set name, price, type (digital/course/service/booking) and delivery method (Drive link, email, webhook)'},
          {step:'2',title:'Share the Checkout Link',desc:'Copy aspreo.com/buy/your-slug and share via WhatsApp, email, social media, or your website'},
          {step:'3',title:'Customer Checks Out',desc:'They fill their details, pay via Razorpay (UPI, card, net banking, EMI) — payment goes to your account'},
          {step:'4',title:'Automatic Delivery',desc:'The moment payment is confirmed: delivery email sent, WhatsApp message sent, booking created, webhooks fired'},
          {step:'5',title:'Track Everything',desc:'See all orders, revenue, delivery status, and buyer details in the Orders tab'},
        ].map(s=>(
          <div key={s.step} style={{display:'flex',gap:14,marginBottom:14,alignItems:'flex-start'}}>
            <div style={{width:28,height:28,borderRadius:'50%',background:PAY_COLOR,color:'#fff',fontWeight:800,fontSize:14,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{s.step}</div>
            <div>
              <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// UPGRADE GATE — shown when user tries to access a locked module
// ══════════════════════════════════════════════════════════════════════════════
const MODULE_INFO = {
  business: { icon:'💼', name:'Business Suite',  plans:['solopreneur','agency'], color:'#24389C' },
  meetings: { icon:'📅', name:'Meeting Scheduler',plans:['solopreneur','agency'], color:'#1A7A6A' },
  payments: { icon:'💳', name:'Payment Automation',plans:['solopreneur','agency'],color:'#24389C' },
  content:  { icon:'📱', name:'Social Media Hub', plans:['solopreneur','agency'], color:'#8A4D12' },
};

function UpgradeGate({ mod, plan, onUpgrade }) {
  const C = useC();
  const info = MODULE_INFO[mod] || { icon:'⭐', name:mod, plans:['solopreneur'], color:'#7C3AED' };
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:64,textAlign:'center',fontFamily:FONT}}>
      <div style={{width:80,height:80,borderRadius:20,background:info.color+'18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:40,marginBottom:20}}>{info.icon}</div>
      <h1 className="ta-serif" style={{margin:'0 0 8px',fontSize:28,fontWeight:400,color:C.text}}>{info.name}</h1>
      <p style={{color:C.muted,fontSize:14,maxWidth:360,lineHeight:1.7,marginBottom:24}}>
        This feature is available on the <strong>Solopreneur</strong> and <strong>Agency</strong> plans. Upgrade to unlock it.
      </p>
      <button onClick={onUpgrade} className="ta-btn ta-btn-primary" style={{fontSize:14,padding:'12px 32px'}}>
        View plans & upgrade
      </button>
      <div style={{marginTop:12,fontSize:12,color:C.muted}}>Current plan: <strong style={{color:C.text}}>{plan||'Hobby'}</strong></div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PRICING PAGE — in-app plan selection + upgrade
// ══════════════════════════════════════════════════════════════════════════════
function PricingPage({ ctx, userPlan, upgradePlan }) {
  const C = useC();
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [billing, setBilling] = React.useState('monthly');
  const [upgrading, setUpgrading] = React.useState(null);
  const { bizProfile={} } = ctx;
  const currentPlan = userPlan?.plan_id || 'hobby';

  React.useEffect(() => {
    supabase.from('plans').select('*').eq('is_active',true).order('sort_order')
      .then(({data}) => { if(data) setPlans(data); setLoading(false); });
  }, []);

  const getPrice = (plan) => {
    if (plan.offer_active && plan.offer_price) return plan.offer_price;
    return billing === 'yearly' ? (plan.price_yearly || plan.price_monthly * 10) : plan.price_monthly;
  };

  const [trialModal, setTrialModal] = React.useState(null);

  const callPlanEdgeFn = async (planId, paymentData={}, isTrial=false) => {
    const uid = ctx.uid;
    const res = await fetch('/api/sb/functions/v1/handle-plan-purchase', {
      method:'POST',
      headers:{'Content-Type':'application/json','Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}`},
      body: JSON.stringify({ user_id:uid, plan_id:planId, is_trial:isTrial, billing_cycle:billing, ...paymentData }),
    });
    const data = await res.json();
    if (data.success) {
      await upgradePlan(planId, { status: isTrial?'trial':'active' });
    }
    return data;
  };

  const handleUpgrade = async (plan) => {
    if (plan.id === currentPlan) return;
    setUpgrading(plan.id);

    // Downgrade to Hobby (free) — require confirmation
    if (plan.price_monthly === 0) {
      const confirmed = window.confirm(
        '⚠️ Downgrade to Hobby?\n\nYou will lose access to:\n• Business CRM & Invoices\n• Meeting Scheduler\n• Payment Automation\n• Social Media Hub\n\nYour data will be kept safely. You can upgrade again anytime.\n\nConfirm downgrade?'
      );
      if (!confirmed) { setUpgrading(null); return; }
      await callPlanEdgeFn('hobby');
      setUpgrading(null); return;
    }

    const price = getPrice(plan);

    // Check if user has had a trial before
    const hadTrial = userPlan?.trial_ends_at != null;
    const canTrial  = !hadTrial && plan.id !== 'hobby';

    // Show trial option first
    if (canTrial) {
      setTrialModal({ plan, price });
      setUpgrading(null); return;
    }

    // Direct payment
    await launchRazorpay(plan, price);
    setUpgrading(null);
  };

  const launchRazorpay = async (plan, price) => {
    // Step 1: Get key from edge function (key stays server-side, never in frontend build)
    let rzpKey = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
    if (!rzpKey) {
      try {
        const cfgRes = await fetch('/api/sb/functions/v1/get-platform-config', {
          method: 'POST',
          headers: { 'Content-Type':'application/json', 'Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}` },
          body: JSON.stringify({ key:'razorpay_key_id' }),
        });
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          rzpKey = cfg.value || '';
        }
      } catch(e) { console.warn('[rzp] config fetch failed:', e); }
    }
    if (!rzpKey) {
      alert('Payment system not configured yet.\n\nPlease contact support@aspreo.com to upgrade your plan, or try again shortly.');
      return;
    }

    // Step 2: Load Razorpay script
    if (!window.Razorpay) {
      await new Promise((resolve, reject) => {
        if (document.querySelector('script[src*="razorpay"]')) {
          const check = setInterval(() => { if (window.Razorpay) { clearInterval(check); resolve(); }}, 100);
          setTimeout(() => { clearInterval(check); reject(new Error('timeout')); }, 10000);
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = resolve;
        s.onerror = () => reject(new Error('Razorpay failed to load'));
        document.head.appendChild(s);
      });
    }
    if (!window.Razorpay) { alert('Payment could not load. Please refresh.'); return; }

    const uid = ctx.uid;
    const isTest = rzpKey.startsWith('rzp_test_');
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key:    rzpKey,
        amount: Math.round(price * 100),
        currency: 'INR',
        name: 'Aspreo',
        description: `${plan.name} Plan - ${billing}${isTest?' (Test Mode)':''}`,
        handler: async (response) => {
          await callPlanEdgeFn(plan.id, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id:   response.razorpay_order_id,
          }, false);
          alert(`🎉 Welcome to ${plan.name}! Check your email for confirmation.`);
          resolve();
        },
        modal: { ondismiss: resolve },
        theme: { color: '#7C3AED' },
      });
      rzp.open();
    });
  };

  const PLAN_COLORS = { hobby:'#64748B', solopreneur:'#7C3AED', agency:'#2B1DC9' };

  if (loading) return <div style={{textAlign:'center',padding:60,color:C.muted}}>Loading plans…</div>;

  return (
    <div style={{fontFamily:FONT,maxWidth:900,margin:'0 auto'}}>
      <div style={{textAlign:'center',marginBottom:32}}>
        <h1 style={{fontSize:32,fontWeight:900,color:C.text,margin:'0 0 8px'}}>Simple, transparent pricing</h1>
        <p style={{color:C.muted,fontSize:16,margin:'0 0 20px'}}>Start free. Upgrade when you're ready.</p>
        {/* Billing toggle */}
        <div style={{display:'inline-flex',background:C.surfaceAlt,borderRadius:10,padding:4,gap:4}}>
          {['monthly','yearly'].map(b=>(
            <button key={b} onClick={()=>setBilling(b)} style={{background:billing===b?'#7C3AED':'transparent',color:billing===b?'#fff':C.muted,border:'none',borderRadius:7,padding:'7px 20px',cursor:'pointer',fontFamily:FONT,fontSize:13,fontWeight:billing===b?700:400,transition:'all .15s'}}>
              {b==='monthly'?'Monthly':'Yearly'}{b==='yearly'&&<span style={{fontSize:10,marginLeft:5,background:'#16A34A22',color:'#16A34A',padding:'1px 6px',borderRadius:10,fontWeight:700}}>Save 20%</span>}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:32}}>
        {plans.map(plan=>{
          const price    = getPrice(plan);
          const isCurr   = plan.id === currentPlan;
          const color    = PLAN_COLORS[plan.id] || '#7C3AED';
          const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
          return (
            <div key={plan.id} style={{background:C.surface,border:`2px solid ${plan.is_featured?color:C.border}`,borderRadius:16,padding:24,position:'relative',boxShadow:plan.is_featured?`0 8px 32px ${color}22`:''}}> 
              {plan.is_featured&&<div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background:color,color:'#fff',fontSize:11,fontWeight:700,padding:'3px 14px',borderRadius:99}}>MOST POPULAR</div>}
              {plan.offer_active&&plan.offer_text&&<div style={{background:'#FEF9C3',border:'1px solid #D97706',borderRadius:8,padding:'5px 10px',fontSize:11,color:'#92400E',fontWeight:700,marginBottom:10}}>{plan.offer_text}</div>}
              <div style={{fontSize:11,fontWeight:700,color,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>{plan.name}</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>{plan.tagline}</div>
              <div style={{marginBottom:20}}>
                {price===0
                  ? <div style={{fontSize:36,fontWeight:900,color:C.text}}>Free <span style={{fontSize:14,fontWeight:400,color:C.muted}}>forever</span></div>
                  : <>
                    {plan.offer_active&&plan.offer_price&&<div style={{fontSize:13,color:C.muted,textDecoration:'line-through'}}>₹{billing==='yearly'?(plan.price_yearly||plan.price_monthly*10):plan.price_monthly}</div>}
                    <div style={{fontSize:36,fontWeight:900,color}}><span style={{fontSize:18,fontWeight:400}}>₹</span>{price}<span style={{fontSize:14,fontWeight:400,color:C.muted}}>/mo</span></div>
                    {billing==='yearly'&&<div style={{fontSize:11,color:'#16A34A',fontWeight:600}}>Billed ₹{price*12}/year</div>}
                  </>
                }
              </div>
              {(() => {
                const PLAN_RANK = { hobby:0, solopreneur:1, agency:2 };
                const currRank  = PLAN_RANK[currentPlan] ?? 0;
                const thisRank  = PLAN_RANK[plan.id]     ?? 0;
                const isUpgrade = thisRank > currRank;
                const label = isCurr ? '✓ Current Plan'
                  : upgrading===plan.id ? '⏳ Processing…'
                  : isUpgrade ? `⬆ Upgrade to ${plan.name}`
                  : `⬇ Downgrade to ${plan.name}`;
                return (
                  <button onClick={()=>handleUpgrade(plan)} disabled={isCurr||upgrading===plan.id}
                    style={{...btn(isCurr?C.border:isUpgrade?color:'#6B7280'),width:'100%',fontSize:13,padding:'10px',borderRadius:9,marginBottom:16,cursor:isCurr?'default':'pointer',color:isCurr?C.muted:'#fff',opacity:upgrading===plan.id?0.6:1}}>
                    {label}
                  </button>
                );
              })()}
              <div style={{display:'flex',flexDirection:'column',gap:7}}>
                {features.slice(0,8).map((f,i)=>(
                  <div key={i} style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:12,color:C.text}}>
                    <span style={{color:'#16A34A',fontWeight:700,flexShrink:0}}>✓</span>{f}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Business workspace section for Agency */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15,color:'#2B1DC9',marginBottom:4}}>🏢 Agency: Business Workspace</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:10}}>Invite your team to the Business module with custom role-based access. Each role only sees what they need.</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8}}>
          {[{role:'Admin',color:'#DC2626',desc:'Full access to everything'},{role:'Manager',color:'#D97706',desc:'CRM + Invoices + Reports'},{role:'Sales',color:'#7C3AED',desc:'CRM + Proposals + Clients'},{role:'Caller',color:'#2563EB',desc:'CRM & Leads only'},{role:'Viewer',color:'#6B7280',desc:'Read-only access'}].map(r=>(
            <div key={r.role} style={{background:r.color+'10',border:`1px solid ${r.color}30`,borderRadius:8,padding:'10px',textAlign:'center'}}>
              <div style={{fontWeight:700,fontSize:12,color:r.color,marginBottom:3}}>{r.role}</div>
              <div style={{fontSize:10,color:C.muted,lineHeight:1.4}}>{r.desc}</div>
            </div>
          ))}
        </div>
        {currentPlan!=='agency'&&<div style={{marginTop:10,fontSize:12,color:C.muted}}>Available on the Agency plan.</div>}
      </div>

      <div style={{textAlign:'center',fontSize:12,color:C.muted,padding:'16px 0'}}>
        All plans include SSL, daily backups, and GDPR-compliant data handling.<br/>
        Questions? Email <a href="mailto:support@aspreo.com" style={{color:'#7C3AED'}}>support@aspreo.com</a>
      </div>

      {/* Trial Modal */}
      {trialModal && ReactDOM.createPortal(
        <div style={{position:'fixed',inset:0,zIndex:6000,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:C.surface,borderRadius:16,padding:32,maxWidth:420,width:'100%',textAlign:'center',fontFamily:FONT}}>
            <div style={{fontSize:48,marginBottom:12}}>🎉</div>
            <h3 style={{margin:'0 0 8px',fontSize:20,fontWeight:800,color:C.text}}>Try {trialModal.plan.name} free for 7 days</h3>
            <p style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:20}}>
              No payment required to start your trial. After 7 days, pay ₹{trialModal.price}/month to continue — or downgrade to Hobby for free.
            </p>
            <button onClick={async()=>{
              setUpgrading(trialModal.plan.id);
              await callPlanEdgeFn(trialModal.plan.id, {}, true);
              setTrialModal(null); setUpgrading(null);
              alert('🎉 Your 7-day trial has started! Check your email for details.');
            }} style={{...btn('#7C3AED'),width:'100%',fontSize:14,padding:'12px',marginBottom:8}}>
              ✨ Start Free Trial (7 days)
            </button>
            <button onClick={async()=>{
              setTrialModal(null);
              setUpgrading(trialModal.plan.id);
              await launchRazorpay(trialModal.plan, trialModal.price);
              setUpgrading(null);
            }} style={{...btn('#6B7280'),width:'100%',fontSize:13,padding:'10px',marginBottom:8}}>
              Skip trial — Pay ₹{trialModal.price}/month now
            </button>
            <button onClick={()=>setTrialModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:C.muted,fontFamily:FONT}}>Cancel</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// WORKSPACE TAB — inside BusinessModule (Agency plan only)
// ══════════════════════════════════════════════════════════════════════════════
function WorkspaceTab({ ctx }) {
  const C = useC();
  const { workspaces=[], workspaceMembers=[], addWorkspace, updateWorkspace, deleteWorkspace,
          inviteWorkspaceMember, updateWorkspaceMember, removeWorkspaceMember, uid } = ctx;

  const [showWsForm, setShowWsForm] = React.useState(false);
  const [wsName, setWsName] = React.useState('');
  const [selWs, setSelWs] = React.useState(null);
  const [inviteForm, setInviteForm] = React.useState({ email:'', role:'caller', access_modules:['crm'] });
  const [showInvite, setShowInvite] = React.useState(false);

  const ROLES = [
    { id:'admin',   label:'Admin',   color:'#DC2626', modules:['setup','services','crm','clients','invoices','proposals','whatsapp','sheets'] },
    { id:'manager', label:'Manager', color:'#D97706', modules:['crm','clients','invoices','proposals'] },
    { id:'sales',   label:'Sales',   color:'#7C3AED', modules:['crm','clients','proposals'] },
    { id:'caller',  label:'Caller',  color:'#2563EB', modules:['crm'] },
    { id:'viewer',  label:'Viewer',  color:'#6B7280', modules:['crm','clients'] },
  ];

  const wsMembers = selWs ? workspaceMembers.filter(m=>m.workspace_id===selWs.id) : [];

  const createWorkspace = async () => {
    if (!wsName.trim()) return;
    const ws = await addWorkspace({ name:wsName.trim() });
    if (ws) setSelWs(ws);
    setWsName(''); setShowWsForm(false);
  };

  const [inviteSending, setInviteSending] = React.useState(false);
  const [inviteSent,    setInviteSent]    = React.useState(false);

  const sendInvite = async () => {
    if (!inviteForm.email.trim() || !selWs) return;
    setInviteSending(true);
    const roleInfo = ROLES.find(r=>r.id===inviteForm.role);
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 24 hours
    const member = await inviteWorkspaceMember({
      workspace_id:   selWs.id,
      member_email:   inviteForm.email.trim().toLowerCase(),
      role:           inviteForm.role,
      access_modules: roleInfo?.modules || ['crm'],
      expires_at:     expiresAt,
      data_owner_id:  uid,
    });
    if (member) {
      // Send invite email via edge function
      const inviteUrl = `${window.location.origin}/workspace-invite/${member.invite_token}`;
      try {
        await fetch('/api/sb/functions/v1/send-workspace-invite', {
          method: 'POST',
          headers: { 'Content-Type':'application/json', 'Authorization':`Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY||''}` },
          body: JSON.stringify({
            member_email:  inviteForm.email.trim().toLowerCase(),
            role:          inviteForm.role,
            workspace_name:selWs.name,
            invite_url:    inviteUrl,
            owner_id:      uid,
          }),
        });
        setInviteSent(true);
        setTimeout(() => setInviteSent(false), 3000);
      } catch(e) {
        console.warn('[workspace invite email]', e);
        // Fallback: copy link
        navigator.clipboard.writeText(inviteUrl);
        alert(`✅ Invite created! (Email could not be sent — link copied instead)\n${inviteUrl}`);
      }
    }
    setInviteForm({ email:'', role:'caller', access_modules:['crm'] });
    setShowInvite(false);
    setInviteSending(false);
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:'#2B1DC9'}}>🏢 Team Workspaces</div>
          <div style={{fontSize:12,color:C.muted}}>Invite coworkers with role-based access to your business module</div>
        </div>
        <button onClick={()=>setShowWsForm(true)} style={btn('#2B1DC9')}>+ New Workspace</button>
      </div>

      {showWsForm&&(
        <div style={{background:C.surface,border:'1.5px solid #2B1DC9',borderRadius:10,padding:14,marginBottom:14,display:'flex',gap:8}}>
          <input value={wsName} onChange={e=>setWsName(e.target.value)} placeholder="Workspace name (e.g. Sales Team)" style={{...inp(),flex:1}}/>
          <button onClick={createWorkspace} style={btn('#2B1DC9')}>Create</button>
          <button onClick={()=>{setShowWsForm(false);setWsName('');}} style={btn('#999')}>Cancel</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:16}}>
        {/* Workspace list */}
        <div>
          {workspaces.length===0&&<div style={{textAlign:'center',color:C.muted,padding:24,fontSize:12}}>No workspaces yet</div>}
          {workspaces.map(ws=>(
            <div key={ws.id} onClick={()=>setSelWs(ws)}
              style={{background:C.surface,border:`2px solid ${selWs?.id===ws.id?'#2B1DC9':C.border}`,borderRadius:9,padding:'10px 14px',cursor:'pointer',marginBottom:6,transition:'all .15s'}}>
              <div style={{fontWeight:700,fontSize:13,color:selWs?.id===ws.id?'#2B1DC9':C.text}}>{ws.name}</div>
              <div style={{fontSize:11,color:C.muted}}>{workspaceMembers.filter(m=>m.workspace_id===ws.id).length} members</div>
            </div>
          ))}
        </div>

        {/* Members panel */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          {!selWs ? (
            <div style={{textAlign:'center',color:C.muted,padding:32}}>Select or create a workspace to manage members</div>
          ) : (
            <>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:14}}>{selWs.name}</div>
                <button onClick={()=>setShowInvite(true)} style={{...btn('#2B1DC9'),fontSize:12}}>+ Invite Member</button>
              </div>

              {showInvite&&(
                <div style={{background:C.surfaceAlt,borderRadius:9,padding:12,marginBottom:12}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 150px',gap:8,marginBottom:8}}>
                    <input value={inviteForm.email} onChange={e=>setInviteForm(p=>({...p,email:e.target.value}))} placeholder="Email address" type="email" style={{...inp(),fontSize:12}}/>
                    <select value={inviteForm.role} onChange={e=>setInviteForm(p=>({...p,role:e.target.value}))} style={{...inp(),fontSize:12}}>
                      {ROLES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  </div>
                  {/* Show what this role can access */}
                  <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
                    <strong>{ROLES.find(r=>r.id===inviteForm.role)?.label}</strong> can access:{' '}
                    {ROLES.find(r=>r.id===inviteForm.role)?.modules.join(', ')}
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <button onClick={sendInvite} disabled={inviteSending} style={{...btn('#2B1DC9'),fontSize:12,opacity:inviteSending?0.6:1}}>
                      {inviteSending?'⏳ Sending…':'📨 Send Invite Email'}
                    </button>
                    <button onClick={()=>setShowInvite(false)} style={{...btn('#999'),fontSize:12}}>Cancel</button>
                    {inviteSent&&<span style={{fontSize:12,color:'#16A34A',fontWeight:600}}>✅ Invite sent!</span>}
                  </div>
                </div>
              )}

              {wsMembers.length===0&&<div style={{textAlign:'center',color:C.muted,padding:24,fontSize:12}}>No members yet. Invite your team.</div>}
              {wsMembers.map(m=>{
                const role = ROLES.find(r=>r.id===m.role);
                return (
                  <div key={m.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{m.member_email}</div>
                      <div style={{fontSize:11,color:C.muted}}>Joined: {m.joined_at?new Date(m.joined_at).toLocaleDateString('en-IN'):'Pending'}</div>
                    </div>
                    <span style={{fontSize:11,padding:'2px 9px',borderRadius:10,background:(role?.color||'#6B7280')+'18',color:role?.color||'#6B7280',fontWeight:700}}>{m.role}</span>
                    <span style={{fontSize:11,padding:'2px 9px',borderRadius:10,background:m.status==='active'?'#DCFCE7':'#FEF9C3',color:m.status==='active'?'#16A34A':'#D97706',fontWeight:600}}>{m.status}</span>
                    <select value={m.role} onChange={e=>updateWorkspaceMember(m.id,{role:e.target.value,access_modules:ROLES.find(r=>r.id===e.target.value)?.modules||['crm']})}
                      style={{fontSize:11,padding:'3px 6px',border:`1px solid ${C.border}`,borderRadius:6,background:C.surface,cursor:'pointer'}}>
                      {ROLES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                    <button onClick={()=>{if(window.confirm('Remove member?'))removeWorkspaceMember(m.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>Remove</button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// BUSINESS EXPENSE TAB
// ══════════════════════════════════════════════════════════════════════════════
function BusinessExpenseTab({ ctx }) {
  const C = useC();
  const { businessExpenses=[], addBusinessExpense, deleteBusinessExpense, invoices=[], uid } = ctx;
  const BIZ_COLOR = '#1D5F8A';
  const blank = { description:'', amount:'', category:'Operations', expense_date:TODAY, vendor:'', invoice_ref:'' };
  const [form, setForm] = React.useState(blank);
  const [showForm, setShowForm] = React.useState(false);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const CATS = ['Operations','Marketing','Software','Equipment','Travel','Salaries','Office','Tax','Other'];

  const save = async () => {
    if (!form.description.trim() || !form.amount) return;
    await addBusinessExpense({ ...form, amount: parseFloat(form.amount), user_id: uid });
    setForm(blank); setShowForm(false);
  };

  const paidRevenue = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(parseFloat(i.total)||0),0);
  const totalExp    = businessExpenses.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);
  const profit      = paidRevenue - totalExp;

  const byCategory = CATS.map(c=>({
    cat: c,
    total: businessExpenses.filter(e=>e.category===c).reduce((s,e)=>s+(parseFloat(e.amount)||0),0)
  })).filter(x=>x.total>0).sort((a,b)=>b.total-a.total);

  const thisMonth = TODAY.slice(0,7);
  const monthExp = businessExpenses.filter(e=>(e.expense_date||'').startsWith(thisMonth)).reduce((s,e)=>s+(parseFloat(e.amount)||0),0);

  return (
    <div>
      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
        {[
          {label:'Total Revenue (Paid)', val:`₹${paidRevenue.toLocaleString('en-IN')}`, color:'#16A34A', bg:'#F0FDF4'},
          {label:'Total Business Expenses', val:`₹${totalExp.toLocaleString('en-IN')}`, color:'#DC2626', bg:'#FEF2F2'},
          {label:'Net Profit', val:`₹${profit.toLocaleString('en-IN')}`, color:profit>=0?'#16A34A':'#DC2626', bg:profit>=0?'#F0FDF4':'#FEF2F2'},
        ].map(card=>(
          <div key={card.label} style={{background:card.bg,border:`1px solid ${card.color}30`,borderRadius:10,padding:14,textAlign:'center'}}>
            <div style={{fontSize:18,fontWeight:800,color:card.color}}>{card.val}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{card.label}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 280px',gap:14}}>
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:14,color:BIZ_COLOR}}>💰 Business Expenses</div>
            <button onClick={()=>setShowForm(v=>!v)} style={{...btn(BIZ_COLOR),fontSize:12}}>+ Add Expense</button>
          </div>
          {showForm&&(
            <div style={{background:C.surface,border:`1.5px solid ${BIZ_COLOR}`,borderRadius:10,padding:14,marginBottom:14}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <div style={{gridColumn:'1/-1'}}><label style={lbl}>Description *</label><input value={form.description} onChange={e=>upd('description',e.target.value)} placeholder="e.g. Canva Pro subscription" style={{...inp(),width:'100%'}} autoFocus/></div>
                <div><label style={lbl}>Amount (₹) *</label><input type="number" value={form.amount} onChange={e=>upd('amount',e.target.value)} placeholder="0" style={{...inp(),width:'100%'}}/></div>
                <div><label style={lbl}>Date</label><input type="date" value={form.expense_date} onChange={e=>upd('expense_date',e.target.value)} style={{...inp(),width:'100%'}}/></div>
                <div><label style={lbl}>Category</label>
                  <select value={form.category} onChange={e=>upd('category',e.target.value)} style={{...inp(),width:'100%'}}>
                    {CATS.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Vendor</label><input value={form.vendor} onChange={e=>upd('vendor',e.target.value)} placeholder="Vendor name" style={{...inp(),width:'100%'}}/></div>
                <div><label style={lbl}>Invoice Ref</label><input value={form.invoice_ref} onChange={e=>upd('invoice_ref',e.target.value)} placeholder="Invoice / receipt #" style={{...inp(),width:'100%'}}/></div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={save} style={{...btn(BIZ_COLOR),fontSize:12}}>💾 Save</button>
                <button onClick={()=>{setShowForm(false);setForm(blank);}} style={{...btn('#999'),fontSize:12}}>Cancel</button>
              </div>
            </div>
          )}
          {businessExpenses.length===0&&<div style={{textAlign:'center',color:C.muted,padding:32}}>No business expenses yet. Track your costs here.</div>}
          {businessExpenses.map(e=>(
            <div key={e.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,marginBottom:6}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{e.description}</div>
                <div style={{fontSize:11,color:C.muted}}>{e.category} · {e.expense_date} {e.vendor&&`· ${e.vendor}`} {e.invoice_ref&&`· #${e.invoice_ref}`}</div>
              </div>
              <div style={{fontWeight:700,color:'#DC2626',fontSize:14}}>₹{parseFloat(e.amount||0).toLocaleString('en-IN')}</div>
              <button onClick={()=>{if(window.confirm('Delete this expense?'))deleteBusinessExpense(e.id);}} style={{...btn(C.red),fontSize:11,padding:'4px 8px'}}>🗑</button>
            </div>
          ))}
        </div>

        {/* Category breakdown */}
        <div>
          <div style={{fontWeight:700,fontSize:13,color:BIZ_COLOR,marginBottom:10}}>📊 This Month: ₹{monthExp.toLocaleString('en-IN')}</div>
          {byCategory.length===0?<div style={{fontSize:12,color:C.muted}}>No categories yet.</div>:byCategory.map(({cat,total})=>(
            <div key={cat} style={{marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:3}}>
                <span style={{color:C.text,fontWeight:600}}>{cat}</span>
                <span style={{color:'#DC2626',fontWeight:700}}>₹{total.toLocaleString('en-IN')}</span>
              </div>
              <div style={{height:4,background:C.border,borderRadius:2}}>
                <div style={{height:4,background:'#DC2626',borderRadius:2,width:`${Math.min(100,(total/totalExp)*100)}%`}}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE — Second Brain (personal knowledge library)
// ══════════════════════════════════════════════════════════════════════════════
const KNOW_COLOR = '#7C3AED';
const KNOWLEDGE_TYPES = [
  { id:'note',       label:'Note',            icon:'📝' },
  { id:'book',       label:'Book',            icon:'📚' },
  { id:'article',    label:'Article',         icon:'📰' },
  { id:'youtube',    label:'YouTube',         icon:'▶️' },
  { id:'website',    label:'Website',         icon:'🌐' },
  { id:'pdf',        label:'PDF',             icon:'📄' },
  { id:'quote',      label:'Quote',           icon:'❝' },
  { id:'framework',  label:'Framework',       icon:'🧩' },
  { id:'idea',       label:'Idea',            icon:'💡' },
  { id:'paper',      label:'Research Paper',  icon:'🔬' },
  { id:'attachment', label:'Attachment',      icon:'📎' },
];
const knowTypeMeta = (t) => KNOWLEDGE_TYPES.find(k=>k.id===t) || KNOWLEDGE_TYPES[0];
const READING_TYPES = new Set(['book','article','pdf','paper']);

// Lightweight markdown -> HTML renderer (headings, bold/italic/code, links,
// images, checklists, bullet/numbered lists, blockquotes, tables, code blocks).
// No external dependency — keeps the bundle small and matches the rest of the app.
function knowledgeMdToHtml(src) {
  if (!src || !src.trim()) return '<span style="color:var(--ta-on-surface-muted);font-style:italic;">Nothing to preview yet.</span>';
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const inline = s => esc(s)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" style="max-width:100%;border-radius:8px;margin:6px 0;display:block;"/>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color:${KNOW_COLOR}">$1</a>`)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--ta-surface-low);padding:1px 5px;border-radius:4px;font-size:.9em;">$1</code>');

  const lines = src.split('\n');
  let html = '', inCode = false, codeBuf = [], listType = null, inTable = false, tableRows = [], paraBuf = [];
  const flushPara = () => { if (paraBuf.length) { html += `<p style="margin:6px 0;">${inline(paraBuf.join(' '))}</p>`; paraBuf = []; } };
  const closeList = () => { if (listType) { html += `</${listType}>`; listType = null; } };
  const cells = row => row.split('|').map(c=>c.trim()).filter((c,idx,arr)=>!(idx===0&&c==='')&&!(idx===arr.length-1&&c===''));
  const flushTable = () => {
    if (!tableRows.length) return;
    const [header, , ...body] = tableRows;
    html += '<table style="border-collapse:collapse;width:100%;margin:10px 0;font-size:13px;"><thead><tr>' +
      cells(header).map(h=>`<th style="text-align:left;border-bottom:2px solid var(--ta-outline-variant);padding:6px 10px;">${inline(h)}</th>`).join('') +
      '</tr></thead><tbody>';
    body.forEach(row => { html += '<tr>' + cells(row).map(c=>`<td style="border-bottom:1px solid var(--ta-outline-variant);padding:6px 10px;">${inline(c)}</td>`).join('') + '</tr>'; });
    html += '</tbody></table>';
    tableRows = []; inTable = false;
  };

  for (let i=0; i<lines.length; i++) {
    const line = lines[i];
    if (/^```/.test(line)) {
      if (inCode) { html += `<pre style="background:var(--ta-surface-low);padding:12px;border-radius:8px;overflow-x:auto;"><code>${esc(codeBuf.join('\n'))}</code></pre>`; codeBuf = []; inCode = false; }
      else { flushPara(); closeList(); inCode = true; }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }

    if (/^\s*\|.*\|\s*$/.test(line)) { flushPara(); closeList(); inTable = true; tableRows.push(line); continue; }
    else if (inTable) flushTable();

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { flushPara(); closeList(); const lvl=h[1].length; html += `<h${lvl} style="margin:14px 0 6px;">${inline(h[2])}</h${lvl}>`; continue; }

    const chk = line.match(/^[-*]\s+\[( |x|X)\]\s+(.*)$/);
    if (chk) {
      flushPara();
      if (listType!=='ul') { closeList(); html += '<ul style="list-style:none;padding-left:4px;margin:6px 0;">'; listType='ul'; }
      const checked = chk[1].toLowerCase()==='x';
      html += `<li style="display:flex;gap:6px;align-items:flex-start;"><input type="checkbox" disabled ${checked?'checked':''} style="margin-top:4px;"/><span style="${checked?'text-decoration:line-through;opacity:.6;':''}">${inline(chk[2])}</span></li>`;
      continue;
    }

    const bq = line.match(/^>\s?(.*)$/);
    if (bq) { flushPara(); closeList(); html += `<blockquote style="border-left:3px solid ${KNOW_COLOR};margin:8px 0;padding:2px 14px;color:var(--ta-on-surface-variant);font-style:italic;">${inline(bq[1])}</blockquote>`; continue; }

    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ol) { flushPara(); if (listType!=='ol') { closeList(); html += '<ol style="margin:6px 0;padding-left:22px;">'; listType='ol'; } html += `<li>${inline(ol[1])}</li>`; continue; }

    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    if (ul) { flushPara(); if (listType!=='ul') { closeList(); html += '<ul style="margin:6px 0;padding-left:22px;">'; listType='ul'; } html += `<li>${inline(ul[1])}</li>`; continue; }

    if (line.trim()==='') { flushPara(); closeList(); continue; }
    paraBuf.push(line);
  }
  flushTable(); flushPara(); closeList();
  return html;
}

function KnowledgeEditorPanel({ ctx, item, onClose, onOpenRelated }) {
  const {
    uid, knowledgeDomains=[], knowledgeCollections=[], knowledgeTags=[], knowledgeItemTags=[],
    knowledgeItems=[], knowledgeRelated=[], knowledgeAttachments=[], addKnowledgeItem, updateKnowledgeItem, deleteKnowledgeItem,
    getOrCreateKnowledgeTag, setKnowledgeItemTagList, addKnowledgeRelated, deleteKnowledgeRelated,
    addKnowledgeAttachment, deleteKnowledgeAttachment,
    getKnowledgeVersions, saveKnowledgeVersion, goals=[],
  } = ctx;
  const isNew = !item?.id;
  const [relatedQuery, setRelatedQuery] = React.useState('');
  const [form, setForm] = React.useState(() => ({
    type: item?.type || 'note', title: item?.title || '', summary: item?.summary || '',
    content: item?.content || '', source_url: item?.source_url || '', author: item?.author || '',
    source_name: item?.source_name || '', domain_id: item?.domain_id || '', collection_id: item?.collection_id || '',
    is_favorite: item?.is_favorite || false, is_pinned: item?.is_pinned || false,
    status: item?.status || 'active', reading_progress_pct: item?.reading_progress_pct || 0,
    reading_status: item?.reading_status || 'unread',
    linked_goal_id: item?.linked_goal_id || '', linked_subitem_id: item?.linked_subitem_id || '',
  }));
  const [tagIds, setTagIds] = React.useState(() =>
    item?.id ? knowledgeItemTags.filter(x=>x.item_id===item.id).map(x=>x.tag_id) : []
  );
  const [tagInput, setTagInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const [previewMode, setPreviewMode] = React.useState(false);
  const contentRef = React.useRef(null);

  const wrapSelection = (before, after=before) => {
    const ta = contentRef.current; if (!ta) return;
    const { selectionStart:s, selectionEnd:e, value } = ta;
    const sel = value.slice(s,e) || 'text';
    const next = value.slice(0,s) + before + sel + after + value.slice(e);
    set('content', next);
    requestAnimationFrame(()=>{ ta.focus(); ta.setSelectionRange(s+before.length, s+before.length+sel.length); });
  };
  const prefixLines = (prefix) => {
    const ta = contentRef.current; if (!ta) return;
    const { selectionStart:s, selectionEnd:e, value } = ta;
    const lineStart = value.lastIndexOf('\n', s-1)+1;
    const segment = value.slice(lineStart, e);
    const updated = segment.split('\n').map(l=>prefix+l).join('\n');
    const next = value.slice(0,lineStart) + updated + value.slice(e);
    set('content', next);
    requestAnimationFrame(()=>ta.focus());
  };
  const insertAtCursor = (text) => {
    const ta = contentRef.current;
    if (!ta) { set('content', (form.content||'')+text); return; }
    const { selectionStart:s, value } = ta;
    const next = value.slice(0,s)+text+value.slice(s);
    set('content', next);
    requestAnimationFrame(()=>{ ta.focus(); ta.setSelectionRange(s+text.length, s+text.length); });
  };
  const MD_TOOLS = [
    { icon:'B',  title:'Bold',           style:{fontWeight:700},    action:()=>wrapSelection('**') },
    { icon:'I',  title:'Italic',         style:{fontStyle:'italic'},action:()=>wrapSelection('*') },
    { icon:'H',  title:'Heading',        action:()=>prefixLines('## ') },
    { icon:'❝',  title:'Quote',          action:()=>prefixLines('> ') },
    { icon:'{}', title:'Code',           action:()=>wrapSelection('`') },
    { icon:'☑',  title:'Checklist',      action:()=>prefixLines('- [ ] ') },
    { icon:'•',  title:'Bulleted list',  action:()=>prefixLines('- ') },
    { icon:'1.', title:'Numbered list',  action:()=>prefixLines('1. ') },
    { icon:'🔗', title:'Link',           action:()=>insertAtCursor('[link text](https://)') },
    { icon:'🖼', title:'Image',          action:()=>insertAtCursor('![alt text](https://)') },
    { icon:'▦',  title:'Table',          action:()=>insertAtCursor('\n| Column 1 | Column 2 |\n| --- | --- |\n| Cell | Cell |\n') },
  ];

  const [uploadingFile, setUploadingFile] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const itemAttachments = isNew ? [] : knowledgeAttachments.filter(a=>a.item_id===item.id);
  const uploadAttachment = async (file) => {
    if (!file || isNew) return;
    setUploadingFile(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${uid}/${item.id}-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage.from('knowledge-files').upload(path, file, { upsert:true, contentType:file.type });
      if (error) { alert('Upload failed: '+error.message); return; }
      const { data: pub } = supabase.storage.from('knowledge-files').getPublicUrl(data.path);
      await addKnowledgeAttachment({ item_id: item.id, file_url: pub.publicUrl, filename: file.name, file_type: file.type, size_bytes: file.size });
    } catch(e) { alert('Upload failed: '+e.message); }
    setUploadingFile(false);
  };
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024*1024) return `${Math.round(bytes/1024)} KB`;
    return `${(bytes/(1024*1024)).toFixed(1)} MB`;
  };

  const [versionsOpen, setVersionsOpen] = React.useState(false);
  const [versions, setVersions] = React.useState([]);
  const [versionsLoading, setVersionsLoading] = React.useState(false);
  const openVersions = async () => {
    setVersionsOpen(true); setVersionsLoading(true);
    setVersions(await getKnowledgeVersions(item.id));
    setVersionsLoading(false);
  };
  const restoreVersion = (v) => {
    if (!window.confirm(`Restore "${v.title||'Untitled'}" from ${new Date(v.created_at).toLocaleString()}? Your current unsaved edits will be replaced.`)) return;
    set('title', v.title||''); set('content', v.content||'');
    setVersionsOpen(false);
  };

  const collectionsForDomain = knowledgeCollections.filter(c => !form.domain_id || c.domain_id===form.domain_id);
  const linkedGoalObj = goals.find(g=>g.id===form.linked_goal_id);
  const linkableSubItems = (linkedGoalObj?.subItems||[]).filter(s=>s.text?.trim());
  const currentTags = tagIds.map(id => knowledgeTags.find(t=>t.id===id)).filter(Boolean);
  const tagSuggestions = knowledgeTags.filter(t =>
    !tagIds.includes(t.id) && tagInput && t.name.toLowerCase().includes(tagInput.toLowerCase())
  ).slice(0,6);

  const addTag = async (name) => {
    const tag = await getOrCreateKnowledgeTag(name);
    if (tag && !tagIds.includes(tag.id)) setTagIds(p=>[...p,tag.id]);
    setTagInput('');
  };
  const removeTag = (id) => setTagIds(p=>p.filter(x=>x!==id));

  const relatedRows = isNew ? [] : knowledgeRelated.filter(r => r.item_id===item.id || r.related_item_id===item.id);
  const relatedItems = relatedRows
    .map(r => ({ relId: r.id, other: knowledgeItems.find(i => i.id === (r.item_id===item.id ? r.related_item_id : r.item_id)) }))
    .filter(x => x.other);
  const relatedSuggestions = isNew ? [] : knowledgeItems.filter(i =>
    i.id !== item.id && !relatedItems.some(r => r.other.id===i.id) && relatedQuery && i.title.toLowerCase().includes(relatedQuery.toLowerCase())
  ).slice(0,6);
  const linkRelated = async (targetId) => { await addKnowledgeRelated(item.id, targetId); setRelatedQuery(''); };
  const unlinkRelated = (relId) => deleteKnowledgeRelated(relId);

  const save = async () => {
    setSaving(true);
    const payload = { ...form, title: form.title.trim()||'Untitled', domain_id: form.domain_id||null, collection_id: form.collection_id||null, linked_goal_id: form.linked_goal_id||null, linked_subitem_id: form.linked_subitem_id||null };
    let saved;
    if (isNew) saved = await addKnowledgeItem(payload);
    else {
      // Snapshot the pre-edit state so version history has something to roll back to.
      if ((item.title||'')!==payload.title || (item.content||'')!==payload.content) {
        await saveKnowledgeVersion(item.id, item.title, item.content);
      }
      updateKnowledgeItem(item.id, payload); saved = { ...item, ...payload };
    }
    if (saved?.id) await setKnowledgeItemTagList(saved.id, tagIds);
    setSaving(false);
    onClose();
  };

  const remove = () => {
    if (!window.confirm('Delete this knowledge item? This cannot be undone.')) return;
    deleteKnowledgeItem(item.id);
    onClose();
  };

  const tm = knowTypeMeta(form.type);

  // Rendered via portal to document.body: `.diary-section` (the ancestor wrapping every
  // app section) carries a CSS transform animation, which — per spec — makes any
  // `position:fixed` descendant center/size itself relative to THAT box instead of the
  // real viewport. A portal escapes that subtree so the modal is truly viewport-centered.
  return ReactDOM.createPortal(
    <div style={{position:'fixed',inset:0,background:'rgba(20,15,35,0.55)',zIndex:200,display:'flex',justifyContent:'center',alignItems:'center',padding:20,overflowY:'auto'}} onClick={onClose}>
      <div className="ta-card" style={{width:'min(760px,100%)',maxHeight:'90vh',padding:0,overflowY:'auto',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 22px',borderBottom:'1px solid var(--ta-outline-variant)'}}>
          <div className="ta-row" style={{gap:10,alignItems:'center'}}>
            <select value={form.type} onChange={e=>set('type',e.target.value)} style={{fontSize:20,border:'none',background:'transparent',cursor:'pointer'}}>
              {KNOWLEDGE_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
            <span className="ta-eh-eyebrow" style={{margin:0}}>{isNew?'New':'Edit'} {tm.label.toLowerCase()}</span>
          </div>
          <div className="ta-row" style={{gap:6}}>
            <button onClick={()=>set('is_favorite',!form.is_favorite)} title="Favorite" style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:form.is_favorite?'#E0A400':'var(--ta-on-surface-muted)'}}>{form.is_favorite?'★':'☆'}</button>
            <button onClick={()=>set('is_pinned',!form.is_pinned)} title="Pin" style={{background:'none',border:'none',cursor:'pointer',fontSize:16,opacity:form.is_pinned?1:0.35}}>📌</button>
            <button onClick={()=>set('status', form.status==='archived'?'active':'archived')} title={form.status==='archived'?'Unarchive':'Archive'} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,opacity:form.status==='archived'?1:0.35}}>🗄</button>
            {!isNew && <button onClick={openVersions} title="Version history" style={{background:'none',border:'none',cursor:'pointer',fontSize:15,opacity:0.6}}>🕘</button>}
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'var(--ta-on-surface-muted)',marginLeft:6}}>✕</button>
          </div>
        </div>

        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>
          <input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Title…" className="ta-serif"
            style={{fontSize:24,border:'none',outline:'none',background:'transparent',color:'var(--ta-on-surface)'}}/>

          <textarea value={form.summary} onChange={e=>set('summary',e.target.value)} placeholder="One-line summary — shown on the card…"
            rows={2} style={{fontSize:13,fontStyle:'italic',border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',resize:'vertical',background:'var(--ta-surface-low)',color:'var(--ta-on-surface-variant)'}}/>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <input value={form.author} onChange={e=>set('author',e.target.value)} placeholder="Author"
              style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
            <input value={form.source_name} onChange={e=>set('source_name',e.target.value)} placeholder="Publisher / channel / journal"
              style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
          </div>
          <input value={form.source_url} onChange={e=>set('source_url',e.target.value)} placeholder="Source URL (article / youtube / website / paper)"
            style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <select value={form.domain_id} onChange={e=>set('domain_id',e.target.value)}
              style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
              <option value="">No domain</option>
              {knowledgeDomains.map(d=><option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}
            </select>
            <select value={form.collection_id} onChange={e=>set('collection_id',e.target.value)}
              style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
              <option value="">No collection</option>
              {collectionsForDomain.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>

          <div style={{display:'grid',gridTemplateColumns: linkedGoalObj && linkableSubItems.length>0 ? '1fr 1fr' : '1fr',gap:10}}>
            <select value={form.linked_goal_id} onChange={e=>{set('linked_goal_id',e.target.value);set('linked_subitem_id','');}}
              style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
              <option value="">🎯 Not linked to a goal</option>
              {goals.filter(g=>!g.archived).map(g=><option key={g.id} value={g.id}>🎯 {g.title}</option>)}
            </select>
            {linkedGoalObj && linkableSubItems.length>0 && (
              <select value={form.linked_subitem_id} onChange={e=>set('linked_subitem_id',e.target.value)}
                style={{fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
                <option value="">Whole goal</option>
                {linkableSubItems.map(s=><option key={s.id} value={String(s.id)}>{s.type==='subgoal'?'🎯':'📌'} {s.text}</option>)}
              </select>
            )}
          </div>

          <div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:6}}>
              {currentTags.map(t=>(
                <span key={t.id} style={{fontSize:11,padding:'3px 9px',borderRadius:12,background:(t.color||'#8888AA')+'22',color:t.color||'#8888AA',display:'flex',alignItems:'center',gap:5}}>
                  #{t.name}<span onClick={()=>removeTag(t.id)} style={{cursor:'pointer',opacity:0.7}}>✕</span>
                </span>
              ))}
            </div>
            <div style={{position:'relative'}}>
              <input value={tagInput} onChange={e=>setTagInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter' && tagInput.trim()){ e.preventDefault(); addTag(tagInput.trim()); } }}
                placeholder="Add a tag and press Enter…"
                style={{width:'100%',fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
              {tagSuggestions.length>0 && (
                <div className="ta-card" style={{position:'absolute',top:'100%',left:0,right:0,zIndex:5,padding:4,marginTop:4}}>
                  {tagSuggestions.map(t=>(
                    <div key={t.id} onClick={()=>addTag(t.name)} style={{padding:'6px 8px',fontSize:12,cursor:'pointer',borderRadius:6}}>#{t.name}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!isNew && (
            <div>
              <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>Related Knowledge</div>
              {relatedItems.length>0 && (
                <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:8}}>
                  {relatedItems.map(({relId,other}) => {
                    const otm = knowTypeMeta(other.type);
                    return (
                      <div key={relId} className="ta-row ta-between" style={{border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'7px 10px',background:'var(--ta-surface-low)'}}>
                        <span onClick={()=>onOpenRelated(other)} style={{cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',gap:6}}>
                          <span>{otm.icon}</span>{other.title||'Untitled'}
                        </span>
                        <span onClick={()=>unlinkRelated(relId)} style={{cursor:'pointer',fontSize:11,opacity:0.5}}>✕</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{position:'relative'}}>
                <input value={relatedQuery} onChange={e=>setRelatedQuery(e.target.value)}
                  placeholder="Link another item by title…"
                  style={{width:'100%',fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
                {relatedSuggestions.length>0 && (
                  <div className="ta-card" style={{position:'absolute',top:'100%',left:0,right:0,zIndex:5,padding:4,marginTop:4}}>
                    {relatedSuggestions.map(i=>{
                      const itm = knowTypeMeta(i.type);
                      return <div key={i.id} onClick={()=>linkRelated(i.id)} style={{padding:'6px 8px',fontSize:12,cursor:'pointer',borderRadius:6,display:'flex',alignItems:'center',gap:6}}><span>{itm.icon}</span>{i.title||'Untitled'}</div>;
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isNew && (
            <div>
              <div className="ta-row ta-between" style={{marginBottom:6}}>
                <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.05em'}}>Attachments</div>
                <span onClick={()=>fileInputRef.current?.click()} style={{fontSize:11,color:KNOW_COLOR,cursor:'pointer'}}>{uploadingFile?'Uploading…':'+ Add file'}</span>
                <input ref={fileInputRef} type="file" style={{display:'none'}} onChange={e=>{const f=e.target.files?.[0]; if(f) uploadAttachment(f); e.target.value='';}}/>
              </div>
              {itemAttachments.length===0 ? (
                <div style={{fontSize:12,color:'var(--ta-on-surface-muted)',fontStyle:'italic'}}>No files yet — attach an image, PDF, or document.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {itemAttachments.map(a=>{
                    const isImg = (a.file_type||'').startsWith('image/');
                    return (
                      <div key={a.id} className="ta-row ta-between" style={{border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'7px 10px',background:'var(--ta-surface-low)'}}>
                        <a href={a.file_url} target="_blank" rel="noopener noreferrer" style={{flex:1,display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--ta-on-surface)',textDecoration:'none',overflow:'hidden'}}>
                          {isImg ? <img src={a.file_url} alt={a.filename} style={{width:28,height:28,objectFit:'cover',borderRadius:4}}/> : <span>{(a.file_type||'').includes('pdf')?'📄':'📎'}</span>}
                          <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.filename}</span>
                          <span style={{fontSize:10,color:'var(--ta-on-surface-muted)',flexShrink:0}}>{formatSize(a.size_bytes)}</span>
                        </a>
                        <span onClick={()=>deleteKnowledgeAttachment(a.id)} style={{cursor:'pointer',fontSize:11,opacity:0.5}}>✕</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {READING_TYPES.has(form.type) && (
            <div>
              <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:4}}>Reading progress — {Math.round(form.reading_progress_pct)}%</div>
              <input type="range" min={0} max={100} value={form.reading_progress_pct}
                onChange={e=>{ const v=Number(e.target.value); set('reading_progress_pct',v); set('reading_status', v>=100?'done':v>0?'in_progress':'unread'); }}
                style={{width:'100%'}}/>
            </div>
          )}

          <div>
            <div className="ta-row ta-between" style={{marginBottom:6,flexWrap:'wrap',gap:6}}>
              <div className="ta-row" style={{gap:3,flexWrap:'wrap'}}>
                {MD_TOOLS.map(t=>(
                  <button key={t.title} type="button" onClick={t.action} title={t.title}
                    style={{...(t.style||{}),width:26,height:26,border:'1px solid var(--ta-outline-variant)',borderRadius:6,background:'var(--ta-surface-low)',cursor:'pointer',fontSize:12,color:'var(--ta-on-surface)'}}>{t.icon}</button>
                ))}
              </div>
              <div className="ta-row" style={{gap:4}}>
                <button type="button" onClick={()=>setPreviewMode(false)} className={'ta-btn'+(!previewMode?' ta-btn-primary':'')} style={{fontSize:11,padding:'4px 10px'}}>Write</button>
                <button type="button" onClick={()=>setPreviewMode(true)} className={'ta-btn'+(previewMode?' ta-btn-primary':'')} style={{fontSize:11,padding:'4px 10px'}}>Preview</button>
              </div>
            </div>
            {previewMode ? (
              <div style={{fontSize:14,lineHeight:1.7,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'12px 14px',minHeight:220,background:'var(--ta-surface-low)',color:'var(--ta-on-surface)',boxSizing:'border-box'}}
                dangerouslySetInnerHTML={{__html: knowledgeMdToHtml(form.content)}}/>
            ) : (
              <textarea ref={contentRef} value={form.content} onChange={e=>set('content',e.target.value)} placeholder="Notes / content (Markdown supported: **bold**, *italic*, # heading, - [ ] checklist, [link](url), ![image](url), tables)…"
                rows={10} style={{width:'100%',fontSize:14,lineHeight:1.7,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'12px 14px',resize:'vertical',fontFamily:'var(--font-body)',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)',boxSizing:'border-box'}}/>
            )}
          </div>
        </div>

        <div style={{display:'flex',justifyContent:'space-between',padding:'14px 22px',borderTop:'1px solid var(--ta-outline-variant)'}}>
          {!isNew ? <button onClick={remove} className="ta-btn" style={{color:'var(--ta-negative)'}}>Delete</button> : <span/>}
          <div className="ta-row" style={{gap:8}}>
            <button onClick={onClose} className="ta-btn">Cancel</button>
            <button onClick={save} disabled={saving} className="ta-btn ta-btn-primary">{saving?'Saving…':'Save'}</button>
          </div>
        </div>
      </div>

      {versionsOpen && (
        <div style={{position:'fixed',inset:0,background:'rgba(20,15,35,0.55)',zIndex:220,display:'flex',justifyContent:'center',alignItems:'center',padding:20}} onClick={()=>setVersionsOpen(false)}>
          <div className="ta-card" style={{width:'min(480px,100%)',maxHeight:'80vh',display:'flex',flexDirection:'column',padding:0}} onClick={e=>e.stopPropagation()}>
            <div className="ta-row ta-between" style={{padding:'14px 20px',borderBottom:'1px solid var(--ta-outline-variant)'}}>
              <span className="ta-eh-eyebrow" style={{margin:0}}>Version history</span>
              <button onClick={()=>setVersionsOpen(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:'var(--ta-on-surface-muted)'}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'8px 12px'}}>
              {versionsLoading ? (
                <div style={{fontSize:12,color:'var(--ta-on-surface-muted)',textAlign:'center',padding:24}}>Loading…</div>
              ) : versions.length===0 ? (
                <div style={{fontSize:12,color:'var(--ta-on-surface-muted)',fontStyle:'italic',textAlign:'center',padding:24}}>No earlier versions yet — a snapshot is saved automatically each time you edit the title or content.</div>
              ) : versions.map(v=>(
                <div key={v.id} className="ta-row ta-between" style={{padding:'10px 8px',borderBottom:'1px solid var(--ta-outline-variant)'}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v.title||'Untitled'}</div>
                    <div style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{new Date(v.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={()=>restoreVersion(v)} className="ta-btn" style={{fontSize:11,padding:'4px 10px',flexShrink:0}}>Restore</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

function KnowledgeCard({ item, tags, onOpen, onToggleFavorite, onTogglePinned }) {
  const tm = knowTypeMeta(item.type);
  return (
    <div className="ta-card" style={{padding:16,cursor:'pointer',display:'flex',flexDirection:'column',gap:8,position:'relative'}} onClick={onOpen}>
      <div className="ta-row ta-between" style={{alignItems:'flex-start'}}>
        <span style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--ta-on-surface-muted)',display:'flex',alignItems:'center',gap:5}}>
          <span style={{fontSize:14}}>{tm.icon}</span>{tm.label}
        </span>
        <div className="ta-row" style={{gap:4}} onClick={e=>e.stopPropagation()}>
          <span onClick={onTogglePinned} style={{cursor:'pointer',fontSize:13,opacity:item.is_pinned?1:0.25}}>📌</span>
          <span onClick={onToggleFavorite} style={{cursor:'pointer',fontSize:15,color:item.is_favorite?'#E0A400':'var(--ta-on-surface-muted)',opacity:item.is_favorite?1:0.35}}>{item.is_favorite?'★':'☆'}</span>
        </div>
      </div>
      <div className="ta-serif" style={{fontSize:16,fontWeight:600,color:'var(--ta-on-surface)'}}>{item.title||'Untitled'}</div>
      {item.summary && <div style={{fontSize:12.5,color:'var(--ta-on-surface-variant)',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:1.5}}>{item.summary}</div>}
      {READING_TYPES.has(item.type) && item.reading_progress_pct>0 && (
        <div style={{height:4,borderRadius:2,background:'var(--ta-outline-variant)',overflow:'hidden'}}>
          <div style={{height:'100%',width:`${item.reading_progress_pct}%`,background:KNOW_COLOR}}/>
        </div>
      )}
      {tags.length>0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
          {tags.slice(0,3).map(t=>(
            <span key={t.id} style={{fontSize:10,padding:'2px 7px',borderRadius:10,background:(t.color||'#8888AA')+'22',color:t.color||'#8888AA'}}>#{t.name}</span>
          ))}
          {tags.length>3 && <span style={{fontSize:10,color:'var(--ta-on-surface-muted)'}}>+{tags.length-3}</span>}
        </div>
      )}
      <div style={{fontSize:10.5,color:'var(--ta-on-surface-muted)',marginTop:2}}>
        {(item.author||item.source_name) ? `${item.author||''}${item.author&&item.source_name?' · ':''}${item.source_name||''} · ` : ''}
        {new Date(item.updated_at||item.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'})}
      </div>
    </div>
  );
}

function KnowledgeMiniRow({ item, onOpen }) {
  const tm = knowTypeMeta(item.type);
  return (
    <div onClick={onOpen} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 2px',cursor:'pointer',borderBottom:'1px solid var(--ta-outline-variant)'}}>
      <span style={{fontSize:15}}>{tm.icon}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.title||'Untitled'}</div>
        {READING_TYPES.has(item.type) && item.reading_progress_pct>0 && (
          <div style={{height:3,borderRadius:2,background:'var(--ta-outline-variant)',overflow:'hidden',marginTop:4}}>
            <div style={{height:'100%',width:`${item.reading_progress_pct}%`,background:KNOW_COLOR}}/>
          </div>
        )}
      </div>
      {item.is_favorite && <span style={{fontSize:11,color:'#E0A400'}}>★</span>}
      {item.is_pinned && <span style={{fontSize:11,opacity:0.6}}>📌</span>}
    </div>
  );
}

function KnowledgeWidget({ title, icon, count, onViewAll, empty, children }) {
  return (
    <div className="ta-card" style={{padding:16,display:'flex',flexDirection:'column'}}>
      <div className="ta-row ta-between" style={{marginBottom:8}}>
        <div style={{fontSize:12.5,fontWeight:700,display:'flex',alignItems:'center',gap:6,color:'var(--ta-on-surface)'}}>
          <span>{icon}</span>{title}
          {typeof count==='number' && <span style={{fontSize:11,color:'var(--ta-on-surface-muted)',fontWeight:400}}>· {count}</span>}
        </div>
        {onViewAll && <span onClick={onViewAll} style={{fontSize:11,color:KNOW_COLOR,cursor:'pointer',whiteSpace:'nowrap'}}>View all →</span>}
      </div>
      <div style={{height:200,overflowY:'auto'}}>
        {empty ? <div style={{fontSize:12,color:'var(--ta-on-surface-muted)',fontStyle:'italic',padding:'20px 4px',textAlign:'center'}}>{empty}</div> : children}
      </div>
    </div>
  );
}

function KnowledgeSection({ ctx }) {
  const {
    knowledgeItems=[], knowledgeDomains=[], knowledgeCollections=[], knowledgeTags=[], knowledgeItemTags=[],
    updateKnowledgeItem, addKnowledgeDomain, addKnowledgeCollection, deleteKnowledgeDomain, deleteKnowledgeCollection,
    recordKnowledgeItemView,
  } = ctx;
  const [dashTab, setDashTab] = React.useState('overview'); // overview | library
  const [shuffleTick, setShuffleTick] = React.useState(0);
  const [quickFilter, setQuickFilter] = React.useState('all'); // all | favorites | pinned | archived
  const [domainId, setDomainId] = React.useState(null);
  const [collectionId, setCollectionId] = React.useState(null);
  const [tagId, setTagId] = React.useState(null);
  const [typeFilter, setTypeFilter] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState('updated_desc');
  const [editing, setEditing] = React.useState(null); // { } item or null; use editingOpen bool separately
  const [editingOpen, setEditingOpen] = React.useState(false);
  const [newTypeMenu, setNewTypeMenu] = React.useState(false);
  const [addingDomain, setAddingDomain] = React.useState(false);
  const [newDomainName, setNewDomainName] = React.useState('');
  const [addingCollectionFor, setAddingCollectionFor] = React.useState(null);
  const [newCollectionName, setNewCollectionName] = React.useState('');
  const [expandedDomains, setExpandedDomains] = React.useState(new Set());

  const tagsForItem = (itemId) => knowledgeItemTags.filter(x=>x.item_id===itemId).map(x=>knowledgeTags.find(t=>t.id===x.tag_id)).filter(Boolean);

  const openItem = (item) => {
    if (item?.id) recordKnowledgeItemView(item.id);
    setEditing(item); setEditingOpen(true);
  };
  const closeEditor = () => { setEditing(null); setEditingOpen(false); };
  const newItem = (type) => { setNewTypeMenu(false); openItem({ type }); };

  const toggleDomain = (id) => setExpandedDomains(prev=>{ const s=new Set(prev); s.has(id)?s.delete(id):s.add(id); return s; });

  const createDomain = async () => {
    if (!newDomainName.trim()) { setAddingDomain(false); return; }
    await addKnowledgeDomain({ name:newDomainName.trim(), icon:'🧠', sort_order:knowledgeDomains.length });
    setNewDomainName(''); setAddingDomain(false);
  };
  const createCollection = async (domId) => {
    if (!newCollectionName.trim()) { setAddingCollectionFor(null); return; }
    await addKnowledgeCollection({ name:newCollectionName.trim(), icon:'📚', domain_id:domId, sort_order:knowledgeCollections.length });
    setNewCollectionName(''); setAddingCollectionFor(null);
  };

  let filtered = knowledgeItems.filter(it => {
    if (quickFilter==='favorites' && !it.is_favorite) return false;
    if (quickFilter==='pinned' && !it.is_pinned) return false;
    if (quickFilter==='archived') { if (it.status!=='archived') return false; }
    else if (it.status==='archived') return false;
    if (domainId && it.domain_id!==domainId) return false;
    if (collectionId && it.collection_id!==collectionId) return false;
    if (typeFilter && it.type!==typeFilter) return false;
    if (tagId && !knowledgeItemTags.some(x=>x.item_id===it.id && x.tag_id===tagId)) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${it.title||''} ${it.summary||''} ${it.content||''} ${it.author||''} ${it.source_name||''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  filtered = filtered.sort((a,b) => {
    if (sort==='title_asc') return (a.title||'').localeCompare(b.title||'');
    if (sort==='created_desc') return new Date(b.created_at)-new Date(a.created_at);
    if (sort==='viewed_desc') return new Date(b.last_viewed_at||0)-new Date(a.last_viewed_at||0);
    return new Date(b.updated_at||b.created_at)-new Date(a.updated_at||a.created_at);
  });
  // Pinned first within the current filter/sort
  filtered = [...filtered.filter(i=>i.is_pinned), ...filtered.filter(i=>!i.is_pinned)];

  const activeCount = knowledgeItems.filter(i=>i.status!=='archived').length;
  const favCount = knowledgeItems.filter(i=>i.is_favorite && i.status!=='archived').length;
  const pinnedCount = knowledgeItems.filter(i=>i.is_pinned && i.status!=='archived').length;
  const archivedCount = knowledgeItems.filter(i=>i.status==='archived').length;

  const activeItems = knowledgeItems.filter(i=>i.status!=='archived');
  const recentlyAdded = [...activeItems].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).slice(0,6);
  const recentlyViewed = activeItems.filter(i=>i.last_viewed_at).sort((a,b)=>new Date(b.last_viewed_at)-new Date(a.last_viewed_at)).slice(0,6);
  const favoritesList = activeItems.filter(i=>i.is_favorite).sort((a,b)=>new Date(b.updated_at||b.created_at)-new Date(a.updated_at||a.created_at)).slice(0,6);
  const pinnedList = activeItems.filter(i=>i.is_pinned).sort((a,b)=>new Date(b.updated_at||b.created_at)-new Date(a.updated_at||a.created_at)).slice(0,6);
  const continueReading = activeItems.filter(i=>READING_TYPES.has(i.type) && i.reading_status==='in_progress')
    .sort((a,b)=>new Date(b.last_viewed_at||b.updated_at||0)-new Date(a.last_viewed_at||a.updated_at||0)).slice(0,6);
  const randomItem = React.useMemo(() => {
    if (!activeItems.length) return null;
    return activeItems[Math.floor(Math.random()*activeItems.length)];
  }, [shuffleTick, activeItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const QuickBtn = ({id,label,count}) => (
    <div className={'ta-sb-item'+(quickFilter===id?' active':'')} style={{padding:'6px 10px',cursor:'pointer'}}
      onClick={()=>{setQuickFilter(id); setDomainId(null); setCollectionId(null);}}>
      <span className="ta-sb-item-left">{label}</span>
      <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{count}</span>
    </div>
  );

  return (
    <div>
      <div className="ta-row ta-between" style={{alignItems:'flex-end',marginBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Knowledge Library · {activeCount} item{activeCount!==1?'s':''}</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Your <em>second brain</em>.</h1>
        </div>
        <div style={{position:'relative'}}>
          <button onClick={()=>setNewTypeMenu(v=>!v)} className="ta-btn ta-btn-primary">+ New</button>
          {newTypeMenu && (
            <div className="ta-card" style={{position:'absolute',right:0,top:'110%',zIndex:20,padding:6,width:200}}>
              {KNOWLEDGE_TYPES.map(t=>(
                <div key={t.id} onClick={()=>newItem(t.id)} style={{padding:'7px 10px',fontSize:13,cursor:'pointer',borderRadius:6,display:'flex',alignItems:'center',gap:8}}>
                  <span>{t.icon}</span>{t.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="ta-row" style={{gap:8,marginBottom:16,flexWrap:'wrap'}}>
        <button onClick={()=>setDashTab('overview')} className={'ta-btn'+(dashTab==='overview'?' ta-btn-primary':'')} style={{fontSize:12.5}}>Overview</button>
        <button onClick={()=>setDashTab('library')} className={'ta-btn'+(dashTab==='library'?' ta-btn-primary':'')} style={{fontSize:12.5}}>Library</button>
        <input value={query} onChange={e=>{setQuery(e.target.value); if(e.target.value) setDashTab('library');}}
          placeholder="🔎 Search title, summary, content, author…"
          style={{flex:'1 1 260px',fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 12px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
      </div>

      {dashTab==='overview' && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:14}}>
          <KnowledgeWidget title="Continue Reading" icon="📖" onViewAll={()=>setDashTab('library')}
            empty={continueReading.length?null:'Nothing in progress — open a book, article, PDF, or paper and set its progress.'}>
            {continueReading.map(item=><KnowledgeMiniRow key={item.id} item={item} onOpen={()=>openItem(item)}/>)}
          </KnowledgeWidget>

          <div className="ta-card" style={{padding:16,display:'flex',flexDirection:'column'}}>
            <div className="ta-row ta-between" style={{marginBottom:8}}>
              <div style={{fontSize:12.5,fontWeight:700,display:'flex',alignItems:'center',gap:6,color:'var(--ta-on-surface)'}}><span>🎲</span>Random Knowledge</div>
              <span onClick={()=>setShuffleTick(t=>t+1)} style={{fontSize:11,color:KNOW_COLOR,cursor:'pointer'}}>🔀 Shuffle</span>
            </div>
            <div style={{height:200,display:'flex',flexDirection:'column',justifyContent:'center'}}>
              {randomItem ? (
                <div onClick={()=>openItem(randomItem)} style={{cursor:'pointer',display:'flex',flexDirection:'column',gap:6}}>
                  <span style={{fontSize:11,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.04em'}}>{knowTypeMeta(randomItem.type).icon} {knowTypeMeta(randomItem.type).label}</span>
                  <div className="ta-serif" style={{fontSize:18,fontWeight:600,color:'var(--ta-on-surface)'}}>{randomItem.title||'Untitled'}</div>
                  {randomItem.summary && <div style={{fontSize:12.5,color:'var(--ta-on-surface-variant)'}}>{randomItem.summary}</div>}
                </div>
              ) : (
                <div style={{fontSize:12,color:'var(--ta-on-surface-muted)',fontStyle:'italic',textAlign:'center'}}>Add some knowledge to rediscover later.</div>
              )}
            </div>
          </div>

          <KnowledgeWidget title="Recently Added" icon="🆕" onViewAll={()=>{setSort('created_desc');setDashTab('library');}}
            empty={recentlyAdded.length?null:'Nothing added yet.'}>
            {recentlyAdded.map(item=><KnowledgeMiniRow key={item.id} item={item} onOpen={()=>openItem(item)}/>)}
          </KnowledgeWidget>

          <KnowledgeWidget title="Recently Viewed" icon="👁" onViewAll={()=>{setSort('viewed_desc');setDashTab('library');}}
            empty={recentlyViewed.length?null:'Open something to see it here.'}>
            {recentlyViewed.map(item=><KnowledgeMiniRow key={item.id} item={item} onOpen={()=>openItem(item)}/>)}
          </KnowledgeWidget>

          <KnowledgeWidget title="Favorites" icon="★" count={favCount} onViewAll={()=>{setQuickFilter('favorites');setDashTab('library');}}
            empty={favoritesList.length?null:'Star an item to pin it here.'}>
            {favoritesList.map(item=><KnowledgeMiniRow key={item.id} item={item} onOpen={()=>openItem(item)}/>)}
          </KnowledgeWidget>

          <KnowledgeWidget title="Pinned" icon="📌" count={pinnedCount} onViewAll={()=>{setQuickFilter('pinned');setDashTab('library');}}
            empty={pinnedList.length?null:'Pin an item to keep it close.'}>
            {pinnedList.map(item=><KnowledgeMiniRow key={item.id} item={item} onOpen={()=>openItem(item)}/>)}
          </KnowledgeWidget>
        </div>
      )}

      {dashTab==='library' && (
      <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:14,alignItems:'start'}}>
        {/* Sidebar */}
        <div className="ta-card" style={{padding:'10px 8px'}}>
          <QuickBtn id="all" label="All items" count={activeCount}/>
          <QuickBtn id="favorites" label="★ Favorites" count={favCount}/>
          <QuickBtn id="pinned" label="📌 Pinned" count={pinnedCount}/>
          <QuickBtn id="archived" label="🗄 Archive" count={archivedCount}/>

          <div className="ta-row ta-between" style={{padding:'14px 10px 6px'}}>
            <span className="ta-section-head-meta" style={{padding:0}}>Domains</span>
            <span onClick={()=>setAddingDomain(v=>!v)} style={{cursor:'pointer',fontSize:12,color:'var(--ta-on-surface-muted)'}}>+</span>
          </div>
          {addingDomain && (
            <div style={{padding:'0 10px 8px'}}>
              <input autoFocus value={newDomainName} onChange={e=>setNewDomainName(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&createDomain()} onBlur={createDomain}
                placeholder="Domain name…" style={{width:'100%',fontSize:12,border:'1px solid var(--ta-outline-variant)',borderRadius:6,padding:'5px 8px'}}/>
            </div>
          )}
          {knowledgeDomains.map(dom => {
            const cols = knowledgeCollections.filter(c=>c.domain_id===dom.id);
            const open = expandedDomains.has(dom.id);
            return (
              <div key={dom.id}>
                <div className={'ta-sb-item'+(domainId===dom.id&&!collectionId?' active':'')} style={{padding:'6px 10px',cursor:'pointer'}}
                  onClick={()=>{setDomainId(dom.id);setCollectionId(null);setQuickFilter('all');}}>
                  <span className="ta-sb-item-left" style={{gap:6}}>
                    <span onClick={e=>{e.stopPropagation();toggleDomain(dom.id);}} style={{fontSize:9,width:10}}>{cols.length?(open?'▾':'▸'):''}</span>
                    <span>{dom.icon||'🧠'}</span><span>{dom.name}</span>
                  </span>
                  <span onClick={e=>{e.stopPropagation(); if(window.confirm(`Delete domain "${dom.name}"?`)) deleteKnowledgeDomain(dom.id);}}
                    style={{fontSize:10,opacity:0.4,cursor:'pointer'}}>🗑</span>
                </div>
                {open && (
                  <div style={{paddingLeft:20}}>
                    {cols.map(col=>(
                      <div key={col.id} className={'ta-sb-item'+(collectionId===col.id?' active':'')} style={{padding:'5px 10px',cursor:'pointer'}}
                        onClick={()=>{setCollectionId(col.id);setDomainId(dom.id);setQuickFilter('all');}}>
                        <span className="ta-sb-item-left" style={{gap:6,fontSize:12.5}}><span>{col.icon||'📚'}</span>{col.name}</span>
                        <span onClick={e=>{e.stopPropagation(); if(window.confirm(`Delete collection "${col.name}"?`)) deleteKnowledgeCollection(col.id);}}
                          style={{fontSize:10,opacity:0.4,cursor:'pointer'}}>🗑</span>
                      </div>
                    ))}
                    {addingCollectionFor===dom.id ? (
                      <input autoFocus value={newCollectionName} onChange={e=>setNewCollectionName(e.target.value)}
                        onKeyDown={e=>e.key==='Enter'&&createCollection(dom.id)} onBlur={()=>createCollection(dom.id)}
                        placeholder="Collection name…" style={{width:'calc(100% - 20px)',margin:'2px 10px',fontSize:11.5,border:'1px solid var(--ta-outline-variant)',borderRadius:6,padding:'4px 7px'}}/>
                    ) : (
                      <div onClick={()=>setAddingCollectionFor(dom.id)} style={{padding:'4px 10px',fontSize:11.5,color:'var(--ta-on-surface-muted)',cursor:'pointer'}}>+ Collection</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {knowledgeTags.length>0 && (
            <>
              <div className="ta-section-head-meta" style={{padding:'14px 10px 6px'}}>Tags</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:5,padding:'0 10px 8px'}}>
                {knowledgeTags.map(t=>(
                  <span key={t.id} onClick={()=>setTagId(id=>id===t.id?null:t.id)}
                    style={{fontSize:10.5,padding:'3px 8px',borderRadius:10,cursor:'pointer',
                      background:tagId===t.id?(t.color||KNOW_COLOR):(t.color||'#8888AA')+'22',
                      color:tagId===t.id?'#fff':(t.color||'#8888AA')}}>#{t.name}</span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Main */}
        <div>
          <div className="ta-row" style={{gap:8,marginBottom:14,flexWrap:'wrap'}}>
            <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
              style={{fontSize:12.5,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
              <option value="">All types</option>
              {KNOWLEDGE_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
            <select value={sort} onChange={e=>setSort(e.target.value)}
              style={{fontSize:12.5,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
              <option value="updated_desc">Recently updated</option>
              <option value="created_desc">Recently added</option>
              <option value="viewed_desc">Recently viewed</option>
              <option value="title_asc">Title A–Z</option>
            </select>
            {(domainId||collectionId||tagId||typeFilter||query) && (
              <button onClick={()=>{setDomainId(null);setCollectionId(null);setTagId(null);setTypeFilter('');setQuery('');}} className="ta-btn" style={{fontSize:12}}>Clear filters</button>
            )}
          </div>

          {filtered.length===0 ? (
            <div className="ta-card" style={{textAlign:'center',padding:48}}>
              <div className="ta-serif ta-italic" style={{fontSize:18,color:'var(--ta-on-surface-variant)',marginBottom:8}}>Nothing here yet</div>
              <div style={{fontSize:13,color:'var(--ta-on-surface-muted)',marginBottom:16}}>Capture your first piece of knowledge — a note, a book, an article, an idea.</div>
              <button onClick={()=>newItem('note')} className="ta-btn ta-btn-primary">+ New item</button>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:14}}>
              {filtered.map(item=>(
                <KnowledgeCard key={item.id} item={item} tags={tagsForItem(item.id)} onOpen={()=>openItem(item)}
                  onToggleFavorite={()=>updateKnowledgeItem(item.id,{is_favorite:!item.is_favorite})}
                  onTogglePinned={()=>updateKnowledgeItem(item.id,{is_pinned:!item.is_pinned})}/>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {editingOpen && <KnowledgeEditorPanel key={editing?.id||'new'} ctx={ctx} item={editing} onClose={closeEditor} onOpenRelated={openItem}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// REFLECTIONS — simple daily reflection, one page: week strip + calendar,
// mood/energy tracker, and a fixed set of dedicated questions per day.
// ══════════════════════════════════════════════════════════════════════════════
const JOURNAL_COLOR = '#8A5A6B';
const MOOD_EMOJI  = ['😞','😕','😐','🙂','😄'];
const MOOD_COLORS = ['var(--ta-negative)', '#C97A54', 'var(--ta-on-surface-muted)', 'var(--ta-primary)', 'var(--ta-positive)'];

const REFLECTION_QUESTIONS = [
  { key:'affirmation',     label:"Today's Affirmation",  placeholder:'I am…' },
  { key:'intention',       label:"Today's Intention",    placeholder:"What's your intention for today?" },
  { key:'gratitude',       label:'Grateful For',         placeholder:'What are you grateful for right now?' },
  { key:'went_well',       label:'What Went Well',       placeholder:'What went well today?' },
  { key:'lesson_learned',  label:'Lesson Learned',       placeholder:'What did you learn?' },
  { key:'drained_energy',  label:'What Drained You',     placeholder:'What drained your energy?' },
  { key:'avoiding',        label:"What You're Avoiding", placeholder:'What are you avoiding?' },
  { key:'win',             label:"Today's Win",          placeholder:"What's today's win?" },
  { key:'tomorrow_better', label:'Make Tomorrow Better', placeholder:'What would make tomorrow better?' },
];

const reflectionSnippet = (r) => {
  if (!r) return null;
  for (const q of REFLECTION_QUESTIONS) {
    const v = (r[q.key]||'').trim();
    if (v) return v.length>64 ? v.slice(0,64)+'…' : v;
  }
  return null;
};
const dayHeadingShort = (d) => new Date(d+"T00:00:00").toLocaleDateString(undefined,{weekday:'short',day:'numeric'});
const dayHeadingFull  = (d) => new Date(d+"T00:00:00").toLocaleDateString(undefined,{weekday:'long',month:'long',day:'numeric'});
const mondayOf = (d) => { const nd=new Date(d); const dow=(nd.getDay()+6)%7; nd.setDate(nd.getDate()-dow); return nd; };

function ReflectionQuestionTile({ label, placeholder, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value||'');
  const ref = useRef(null);
  useEffect(() => { setDraft(value||''); }, [value]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); const l=ref.current.value.length; ref.current.setSelectionRange(l,l); } }, [editing]);
  const answered = !!(value && value.trim());
  const commit = () => {
    setEditing(false);
    if ((draft||'').trim() !== (value||'').trim()) onSave((draft||'').trim());
  };
  return (
    <div className="ta-card-inset" style={{padding:'12px 14px',cursor:editing?'default':'pointer'}} onClick={()=>{ if(!editing) setEditing(true); }}>
      <div className="ta-row ta-between" style={{marginBottom:(editing||answered)?6:0}}>
        <span style={{fontSize:11,fontWeight:700,color:JOURNAL_COLOR,textTransform:'uppercase',letterSpacing:'.05em'}}>{label}</span>
        {!editing && !answered && <span style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>+</span>}
      </div>
      {editing ? (
        <textarea ref={ref} value={draft} onChange={e=>setDraft(e.target.value)} onBlur={commit} onClick={e=>e.stopPropagation()}
          placeholder={placeholder} rows={3}
          style={{width:'100%',border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',fontSize:13.5,fontFamily:'inherit',resize:'vertical',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)',boxSizing:'border-box'}}/>
      ) : answered ? (
        <div style={{fontSize:13.5,color:'var(--ta-on-surface-variant)',whiteSpace:'pre-wrap',lineHeight:1.5}}>{value}</div>
      ) : (
        <div style={{fontSize:12.5,color:'var(--ta-on-surface-muted)',fontStyle:'italic'}}>{placeholder}</div>
      )}
    </div>
  );
}

function JournalCalendarGrid({ calMonth, setCalMonth, byDate, selectedDate, onSelect }) {
  const year = calMonth.getFullYear(), month = calMonth.getMonth();
  const startPad = (new Date(year, month, 1).getDay()+6)%7;
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  const monthLabel = calMonth.toLocaleDateString(undefined,{month:'long',year:'numeric'});
  const goMonth = (delta) => setCalMonth(m => { const nd=new Date(m); nd.setMonth(nd.getMonth()+delta); return nd; });
  return (
    <div className="ta-card" style={{padding:12}}>
      <div className="ta-row ta-between" style={{marginBottom:8,alignItems:'center'}}>
        <span onClick={()=>goMonth(-1)} style={{cursor:'pointer',fontSize:13,padding:'2px 8px'}}>‹</span>
        <span style={{fontSize:12,fontWeight:600,color:'var(--ta-on-surface)'}}>{monthLabel}</span>
        <span onClick={()=>goMonth(1)} style={{cursor:'pointer',fontSize:13,padding:'2px 8px'}}>›</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,fontSize:9,color:'var(--ta-on-surface-muted)',marginBottom:4,textAlign:'center'}}>
        {['M','T','W','T','F','S','S'].map((d,i)=><span key={i}>{d}</span>)}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
        {cells.map((d,i) => {
          if (!d) return <span key={i}/>;
          const dateStr = fmt(new Date(year,month,d));
          const r = byDate[dateStr];
          const isSel = dateStr===selectedDate, isToday = dateStr===TODAY, isFuture = dateStr>TODAY;
          return (
            <div key={i} onClick={()=>!isFuture && onSelect(dateStr)}
              style={{aspectRatio:'1',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:1,
                borderRadius:6,cursor:isFuture?'default':'pointer',opacity:isFuture?0.3:1,
                background:isSel?JOURNAL_COLOR:'transparent',color:isSel?'#fff':'var(--ta-on-surface)',
                border:isToday&&!isSel?`1px solid ${JOURNAL_COLOR}`:'1px solid transparent'}}>
              <span style={{fontSize:11,fontWeight:isToday?700:400}}>{d}</span>
              {r?.mood_score ? <span style={{fontSize:7,lineHeight:1}}>{MOOD_EMOJI[r.mood_score-1]}</span>
                : r ? <span style={{width:4,height:4,borderRadius:2,background:isSel?'#fff':JOURNAL_COLOR}}/> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JournalSection({ ctx }) {
  const { journalEntries=[], addJournalEntry, updateJournalEntry } = ctx;

  const reflections = journalEntries.filter(e=>e.type==='daily_reflection');
  const byDate = {};
  reflections.forEach(r => { byDate[r.entry_date] = r; });

  const [weekOffset, setWeekOffset] = useState(0);
  const [leftMode, setLeftMode] = useState('week'); // week | calendar
  const [calMonth, setCalMonth] = useState(() => new Date(TODAY+"T00:00:00"));
  const [selectedDate, setSelectedDate] = useState(TODAY);

  const weekStart = (() => { const d = mondayOf(new Date(TODAY+"T00:00:00")); d.setDate(d.getDate()+weekOffset*7); return d; })();
  const weekDates = Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(d.getDate()+i); return fmt(d); });
  const weekLabel = `${new Date(weekDates[0]+"T00:00:00").toLocaleDateString(undefined,{month:'short',day:'numeric'})} – ${new Date(weekDates[6]+"T00:00:00").toLocaleDateString(undefined,{month:'short',day:'numeric'})}`;

  const last14 = Array.from({length:14},(_,i)=>{ const d=new Date(TODAY+"T00:00:00"); d.setDate(d.getDate()-(13-i)); return fmt(d); });

  const ensureReflection = async (dateStr) => {
    const existing = byDate[dateStr];
    if (existing) return existing;
    return await addJournalEntry({ type:'daily_reflection', entry_date: dateStr, content:'' });
  };
  const saveField = async (dateStr, field, value) => {
    const row = await ensureReflection(dateStr);
    if (row) updateJournalEntry(row.id, { [field]: value });
  };
  const setMood   = async (dateStr, score) => { const row = await ensureReflection(dateStr); if (row) updateJournalEntry(row.id, { mood_score: score }); };
  const setEnergy = async (dateStr, score) => { const row = await ensureReflection(dateStr); if (row) updateJournalEntry(row.id, { energy_score: score }); };

  const selected = byDate[selectedDate] || null;
  const selHeaderLabel = selectedDate===TODAY ? "Today's" : dayHeadingShort(selectedDate);

  return (
    <div>
      <div className="ta-row ta-between" style={{alignItems:'flex-start',marginBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Personal · {dayHeadingFull(TODAY)}</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Notice what's <em>true</em>.</h1>
        </div>

        <div className="ta-card" style={{padding:'12px 14px',minWidth:230}}>
          <div style={{fontSize:10.5,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>{selHeaderLabel} mood &amp; energy</div>
          <div className="ta-row ta-between" style={{marginBottom:8,alignItems:'center'}}>
            <span style={{fontSize:11,color:'var(--ta-on-surface-muted)',width:46}}>Mood</span>
            <div style={{display:'flex',gap:4}}>
              {[1,2,3,4,5].map(n=>(
                <span key={n} onClick={()=>setMood(selectedDate,n)}
                  style={{cursor:'pointer',fontSize:16,opacity:(selected?.mood_score||0)===n?1:0.35,transform:(selected?.mood_score||0)===n?'scale(1.25)':'none',transition:'transform .1s'}}>{MOOD_EMOJI[n-1]}</span>
              ))}
            </div>
          </div>
          <div className="ta-row ta-between" style={{marginBottom:10,alignItems:'center'}}>
            <span style={{fontSize:11,color:'var(--ta-on-surface-muted)',width:46}}>Energy</span>
            <div style={{display:'flex',gap:4}}>
              {[1,2,3,4,5].map(n=>(
                <span key={n} onClick={()=>setEnergy(selectedDate,n)}
                  style={{cursor:'pointer',width:13,height:13,borderRadius:4,background:(selected?.energy_score||0)>=n?JOURNAL_COLOR:'var(--ta-outline-variant)'}}/>
              ))}
            </div>
          </div>
          <div style={{display:'flex',gap:2,alignItems:'flex-end',height:26}}>
            {last14.map(d => {
              const r = byDate[d]; const score = r?.mood_score||0;
              return <div key={d} title={dayHeadingShort(d)} style={{flex:1,height:score?`${score/5*100}%`:'8%',borderRadius:2,
                background:score?MOOD_COLORS[score-1]:'var(--ta-outline-variant)',opacity:d===selectedDate?1:0.55}}/>;
            })}
          </div>
        </div>
      </div>

      <div className="ta-card" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',marginBottom:16}}>
        <button className="ta-btn" onClick={()=>setWeekOffset(o=>o-1)}>‹ Prev</button>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)'}}>Week of {weekLabel}</div>
          {weekOffset!==0 && <span onClick={()=>setWeekOffset(0)} style={{fontSize:11,color:JOURNAL_COLOR,cursor:'pointer'}}>Back to this week</span>}
        </div>
        <button className="ta-btn" onClick={()=>setWeekOffset(o=>o+1)} disabled={weekOffset>=0}>Next ›</button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:16,alignItems:'start'}}>
        <div className="ta-col ta-gap-2">
          <div className="ta-row" style={{gap:6}}>
            <button className={'ta-btn'+(leftMode==='week'?' ta-btn-primary':'')} style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setLeftMode('week')}>Week</button>
            <button className={'ta-btn'+(leftMode==='calendar'?' ta-btn-primary':'')} style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setLeftMode('calendar')}>Journal Calendar</button>
          </div>

          {leftMode==='week' ? (
            <div className="ta-col" style={{gap:4}}>
              {weekDates.map(d => {
                const r = byDate[d];
                const isSel = d===selectedDate, isToday = d===TODAY, isFuture = d>TODAY;
                const snippet = reflectionSnippet(r);
                return (
                  <div key={d} onClick={()=>{ if(!isFuture) setSelectedDate(d); }} className="ta-lift"
                    style={{padding:'10px 12px',borderRadius:'var(--r-md)',cursor:isFuture?'default':'pointer',opacity:isFuture?0.4:1,
                      background:isSel?'var(--ta-surface-low)':'transparent',border:isSel?`1px solid ${JOURNAL_COLOR}55`:'1px solid transparent'}}>
                    <div className="ta-row ta-between" style={{marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:isToday?700:600,color:'var(--ta-on-surface)'}}>{dayHeadingShort(d)}</span>
                      <span style={{display:'flex',gap:5,alignItems:'center'}}>
                        {r?.mood_score ? <span style={{fontSize:13}}>{MOOD_EMOJI[r.mood_score-1]}</span> : null}
                        {r?.energy_score ? <span style={{fontSize:9,color:JOURNAL_COLOR,fontWeight:700}}>⚡{r.energy_score}</span> : null}
                      </span>
                    </div>
                    <div style={{fontSize:11.5,color:'var(--ta-on-surface-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {snippet || (isFuture ? '—' : 'No reflection yet')}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <JournalCalendarGrid calMonth={calMonth} setCalMonth={setCalMonth} byDate={byDate} selectedDate={selectedDate} onSelect={setSelectedDate}/>
          )}
        </div>

        <div className="ta-card" style={{padding:20}}>
          <div className="ta-row ta-between" style={{alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:10}}>
            <div>
              <div className="ta-eh-eyebrow" style={{marginBottom:2}}>{selectedDate===TODAY ? 'Today' : ''}</div>
              <div className="ta-serif" style={{fontSize:22,fontWeight:600,color:'var(--ta-on-surface)'}}>{dayHeadingFull(selectedDate)}</div>
            </div>
            {selectedDate!==TODAY && <button className="ta-btn" style={{fontSize:11}} onClick={()=>setSelectedDate(TODAY)}>Jump to today</button>}
          </div>

          <div className="ta-col ta-gap-2">
            {REFLECTION_QUESTIONS.map(q => (
              <ReflectionQuestionTile key={selectedDate+q.key} label={q.label} placeholder={q.placeholder}
                value={selected?.[q.key]||''} onSave={(val)=>saveField(selectedDate,q.key,val)}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FITNESS & DIET — new module, Tactile Archive design, real Supabase tables
// ══════════════════════════════════════════════════════════════════════════════
const DIET_COLOR = '#1F7A5C';
const MEAL_TYPES = [
  { id:'breakfast',          label:'Breakfast',          icon:'🌅' },
  { id:'mid_morning_snack',  label:'Mid-Morning Snack',  icon:'🍎' },
  { id:'lunch',              label:'Lunch',              icon:'🍛' },
  { id:'evening_snack',      label:'Evening Snack',      icon:'☕' },
  { id:'dinner',             label:'Dinner',             icon:'🌙' },
  { id:'pre_workout',        label:'Pre-Workout',        icon:'🏋️' },
  { id:'post_workout',       label:'Post-Workout',       icon:'💪' },
  { id:'other',              label:'Other',              icon:'🍽️' },
];
const mealTypeMeta = (t) => MEAL_TYPES.find(m=>m.id===t) || MEAL_TYPES[MEAL_TYPES.length-1];
const FOOD_CATEGORIES = [
  { id:'grains',      label:'Grains & Breads' }, { id:'dal',    label:'Dal & Legumes' },
  { id:'sabzi',       label:'Sabzi' },            { id:'nonveg', label:'Non-Veg' },
  { id:'dairy',       label:'Dairy' },            { id:'rice',   label:'Rice Dishes' },
  { id:'snack',       label:'Snacks' },           { id:'sweet',  label:'Sweets' },
  { id:'beverage',    label:'Beverages' },        { id:'fruit',  label:'Fruits' },
  { id:'nuts',        label:'Nuts & Seeds' },     { id:'fastfood', label:'Fast Food' },
  { id:'supplement',  label:'Supplements' },      { id:'other',  label:'Other' },
];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function FoodPicker({ foods, onSelect, placeholder }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const matches = query.trim() ? foods.filter(f=>f.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0,8) : [];
  return (
    <div style={{position:'relative'}}>
      <input value={query} onChange={e=>{setQuery(e.target.value); setOpen(true);}} onFocus={()=>setOpen(true)}
        placeholder={placeholder||'Search food…'} className="ta-input" style={{width:'100%'}}/>
      {open && matches.length>0 && (
        <div className="ta-card" style={{position:'absolute',top:'100%',left:0,right:0,zIndex:10,padding:4,marginTop:4,maxHeight:220,overflowY:'auto'}}>
          {matches.map(f=>(
            <div key={f.id} onClick={()=>{onSelect(f); setQuery(''); setOpen(false);}}
              style={{padding:'7px 10px',fontSize:12.5,cursor:'pointer',borderRadius:6,display:'flex',justifyContent:'space-between',gap:8}}>
              <span>{f.name}</span>
              <span style={{color:'var(--ta-on-surface-muted)',fontSize:11,whiteSpace:'nowrap'}}>{f.serving_unit} · {Math.round(f.calories)} kcal</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RingGauge({ pct, color, size=64, trackColor='var(--ta-surface-high)' }) {
  const r = size/2 - 4.5;
  const circumference = 2*Math.PI*r;
  const dash = Math.max(0, Math.min(100, pct))/100 * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={trackColor} strokeWidth={5}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5} strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`} transform={`rotate(-90 ${size/2} ${size/2})`} style={{transition:'stroke-dasharray .5s ease'}}/>
    </svg>
  );
}

function NutritionTodayTab({ ctx }) {
  const {
    dietFoods=[], dietMealLogs=[], addDietMealLog, deleteDietMealLog,
    dietWaterLogs=[], addDietWaterLog, dietSupplements=[], dietSupplementLogs=[], toggleDietSupplementLog,
    dietTargets, updateDietTargets, dietMealPlan=[],
  } = ctx;
  const targets = dietTargets || { calorie_target:2000, protein_target:120, carbs_target:220, fat_target:65, fiber_target:30, water_target_ml:3000 };
  const todayLogs = dietMealLogs.filter(l=>l.log_date===TODAY);
  const totals = todayLogs.reduce((acc,l)=>({
    calories: acc.calories+Number(l.calories||0), protein: acc.protein+Number(l.protein_g||0),
    carbs: acc.carbs+Number(l.carbs_g||0), fat: acc.fat+Number(l.fat_g||0), fiber: acc.fiber+Number(l.fiber_g||0),
  }), {calories:0,protein:0,carbs:0,fat:0,fiber:0});
  const todayWaterMl = dietWaterLogs.filter(w=>w.log_date===TODAY).reduce((s,w)=>s+Number(w.amount_ml||0),0);
  const todayDow = new Date(TODAY+"T00:00:00").getDay();
  const plannedToday = dietMealPlan.filter(p=>p.day_of_week===null||p.day_of_week===todayDow);
  const activeSupplements = dietSupplements.filter(s=>s.active);
  const takenToday = new Set(dietSupplementLogs.filter(l=>l.log_date===TODAY).map(l=>l.supplement_id));

  const [selectedFood, setSelectedFood] = React.useState(null);
  const [quantity, setQuantity] = React.useState(1);
  const [mealType, setMealType] = React.useState('breakfast');
  const [customMode, setCustomMode] = React.useState(false);
  const [customForm, setCustomForm] = React.useState({ food_name:'', calories:'', protein_g:'', carbs_g:'', fat_g:'' });

  const logSelectedFood = () => {
    if (!selectedFood) return;
    const q = Number(quantity)||1;
    addDietMealLog({
      log_date: TODAY, meal_type: mealType, food_id: selectedFood.id, food_name: selectedFood.name, quantity: q,
      calories: selectedFood.calories*q, protein_g: selectedFood.protein_g*q, carbs_g: selectedFood.carbs_g*q,
      fat_g: selectedFood.fat_g*q, fiber_g: (selectedFood.fiber_g||0)*q,
    });
    setSelectedFood(null); setQuantity(1);
  };
  const logCustom = () => {
    if (!customForm.food_name.trim()) return;
    addDietMealLog({
      log_date: TODAY, meal_type: mealType, food_id: null, food_name: customForm.food_name.trim(), quantity: 1,
      calories: Number(customForm.calories)||0, protein_g: Number(customForm.protein_g)||0,
      carbs_g: Number(customForm.carbs_g)||0, fat_g: Number(customForm.fat_g)||0, fiber_g: 0,
    });
    setCustomForm({ food_name:'', calories:'', protein_g:'', carbs_g:'', fat_g:'' });
  };
  const logPlannedItem = (item) => {
    const food = dietFoods.find(f=>f.id===item.food_id);
    if (!food) return;
    addDietMealLog({
      log_date: TODAY, meal_type: item.meal_type, food_id: food.id, food_name: food.name, quantity: item.quantity,
      calories: food.calories*item.quantity, protein_g: food.protein_g*item.quantity, carbs_g: food.carbs_g*item.quantity,
      fat_g: food.fat_g*item.quantity, fiber_g: (food.fiber_g||0)*item.quantity,
    });
  };

  const macroRow = (label, val, target, tone) => (
    <div style={{marginBottom:12}}>
      <div className="ta-row ta-between" style={{marginBottom:5}}>
        <span className="ta-mono" style={{fontSize:10,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--ta-on-surface-muted)'}}>{label}</span>
        <span className="ta-mono tnum" style={{fontSize:11}}>{Math.round(val)} / {target}{label==="Calories"?"":"g"}</span>
      </div>
      <div className="ta-bar"><div className="ta-bar-fill" style={{width:`${Math.min(100,(val/target)*100)}%`,background:tone}}/></div>
    </div>
  );

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20,alignItems:'start'}}>
      <div className="ta-col ta-gap-4">
        {plannedToday.length>0 && (
          <div className="ta-card">
            <div className="ta-section-head"><h2 className="ta-section-head-h">Today's plan</h2><span className="ta-section-head-meta">{plannedToday.length} planned</span></div>
            <div className="ta-col ta-gap-3">
              {MEAL_TYPES.map(mt=>{
                const items = plannedToday.filter(item=>item.meal_type===mt.id);
                if (!items.length) return null;
                return (
                  <div key={mt.id} style={{background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',padding:'10px 12px',borderLeft:'2px solid var(--ta-primary)'}}>
                    <div className="ta-mono" style={{fontSize:10,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--ta-on-surface-muted)',marginBottom:6}}>{mt.icon} {mt.label}</div>
                    <div className="ta-col ta-gap-1">
                      {items.map(item=>{
                        const food = dietFoods.find(f=>f.id===item.food_id);
                        const alreadyLogged = todayLogs.some(l=>l.food_id===item.food_id && l.meal_type===item.meal_type);
                        return (
                          <div key={item.id} className="ta-row ta-between">
                            <div>
                              <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{food?.name||'Unknown food'}</span>
                              {item.quantity!==1 && <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginLeft:6}}>×{item.quantity}</span>}
                            </div>
                            <button onClick={()=>logPlannedItem(item)} disabled={alreadyLogged} className="ta-btn ta-btn-secondary" style={{fontSize:11,padding:'4px 12px',opacity:alreadyLogged?0.5:1}}>{alreadyLogged?'✓ Logged':'Log'}</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Log a meal</h2>
            <span onClick={()=>setCustomMode(v=>!v)} className="ta-section-head-meta" style={{cursor:'pointer',color:DIET_COLOR}}>{customMode?'Search library instead':'Enter custom food'}</span>
          </div>
          <div className="ta-row ta-gap-2" style={{flexWrap:'wrap',marginBottom:10}}>
            {MEAL_TYPES.map(t=>(
              <button key={t.id} onClick={()=>setMealType(t.id)} className="ta-chip" style={{cursor:'pointer',border:'none',background:mealType===t.id?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:mealType===t.id?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{t.icon} {t.label}</button>
            ))}
          </div>
          {!customMode ? (
            <div style={{display:'grid',gridTemplateColumns:'3fr 1fr auto',gap:8,alignItems:'start'}}>
              <FoodPicker foods={dietFoods} onSelect={setSelectedFood} placeholder="Search 130+ Indian foods…"/>
              <input type="number" min={0.25} step={0.25} value={quantity} onChange={e=>setQuantity(e.target.value)} className="ta-input" placeholder="Qty"/>
              <button onClick={logSelectedFood} disabled={!selectedFood} className="ta-btn ta-btn-primary" style={{opacity:selectedFood?1:0.5}}>+ Add</button>
              {selectedFood && (
                <div style={{gridColumn:'1/-1',fontSize:11,color:'var(--ta-on-surface-muted)'}}>
                  {selectedFood.name} · {selectedFood.serving_unit} · {Math.round(selectedFood.calories*quantity)} kcal · P{Math.round(selectedFood.protein_g*quantity)}g · C{Math.round(selectedFood.carbs_g*quantity)}g · F{Math.round(selectedFood.fat_g*quantity)}g
                </div>
              )}
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr',gap:8}}>
              <input placeholder="Food name" value={customForm.food_name} onChange={e=>setCustomForm(f=>({...f,food_name:e.target.value}))} className="ta-input"/>
              <input placeholder="Kcal" type="number" value={customForm.calories} onChange={e=>setCustomForm(f=>({...f,calories:e.target.value}))} className="ta-input"/>
              <input placeholder="Protein g" type="number" value={customForm.protein_g} onChange={e=>setCustomForm(f=>({...f,protein_g:e.target.value}))} className="ta-input"/>
              <input placeholder="Carbs g" type="number" value={customForm.carbs_g} onChange={e=>setCustomForm(f=>({...f,carbs_g:e.target.value}))} className="ta-input"/>
              <input placeholder="Fat g" type="number" value={customForm.fat_g} onChange={e=>setCustomForm(f=>({...f,fat_g:e.target.value}))} className="ta-input"/>
              <button onClick={logCustom} className="ta-btn ta-btn-primary" style={{gridColumn:'1/-1'}}>+ Add custom meal</button>
            </div>
          )}
        </div>

        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Today's meals</h2><span className="ta-section-head-meta">{todayLogs.length} logged</span></div>
          {todayLogs.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>Nothing logged yet today.</div>}
          <div className="ta-col ta-gap-3">
            {MEAL_TYPES.map(mt=>{
              const items = todayLogs.filter(l=>l.meal_type===mt.id);
              if (!items.length) return null;
              const subtotal = items.reduce((s,l)=>s+Number(l.calories||0),0);
              return (
                <div key={mt.id} style={{background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',padding:'10px 12px'}}>
                  <div className="ta-row ta-between" style={{marginBottom:6}}>
                    <span className="ta-mono" style={{fontSize:10,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--ta-on-surface-muted)'}}>{mt.icon} {mt.label}</span>
                    <span className="ta-mono tnum" style={{fontSize:10,color:'var(--ta-on-surface-muted)'}}>{Math.round(subtotal)} kcal</span>
                  </div>
                  <div className="ta-col ta-gap-1">
                    {items.map(m=>(
                      <div key={m.id} className="ta-row ta-between">
                        <div>
                          <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{m.food_name}</span>
                          {m.quantity!==1 && <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginLeft:6}}>×{m.quantity}</span>}
                        </div>
                        <div className="ta-row ta-gap-2">
                          <span className="ta-mono tnum" style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{Math.round(m.calories)} kcal</span>
                          <button onClick={()=>deleteDietMealLog(m.id)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:20,height:20}}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {activeSupplements.length>0 && (
          <div className="ta-card">
            <div className="ta-section-head"><h2 className="ta-section-head-h">Supplements</h2></div>
            <div className="ta-col ta-gap-2">
              {activeSupplements.map(s=>(
                <label key={s.id} className="ta-row ta-gap-2" style={{cursor:'pointer',alignItems:'center'}}>
                  <input type="checkbox" checked={takenToday.has(s.id)} onChange={()=>toggleDietSupplementLog(s.id, TODAY)}/>
                  <span style={{fontSize:13,color:'var(--ta-on-surface)',textDecoration:takenToday.has(s.id)?'line-through':'none'}}>{s.name}</span>
                  {s.dosage && <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)'}}>{s.dosage}</span>}
                  {s.timing && <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)'}}>· {s.timing}</span>}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="ta-col ta-gap-4">
        <div className="ta-card-prominent">
          <div className="ta-eh-eyebrow">Today's plate</div>
          <div style={{marginTop:10,marginBottom:4}}>
            <div className="ta-serif tnum" style={{fontSize:48,fontWeight:300,color:DIET_COLOR,lineHeight:1}}>{Math.round(totals.calories)}</div>
            <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginTop:4}}>of {targets.calorie_target} kcal target</div>
          </div>
          <div className="ta-row" style={{gap:18,marginTop:18,flexWrap:'wrap',justifyContent:'space-between'}}>
            {[
              { label:'Protein', val:totals.protein, target:targets.protein_target, color:'var(--ta-primary)' },
              { label:'Carbs',   val:totals.carbs,   target:targets.carbs_target,   color:'var(--ta-tertiary)' },
              { label:'Fat',     val:totals.fat,     target:targets.fat_target,     color:'var(--ta-positive)' },
              { label:'Water',   val:todayWaterMl,   target:targets.water_target_ml, color:'#2196C4' },
            ].map(m=>{
              const pct = m.target ? Math.round((m.val/m.target)*100) : 0;
              return (
                <div key={m.label} style={{textAlign:'center'}}>
                  <div style={{position:'relative',width:64,height:64}}>
                    <RingGauge pct={pct} color={m.color}/>
                    <div className="ta-mono tnum" style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'var(--ta-on-surface)'}}>{pct}%</div>
                  </div>
                  <div className="ta-mono" style={{fontSize:9,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--ta-on-surface-muted)',marginTop:6}}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ta-card-inset">
          {macroRow("Fiber", totals.fiber, targets.fiber_target, "var(--ta-primary)")}
          <div className="ta-row ta-gap-2" style={{marginTop:4}}>
            {[250,500,1000].map(ml=>(
              <button key={ml} onClick={()=>addDietWaterLog(ml, TODAY)} className="ta-btn ta-btn-secondary" style={{fontSize:11,flex:1,justifyContent:'center'}}>+{ml}ml water</button>
            ))}
          </div>
          <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid var(--ta-outline-variant)'}}>
            <div className="ta-section-head-meta" style={{marginBottom:8}}>Adjust targets</div>
            <div className="ta-col ta-gap-2">
              {[["calorie_target","Calories"],["protein_target","Protein g"],["carbs_target","Carbs g"],["fat_target","Fat g"],["fiber_target","Fiber g"],["water_target_ml","Water ml"]].map(([k,l])=>(
                <div key={k} className="ta-row ta-between ta-gap-2">
                  <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{l}</span>
                  <input type="number" defaultValue={targets[k]} onBlur={e=>updateDietTargets({[k]:Number(e.target.value)||0})} className="ta-input" style={{width:80,padding:'4px 8px'}}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FoodLibraryTab({ ctx }) {
  const { dietFoods=[], addDietFood, updateDietFood, deleteDietFood } = ctx;
  const [query, setQuery] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [showAdd, setShowAdd] = React.useState(false);
  const blank = { name:'', category:'other', serving_unit:'1 serving', calories:'', protein_g:'', carbs_g:'', fat_g:'', fiber_g:'' };
  const [form, setForm] = React.useState(blank);

  const filtered = dietFoods.filter(f => {
    if (category && f.category!==category) return false;
    if (query && !f.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }).sort((a,b)=>a.name.localeCompare(b.name));

  const submit = async () => {
    if (!form.name.trim()) return;
    await addDietFood({
      name: form.name.trim(), category: form.category, serving_unit: form.serving_unit||'1 serving',
      calories: Number(form.calories)||0, protein_g: Number(form.protein_g)||0, carbs_g: Number(form.carbs_g)||0,
      fat_g: Number(form.fat_g)||0, fiber_g: Number(form.fiber_g)||0,
    });
    setForm(blank); setShowAdd(false);
  };

  return (
    <div>
      <div className="ta-row" style={{gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search foods…"
          style={{flex:'1 1 220px',fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 12px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
        <select value={category} onChange={e=>setCategory(e.target.value)}
          style={{fontSize:12.5,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
          <option value="">All categories</option>
          {FOOD_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button onClick={()=>setShowAdd(v=>!v)} className="ta-btn ta-btn-primary">+ Add custom food</button>
      </div>

      {showAdd && (
        <div className="ta-card" style={{marginBottom:14}}>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:8,marginBottom:8}}>
            <input placeholder="Food name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className="ta-input"/>
            <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="ta-input">
              {FOOD_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <input placeholder="Serving unit (e.g. 1 bowl)" value={form.serving_unit} onChange={e=>setForm(f=>({...f,serving_unit:e.target.value}))} className="ta-input"/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:10}}>
            <input placeholder="Calories" type="number" value={form.calories} onChange={e=>setForm(f=>({...f,calories:e.target.value}))} className="ta-input"/>
            <input placeholder="Protein g" type="number" value={form.protein_g} onChange={e=>setForm(f=>({...f,protein_g:e.target.value}))} className="ta-input"/>
            <input placeholder="Carbs g" type="number" value={form.carbs_g} onChange={e=>setForm(f=>({...f,carbs_g:e.target.value}))} className="ta-input"/>
            <input placeholder="Fat g" type="number" value={form.fat_g} onChange={e=>setForm(f=>({...f,fat_g:e.target.value}))} className="ta-input"/>
          </div>
          <button onClick={submit} className="ta-btn ta-btn-primary">Save food</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
        {filtered.map(f=>(
          <div key={f.id} className="ta-card" style={{padding:14}}>
            <div className="ta-row ta-between" style={{marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)'}}>{f.name}</span>
              {!f.is_global && <span onClick={()=>{if(window.confirm(`Delete "${f.name}"?`))deleteDietFood(f.id);}} style={{fontSize:11,opacity:0.5,cursor:'pointer'}}>🗑</span>}
            </div>
            <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:6}}>{f.serving_unit}{f.region&&f.region!=='pan-indian'?` · ${f.region}`:''}</div>
            <div className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-variant)'}}>{Math.round(f.calories)} kcal · P{Math.round(f.protein_g)} C{Math.round(f.carbs_g)} F{Math.round(f.fat_g)}</div>
          </div>
        ))}
        {filtered.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)',gridColumn:'1/-1',textAlign:'center',padding:40}}>No foods match.</div>}
      </div>
    </div>
  );
}

function MealPlanTab({ ctx }) {
  const { dietFoods=[], dietMealPlan=[], addDietMealPlanItem, deleteDietMealPlanItem } = ctx;
  const [selectedDay, setSelectedDay] = React.useState(null); // null = "Every day"
  const [mealType, setMealType] = React.useState('breakfast');
  const [selectedFood, setSelectedFood] = React.useState(null);
  const [quantity, setQuantity] = React.useState(1);

  const dayItems = dietMealPlan.filter(p=>p.day_of_week===selectedDay).sort((a,b)=>a.sort_order-b.sort_order);

  const addItem = async () => {
    if (!selectedFood) return;
    await addDietMealPlanItem({ day_of_week: selectedDay, meal_type: mealType, food_id: selectedFood.id, quantity: Number(quantity)||1, sort_order: dayItems.length });
    setSelectedFood(null); setQuantity(1);
  };

  return (
    <div>
      <div className="ta-card" style={{marginBottom:14,color:'var(--ta-on-surface-variant)',fontSize:13}}>
        Set this up once — plan what you eat on each day of the week, then use "Today's plan" on the Today tab to log it with one tap.
      </div>
      <div className="ta-row ta-gap-2" style={{flexWrap:'wrap',marginBottom:16}}>
        <button onClick={()=>setSelectedDay(null)} className="ta-chip" style={{cursor:'pointer',border:'none',background:selectedDay===null?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:selectedDay===null?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>Every day</button>
        {DAY_NAMES.map((d,i)=>(
          <button key={i} onClick={()=>setSelectedDay(i)} className="ta-chip" style={{cursor:'pointer',border:'none',background:selectedDay===i?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:selectedDay===i?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{d}</button>
        ))}
      </div>

      <div className="ta-card" style={{marginBottom:14}}>
        <div className="ta-row ta-gap-2" style={{flexWrap:'wrap',marginBottom:10}}>
          {MEAL_TYPES.map(t=>(
            <button key={t.id} onClick={()=>setMealType(t.id)} className="ta-chip" style={{cursor:'pointer',border:'none',background:mealType===t.id?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:mealType===t.id?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{t.icon} {t.label}</button>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'3fr 1fr auto',gap:8}}>
          <FoodPicker foods={dietFoods} onSelect={setSelectedFood} placeholder="Search food to plan…"/>
          <input type="number" min={0.25} step={0.25} value={quantity} onChange={e=>setQuantity(e.target.value)} className="ta-input" placeholder="Qty"/>
          <button onClick={addItem} disabled={!selectedFood} className="ta-btn ta-btn-primary" style={{opacity:selectedFood?1:0.5}}>+ Add to plan</button>
        </div>
      </div>

      <div className="ta-card">
        <div className="ta-section-head"><h2 className="ta-section-head-h">{selectedDay===null?'Every day':DAY_NAMES[selectedDay]} plan</h2><span className="ta-section-head-meta">{dayItems.length} items</span></div>
        {dayItems.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>Nothing planned yet.</div>}
        <div className="ta-col ta-gap-2">
          {dayItems.map(item=>{
            const food = dietFoods.find(f=>f.id===item.food_id);
            return (
              <div key={item.id} className="ta-row ta-between" style={{padding:'8px 12px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)'}}>
                <div>
                  <span className="ta-chip" style={{marginRight:8}}>{mealTypeMeta(item.meal_type).icon} {mealTypeMeta(item.meal_type).label}</span>
                  <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{food?.name||'Unknown food'}</span>
                  {item.quantity!==1 && <span className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginLeft:6}}>×{item.quantity}</span>}
                </div>
                <button onClick={()=>deleteDietMealPlanItem(item.id)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:24,height:24}}>✕</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NutritionHistoryTab({ ctx }) {
  const { dietMealLogs=[] } = ctx;
  const byDate = {};
  dietMealLogs.forEach(l => { (byDate[l.log_date] ||= []).push(l); });
  const dates = Object.keys(byDate).sort((a,b)=>b.localeCompare(a)).slice(0,14);

  return (
    <div className="ta-col ta-gap-3">
      {dates.length===0 && <div className="ta-card" style={{textAlign:'center',padding:40}}><div className="ta-serif ta-italic" style={{fontSize:16,color:'var(--ta-on-surface-muted)'}}>No history yet — log a meal to start building your nutrition history.</div></div>}
      {dates.map(date=>{
        const logs = byDate[date];
        const totals = logs.reduce((acc,l)=>({calories:acc.calories+Number(l.calories||0),protein:acc.protein+Number(l.protein_g||0),carbs:acc.carbs+Number(l.carbs_g||0),fat:acc.fat+Number(l.fat_g||0)}),{calories:0,protein:0,carbs:0,fat:0});
        return (
          <div key={date} className="ta-card">
            <div className="ta-section-head">
              <h2 className="ta-section-head-h">{new Date(date+"T00:00:00").toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'})}</h2>
              <span className="ta-mono ta-section-head-meta">{Math.round(totals.calories)} kcal · P{Math.round(totals.protein)} C{Math.round(totals.carbs)} F{Math.round(totals.fat)}</span>
            </div>
            <div className="ta-col ta-gap-1">
              {logs.map(l=>(
                <div key={l.id} className="ta-row ta-between" style={{fontSize:12,padding:'4px 0',color:'var(--ta-on-surface-variant)'}}>
                  <span>{mealTypeMeta(l.meal_type).icon} {l.food_name}{l.quantity!==1?` ×${l.quantity}`:''}</span>
                  <span className="ta-mono" style={{color:'var(--ta-on-surface-muted)'}}>{Math.round(l.calories)} kcal</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MUSCLE_GROUPS = [
  { id:'chest',label:'Chest' }, { id:'back',label:'Back' }, { id:'legs',label:'Legs' },
  { id:'shoulders',label:'Shoulders' }, { id:'arms',label:'Arms' }, { id:'core',label:'Core' },
  { id:'full_body',label:'Full Body' }, { id:'cardio',label:'Cardio' }, { id:'flexibility',label:'Flexibility' },
];
const muscleGroupMeta = (mg) => MUSCLE_GROUPS.find(m=>m.id===mg) || MUSCLE_GROUPS[MUSCLE_GROUPS.length-1];

function ExercisePicker({ exercises, onSelect, placeholder }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const matches = query.trim() ? exercises.filter(e=>e.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0,8) : [];
  return (
    <div style={{position:'relative'}}>
      <input value={query} onChange={e=>{setQuery(e.target.value); setOpen(true);}} onFocus={()=>setOpen(true)}
        placeholder={placeholder||'Search exercises…'} className="ta-input" style={{width:'100%'}}/>
      {open && matches.length>0 && (
        <div className="ta-card" style={{position:'absolute',top:'100%',left:0,right:0,zIndex:10,padding:4,marginTop:4,maxHeight:220,overflowY:'auto'}}>
          {matches.map(ex=>(
            <div key={ex.id} onClick={()=>{onSelect(ex); setQuery(''); setOpen(false);}}
              style={{padding:'7px 10px',fontSize:12.5,cursor:'pointer',borderRadius:6,display:'flex',justifyContent:'space-between',gap:8}}>
              <span>{ex.name}</span>
              <span style={{color:'var(--ta-on-surface-muted)',fontSize:11,whiteSpace:'nowrap'}}>{muscleGroupMeta(ex.muscle_group).label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExerciseLibraryTab({ ctx }) {
  const { workoutExercises=[], addWorkoutExercise, deleteWorkoutExercise } = ctx;
  const [query, setQuery] = React.useState('');
  const [muscle, setMuscle] = React.useState('');
  const [showAdd, setShowAdd] = React.useState(false);
  const blank = { name:'', exercise_type:'strength', muscle_group:'chest', equipment:'none' };
  const [form, setForm] = React.useState(blank);

  const filtered = workoutExercises.filter(e => {
    if (muscle && e.muscle_group!==muscle) return false;
    if (query && !e.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }).sort((a,b)=>a.name.localeCompare(b.name));

  const submit = async () => {
    if (!form.name.trim()) return;
    await addWorkoutExercise(form);
    setForm(blank); setShowAdd(false);
  };

  return (
    <div>
      <div className="ta-row" style={{gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search exercises…"
          style={{flex:'1 1 220px',fontSize:13,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 12px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}/>
        <select value={muscle} onChange={e=>setMuscle(e.target.value)}
          style={{fontSize:12.5,border:'1px solid var(--ta-outline-variant)',borderRadius:8,padding:'8px 10px',background:'var(--ta-surface-low)',color:'var(--ta-on-surface)'}}>
          <option value="">All muscle groups</option>
          {MUSCLE_GROUPS.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <button onClick={()=>setShowAdd(v=>!v)} className="ta-btn ta-btn-primary">+ Add custom exercise</button>
      </div>

      {showAdd && (
        <div className="ta-card" style={{marginBottom:14}}>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:8,marginBottom:10}}>
            <input placeholder="Exercise name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className="ta-input"/>
            <select value={form.exercise_type} onChange={e=>setForm(f=>({...f,exercise_type:e.target.value}))} className="ta-input">
              <option value="strength">Strength</option><option value="cardio">Cardio</option><option value="flexibility">Flexibility</option>
            </select>
            <select value={form.muscle_group} onChange={e=>setForm(f=>({...f,muscle_group:e.target.value}))} className="ta-input">
              {MUSCLE_GROUPS.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <input placeholder="Equipment" value={form.equipment} onChange={e=>setForm(f=>({...f,equipment:e.target.value}))} className="ta-input"/>
          </div>
          <button onClick={submit} className="ta-btn ta-btn-primary">Save exercise</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
        {filtered.map(ex=>(
          <div key={ex.id} className="ta-card" style={{padding:14}}>
            <div className="ta-row ta-between" style={{marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)'}}>{ex.name}</span>
              {!ex.is_global && <span onClick={()=>{if(window.confirm(`Delete "${ex.name}"?`))deleteWorkoutExercise(ex.id);}} style={{fontSize:11,opacity:0.5,cursor:'pointer'}}>🗑</span>}
            </div>
            <div style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{muscleGroupMeta(ex.muscle_group).label} · {ex.equipment}</div>
            <div className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-variant)',marginTop:4,textTransform:'uppercase'}}>{ex.exercise_type}</div>
          </div>
        ))}
        {filtered.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)',gridColumn:'1/-1',textAlign:'center',padding:40}}>No exercises match.</div>}
      </div>
    </div>
  );
}

function WorkoutPlanTab({ ctx }) {
  const {
    workoutExercises=[], workoutPlans=[], addWorkoutPlan, deleteWorkoutPlan,
    workoutPlanExercises=[], addWorkoutPlanExercise, deleteWorkoutPlanExercise,
    workoutSchedule=[], addWorkoutScheduleItem, deleteWorkoutScheduleItem,
  } = ctx;
  const [selectedPlanId, setSelectedPlanId] = React.useState(null);
  const [newPlanName, setNewPlanName] = React.useState('');
  const [selectedExercise, setSelectedExercise] = React.useState(null);
  const [sets, setSets] = React.useState(3);
  const [reps, setReps] = React.useState(10);
  const [weight, setWeight] = React.useState('');

  React.useEffect(()=>{ if(!selectedPlanId && workoutPlans.length) setSelectedPlanId(workoutPlans[0].id); }, [workoutPlans]); // eslint-disable-line

  const activePlan = workoutPlans.find(p=>p.id===selectedPlanId);
  const planExercises = workoutPlanExercises.filter(pe=>pe.plan_id===selectedPlanId).sort((a,b)=>a.sort_order-b.sort_order);
  const planScheduleDays = workoutSchedule.filter(s=>s.plan_id===selectedPlanId).map(s=>s.day_of_week);

  const createPlan = async () => {
    if (!newPlanName.trim()) return;
    const saved = await addWorkoutPlan({ name: newPlanName.trim() });
    if (saved?.id) setSelectedPlanId(saved.id);
    setNewPlanName('');
  };
  const addExerciseToPlan = async () => {
    if (!selectedExercise || !selectedPlanId) return;
    await addWorkoutPlanExercise({ plan_id: selectedPlanId, exercise_id: selectedExercise.id, target_sets: Number(sets)||null, target_reps: Number(reps)||null, target_weight_kg: weight?Number(weight):null, sort_order: planExercises.length });
    setSelectedExercise(null);
  };
  const toggleScheduleDay = async (day) => {
    const existing = workoutSchedule.find(s=>s.plan_id===selectedPlanId && s.day_of_week===day);
    if (existing) await deleteWorkoutScheduleItem(existing.id);
    else await addWorkoutScheduleItem({ day_of_week: day, plan_id: selectedPlanId });
  };

  return (
    <div>
      <div className="ta-card" style={{marginBottom:14,color:'var(--ta-on-surface-variant)',fontSize:13}}>
        Build named routines (e.g. "Push Day", "Leg Day"), then assign them to days of the week — they'll show up on the Today tab ready to log.
      </div>
      <div className="ta-row ta-gap-2" style={{flexWrap:'wrap',marginBottom:16,alignItems:'center'}}>
        {workoutPlans.map(p=>(
          <button key={p.id} onClick={()=>setSelectedPlanId(p.id)} className="ta-chip" style={{cursor:'pointer',border:'none',background:selectedPlanId===p.id?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:selectedPlanId===p.id?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{p.name}</button>
        ))}
        <input value={newPlanName} onChange={e=>setNewPlanName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&createPlan()} placeholder="New plan name…" className="ta-input" style={{width:180}}/>
        <button onClick={createPlan} className="ta-btn ta-btn-secondary">+ New plan</button>
      </div>

      {activePlan ? (
        <>
          <div className="ta-card" style={{marginBottom:14}}>
            <div className="ta-row ta-between" style={{marginBottom:10}}>
              <h2 className="ta-section-head-h" style={{margin:0}}>{activePlan.name}</h2>
              <button onClick={()=>{if(window.confirm(`Delete plan "${activePlan.name}"?`)){deleteWorkoutPlan(activePlan.id); setSelectedPlanId(null);}}} className="ta-btn" style={{color:'var(--ta-negative)',fontSize:11}}>Delete plan</button>
            </div>
            <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:6}}>Runs on:</div>
            <div className="ta-row ta-gap-2" style={{flexWrap:'wrap'}}>
              {DAY_NAMES.map((d,i)=>(
                <button key={i} onClick={()=>toggleScheduleDay(i)} className="ta-chip" style={{cursor:'pointer',border:'none',background:planScheduleDays.includes(i)?DIET_COLOR:'var(--ta-surface-high)',color:planScheduleDays.includes(i)?'#fff':'var(--ta-on-surface-variant)'}}>{d}</button>
              ))}
            </div>
          </div>

          <div className="ta-card" style={{marginBottom:14}}>
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto',gap:8}}>
              <ExercisePicker exercises={workoutExercises} onSelect={setSelectedExercise} placeholder="Search exercise to add…"/>
              <input type="number" value={sets} onChange={e=>setSets(e.target.value)} className="ta-input" placeholder="Sets"/>
              <input type="number" value={reps} onChange={e=>setReps(e.target.value)} className="ta-input" placeholder="Reps"/>
              <input type="number" value={weight} onChange={e=>setWeight(e.target.value)} className="ta-input" placeholder="Weight kg"/>
              <button onClick={addExerciseToPlan} disabled={!selectedExercise} className="ta-btn ta-btn-primary" style={{opacity:selectedExercise?1:0.5}}>+ Add</button>
            </div>
            {selectedExercise && <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginTop:6}}>{selectedExercise.name} selected</div>}
          </div>

          <div className="ta-card">
            <div className="ta-section-head"><h2 className="ta-section-head-h">Exercises in this plan</h2><span className="ta-section-head-meta">{planExercises.length}</span></div>
            {planExercises.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>No exercises yet.</div>}
            <div className="ta-col ta-gap-2">
              {planExercises.map(pe=>{
                const ex = workoutExercises.find(e=>e.id===pe.exercise_id);
                return (
                  <div key={pe.id} className="ta-row ta-between" style={{padding:'8px 12px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)'}}>
                    <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{ex?.name||'Unknown'}</span>
                    <div className="ta-row ta-gap-2" style={{alignItems:'center'}}>
                      <span className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>
                        {pe.target_sets?`${pe.target_sets}×${pe.target_reps||'?'}`:''}{pe.target_weight_kg?` @ ${pe.target_weight_kg}kg`:''}
                      </span>
                      <button onClick={()=>deleteWorkoutPlanExercise(pe.id)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:24,height:24}}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="ta-card" style={{textAlign:'center',padding:40}}>
          <div className="ta-serif ta-italic" style={{fontSize:16,color:'var(--ta-on-surface-muted)'}}>Create your first workout plan to get started.</div>
        </div>
      )}
    </div>
  );
}

function WorkoutLogExerciseRow({ ctx, logExercise }) {
  const { workoutLogSets=[], addWorkoutLogSet, deleteWorkoutLogExercise } = ctx;
  const sets = workoutLogSets.filter(s=>s.workout_log_exercise_id===logExercise.id).sort((a,b)=>a.set_number-b.set_number);
  const [reps, setReps] = React.useState('');
  const [weight, setWeight] = React.useState('');
  const isCardio = logExercise.exercise_type !== 'strength';

  const addSet = () => {
    addWorkoutLogSet({ workout_log_exercise_id: logExercise.id, set_number: sets.length+1, reps: Number(reps)||null, weight_kg: weight?Number(weight):null });
    setReps(''); setWeight('');
  };

  return (
    <div style={{padding:'8px 0',borderTop:'1px solid var(--ta-outline-variant)'}}>
      <div className="ta-row ta-between" style={{marginBottom:6}}>
        <span style={{fontSize:12.5,fontWeight:600,color:'var(--ta-on-surface)'}}>{logExercise.exercise_name}</span>
        <span onClick={()=>deleteWorkoutLogExercise(logExercise.id)} style={{fontSize:11,opacity:0.5,cursor:'pointer'}}>✕</span>
      </div>
      {isCardio ? (
        <div className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>
          {logExercise.duration_mins?`${logExercise.duration_mins} min`:''} {logExercise.distance_km?`· ${logExercise.distance_km} km`:''} {logExercise.calories_burned?`· ${logExercise.calories_burned} kcal`:''}
          {!logExercise.duration_mins && !logExercise.distance_km && !logExercise.calories_burned && 'No details logged.'}
        </div>
      ) : (
        <>
          {sets.map(s=>(
            <div key={s.id} className="ta-row ta-gap-2" style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:2}}>
              <span style={{width:50}}>Set {s.set_number}</span><span>{s.reps||'?'} reps</span>{s.weight_kg?<span>@ {s.weight_kg}kg</span>:null}
            </div>
          ))}
          <div className="ta-row ta-gap-2">
            <input type="number" value={reps} onChange={e=>setReps(e.target.value)} placeholder="Reps" className="ta-input" style={{width:70}}/>
            <input type="number" value={weight} onChange={e=>setWeight(e.target.value)} placeholder="Weight kg" className="ta-input" style={{width:90}}/>
            <button onClick={addSet} className="ta-btn ta-btn-secondary" style={{fontSize:11}}>+ Set</button>
          </div>
        </>
      )}
    </div>
  );
}

function WorkoutLogCard({ ctx, log, expanded, onToggle }) {
  const { workoutExercises=[], workoutLogExercises=[], addWorkoutLogExercise, updateWorkoutLog, deleteWorkoutLog } = ctx;
  const logExercises = workoutLogExercises.filter(le=>le.workout_log_id===log.id).sort((a,b)=>a.sort_order-b.sort_order);
  const [pickedExercise, setPickedExercise] = React.useState(null);
  const [cardioDuration, setCardioDuration] = React.useState('');
  const [cardioDistance, setCardioDistance] = React.useState('');
  const [cardioCalories, setCardioCalories] = React.useState('');
  const [finishCalories, setFinishCalories] = React.useState(log.calories_burned || '');

  const isPickedCardio = pickedExercise && pickedExercise.exercise_type !== 'strength';

  const addExercise = async () => {
    if (!pickedExercise) return;
    await addWorkoutLogExercise({
      workout_log_id: log.id, exercise_id: pickedExercise.id, exercise_name: pickedExercise.name, exercise_type: pickedExercise.exercise_type, sort_order: logExercises.length,
      duration_mins: isPickedCardio && cardioDuration ? Number(cardioDuration) : null,
      distance_km: isPickedCardio && cardioDistance ? Number(cardioDistance) : null,
      calories_burned: isPickedCardio && cardioCalories ? Number(cardioCalories) : null,
    });
    setPickedExercise(null); setCardioDuration(''); setCardioDistance(''); setCardioCalories('');
  };
  const finishWorkout = () => {
    const startedMs = log.started_at ? new Date(log.started_at).getTime() : Date.now();
    const durationMins = Math.max(1, Math.round((Date.now()-startedMs)/60000));
    updateWorkoutLog(log.id, { ended_at: new Date().toISOString(), duration_mins: durationMins, calories_burned: finishCalories?Number(finishCalories):null });
  };

  return (
    <div style={{border:'1px solid var(--ta-outline-variant)',borderRadius:'var(--r-md)',padding:12,marginBottom:8}}>
      <div className="ta-row ta-between" onClick={onToggle} style={{cursor:'pointer'}}>
        <span style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)'}}>{log.name}{log.ended_at?' · Completed':''}</span>
        <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{log.duration_mins?`${log.duration_mins}m`:''} {expanded?'▾':'▸'}</span>
      </div>
      {expanded && (
        <div style={{marginTop:10}}>
          {logExercises.map(le => <WorkoutLogExerciseRow key={le.id} ctx={ctx} logExercise={le}/>)}
          <div style={{display:'grid',gridTemplateColumns: isPickedCardio ? '2fr 1fr 1fr 1fr auto' : '3fr auto',gap:8,marginTop:8}}>
            <ExercisePicker exercises={workoutExercises} onSelect={setPickedExercise} placeholder="Add another exercise…"/>
            {isPickedCardio && (<>
              <input type="number" value={cardioDuration} onChange={e=>setCardioDuration(e.target.value)} placeholder="Mins" className="ta-input"/>
              <input type="number" value={cardioDistance} onChange={e=>setCardioDistance(e.target.value)} placeholder="Km" className="ta-input"/>
              <input type="number" value={cardioCalories} onChange={e=>setCardioCalories(e.target.value)} placeholder="Kcal" className="ta-input"/>
            </>)}
            <button onClick={addExercise} disabled={!pickedExercise} className="ta-btn ta-btn-secondary" style={{opacity:pickedExercise?1:0.5}}>+ Add</button>
          </div>
          {!log.ended_at && (
            <div className="ta-row ta-gap-2" style={{marginTop:12,alignItems:'center'}}>
              <input type="number" value={finishCalories} onChange={e=>setFinishCalories(e.target.value)} placeholder="Calories burned (optional)" className="ta-input" style={{flex:1}}/>
              <button onClick={finishWorkout} className="ta-btn ta-btn-primary">Finish workout</button>
            </div>
          )}
          <button onClick={()=>{if(window.confirm('Delete this workout log?'))deleteWorkoutLog(log.id);}} className="ta-btn" style={{fontSize:11,color:'var(--ta-negative)',marginTop:8}}>Delete log</button>
        </div>
      )}
    </div>
  );
}

function TodayWorkoutCard({ ctx }) {
  const {
    workoutSchedule=[], workoutPlans=[], workoutPlanExercises=[], workoutExercises=[],
    workoutLogs=[], addWorkoutLog, addWorkoutLogExercise,
  } = ctx;
  const todayDow = new Date(TODAY+"T00:00:00").getDay();
  const scheduledToday = workoutSchedule.filter(s=>s.day_of_week===todayDow);
  const todayLogs = workoutLogs.filter(l=>l.log_date===TODAY);
  const [expandedLogId, setExpandedLogId] = React.useState(null);

  const startWorkout = async (plan) => {
    const planExs = workoutPlanExercises.filter(pe=>pe.plan_id===plan.id).sort((a,b)=>a.sort_order-b.sort_order);
    const log = await addWorkoutLog({ log_date: TODAY, plan_id: plan.id, name: plan.name, started_at: new Date().toISOString() });
    if (!log?.id) return;
    for (const pe of planExs) {
      const ex = workoutExercises.find(e=>e.id===pe.exercise_id);
      if (!ex) continue;
      await addWorkoutLogExercise({ workout_log_id: log.id, exercise_id: ex.id, exercise_name: ex.name, exercise_type: ex.exercise_type, sort_order: planExs.indexOf(pe) });
    }
    setExpandedLogId(log.id);
  };
  const startAdhoc = async () => {
    const log = await addWorkoutLog({ log_date: TODAY, name: 'Workout', started_at: new Date().toISOString() });
    if (log?.id) setExpandedLogId(log.id);
  };

  return (
    <div className="ta-card">
      <div className="ta-section-head"><h2 className="ta-section-head-h">🏋️ Today's workout</h2></div>
      {scheduledToday.length===0 && todayLogs.length===0 && (
        <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)',marginBottom:10}}>Nothing scheduled today.</div>
      )}
      {scheduledToday.map(s=>{
        const plan = workoutPlans.find(p=>p.id===s.plan_id);
        if (!plan) return null;
        const alreadyStarted = todayLogs.some(l=>l.plan_id===plan.id);
        return (
          <div key={s.id} className="ta-row ta-between" style={{padding:'8px 12px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',marginBottom:8}}>
            <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{plan.name}</span>
            <button onClick={()=>startWorkout(plan)} disabled={alreadyStarted} className="ta-btn ta-btn-secondary" style={{fontSize:11,opacity:alreadyStarted?0.5:1}}>{alreadyStarted?'✓ Started':'Start'}</button>
          </div>
        );
      })}
      <button onClick={startAdhoc} className="ta-btn" style={{fontSize:12,marginBottom:10}}>+ Quick log a workout</button>

      {todayLogs.map(log => (
        <WorkoutLogCard key={log.id} ctx={ctx} log={log} expanded={expandedLogId===log.id} onToggle={()=>setExpandedLogId(id=>id===log.id?null:log.id)}/>
      ))}
    </div>
  );
}

function WorkoutHistoryTab({ ctx }) {
  const { workoutLogs=[], workoutLogExercises=[], workoutLogSets=[] } = ctx;
  const completedLogs = [...workoutLogs].filter(l=>l.log_date).sort((a,b)=>b.log_date.localeCompare(a.log_date)).slice(0,14);

  const progressionMap = {};
  workoutLogExercises.forEach(le => {
    const log = workoutLogs.find(l=>l.id===le.workout_log_id);
    if (!log) return;
    const sets = workoutLogSets.filter(s=>s.workout_log_exercise_id===le.id);
    const maxWeight = sets.reduce((m,s)=>Math.max(m, Number(s.weight_kg)||0), 0);
    if (maxWeight<=0) return;
    if (!progressionMap[le.exercise_name]) progressionMap[le.exercise_name] = { allTimeMax:0, latest:0, latestDate:'' };
    const p = progressionMap[le.exercise_name];
    p.allTimeMax = Math.max(p.allTimeMax, maxWeight);
    if (log.log_date >= p.latestDate) { p.latest = maxWeight; p.latestDate = log.log_date; }
  });
  const progressionEntries = Object.entries(progressionMap).sort((a,b)=>a[0].localeCompare(b[0]));

  return (
    <div className="ta-col ta-gap-3">
      {progressionEntries.length>0 && (
        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Exercise progression</h2></div>
          <div className="ta-col ta-gap-1">
            {progressionEntries.map(([name,p])=>(
              <div key={name} className="ta-row ta-between" style={{fontSize:12,padding:'4px 0'}}>
                <span style={{color:'var(--ta-on-surface)'}}>{name}</span>
                <span className="ta-mono" style={{color:'var(--ta-on-surface-muted)'}}>Best: {p.allTimeMax}kg · Latest: {p.latest}kg</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {completedLogs.length===0 && <div className="ta-card" style={{textAlign:'center',padding:40}}><div className="ta-serif ta-italic" style={{fontSize:16,color:'var(--ta-on-surface-muted)'}}>No workouts logged yet.</div></div>}
      {completedLogs.map(log=>{
        const exs = workoutLogExercises.filter(le=>le.workout_log_id===log.id);
        return (
          <div key={log.id} className="ta-card">
            <div className="ta-section-head">
              <h2 className="ta-section-head-h">{log.name} · {new Date(log.log_date+"T00:00:00").toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}</h2>
              <span className="ta-mono ta-section-head-meta">{log.duration_mins?`${log.duration_mins}m`:''} {log.calories_burned?`· ${log.calories_burned} kcal`:''}</span>
            </div>
            <div className="ta-col ta-gap-1">
              {exs.map(le=>(
                <div key={le.id} style={{fontSize:12,color:'var(--ta-on-surface-variant)',padding:'3px 0'}}>{le.exercise_name}</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BodyTab({ ctx }) {
  const { bodyProfile, updateBodyProfile, bodyCompositionLogs=[], addBodyCompositionLog, deleteBodyCompositionLog } = ctx;
  const [height, setHeight] = React.useState(bodyProfile?.height_cm || '');
  const blank = { weight_kg:'', body_fat_pct:'', waist_cm:'', chest_cm:'', hips_cm:'', arm_cm:'', thigh_cm:'', neck_cm:'' };
  const [form, setForm] = React.useState(blank);
  const [showForm, setShowForm] = React.useState(false);

  const sortedLogs = [...bodyCompositionLogs].sort((a,b)=>b.log_date.localeCompare(a.log_date));
  const latest = sortedLogs[0];
  const heightM = height ? Number(height)/100 : null;
  const bmi = latest?.weight_kg && heightM ? (latest.weight_kg / (heightM*heightM)) : null;
  const bmiCategory = (b) => !b ? '' : b<18.5?'Underweight':b<25?'Normal':b<30?'Overweight':'Obese';
  const bmiColor = (b) => !b ? 'var(--ta-on-surface-muted)' : b<18.5?'var(--ta-notice)':b<25?'var(--ta-positive)':b<30?'var(--ta-notice)':'var(--ta-negative)';

  const submit = () => {
    if (!form.weight_kg && !form.body_fat_pct && !form.waist_cm) return;
    const payload = { log_date: TODAY };
    Object.entries(form).forEach(([k,v]) => { if (v!=='') payload[k] = Number(v); });
    addBodyCompositionLog(payload);
    setForm(blank); setShowForm(false);
  };

  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20,alignItems:'start'}}>
      <div className="ta-col ta-gap-4">
        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Log a check-in</h2>
            <span onClick={()=>setShowForm(v=>!v)} className="ta-section-head-meta" style={{cursor:'pointer',color:DIET_COLOR}}>{showForm?'Cancel':'+ New check-in'}</span>
          </div>
          {showForm && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:8}}>
                <input placeholder="Weight kg" type="number" value={form.weight_kg} onChange={e=>setForm(f=>({...f,weight_kg:e.target.value}))} className="ta-input"/>
                <input placeholder="Body fat %" type="number" value={form.body_fat_pct} onChange={e=>setForm(f=>({...f,body_fat_pct:e.target.value}))} className="ta-input"/>
                <input placeholder="Waist cm" type="number" value={form.waist_cm} onChange={e=>setForm(f=>({...f,waist_cm:e.target.value}))} className="ta-input"/>
                <input placeholder="Chest cm" type="number" value={form.chest_cm} onChange={e=>setForm(f=>({...f,chest_cm:e.target.value}))} className="ta-input"/>
                <input placeholder="Hips cm" type="number" value={form.hips_cm} onChange={e=>setForm(f=>({...f,hips_cm:e.target.value}))} className="ta-input"/>
                <input placeholder="Arm cm" type="number" value={form.arm_cm} onChange={e=>setForm(f=>({...f,arm_cm:e.target.value}))} className="ta-input"/>
                <input placeholder="Thigh cm" type="number" value={form.thigh_cm} onChange={e=>setForm(f=>({...f,thigh_cm:e.target.value}))} className="ta-input"/>
                <input placeholder="Neck cm" type="number" value={form.neck_cm} onChange={e=>setForm(f=>({...f,neck_cm:e.target.value}))} className="ta-input"/>
              </div>
              <button onClick={submit} className="ta-btn ta-btn-primary">Save check-in</button>
            </>
          )}
        </div>

        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">History</h2><span className="ta-section-head-meta">{sortedLogs.length} check-ins</span></div>
          {sortedLogs.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>No check-ins logged yet.</div>}
          <div className="ta-col ta-gap-2">
            {sortedLogs.map((log,i)=>{
              const prev = sortedLogs[i+1];
              const delta = prev && log.weight_kg && prev.weight_kg ? (log.weight_kg-prev.weight_kg) : null;
              return (
                <div key={log.id} className="ta-row ta-between" style={{padding:'8px 12px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)'}}>
                  <div>
                    <span className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginRight:8}}>{new Date(log.log_date+"T00:00:00").toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
                    {log.weight_kg!=null && <span style={{fontSize:13,color:'var(--ta-on-surface)'}}>{log.weight_kg}kg</span>}
                    {delta!==null && <span className="ta-mono" style={{fontSize:11,marginLeft:6,color:delta>0?'var(--ta-negative)':delta<0?'var(--ta-positive)':'var(--ta-on-surface-muted)'}}>{delta>0?'▲':delta<0?'▼':'–'}{Math.abs(delta).toFixed(1)}kg</span>}
                    {log.body_fat_pct!=null && <span className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginLeft:8}}>{log.body_fat_pct}% BF</span>}
                  </div>
                  <button onClick={()=>deleteBodyCompositionLog(log.id)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:24,height:24}}>✕</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="ta-card-inset">
        <div className="ta-section-head-meta" style={{marginBottom:14}}>Current stats</div>
        <div style={{marginBottom:14}}>
          <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>Height (cm)</span>
          <input type="number" value={height} onChange={e=>setHeight(e.target.value)} onBlur={()=>updateBodyProfile({height_cm:Number(height)||null})} className="ta-input" style={{width:'100%',marginTop:4}}/>
        </div>
        {latest?.weight_kg!=null && (
          <div style={{marginBottom:10}}>
            <div className="ta-serif" style={{fontSize:28,fontWeight:300,color:'var(--ta-on-surface)'}}>{latest.weight_kg}<span style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}> kg</span></div>
            <div style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>Last logged {new Date(latest.log_date+"T00:00:00").toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
          </div>
        )}
        {bmi && (
          <div style={{paddingTop:10,borderTop:'1px solid var(--ta-outline-variant)'}}>
            <div className="ta-row ta-between">
              <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>BMI</span>
              <span className="ta-mono" style={{fontSize:13,fontWeight:700,color:bmiColor(bmi)}}>{bmi.toFixed(1)} · {bmiCategory(bmi)}</span>
            </div>
          </div>
        )}
        {latest?.body_fat_pct!=null && (
          <div className="ta-row ta-between" style={{marginTop:8}}>
            <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>Body Fat</span>
            <span className="ta-mono" style={{fontSize:13}}>{latest.body_fat_pct}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

const GOAL_TYPES = [
  { id:'fat_loss',    label:'Fat Loss',     icon:'🔥' },
  { id:'muscle_gain', label:'Muscle Gain',  icon:'💪' },
  { id:'weight_gain', label:'Weight Gain',  icon:'⬆️' },
  { id:'maintenance', label:'Maintenance',  icon:'⚖️' },
  { id:'strength',    label:'Strength',     icon:'🏋️' },
  { id:'endurance',   label:'Endurance',    icon:'🏃' },
];
const goalTypeMeta = (t) => GOAL_TYPES.find(g=>g.id===t) || GOAL_TYPES[0];
const WEIGHT_BASED_GOALS = new Set(['fat_loss','muscle_gain','weight_gain','maintenance']);

function computeGoalProgress(goal, ctx) {
  const { bodyCompositionLogs=[], workoutLogExercises=[], workoutLogSets=[] } = ctx;
  if (WEIGHT_BASED_GOALS.has(goal.goal_type)) {
    const sortedLogs = [...bodyCompositionLogs].filter(l=>l.weight_kg!=null).sort((a,b)=>b.log_date.localeCompare(a.log_date));
    const currentWeight = sortedLogs[0]?.weight_kg ?? goal.start_weight_kg;
    if (goal.start_weight_kg==null || goal.target_weight_kg==null || currentWeight==null) return { pct:0, label:'No data yet' };
    const total = goal.target_weight_kg - goal.start_weight_kg;
    const done = currentWeight - goal.start_weight_kg;
    const pct = total === 0 ? 100 : Math.max(0, Math.min(100, (done/total)*100));
    return { pct, label:`${currentWeight}kg → ${goal.target_weight_kg}kg` };
  }
  if (goal.goal_type === 'strength') {
    const relevantSets = workoutLogExercises.filter(le=>le.exercise_id===goal.target_exercise_id)
      .flatMap(le => workoutLogSets.filter(s=>s.workout_log_exercise_id===le.id));
    const maxWeight = relevantSets.reduce((m,s)=>Math.max(m, Number(s.weight_kg)||0), 0);
    const pct = goal.target_metric_value ? Math.max(0,Math.min(100,(maxWeight/goal.target_metric_value)*100)) : 0;
    return { pct, label:`${maxWeight}kg → ${goal.target_metric_value||'?'}kg` };
  }
  if (goal.goal_type === 'endurance') {
    const relevantExs = workoutLogExercises.filter(le=>le.exercise_id===goal.target_exercise_id);
    const isDistance = goal.target_metric_unit === 'km';
    const best = relevantExs.reduce((m,le)=>Math.max(m, Number(isDistance?le.distance_km:le.duration_mins)||0), 0);
    const pct = goal.target_metric_value ? Math.max(0,Math.min(100,(best/goal.target_metric_value)*100)) : 0;
    return { pct, label:`${best}${isDistance?'km':'min'} → ${goal.target_metric_value||'?'}${isDistance?'km':'min'}` };
  }
  return { pct:0, label:'' };
}

function HealthGoalCard({ ctx, goal }) {
  const { updateHealthGoal, deleteHealthGoal, workoutExercises=[] } = ctx;
  const progress = computeGoalProgress(goal, ctx);
  const meta = goalTypeMeta(goal.goal_type);
  const exercise = goal.target_exercise_id ? workoutExercises.find(e=>e.id===goal.target_exercise_id) : null;
  return (
    <div className="ta-card" style={{padding:16}}>
      <div className="ta-row ta-between" style={{marginBottom:8}}>
        <span style={{fontSize:13,fontWeight:600,color:'var(--ta-on-surface)'}}>{meta.icon} {goal.title || meta.label}</span>
        <span onClick={()=>{if(window.confirm('Delete this goal?'))deleteHealthGoal(goal.id);}} style={{fontSize:11,opacity:0.5,cursor:'pointer'}}>🗑</span>
      </div>
      {exercise && <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:6}}>{exercise.name}</div>}
      <div className="ta-bar" style={{marginBottom:6}}><div className="ta-bar-fill" style={{width:`${progress.pct}%`,background:DIET_COLOR}}/></div>
      <div className="ta-row ta-between">
        <span className="ta-mono" style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>{progress.label}</span>
        <span className="ta-mono" style={{fontSize:11,fontWeight:700,color:DIET_COLOR}}>{Math.round(progress.pct)}%</span>
      </div>
      {goal.target_date && <div style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginTop:6}}>Target: {new Date(goal.target_date+"T00:00:00").toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>}
      {progress.pct>=100 && goal.status==='active' && (
        <button onClick={()=>updateHealthGoal(goal.id,{status:'completed'})} className="ta-btn ta-btn-primary" style={{fontSize:11,marginTop:10,width:'100%',justifyContent:'center'}}>🎉 Mark complete</button>
      )}
    </div>
  );
}

function GoalsTab({ ctx }) {
  const { healthGoals=[], addHealthGoal, workoutExercises=[], bodyCompositionLogs=[] } = ctx;
  const [showForm, setShowForm] = React.useState(false);
  const blank = { goal_type:'fat_loss', title:'', target_date:'', start_weight_kg:'', target_weight_kg:'', target_metric_value:'', target_metric_unit:'mins' };
  const [form, setForm] = React.useState(blank);
  const [pickedExercise, setPickedExercise] = React.useState(null);

  const latestWeight = [...bodyCompositionLogs].filter(l=>l.weight_kg!=null).sort((a,b)=>b.log_date.localeCompare(a.log_date))[0]?.weight_kg;
  const isWeightGoal = WEIGHT_BASED_GOALS.has(form.goal_type);
  const activeGoals = healthGoals.filter(g=>g.status==='active');
  const pastGoals = healthGoals.filter(g=>g.status!=='active');

  const submit = async () => {
    const payload = { goal_type: form.goal_type, title: form.title.trim()||null, target_date: form.target_date||null };
    if (isWeightGoal) {
      payload.start_weight_kg = Number(form.start_weight_kg)||latestWeight||null;
      payload.target_weight_kg = Number(form.target_weight_kg)||null;
    } else {
      payload.target_exercise_id = pickedExercise?.id || null;
      payload.target_metric_value = Number(form.target_metric_value)||null;
      payload.target_metric_unit = form.goal_type==='endurance' ? form.target_metric_unit : 'kg';
    }
    await addHealthGoal(payload);
    setForm(blank); setPickedExercise(null); setShowForm(false);
  };

  return (
    <div>
      <div className="ta-row ta-between" style={{marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>Track long-term progress toward what actually matters to you.</div>
        <button onClick={()=>setShowForm(v=>!v)} className="ta-btn ta-btn-primary">+ New goal</button>
      </div>

      {showForm && (
        <div className="ta-card" style={{marginBottom:16}}>
          <div className="ta-row ta-gap-2" style={{flexWrap:'wrap',marginBottom:10}}>
            {GOAL_TYPES.map(t=>(
              <button key={t.id} onClick={()=>setForm(f=>({...f,goal_type:t.id}))} className="ta-chip" style={{cursor:'pointer',border:'none',background:form.goal_type===t.id?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:form.goal_type===t.id?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{t.icon} {t.label}</button>
            ))}
          </div>
          <input placeholder="Goal title (optional, e.g. Summer Cut)" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} className="ta-input" style={{width:'100%',marginBottom:8}}/>
          {isWeightGoal ? (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
              <input placeholder={`Start weight kg (default ${latestWeight||'—'})`} type="number" value={form.start_weight_kg} onChange={e=>setForm(f=>({...f,start_weight_kg:e.target.value}))} className="ta-input"/>
              <input placeholder="Target weight kg" type="number" value={form.target_weight_kg} onChange={e=>setForm(f=>({...f,target_weight_kg:e.target.value}))} className="ta-input"/>
              <input type="date" value={form.target_date} onChange={e=>setForm(f=>({...f,target_date:e.target.value}))} className="ta-input"/>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:8,marginBottom:10}}>
              <ExercisePicker exercises={workoutExercises} onSelect={setPickedExercise} placeholder={form.goal_type==='strength'?'Target exercise (e.g. Barbell Squat)…':'Target exercise (e.g. Running)…'}/>
              <input placeholder="Target value" type="number" value={form.target_metric_value} onChange={e=>setForm(f=>({...f,target_metric_value:e.target.value}))} className="ta-input"/>
              {form.goal_type==='endurance' ? (
                <select value={form.target_metric_unit} onChange={e=>setForm(f=>({...f,target_metric_unit:e.target.value}))} className="ta-input">
                  <option value="mins">Minutes</option><option value="km">Kilometers</option>
                </select>
              ) : <div/>}
              <input type="date" value={form.target_date} onChange={e=>setForm(f=>({...f,target_date:e.target.value}))} className="ta-input"/>
            </div>
          )}
          {pickedExercise && <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginBottom:8}}>{pickedExercise.name} selected</div>}
          <button onClick={submit} className="ta-btn ta-btn-primary">Create goal</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
        {activeGoals.map(goal => <HealthGoalCard key={goal.id} ctx={ctx} goal={goal}/>)}
      </div>
      {activeGoals.length===0 && !showForm && (
        <div className="ta-card" style={{textAlign:'center',padding:40}}>
          <div className="ta-serif ta-italic" style={{fontSize:16,color:'var(--ta-on-surface-muted)'}}>No active goals — set one to start tracking progress.</div>
        </div>
      )}

      {pastGoals.length>0 && (
        <div style={{marginTop:24}}>
          <div className="ta-section-head-meta" style={{marginBottom:8}}>Past goals</div>
          <div className="ta-col ta-gap-2">
            {pastGoals.map(g=>(
              <div key={g.id} className="ta-row ta-between" style={{fontSize:12,padding:'6px 12px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)',color:'var(--ta-on-surface-muted)'}}>
                <span>{goalTypeMeta(g.goal_type).icon} {g.title||goalTypeMeta(g.goal_type).label}</span>
                <span>{g.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FitnessDashboardSummary({ ctx }) {
  const { dietMealLogs=[], dietWaterLogs=[], workoutLogs=[], bodyCompositionLogs=[], healthGoals=[] } = ctx;

  const daysBack = (n) => { const d = new Date(TODAY+"T00:00:00"); d.setDate(d.getDate()-n); return fmt(d); };
  const hasMealOn = (date) => dietMealLogs.some(l=>l.log_date===date);
  const hasCompletedWorkoutOn = (date) => workoutLogs.some(l=>l.log_date===date && l.ended_at);
  const computeStreak = (checkFn) => {
    let streak = 0;
    for (let i=0; i<365; i++) { if (checkFn(daysBack(i))) streak++; else break; }
    return streak;
  };
  const nutritionStreak = computeStreak(hasMealOn);
  const workoutStreak = computeStreak(hasCompletedWorkoutOn);

  const last7 = Array.from({length:7},(_,i)=>daysBack(6-i));
  const dayLabel = (date) => new Date(date+"T00:00:00").toLocaleDateString('en-US',{weekday:'short'})[0];

  const weekMealLogs = dietMealLogs.filter(l=>last7.includes(l.log_date));
  const weekCalDays = {}; weekMealLogs.forEach(l=>{ weekCalDays[l.log_date] = (weekCalDays[l.log_date]||0) + Number(l.calories||0); });
  const loggedDaysCount = Object.keys(weekCalDays).length;
  const avgWeekCalories = loggedDaysCount ? Math.round(Object.values(weekCalDays).reduce((a,b)=>a+b,0)/loggedDaysCount) : 0;
  const weekWorkoutsCompleted = workoutLogs.filter(l=>last7.includes(l.log_date) && l.ended_at).length;
  const weekWaterLogs = dietWaterLogs.filter(w=>last7.includes(w.log_date));
  const weekWaterDays = {}; weekWaterLogs.forEach(w=>{ weekWaterDays[w.log_date] = (weekWaterDays[w.log_date]||0) + Number(w.amount_ml||0); });
  const weekWaterDayCount = Object.keys(weekWaterDays).length;
  const avgWeekWaterMl = weekWaterDayCount ? Math.round(Object.values(weekWaterDays).reduce((a,b)=>a+b,0)/weekWaterDayCount) : 0;

  const last30Start = daysBack(29);
  const monthWorkouts = workoutLogs.filter(l=>l.log_date>=last30Start && l.ended_at).length;
  const monthBodyLogs = [...bodyCompositionLogs].filter(l=>l.log_date>=last30Start && l.weight_kg!=null).sort((a,b)=>a.log_date.localeCompare(b.log_date));
  const weightChange = monthBodyLogs.length>=2 ? (monthBodyLogs[monthBodyLogs.length-1].weight_kg - monthBodyLogs[0].weight_kg) : null;
  const monthMealLogs = dietMealLogs.filter(l=>l.log_date>=last30Start);
  const monthCalDays = {}; monthMealLogs.forEach(l=>{ monthCalDays[l.log_date] = (monthCalDays[l.log_date]||0) + Number(l.calories||0); });
  const monthLoggedDays = Object.keys(monthCalDays).length;
  const avgMonthCalories = monthLoggedDays ? Math.round(Object.values(monthCalDays).reduce((a,b)=>a+b,0)/monthLoggedDays) : 0;

  const completedWorkoutsTotal = workoutLogs.filter(l=>l.ended_at).length;
  const completedGoalsTotal = healthGoals.filter(g=>g.status==='completed').length;
  const achievements = [
    { id:'streak7',       icon:'🔥', label:'7-Day Nutrition Streak',   unlocked: nutritionStreak>=7 },
    { id:'streak30',      icon:'🔥', label:'30-Day Nutrition Streak',  unlocked: nutritionStreak>=30 },
    { id:'firstworkout',  icon:'🏋️', label:'First Workout Logged',     unlocked: completedWorkoutsTotal>=1 },
    { id:'tenworkouts',   icon:'💪', label:'10 Workouts Completed',    unlocked: completedWorkoutsTotal>=10 },
    { id:'firstgoal',     icon:'🎯', label:'First Goal Completed',     unlocked: completedGoalsTotal>=1 },
  ];
  const unlockedCount = achievements.filter(a=>a.unlocked).length;

  return (
    <div className="ta-col ta-gap-4">
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14}}>
        <div className="ta-card-inset" style={{textAlign:'center'}}>
          <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Nutrition Streak</div>
          <div className="ta-serif" style={{fontSize:32,fontWeight:300,color:DIET_COLOR}}>{nutritionStreak}<span style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}> day{nutritionStreak!==1?'s':''}</span></div>
        </div>
        <div className="ta-card-inset" style={{textAlign:'center'}}>
          <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Workout Streak</div>
          <div className="ta-serif" style={{fontSize:32,fontWeight:300,color:'#6B3FA0'}}>{workoutStreak}<span style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}> day{workoutStreak!==1?'s':''}</span></div>
        </div>
        <div className="ta-card-inset" style={{textAlign:'center'}}>
          <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Achievements</div>
          <div className="ta-serif" style={{fontSize:32,fontWeight:300,color:'#C8860A'}}>{unlockedCount}<span style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}> / {achievements.length}</span></div>
        </div>
      </div>

      <div className="ta-card">
        <div className="ta-section-head"><h2 className="ta-section-head-h">This week</h2></div>
        <div className="ta-row ta-gap-2" style={{marginBottom:14}}>
          {last7.map(date=>{
            const meal = hasMealOn(date); const workout = hasCompletedWorkoutOn(date);
            const isToday = date===TODAY;
            return (
              <div key={date} style={{flex:1,textAlign:'center',padding:'6px 4px',borderRadius:8,background:isToday?'var(--ta-surface-low)':'transparent'}}>
                <div style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginBottom:4,fontWeight:isToday?700:400}}>{dayLabel(date)}</div>
                <div style={{fontSize:14}}>{meal?'🍽️':'·'}</div>
                <div style={{fontSize:14}}>{workout?'🏋️':'·'}</div>
              </div>
            );
          })}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,fontSize:12,color:'var(--ta-on-surface-variant)'}}>
          <div><span className="ta-mono" style={{fontWeight:700,color:'var(--ta-on-surface)'}}>{avgWeekCalories}</span> avg kcal/day</div>
          <div><span className="ta-mono" style={{fontWeight:700,color:'var(--ta-on-surface)'}}>{weekWorkoutsCompleted}</span> workouts done</div>
          <div><span className="ta-mono" style={{fontWeight:700,color:'var(--ta-on-surface)'}}>{(avgWeekWaterMl/1000).toFixed(1)}L</span> avg water/day</div>
        </div>
      </div>

      <div className="ta-card">
        <div className="ta-section-head"><h2 className="ta-section-head-h">Last 30 days</h2></div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,fontSize:12,color:'var(--ta-on-surface-variant)'}}>
          <div><span className="ta-mono" style={{fontWeight:700,color:'var(--ta-on-surface)'}}>{avgMonthCalories}</span> avg kcal/day</div>
          <div><span className="ta-mono" style={{fontWeight:700,color:'var(--ta-on-surface)'}}>{monthWorkouts}</span> workouts completed</div>
          <div><span className="ta-mono" style={{fontWeight:700,color: weightChange==null?'var(--ta-on-surface)': weightChange<0?'var(--ta-positive)':weightChange>0?'var(--ta-negative)':'var(--ta-on-surface)'}}>{weightChange==null?'—':`${weightChange>0?'+':''}${weightChange.toFixed(1)}kg`}</span> weight change</div>
        </div>
      </div>

      <div className="ta-card">
        <div className="ta-section-head"><h2 className="ta-section-head-h">Achievements</h2></div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10}}>
          {achievements.map(a=>(
            <div key={a.id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderRadius:'var(--r-md)',background:a.unlocked?'var(--ta-surface-low)':'transparent',opacity:a.unlocked?1:0.4}}>
              <span style={{fontSize:18}}>{a.icon}</span>
              <span style={{fontSize:12,color:'var(--ta-on-surface)'}}>{a.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FITNESS_GROUPS = {
  nutrition: { label:'Nutrition', subtabs:[["today","Today"],["library","Food Library"],["plan","Meal Plan"],["history","History"]] },
  training:  { label:'Training',  subtabs:[["today","Today"],["exercises","Exercises"],["workoutplan","Workout Plan"],["history","History"]] },
  metrics:   { label:'Metrics',   subtabs:[["overview","Overview"],["body","Body"],["goals","Goals"]] },
};

function FitnessSection({ ctx }) {
  const [group, setGroup] = React.useState('nutrition'); // nutrition | training | metrics
  const [subtab, setSubtab] = React.useState({ nutrition:'today', training:'today', metrics:'overview' });
  const tab = subtab[group];
  const setTab = (id) => setSubtab(s => ({ ...s, [group]: id }));

  return (
    <div>
      <div className="ta-row ta-between" style={{alignItems:'flex-end',marginBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          <div className="ta-eh-eyebrow">Personal · {new Date(TODAY+"T00:00:00").toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Fuel & <em>movement</em>.</h1>
        </div>
        <div className="ta-segmented">
          {Object.entries(FITNESS_GROUPS).map(([id,g])=>(
            <button key={id} onClick={()=>setGroup(id)} className={'ta-segmented-item'+(group===id?' active':'')}>{g.label}</button>
          ))}
        </div>
      </div>

      <div className="ta-subtabs" style={{marginBottom:20}}>
        {FITNESS_GROUPS[group].subtabs.map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} className={'ta-subtab-item'+(tab===id?' active':'')}>{label}</button>
        ))}
      </div>

      {group==='nutrition' && tab==='today'   && <NutritionTodayTab ctx={ctx}/>}
      {group==='nutrition' && tab==='library' && <FoodLibraryTab ctx={ctx}/>}
      {group==='nutrition' && tab==='plan'    && <MealPlanTab ctx={ctx}/>}
      {group==='nutrition' && tab==='history' && <NutritionHistoryTab ctx={ctx}/>}

      {group==='training' && tab==='today'       && <TodayWorkoutCard ctx={ctx}/>}
      {group==='training' && tab==='exercises'   && <ExerciseLibraryTab ctx={ctx}/>}
      {group==='training' && tab==='workoutplan' && <WorkoutPlanTab ctx={ctx}/>}
      {group==='training' && tab==='history'     && <WorkoutHistoryTab ctx={ctx}/>}

      {group==='metrics' && tab==='overview' && <FitnessDashboardSummary ctx={ctx}/>}
      {group==='metrics' && tab==='body'     && <BodyTab ctx={ctx}/>}
      {group==='metrics' && tab==='goals'    && <GoalsTab ctx={ctx}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DEEP WORK — new module, Tactile Archive design, real Supabase table
// ══════════════════════════════════════════════════════════════════════════════
const DEEPWORK_PRESETS = [25, 50, 90];

function DeepWorkSection({ ctx }) {
  const { deepWorkSessions, addDeepWorkSession, deleteDeepWorkSession, tasks, focusTask, setFocusTask } = ctx;
  const [label, setLabel] = useState(() => focusTask?.text || "");
  const [dur, setDur] = useState(50);
  const [secLeft, setSecLeft] = useState(50*60);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [rating, setRating] = useState(null); // { mins } — awaiting a quality pick before logging
  const [quality, setQuality] = useState(0);
  const tickRef = useRef(null);

  // Arriving via "Focus on this" from a task card
  useEffect(() => { if (focusTask?.text) { setLabel(focusTask.text); setFocusTask && setFocusTask(null); } }, [focusTask]); // eslint-disable-line

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => setSecLeft(s => {
      if (s <= 1) { clearInterval(tickRef.current); finish(true); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(tickRef.current);
  }, [running]); // eslint-disable-line

  const start = () => { setStartedAt(new Date()); setSecLeft(dur*60); setRunning(true); };
  const finish = (completed) => {
    setRunning(false);
    const mins = completed ? dur : Math.max(1, dur - Math.round(secLeft/60));
    setRating({ mins });
    if (completed) {
      playDing("complete");
      addLocalNotif("⏱️ Deep Work Complete!", `${mins} minutes of focused work — nice.`);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification("⏱️ Deep Work Complete!", { body: `${mins} minutes of focused work — nice.`, icon: '/favicon.ico' }); } catch {}
      }
    }
  };
  const saveSession = () => {
    addDeepWorkSession({ label: label.trim()||"Deep work", category:'general', duration_minutes: rating.mins,
      started_at: startedAt?.toISOString(), ended_at: new Date().toISOString(), session_date: TODAY,
      quality: quality || null, task_id: focusTask?.id });
    setRating(null); setQuality(0); setSecLeft(dur*60); setLabel("");
  };

  const min = Math.floor(secLeft/60).toString().padStart(2,"0");
  const sec = (secLeft%60).toString().padStart(2,"0");
  const pct = ((dur*60 - secLeft) / (dur*60)) * 100;

  const todaySessions = deepWorkSessions.filter(s=>s.session_date===TODAY);
  const todayMins = todaySessions.reduce((s,x)=>s+Number(x.duration_minutes||0),0);
  const weekStart = new Date(TODAY+"T00:00:00"); weekStart.setDate(weekStart.getDate()-6);
  const last7 = Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(d.getDate()+i); return fmt(d); });
  const minsByDay = last7.map(d => deepWorkSessions.filter(s=>s.session_date===d).reduce((s,x)=>s+Number(x.duration_minutes||0),0));
  const maxMins = Math.max(60, ...minsByDay);
  const weekTotalHrs = Math.round(minsByDay.reduce((a,b)=>a+b,0)/60*10)/10;
  const avgQuality = (() => { const rated = deepWorkSessions.filter(s=>s.quality); return rated.length ? Math.round(rated.reduce((s,x)=>s+x.quality,0)/rated.length*10)/10 : null; })();

  // Candidate tasks to focus on: today's incomplete tasks, critical/high first
  const focusCandidates = (tasks[TODAY]||[]).filter(t=>!t.done).sort((a,b)=>(a.priority==='critical'?-1:1)-(b.priority==='critical'?-1:1)).slice(0,5);

  return (
    <div>
      <div className="ta-eh-eyebrow">Execution · {todayMins}m today · target {DEEPWORK_PRESETS[1]*3}m</div>
      <div className="ta-row ta-between" style={{alignItems:'flex-end',marginBottom:20,flexWrap:'wrap',gap:16}}>
        <div>
          <h1 className="ta-eh-title" style={{fontSize:36}}>Where the <em>real work</em> happens.</h1>
          <div className="ta-eh-sub" style={{maxWidth:540}}>The ability to focus without distraction is the rarest, most valuable skill.</div>
        </div>
      </div>

      {/* Session timer — inverts to dark focus tone while running */}
      <div className="ta-card-prominent" style={{
        marginBottom:20, padding:'32px 36px', textAlign:'center',
        background: running ? "linear-gradient(135deg,#1a1c1c 0%,#2a2d35 100%)" : "var(--ta-surface-lowest)",
        color: running ? "#f2f1ee" : "var(--ta-on-surface)",
        transition:"background .5s ease, color .5s ease",
      }}>
        {rating ? (
          <div>
            <div className="ta-serif" style={{fontSize:20,marginBottom:6}}>{rating.mins} minutes of focus.</div>
            <div className="ta-variant" style={{fontSize:13,marginBottom:18}}>How was the quality of this session?</div>
            <div className="ta-row ta-gap-2" style={{justifyContent:'center',marginBottom:20}}>
              {[1,2,3,4,5].map(n=>(
                <button key={n} onClick={()=>setQuality(n)} style={{width:32,height:32,borderRadius:16,border:'none',cursor:'pointer',background:n<=quality?'var(--ta-tertiary)':'var(--ta-surface-high)'}}/>
              ))}
            </div>
            <button onClick={saveSession} className="ta-btn ta-btn-primary">Save session</button>
          </div>
        ) : (<>
          {!running && (
            <div className="ta-row ta-gap-2" style={{justifyContent:'center',marginBottom:20}}>
              {DEEPWORK_PRESETS.map(p=>(
                <button key={p} onClick={()=>{setDur(p);setSecLeft(p*60);}} className="ta-mono" style={{
                  padding:'6px 14px',borderRadius:6,border:'none',cursor:'pointer',fontSize:11,letterSpacing:'.1em',fontWeight:600,
                  background: dur===p ? 'var(--ta-primary)' : 'var(--ta-surface-high)', color: dur===p ? '#fff' : 'var(--ta-on-surface-muted)',
                }}>{p} MIN</button>
              ))}
            </div>
          )}
          {!running && focusCandidates.length>0 && (
            <div className="ta-row ta-gap-2" style={{justifyContent:'center',marginBottom:14,flexWrap:'wrap'}}>
              {focusCandidates.map(t=>(
                <button key={t.id} onClick={()=>setLabel(t.text)} className="ta-chip" style={{cursor:'pointer',border:'none',background:label===t.text?'var(--ta-primary-fixed)':'var(--ta-surface-high)',color:label===t.text?'var(--ta-primary)':'var(--ta-on-surface-variant)'}}>{t.text}</button>
              ))}
            </div>
          )}
          <input
            value={label} onChange={e=>setLabel(e.target.value)} disabled={running}
            placeholder="What are you working on?" className="ta-serif"
            style={{ width:'100%', textAlign:'center', background:'transparent', border:'none', outline:'none', fontSize:18, fontStyle:'italic', marginBottom:18, color: running ? 'rgba(242,241,238,.85)' : 'var(--ta-on-surface-variant)' }}
          />
          <div className="ta-serif tnum" style={{fontSize:80,lineHeight:1,fontWeight:300,letterSpacing:'-0.03em'}}>{min}<span style={{opacity:.3,margin:'0 4px'}}>:</span>{sec}</div>
          <div style={{height:4,background:running?'rgba(255,255,255,.15)':'var(--ta-surface-high)',borderRadius:4,margin:'20px 0',overflow:'hidden'}}>
            <div style={{height:'100%',width:pct+'%',background:running?'#fff':'var(--ta-primary)',transition:'width .4s'}}/>
          </div>
          <div className="ta-row ta-gap-2" style={{justifyContent:'center'}}>
            {!running ? (
              <button onClick={start} className="ta-btn ta-btn-primary" style={{width:160,justifyContent:'center'}}>Enter focus</button>
            ) : (<>
              <button onClick={()=>finish(false)} className="ta-btn" style={{width:160,justifyContent:'center',background:'rgba(255,255,255,.14)',color:'#fff'}}>Stop & log</button>
              <button onClick={()=>{setRunning(false);setSecLeft(dur*60);}} className="ta-btn ta-btn-ghost" style={{color:'rgba(242,241,238,.7)'}}>Cancel</button>
            </>)}
          </div>
        </>)}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20,alignItems:'start'}}>
        {/* Session log */}
        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Session log</h2><span className="ta-section-head-meta">{deepWorkSessions.length} total</span></div>
          <div className="ta-col ta-gap-2">
            {deepWorkSessions.slice(0,20).map(s=>(
              <div key={s.id} className="ta-row ta-between" style={{padding:'12px 14px',background:'var(--ta-surface-low)',borderRadius:'var(--r-md)'}}>
                <span className="ta-serif tnum" style={{fontSize:20,fontWeight:300,color:'var(--ta-on-surface)',minWidth:50}}>{s.duration_minutes}<span className="ta-variant" style={{fontSize:11}}>m</span></span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:'var(--ta-on-surface)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.label}</div>
                  <div className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginTop:2}}>{s.session_date}</div>
                </div>
                {s.quality>0 && (
                  <div className="ta-row ta-gap-1">
                    {[1,2,3,4,5].map(n=><span key={n} style={{width:6,height:6,borderRadius:3,background:n<=s.quality?'var(--ta-tertiary)':'var(--ta-surface-high)'}}/>)}
                  </div>
                )}
                <button onClick={()=>deleteDeepWorkSession(s.id)} className="ta-btn-icon" style={{border:'none',cursor:'pointer',width:24,height:24}}>✕</button>
              </div>
            ))}
            {deepWorkSessions.length===0 && <div className="ta-serif ta-italic" style={{fontSize:14,color:'var(--ta-on-surface-muted)'}}>No sessions logged yet.</div>}
          </div>
        </div>

        <div className="ta-col ta-gap-4">
          {/* Week bar chart */}
          <div className="ta-card-inset">
            <div className="ta-section-head-meta" style={{marginBottom:14}}>Last 7 days</div>
            <div style={{display:'flex',alignItems:'flex-end',gap:8,height:120}}>
              {minsByDay.map((m,i)=>(
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
                  <div style={{width:'100%',height:100,display:'flex',alignItems:'flex-end'}}>
                    <div style={{width:'100%',height:`${Math.max(3,(m/maxMins)*100)}%`,background:last7[i]===TODAY?'var(--ta-primary)':'var(--ta-surface-high)',borderRadius:'var(--r-sm) var(--r-sm) 0 0'}}/>
                  </div>
                  <span className="ta-mono" style={{fontSize:9,color:'var(--ta-on-surface-muted)'}}>{new Date(last7[i]+"T00:00:00").toLocaleDateString('en-US',{weekday:'narrow'})}</span>
                </div>
              ))}
            </div>
            <div className="ta-row ta-between" style={{marginTop:14,paddingTop:14,borderTop:'1px solid var(--ta-outline-variant)'}}>
              <span style={{fontSize:11,color:'var(--ta-on-surface-muted)'}}>This week</span>
              <span className="ta-serif tnum" style={{fontSize:20}}>{weekTotalHrs}h</span>
            </div>
          </div>
          {avgQuality!=null && (
            <div className="ta-card">
              <div className="ta-section-head-meta" style={{marginBottom:8}}>Avg quality</div>
              <div className="ta-serif tnum" style={{fontSize:36,fontWeight:300,lineHeight:1}}>{avgQuality}<span className="ta-variant" style={{fontSize:14}}>/5</span></div>
              <div className="ta-mono" style={{fontSize:10,color:'var(--ta-on-surface-muted)',marginTop:6,letterSpacing:'.1em'}}>SELF-RATED</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ANALYTICS — new module, aggregates existing + new data, no own table
// ══════════════════════════════════════════════════════════════════════════════
function AnalyticsSection({ ctx }) {
  const { tasks, habits, habitLog, deepWorkSessions, fitnessMeals, reflections, goals } = ctx;

  const weekStart = new Date(TODAY+"T00:00:00"); weekStart.setDate(weekStart.getDate()-6);
  const last7 = Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(d.getDate()+i); return fmt(d); });

  const tasksThisWeek = last7.flatMap(d=>tasks[d]||[]);
  const tasksDone = tasksThisWeek.filter(t=>t.done).length;
  const taskRate = tasksThisWeek.length ? Math.round(tasksDone/tasksThisWeek.length*100) : 0;

  const activeHabits = habits.filter(h=>!h.archived);
  const habitPossible = activeHabits.length * 7;
  const habitDone = last7.reduce((sum,d)=> sum + activeHabits.filter(h=>habitLog[`${d}:${h.id}`]).length, 0);
  const habitRate = habitPossible ? Math.round(habitDone/habitPossible*100) : 0;

  const deepWorkMins = deepWorkSessions.filter(s=>last7.includes(s.session_date)).reduce((s,x)=>s+Number(x.duration_minutes||0),0);
  const reflectionsWeek = reflections.filter(r=>last7.includes(r.entry_date)).length;
  const avgCalories = (() => {
    const byDay = last7.map(d => fitnessMeals.filter(m=>m.log_date===d).reduce((s,m)=>s+Number(m.calories||0),0)).filter(v=>v>0);
    return byDay.length ? Math.round(byDay.reduce((a,b)=>a+b,0)/byDay.length) : 0;
  })();

  const goalProgress = goals.filter(g=>!g.archived).length
    ? Math.round(goals.filter(g=>!g.archived).reduce((s,g)=>{
        const total = g.startDate&&g.endDate?daysBetween(g.startDate,g.endDate):100;
        const elapsed = g.startDate?Math.min(total,daysBetween(g.startDate,TODAY)):0;
        return s + (total>0?elapsed/total*100:0);
      },0) / goals.filter(g=>!g.archived).length)
    : 0;

  const domains = [
    { label:"Tasks",     pct:taskRate,    tone:"var(--ta-primary)"  },
    { label:"Habits",    pct:habitRate,   tone:"var(--ta-positive)" },
    { label:"Goals",     pct:goalProgress,tone:"var(--ta-tertiary)" },
    { label:"Deep Work", pct:Math.min(100,Math.round(deepWorkMins/(7*120)*100)), tone:"var(--ta-notice)" },
  ];

  const metric = (label, value, sub, tone) => (
    <div className="ta-card">
      <div className="ta-section-head-meta" style={{marginBottom:10}}>{label}</div>
      <div className="ta-serif tnum" style={{fontSize:40,fontWeight:300,color:tone,lineHeight:1}}>{value}</div>
      <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginTop:6}}>{sub}</div>
    </div>
  );

  return (
    <div>
      <div className="ta-eh-eyebrow">System · Last 7 days</div>
      <h1 className="ta-eh-title" style={{fontSize:36,marginBottom:20}}>The <em>whole</em> picture.</h1>

      <div className="ta-grid-4" style={{marginBottom:20}}>
        {metric("Task completion", `${taskRate}%`, `${tasksDone}/${tasksThisWeek.length} done this week`, "var(--ta-primary)")}
        {metric("Habit consistency", `${habitRate}%`, `${habitDone}/${habitPossible} check-ins`, "var(--ta-positive)")}
        {metric("Deep work", `${Math.round(deepWorkMins/60*10)/10}h`, `${deepWorkMins} minutes this week`, "var(--ta-notice)")}
        {metric("Reflections", reflectionsWeek, "entries logged this week", "var(--ta-tertiary)")}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Life balance</h2></div>
          <div className="ta-col ta-gap-3">
            {domains.map(d=>(
              <div key={d.label}>
                <div className="ta-row ta-between" style={{marginBottom:5}}>
                  <span style={{fontSize:12,color:'var(--ta-on-surface-variant)'}}>{d.label}</span>
                  <span className="ta-mono tnum" style={{fontSize:11}}>{d.pct}%</span>
                </div>
                <div className="ta-bar"><div className="ta-bar-fill" style={{width:`${d.pct}%`,background:d.tone}}/></div>
              </div>
            ))}
          </div>
        </div>
        <div className="ta-card">
          <div className="ta-section-head"><h2 className="ta-section-head-h">Nutrition</h2></div>
          <div className="ta-serif tnum" style={{fontSize:36,fontWeight:300,color:'var(--ta-primary)'}}>{avgCalories||'—'}</div>
          <div style={{fontSize:11,color:'var(--ta-on-surface-muted)',marginTop:4}}>avg kcal/day logged this week</div>
          <div className="ta-marginalia" style={{marginTop:16}}>
            {deepWorkMins>0 && reflectionsWeek>0
              ? "Deep work and reflection are both active this week — that combination tends to compound."
              : "Log a few more days across modules to start seeing correlations here."}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// INTEGRATIONS SECTION
// ══════════════════════════════════════════════════════════════════════════════
function IntegrationsSection({ ctx }) {
  const C = useC();
  const { integrations=[], saveIntegration, uid, profile } = ctx;
  const INT_COLOR = '#2B1DC9';
  const [configuring, setConfiguring] = React.useState(null); // integration type
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const getIntegration = type => integrations.find(i=>i.type===type);
  const isConnected = type => !!getIntegration(type)?.is_active;

  const save = async (type) => {
    setSaving(true);
    await saveIntegration({ user_id:uid, type, config:form, is_active:true });
    setSaving(false); setSaved(true); setTimeout(()=>{setSaved(false);setConfiguring(null);},1500);
  };

  const INTEGRATIONS = [
    {
      type:'google_calendar', icon:'📅', name:'Google Calendar', color:'#4285F4',
      desc:'Connect your Google Calendar for automatic meeting event creation and availability sync.',
      fields:[{key:'note',label:'',type:'note',note:'Google Calendar is connected via the Meetings → Setup tab. Click below to go there.'}],
      action: () => ctx.setView?.('meetings'),
      actionLabel:'Go to Meetings Setup →',
    },
    {
      type:'google_sheets', icon:'📊', name:'Google Sheets', color:'#16A34A',
      desc:'Connect public Google Sheets using an API key. Sync lead/client data automatically.',
      fields:[{key:'api_key',label:'Google API Key',placeholder:'AIzaSy...'}],
    },
    {
      type:'zoom', icon:'🎥', name:'Zoom', color:'#2D8CFF',
      desc:'Connect Zoom to generate meeting links automatically when bookings are confirmed.',
      fields:[{key:'account_id',label:'Account ID',placeholder:'zoom account ID'},{key:'client_id',label:'Client ID',placeholder:'zoom client ID'},{key:'client_secret',label:'Client Secret',placeholder:'zoom client secret',secret:true}],
    },
    {
      type:'twilio', icon:'💬', name:'Twilio WhatsApp', color:'#F22F46',
      desc:'Use Twilio as an alternative to Meta Cloud API for WhatsApp messaging.',
      fields:[{key:'account_sid',label:'Account SID',placeholder:'ACxxxxxxxx'},{key:'auth_token',label:'Auth Token',placeholder:'twilio auth token',secret:true},{key:'from_number',label:'WhatsApp From Number',placeholder:'whatsapp:+14155238886'}],
    },
    {
      type:'instagram', icon:'📸', name:'Instagram Business', color:'#E1306C',
      desc:'Connect your Instagram Business account to send DMs automatically and sync post metrics.',
      oauth: true,
      fields: [],
      oauthLabel: '🔗 Connect Instagram',
      oauthUrl: (uid) => {
        const state = encodeURIComponent(JSON.stringify({ uid }));
        const appId = '3492975334184887';
        return `https://api.instagram.com/oauth/authorize?client_id=${appId}&redirect_uri=https://www.aspreo.com/api/auth/instagram-callback&scope=instagram_business_basic,instagram_business_manage_messages,instagram_manage_comments&response_type=code&state=${state}`;
      },
    },
    {
      type:'resend', icon:'📧', name:'Resend (Email)', color:'#000000',
      desc:'Connect Resend to send booking confirmation and invoice emails from your own domain.',
      fields:[{key:'api_key',label:'Resend API Key',placeholder:'re_xxxxxxxx',secret:true},{key:'from_email',label:'From Email',placeholder:'you@yourdomain.com'},{key:'from_name',label:'From Name',placeholder:'Your Name / Business Name'}],
    },
  ];

  return (
    <div>
      <SectionMasthead
        eyebrow="System · Credentials stored securely per-user"
        title={<>Connect the <em>tools</em>.</>}
      />

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:14}}>
        {INTEGRATIONS.map(intg=>{
          const connected = isConnected(intg.type);
          const existingConfig = getIntegration(intg.type)?.config||{};
          return (
            <div key={intg.type} style={{background:C.surface,border:`1.5px solid ${connected?intg.color:C.border}`,borderRadius:12,padding:18}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                <span style={{fontSize:24}}>{intg.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.text}}>{intg.name}</div>
                  <div style={{fontSize:11,color:connected?intg.color:C.muted,fontWeight:connected?700:400}}>{connected?'✅ Connected':'Not connected'}</div>
                </div>
              </div>
              <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.5}}>{intg.desc}</div>
              {configuring===intg.type && !intg.oauth ? (
                <div>
                  {intg.fields.filter(f=>f.type!=='note').map(field=>(
                    <div key={field.key} style={{marginBottom:8}}>
                      <label style={lbl}>{field.label}</label>
                      <input type={field.secret?'password':'text'} value={form[field.key]||''} onChange={e=>setForm(p=>({...p,[field.key]:e.target.value}))} placeholder={field.placeholder} style={{...inp(),width:'100%'}}/>
                    </div>
                  ))}
                  <div style={{display:'flex',gap:6,marginTop:10}}>
                    <button onClick={()=>save(intg.type)} disabled={saving} style={{...btn(intg.color),fontSize:12,flex:1,opacity:saving?0.6:1}}>{saving?'Saving…':saved?'✅ Saved!':'💾 Save'}</button>
                    <button onClick={()=>setConfiguring(null)} style={{...btn('#999'),fontSize:12}}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{display:'flex',gap:8}}>
                  {intg.action
                    ? <button onClick={intg.action} style={{...btn(intg.color),fontSize:12,width:'100%'}}>{intg.actionLabel}</button>
                    : intg.oauth
                      ? <div style={{display:'flex',flexDirection:'column',gap:6,width:'100%'}}>
                          {connected
                            ? <div>
                                <div style={{fontSize:11,color:intg.color,fontWeight:700,marginBottom:6}}>✅ Connected as @{getIntegration(intg.type)?.config?.username||'unknown'}</div>
                                <button onClick={()=>{setConfiguring(intg.type);setForm({...existingConfig});}} style={{...btn('#888'),fontSize:11,width:'100%'}}>⚙️ Reconnect</button>
                              </div>
                            : <button onClick={()=>{
                                const url = intg.oauthUrl(uid);
                                console.log('[Instagram OAuth] Opening URL:', url);
                                if(!url||url.includes('undefined')){
                                  alert('Instagram App ID not configured. Check REACT_APP_INSTAGRAM_APP_ID env var.');
                                  return;
                                }
                                const popup = window.open(url,'instagram_auth','width=600,height=700,scrollbars=yes,resizable=yes');
                                if(!popup||popup.closed||typeof popup.closed==='undefined'){
                                  // Popup was blocked — open in same tab
                                  window.location.href = url;
                                  return;
                                }
                                const handler = (e) => {
                                  if(e.data?.type==='ig_connected'){
                                    saveIntegration({user_id:uid,type:intg.type,config:{username:e.data.username,ig_user_id:e.data.ig_user_id},is_active:true});
                                    window.removeEventListener('message',handler);
                                    popup?.close();
                                    ctx.setAlert?.('✅ Instagram connected!');
                                  }
                                  if(e.data?.type==='ig_error'){
                                    window.removeEventListener('message',handler);
                                    ctx.setAlert?.('❌ Instagram: '+e.data.error);
                                  }
                                };
                                window.addEventListener('message',handler);
                              }} style={{...btn(intg.color),fontSize:12,width:'100%'}}>{intg.oauthLabel}</button>
                          }
                        </div>
                      : <button onClick={()=>{setConfiguring(intg.type);setForm({...existingConfig});}} style={{...btn(connected?'#888':intg.color),fontSize:12,width:'100%'}}>{connected?'⚙️ Reconfigure':'🔗 Connect'}</button>
                  }
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
